/* eslint-disable */
import React, { useState, useEffect, useRef, createContext, useContext } from "react";
import { createClient } from "@supabase/supabase-js";

// ─── Supabase ─────────────────────────────────────────────────────────────────
const SUPABASE_URL = "https://ilfooyjtbtpsmzaezroj.supabase.co";
const SUPABASE_ANON_KEY = "sb_publishable_eejIrxmMGgnBdKie9W0ZQA_7oW1Ewtv";
const sb = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// ─── Supabase Data Layer ───────────────────────────────────────────────────────
// Loads
const sbGetLoads = async (uid) => {
  const { data } = await sb.from("loads").select("*").eq("user_id", uid).order("created_at", { ascending: false });
  return (data || [])
    .map(r => ({ id: r.id, user_id: r.user_id, created_at: r.created_at, ...r.data, completed: r.completed }))
    .filter(l => !l.deleted && !l._payStatusFor);
};

// Fleet load/expense/fuel functions removed — no driver-owner connection
const sbGetFleetLoads = async () => [];
const sbGetFleetFuelLogs = async () => [];
const sbGetFleetDriverBusinessExpenses = async () => [];
const sbGetFleetExpenses = async () => [];
const sbSaveFleetExpense = async () => {};
const sbDeleteFleetExpense = async () => {};
const sbGetDriverFleetRates = async () => null;
const sbSaveDriverFleetRates = async () => {};


const sbSaveLoad = async (load, uid) => {
  const { id, ...data } = load;
  await sb.from("loads").upsert({ id, user_id: uid, data, completed: !!load.completed }, { onConflict: "id" });
};
const sbDeleteLoad = async (id) => { await sb.from("loads").delete().eq("id", id); };
const sbSoftDeleteLoad = async (load, uid) => {
  const { id, ...rest } = load;
  const data = { ...rest, deleted: true, deleted_at: new Date().toISOString() };
  await sb.from("loads").upsert({ id, user_id: uid, data, completed: !!load.completed }, { onConflict: "id" });
};

// Expenses
const sbGetExpenses = async (uid) => {
  const { data } = await sb.from("expenses").select("*").eq("user_id", uid).order("created_at", { ascending: false });
  return (data || []).map(r => ({ id: r.id, user_id: r.user_id, ...r.data })).filter(e => !e.deleted);
};
const sbUploadReceipt = async (expId, base64DataUrl) => {
  try {
    if (!base64DataUrl || !base64DataUrl.startsWith("data:")) return null;
    const [header, base64] = base64DataUrl.split(",");
    const mimeType = header.match(/:(.*?);/)?.[1] || "image/jpeg";
    const ext = mimeType === "application/pdf" ? "pdf" : mimeType.split("/")[1] || "jpg";
    const byteChars = atob(base64);
    const bytes = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
    const blob = new Blob([bytes], { type: mimeType });
    const path = `receipts/${expId}.${ext}`;
    const { error } = await sb.storage.from("expense-receipts").upload(path, blob, { upsert: true, contentType: mimeType });
    if (error) return null;
    const { data: urlData } = sb.storage.from("expense-receipts").getPublicUrl(path);
    return urlData?.publicUrl || null;
  } catch (e) { return null; }
};

const sbSaveExpense = async (exp, uid) => {
  const { id, receipt, ...rest } = exp;
  let receiptUrl = exp.receiptUrl || null;
  // If there's a new local base64 receipt, upload it to Storage
  if (receipt && receipt.startsWith("data:")) {
    const uploaded = await sbUploadReceipt(id, receipt);
    if (uploaded) receiptUrl = uploaded;
  }
  const data = { ...rest, receiptUrl };
  await sb.from("expenses").upsert({ id, user_id: uid, data }, { onConflict: "id" });
};
const sbDeleteExpense = async (id) => { await sb.from("expenses").delete().eq("id", id); };
const sbSoftDeleteExpense = async (exp, uid) => {
  const { id, receipt, ...rest } = exp;
  const data = { ...rest, deleted: true, deleted_at: new Date().toISOString() };
  await sb.from("expenses").upsert({ id, user_id: uid, data }, { onConflict: "id" });
};
const sbGetDeletedItems = async (uid) => {
  const { data: loadData } = await sb.from("loads").select("*").eq("user_id", uid);
  const loads = (loadData || [])
    .map(r => ({ id: r.id, user_id: r.user_id, owner_uid: r.owner_uid, created_at: r.created_at, ...r.data, completed: r.completed }))
    .filter(l => l.deleted === true);
  const { data: expData } = await sb.from("expenses").select("*").eq("user_id", uid);
  const expenses = (expData || [])
    .map(r => ({ id: r.id, user_id: r.user_id, ...r.data }))
    .filter(e => e.deleted === true);
  return { loads, expenses };
};
const sbGetAllDeletedItems = async () => {
  const cutoff = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString();
  const { data: loadData } = await sb.from("loads").select("*").order("created_at", { ascending: false });
  const loads = (loadData || [])
    .map(r => ({ id: r.id, user_id: r.user_id, owner_uid: r.owner_uid, created_at: r.created_at, ...r.data, completed: r.completed }))
    .filter(l => l.deleted === true);
  const { data: expData } = await sb.from("expenses").select("*").order("created_at", { ascending: false });
  const expenses = (expData || [])
    .map(r => ({ id: r.id, user_id: r.user_id, ...r.data }))
    .filter(e => e.deleted === true);
  return { loads, expenses };
};
const sbRestoreLoad = async (load, uid) => {
  const { deleted, deleted_at, id, ...rest } = load;
  const data = { ...rest, deleted: false, deleted_at: null };
  await sb.from("loads").upsert({ id, user_id: uid, data, completed: !!load.completed }, { onConflict: "id" });
};
const sbRestoreExpense = async (exp, uid) => {
  const { deleted, deleted_at, id, receipt, ...rest } = exp;
  await sb.from("expenses").upsert({ id, user_id: uid, data: { ...rest, deleted: false, deleted_at: null } }, { onConflict: "id" });
};
const sbGetAllExpenses = async (uid) => sbGetExpenses(uid);

// Fleet fuel/expense functions — removed (no driver-owner connection)

// Trucks
const sbGetTrucks = async (ownerUid) => {
  const { data } = await sb.from("trucks").select("*").eq("user_id", ownerUid);
  return (data || []).map(r => ({ id: r.id, ...r.data }));
};
const sbSaveTrucks = async (trucks, ownerUid) => {
  if (!trucks || !trucks.length) {
    await sb.from("trucks").delete().eq("user_id", ownerUid);
    return;
  }
  // Use upsert to avoid 409 conflicts from delete+insert race conditions
  await sb.from("trucks").delete().eq("user_id", ownerUid);
  const { error } = await sb.from("trucks").insert(trucks.map(t => { const { id, ...data } = t; return { id, user_id: ownerUid, data }; }));
  if (error) {
    // If insert fails due to conflict, try upsert as fallback
    await sb.from("trucks").upsert(trucks.map(t => { const { id, ...data } = t; return { id, user_id: ownerUid, data }; }), { onConflict: "id" });
  }
};

// Settings (rates + routes)
const sbGetSettings = async (ownerUid) => {
  const { data } = await sb.from("settings").select("*").eq("user_id", ownerUid).maybeSingle();
  return data || null;
};
const sbSaveSettings = async (ownerUid, rates, routes) => {
  await sb.from("settings").upsert({ user_id: ownerUid, rates, routes }, { onConflict: "user_id" });
};

// Profiles
const sbGetProfile = async (uid) => {
  const { data } = await sb.from("profiles").select("*").eq("id", uid).maybeSingle();
  return data || null;
};
const sbSaveProfile = async (profile) => {
  await sb.from("profiles").upsert(profile, { onConflict: "id" });
};
const sbGetProfileByInviteCode = async () => null;

// ─── Recurring Routes ─────────────────────────────────────────────────────────
const sbGetRecurringRoutes = async (ownerUid) => {
  const { data } = await sb.from("recurring_routes").select("*").eq("owner_uid", ownerUid).order("created_at", { ascending: false });
  return data || [];
};
const sbSaveRecurringRoute = async (route) => {
  const { id, ...rest } = route;
  if (id) await sb.from("recurring_routes").update(rest).eq("id", id);
  else await sb.from("recurring_routes").insert(rest);
};
const sbDeleteRecurringRoute = async (id) => { await sb.from("recurring_routes").delete().eq("id", id); };

// Driver ratings removed — no driver-owner connection
const sbGetDriverRatings = async () => [];
const sbSaveDriverRating = async () => {};

// ─── Fuel Log ─────────────────────────────────────────────────────────────────
const sbGetFuelLog = async (uid) => {
  const { data } = await sb.from("fuel_log").select("*").eq("user_id", uid).order("date", { ascending: false });
  return data || [];
};
const sbSaveFuelEntry = async (entry) => {
  const { id, ...rest } = entry;
  if (id) await sb.from("fuel_log").update(rest).eq("id", id).eq("user_id", rest.user_id);
  else await sb.from("fuel_log").insert(rest);
};
const sbDeleteFuelEntry = async (id) => { await sb.from("fuel_log").delete().eq("id", id); };

// Payments table removed — no driver-owner connection
const sbGetPayments = async () => [];
const sbGetDriverPayments = async () => [];
const sbSavePayment = async () => null;
// Update load payment_status field in Supabase
const sbUpdateLoadPaymentStatus = async () => {};

// ─── Driver Documents / Expiry Alerts ────────────────────────────────────────
const sbGetDriverDocuments = async (uid) => {
  const { data } = await sb.from("driver_documents").select("*").eq("user_id", uid).order("expiry_date", { ascending: true });
  return data || [];
};
const sbGetFleetDocuments = async () => [];
const sbSaveDriverDocument = async (doc) => {
  const { id, ...rest } = doc;
  if (id) await sb.from("driver_documents").update(rest).eq("id", id);
  else await sb.from("driver_documents").insert(rest);
};
const sbDeleteDriverDocument = async (id) => { await sb.from("driver_documents").delete().eq("id", id); };
// ONE row per user. "message" = JSON array of msgs. "reply" = "__closed__" = closed.

const chatParse = (row) => {
  if (!row) return null;
  let msgs = [];
  try {
    const raw = row.message;
    if (Array.isArray(raw)) msgs = raw;
    else if (typeof raw === "string" && raw.trim().startsWith("[")) msgs = JSON.parse(raw);
    else if (raw) msgs = [{ id:"1", from:"user", text: String(raw), time: row.created_at }];
  } catch(e) { msgs = []; }
  return { ...row, msgs, closed: row.reply === "__closed__" };
};

const chatGetThread = async (uid) => {
  // Use select all + pick best row — handles duplicate rows gracefully
  const { data, error } = await sb.from("support_messages")
    .select("*").eq("from_uid", uid).order("created_at", { ascending: false });
  if (error) { console.error("chatGetThread:", error); return null; }
  if (!data || data.length === 0) return null;
  // Parse all rows and pick the one with the most messages
  const parsed = data.map(chatParse).filter(Boolean);
  parsed.sort((a, b) => (b.msgs?.length || 0) - (a.msgs?.length || 0));
  return parsed[0];
};

const chatGetAll = async () => {
  const { data, error } = await sb.from("support_messages").select("*").order("created_at", { ascending: false });
  if (error) { console.error("chatGetAll:", error); return []; }
  return (data || []).map(chatParse);
};

const chatSendMsg = async (uid, fromName, fromEmail, newMsg) => {
  // Fetch existing thread first to get current messages
  const existing = await chatGetThread(uid);
  const msgs = existing ? [...existing.msgs, newMsg] : [newMsg];
  // upsert — inserts if no row exists, updates if it does. Prevents duplicate rows.
  const { error } = await sb.from("support_messages").upsert({
    from_uid: uid, from_name: fromName, from_email: fromEmail,
    message: JSON.stringify(msgs), read: false,
    reply: existing?.reply === "__closed__" ? null : (existing?.reply || null),
  }, { onConflict: "from_uid" });
  return error;
};

const chatAdminSend = async (uid, currentMsgs, newMsg) => {
  // Re-fetch to get latest messages before appending (catches any new customer msgs)
  const fresh = await chatGetThread(uid);
  const base = fresh ? fresh.msgs : currentMsgs;
  const msgs = [...base, newMsg];
  const { error } = await sb.from("support_messages")
    .update({ message: JSON.stringify(msgs), read: true }).eq("from_uid", uid);
  return error;
};

const chatSetClosed = async (uid, closed) => {
  const { error } = await sb.from("support_messages")
    .update({ reply: closed ? "__closed__" : null }).eq("from_uid", uid);
  return error;
};

const chatMarkRead = async (uid) => {
  await sb.from("support_messages").update({ read: true }).eq("from_uid", uid);
};

const sbSendSupportMessage = async () => {};
const sbGetSupportMessages = async () => [];
const sbReplyToSupport = async () => {};
const sbMarkSupportRead = async () => {};


// Driver listing removed — no driver-owner connection
const sbGetDrivers = async () => [];

// Fleet connection removed — each user manages their own data independently
const sbJoinFleet = async () => ({ error: "Fleet connections disabled" });
const sbGetMyFleets = async () => [];
const sbGetFleetDrivers = async () => [];
const sbLeaveFleet = async () => {};
const sbRemoveDriverFromFleet = async () => {};
const sbGetInactiveDrivers = async () => [];
const sbSendFleetRequest = async () => ({ error: null });
const sbGetFleetRequests = async () => [];
const sbApproveFleetRequest = async () => {};
const sbRejectFleetRequest = async () => {};

const sbGetMaintenance = async (ownerUid) => {
  const { data } = await sb.from("maintenance").select("*").eq("user_id", ownerUid).order("created_at", { ascending: false });
  return (data || []).map(r => ({ id: r.id, ...r.data }));
};
const sbSaveMaintenance = async (record, ownerUid) => {
  const { id, ...data } = record;
  await sb.from("maintenance").upsert({ id, user_id: ownerUid, data }, { onConflict: "id" });
};
const sbDeleteMaintenance = async (id) => { await sb.from("maintenance").delete().eq("id", id); };

// ─── Storage Keys (kept for non-synced local state only) ──────────────────────
const USERS_KEY = "tp-users-v1";
const SESSION_KEY = "tp-session-v1";
const loadsKey = (uid) => `tp-loads-${uid}`;
const ratesKey = (uid) => `tp-rates-${uid}`;
const routesKey = (uid) => `tp-routes-${uid}`;
const expensesKey = (uid) => `tp-expenses-${uid}`;
const trucksKey = (uid) => `tp-trucks-${uid}`;
const maintenanceKey = (uid) => `tp-maint-${uid}`;

const hashPass = (s) => { let h = 0; for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0; return h.toString(36); };
const getUsers = () => { try { return JSON.parse(localStorage.getItem(USERS_KEY) || "{}"); } catch { return {}; } };
const saveUsers = (u) => localStorage.setItem(USERS_KEY, JSON.stringify(u));
const getSession = () => { try { return JSON.parse(localStorage.getItem(SESSION_KEY) || "null"); } catch { return null; } };
const saveSession = (s) => localStorage.setItem(SESSION_KEY, JSON.stringify(s));
const clearSession = () => localStorage.removeItem(SESSION_KEY);
const genCode = () => { const c = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; let r = ""; for (let i = 0; i < 6; i++) r += c[Math.floor(Math.random() * c.length)]; return r; };
const getStored = (key) => { try { return JSON.parse(localStorage.getItem(key) || "[]"); } catch { return []; } };

// ─── PWA Update Banner ────────────────────────────────────────────────────────
function useServiceWorkerUpdate() {
  const [showUpdate, setShowUpdate] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then((reg) => {
        reg.addEventListener("updatefound", () => {
          const newWorker = reg.installing;
          newWorker.addEventListener("statechange", () => {
            if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
              setWaitingWorker(newWorker);
              setShowUpdate(true);
            }
          });
        });
      });
    }
  }, []);
  const applyUpdate = () => {
    if (waitingWorker) {
      waitingWorker.postMessage({ type: "SKIP_WAITING" });
      window.location.reload();
    }
  };
  return { showUpdate, applyUpdate };
}

// ─── Universal PDF Helper ─────────────────────────────────────────────────────
// Loads html2pdf.js from CDN on first use, then generates a real .pdf download.
const _loadHtml2Pdf = () => new Promise((resolve, reject) => {
  if (window.html2pdf) { resolve(window.html2pdf); return; }
  const s = document.createElement("script");
  s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js";
  s.onload = () => resolve(window.html2pdf);
  s.onerror = reject;
  document.head.appendChild(s);
});

const downloadPDF = async (htmlContent, filename, session=null) => {
  const companyName = session?.companyName || null;
  const userName = session?.fullName || session?.name || null;
  // No TruckPilot branding — company header only
  const docHeader = `
    <div style="display:flex;justify-content:space-between;align-items:flex-start;padding:16px 20px;background:#1C2B4A;margin-bottom:0;border-radius:8px 8px 0 0;border-bottom:3px solid #E8962E;">
      <div>
        ${companyName ? `<div style="font-size:18px;font-weight:900;color:#fff;letter-spacing:0.5px;">${companyName}</div>` : ""}
        ${userName ? `<div style="font-size:12px;color:rgba(255,255,255,0.65);margin-top:3px;">${userName}</div>` : ""}
      </div>
      <div style="text-align:right;font-size:11px;color:rgba(255,255,255,0.5);margin-top:4px;">
        ${new Date().toLocaleDateString("en-CA",{year:"numeric",month:"long",day:"numeric"})}
      </div>
    </div>
    <div style="height:2px;background:linear-gradient(90deg,#E8962E,#FFD700);margin-bottom:24px;"></div>
  `;
  const fullHtml = `<div style="font-family:Arial,sans-serif;color:#1A1A1A;font-size:13px;line-height:1.5;">
<style>
  /* Kill any legacy brand/header/footer divs that may be inside report content */
  .brand,.footer,.header-brand,.report-logo{display:none!important}
  /* Amount columns always right-align */
  tfoot td{text-align:right!important;padding:10px!important;font-weight:900!important}
  tfoot td:first-child{text-align:left!important}
  td.right,th.right{text-align:right!important}
</style>
${docHeader}${htmlContent}
</div>`;

  try {
    const html2pdf = await _loadHtml2Pdf();
    const el = document.createElement("div");
    el.innerHTML = fullHtml;
    // Inject print styles inline so html2canvas renders them correctly
    el.querySelectorAll("table").forEach(t => { t.style.width="100%"; t.style.borderCollapse="collapse"; });
    document.body.appendChild(el);
    await html2pdf().set({
      margin: [12, 12, 12, 12],
      filename: filename + ".pdf",
      image: { type: "jpeg", quality: 0.97 },
      html2canvas: { scale: 2, useCORS: true, logging: false },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" }
    }).from(el).save();
    document.body.removeChild(el);
    tpToast("PDF downloaded", "download", filename + ".pdf");
  } catch(err) {
    // Fallback: open in new window → browser print-to-PDF
    console.warn("html2pdf failed, falling back to print:", err);
    const styled = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${filename}</title>
<style>
  @page{margin:20mm} *{box-sizing:border-box}
  body{font-family:Arial,sans-serif;color:#1A1A1A;margin:0;padding:16px;font-size:13px;line-height:1.5}
  table{width:100%;border-collapse:collapse;margin:16px 0}
  th{background:#1C2B4A;padding:9px 12px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:0.8px;color:#fff;font-weight:700}
  th.right{text-align:right}
  td{padding:9px 12px;border-bottom:1px solid #E8EEF4;font-size:13px;vertical-align:middle}
  td.right{text-align:right;font-weight:700}
  tfoot td{padding:10px 12px;font-weight:900;font-size:13px;background:#F5F8FF;border-top:2px solid #E8962E;text-align:right}
  tfoot td:first-child{text-align:left}
  .brand,.footer,.header,.header-brand,.report-logo{display:none!important}
  @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style></head><body>${fullHtml}</body></html>`;
    const w = window.open("", "_blank");
    if (w) { w.document.write(styled); w.document.close(); w.focus(); w.print(); }
  }
};

const previewPDF = (htmlContent, filename, setPreviewHtml, setPreviewTitle, setShowPreview) => {
  setPreviewHtml(htmlContent);
  setPreviewTitle(filename);
  setShowPreview(true);
};

const DEFAULT_RATES = { companyWaitRate: 0, driverWaitRate: 0, billingMethod: "per_load", perLoadRate: 0, payFrequency: "weekly", payDay: "friday", cutoffDay: "thursday", cutoffTime: "23:59", ownerPayPerLoad: 0, ownerWaitRate: 0 };

// ── Pay Period Calculator ─────────────────────────────────────────
const getPayPeriod = (rates = {}) => {
  const now = new Date();

  // If manual period dates are set in settings, use those as source of truth
  if (rates.periodStart && rates.periodEnd && rates.payDate) {
    const periodStart = new Date(rates.periodStart + "T12:00:00");
    const periodEnd = new Date(rates.periodEnd + "T12:00:00");
    const nextPayDate = new Date(rates.payDate + "T12:00:00");
    const fmt = (d) => d.toLocaleDateString("en-CA", {month:"short", day:"numeric"});
    const daysUntilNext = Math.ceil((nextPayDate - now) / (1000*60*60*24));
    return {
      periodStart, periodEnd, nextPayDate,
      label: `${fmt(periodStart)} – ${fmt(periodEnd)}`,
      nextPayLabel: fmt(nextPayDate),
      daysUntilNext: Math.max(0, daysUntilNext),
      cutoffLabel: `${fmt(periodEnd)} @ 23:59`,
      freqLabel: "Custom",
      periodStart: rates.periodStart,
      periodEnd: rates.periodEnd,
    };
  }

  const freq = rates.payFrequency || "weekly";
  const payDay = rates.payDay || "friday";
  const cutoffDay = rates.cutoffDay || "thursday";
  const cutoffTime = rates.cutoffTime || "23:59";
  const days = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];
  const payDayNum = days.indexOf(payDay);
  const cutDayNum = days.indexOf(cutoffDay);
  const todayNum = now.getDay();

  let periodStart, periodEnd, nextPayDate;

  if (freq === "weekly") {
    let daysBack = (todayNum - cutDayNum + 7) % 7;
    if (daysBack === 0) daysBack = 7;
    periodEnd = new Date(now); periodEnd.setDate(now.getDate() - daysBack + 7);
    periodStart = new Date(periodEnd); periodStart.setDate(periodEnd.getDate() - 6);
    let daysUntilPay = (payDayNum - todayNum + 7) % 7 || 7;
    nextPayDate = new Date(now); nextPayDate.setDate(now.getDate() + daysUntilPay);
  } else if (freq === "biweekly") {
    let daysBack = (todayNum - cutDayNum + 7) % 7;
    if (daysBack === 0) daysBack = 14;
    periodEnd = new Date(now); periodEnd.setDate(now.getDate() - daysBack + 14);
    periodStart = new Date(periodEnd); periodStart.setDate(periodEnd.getDate() - 13);
    let daysUntilPay = (payDayNum - todayNum + 7) % 7 || 7;
    nextPayDate = new Date(now); nextPayDate.setDate(now.getDate() + daysUntilPay);
  } else if (freq === "semimonthly") {
    const d = now.getDate();
    if (d <= 15) {
      periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
      periodEnd = new Date(now.getFullYear(), now.getMonth(), 15);
      nextPayDate = new Date(now.getFullYear(), now.getMonth(), 15);
    } else {
      periodStart = new Date(now.getFullYear(), now.getMonth(), 16);
      periodEnd = new Date(now.getFullYear(), now.getMonth()+1, 0);
      nextPayDate = new Date(now.getFullYear(), now.getMonth()+1, 1);
    }
  } else {
    periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
    periodEnd = new Date(now.getFullYear(), now.getMonth()+1, 0);
    nextPayDate = new Date(now.getFullYear(), now.getMonth()+1, 1);
  }

  const fmt = (d) => d.toLocaleDateString("en-CA", {month:"short", day:"numeric"});
  const daysUntilNext = Math.ceil((nextPayDate - now) / (1000*60*60*24));

  return {
    periodStart, periodEnd, nextPayDate,
    label: `${fmt(periodStart)} – ${fmt(periodEnd)}`,
    nextPayLabel: fmt(nextPayDate),
    daysUntilNext: Math.max(0, daysUntilNext),
    cutoffLabel: `${cutoffDay.charAt(0).toUpperCase()+cutoffDay.slice(1)} @ ${cutoffTime}`,
    freqLabel: { weekly:"Weekly", biweekly:"Bi-Weekly", semimonthly:"Semi-Monthly", monthly:"Monthly" }[freq] || freq,
  };
};

// ─── Subscription Plans ───────────────────────────────────────────────────────
const PLANS = {
  free: {
    id: "free", label: "⭐ Beta", price: 0, color: "#92400e", emoji: "⭐",
    desc: "Perfect for individual drivers",
    features: ["Log loads", "Expenses", "Fuel & Food finder", "Messages", "Documents", "Emergency"],
    limits: { loads: 50, drivers: 0 },
  },
  basic: {
    id: "basic", label: "💼 Hauler", price: 9.99, color: "#E8962E", emoji: "💼",
    desc: "For small fleet owners",
    features: ["Everything in Free", "Up to 3 drivers", "Reports & Analytics", "Maintenance tracker", "Pay Calculator", "Payroll"],
    limits: { loads: 500, drivers: 3 },
  },
  pro: {
    id: "pro", label: "🚀 Pro", price: 24.99, color: "#166534", emoji: "🚀",
    desc: "Full fleet management power",
    features: ["Everything in Basic", "Unlimited drivers", "IFTA Tax", "Tax Export", "Load Board", "Priority support", "Referral commissions"],
    limits: { loads: Infinity, drivers: Infinity },
  },
};

// ─── Referral System ──────────────────────────────────────────────────────────
const REFERRAL_KEY = "tp-referrals-v1";
const INSPECTION_ALERTS_KEY = (ownerUid) => `tp-inspection-alerts-v1-${ownerUid}`;
const getInspectionAlerts = (ownerUid) => { try { return JSON.parse(localStorage.getItem(INSPECTION_ALERTS_KEY(ownerUid)) || "[]"); } catch { return []; } };
const loadInspectionAlertsFromSb = async (ownerUid) => {
  try {
    const d = await sbGetSettings(ownerUid);
    if (d && d.inspectionAlerts && d.inspectionAlerts.length > 0) {
      localStorage.setItem(INSPECTION_ALERTS_KEY(ownerUid), JSON.stringify(d.inspectionAlerts));
      return d.inspectionAlerts;
    }
  } catch {}
  return getInspectionAlerts(ownerUid);
};
const saveInspectionAlerts = (ownerUid, alerts) => {
  localStorage.setItem(INSPECTION_ALERTS_KEY(ownerUid), JSON.stringify(alerts));
  // Also persist to Supabase so alerts survive on other devices
  sbGetSettings(ownerUid).then(existing => {
    sbSaveSettings(ownerUid, {...(existing||{}), inspectionAlerts: alerts}, []);
  }).catch(()=>{});
};
const getReferrals = () => { try { return JSON.parse(localStorage.getItem(REFERRAL_KEY) || "{}"); } catch { return {}; } };
const saveReferrals = (r) => localStorage.setItem(REFERRAL_KEY, JSON.stringify(r));
const genReferralCode = (uid) => "TIQ-" + uid.slice(0,4).toUpperCase() + Math.random().toString(36).slice(2,5).toUpperCase();
const REFERRAL_COMMISSION_PCT = 20;
const REFERRAL_MONTHS = 3;

const getUserPlan = (uid) => { try { const s = JSON.parse(localStorage.getItem("tp-session-v1") || "null"); if (s && s.plan) return s.plan; } catch {} return "free"; };
const canAccessFeature = (plan, feature) => {
  return true; // Beta mode — all features free for everyone
};
const todayStr = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const getPlanColor = (plan) => {
  if (plan === "pro") return { bg:"#166534", text:"#fff", label:"🚀 Pro" };
  if (plan === "basic") return { bg:"#E8962E", text:"#fff", label:"💼 Hauler" };
  return { bg:"#92400e", text:"#fff", label:"⭐ Beta" };
};
const fmtC = (v) => `$${Number(v || 0).toLocaleString('en-CA', {minimumFractionDigits:2, maximumFractionDigits:2})}`;

/* ── Ripple helper — call in onClick: tpRipple(e) ── */
const tpRipple = (e) => {
  try {
    const btn = e.currentTarget;
    const r = document.createElement("span");
    const rect = btn.getBoundingClientRect();
    const sz = Math.max(rect.width, rect.height);
    r.style.cssText = `width:${sz}px;height:${sz}px;left:${e.clientX - rect.left - sz / 2}px;top:${e.clientY - rect.top - sz / 2}px`;
    r.className = "tp-ripple";
    btn.appendChild(r);
    setTimeout(() => r.remove(), 700);
  } catch(err) {}
};

/* ── Global toast system ── */
let _tpToastEmit = null;
const tpToast = (msg, type="success", sub="") => { if (_tpToastEmit) _tpToastEmit({msg, type, sub}); };

function TPToastContainer() {
  const [toasts, setToasts] = useState([]);
  useEffect(() => {
    _tpToastEmit = (t) => {
      const id = Date.now();
      setToasts(prev => [...prev, { ...t, id }]);
      setTimeout(() => setToasts(prev => prev.map(x => x.id===id ? {...x, hiding:true} : x)), 2800);
      setTimeout(() => setToasts(prev => prev.filter(x => x.id !== id)), 3200);
    };
    return () => { _tpToastEmit = null; };
  }, []);

  if (!toasts.length) return null;
  const icons = { success:"✅", error:"⚠️", info:"ℹ️", download:"⬇" };
  const accents = { success:"#48c78e", error:"#EF4444", info:"#E8962E", download:"#1C2B4A" };
  return (
    <div style={{position:"fixed",bottom:100,left:"50%",transform:"translateX(-50%)",zIndex:99999,display:"flex",flexDirection:"column",gap:8,alignItems:"center",pointerEvents:"none",width:"calc(100% - 32px)",maxWidth:380}}>
      {toasts.map(t => (
        <div key={t.id} className={`tp-toast${t.hiding?" tp-toast-out":""}`}
          style={{background:"#1C2B4A",color:"#fff",padding:"13px 17px",borderRadius:14,display:"flex",alignItems:"center",gap:11,boxShadow:"0 8px 32px rgba(0,0,0,.35)",borderLeft:`4px solid ${accents[t.type]||accents.info}`,fontSize:13,fontWeight:600,width:"100%"}}>
          <span style={{fontSize:19}}>{icons[t.type]||icons.info}</span>
          <div>
            <div style={{fontWeight:700,marginBottom:t.sub?2:0}}>{t.msg}</div>
            {t.sub && <div style={{fontSize:11,opacity:.6}}>{t.sub}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}
const fmt = (m) => { const h = Math.floor(m / 60), mn = m % 60; return `${h}h ${mn}m`; };
const secsToHMS = (s) => { const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60; return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:${String(ss).padStart(2,"0")}`; };

// ─── Contact Us Tab ───────────────────────────────────────────────────────────
const COMPANY_PHONE = "437-700-5835";
const WHATSAPP_NUMBER = "14377005835";
const COMPANY_EMAIL = "support@truckpilot.ca";

// ─── Feature Flags Section (used inside SuperAdminTab) ────────────────────────
function FeatureFlagsSection() {
  const ALL_FEATURES = [
    { id:"dashboard",          label:"🏠 Dashboard",           desc:"Main home screen" },
    { id:"new",                label:"➕ Add Load",             desc:"Log a new load" },
    { id:"log",                label:"📋 Load Log",             desc:"View all logged loads" },
    { id:"report",             label:"📊 Reports",              desc:"Earnings and load reports" },
    { id:"expenses",           label:"🧾 Expenses",             desc:"Track business expenses" },
    { id:"payroll",            label:"💵 Payroll",              desc:"Driver payroll management" },
    { id:"analytics",          label:"📈 Analytics",            desc:"Fleet analytics and trends" },
    { id:"financial_reports",  label:"📋 Financial Reports",    desc:"Detailed financial breakdowns" },
    { id:"tax",                label:"🗂 Tax Export",           desc:"Export data for tax filing" },
    { id:"ifta",               label:"🧮 IFTA Tax",             desc:"Quarterly IFTA calculations" },
    { id:"documents",          label:"📁 Documents",            desc:"Store and manage documents" },
    { id:"drivers",            label:"👥 Drivers",              desc:"Fleet driver management" },
    { id:"maintenance",        label:"🔧 Maintenance",          desc:"Vehicle maintenance tracker" },
    { id:"fuel_log",           label:"🪣 Fuel Log",             desc:"Track fuel fill-ups" },
    { id:"emergency",          label:"🚨 Emergency",            desc:"Roadside emergency contacts" },
    { id:"doc_expiry",         label:"📅 Doc Expiry",           desc:"Document expiry alerts" },
    { id:"messages",           label:"💬 Messages",             desc:"Fleet messaging" },
    { id:"contact",            label:"📞 Contact Us",           desc:"Support and contact page" },
    { id:"profile",            label:"👤 Profile",              desc:"User profile and settings" },
  ];
  const [flags, setFlags] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    sb.from("app_config").select("feature_flags").eq("id","global").maybeSingle().then(({data}) => {
      if (data?.feature_flags) setFlags(data.feature_flags);
    });
  }, []);

  const toggle = (id, field) => {
    setFlags(prev => ({
      ...prev,
      [id]: { ...prev[id], [field]: !prev[id]?.[field] }
    }));
    setSaved(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { error } = await sb.from("app_config").upsert(
        { id:"global", feature_flags: flags, updated_at: new Date().toISOString() },
        { onConflict:"id" }
      );
      if (error) {
        console.error("Feature flags save error:", error);
        alert("Save failed: " + error.message + "\n\nYou may need to add a 'feature_flags' column (jsonb) to your app_config table in Supabase.");
      } else {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch(e) {
      console.error("Save exception:", e);
      alert("Save error: " + e.message);
    }
    setSaving(false);
  };

  return (
    <div style={{ padding:"16px 16px 40px" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
        <div>
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:22 }}>🚦 Feature Flags</div>
          <div style={{ fontSize:12, color:"#374151", marginTop:2 }}>Control which features are visible, enabled, or marked Coming Soon.</div>
        </div>
        <button onClick={save} disabled={saving}
          style={{ padding:"10px 22px", background:saved?"#166534":"#E8962E", color:"#fff", border:"none", borderRadius:10, fontWeight:800, fontSize:13, cursor:"pointer" }}>
          {saving ? "Saving..." : saved ? "✅ Saved!" : "💾 Save"}
        </button>
      </div>

      <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
        {ALL_FEATURES.map(f => {
          const flag = flags[f.id] || {};
          return (
            <div key={f.id} style={{ background:"#fff", border:"1px solid #E8EEF4", borderRadius:12, padding:"14px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
              <div>
                <div style={{ fontWeight:700, fontSize:14, color:"#1A1A1A" }}>{f.label}</div>
                <div style={{ fontSize:12, color:"#374151", marginTop:2 }}>{f.desc}</div>
              </div>
              <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                {/* Coming Soon toggle */}
                <label style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer" }}>
                  <div onClick={() => toggle(f.id, "comingSoon")}
                    style={{ width:40, height:22, borderRadius:11, background: flag.comingSoon ? "#F59E0B" : "#E5E7EB",
                      position:"relative", transition:"background .2s", cursor:"pointer" }}>
                    <div style={{ position:"absolute", top:3, left: flag.comingSoon ? 21 : 3, width:16, height:16,
                      borderRadius:"50%", background:"#fff", transition:"left .2s", boxShadow:"0 1px 3px rgba(0,0,0,0.55)" }}/>
                  </div>
                  <span style={{ fontSize:12, fontWeight:600, color: flag.comingSoon ? "#B45309" : "#666" }}>
                    {flag.comingSoon ? "🚧 Coming Soon" : "Coming Soon"}
                  </span>
                </label>
                {/* Hidden toggle */}
                <label style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer" }}>
                  <div onClick={() => toggle(f.id, "hidden")}
                    style={{ width:40, height:22, borderRadius:11, background: flag.hidden ? "#EF4444" : "#E5E7EB",
                      position:"relative", transition:"background .2s", cursor:"pointer" }}>
                    <div style={{ position:"absolute", top:3, left: flag.hidden ? 21 : 3, width:16, height:16,
                      borderRadius:"50%", background:"#fff", transition:"left .2s", boxShadow:"0 1px 3px rgba(0,0,0,0.55)" }}/>
                  </div>
                  <span style={{ fontSize:12, fontWeight:600, color: flag.hidden ? "#EF4444" : "#666" }}>
                    {flag.hidden ? "🔒 Hidden" : "Hidden"}
                  </span>
                </label>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop:16, padding:"12px 14px", background:"#F5F6F8", border:"1px solid #F59E0B", borderRadius:10, fontSize:12, color:"#B45309" }}>
        <strong>How it works:</strong> Changes save to Supabase instantly and apply to all users on their next app load. <strong>Coming Soon</strong> shows a badge and disables the tap. <strong>Hidden</strong> removes the feature entirely from the app.
      </div>
    </div>
  );
}

function SuperAdminTab({ session }) {
  const [activeSection, setActiveSection] = useState("overview");
  const [allUsers, setAllUsers] = useState([]);
  const [allMessages, setAllMessages] = useState([]);
  const [allLoads, setAllLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userSearch, setUserSearch] = useState("");
  const [userRoleFilter, setUserRoleFilter] = useState("all");
  const [userPlanFilter, setUserPlanFilter] = useState("all");
  const [expandedUser, setExpandedUser] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);

  // ── Recover Data State ──
  const [recoverItems, setRecoverItems] = useState({ loads: [], expenses: [] });
  const [recoverLoading, setRecoverLoading] = useState(false);
  const [recoverSearch, setRecoverSearch] = useState("");
  const [recoverRestoring, setRecoverRestoring] = useState(null);
  const [recoverMsg, setRecoverMsg] = useState("");

  // ── Delete Data State ──
  const [deleteDataItems, setDeleteDataItems] = useState({ loads: [], expenses: [] });
  const [deleteDataLoading, setDeleteDataLoading] = useState(false);
  const [deleteDataSearch, setDeleteDataSearch] = useState("");
  const [deleteDataDeleting, setDeleteDataDeleting] = useState(null);
  const [deleteDataMsg, setDeleteDataMsg] = useState("");

  // ── Orphan Cleanup State ──
  const [orphans, setOrphans] = useState([]);
  const [orphanLoading, setOrphanLoading] = useState(false);
  const [orphanDeleting, setOrphanDeleting] = useState(null);
  const [orphanMsg, setOrphanMsg] = useState("");
  const [orphanScanned, setOrphanScanned] = useState(false);

  // ── Stale Driver Report Expense State ──
  const [staleExps, setStaleExps] = useState([]);
  const [staleLoading, setStaleLoading] = useState(false);
  const [staleDeleting, setStaleDeleting] = useState(null);
  const [staleMsg, setStaleMsg] = useState("");
  const [staleScanned, setStaleScanned] = useState(false);

  const scanOrphanedMirrors = async () => {
    setOrphanLoading(true);
    setOrphanMsg("");
    setOrphans([]);
    setOrphanScanned(false);
    try {
      // Fetch ALL expenses rows from Supabase
      const { data: allRows, error } = await sb.from("expenses").select("id, user_id, data").order("id");
      if (error) throw error;

      // Build a set of all base IDs (strip "mirror-" prefix)
      const baseIds = new Set();
      const mirrorRows = [];
      (allRows || []).forEach(row => {
        const id = row.id || "";
        if (id.startsWith("mirror-")) {
          mirrorRows.push(row);
        } else {
          baseIds.add(id);
        }
      });

      // Build a map of all users for name lookup
      const { data: profiles } = await sb.from("profiles").select("id, name, role, company_name");
      const userMap = {};
      (profiles || []).forEach(p => { userMap[p.id] = p; });

      // An orphan mirror is one whose base ID either:
      // 1. Does NOT exist at all (original was hard-deleted), OR
      // 2. EXISTS but has data.deleted === true (original was soft-deleted)
      const baseIdToRow = {};
      (allRows || []).forEach(row => {
        if (!row.id.startsWith("mirror-")) baseIdToRow[row.id] = row;
      });

      const found = [];
      for (const mirrorRow of mirrorRows) {
        const baseId = mirrorRow.id.replace(/^mirror-/, "");
        const originalRow = baseIdToRow[baseId];
        const isOrphan = !originalRow || originalRow?.data?.deleted === true;
        if (!isOrphan) continue;

        const d = mirrorRow.data || {};
        const ownerProfile = userMap[mirrorRow.user_id];
        const driverProfile = d.user_id ? userMap[d.user_id] : null;

        found.push({
          mirrorId: mirrorRow.id,
          baseId,
          ownerUid: mirrorRow.user_id,
          ownerName: ownerProfile?.company_name || ownerProfile?.name || mirrorRow.user_id,
          driverUid: d.user_id || "—",
          driverName: d.driverName || driverProfile?.name || d.user_id || "Unknown Driver",
          amount: Number(d.amount || 0),
          category: d.category || "expense",
          merchant: d.merchant || "",
          date: d.date || "—",
          source: d.source || "—",
          expenseType: d.expenseType || "—",
          originalDeleted: !!originalRow?.data?.deleted,
          mirrorDeleted: !!d.deleted,
        });
      }
      setOrphans(found);
      setOrphanScanned(true);
      setOrphanMsg(found.length === 0 ? "✅ No orphaned mirror expenses found. Database is clean." : `⚠️ Found ${found.length} orphaned mirror expense${found.length !== 1 ? "s" : ""}.`);
    } catch(e) {
      setOrphanMsg("❌ Scan failed: " + (e.message || String(e)));
    }
    setOrphanLoading(false);
  };

  const deleteOrphan = async (mirrorId) => {
    setOrphanDeleting(mirrorId);
    try {
      await sbDeleteExpense(mirrorId);
      setOrphans(prev => prev.filter(o => o.mirrorId !== mirrorId));
      setOrphanMsg(`✅ Deleted mirror expense ${mirrorId}`);
      setTimeout(() => setOrphanMsg(""), 5000);
    } catch(e) {
      setOrphanMsg("❌ Delete failed: " + (e.message || String(e)));
    }
    setOrphanDeleting(null);
  };

  const deleteAllOrphans = async () => {
    if (!window.confirm(`Permanently delete ALL ${orphans.length} orphaned mirror expenses?\n\nThis removes mirror entries whose originals were already deleted by the driver.\nThis CANNOT be undone.`)) return;
    setOrphanLoading(true);
    let deleted = 0;
    for (const o of orphans) {
      try { await sbDeleteExpense(o.mirrorId); deleted++; } catch(e) {}
    }
    setOrphans([]);
    setOrphanMsg(`✅ Cleaned up ${deleted} orphaned mirror expense${deleted !== 1 ? "s" : ""}.`);
    setOrphanLoading(false);
  };

  // ── Stale Driver Report Expenses: scan & clear via notification command bus ──
  // RLS prevents admin from reading/deleting driver rows directly.
  // Solution: admin inserts an app_notification with type="expense_cleanup",
  // target_uid = driver's UID, message = JSON array of expense IDs.
  // The driver's app self-executes the deletes on next load using their own session.
  const [staleDriverFilter, setStaleDriverFilter] = useState("all");
  const [pendingCmds, setPendingCmds] = useState([]); // cleanup commands waiting to execute

  const scanStaleDriverExpenses = async () => {
    setStaleLoading(true);
    setStaleMsg("");
    setStaleExps([]);
    setStaleScanned(false);
    try {
      const fleetDrivers = [];

      const { data: profiles } = await sb.from("profiles").select("id, name, role, company_name");
      const userMap = {};
      (profiles || []).forEach(p => { userMap[p.id] = p; });

      if (!fleetDrivers || fleetDrivers.length === 0) {
        setStaleMsg("✅ No fleet drivers found.");
        setStaleScanned(true);
        setStaleLoading(false);
        return;
      }

      // Use sbGetFleetDriverBusinessExpenses — this already works cross-user via OR query
      // (same RLS path that successfully fetches driver expenses for the owner dashboard)
      const bizExps = await sbGetFleetDriverBusinessExpenses(session.uid);

      // Also fetch any pending cleanup commands already queued
      const { data: existingCmds } = await sb
        .from("app_notifications")
        .select("id, target_uid, message, created_at")
        .eq("type", "expense_cleanup")
        .eq("active", true);
      const pendingExpIds = new Set();
      (existingCmds || []).forEach(cmd => {
        try { JSON.parse(cmd.message).forEach(id => pendingExpIds.add(id)); } catch(e) {}
      });
      setPendingCmds(existingCmds || []);

      const found = bizExps.map(e => {
        const driverProfile = userMap[e.user_id];
        const fleetDriver = fleetDrivers.find(d => d.driver_uid === e.user_id);
        return {
          id: e.id,
          driverUid: e.user_id,
          driverName: e.driverName || driverProfile?.name || fleetDriver?.driver_name || e.user_id,
          amount: Number(e.amount || 0),
          category: e.category || "expense",
          merchant: e.merchant || e.note || "",
          date: e.date || "—",
          source: e.source || "—",
          expenseType: e.expenseType || "—",
          isPendingCleanup: pendingExpIds.has(e.id),
        };
      });

      // Sort: pending cleanup first, then by driver, then date desc
      found.sort((a, b) => {
        if (a.isPendingCleanup && !b.isPendingCleanup) return -1;
        if (!a.isPendingCleanup && b.isPendingCleanup) return 1;
        if (a.driverName < b.driverName) return -1;
        if (a.driverName > b.driverName) return 1;
        return (b.date || "").localeCompare(a.date || "");
      });

      setStaleExps(found);
      setStaleScanned(true);
      setStaleMsg(
        found.length === 0
          ? "✅ No active driver business expenses found."
          : `Found ${found.length} active business expense${found.length !== 1 ? "s" : ""} across ${new Set(found.map(e => e.driverUid)).size} driver${new Set(found.map(e => e.driverUid)).size !== 1 ? "s" : ""}. Select expenses to force-clear from driver reports.`
      );
    } catch(e) {
      setStaleMsg("❌ Scan failed: " + (e.message || String(e)));
    }
    setStaleLoading(false);
  };

  // Queue a cleanup command: admin writes notification, driver self-executes on next load
  const queueCleanupForExpense = async (exp) => {
    try {
      // Check if already queued
      if (exp.isPendingCleanup) {
        setStaleMsg(`ℹ️ Expense ${exp.id} is already queued for cleanup. Driver will see it cleared on next app load.`);
        return;
      }
      await sb.from("app_notifications").insert({
        title: "Admin Expense Cleanup",
        message: JSON.stringify([exp.id]),
        type: "expense_cleanup",
        target: "specific",
        target_uid: exp.driverUid,
        active: true,
        visibility: "silent", // don't show as a UI notification to driver
      });
      // Mark as pending in local state
      setStaleExps(prev => prev.map(e => e.id === exp.id ? { ...e, isPendingCleanup: true } : e));
      setStaleMsg(`✅ Cleanup queued for "${exp.category}" ($${exp.amount.toFixed(2)}) — will clear from ${exp.driverName}'s report on their next app load.`);
      setTimeout(() => setStaleMsg(""), 6000);
    } catch(e) {
      setStaleMsg("❌ Failed to queue cleanup: " + (e.message || String(e)));
    }
  };

  const queueCleanupForAll = async () => {
    const toQueue = (staleDriverFilter === "all" ? staleExps : staleExps.filter(e => e.driverUid === staleDriverFilter))
      .filter(e => !e.isPendingCleanup);
    if (toQueue.length === 0) { setStaleMsg("ℹ️ All selected expenses are already queued for cleanup."); return; }
    if (!window.confirm(`Queue cleanup for ${toQueue.length} expense${toQueue.length !== 1 ? "s" : ""}?\n\nThese will be removed from the driver's report the next time they open the app.\nThis cannot be undone.`)) return;
    setStaleLoading(true);
    // Group by driver for efficiency — one notification per driver
    const byDriver = {};
    toQueue.forEach(e => { if (!byDriver[e.driverUid]) byDriver[e.driverUid] = []; byDriver[e.driverUid].push(e.id); });
    let queued = 0;
    for (const [driverUid, ids] of Object.entries(byDriver)) {
      try {
        await sb.from("app_notifications").insert({
          title: "Admin Expense Cleanup",
          message: JSON.stringify(ids),
          type: "expense_cleanup",
          target: "specific",
          target_uid: driverUid,
          active: true,
          visibility: "silent",
        });
        queued += ids.length;
      } catch(err) {}
    }
    setStaleExps(prev => prev.map(e => toQueue.find(q => q.id === e.id) ? { ...e, isPendingCleanup: true } : e));
    setStaleMsg(`✅ Cleanup queued for ${queued} expense${queued !== 1 ? "s" : ""} across ${Object.keys(byDriver).length} driver${Object.keys(byDriver).length !== 1 ? "s" : ""}. Will clear on their next app load.`);
    setStaleLoading(false);
  };

  // Cancel a pending cleanup command
  const cancelPendingCleanup = async (expId) => {
    try {
      // Find which cmd contains this expId and remove it
      const cmd = pendingCmds.find(c => { try { return JSON.parse(c.message).includes(expId); } catch(e) { return false; } });
      if (!cmd) return;
      const remaining = JSON.parse(cmd.message).filter(id => id !== expId);
      if (remaining.length === 0) {
        await sb.from("app_notifications").update({ active: false }).eq("id", cmd.id);
      } else {
        await sb.from("app_notifications").update({ message: JSON.stringify(remaining) }).eq("id", cmd.id);
      }
      setStaleExps(prev => prev.map(e => e.id === expId ? { ...e, isPendingCleanup: false } : e));
      setStaleMsg(`↩️ Cleanup cancelled for expense ${expId}.`);
      setTimeout(() => setStaleMsg(""), 4000);
    } catch(e) {
      setStaleMsg("❌ Cancel failed: " + (e.message || String(e)));
    }
  };

  // ── Profile Editor State ──
  const [profileConfig, setProfileConfig] = useState(null);
  const [profileConfigLoading, setProfileConfigLoading] = useState(false);
  const [profileConfigSaved, setProfileConfigSaved] = useState(false);
  const [profileConfigSaving, setProfileConfigSaving] = useState(false);
  const [profileTargetUser, setProfileTargetUser] = useState("global");

  // ── Notifications State ──
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [newNotif, setNewNotif] = useState({
    title: "", message: "", type: "info", target: "all", target_uid: "", expires_at: "", visibility: "after"
  });
  const [notifSending, setNotifSending] = useState(false);
  const [notifSent, setNotifSent] = useState(false);

  const loadNotifications = async () => {
    setNotifLoading(true);
    try {
      const { data } = await sb.from("app_notifications").select("*").order("created_at", { ascending: false });
      setNotifications(data || []);
    } catch(e) {}
    setNotifLoading(false);
  };

  const sendNotification = async () => {
    if (!newNotif.message.trim()) return alert("Please enter a message.");
    if (newNotif.target === "specific" && !newNotif.target_uid) return alert("Please select a specific user.");
    setNotifSending(true);
    try {
      await sb.from("app_notifications").insert({
        title: newNotif.title.trim(),
        message: newNotif.message.trim(),
        type: newNotif.type,
        target: newNotif.target,
        target_uid: newNotif.target === "specific" ? newNotif.target_uid : null,
        active: true,
        visibility: newNotif.visibility || "after",
        expires_at: newNotif.expires_at || null,
      });
      setNotifSent(true);
      setTimeout(() => setNotifSent(false), 2500);
      setNewNotif({ title:"", message:"", type:"info", target:"all", target_uid:"", expires_at:"", visibility:"after" });
      await loadNotifications();
    } catch(e) { console.error(e); }
    setNotifSending(false);
  };

  const toggleNotifActive = async (id, current) => {
    await sb.from("app_notifications").update({ active: !current }).eq("id", id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, active: !current } : n));
  };

  const deleteNotif = async (id) => {
    if (!window.confirm("Delete this notification?")) return;
    await sb.from("app_notifications").delete().eq("id", id);
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // ── Admin Profile Edit ──
  const [adminProfile, setAdminProfile] = useState({
    name: session?.fullName || session?.name || "",
    username: "",
    email: session?.email || "",
    newPassword: "",
    confirmPassword: "",
    avatarUrl: "",
  });
  const [adminProfileSaving, setAdminProfileSaving] = useState(false);
  const [adminProfileSaved, setAdminProfileSaved] = useState(false);
  const [adminProfileMsg, setAdminProfileMsg] = useState({ text:"", type:"" });
  const [adminAvatarUploading, setAdminAvatarUploading] = useState(false);

  const loadAdminProfile = async () => {
    try {
      const { data } = await sb.from("profiles").select("*").eq("id", session.uid).maybeSingle();
      if (data) {
        setAdminProfile(p => ({
          ...p,
          name: data.name || "",
          username: data.username || "",
          email: data.username_email || session?.email || "",
          avatarUrl: data.avatar_url || "",
        }));
      }
    } catch(e) {}
  };

  const saveAdminProfile = async () => {
    if (!adminProfile.name.trim()) return setAdminProfileMsg({ text:"Name is required.", type:"error" });
    if (adminProfile.newPassword && adminProfile.newPassword !== adminProfile.confirmPassword)
      return setAdminProfileMsg({ text:"Passwords don't match.", type:"error" });
    if (adminProfile.newPassword && adminProfile.newPassword.length < 6)
      return setAdminProfileMsg({ text:"Password must be at least 6 characters.", type:"error" });
    setAdminProfileSaving(true);
    setAdminProfileMsg({ text:"", type:"" });
    try {
      // Update profile table
      await sb.from("profiles").update({
        name: adminProfile.name.trim(),
        username: adminProfile.username.trim().toLowerCase() || null,
        username_email: adminProfile.email.trim().toLowerCase() || null,
        avatar_url: adminProfile.avatarUrl || null,
      }).eq("id", session.uid);
      // Update email in auth if changed
      if (adminProfile.email && adminProfile.email !== session.email) {
        await sb.auth.updateUser({ email: adminProfile.email.trim() });
      }
      // Update password if provided
      if (adminProfile.newPassword) {
        const { error } = await sb.auth.updateUser({ password: adminProfile.newPassword });
        if (error) { setAdminProfileMsg({ text:"Password update failed: " + error.message, type:"error" }); setAdminProfileSaving(false); return; }
      }
      setAdminProfileSaved(true);
      setAdminProfileMsg({ text:"✅ Profile updated successfully!", type:"success" });
      setTimeout(() => { setAdminProfileSaved(false); setAdminProfileMsg({ text:"", type:"" }); }, 3000);
      setAdminProfile(p => ({ ...p, newPassword:"", confirmPassword:"" }));
    } catch(e) { setAdminProfileMsg({ text:"Something went wrong.", type:"error" }); }
    setAdminProfileSaving(false);
  };

  const uploadAdminAvatar = async (file) => {
    if (!file) return;
    setAdminAvatarUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result;
        // Store as base64 in profile (simple approach, no storage bucket needed)
        await sb.from("profiles").update({ avatar_url: base64 }).eq("id", session.uid);
        setAdminProfile(p => ({ ...p, avatarUrl: base64 }));
        setAdminAvatarUploading(false);
      };
      reader.readAsDataURL(file);
    } catch(e) { setAdminAvatarUploading(false); }
  };

  // ── Create New Admin Account ──
  const [newAdmin, setNewAdmin] = useState({ name:"", email:"", username:"", password:"", sendLink:false });
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [createAdminMsg, setCreateAdminMsg] = useState({ text:"", type:"" });

  const createAdminAccount = async () => {
    if (!newAdmin.name.trim()) return setCreateAdminMsg({ text:"Name is required.", type:"error" });
    if (!newAdmin.email.trim()) return setCreateAdminMsg({ text:"Email is required.", type:"error" });
    if (!newAdmin.sendLink && !newAdmin.password) return setCreateAdminMsg({ text:"Password is required, or choose to send a reset link.", type:"error" });
    if (!newAdmin.sendLink && newAdmin.password.length < 6) return setCreateAdminMsg({ text:"Password must be at least 6 characters.", type:"error" });
    setCreatingAdmin(true);
    setCreateAdminMsg({ text:"", type:"" });
    try {
      // Create user in Supabase Auth
      const { data, error } = await sb.auth.signUp({
        email: newAdmin.email.trim(),
        password: newAdmin.sendLink ? Math.random().toString(36).slice(-12) + "Aa1!" : newAdmin.password,
        options: { data: { name: newAdmin.name.trim(), role: "superadmin", ownerUid: "PENDING", plan: "pro" } }
      });
      if (error) { setCreateAdminMsg({ text:"Error: " + error.message, type:"error" }); setCreatingAdmin(false); return; }
      const uid = data.user?.id;
      if (uid) {
        await sb.from("profiles").upsert({
          id: uid, name: newAdmin.name.trim(), role: "superadmin",
          owner_uid: uid, plan: "pro",
          username: newAdmin.username.trim() || null,
          username_email: newAdmin.email.trim().toLowerCase(),
        }, { onConflict:"id" });
        // Send password reset link if chosen
        if (newAdmin.sendLink) {
          await sb.auth.resetPasswordForEmail(newAdmin.email.trim(), { redirectTo: "https://app.truckpilot.ca" });
          setCreateAdminMsg({ text:`✅ Admin account created! Password setup link sent to ${newAdmin.email}.`, type:"success" });
        } else {
          setCreateAdminMsg({ text:`✅ Admin account created for ${newAdmin.name}!`, type:"success" });
        }
        setNewAdmin({ name:"", email:"", username:"", password:"", sendLink:false });
        await loadData();
      }
    } catch(e) { setCreateAdminMsg({ text:"Something went wrong.", type:"error" }); }
    setCreatingAdmin(false);
  };
  const [adminNotes, setAdminNotes] = useState({});
  const [newNote, setNewNote] = useState({});
  const [savingNote, setSavingNote] = useState({});

  const loadAdminNotes = async (uid) => {
    try {
      const { data } = await sb.from("admin_notes").select("*").eq("user_id", uid).order("created_at", { ascending: false });
      setAdminNotes(prev => ({ ...prev, [uid]: data || [] }));
    } catch(e) {}
  };

  const saveAdminNote = async (uid) => {
    const text = (newNote[uid] || "").trim();
    if (!text) return;
    setSavingNote(prev => ({ ...prev, [uid]: true }));
    try {
      await sb.from("admin_notes").insert({ user_id: uid, note: text, created_by: session?.uid || "admin" });
      setNewNote(prev => ({ ...prev, [uid]: "" }));
      await loadAdminNotes(uid);
    } catch(e) {}
    setSavingNote(prev => ({ ...prev, [uid]: false }));
  };

  const deleteAdminNote = async (noteId, uid) => {
    await sb.from("admin_notes").delete().eq("id", noteId);
    setAdminNotes(prev => ({ ...prev, [uid]: (prev[uid] || []).filter(n => n.id !== noteId) }));
  };

  // ── Feature Overrides per user ──
  const FEATURE_LIST = [
    { id:"payroll",           label:"💵 Payroll"            },
    { id:"analytics",         label:"📈 Analytics"          },
    { id:"tax",               label:"🗂 Tax Export"         },
    { id:"financial_reports", label:"📋 Financial Reports"  },
    { id:"maintenance",       label:"🔧 Maintenance"        },
    { id:"documents",         label:"📁 Documents"          },
    { id:"drivers",           label:"👥 Drivers"            },
  ];

  const toggleFeatureOverride = async (uid, featureId, currentOverrides) => {
    const updated = { ...currentOverrides };
    if (updated[featureId] === true) delete updated[featureId];
    else updated[featureId] = true;
    await sb.from("profiles").update({ feature_overrides: updated }).eq("id", uid);
    setAllUsers(prev => prev.map(u => u.id === uid ? { ...u, feature_overrides: updated } : u));
  };

  // ── Export CSV ──
  const exportUsersCSV = () => {
    const headers = ["Name","Email","Role","Plan","Loads","Joined","Last Seen","Invite Code"];
    const rows = allUsers.map(u => [
      u.name || "Unknown",
      u.username_email || u.email || "—",
      u.role || "—",
      u.plan || "free",
      allLoads.filter(l => l.user_id === u.id).length,
      u.created_at?.slice(0,10) || "—",
      u.last_seen ? new Date(u.last_seen).toLocaleDateString() : "Never",
      u.invite_code || "—",
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type:"text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `truckpilot-users-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  // ── Message Search ──
  const [msgSearch, setMsgSearch] = useState("");

  const DEFAULT_PROFILE_ITEMS = {
    settingsMore: [
      { id:"ai",       icon:"🤖", label:"TruckPilot AI",    subtitle:"Ask anything, get tax help",        visible:true },
      { id:"darkmode", icon:"🌙", label:"Dark Mode",         subtitle:"Switch display theme",              visible:true },
      { id:"support",  icon:"🆘", label:"Support / Help",   subtitle:"Contact us anytime",                visible:true },
      { id:"settings", icon:"⚙️", label:"App Settings",    subtitle:"Rates, routes, trucks",             visible:true, ownerOnly:false },
      { id:"privacy",  icon:"🔒", label:"Privacy & Security",subtitle:"Password, data, notifications",    visible:true },
    ],
    ownerGroups: [
      { group:"Fleet",  visible:true, items:[
        { id:"drivers", icon:"👥", label:"Drivers",        visible:true },
        { id:"log",     icon:"🚛", label:"My Loads",       visible:true },
        { id:"new",     icon:"➕", label:"Add Load",      visible:true },
      ]},
      { group:"Money", visible:true, items:[
        { id:"expenses",          icon:"🧾", label:"Expenses",           visible:true },
        { id:"payroll",           icon:"💵", label:"Payroll",            visible:true },
        { id:"report",            icon:"📊", label:"Reports",            visible:true },
        { id:"analytics",         icon:"📈", label:"Analytics",          visible:true },
        { id:"tax",               icon:"🗂", label:"Tax Export",         visible:true },
        { id:"financial_reports", icon:"📋", label:"Financial Reports",  visible:true },
        { id:"documents",         icon:"📁", label:"Documents",          visible:true },
      ]},
      { group:"Operations", visible:true, items:[
        { id:"maintenance", icon:"🔧", label:"Maintenance",  visible:true },
        { id:"emergency",   icon:"🚨", label:"Emergency",    visible:true },
      ]},
      { group:"Community", visible:true, items:[
      ]},
    ],
    driverGroups: [
      { group:"My Work", visible:true, items:[
        { id:"log",    icon:"📋", label:"My Loads",  visible:true },
        { id:"new",    icon:"➕", label:"Add Load",  visible:true },
        { id:"report", icon:"📊", label:"Reports",   visible:true },
      ]},
      { group:"Money", visible:true, items:[
        { id:"expenses",          icon:"🧾", label:"Expenses",          visible:true },
        { id:"analytics",         icon:"📈", label:"Analytics",         visible:true },
        { id:"tax",               icon:"🗂", label:"Tax Export",        visible:true },
        { id:"financial_reports", icon:"📋", label:"Financial Reports", visible:true },
        { id:"documents",         icon:"📁", label:"Documents",         visible:true },
      ]},
      { group:"Operations", visible:true, items:[
        { id:"maintenance", icon:"🔧", label:"Maintenance", visible:true },
        { id:"emergency",   icon:"🚨", label:"Emergency",   visible:true },
      ]},
      { group:"Community", visible:true, items:[
      ]},
    ],
    theme: {
      primaryColor:   "#E8962E",
      accentColor:    "#FFD700",
      bgColor:        "#F5F6F8",
      cardBg:         "#FFFFFF",
      textColor:      "#1A1A1A",
      fontFamily:     "Barlow",
      borderRadius:   "18",
    },
  };

  const loadProfileConfig = async () => {
    setProfileConfigLoading(true);
    try {
      const { data } = await sb.from("app_config").select("*").eq("id", profileTargetUser === "global" ? "global" : profileTargetUser).maybeSingle();
      if (data?.profile_items) {
        const raw = typeof data.profile_items === "string"
          ? JSON.parse(data.profile_items)
          : data.profile_items;
        const def = JSON.parse(JSON.stringify(DEFAULT_PROFILE_ITEMS));
        setProfileConfig({
          settingsMore:  raw.settingsMore  || def.settingsMore,
          ownerGroups:   raw.ownerGroups   || def.ownerGroups,
          driverGroups:  raw.driverGroups  || def.driverGroups,
          theme:         { ...def.theme, ...(raw.theme || {}) },
        });
      } else {
        setProfileConfig(JSON.parse(JSON.stringify(DEFAULT_PROFILE_ITEMS)));
      }
    } catch(e) {
      setProfileConfig(JSON.parse(JSON.stringify(DEFAULT_PROFILE_ITEMS)));
    }
    setProfileConfigLoading(false);
  };

  const saveProfileConfig = async () => {
    if (!profileConfig) return;
    setProfileConfigSaving(true);
    try {
      const id = profileTargetUser === "global" ? "global" : profileTargetUser;
      await sb.from("app_config").upsert({ id, profile_items: profileConfig, updated_at: new Date().toISOString() }, { onConflict: "id" });
      setProfileConfigSaved(true);
      setTimeout(() => setProfileConfigSaved(false), 2500);
    } catch(e) { console.error(e); }
    setProfileConfigSaving(false);
  };

  const resetProfileConfig = () => {
    if (!window.confirm("Reset to default profile layout? This will discard all changes.")) return;
    setProfileConfig(JSON.parse(JSON.stringify(DEFAULT_PROFILE_ITEMS)));
  };

  const toggleSettingsItem = (id) => {
    setProfileConfig(prev => ({
      ...prev,
      settingsMore: prev.settingsMore.map(item => item.id === id ? { ...item, visible: !item.visible } : item)
    }));
  };

  const toggleGroupVisible = (groupIdx, isOwner) => {
    const key = isOwner ? "ownerGroups" : "driverGroups";
    setProfileConfig(prev => {
      const groups = [...prev[key]];
      groups[groupIdx] = { ...groups[groupIdx], visible: !groups[groupIdx].visible };
      return { ...prev, [key]: groups };
    });
  };

  const toggleGroupItem = (groupIdx, itemId, isOwner) => {
    const key = isOwner ? "ownerGroups" : "driverGroups";
    setProfileConfig(prev => {
      const groups = prev[key].map((g, gi) => gi !== groupIdx ? g : {
        ...g,
        items: g.items.map(item => item.id === itemId ? { ...item, visible: !item.visible } : item)
      });
      return { ...prev, [key]: groups };
    });
  };

  const updateTheme = (key, value) => {
    setProfileConfig(prev => ({ ...prev, theme: { ...prev.theme, [key]: value } }));
  };
  const [appSettings, setAppSettings] = useState({
    supportPhone: "437-700-5835",
    supportEmail: "truckpilot.ca@gmail.com",
    whatsapp: "14377005835",
    appVersion: "v3.0",
    appName: "TruckPilot",
    basicPrice: "9.99",
    proPrice: "24.99",
    maintenanceMode: false,
    betaMode: true,
    showUpgradeOption: true,
    maxFreeLoads: "50",
    maxBasicDrivers: "3",
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersRes, msgsRes, loadsRes, settingsRes] = await Promise.all([
        sb.from("profiles").select("*").order("created_at", { ascending: false }),
        sb.from("support_messages").select("*").order("created_at", { ascending: false }),
        sb.from("loads").select("id,user_id,owner_uid,completed,created_at").order("created_at", { ascending: false }),
        sb.from("settings").select("*").eq("user_id", "__app__").maybeSingle(),
      ]);
      setAllUsers(usersRes.data || []);
      setAllMessages((msgsRes.data || []).map(chatParse));
      setAllLoads(loadsRes.data || []);
      if (settingsRes.data?.rates) {
        setAppSettings(prev => ({ ...prev, ...settingsRes.data.rates }));
      }
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const updateUserPlan = async (uid, plan) => {
    await sb.from("profiles").update({ plan }).eq("id", uid);
    setAllUsers(prev => prev.map(u => u.id === uid ? { ...u, plan } : u));
  };

  const updateUserRole = async (uid, role) => {
    await sb.from("profiles").update({ role }).eq("id", uid);
    setAllUsers(prev => prev.map(u => u.id === uid ? { ...u, role } : u));
  };

  const updateUserName = async (uid, name) => {
    await sb.from("profiles").update({ name }).eq("id", uid);
    setAllUsers(prev => prev.map(u => u.id === uid ? { ...u, name } : u));
  };

  const deleteUser = async (uid) => {
    const user = allUsers.find(u => u.id === uid);
    const isOwnerUser = user?.role === "owner";
    if (!window.confirm(
      isOwnerUser
        ? "Delete this owner account?\n\n✅ Owner's loads, expenses, settings, trucks → DELETED\n✅ Fleet links → DELETED\n✅ Drivers' own data → UNTOUCHED (they keep their records)\n\nThis cannot be undone."
        : "Delete this driver account?\n\n✅ Driver's loads, expenses, fuel logs → DELETED\n✅ Fleet links → REMOVED\n\nThis cannot be undone."
    )) return;

    if (isOwnerUser) {
      // Unlink all driver loads from this owner first
      await sb.from("loads").update({ owner_uid: null }).eq("owner_uid", uid);
      await Promise.all([
        sb.from("loads").delete().eq("user_id", uid),
        sb.from("expenses").delete().eq("user_id", uid),
        sb.from("maintenance").delete().eq("user_id", uid),
        sb.from("support_messages").delete().eq("from_uid", uid),
        sb.from("settings").delete().eq("user_id", uid),
        sb.from("trucks").delete().eq("user_id", uid),
        sb.from("fuel_log").delete().eq("user_id", uid),
        sb.from("recurring_routes").delete().eq("owner_uid", uid),
        sb.from("app_config").delete().eq("id", uid),
        sb.from("admin_notes").delete().eq("user_id", uid),
      ]);
    } else {
      await Promise.all([
        sb.from("loads").delete().eq("user_id", uid),
        sb.from("expenses").delete().eq("user_id", uid),
        sb.from("maintenance").delete().eq("user_id", uid),
        sb.from("support_messages").delete().eq("from_uid", uid),
        sb.from("fuel_log").delete().eq("user_id", uid),
      ]);
    }
    // Delete profile first
    await sb.from("profiles").delete().eq("id", uid);
    // Delete from auth — try both rpc and admin API
    try { await sb.rpc("delete_user", { user_id: uid }); } catch(e) { console.log("rpc delete_user:", e); }
    try { await sb.auth.admin.deleteUser(uid); } catch(e) { console.log("admin delete:", e); }
    setAllUsers(prev => prev.filter(u => u.id !== uid));
    if (expandedUser === uid) setExpandedUser(null);
    alert(isOwnerUser
      ? "✅ Owner account deleted. Drivers' data preserved and untouched."
      : "✅ Driver account permanently deleted."
    );
  };

  const clearUserData = async (uid) => {
    const user = allUsers.find(u => u.id === uid);
    const isOwnerUser = user?.role === "owner";
    if (!window.confirm(
      isOwnerUser
        ? "Clear this owner's data?\n\n✅ Owner's loads, expenses, settings, trucks → CLEARED\n✅ All driver loads linked to this owner → UNLINKED from owner\n✅ Fleet disconnected\n✅ Drivers keep their own data\n\nAccount stays active. This cannot be undone."
        : "Clear this driver's data?\n\n✅ Driver's loads, expenses, fuel logs → CLEARED\n✅ Fleet links → REMOVED\n\nAccount stays active. This cannot be undone."
    )) return;

    if (isOwnerUser) {
      await Promise.all([
        sb.from("loads").delete().eq("user_id", uid),
        sb.from("expenses").delete().eq("user_id", uid),
        sb.from("maintenance").delete().eq("user_id", uid),
        sb.from("support_messages").delete().eq("from_uid", uid),
        sb.from("settings").delete().eq("user_id", uid),
        sb.from("trucks").delete().eq("user_id", uid),
        sb.from("fuel_log").delete().eq("user_id", uid),
        sb.from("recurring_routes").delete().eq("owner_uid", uid),
        sb.from("app_config").delete().eq("id", uid),
        sb.from("admin_notes").delete().eq("user_id", uid),
        sb.from("profiles").update({
          clear_flag: new Date().toISOString(),
          invite_code: null,
          company_name: null,
          avatar_url: null,
        }).eq("id", uid),
      ]);
      alert("✅ Owner data fully cleared. All loads, expenses, settings, routes and trucks removed.");
    } else {
      await Promise.all([
        sb.from("loads").delete().eq("user_id", uid),
        sb.from("expenses").delete().eq("user_id", uid),
        sb.from("maintenance").delete().eq("user_id", uid),
        sb.from("support_messages").delete().eq("from_uid", uid),
        sb.from("fuel_log").delete().eq("user_id", uid),
        sb.from("profiles").update({ clear_flag: new Date().toISOString() }).eq("id", uid),
      ]);
      alert("✅ Driver data cleared. Account still active.");
    }
  };

  const resetPassword = async (uid) => {
    // Get user email from Supabase auth
    const { data, error } = await sb.auth.admin?.getUserById?.(uid) || {};
    const email = data?.user?.email || allUsers.find(u => u.id === uid)?.email;
    if (!email) {
      // Fallback: prompt admin to enter email manually
      const manualEmail = window.prompt("Enter the user's email address to send reset link:");
      if (!manualEmail) return;
      const { error: resetErr } = await sb.auth.resetPasswordForEmail(manualEmail, {
        redirectTo: "https://truckpilot.ca"
      });
      if (resetErr) { alert("Error: " + resetErr.message); return; }
      alert("✅ Password reset email sent to " + manualEmail);
      return;
    }
    const { error: resetErr } = await sb.auth.resetPasswordForEmail(email, {
      redirectTo: "https://truckpilot.ca"
    });
    if (resetErr) { alert("Error: " + resetErr.message); return; }
    alert("✅ Password reset email sent to " + email);
  };

  const saveAppSettings = async () => {
    setSavingSettings(true);
    try {
      await sb.from("settings").upsert({ user_id: "__app__", rates: appSettings, routes: [] }, { onConflict: "user_id" });
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 2500);
    } catch(e) { console.error(e); }
    setSavingSettings(false);
  };

  const bulkSetPlan = async (plan) => {
    if (!window.confirm(`Set ALL users to ${plan} plan? This affects everyone.`)) return;
    await sb.from("profiles").update({ plan }).neq("role", "superadmin");
    setAllUsers(prev => prev.map(u => u.role === "superadmin" ? u : { ...u, plan }));
  };

  const owners = allUsers.filter(u => u.role === "owner");
  const drivers = allUsers.filter(u => u.role === "driver");
  const superAdmins = allUsers.filter(u => u.role === "superadmin");
  const unreadMsgs = allMessages.filter(m => !m.read);

  // Filtered users for the Users tab
  const filteredUsers = allUsers.filter(u => {
    const matchSearch = !userSearch || (u.name || "").toLowerCase().includes(userSearch.toLowerCase()) || (u.id || "").toLowerCase().includes(userSearch.toLowerCase()) || (u.username_email || "").toLowerCase().includes(userSearch.toLowerCase()) || (u.username || "").toLowerCase().includes(userSearch.toLowerCase());
    const matchRole = userRoleFilter === "all" || u.role === userRoleFilter;
    const matchPlan = userPlanFilter === "all" || (u.plan || "free") === userPlanFilter;
    return matchSearch && matchRole && matchPlan;
  });

  // Stats for overview
  const totalLoads = allLoads.length;
  const completedLoads = allLoads.filter(l => l.completed).length;
  const todayUsers = allUsers.filter(u => u.created_at?.slice(0,10) === new Date().toISOString().slice(0,10)).length;
  const proUsers = allUsers.filter(u => u.plan === "pro").length;
  const basicUsers = allUsers.filter(u => u.plan === "basic").length;
  const freeUsers = allUsers.filter(u => !u.plan || u.plan === "free").length;

  // Revenue estimate (basic: $9.99, pro: $24.99)
  const estMonthlyRevenue = (basicUsers * 9.99 + proUsers * 24.99).toFixed(2);

  const NAVS = [
    { id:"overview",       icon:"📊", label:"Overview"      },
    { id:"users",          icon:"👥", label:"Users"         },
    { id:"messages",       icon:"🎧", label:"Messages"      },
    { id:"plans",          icon:"💰", label:"Plans"         },
    { id:"analytics",      icon:"📈", label:"Analytics"     },
    { id:"feature_flags",  icon:"🚦", label:"Feature Flags" },
    { id:"settings",       icon:"⚙️", label:"App Settings" },
    { id:"profile_editor", icon:"🎨", label:"Profile Editor"},
    { id:"notifications",  icon:"📣", label:"Notifications" },
    { id:"my_profile",     icon:"👤", label:"My Profile"    },
    { id:"create_admin",   icon:"➕", label:"Create Admin"  },
    { id:"recover_data",   icon:"🗂️", label:"Recover Data"  },
    { id:"delete_data",    icon:"🗑️", label:"Delete Data"   },
    { id:"orphan_cleanup", icon:"🧹", label:"Orphan Cleanup" },
  ];

  const sectionStyle = { padding: "0 16px 40px" };
  const inputStyle = { padding:"8px 12px", borderRadius:8, border:`1px solid ${C.border}`, fontSize:13, width:"100%", boxSizing:"border-box", marginTop:4, fontFamily:"'Barlow',sans-serif" };
  const labelStyle = { fontSize:12, fontWeight:700, color:C.textDarkMed, display:"block", marginBottom:2 };

  return (
    <div className="slt-page" style={{background:"#070B14",color:"#F0F0F0"}}>
      {/* Admin Nav Tabs */}
      <div style={{ background:"#fff", padding:"0 12px", borderBottom:`1px solid ${C.border}`, display:"flex", gap:2, overflowX:"auto", position:"sticky", top:0, zIndex:50 }}>
        {NAVS.map(n => (
          <button key={n.id} onClick={()=>setActiveSection(n.id)}
            style={{ padding:"12px 14px", border:"none", background:"transparent", color:activeSection===n.id?"#E8962E":C.textMed, fontWeight:activeSection===n.id?800:600, fontSize:13, cursor:"pointer", whiteSpace:"nowrap", borderBottom:activeSection===n.id?"3px solid #E8962E":"3px solid transparent", display:"flex", alignItems:"center", gap:5, transition:"all 0.15s" }}>
            {n.icon} {n.label}
            {n.id==="messages" && unreadMsgs.length > 0 && <span style={{ background:"#E53935", color:"#fff", borderRadius:20, padding:"1px 6px", fontSize:12, fontWeight:800 }}>{unreadMsgs.length}</span>}
            {n.id==="users" && <span style={{ background:"#E8962E22", color:"#E8962E", borderRadius:20, padding:"1px 6px", fontSize:12, fontWeight:800 }}>{allUsers.length}</span>}
          </button>
        ))}
        <button onClick={loadData} title="Refresh all data" style={{ marginLeft:"auto", padding:"8px 12px", border:"none", background:"transparent", color:C.textDarkMed, fontSize:14, cursor:"pointer" }}>🔄</button>
      </div>

      {loading && (
        <div className="slt-container">
          <div className="slt-card" style={{ textAlign:"center", padding:48 }}>
            <div style={{ fontSize:40 }}>⏳</div>
            <div style={{ marginTop:12, color:C.textDarkMed, fontWeight:700 }}>Loading admin data...</div>
          </div>
        </div>
      )}

      {/* ─────────────────── OVERVIEW ─────────────────── */}
      {!loading && activeSection === "overview" && (
        <div style={sectionStyle}>
          <div style={{ paddingTop:16 }}>
            {/* Hero banner */}
            <div style={{ background:"linear-gradient(135deg,#E8962E,#E8962E)", borderRadius:16, padding:"20px 20px", marginBottom:16, color:"#fff", textAlign:"center" }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:10, marginBottom:4 }}>
                <div style={{ fontSize:28 }}>👑</div>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:24, color:"#fff" }}>TruckPilot Admin</div>
              </div>
              <div style={{ fontSize:13, opacity:0.8, marginTop:4 }}>Super Admin Dashboard · Full Control</div>
              <div style={{ marginTop:8, fontSize:12, opacity:0.7 }}>Last refreshed: {new Date().toLocaleString()}</div>
            </div>

            {/* Stats grid */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))", gap:10, marginBottom:16 }}>
              {[
                { l:"Total Users", v:allUsers.length, c:"#E8962E", icon:"👤" },
                { l:"Fleet Owners", v:owners.length, c:C.blue, icon:"🚛" },
                { l:"Drivers", v:drivers.length, c:C.teal, icon:"🧑‍✈️" },
                { l:"Pro Users", v:proUsers, c:C.green, icon:"🚀" },
                { l:"Basic Users", v:basicUsers, c:C.orange, icon:"💼" },
                { l:"Beta Users", v:freeUsers, c:"#92400e", icon:"⭐" },
                { l:"Total Loads", v:totalLoads, c:C.blue, icon:"📦" },
                { l:"Completed", v:completedLoads, c:C.green, icon:"✅" },
                { l:"Support Msgs", v:allMessages.length, c:C.orange, icon:"💬" },
                { l:"Unread Msgs", v:unreadMsgs.length, c:C.red, icon:"🔴" },
                { l:"New Today", v:todayUsers, c:"#E8962E", icon:"🆕" },
                { l:"Est. Revenue", v:`$${estMonthlyRevenue}`, c:C.green, icon:"💵" },
              ].map(({l,v,c,icon}) => (
                <div key={l} className="slt-card-sm" style={{ borderTop:`3px solid ${c}`, textAlign:"center", padding:"12px 8px" }}>
                  <div style={{ fontSize:18, marginBottom:2 }}>{icon}</div>
                  <div style={{ fontSize:12, color:C.textDarkMut, fontWeight:700, marginBottom:3 }}>{l.toUpperCase()}</div>
                  <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:22, fontWeight:900, color:c }}>{v}</div>
                </div>
              ))}
            </div>

            {/* Plan distribution bar */}
            <div className="slt-card" style={{ marginBottom:16 }}>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:14, marginBottom:12 }}>Plan Distribution</div>
              {allUsers.length > 0 && (
                <div>
                  <div style={{ display:"flex", borderRadius:8, overflow:"hidden", height:20, marginBottom:10 }}>
                    <div style={{ width:`${(proUsers/allUsers.length)*100}%`, background:C.green }} title={`Pro: ${proUsers}`} />
                    <div style={{ width:`${(basicUsers/allUsers.length)*100}%`, background:C.orange }} title={`Basic: ${basicUsers}`} />
                    <div style={{ width:`${(freeUsers/allUsers.length)*100}%`, background:"#ccc" }} title={`Free: ${freeUsers}`} />
                  </div>
                  <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
                    {[["Pro", proUsers, C.green],["Basic", basicUsers, C.orange],["Free", freeUsers, "#888"]].map(([l,v,c]) => (
                      <div key={l} style={{ display:"flex", alignItems:"center", gap:5, fontSize:12 }}>
                        <div style={{ width:10, height:10, borderRadius:2, background:c }} />
                        <span style={{ fontWeight:700, color:c }}>{l}</span>
                        <span style={{ color:C.textDarkMut }}>({v} · {allUsers.length ? Math.round(v/allUsers.length*100) : 0}%)</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Recent sign-ups */}
            <div className="slt-card" style={{ marginBottom:16 }}>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:14, marginBottom:10 }}>🆕 Recent Sign-ups</div>
              {allUsers.slice(0,8).map(u => (
                <div key={u.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:`1px solid ${C.border}` }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <div style={{ width:32, height:32, borderRadius:"50%", background: u.role==="superadmin"?"#E8962E":u.role==="owner"?C.blue:C.teal, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:800, fontSize:13 }}>
                      {(u.name||"?")[0].toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight:700, fontSize:13 }}>{u.name || "Unknown"}</div>
                      <div style={{ fontSize:13, color:C.textDarkMut }}>{u.role} · <span style={{ color: u.plan==="pro"?"#166534":u.plan==="basic"?"#E8962E":"#92400e", fontWeight:700 }}>{u.plan==="pro"?"🚀 Pro":u.plan==="basic"?"💼 Basic":"⭐ Beta"}</span></div>
                    </div>
                  </div>
                  <div style={{ fontSize:13, color:C.textDarkMut }}>{u.created_at?.slice(0,10)}</div>
                </div>
              ))}
              {allUsers.length > 8 && <div style={{ textAlign:"center", padding:"10px 0", fontSize:12, color:C.textDarkMut }}>+{allUsers.length-8} more · go to Users tab</div>}
            </div>

            {/* Recent messages */}
            {unreadMsgs.length > 0 && (
              <div className="slt-card" style={{ border:`1.5px solid ${C.red}` }}>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:14, marginBottom:10, color:C.red }}>🔴 {unreadMsgs.length} Unread Support Messages</div>
                {unreadMsgs.slice(0,3).map(m => (
                  <div key={m.id} style={{ padding:"8px 0", borderBottom:`1px solid ${C.border}` }}>
                    <div style={{ fontWeight:700, fontSize:13 }}>{m.from_name}</div>
                    <div style={{ fontSize:12, color:C.textDarkMed }}>{chatParse(m)?.msgs?.slice(-1)[0]?.text?.slice(0,80) || "Photo"}</div>
                    <div style={{ fontSize:13, color:C.textDarkMut }}>{new Date(m.created_at).toLocaleString()}</div>
                  </div>
                ))}
                <button onClick={()=>setActiveSection("messages")} className="slt-btn-primary slt-btn-dark-text" style={{ width:"100%", marginTop:10 }}>📬 Go to Messages</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─────────────────── USERS ─────────────────── */}
      {!loading && activeSection === "users" && (
        <div style={sectionStyle}>
          <div style={{ paddingTop:16 }}>

            {/* Broadcast Email */}
            <div className="slt-card" style={{ marginBottom:12, borderLeft:'4px solid #E8962E' }}>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:16, marginBottom:4, color:C.blue }}>📧 Email Center</div>
              <div style={{ fontSize:12, color:C.textDarkMut, marginBottom:14 }}>Send emails to all users, a group, or a specific user</div>

              {/* Recipient selector */}
              <div style={{ marginBottom:10 }}>
                <label style={labelStyle}>Send To</label>
                <select id="broadcast-recipients" style={inputStyle}>
                  <option value="all">👥 All Users ({allUsers.filter(u=>u.username_email).length} with email)</option>
                  <option value="owners">🔑 Owners Only</option>
                  <option value="drivers">🚛 Drivers Only</option>
                  <option value="free">⭐ Beta Users Only</option>
                  <option value="specific">✉️ Specific Email Address</option>
                </select>
              </div>
              <div id="broadcast-specific-email-wrapper" style={{ marginBottom:10, display:"none" }}>
                <label style={labelStyle}>Email Address</label>
                <input id="broadcast-specific-email" style={inputStyle} placeholder="user@example.com" />
              </div>
              <div style={{ marginBottom:8 }}>
                <label style={labelStyle}>Subject</label>
                <input id="broadcast-subject" style={inputStyle} placeholder="e.g. Important update from TruckPilot" />
              </div>
              <div style={{ marginBottom:10 }}>
                <label style={labelStyle}>Message <span style={{fontWeight:500,color:C.textDarkMut}}>(use {"{name}"} to personalize)</span></label>
                <textarea id="broadcast-body" style={{ ...inputStyle, minHeight:100, resize:"vertical" }} placeholder="Write your message here..." />
              </div>
              {/* Welcome Email Blast */}
              <div style={{ marginBottom:12, padding:"14px 16px", background:"linear-gradient(135deg,#E8962E,#E8962E)", borderRadius:12 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                  <div>
                    <div style={{ fontWeight:900, fontSize:13, color:"#FFD700", letterSpacing:0.5 }}>🎉 Send Welcome Email</div>
                    <div style={{ fontSize:12, color:"rgba(255,255,255,0.6)", marginTop:2 }}>Sends the full branded welcome email to every registered user using the template stored in your Edge Function.</div>
                  </div>
                </div>
                <div style={{ display:"flex", gap:8, marginTop:10 }}>
                  <button style={{ flex:1, padding:"10px", borderRadius:10, border:"none", background:"#FFD700", color:"#E8962E", fontWeight:900, fontSize:13, cursor:"pointer" }}
                    onClick={async () => {
                      const targets = allUsers.filter(u => u.username_email);
                      if (!targets.length) return alert("No users with email addresses found.");
                      if (!window.confirm(`Send the welcome email to all ${targets.length} users?\n\nSubject: "You're In! Welcome to TruckPilot ✈️"\nSender: TruckPilot <support@truckpilot.ca>`)) return;
                      let sent = 0, failed = 0, skipped = 0;
                      for (const u of targets) {
                        try {
                          const res = await fetch(`${SUPABASE_URL}/functions/v1/resend-email`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${SUPABASE_ANON_KEY}` },
                            body: JSON.stringify({ to: u.username_email, name: u.name || "there" }),
                          });
                          if (res.ok) sent++; else { failed++; console.warn("Failed for", u.username_email, await res.text()); }
                        } catch (e) { failed++; console.warn("Error for", u.username_email, e); }
                        // Small delay to avoid Resend rate limits
                        await new Promise(r => setTimeout(r, 120));
                      }
                      alert(`✅ Welcome email blast complete!\n\nSent: ${sent}\nFailed: ${failed}\nTotal: ${targets.length}`);
                    }}>
                    🚀 Send to All {allUsers.filter(u=>u.username_email).length} Users
                  </button>
                  <button style={{ flex:1, padding:"10px", borderRadius:10, border:"1px solid rgba(255,255,255,0.3)", background:"transparent", color:"rgba(255,255,255,0.85)", fontWeight:800, fontSize:13, cursor:"pointer" }}
                    onClick={async () => {
                      const email = window.prompt("Enter a single email to test the welcome email:");
                      if (!email) return;
                      const name = window.prompt("First name for this test:", "there") || "there";
                      try {
                        const res = await fetch(`${SUPABASE_URL}/functions/v1/resend-email`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${SUPABASE_ANON_KEY}` },
                          body: JSON.stringify({ to: email.trim(), name }),
                        });
                        const data = await res.json();
                        alert(res.ok ? `✅ Test welcome email sent to ${email}` : `❌ Failed: ${JSON.stringify(data)}`);
                      } catch(e) { alert("Error: " + e.message); }
                    }}>
                    🧪 Test Send
                  </button>
                </div>
              </div>

              <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:12, marginBottom:10 }}>
                <div style={{ fontSize:12, fontWeight:700, color:C.textDarkMed, marginBottom:10, textTransform:"uppercase", letterSpacing:0.5 }}>📝 Custom Broadcast</div>
              </div>

              <button className="slt-btn-primary slt-btn-dark-text" style={{ width:"100%" }} onClick={async () => {
                const recipients = document.getElementById("broadcast-recipients").value;
                const subject = document.getElementById("broadcast-subject").value.trim();
                const body = document.getElementById("broadcast-body").value.trim();
                const specificEmail = document.getElementById("broadcast-specific-email").value.trim();
                if (!subject || !body) return alert("Please fill in subject and message.");

                let targetUsers = [];
                if (recipients === "all") targetUsers = allUsers.filter(u => u.username_email);
                else if (recipients === "owners") targetUsers = allUsers.filter(u => u.role === "owner" && u.username_email);
                else if (recipients === "drivers") targetUsers = allUsers.filter(u => u.role === "driver" && u.username_email);
                else if (recipients === "free") targetUsers = allUsers.filter(u => (!u.plan || u.plan === "free") && u.username_email);
                else if (recipients === "specific") {
                  if (!specificEmail) return alert("Please enter an email address.");
                  targetUsers = [{ username_email: specificEmail, name: "there" }];
                }

                if (targetUsers.length === 0) return alert("No users found for this selection.");
                if (!window.confirm(`Send to ${targetUsers.length} recipient${targetUsers.length !== 1 ? "s" : ""}?`)) return;

                let sent = 0; let failed = 0;
                for (const u of targetUsers) {
                  try {
                    const personalizedBody = body.replace(/\{name\}/g, u.name||"there");
                    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"></head><body style="margin:0;padding:0;background:#f4f4f4;font-family:Arial,sans-serif;"><table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f4;padding:30px 0;"><tr><td align="center"><table width="600" cellpadding="0" cellspacing="0" style="background:#fff;border-radius:12px;overflow:hidden;max-width:600px;width:100%;"><tr><td style="background:linear-gradient(135deg,#E8962E,#E8962E);padding:28px 40px;text-align:center;"><div style="font-weight:900;font-size:32px;letter-spacing:6px;"><span style="color:#fff;text-decoration:underline;text-decoration-color:#FFD700;">TRUCK</span><span style="color:#FFD700;">PILOT</span></div><div style="color:rgba(255,255,255,0.6);font-size:11px;letter-spacing:2px;margin-top:6px;">LOG LOADS &middot; SAVE TAXES &middot; STAY COMPLIANT</div></td></tr><tr><td style="background:#FFD700;padding:14px 40px;text-align:center;"><div style="font-size:13px;font-weight:800;color:#E8962E;margin-bottom:6px;">See everything TruckPilot can do for your business</div><a href="https://www.truckpilot.ca" style="display:inline-block;background:#E8962E;color:#FFD700;text-decoration:none;font-size:13px;font-weight:900;padding:8px 24px;border-radius:20px;">Visit www.truckpilot.ca</a></td></tr><tr><td style="padding:32px 40px;"><p style="color:#333;font-size:15px;line-height:1.7;margin:0 0 16px;">Hi <strong>${u.name||"there"}</strong>,</p><p style="color:#333;font-size:15px;line-height:1.7;white-space:pre-wrap;">${personalizedBody}</p><p style="font-weight:800;font-size:14px;color:#E8962E;margin:24px 0 0;">&mdash; TruckPilot Support Team</p></td></tr><tr><td style="padding:0 40px 32px;text-align:center;"><a href="https://app.truckpilot.ca" style="background:linear-gradient(135deg,#E8962E,#E8962E);color:#FFD700;text-decoration:none;font-size:15px;font-weight:900;padding:14px 40px;border-radius:30px;display:inline-block;">Open TruckPilot Now &rarr;</a></td></tr><tr><td style="background:#E8962E;padding:20px 40px;text-align:center;"><div style="font-weight:900;font-size:18px;letter-spacing:4px;margin-bottom:6px;"><span style="color:#fff;">TRUCK</span><span style="color:#FFD700;">PILOT</span></div><div style="font-size:12px;color:rgba(255,255,255,0.5);margin-bottom:4px;"><a href="https://www.truckpilot.ca" style="color:rgba(255,255,255,0.5);text-decoration:none;">www.truckpilot.ca</a> &middot; <a href="mailto:support@truckpilot.ca" style="color:rgba(255,255,255,0.5);text-decoration:none;">support@truckpilot.ca</a></div><div style="color:rgba(255,255,255,0.3);font-size:11px;">&copy; 2026 TruckPilot &middot; Alberta, Canada</div></td></tr></table></td></tr></table></body></html>`;
                    const res = await fetch(`${SUPABASE_URL}/functions/v1/resend-email`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ to: u.username_email, name: u.name||"there", subject, html }),
                    });
                    if (res.ok) sent++; else failed++;
                  } catch { failed++; }
                }
                alert(`Done! Sent: ${sent} · Failed: ${failed}`);
              }}>📤 Send Email</button>
            </div>

            {/* Filters */}
            <div className="slt-card" style={{ marginBottom:12 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:14 }}>🔍 Search & Filter — {filteredUsers.length} of {allUsers.length} users</div>
                <button onClick={exportUsersCSV} style={{ padding:"7px 14px", background:"#166534", color:"#fff", border:"none", borderRadius:8, fontWeight:800, fontSize:12, cursor:"pointer" }}>⬇️ Export CSV</button>
              </div>
              <input
                type="text" placeholder="Search by name or ID..."
                value={userSearch} onChange={e=>setUserSearch(e.target.value)}
                style={{ ...inputStyle, marginBottom:10 }}
              />
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                <div style={{ flex:1, minWidth:120 }}>
                  <label style={labelStyle}>Role</label>
                  <select value={userRoleFilter} onChange={e=>setUserRoleFilter(e.target.value)} style={inputStyle}>
                    <option value="all">All Roles</option>
                    <option value="owner">Owner</option>
                    <option value="driver">Driver</option>
                    <option value="superadmin">Super Admin</option>
                  </select>
                </div>
                <div style={{ flex:1, minWidth:120 }}>
                  <label style={labelStyle}>Plan</label>
                  <select value={userPlanFilter} onChange={e=>setUserPlanFilter(e.target.value)} style={inputStyle}>
                    <option value="all">All Plans</option>
                    <option value="free">⭐ Beta</option>
                    <option value="basic">💼 Basic</option>
                    <option value="pro">Pro</option>
                  </select>
                </div>
                <div style={{ flex:1, minWidth:120, display:"flex", alignItems:"flex-end" }}>
                  <button onClick={()=>{setUserSearch("");setUserRoleFilter("all");setUserPlanFilter("all");}} style={{ ...inputStyle, background:"#F5F6F8", cursor:"pointer", textAlign:"center", border:`1px solid ${C.border}` }}>Clear</button>
                </div>
              </div>
            </div>

            {filteredUsers.length === 0 && (
              <div className="slt-card" style={{ textAlign:"center", padding:32, color:C.textDarkMut }}>No users match your filter.</div>
            )}

            {filteredUsers.map(u => {
              const isExpanded = expandedUser === u.id;
              const userLoads = allLoads.filter(l => l.user_id === u.id);
              const roleColor = u.role==="superadmin"?"#E8962E":u.role==="owner"?C.blue:C.teal;
              const planColor = u.plan==="pro"?"#166534":u.plan==="basic"?"#E8962E":"#92400e";
              return (
                <div key={u.id} className="slt-card" style={{ marginBottom:10, borderLeft:`4px solid ${roleColor}`, padding:0, overflow:"hidden" }}>
                  {/* User header row */}
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 14px", cursor:"pointer" }}
                    onClick={()=>setExpandedUser(isExpanded?null:u.id)}>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <div style={{ width:36, height:36, borderRadius:"50%", background:roleColor, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:900, fontSize:15, flexShrink:0 }}>
                        {(u.name||"?")[0].toUpperCase()}
                      </div>
                      <div>
                        <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                          <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:14 }}>{u.name || "Unknown"}</span>
                          <span style={{ background:roleColor, color:"#fff", borderRadius:20, padding:"1px 7px", fontSize:12, fontWeight:800 }}>{u.role}</span>
                          <span style={{ background:planColor, color:"#fff", borderRadius:20, padding:"1px 7px", fontSize:12, fontWeight:800 }}>{u.plan==="pro"?"🚀 Pro":u.plan==="basic"?"💼 Basic":"⭐ Beta"}</span>
                        </div>
                        <div style={{ fontSize:13, color:C.textDarkMut, marginTop:2 }}>
                          {u.email || u.id?.slice(0,16)+"..."} 
                        </div>
                        <div style={{ fontSize:13, color:C.textDarkMut, marginTop:1 }}>
                          {userLoads.length} loads · Joined {u.created_at?.slice(0,10)||"unknown"}
                        </div>
                      </div>
                    </div>
                    <div style={{ fontSize:16, color:C.textDarkMut }}>{isExpanded?"▲":"▼"}</div>
                  </div>

                  {/* Expanded controls */}
                  {isExpanded && (
                    <div style={{ padding:"14px", borderTop:`1px solid ${C.border}`, background:C.offWhite }}>
                      <div style={{ fontSize:13, color:C.textDarkMut, marginBottom:12, wordBreak:"break-all" }}>
                        <strong>ID:</strong> {u.id}<br/>
                        <strong>Email:</strong> {u.username_email || u.email || "—"}<br/>
                        <strong>Username:</strong> {u.username || "—"}<br/>
                        <strong>Invite Code:</strong> {u.invite_code || "—"}<br/>

                        <strong>Loads:</strong> {userLoads.length} total · {userLoads.filter(l=>l.completed).length} completed<br/>
                        <strong>Last Seen:</strong> {u.last_seen ? new Date(u.last_seen).toLocaleString() : "Never recorded"}
                      </div>

                      {/* Editable name */}
                      <div style={{ marginBottom:12 }}>
                        <label style={labelStyle}>Display Name</label>
                        <div style={{ display:"flex", gap:8 }}>
                          <input id={`name-${u.id}`} defaultValue={u.name} style={{ ...inputStyle, flex:1 }} />
                          <button onClick={()=>{ const v=document.getElementById(`name-${u.id}`).value; if(v) updateUserName(u.id,v); }}
                            className="slt-btn-primary slt-btn-dark-text" style={{ whiteSpace:"nowrap", padding:"8px 14px" }}>Save</button>
                        </div>
                      </div>

                      <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:12 }}>
                        <div style={{ flex:1, minWidth:130 }}>
                          <label style={labelStyle}>Plan</label>
                          <select value={u.plan||"free"} onChange={e=>updateUserPlan(u.id,e.target.value)} style={inputStyle}>
                            <option value="free">⭐ Beta</option>
                            <option value="basic">💼 Basic</option>
                            <option value="pro">🚀 Pro</option>
                          </select>
                        </div>
                        <div style={{ flex:1, minWidth:130 }}>
                          <label style={labelStyle}>Role</label>
                          <select value={u.role||"owner"} onChange={e=>updateUserRole(u.id,e.target.value)} style={inputStyle}>
                            <option value="owner">🚛 Owner</option>
                            <option value="driver">🧑‍✈️ Driver</option>
                            <option value="superadmin">🛡 Super Admin</option>
                          </select>
                        </div>
                      </div>

                      {/* Feature Overrides */}
                      <div style={{ marginBottom:12 }}>
                        <label style={{ ...labelStyle, marginBottom:8 }}>🔧 Feature Overrides (grant access regardless of plan)</label>
                        <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                          {FEATURE_LIST.map(f => {
                            const overrides = u.feature_overrides || {};
                            const enabled = overrides[f.id] === true;
                            return (
                              <div key={f.id} onClick={()=>toggleFeatureOverride(u.id, f.id, overrides)}
                                style={{ padding:"5px 12px", borderRadius:20, fontSize:12, fontWeight:700, cursor:"pointer",
                                  background: enabled?"#E8962E":"#EEEEEE", color: enabled?"#fff":"#666",
                                  border:`1.5px solid ${enabled?"#E8962E":"#ddd"}`, transition:"all 0.15s" }}>
                                {f.label} {enabled?"✓":""}
                              </div>
                            );
                          })}
                        </div>
                        <div style={{ fontSize:13, color:C.textDarkMut, marginTop:6 }}>Tap a feature to toggle access for this user only</div>
                      </div>

                      {/* Admin Notes */}
                      <div style={{ marginBottom:12 }}>
                        <label style={{ ...labelStyle, marginBottom:8 }}>📝 Admin Notes (private, only you see these)</label>
                        {!(adminNotes[u.id]) && (
                          <button onClick={()=>loadAdminNotes(u.id)} style={{ padding:"6px 14px", background:"#F5F6F8", border:"1px solid #E8962E", borderRadius:8, fontWeight:700, fontSize:12, cursor:"pointer", color:"#E8962E", marginBottom:8 }}>
                            Load Notes
                          </button>
                        )}
                        {adminNotes[u.id] && (
                          <div>
                            {adminNotes[u.id].length === 0 && <div style={{ fontSize:12, color:C.textDarkMut, marginBottom:8 }}>No notes yet.</div>}
                            {adminNotes[u.id].map(n => (
                              <div key={n.id} style={{ background:"#fff", borderRadius:8, padding:"8px 10px", marginBottom:6, border:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                                <div>
                                  <div style={{ fontSize:13, color:"#1a1a1a" }}>{n.note}</div>
                                  <div style={{ fontSize:13, color:C.textDarkMut, marginTop:2 }}>{new Date(n.created_at).toLocaleString()}</div>
                                </div>
                                <button onClick={()=>deleteAdminNote(n.id, u.id)} style={{ background:"none", border:"none", color:"#EF4444", cursor:"pointer", fontSize:16, padding:"0 4px" }}>✕</button>
                              </div>
                            ))}
                            <div style={{ display:"flex", gap:8, marginTop:6 }}>
                              <input value={newNote[u.id]||""} onChange={e=>setNewNote(p=>({...p,[u.id]:e.target.value}))}
                                placeholder="Add a private note..." style={{ ...inputStyle, flex:1, marginTop:0 }}
                                onKeyDown={e=>{ if(e.key==="Enter") saveAdminNote(u.id); }} />
                              <button onClick={()=>saveAdminNote(u.id)} disabled={savingNote[u.id]}
                                className="slt-btn-primary slt-btn-dark-text" style={{ padding:"8px 14px", whiteSpace:"nowrap" }}>
                                {savingNote[u.id]?"...":"+ Add"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      <div style={{ display:"flex", gap:8, justifyContent:"flex-end", flexWrap:"wrap" }}>
                        <button onClick={()=>resetPassword(u.id)}
                          style={{ padding:"8px 16px", borderRadius:8, border:`1.5px solid #E8962E`, background:"#fff", color:C.blue, fontWeight:800, fontSize:12, cursor:"pointer" }}>
                          🔑 Reset Password
                        </button>
                        {u.username_email && (
                          <button onClick={()=>{
                            const subject = window.prompt("Email subject:", "A message from TruckPilot");
                            if (!subject) return;
                            const body = window.prompt("Message (use {name} for their name):", "");
                            if (!body) return;
                            const personalizedBody = body.replace(/\{name\}/g, u.name||"there");
                            const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"></head><body style="margin:0;padding:0;background:#f4f4f4;font-family:Arial,sans-serif;"><table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f4;padding:30px 0;"><tr><td align="center"><table width="600" cellpadding="0" cellspacing="0" style="background:#fff;border-radius:12px;overflow:hidden;max-width:600px;width:100%;"><tr><td style="background:linear-gradient(135deg,#E8962E,#E8962E);padding:28px 40px;text-align:center;"><div style="font-weight:900;font-size:32px;letter-spacing:6px;"><span style="color:#fff;">TRUCK</span><span style="color:#FFD700;">PILOT</span></div></td></tr><tr><td style="padding:32px 40px;"><p style="color:#333;font-size:15px;line-height:1.7;margin:0 0 16px;">Hi <strong>${u.name||"there"}</strong>,</p><p style="color:#333;font-size:15px;line-height:1.7;white-space:pre-wrap;">${personalizedBody}</p><p style="font-weight:800;font-size:14px;color:#E8962E;margin:24px 0 0;">&mdash; TruckPilot Support Team</p></td></tr><tr><td style="background:#E8962E;padding:20px 40px;text-align:center;"><span style="color:#fff;font-weight:900;">TRUCK</span><span style="color:#FFD700;font-weight:900;">PILOT</span><div style="color:rgba(255,255,255,0.4);font-size:11px;margin-top:4px;">&copy; 2026 TruckPilot</div></td></tr></table></td></tr></table></body></html>`;
                            fetch(`${SUPABASE_URL}/functions/v1/resend-email`, {
                              method:"POST", headers:{"Content-Type":"application/json"},
                              body: JSON.stringify({ to: u.username_email, name: u.name||"there", subject, html })
                            }).then(r=>r.json()).then(d=>alert(d.id ? "✅ Email sent!" : "❌ Failed: " + JSON.stringify(d)));
                          }}
                          style={{ padding:"8px 16px", borderRadius:8, border:`1.5px solid ${C.teal}`, background:"#fff", color:'#E8962E', fontWeight:800, fontSize:12, cursor:"pointer" }}>
                          ✉️ Send Email
                        </button>
                        )}
                        <button onClick={()=>clearUserData(u.id)}
                          style={{ padding:"8px 16px", borderRadius:8, border:`1.5px solid ${C.orange}`, background:"#fff", color:C.orange, fontWeight:800, fontSize:12, cursor:"pointer" }}>
                          🧹 Clear Data
                        </button>
                        <button onClick={()=>deleteUser(u.id)} className="slt-btn-danger" style={{ padding:"8px 16px", fontSize:12 }}>
                          🗑 Delete User
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ─────────────────── MESSAGES ─────────────────── */}
      {!loading && activeSection === "messages" && (
        <div style={{ padding:"0 16px 40px" }}>
          <div style={{ paddingTop:16, marginBottom:12 }}>
            <input value={msgSearch} onChange={e=>setMsgSearch(e.target.value)}
              placeholder="🔍 Search messages by name or content..."
              style={{ ...inputStyle, marginTop:0, background:"#fff" }} />
          </div>
          <SupportInboxTab session={session} embedded={true} searchFilter={msgSearch} />
        </div>
      )}

      {/* ─────────────────── ANALYTICS ─────────────────── */}
      {!loading && activeSection === "analytics" && (
        <div style={sectionStyle}>
          <div style={{ paddingTop:16 }}>
            <div style={{ background:"linear-gradient(135deg,#1B5E20,#2E7D32)", borderRadius:16, padding:"18px 20px", color:"#fff", marginBottom:16 }}>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:22, marginBottom:4 }}>📈 App Analytics</div>
              <div style={{ fontSize:13, opacity:0.8 }}>Growth, activity and revenue trends</div>
            </div>

            {/* User growth by month */}
            <div className="slt-card" style={{ marginBottom:14 }}>
              <div style={{ fontWeight:800, fontSize:14, marginBottom:14, color:"#E8962E" }}>👤 User Signups by Month</div>
              {(() => {
                const months = {};
                allUsers.forEach(u => {
                  const m = u.created_at?.slice(0,7);
                  if (m) months[m] = (months[m]||0) + 1;
                });
                const sorted = Object.entries(months).sort((a,b)=>a[0].localeCompare(b[0])).slice(-6);
                const max = Math.max(...sorted.map(([,v])=>v), 1);
                return sorted.length === 0 ? <div style={{ color:C.textDarkMut, fontSize:13 }}>No data yet</div> : (
                  <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                    {sorted.map(([month, count]) => (
                      <div key={month} style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <div style={{ fontSize:12, fontWeight:700, color:C.textDarkMed, width:60, flexShrink:0 }}>{month}</div>
                        <div style={{ flex:1, background:"#EEEEEE", borderRadius:6, height:24, overflow:"hidden" }}>
                          <div style={{ width:`${(count/max)*100}%`, background:"#E8962E", height:"100%", borderRadius:6, display:"flex", alignItems:"center", paddingLeft:8, minWidth:30 }}>
                            <span style={{ fontSize:13, fontWeight:800, color:"#fff" }}>{count}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Loads by month */}
            <div className="slt-card" style={{ marginBottom:14 }}>
              <div style={{ fontWeight:800, fontSize:14, marginBottom:14, color:"#E8962E" }}>📦 Loads Logged by Month</div>
              {(() => {
                const months = {};
                allLoads.forEach(l => {
                  const m = l.created_at?.slice(0,7);
                  if (m) months[m] = (months[m]||0) + 1;
                });
                const sorted = Object.entries(months).sort((a,b)=>a[0].localeCompare(b[0])).slice(-6);
                const max = Math.max(...sorted.map(([,v])=>v), 1);
                return sorted.length === 0 ? <div style={{ color:C.textDarkMut, fontSize:13 }}>No data yet</div> : (
                  <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                    {sorted.map(([month, count]) => (
                      <div key={month} style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <div style={{ fontSize:12, fontWeight:700, color:C.textDarkMed, width:60, flexShrink:0 }}>{month}</div>
                        <div style={{ flex:1, background:"#EEEEEE", borderRadius:6, height:24, overflow:"hidden" }}>
                          <div style={{ width:`${(count/max)*100}%`, background:"#166534", height:"100%", borderRadius:6, display:"flex", alignItems:"center", paddingLeft:8, minWidth:30 }}>
                            <span style={{ fontSize:13, fontWeight:800, color:"#fff" }}>{count}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Revenue estimate by month */}
            <div className="slt-card" style={{ marginBottom:14 }}>
              <div style={{ fontWeight:800, fontSize:14, marginBottom:14, color:"#E8962E" }}>💵 Est. Revenue by Month</div>
              {(() => {
                const months = {};
                allUsers.forEach(u => {
                  const m = u.created_at?.slice(0,7);
                  if (!m) return;
                  const rev = u.plan==="pro" ? 24.99 : u.plan==="basic" ? 9.99 : 0;
                  months[m] = (months[m]||0) + rev;
                });
                const sorted = Object.entries(months).sort((a,b)=>a[0].localeCompare(b[0])).slice(-6);
                const max = Math.max(...sorted.map(([,v])=>v), 1);
                return sorted.length === 0 ? <div style={{ color:C.textDarkMut, fontSize:13 }}>No paid users yet</div> : (
                  <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                    {sorted.map(([month, rev]) => (
                      <div key={month} style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <div style={{ fontSize:12, fontWeight:700, color:C.textDarkMed, width:60, flexShrink:0 }}>{month}</div>
                        <div style={{ flex:1, background:"#EEEEEE", borderRadius:6, height:24, overflow:"hidden" }}>
                          <div style={{ width:`${(rev/max)*100}%`, background:"#B45309", height:"100%", borderRadius:6, display:"flex", alignItems:"center", paddingLeft:8, minWidth:40 }}>
                            <span style={{ fontSize:13, fontWeight:800, color:"#fff" }}>${rev.toFixed(0)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Top active users */}
            <div className="slt-card" style={{ marginBottom:14 }}>
              <div style={{ fontWeight:800, fontSize:14, marginBottom:14, color:"#E8962E" }}>🏆 Most Active Users (by loads)</div>
              {allUsers
                .map(u => ({ ...u, loadCount: allLoads.filter(l=>l.user_id===u.id).length }))
                .sort((a,b)=>b.loadCount-a.loadCount)
                .slice(0,10)
                .map((u,i) => (
                  <div key={u.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:`1px solid ${C.border}` }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:16, color:i===0?"#FFD700":i===1?"#aaa":i===2?"#cd7f32":C.textLight, width:20 }}>#{i+1}</span>
                      <div>
                        <div style={{ fontWeight:700, fontSize:13 }}>{u.name||"Unknown"}</div>
                        <div style={{ fontSize:13, color:C.textDarkMut }}>{u.role} · {u.plan||"beta"}</div>
                      </div>
                    </div>
                    <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:18, color:"#E8962E" }}>{u.loadCount}</div>
                  </div>
                ))}
            </div>

            {/* Last seen summary */}
            <div className="slt-card" style={{ marginBottom:14 }}>
              <div style={{ fontWeight:800, fontSize:14, marginBottom:14, color:"#E8962E" }}>👁 User Activity</div>
              {[
                { label:"Active today", count: allUsers.filter(u=>u.last_seen&&new Date(u.last_seen)>new Date(Date.now()-86400000)).length, color:"#166534" },
                { label:"Active this week", count: allUsers.filter(u=>u.last_seen&&new Date(u.last_seen)>new Date(Date.now()-7*86400000)).length, color:"#E8962E" },
                { label:"Active this month", count: allUsers.filter(u=>u.last_seen&&new Date(u.last_seen)>new Date(Date.now()-30*86400000)).length, color:"#B45309" },
                { label:"Never seen", count: allUsers.filter(u=>!u.last_seen).length, color:"#4B5563" },
              ].map(({label,count,color})=>(
                <div key={label} style={{ display:"flex", justifyContent:"space-between", padding:"9px 0", borderBottom:`1px solid ${C.border}`, fontSize:13 }}>
                  <span style={{ color:C.textDarkMed, fontWeight:600 }}>{label}</span>
                  <span style={{ fontWeight:900, color }}>{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────── PLANS ─────────────────── */}
      {!loading && activeSection === "plans" && (
        <div style={sectionStyle}>
          <div style={{ paddingTop:16 }}>
            {/* Revenue summary */}
            <div style={{ background:"linear-gradient(135deg,#1B5E20,#2E7D32)", borderRadius:14, padding:"18px 20px", color:"#fff", marginBottom:16 }}>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:18, marginBottom:4 }}>💵 Est. Monthly Revenue</div>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:36 }}>${estMonthlyRevenue}</div>
              <div style={{ fontSize:12, opacity:0.8, marginTop:4 }}>{basicUsers} Basic × $9.99 + {proUsers} Pro × $24.99</div>
            </div>

            {/* Bulk actions */}
            <div className="slt-card" style={{ marginBottom:16, background:"#F5F6F8", border:`1.5px solid ${C.orange}` }}>
              <div style={{ fontWeight:800, color:C.orange, marginBottom:10 }}>⚡ Bulk Actions</div>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                {["free","basic","pro"].map(plan => (
                  <button key={plan} onClick={()=>bulkSetPlan(plan)} style={{ padding:"8px 16px", borderRadius:8, border:`1.5px solid ${plan==="pro"?"#166534":plan==="basic"?"#E8962E":"#92400e"}`, background:"#fff", color:plan==="pro"?"#166534":plan==="basic"?"#E8962E":"#92400e", fontWeight:800, fontSize:12, cursor:"pointer" }}>
                    {plan==="pro"?"🚀 Set All → Pro":plan==="basic"?"💼 Set All → Basic":"⭐ Set All → Beta"}
                  </button>
                ))}
              </div>
              <div style={{ fontSize:13, color:C.textDarkMut, marginTop:8 }}>⚠️ These actions affect all non-admin users</div>
            </div>

            {/* Users per plan */}
            {[
              { plan:"pro",   label:"🚀 Pro Plan",   color:C.green,  price:"$24.99/mo", users: allUsers.filter(u=>u.plan==="pro") },
              { plan:"basic", label:"💼 Hauler",  color:"#E8962E", price:"$9.99/mo",  users: allUsers.filter(u=>u.plan==="basic") },
              { plan:"free",  label:"⭐ Beta",   color:"#92400e",   price:"Free",       users: allUsers.filter(u=>!u.plan||u.plan==="free") },
            ].map(({ plan, label, color, price, users: planUsers }) => (
              <div key={plan} className="slt-card" style={{ marginBottom:14, borderTop:`4px solid ${color}` }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                  <div>
                    <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:15, color }}>{label}</div>
                    <div style={{ fontSize:12, color:C.textDarkMut }}>{price}</div>
                  </div>
                  <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:28, color }}>{planUsers.length}</div>
                </div>
                {planUsers.length === 0 && <div style={{ fontSize:12, color:C.textDarkMut, padding:"8px 0" }}>No users on this plan.</div>}
                {planUsers.map(u => (
                  <div key={u.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:`1px solid ${C.border}` }}>
                    <div>
                      <div style={{ fontWeight:700, fontSize:13 }}>{u.name || "Unknown"}</div>
                      <div style={{ fontSize:13, color:C.textDarkMut }}>{u.role} · {u.created_at?.slice(0,10)}</div>
                    </div>
                    <select value={u.plan||"free"} onChange={e=>updateUserPlan(u.id,e.target.value)}
                      style={{ padding:"5px 8px", borderRadius:8, border:`1px solid ${C.border}`, fontSize:12, cursor:"pointer" }}>
                      <option value="free">⭐ Beta</option>
                      <option value="basic">💼 Basic</option>
                      <option value="pro">Pro</option>
                    </select>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─────────────────── NOTIFICATIONS ─────────────────── */}
      {!loading && activeSection === "notifications" && (
        <div style={sectionStyle}>
          <div style={{ paddingTop:16 }}>
          {notifLoading === false && notifications.length === 0 && !notifSending && (() => { loadNotifications(); return null; })()}

            {/* Header */}
            <div style={{ background:"linear-gradient(135deg,#166534,#15803D)", borderRadius:16, padding:"18px 20px", marginBottom:16, color:"#fff" }}>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:22, marginBottom:4 }}>📣 Send Notification</div>
              <div style={{ fontSize:13, opacity:0.8 }}>Send banners that appear at the top of users' app. Supports targeting all users, owners, drivers, or a specific person.</div>
            </div>

            {/* Compose */}
            <div className="slt-card" style={{ marginBottom:14 }}>
              <div style={{ fontWeight:800, fontSize:14, marginBottom:14, color:"#E8962E" }}>✍️ Compose Notification</div>

              <div style={{ marginBottom:12 }}>
                <label style={labelStyle}>Title (optional)</label>
                <input value={newNotif.title} onChange={e=>setNewNotif(p=>({...p,title:e.target.value}))}
                  placeholder="e.g. App Update, Important Notice..." style={inputStyle} />
              </div>

              <div style={{ marginBottom:12 }}>
                <label style={labelStyle}>Message *</label>
                <textarea value={newNotif.message} onChange={e=>setNewNotif(p=>({...p,message:e.target.value}))}
                  placeholder="Write your notification message here..."
                  style={{ ...inputStyle, minHeight:80, resize:"vertical" }} />
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
                <div>
                  <label style={labelStyle}>Type</label>
                  <select value={newNotif.type} onChange={e=>setNewNotif(p=>({...p,type:e.target.value}))} style={inputStyle}>
                    <option value="info">💬 Info</option>
                    <option value="update">🚀 App Update</option>
                    <option value="maintenance">🔧 Maintenance</option>
                    <option value="announcement">📣 Announcement</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Send To</label>
                  <select value={newNotif.target} onChange={e=>setNewNotif(p=>({...p,target:e.target.value,target_uid:""}))} style={inputStyle}>
                    <option value="all">👥 All Users</option>
                    <option value="owners">🚛 Owners Only</option>
                    <option value="drivers">🧑‍✈️ Drivers Only</option>
                    <option value="specific">👤 Specific User</option>
                  </select>
                </div>
              </div>

              {newNotif.target === "specific" && (
                <div style={{ marginBottom:12 }}>
                  <label style={labelStyle}>Select User</label>
                  <select value={newNotif.target_uid} onChange={e=>setNewNotif(p=>({...p,target_uid:e.target.value}))} style={inputStyle}>
                    <option value="">— Choose a user —</option>
                    {allUsers.filter(u=>u.role!=="superadmin").map(u=>(
                      <option key={u.id} value={u.id}>{u.name||"Unknown"} ({u.role} · {u.plan||"beta"})</option>
                    ))}
                  </select>
                </div>
              )}

              <div style={{ marginBottom:14 }}>
                <label style={labelStyle}>Expires At (optional — leave blank = never)</label>
                <input type="datetime-local" value={newNotif.expires_at} onChange={e=>setNewNotif(p=>({...p,expires_at:e.target.value}))} style={inputStyle} />
              </div>

              <div style={{ marginBottom:14 }}>
                <label style={labelStyle}>👁 Visibility — Who sees this and when?</label>
                <div style={{ display:"flex", flexDirection:"column", gap:8, marginTop:6 }}>
                  {[
                    { value:"after",  label:"After Login Only",   desc:"Only logged-in users see this" },
                    { value:"before", label:"Before Login Only",  desc:"Only shows on the login screen" },
                    { value:"both",   label:"Both (Before & After Login)", desc:"Everyone sees it — best for offers & announcements" },
                  ].map(opt=>(
                    <div key={opt.value} onClick={()=>setNewNotif(p=>({...p,visibility:opt.value}))}
                      style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 14px", borderRadius:10, border:`2px solid ${newNotif.visibility===opt.value?"#E8962E":"#ddd"}`, background:newNotif.visibility===opt.value?"#F5F6F8":"#fff", cursor:"pointer" }}>
                      <div style={{ width:18, height:18, borderRadius:"50%", border:`2px solid ${newNotif.visibility===opt.value?"#E8962E":"#ccc"}`, background:newNotif.visibility===opt.value?"#E8962E":"#fff", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                        {newNotif.visibility===opt.value && <div style={{ width:8, height:8, borderRadius:"50%", background:"#fff" }} />}
                      </div>
                      <div>
                        <div style={{ fontSize:13, fontWeight:700, color:newNotif.visibility===opt.value?"#E8962E":"#1a1a1a" }}>{opt.label}</div>
                        <div style={{ fontSize:13, color:"#4B5563" }}>{opt.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Preview */}
              {newNotif.message && (
                <div style={{ marginBottom:14 }}>
                  <label style={labelStyle}>Preview</label>
                  <div style={{
                    borderRadius:10, padding:"12px 16px", fontSize:14, fontWeight:700,
                    background: newNotif.type==="update"?"#E8962E":newNotif.type==="maintenance"?"#B45309":newNotif.type==="announcement"?"#166534":"#E8962E",
                    color:"#fff", display:"flex", alignItems:"center", gap:10
                  }}>
                    <span style={{ fontSize:18 }}>{newNotif.type==="update"?"🚀":newNotif.type==="maintenance"?"🔧":newNotif.type==="announcement"?"📣":"💬"}</span>
                    <div>
                      {newNotif.title && <span style={{ fontWeight:900, marginRight:8 }}>{newNotif.title}:</span>}
                      {newNotif.message}
                    </div>
                    <span style={{ marginLeft:"auto", opacity:0.6, fontSize:18 }}>✕</span>
                  </div>
                </div>
              )}

              <button onClick={sendNotification} disabled={notifSending}
                style={{ width:"100%", padding:"13px", background: notifSent?"#166534":"#E8962E", color:"#fff", border:"none", borderRadius:10, fontWeight:800, fontSize:15, cursor:"pointer" }}>
                {notifSending ? "Sending..." : notifSent ? "✅ Notification Sent!" : "📣 Send Notification"}
              </button>
            </div>

            {/* Active Notifications */}
            <div className="slt-card" style={{ marginBottom:14 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                <div style={{ fontWeight:800, fontSize:14, color:"#E8962E" }}>📋 All Notifications</div>
                <button onClick={loadNotifications} style={{ padding:"6px 14px", background:"#F5F6F8", border:"1px solid #E8962E", borderRadius:8, fontWeight:700, fontSize:12, cursor:"pointer", color:"#E8962E" }}>
                  🔄 Refresh
                </button>
              </div>

              {notifLoading && <div style={{ textAlign:"center", padding:24, color:C.textDarkMut }}>Loading...</div>}

              {!notifLoading && notifications.length === 0 && (
                <div style={{ textAlign:"center", padding:24, color:C.textDarkMut, fontSize:13 }}>No notifications yet. Send one above!</div>
              )}

              {!notifLoading && notifications.map(n => {
                const typeColors = {
                  update:       "#E8962E",
                  maintenance:  "#B45309",
                  announcement: "#166534",
                  info:         "#E8962E",
                };
                const typeIcons = { update:"🚀", maintenance:"🔧", announcement:"📣", info:"💬" };
                return (
                  <div key={n.id} style={{ borderRadius:12, border:`1.5px solid ${n.active?"#E8962E":"#ddd"}`, padding:"12px 14px", marginBottom:10, background: n.active?"#F5F6F8":"#F5F6F8" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <span style={{ fontSize:18 }}>{typeIcons[n.type]||"💬"}</span>
                        <div>
                          {n.title && <div style={{ fontWeight:800, fontSize:13, color: typeColors[n.type]||"#E8962E" }}>{n.title}</div>}
                          <div style={{ fontSize:13, color:"#1a1a1a", fontWeight:600 }}>{n.message}</div>
                        </div>
                      </div>
                      <div style={{ display:"flex", gap:6, flexShrink:0, marginLeft:8 }}>
                        <div onClick={()=>toggleNotifActive(n.id, n.active)}
                          style={{ width:40, height:22, borderRadius:20, background:n.active?"#E8962E":"#ccc", cursor:"pointer", position:"relative", transition:"background 0.2s" }}>
                          <div style={{ width:18, height:18, borderRadius:"50%", background:"#fff", position:"absolute", top:2, left:n.active?20:2, transition:"left 0.2s" }} />
                        </div>
                        <button onClick={()=>deleteNotif(n.id)}
                          style={{ padding:"3px 10px", background:"#FFF0F0", border:"1px solid #EF4444", borderRadius:8, color:"#EF4444", fontWeight:800, fontSize:12, cursor:"pointer" }}>
                          🗑
                        </button>
                      </div>
                    </div>
                    <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                      <span style={{ fontSize:13, fontWeight:700, padding:"2px 8px", borderRadius:20, background: n.active?"#E8962E":"#ccc", color:"#fff" }}>
                        {n.active ? "● Active" : "○ Inactive"}
                      </span>
                      <span style={{ fontSize:13, fontWeight:700, padding:"2px 8px", borderRadius:20, background:"#F5F6F8", color:"#E8962E" }}>
                        {n.target === "all" ? "👥 All Users" : n.target === "owners" ? "🚛 Owners" : n.target === "drivers" ? "🧑‍✈️ Drivers" : "👤 Specific User"}
                      </span>
                      <span style={{ fontSize:13, fontWeight:700, padding:"2px 8px", borderRadius:20, background:"#f0fdf4", color:"#166534" }}>
                        {n.visibility === "before" ? "👁 Login screen only" : n.visibility === "both" ? "👁 Before & after login" : "👁 After login only"}
                      </span>
                      <span style={{ fontSize:13, color:C.textDarkMut }}>
                        {new Date(n.created_at).toLocaleString()}
                      </span>
                      {n.expires_at && (
                        <span style={{ fontSize:13, color:C.textDarkMut }}>· Expires {new Date(n.expires_at).toLocaleString()}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────── PROFILE EDITOR ─────────────────── */}
      {!loading && activeSection === "profile_editor" && (
        <div style={sectionStyle}>
          <div style={{ paddingTop:16 }}>

            {/* Header */}
            <div style={{ background:"linear-gradient(135deg,#E8962E,#E8962E)", borderRadius:16, padding:"18px 20px", marginBottom:16, color:"#fff" }}>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:22, marginBottom:4 }}>🎨 Profile Tab Editor</div>
              <div style={{ fontSize:13, opacity:0.8 }}>Control what every user sees in their Profile tab — items, groups, colors and fonts. Changes save to Supabase and apply live.</div>
            </div>

            {/* Target User Selector */}
            <div className="slt-card" style={{ marginBottom:14 }}>
              <div style={{ fontWeight:800, fontSize:14, marginBottom:10, color:"#E8962E" }}>👤 Apply To</div>
              <select value={profileTargetUser} onChange={e=>{ setProfileTargetUser(e.target.value); setProfileConfig(null); }}
                style={inputStyle}>
                <option value="global">🌍 All Users (Global)</option>
                {allUsers.filter(u=>u.role!=="superadmin").map(u=>(
                  <option key={u.id} value={u.id}>{u.name || "Unknown"} ({u.role} · {u.plan||"beta"})</option>
                ))}
              </select>
              <button onClick={loadProfileConfig} style={{ marginTop:10, padding:"9px 20px", background:"#E8962E", color:"#fff", border:"none", borderRadius:8, fontWeight:800, fontSize:13, cursor:"pointer", width:"100%" }}>
                {profileConfigLoading ? "Loading..." : "📥 Load Config"}
              </button>
            </div>

            {profileConfig && (<>

              {/* Save / Reset bar */}
              <div style={{ display:"flex", gap:10, marginBottom:14 }}>
                <button onClick={saveProfileConfig} disabled={profileConfigSaving}
                  style={{ flex:1, padding:"11px", background: profileConfigSaved?"#166534":"#E8962E", color:"#fff", border:"none", borderRadius:10, fontWeight:800, fontSize:14, cursor:"pointer" }}>
                  {profileConfigSaving ? "Saving..." : profileConfigSaved ? "✅ Saved!" : "💾 Save Changes"}
                </button>
                <button onClick={resetProfileConfig}
                  style={{ padding:"11px 18px", background:"#fff", color:"#E53935", border:"1.5px solid #E53935", borderRadius:10, fontWeight:800, fontSize:13, cursor:"pointer" }}>
                  ↺ Reset
                </button>
              </div>

              {/* ── THEME ── */}
              <div className="slt-card" style={{ marginBottom:14 }}>
                <div style={{ fontWeight:800, fontSize:14, marginBottom:14, color:"#E8962E" }}>🎨 Theme & Colors</div>

                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
                  {[
                    { key:"primaryColor",  label:"Primary Color"  },
                    { key:"accentColor",   label:"Accent Color"   },
                    { key:"bgColor",       label:"Background"     },
                    { key:"cardBg",        label:"Card Background"},
                    { key:"textColor",     label:"Text Color"     },
                  ].map(({key,label})=>(
                    <div key={key}>
                      <label style={{...labelStyle, marginBottom:4}}>{label}</label>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <input type="color" value={profileConfig.theme[key]||"#000000"}
                          onChange={e=>updateTheme(key,e.target.value)}
                          style={{ width:40, height:36, borderRadius:8, border:"1px solid #ddd", cursor:"pointer", padding:2 }} />
                        <input type="text" value={profileConfig.theme[key]||""}
                          onChange={e=>updateTheme(key,e.target.value)}
                          style={{ ...inputStyle, marginTop:0, flex:1, fontSize:12 }} />
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ marginBottom:12 }}>
                  <label style={labelStyle}>Font Family</label>
                  <select value={profileConfig.theme.fontFamily||"Barlow"} onChange={e=>updateTheme("fontFamily",e.target.value)} style={inputStyle}>
                    {["Barlow","Inter","Roboto","Poppins","Montserrat","Oswald","Lato","Nunito","Raleway","Open Sans"].map(f=>(
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={labelStyle}>Card Border Radius (px): {profileConfig.theme.borderRadius}px</label>
                  <input type="range" min="0" max="32" value={profileConfig.theme.borderRadius||18}
                    onChange={e=>updateTheme("borderRadius",e.target.value)}
                    style={{ width:"100%", marginTop:6 }} />
                </div>

                {/* Live Preview */}
                <div style={{ marginTop:14, borderRadius:12, border:"1px solid #ddd", overflow:"hidden" }}>
                  <div style={{ fontSize:13, fontWeight:700, color:"#4B5563", padding:"8px 12px", background:"#F5F6F8", borderBottom:"1px solid #ddd" }}>PREVIEW</div>
                  <div style={{ background: profileConfig.theme.bgColor, padding:16, fontFamily: profileConfig.theme.fontFamily+",sans-serif" }}>
                    <div style={{ background: profileConfig.theme.cardBg, borderRadius: profileConfig.theme.borderRadius+"px", padding:"14px 16px", border:"1px solid rgba(0,0,0,0.08)", marginBottom:10 }}>
                      <div style={{ fontWeight:800, fontSize:15, color: profileConfig.theme.textColor }}>Sample Card</div>
                      <div style={{ fontSize:12, color: profileConfig.theme.textColor, opacity:0.5, marginTop:3 }}>This is how cards will look</div>
                    </div>
                    <div style={{ display:"flex", gap:8 }}>
                      <div style={{ flex:1, background: profileConfig.theme.primaryColor, borderRadius:8, padding:"10px", textAlign:"center", color:"#fff", fontWeight:800, fontSize:12 }}>Primary</div>
                      <div style={{ flex:1, background: profileConfig.theme.accentColor, borderRadius:8, padding:"10px", textAlign:"center", color: profileConfig.theme.primaryColor, fontWeight:800, fontSize:12 }}>Accent</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── SETTINGS & MORE ── */}
              <div className="slt-card" style={{ marginBottom:14 }}>
                <div style={{ fontWeight:800, fontSize:14, marginBottom:4, color:"#E8962E" }}>⚙️ Settings & More Items</div>
                <div style={{ fontSize:12, color:C.textDarkMut, marginBottom:12 }}>Toggle which items appear in the Settings & More section of the Profile tab</div>
                {profileConfig.settingsMore.map(item=>(
                  <div key={item.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"11px 0", borderBottom:`1px solid ${C.border}` }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <span style={{ fontSize:20 }}>{item.icon}</span>
                      <div>
                        <div style={{ fontSize:14, fontWeight:700, color: item.visible?C.text:"#bbb" }}>{item.label}</div>
                        <div style={{ fontSize:13, color:C.textDarkMut }}>{item.subtitle}{item.ownerOnly?" · Owner only":""}</div>
                      </div>
                    </div>
                    <div onClick={()=>toggleSettingsItem(item.id)}
                      style={{ width:44, height:24, borderRadius:20, background:item.visible?"#E8962E":"#ccc", cursor:"pointer", position:"relative", transition:"background 0.2s", flexShrink:0 }}>
                      <div style={{ width:20, height:20, borderRadius:"50%", background:"#fff", position:"absolute", top:2, left:item.visible?22:2, transition:"left 0.2s" }} />
                    </div>
                  </div>
                ))}
              </div>

              {/* ── OWNER TOOL GROUPS ── */}
              <div className="slt-card" style={{ marginBottom:14 }}>
                <div style={{ fontWeight:800, fontSize:14, marginBottom:4, color:"#E8962E" }}>🚛 Owner Tool Groups</div>
                <div style={{ fontSize:12, color:C.textDarkMut, marginBottom:12 }}>Control which tool groups and items owners see in their Profile tab</div>
                {profileConfig.ownerGroups.map((group, gi)=>(
                  <div key={group.group} style={{ marginBottom:14, borderRadius:10, border:`1px solid ${C.border}`, overflow:"hidden" }}>
                    {/* Group header */}
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 14px", background: group.visible?"#F5F6F8":"#F5F6F8" }}>
                      <div style={{ fontWeight:800, fontSize:13, color: group.visible?"#E8962E":"#bbb" }}>📁 {group.group}</div>
                      <div onClick={()=>toggleGroupVisible(gi,true)}
                        style={{ width:44, height:24, borderRadius:20, background:group.visible?"#E8962E":"#ccc", cursor:"pointer", position:"relative", transition:"background 0.2s" }}>
                        <div style={{ width:20, height:20, borderRadius:"50%", background:"#fff", position:"absolute", top:2, left:group.visible?22:2, transition:"left 0.2s" }} />
                      </div>
                    </div>
                    {/* Group items */}
                    {group.items.map(item=>(
                      <div key={item.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"9px 14px 9px 28px", borderTop:`1px solid ${C.border}`, background:"#fff" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          <span style={{ fontSize:16 }}>{item.icon}</span>
                          <span style={{ fontSize:13, fontWeight:600, color: item.visible?"#1a1a1a":"#bbb" }}>{item.label}</span>
                        </div>
                        <div onClick={()=>toggleGroupItem(gi,item.id,true)}
                          style={{ width:38, height:22, borderRadius:20, background:item.visible?"#E8962E":"#ccc", cursor:"pointer", position:"relative", transition:"background 0.2s" }}>
                          <div style={{ width:18, height:18, borderRadius:"50%", background:"#fff", position:"absolute", top:2, left:item.visible?18:2, transition:"left 0.2s" }} />
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              {/* ── DRIVER TOOL GROUPS ── */}
              <div className="slt-card" style={{ marginBottom:14 }}>
                <div style={{ fontWeight:800, fontSize:14, marginBottom:4, color:"#E8962E" }}>🧑‍✈️ Driver Tool Groups</div>
                <div style={{ fontSize:12, color:C.textDarkMut, marginBottom:12 }}>Control which tool groups and items drivers see in their Profile tab</div>
                {profileConfig.driverGroups.map((group, gi)=>(
                  <div key={group.group} style={{ marginBottom:14, borderRadius:10, border:`1px solid ${C.border}`, overflow:"hidden" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 14px", background: group.visible?"#f0fff4":"#F5F6F8" }}>
                      <div style={{ fontWeight:800, fontSize:13, color: group.visible?"#166534":"#bbb" }}>📁 {group.group}</div>
                      <div onClick={()=>toggleGroupVisible(gi,false)}
                        style={{ width:44, height:24, borderRadius:20, background:group.visible?"#166534":"#ccc", cursor:"pointer", position:"relative", transition:"background 0.2s" }}>
                        <div style={{ width:20, height:20, borderRadius:"50%", background:"#fff", position:"absolute", top:2, left:group.visible?22:2, transition:"left 0.2s" }} />
                      </div>
                    </div>
                    {group.items.map(item=>(
                      <div key={item.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"9px 14px 9px 28px", borderTop:`1px solid ${C.border}`, background:"#fff" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          <span style={{ fontSize:16 }}>{item.icon}</span>
                          <span style={{ fontSize:13, fontWeight:600, color: item.visible?"#1a1a1a":"#bbb" }}>{item.label}</span>
                        </div>
                        <div onClick={()=>toggleGroupItem(gi,item.id,false)}
                          style={{ width:38, height:22, borderRadius:20, background:item.visible?"#166534":"#ccc", cursor:"pointer", position:"relative", transition:"background 0.2s" }}>
                          <div style={{ width:18, height:18, borderRadius:"50%", background:"#fff", position:"absolute", top:2, left:item.visible?18:2, transition:"left 0.2s" }} />
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              {/* Save again at bottom */}
              <button onClick={saveProfileConfig} disabled={profileConfigSaving}
                style={{ width:"100%", padding:"14px", background: profileConfigSaved?"#166534":"#E8962E", color:"#fff", border:"none", borderRadius:12, fontWeight:800, fontSize:15, cursor:"pointer" }}>
                {profileConfigSaving ? "Saving..." : profileConfigSaved ? "✅ Saved!" : "💾 Save All Changes"}
              </button>

            </>)}

            {!profileConfig && !profileConfigLoading && (
              <div className="slt-card" style={{ textAlign:"center", padding:40, color:C.textDarkMut }}>
                <div style={{ fontSize:36, marginBottom:12 }}>🎨</div>
                <div style={{ fontWeight:700, marginBottom:6 }}>Select a target and click "Load Config"</div>
                <div style={{ fontSize:13 }}>Choose "All Users" for global changes, or pick a specific user to customize their profile individually.</div>
              </div>
            )}

            {profileConfigLoading && (
              <div className="slt-card" style={{ textAlign:"center", padding:40 }}>
                <div style={{ fontSize:32 }}>⏳</div>
                <div style={{ marginTop:10, color:C.textDarkMed, fontWeight:700 }}>Loading config...</div>
              </div>
            )}

          </div>
        </div>
      )}

      {/* ─────────────────── FEATURE FLAGS ─────────────────── */}
      {!loading && activeSection === "feature_flags" && (
        <FeatureFlagsSection />
      )}

      {/* ─────────────────── APP SETTINGS ─────────────────── */}
      {!loading && activeSection === "settings" && (
        <div style={sectionStyle}>
          <div style={{ paddingTop:16 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:18 }}>⚙️ App Settings</div>
              <button onClick={saveAppSettings} disabled={savingSettings}
                style={{ padding:"10px 22px", background: settingsSaved ? C.green : "#E8962E", color:"#fff", border:"none", borderRadius:10, fontWeight:800, fontSize:13, cursor:"pointer" }}>
                {savingSettings ? "Saving..." : settingsSaved ? "✅ Saved!" : "💾 Save All"}
              </button>
            </div>

            {/* Contact Info */}
            <div className="slt-card" style={{ marginBottom:14 }}>
              <div style={{ fontWeight:800, fontSize:14, marginBottom:14, color:"#E8962E" }}>📞 Contact & Support Info</div>
              {[
                { key:"supportPhone", label:"Support Phone", type:"tel", placeholder:"437-700-5835" },
                { key:"supportEmail", label:"Support Email", type:"email", placeholder:"truckpilot.ca@gmail.com" },
                { key:"whatsapp", label:"WhatsApp Number (digits only)", type:"text", placeholder:"14377005835" },
              ].map(({key,label,type,placeholder}) => (
                <div key={key} style={{ marginBottom:12 }}>
                  <label style={labelStyle}>{label}</label>
                  <input type={type} value={appSettings[key]} placeholder={placeholder}
                    onChange={e=>setAppSettings(p=>({...p,[key]:e.target.value}))} style={inputStyle} />
                </div>
              ))}
            </div>

            {/* App Info */}
            <div className="slt-card" style={{ marginBottom:14 }}>
              <div style={{ fontWeight:800, fontSize:14, marginBottom:14, color:"#E8962E" }}>🏷 App Info</div>
              {[
                { key:"appName", label:"App Name", type:"text", placeholder:"TruckPilot" },
                { key:"appVersion", label:"App Version", type:"text", placeholder:"v3.0" },
              ].map(({key,label,type,placeholder}) => (
                <div key={key} style={{ marginBottom:12 }}>
                  <label style={labelStyle}>{label}</label>
                  <input type={type} value={appSettings[key]} placeholder={placeholder}
                    onChange={e=>setAppSettings(p=>({...p,[key]:e.target.value}))} style={inputStyle} />
                </div>
              ))}
            </div>

            {/* Plan Pricing */}
            <div className="slt-card" style={{ marginBottom:14 }}>
              <div style={{ fontWeight:800, fontSize:14, marginBottom:14, color:"#E8962E" }}>💰 Plan Pricing</div>
              {[
                { key:"basicPrice", label:"Basic Plan Price ($/mo)", placeholder:"9.99" },
                { key:"proPrice",   label:"Pro Plan Price ($/mo)",   placeholder:"24.99" },
                { key:"maxFreeLoads",   label:"Free Plan — Max Loads",   placeholder:"50" },
                { key:"maxBasicDrivers", label:"Basic Plan — Max Drivers", placeholder:"3" },
              ].map(({key,label,placeholder}) => (
                <div key={key} style={{ marginBottom:12 }}>
                  <label style={labelStyle}>{label}</label>
                  <input type="number" step="0.01" value={appSettings[key]} placeholder={placeholder}
                    onChange={e=>setAppSettings(p=>({...p,[key]:e.target.value}))} style={inputStyle} />
                </div>
              ))}
            </div>

            {/* Feature Flags */}
            <div className="slt-card" style={{ marginBottom:14 }}>
              <div style={{ fontWeight:800, fontSize:14, marginBottom:14, color:"#E8962E" }}>🚦 Feature Flags</div>
              {[
                { key:"maintenanceMode", label:"🔧 Maintenance Mode (locks app for all non-admins)" },
                { key:"betaMode", label:"🧪 Beta Mode (all features free for everyone)" },
                { key:"showUpgradeOption", label:"💳 Show Upgrade/Plans to users" },
              ].map(({key,label}) => (
                <div key={key} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 0", borderBottom:`1px solid ${C.border}` }}>
                  <div style={{ fontSize:13, fontWeight:600, flex:1 }}>{label}</div>
                  <div onClick={async()=>{ const newVal = !appSettings[key]; const updated = {...appSettings,[key]:newVal}; setAppSettings(updated); await sb.from("settings").upsert({ user_id:"__app__", rates:updated, routes:[] },{ onConflict:"user_id" }); await sb.from("settings").update({ rates: {...(updated), showUpgradeOption:updated.showUpgradeOption, maintenanceMode:updated.maintenanceMode, betaMode:updated.betaMode} }).eq("user_id","55661ff3-cd77-4e29-8de3-a4e2ad129dca"); }}
                    style={{ width:44, height:24, borderRadius:20, background:appSettings[key]?"#E8962E":"#ccc", cursor:"pointer", position:"relative", transition:"background 0.2s", flexShrink:0 }}>
                    <div style={{ width:20, height:20, borderRadius:"50%", background:"#fff", position:"absolute", top:2, left:appSettings[key]?22:2, transition:"left 0.2s", boxShadow:"0 1px 3px rgba(0,0,0,0.55)" }} />
                  </div>
                </div>
              ))}
              <div style={{ fontSize:13, color:C.textDarkMut, marginTop:8 }}>⚠️ Feature flags are saved to Supabase. Some may require a code deploy to fully take effect.</div>
            </div>

            {/* System stats */}
            <div className="slt-card" style={{ marginBottom:14 }}>
              <div style={{ fontWeight:800, fontSize:14, marginBottom:14, color:"#E8962E" }}>📊 System Stats (read-only)</div>
              {[
                ["Total Users", allUsers.length],
                ["Total Loads", totalLoads],
                ["Total Support Messages", allMessages.length],
                ["Super Admins", superAdmins.length],
                ["Unread Messages", unreadMsgs.length],
                ["Est. Monthly Revenue", `$${estMonthlyRevenue}`],
              ].map(([l,v]) => (
                <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"9px 0", borderBottom:`1px solid ${C.border}`, fontSize:13 }}>
                  <span style={{ color:C.textDarkMed, fontWeight:600 }}>{l}</span>
                  <span style={{ fontWeight:800, color:"#E8962E" }}>{v}</span>
                </div>
              ))}
            </div>

            {/* Danger zone */}
            <div className="slt-card" style={{ background:"#FFF3E0", border:"1.5px solid #E8962E" }}>
              <div style={{ fontWeight:800, color:"#E8962E", marginBottom:8, fontSize:14 }}>⚠️ Danger Zone</div>
              <div style={{ fontSize:13, color:C.textDarkMed, marginBottom:12 }}>These actions are irreversible. Proceed with extreme caution.</div>
              <button onClick={()=>{ if(window.confirm("Clear ALL support messages? This cannot be undone.")) sb.from("support_messages").delete().neq("id","00000000-0000-0000-0000-000000000000").then(()=>{ setAllMessages([]); }); }}
                style={{ padding:"10px 16px", background:"#E53935", color:"#fff", border:"none", borderRadius:8, fontWeight:800, fontSize:13, cursor:"pointer" }}>
                🗑 Clear All Support Messages
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ─────────────────── MY PROFILE ─────────────────── */}
      {!loading && activeSection === "my_profile" && (
        <div style={sectionStyle}>
          <div style={{ paddingTop:16 }}>

            <div style={{ background:"linear-gradient(135deg,#E8962E,#E8962E)", borderRadius:16, padding:"18px 20px", marginBottom:16, color:"#fff" }}>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:22, marginBottom:4 }}>👤 My Admin Profile</div>
              <div style={{ fontSize:13, opacity:0.8 }}>Edit your name, username, email, password and profile photo</div>
            </div>

            {/* Load profile button */}
            {!adminProfile.name && (
              <button onClick={loadAdminProfile} style={{ width:"100%", padding:"12px", background:"#E8962E", color:"#fff", border:"none", borderRadius:10, fontWeight:800, fontSize:14, cursor:"pointer", marginBottom:14 }}>
                📥 Load My Profile
              </button>
            )}

            {/* Avatar */}
            <div className="slt-card" style={{ marginBottom:14, textAlign:"center" }}>
              <div style={{ fontWeight:800, fontSize:14, marginBottom:14, color:"#E8962E" }}>🖼 Profile Photo</div>
              <div style={{ width:90, height:90, borderRadius:"50%", background:"#E8962E", margin:"0 auto 14px", overflow:"hidden", display:"flex", alignItems:"center", justifyContent:"center" }}>
                {adminProfile.avatarUrl
                  ? <img src={adminProfile.avatarUrl} alt="avatar" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                  : <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:32, color:"#fff" }}>
                      {(adminProfile.name||"A")[0].toUpperCase()}
                    </span>
                }
              </div>
              <label style={{ display:"inline-block", padding:"9px 20px", background:"#F5F6F8", border:"1.5px solid #E8962E", borderRadius:10, fontWeight:800, fontSize:13, cursor:"pointer", color:"#E8962E" }}>
                {adminAvatarUploading ? "Uploading..." : "📷 Upload Photo"}
                <input type="file" accept="image/*" style={{ display:"none" }} onChange={e=>uploadAdminAvatar(e.target.files[0])} />
              </label>
              <div style={{ fontSize:13, color:"#4B5563", marginTop:8 }}>JPG, PNG or GIF. Max 2MB recommended.</div>
            </div>

            {/* Profile fields */}
            <div className="slt-card" style={{ marginBottom:14 }}>
              <div style={{ fontWeight:800, fontSize:14, marginBottom:14, color:"#E8962E" }}>✏️ Profile Info</div>
              {[
                { key:"name",     label:"Full Name",  type:"text",  placeholder:"Your name" },
                { key:"username", label:"Username",   type:"text",  placeholder:"your_username" },
                { key:"email",    label:"Email",      type:"email", placeholder:"you@email.com" },
              ].map(({key,label,type,placeholder})=>(
                <div key={key} style={{ marginBottom:12 }}>
                  <label style={labelStyle}>{label}</label>
                  <input type={type} value={adminProfile[key]} placeholder={placeholder}
                    onChange={e=>setAdminProfile(p=>({...p,[key]:e.target.value}))} style={inputStyle} />
                </div>
              ))}
            </div>

            {/* Password */}
            <div className="slt-card" style={{ marginBottom:14 }}>
              <div style={{ fontWeight:800, fontSize:14, marginBottom:4, color:"#E8962E" }}>🔑 Change Password</div>
              <div style={{ fontSize:12, color:"#4B5563", marginBottom:12 }}>Leave blank to keep current password</div>
              {[
                { key:"newPassword",     label:"New Password",     placeholder:"Min 6 characters" },
                { key:"confirmPassword", label:"Confirm Password", placeholder:"Repeat new password" },
              ].map(({key,label,placeholder})=>(
                <div key={key} style={{ marginBottom:12 }}>
                  <label style={labelStyle}>{label}</label>
                  <input type="password" value={adminProfile[key]} placeholder={placeholder}
                    onChange={e=>setAdminProfile(p=>({...p,[key]:e.target.value}))} style={inputStyle} />
                </div>
              ))}
            </div>

            {adminProfileMsg.text && (
              <div style={{ padding:"12px 16px", borderRadius:10, marginBottom:14, fontSize:13, fontWeight:700,
                background: adminProfileMsg.type==="success"?"#f0fdf4":"#FFF0F0",
                color: adminProfileMsg.type==="success"?"#166534":"#EF4444",
                border: `1px solid ${adminProfileMsg.type==="success"?"#bbf7d0":"#fecaca"}` }}>
                {adminProfileMsg.text}
              </div>
            )}

            <button onClick={saveAdminProfile} disabled={adminProfileSaving}
              style={{ width:"100%", padding:"14px", background: adminProfileSaved?"#166534":"#E8962E", color:"#fff", border:"none", borderRadius:12, fontWeight:800, fontSize:15, cursor:"pointer" }}>
              {adminProfileSaving ? "Saving..." : adminProfileSaved ? "✅ Saved!" : "💾 Save Profile"}
            </button>
          </div>
        </div>
      )}

      {/* ─────────────────── CREATE ADMIN ─────────────────── */}
      {!loading && activeSection === "create_admin" && (
        <div style={sectionStyle}>
          <div style={{ paddingTop:16 }}>

            <div style={{ background:"linear-gradient(135deg,#E8962E,#E8962E)", borderRadius:16, padding:"18px 20px", marginBottom:16, color:"#fff" }}>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:22, marginBottom:4 }}>➕ Create Admin Account</div>
              <div style={{ fontSize:13, opacity:0.8 }}>Create a new superadmin account. They will have full access to this admin panel.</div>
            </div>

            <div className="slt-card" style={{ marginBottom:14 }}>
              <div style={{ fontWeight:800, fontSize:14, marginBottom:14, color:"#E8962E" }}>👤 New Admin Details</div>

              {[
                { key:"name",     label:"Full Name *",  type:"text",  placeholder:"Admin's full name" },
                { key:"email",    label:"Email *",      type:"email", placeholder:"admin@truckpilot.ca" },
                { key:"username", label:"Username",     type:"text",  placeholder:"admin_username (optional)" },
              ].map(({key,label,type,placeholder})=>(
                <div key={key} style={{ marginBottom:12 }}>
                  <label style={labelStyle}>{label}</label>
                  <input type={type} value={newAdmin[key]} placeholder={placeholder}
                    onChange={e=>setNewAdmin(p=>({...p,[key]:e.target.value}))} style={inputStyle} />
                </div>
              ))}

              {/* Password method */}
              <div style={{ marginBottom:14 }}>
                <label style={{ ...labelStyle, marginBottom:8 }}>🔑 Password Setup</label>
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  <div onClick={()=>setNewAdmin(p=>({...p,sendLink:false}))}
                    style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 14px", borderRadius:10, border:`2px solid ${!newAdmin.sendLink?"#E8962E":"#ddd"}`, background:!newAdmin.sendLink?"#f5f3ff":"#fff", cursor:"pointer" }}>
                    <div style={{ width:18, height:18, borderRadius:"50%", border:`2px solid ${!newAdmin.sendLink?"#E8962E":"#ccc"}`, background:!newAdmin.sendLink?"#E8962E":"#fff", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                      {!newAdmin.sendLink && <div style={{ width:8, height:8, borderRadius:"50%", background:"#fff" }} />}
                    </div>
                    <div>
                      <div style={{ fontSize:13, fontWeight:700, color:!newAdmin.sendLink?"#E8962E":"#1a1a1a" }}>Set password manually</div>
                      <div style={{ fontSize:13, color:"#4B5563" }}>You type their password now</div>
                    </div>
                  </div>
                  <div onClick={()=>setNewAdmin(p=>({...p,sendLink:true,password:""}))}
                    style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 14px", borderRadius:10, border:`2px solid ${newAdmin.sendLink?"#E8962E":"#ddd"}`, background:newAdmin.sendLink?"#f5f3ff":"#fff", cursor:"pointer" }}>
                    <div style={{ width:18, height:18, borderRadius:"50%", border:`2px solid ${newAdmin.sendLink?"#E8962E":"#ccc"}`, background:newAdmin.sendLink?"#E8962E":"#fff", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                      {newAdmin.sendLink && <div style={{ width:8, height:8, borderRadius:"50%", background:"#fff" }} />}
                    </div>
                    <div>
                      <div style={{ fontSize:13, fontWeight:700, color:newAdmin.sendLink?"#E8962E":"#1a1a1a" }}>Send password setup link</div>
                      <div style={{ fontSize:13, color:"#4B5563" }}>They get an email to set their own password</div>
                    </div>
                  </div>
                </div>
              </div>

              {!newAdmin.sendLink && (
                <div style={{ marginBottom:12 }}>
                  <label style={labelStyle}>Password *</label>
                  <input type="password" value={newAdmin.password} placeholder="Min 6 characters"
                    onChange={e=>setNewAdmin(p=>({...p,password:e.target.value}))} style={inputStyle} />
                </div>
              )}

              {createAdminMsg.text && (
                <div style={{ padding:"12px 16px", borderRadius:10, marginBottom:12, fontSize:13, fontWeight:700,
                  background: createAdminMsg.type==="success"?"#f0fdf4":"#FFF0F0",
                  color: createAdminMsg.type==="success"?"#166534":"#EF4444",
                  border:`1px solid ${createAdminMsg.type==="success"?"#bbf7d0":"#fecaca"}` }}>
                  {createAdminMsg.text}
                </div>
              )}

              <button onClick={createAdminAccount} disabled={creatingAdmin}
                style={{ width:"100%", padding:"13px", background:"#E8962E", color:"#fff", border:"none", borderRadius:10, fontWeight:800, fontSize:15, cursor:"pointer" }}>
                {creatingAdmin ? "Creating..." : "➕ Create Admin Account"}
              </button>
            </div>

            {/* Existing admins list */}
            <div className="slt-card">
              <div style={{ fontWeight:800, fontSize:14, marginBottom:12, color:"#E8962E" }}>🛡 Current Admins</div>
              {allUsers.filter(u=>u.role==="superadmin").map(u=>(
                <div key={u.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:`1px solid ${C.border}` }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <div style={{ width:36, height:36, borderRadius:"50%", background:"#E8962E", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:900, fontSize:15 }}>
                      {(u.name||"A")[0].toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight:700, fontSize:13 }}>{u.name||"Unknown"}</div>
                      <div style={{ fontSize:13, color:C.textDarkMut }}>{u.username_email||u.email||"—"} · Joined {u.created_at?.slice(0,10)}</div>
                    </div>
                  </div>
                  <span style={{ background:"#E8962E22", color:"#E8962E", borderRadius:20, padding:"3px 10px", fontSize:13, fontWeight:800 }}>🛡 Admin</span>
                </div>
              ))}
              {allUsers.filter(u=>u.role==="superadmin").length===0 && (
                <div style={{ color:C.textDarkMut, fontSize:13, textAlign:"center", padding:20 }}>No admins found</div>
              )}
            </div>

          </div>
        </div>
      )}

      {/* ─────────────────── RECOVER DATA ─────────────────── */}
      {!loading && activeSection === "recover_data" && (
        <div className="slt-container" style={sectionStyle}>
          <div className="slt-card" style={{ marginBottom:16 }}>
            <div style={{ fontWeight:900, fontSize:18, color:"#1A1A1A", marginBottom:4 }}>🗂️ Recover Deleted Data</div>
            <div style={{ fontSize:13, color:C.textDarkMed, marginBottom:16 }}>
              Loads and expenses deleted by users are kept for <strong>90 days</strong> before permanent removal. You can restore any item below.
            </div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
              <input
                placeholder="Search by user ID, name, route…"
                value={recoverSearch}
                onChange={e => setRecoverSearch(e.target.value)}
                style={{ flex:1, minWidth:220, padding:"9px 12px", borderRadius:8, border:`1px solid ${C.border}`, fontSize:13, fontFamily:"'Barlow',sans-serif" }}
              />
              <button
                onClick={async () => {
                  setRecoverLoading(true);
                  setRecoverMsg("");
                  try {
                    const items = await sbGetAllDeletedItems();
                    setRecoverItems(items);
                  } catch(e) { setRecoverMsg("Error loading deleted items."); }
                  setRecoverLoading(false);
                }}
                style={{ padding:"9px 18px", background:"#1A1A1A", color:"#fff", border:"none", borderRadius:8, fontWeight:700, fontSize:13, cursor:"pointer" }}>
                {recoverLoading ? "Loading…" : "🔍 Load Deleted Items"}
              </button>
            </div>
            {recoverMsg && <div style={{ marginTop:8, fontSize:13, color:"#EF4444", fontWeight:700 }}>{recoverMsg}</div>}
          </div>

          {/* ── Deleted Loads ── */}
          <div className="slt-card" style={{ marginBottom:16 }}>
            <div style={{ fontWeight:800, fontSize:15, color:"#1A1A1A", marginBottom:12 }}>
              🚛 Deleted Loads ({recoverItems.loads.filter(l => {
                const s = recoverSearch.toLowerCase();
                return !s || JSON.stringify(l).toLowerCase().includes(s);
              }).length})
            </div>
            {recoverItems.loads.length === 0 && !recoverLoading && (
              <div style={{ color:C.textDarkMut, fontSize:13, textAlign:"center", padding:"20px 0" }}>
                Click "Load Deleted Items" to fetch deleted records.
              </div>
            )}
            {recoverItems.loads
              .filter(l => {
                const s = recoverSearch.toLowerCase();
                return !s || JSON.stringify(l).toLowerCase().includes(s);
              })
              .sort((a,b) => new Date(b.deleted_at||0) - new Date(a.deleted_at||0))
              .map(load => {
                const deletedAt = load.deleted_at ? new Date(load.deleted_at) : null;
                const expiresAt = deletedAt ? new Date(deletedAt.getTime() + 90*24*60*60*1000) : null;
                const daysLeft = expiresAt ? Math.ceil((expiresAt - Date.now()) / (24*60*60*1000)) : null;
                return (
                  <div key={load.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", padding:"12px 0", borderBottom:`1px solid ${C.border}`, gap:8 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, fontSize:13 }}>
                        {load.origin || "Unknown"} → {load.destination || "—"}
                        <span style={{ marginLeft:8, fontSize:12, color:C.textDarkMut, fontWeight:600 }}>
                          {load.date || load.created_at?.slice(0,10) || "—"}
                        </span>
                      </div>
                      <div style={{ fontSize:12, color:C.textDarkMed, marginTop:2 }}>
                        User: <span style={{ fontFamily:"monospace", fontSize:11 }}>{load.user_id || "—"}</span>
                        {load.assignedDriver && <span> · Driver: {load.assignedDriver}</span>}
                        {load.totalMiles && <span> · {load.totalMiles} mi</span>}
                      </div>
                      <div style={{ fontSize:12, color:"#EF4444", marginTop:2 }}>
                        Deleted: {deletedAt?.toLocaleDateString()} · {daysLeft !== null ? `${daysLeft}d until permanent removal` : ""}
                      </div>
                    </div>
                    <button
                      disabled={recoverRestoring === load.id}
                      onClick={async () => {
                        if (!window.confirm(`Restore this load (${load.origin} → ${load.destination})?`)) return;
                        setRecoverRestoring(load.id);
                        try {
                          await sbRestoreLoad(load, load.user_id);
                          setRecoverItems(prev => ({ ...prev, loads: prev.loads.filter(l => l.id !== load.id) }));
                          setRecoverMsg(`✅ Load restored for user ${load.user_id?.slice(0,8)}…`);
                          setTimeout(() => setRecoverMsg(""), 4000);
                        } catch(e) { setRecoverMsg("Restore failed: " + e.message); }
                        setRecoverRestoring(null);
                      }}
                      style={{ padding:"7px 14px", background: recoverRestoring===load.id?"#ccc":"#16A34A", color:"#fff", border:"none", borderRadius:8, fontWeight:700, fontSize:12, cursor:"pointer", whiteSpace:"nowrap", flexShrink:0 }}>
                      {recoverRestoring === load.id ? "Restoring…" : "↩️ Restore"}
                    </button>
                  </div>
                );
              })
            }
          </div>

          {/* ── Deleted Expenses ── */}
          <div className="slt-card">
            <div style={{ fontWeight:800, fontSize:15, color:"#1A1A1A", marginBottom:12 }}>
              💰 Deleted Expenses ({recoverItems.expenses.filter(e => {
                const s = recoverSearch.toLowerCase();
                return !s || JSON.stringify(e).toLowerCase().includes(s);
              }).length})
            </div>
            {recoverItems.expenses.length === 0 && !recoverLoading && (
              <div style={{ color:C.textDarkMut, fontSize:13, textAlign:"center", padding:"20px 0" }}>
                Click "Load Deleted Items" to fetch deleted records.
              </div>
            )}
            {recoverItems.expenses
              .filter(e => {
                const s = recoverSearch.toLowerCase();
                return !s || JSON.stringify(e).toLowerCase().includes(s);
              })
              .sort((a,b) => new Date(b.deleted_at||0) - new Date(a.deleted_at||0))
              .map(exp => {
                const deletedAt = exp.deleted_at ? new Date(exp.deleted_at) : null;
                const expiresAt = deletedAt ? new Date(deletedAt.getTime() + 90*24*60*60*1000) : null;
                const daysLeft = expiresAt ? Math.ceil((expiresAt - Date.now()) / (24*60*60*1000)) : null;
                return (
                  <div key={exp.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", padding:"12px 0", borderBottom:`1px solid ${C.border}`, gap:8 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, fontSize:13 }}>
                        {exp.category || exp.expenseType || "Expense"} — ${Number(exp.amount||0).toFixed(2)}
                        {exp.description && <span style={{ fontWeight:400, marginLeft:6, color:C.textDarkMed }}>{exp.description}</span>}
                      </div>
                      <div style={{ fontSize:12, color:C.textDarkMed, marginTop:2 }}>
                        User: <span style={{ fontFamily:"monospace", fontSize:11 }}>{exp.user_id || "—"}</span>
                        {exp.driverName && <span> · {exp.driverName}</span>}
                        {exp.date && <span> · {exp.date}</span>}
                      </div>
                      <div style={{ fontSize:12, color:"#EF4444", marginTop:2 }}>
                        Deleted: {deletedAt?.toLocaleDateString()} · {daysLeft !== null ? `${daysLeft}d until permanent removal` : ""}
                      </div>
                    </div>
                    <button
                      disabled={recoverRestoring === exp.id}
                      onClick={async () => {
                        if (!window.confirm(`Restore this expense ($${Number(exp.amount||0).toFixed(2)} — ${exp.category || exp.expenseType})?`)) return;
                        setRecoverRestoring(exp.id);
                        try {
                          await sbRestoreExpense(exp, exp.user_id);
                          setRecoverItems(prev => ({ ...prev, expenses: prev.expenses.filter(e => e.id !== exp.id) }));
                          setRecoverMsg(`✅ Expense restored for user ${exp.user_id?.slice(0,8)}…`);
                          setTimeout(() => setRecoverMsg(""), 4000);
                        } catch(e) { setRecoverMsg("Restore failed: " + e.message); }
                        setRecoverRestoring(null);
                      }}
                      style={{ padding:"7px 14px", background: recoverRestoring===exp.id?"#ccc":"#16A34A", color:"#fff", border:"none", borderRadius:8, fontWeight:700, fontSize:12, cursor:"pointer", whiteSpace:"nowrap", flexShrink:0 }}>
                      {recoverRestoring === exp.id ? "Restoring…" : "↩️ Restore"}
                    </button>
                  </div>
                );
              })
            }
          </div>
          {recoverMsg && <div style={{ marginTop:12, padding:"10px 14px", background:"#f0fdf4", color:"#166534", borderRadius:8, fontWeight:700, fontSize:13, border:"1px solid #bbf7d0" }}>{recoverMsg}</div>}
        </div>
      )}

      {/* ── Delete Data ── */}
      {!loading && activeSection === "delete_data" && (
        <div className="slt-container" style={sectionStyle}>
          <div className="slt-card" style={{ marginBottom:16 }}>
            <div style={{ fontWeight:900, fontSize:18, color:"#1A1A1A", marginBottom:4 }}>🗑️ Delete Any Data</div>
            <div style={{ fontSize:13, color:C.textDarkMed, marginBottom:16 }}>
              Permanently delete any expense or load for any user. <strong style={{color:"#EF4444"}}>This cannot be undone.</strong>
            </div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
              <input
                placeholder="Search by user ID, amount, route, category…"
                value={deleteDataSearch}
                onChange={e => setDeleteDataSearch(e.target.value)}
                style={{ flex:1, minWidth:220, padding:"9px 12px", borderRadius:8, border:`1px solid ${C.border}`, fontSize:13, fontFamily:"'Barlow',sans-serif" }}
              />
              <button
                onClick={async () => {
                  setDeleteDataLoading(true);
                  setDeleteDataMsg("");
                  try {
                    const { data: loadRows } = await sb.from("loads").select("*").order("created_at", { ascending: false });
                    const loads = (loadRows || []).map(r => ({ id:r.id, user_id:r.user_id, owner_uid:r.owner_uid, created_at:r.created_at, completed:r.completed, ...r.data })).filter(l => !l.deleted);
                    const { data: expRows } = await sb.from("expenses").select("*").order("created_at", { ascending: false });
                    const expenses = (expRows || []).map(r => ({ id:r.id, user_id:r.user_id, created_at:r.created_at, ...r.data })).filter(e => !e.deleted);
                    setDeleteDataItems({ loads, expenses });
                  } catch(e) { setDeleteDataMsg("Error loading data: " + e.message); }
                  setDeleteDataLoading(false);
                }}
                style={{ padding:"9px 18px", background:"#1A1A1A", color:"#fff", border:"none", borderRadius:8, fontWeight:700, fontSize:13, cursor:"pointer" }}>
                {deleteDataLoading ? "Loading…" : "🔍 Load All Data"}
              </button>
            </div>
            {deleteDataMsg && <div style={{ marginTop:8, fontSize:13, color: deleteDataMsg.startsWith("✅") ? "#16A34A" : "#EF4444", fontWeight:700 }}>{deleteDataMsg}</div>}
          </div>

          {/* All Loads */}
          <div className="slt-card" style={{ marginBottom:16 }}>
            <div style={{ fontWeight:800, fontSize:15, color:"#1A1A1A", marginBottom:12 }}>
              🚛 All Active Loads ({deleteDataItems.loads.filter(l => { const s=deleteDataSearch.toLowerCase(); return !s||JSON.stringify(l).toLowerCase().includes(s); }).length})
            </div>
            {deleteDataItems.loads.length === 0 && !deleteDataLoading && (
              <div style={{ color:C.textDarkMut, fontSize:13, textAlign:"center", padding:"20px 0" }}>Click "Load All Data" to fetch records.</div>
            )}
            {deleteDataItems.loads
              .filter(l => { const s=deleteDataSearch.toLowerCase(); return !s||JSON.stringify(l).toLowerCase().includes(s); })
              .sort((a,b) => new Date(b.created_at||0) - new Date(a.created_at||0))
              .map(load => (
                <div key={load.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", padding:"12px 0", borderBottom:`1px solid ${C.border}`, gap:8 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:13 }}>
                      {load.origin||"Unknown"} → {load.destination||"—"}
                      <span style={{ marginLeft:8, fontSize:12, color:C.textDarkMut, fontWeight:600 }}>{load.date||load.created_at?.slice(0,10)||"—"}</span>
                    </div>
                    <div style={{ fontSize:12, color:C.textDarkMed, marginTop:2 }}>
                      User: <span style={{ fontFamily:"monospace", fontSize:11 }}>{load.user_id||"—"}</span>
                      {load.assignedDriver && <span> · Driver: {load.assignedDriver}</span>}
                      {load.totalPay && <span> · Pay: ${Number(load.totalPay).toFixed(2)}</span>}
                    </div>
                  </div>
                  <button
                    disabled={deleteDataDeleting === load.id}
                    onClick={async () => {
                      if (!window.confirm(`Permanently delete this load?\n${load.origin} → ${load.destination}\nUser: ${load.user_id}\n\nThis CANNOT be undone.`)) return;
                      setDeleteDataDeleting(load.id);
                      try {
                        await sbDeleteLoad(load.id);
                        setDeleteDataItems(prev => ({ ...prev, loads: prev.loads.filter(l => l.id !== load.id) }));
                        setDeleteDataMsg(`✅ Load deleted permanently.`);
                        setTimeout(() => setDeleteDataMsg(""), 4000);
                      } catch(e) { setDeleteDataMsg("Delete failed: " + e.message); }
                      setDeleteDataDeleting(null);
                    }}
                    style={{ padding:"7px 14px", background: deleteDataDeleting===load.id?"#ccc":"#EF4444", color:"#fff", border:"none", borderRadius:8, fontWeight:700, fontSize:12, cursor:"pointer", whiteSpace:"nowrap", flexShrink:0 }}>
                    {deleteDataDeleting === load.id ? "Deleting…" : "🗑️ Delete"}
                  </button>
                </div>
              ))
            }
          </div>

          {/* All Expenses */}
          <div className="slt-card">
            <div style={{ fontWeight:800, fontSize:15, color:"#1A1A1A", marginBottom:12 }}>
              💰 All Active Expenses ({deleteDataItems.expenses.filter(e => { const s=deleteDataSearch.toLowerCase(); return !s||JSON.stringify(e).toLowerCase().includes(s); }).length})
            </div>
            {deleteDataItems.expenses.length === 0 && !deleteDataLoading && (
              <div style={{ color:C.textDarkMut, fontSize:13, textAlign:"center", padding:"20px 0" }}>Click "Load All Data" to fetch records.</div>
            )}
            {deleteDataItems.expenses
              .filter(e => { const s=deleteDataSearch.toLowerCase(); return !s||JSON.stringify(e).toLowerCase().includes(s); })
              .sort((a,b) => new Date(b.created_at||b.date||0) - new Date(a.created_at||a.date||0))
              .map(exp => (
                <div key={exp.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", padding:"12px 0", borderBottom:`1px solid ${C.border}`, gap:8 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:13 }}>
                      {exp.category||exp.expenseType||"Expense"} — ${Number(exp.amount||0).toFixed(2)}
                      <span style={{ marginLeft:8, fontSize:12, color:C.textDarkMut, fontWeight:600 }}>{exp.date||exp.created_at?.slice(0,10)||"—"}</span>
                    </div>
                    <div style={{ fontSize:12, color:C.textDarkMed, marginTop:2 }}>
                      User: <span style={{ fontFamily:"monospace", fontSize:11 }}>{exp.user_id||"—"}</span>
                      {exp.merchant && <span> · {exp.merchant}</span>}
                      {exp.source && <span> · Source: {exp.source}</span>}
                      {(exp.ownerExpense||exp.expenseType==="business") && <span style={{color:C.blue}}> · Business</span>}
                    </div>
                  </div>
                  <button
                    disabled={deleteDataDeleting === exp.id}
                    onClick={async () => {
                      if (!window.confirm(`Permanently delete this expense?\n${exp.category||"Expense"}: $${Number(exp.amount||0).toFixed(2)}\nUser: ${exp.user_id}\n\nThis CANNOT be undone.`)) return;
                      setDeleteDataDeleting(exp.id);
                      try {
                        await sbDeleteExpense(exp.id);
                        setDeleteDataItems(prev => ({ ...prev, expenses: prev.expenses.filter(e => e.id !== exp.id) }));
                        setDeleteDataMsg(`✅ Expense deleted permanently.`);
                        setTimeout(() => setDeleteDataMsg(""), 4000);
                      } catch(e) { setDeleteDataMsg("Delete failed: " + e.message); }
                      setDeleteDataDeleting(null);
                    }}
                    style={{ padding:"7px 14px", background: deleteDataDeleting===exp.id?"#ccc":"#EF4444", color:"#fff", border:"none", borderRadius:8, fontWeight:700, fontSize:12, cursor:"pointer", whiteSpace:"nowrap", flexShrink:0 }}>
                    {deleteDataDeleting === exp.id ? "Deleting…" : "🗑️ Delete"}
                  </button>
                </div>
              ))
            }
            {deleteDataMsg && <div style={{ marginTop:12, padding:"10px 14px", background: deleteDataMsg.startsWith("✅")?"#f0fdf4":"#fef2f2", color: deleteDataMsg.startsWith("✅")?"#166534":"#991b1b", borderRadius:8, fontWeight:700, fontSize:13 }}>{deleteDataMsg}</div>}
          </div>
        </div>
      )}

      {/* ── Orphan Mirror Expense Cleanup ── */}
      {!loading && activeSection === "orphan_cleanup" && (
        <div className="slt-container" style={sectionStyle}>
          <div className="slt-card" style={{ marginBottom:16 }}>
            <div style={{ fontWeight:900, fontSize:18, color:"#1A1A1A", marginBottom:6 }}>🧹 Orphaned Mirror Expense Cleanup</div>
            <div style={{ fontSize:13, color:C.textDarkMed, marginBottom:16, lineHeight:1.6 }}>
              When a driver deletes a business expense, the mirrored copy in the fleet owner's account should also be removed.
              <br/>This tool scans for <strong>"mirror-*"</strong> expense rows whose original was already deleted — and lets you remove them permanently.
            </div>

            <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:16 }}>
              <button
                onClick={scanOrphanedMirrors}
                disabled={orphanLoading}
                style={{ padding:"10px 20px", background:"#E8962E", color:"#fff", border:"none", borderRadius:10, fontWeight:800, fontSize:13, cursor:"pointer" }}>
                {orphanLoading ? "🔍 Scanning…" : "🔍 Scan for Orphans"}
              </button>
              {orphans.length > 0 && (
                <button
                  onClick={deleteAllOrphans}
                  disabled={orphanLoading}
                  style={{ padding:"10px 20px", background:"#DC2626", color:"#fff", border:"none", borderRadius:10, fontWeight:800, fontSize:13, cursor:"pointer" }}>
                  🗑️ Delete All {orphans.length} Orphans
                </button>
              )}
            </div>

            {orphanMsg && (
              <div style={{ marginBottom:14, padding:"10px 14px", borderRadius:8, fontWeight:700, fontSize:13,
                background: orphanMsg.startsWith("✅") ? "#f0fdf4" : orphanMsg.startsWith("⚠️") ? "#fffbeb" : "#fef2f2",
                color: orphanMsg.startsWith("✅") ? "#166534" : orphanMsg.startsWith("⚠️") ? "#92400e" : "#991b1b",
                border: `1px solid ${orphanMsg.startsWith("✅") ? "#bbf7d0" : orphanMsg.startsWith("⚠️") ? "#fde68a" : "#fecaca"}`
              }}>
                {orphanMsg}
              </div>
            )}

            {orphanScanned && orphans.length > 0 && (
              <>
                {/* Column headers */}
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 80px 90px 80px", gap:6, padding:"8px 10px", background:"#F5F6F8", borderRadius:8, marginBottom:6, fontSize:11, fontWeight:800, color:"#E8962E", textTransform:"uppercase", letterSpacing:0.5 }}>
                  <span>Driver</span>
                  <span>Owner Account</span>
                  <span>Expense</span>
                  <span>Amount</span>
                  <span>Date</span>
                  <span>Action</span>
                </div>

                {orphans.map(o => (
                  <div key={o.mirrorId} style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 80px 90px 80px", gap:6, padding:"10px 10px", borderBottom:`1px solid ${C.border}`, alignItems:"start", fontSize:12 }}>
                    {/* Driver */}
                    <div>
                      <div style={{ fontWeight:700, color:"#E8962E" }}>👤 {o.driverName}</div>
                      <div style={{ fontSize:11, color:C.textDarkMut, fontFamily:"monospace", wordBreak:"break-all" }}>{o.driverUid}</div>
                    </div>

                    {/* Owner account where mirror sits */}
                    <div>
                      <div style={{ fontWeight:700, color:"#166534" }}>🏢 {o.ownerName}</div>
                      <div style={{ fontSize:11, color:C.textDarkMut, fontFamily:"monospace", wordBreak:"break-all" }}>{o.ownerUid}</div>
                      <div style={{ fontSize:11, color:C.textDarkMut, marginTop:2 }}>Mirror ID: <span style={{ fontFamily:"monospace" }}>{o.mirrorId}</span></div>
                    </div>

                    {/* Expense detail */}
                    <div>
                      <div style={{ fontWeight:700, color:"#374151", textTransform:"capitalize" }}>{o.category.replace(/_/g," ")}</div>
                      {o.merchant && <div style={{ fontSize:11, color:C.textDarkMed }}>{o.merchant}</div>}
                      <div style={{ fontSize:11, color:C.textDarkMut }}>
                        Source: {o.source}
                        {o.originalDeleted
                          ? <span style={{ color:"#b45309", marginLeft:4 }}>· orig soft-deleted</span>
                          : <span style={{ color:"#dc2626", marginLeft:4 }}>· orig missing</span>
                        }
                      </div>
                    </div>

                    {/* Amount */}
                    <div style={{ fontWeight:800, color:"#dc2626", fontSize:13 }}>
                      ${o.amount.toFixed(2)}
                    </div>

                    {/* Date */}
                    <div style={{ color:C.textDarkMed, fontSize:12 }}>{o.date}</div>

                    {/* Delete button */}
                    <button
                      disabled={orphanDeleting === o.mirrorId}
                      onClick={() => {
                        if (!window.confirm(`Delete orphaned mirror?\n\nDriver: ${o.driverName}\nOwner account: ${o.ownerName}\nExpense: ${o.category} — $${o.amount.toFixed(2)}\nDate: ${o.date}\n\nThis cannot be undone.`)) return;
                        deleteOrphan(o.mirrorId);
                      }}
                      style={{ padding:"6px 10px", background: orphanDeleting===o.mirrorId ? "#ccc" : "#EF4444", color:"#fff", border:"none", borderRadius:7, fontWeight:700, fontSize:11, cursor:"pointer", whiteSpace:"nowrap" }}>
                      {orphanDeleting === o.mirrorId ? "…" : "🗑️ Delete"}
                    </button>
                  </div>
                ))}

                <div style={{ marginTop:14, padding:"10px 14px", background:"#fffbeb", borderRadius:8, fontSize:12, color:"#92400e", fontWeight:600, border:"1px solid #fde68a" }}>
                  ⚠️ Deleting an orphan permanently removes the mirror row from the owner's Expenses. The driver's original expense record is already gone. This action cannot be undone.
                </div>
              </>
            )}

            {orphanScanned && orphans.length === 0 && !orphanMsg.startsWith("❌") && (
              <div style={{ textAlign:"center", padding:"30px 0", color:C.textDarkMut, fontSize:14 }}>
                ✅ No orphaned mirror expenses found.
              </div>
            )}
          </div>

          {/* ── Stale Driver Report Expenses ── */}
          <div className="slt-card" style={{ marginBottom:16 }}>
            <div style={{ fontWeight:900, fontSize:18, color:"#1A1A1A", marginBottom:6 }}>🗂️ Driver Report Expense Cleanup</div>
            <div style={{ fontSize:13, color:C.textDarkMed, marginBottom:10, lineHeight:1.7 }}>
              Supabase RLS prevents admin from directly deleting driver rows. This tool uses a <strong>cleanup command bus</strong>:
              admin queues a removal, the driver's app self-executes it on next load using their own session.
            </div>
            <div style={{ fontSize:12, background:"#F5F6F8", borderRadius:8, padding:"10px 14px", marginBottom:16, color:"#E8962E", border:"1px solid #F5F6F8" }}>
              <strong>How it works:</strong> Scan → select expenses to clear → click <em>Queue Cleanup</em> → driver opens app → expenses auto-deleted ✅
            </div>

            <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:16, alignItems:"center" }}>
              <button
                onClick={scanStaleDriverExpenses}
                disabled={staleLoading}
                style={{ padding:"10px 20px", background:"#E8962E", color:"#fff", border:"none", borderRadius:10, fontWeight:800, fontSize:13, cursor:"pointer" }}>
                {staleLoading ? "🔍 Scanning…" : "🔍 Scan Driver Expenses"}
              </button>
              {staleExps.length > 0 && (
                <>
                  <select
                    value={staleDriverFilter}
                    onChange={e => setStaleDriverFilter(e.target.value)}
                    style={{ padding:"9px 12px", borderRadius:8, border:`1px solid ${C.border}`, fontSize:13, fontWeight:600, color:"#1A1A1A", background:"#fff", cursor:"pointer" }}>
                    <option value="all">All Drivers ({staleExps.length})</option>
                    {[...new Map(staleExps.map(e => [e.driverUid, e.driverName])).entries()].map(([uid, name]) => (
                      <option key={uid} value={uid}>{name} ({staleExps.filter(e => e.driverUid === uid).length})</option>
                    ))}
                  </select>
                  <button
                    onClick={queueCleanupForAll}
                    disabled={staleLoading}
                    style={{ padding:"10px 20px", background:"#d97706", color:"#fff", border:"none", borderRadius:10, fontWeight:800, fontSize:13, cursor:"pointer" }}>
                    🗂️ Queue Cleanup — {staleDriverFilter === "all"
                      ? staleExps.filter(e => !e.isPendingCleanup).length
                      : staleExps.filter(e => e.driverUid === staleDriverFilter && !e.isPendingCleanup).length} pending
                  </button>
                </>
              )}
            </div>

            {staleMsg && (
              <div style={{ marginBottom:14, padding:"10px 14px", borderRadius:8, fontWeight:700, fontSize:13,
                background: staleMsg.startsWith("✅") ? "#f0fdf4" : staleMsg.startsWith("ℹ️") ? "#eff6ff" : staleMsg.startsWith("↩️") ? "#f5f3ff" : staleMsg.startsWith("❌") ? "#fef2f2" : "#fffbeb",
                color: staleMsg.startsWith("✅") ? "#166534" : staleMsg.startsWith("ℹ️") ? "#E8962E" : staleMsg.startsWith("↩️") ? "#E8962E" : staleMsg.startsWith("❌") ? "#991b1b" : "#92400e",
                border: `1px solid ${staleMsg.startsWith("✅") ? "#bbf7d0" : staleMsg.startsWith("ℹ️") ? "#FFD580" : staleMsg.startsWith("↩️") ? "#F5F6F8" : staleMsg.startsWith("❌") ? "#fecaca" : "#fde68a"}`
              }}>
                {staleMsg}
              </div>
            )}

            {staleScanned && staleExps.length > 0 && (() => {
              const visible = staleDriverFilter === "all" ? staleExps : staleExps.filter(e => e.driverUid === staleDriverFilter);
              return (
                <>
                  {/* Column headers */}
                  <div style={{ display:"grid", gridTemplateColumns:"1.4fr 1fr 100px 70px 80px 110px", gap:6, padding:"8px 10px", background:"#F5F6F8", borderRadius:8, marginBottom:6, fontSize:11, fontWeight:800, color:"#E8962E", textTransform:"uppercase", letterSpacing:0.5 }}>
                    <span>Driver / Expense</span>
                    <span>Category</span>
                    <span>Status</span>
                    <span>Amount</span>
                    <span>Date</span>
                    <span>Action</span>
                  </div>

                  {visible.map(e => (
                    <div key={e.id} style={{ display:"grid", gridTemplateColumns:"1.4fr 1fr 100px 70px 80px 110px", gap:6, padding:"10px 10px", borderBottom:`1px solid ${C.border}`, alignItems:"center", fontSize:12, background: e.isPendingCleanup ? "#f0fdf4" : "transparent" }}>
                      {/* Driver + expense ID */}
                      <div>
                        <div style={{ fontWeight:700, color:"#E8962E" }}>👤 {e.driverName}</div>
                        <div style={{ fontSize:10, color:C.textDarkMut, fontFamily:"monospace", wordBreak:"break-all", marginTop:1 }}>{e.id}</div>
                        {e.merchant && <div style={{ fontSize:11, color:C.textDarkMed, marginTop:2 }}>{e.merchant}</div>}
                      </div>

                      {/* Category */}
                      <div>
                        <div style={{ fontWeight:700, color:"#374151", textTransform:"capitalize" }}>{e.category.replace(/_/g," ")}</div>
                        <div style={{ fontSize:11, color:C.textDarkMut }}>src: {e.source}</div>
                      </div>

                      {/* Status badge */}
                      <div>
                        {e.isPendingCleanup
                          ? <span style={{ display:"inline-block", padding:"3px 8px", borderRadius:20, background:"#dcfce7", color:"#166534", fontSize:10, fontWeight:800, border:"1px solid #bbf7d0" }}>⏳ QUEUED</span>
                          : <span style={{ display:"inline-block", padding:"3px 8px", borderRadius:20, background:"#fef2f2", color:"#dc2626", fontSize:10, fontWeight:800, border:"1px solid #fecaca" }}>ACTIVE</span>
                        }
                      </div>

                      {/* Amount */}
                      <div style={{ fontWeight:800, color:"#374151", fontSize:13 }}>
                        ${e.amount.toFixed(2)}
                      </div>

                      {/* Expense date */}
                      <div style={{ color:C.textDarkMed, fontSize:12 }}>{e.date}</div>

                      {/* Action button */}
                      <div>
                        {e.isPendingCleanup ? (
                          <button
                            onClick={() => cancelPendingCleanup(e.id)}
                            style={{ padding:"5px 10px", background:"#fff", color:"#E8962E", border:"1.5px solid #F5F6F8", borderRadius:7, fontWeight:700, fontSize:11, cursor:"pointer", whiteSpace:"nowrap" }}>
                            ↩️ Cancel
                          </button>
                        ) : (
                          <button
                            onClick={() => queueCleanupForExpense(e)}
                            style={{ padding:"5px 10px", background:"#d97706", color:"#fff", border:"none", borderRadius:7, fontWeight:700, fontSize:11, cursor:"pointer", whiteSpace:"nowrap" }}>
                            🗂️ Queue Clear
                          </button>
                        )}
                      </div>
                    </div>
                  ))}

                  <div style={{ marginTop:14, padding:"10px 14px", background:"#F5F6F8", borderRadius:8, fontSize:12, color:"#E8962E", fontWeight:600, border:"1px solid #F5F6F8" }}>
                    ℹ️ Queued expenses are removed the next time the driver opens TruckPilot. Green rows = cleanup pending. Use ↩️ Cancel to undo a queued cleanup before the driver logs in.
                  </div>
                </>
              );
            })()}

            {staleScanned && staleExps.length === 0 && !staleMsg.startsWith("❌") && (
              <div style={{ textAlign:"center", padding:"30px 0", color:C.textDarkMut, fontSize:14 }}>
                ✅ No active driver business expenses found.
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

function MessageDetailModal({ thread, onClose, onThreadUpdate, session }) {
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState(null);
  const [imgB64, setImgB64] = useState(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);
  const msgsLen = useRef(thread?.msgs?.length || 0);

  const msgs = thread?.msgs || [];
  const isClosed = thread?.closed;

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:"smooth" }); }, [msgs.length]);
  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 150); }, []);

  useEffect(() => {
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await sb.from("support_messages")
          .select("message,reply").eq("from_uid", thread.from_uid).maybeSingle();
        if (!data) return;
        const fresh = chatParse({ ...thread, ...data });
        if (!fresh) return;
        if (fresh.msgs.length > msgsLen.current) {
          msgsLen.current = fresh.msgs.length;
          onThreadUpdate(fresh);
        }
      } catch(e) {}
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [thread.from_uid]);

  const compressImg = (file) => new Promise(res => {
    const r = new FileReader();
    r.onload = e => {
      const img = new Image();
      img.onload = () => {
        const c = document.createElement("canvas");
        let [w, h] = [img.width, img.height];
        if (w > 800) { h = h*800/w; w = 800; }
        c.width = w; c.height = h;
        c.getContext("2d").drawImage(img, 0, 0, w, h);
        res(c.toDataURL("image/jpeg", 0.65));
      };
      img.src = e.target.result;
    };
    r.readAsDataURL(file);
  });

  const send = async () => {
    if ((!input.trim() && !imgB64) || sending || isClosed) return;
    setSending(true); setSendErr(null);
    const msg = { id: Date.now().toString(), from:"admin", text: input.trim(), image: imgB64||null, time: new Date().toISOString() };
    const err = await chatAdminSend(thread.from_uid, msgs, msg);
    if (err) { setSendErr("Failed: " + err.message); setSending(false); return; }
    const updatedMsgs = [...msgs, msg];
    msgsLen.current = updatedMsgs.length; // update BEFORE poll can fire
    onThreadUpdate({ ...thread, msgs: updatedMsgs });
    setInput(""); setImgB64(null); setSending(false);
    setTimeout(() => inputRef.current?.focus(), 80);
  };

  const toggleClose = async () => {
    const closing = !isClosed;
    if (closing && !window.confirm("End this conversation?")) return;
    await chatSetClosed(thread.from_uid, closing);
    onThreadUpdate({ ...thread, closed: closing, reply: closing ? "__closed__" : null });
  };

  return (
    <div style={{ position:"fixed", inset:0, zIndex:9500, display:"flex", flexDirection:"column", background:"#fff" }}>
      <div style={{ background:"linear-gradient(135deg,#E8962E,#E8962E)", padding:"12px 14px", display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
        <button onClick={onClose} style={{ background:"rgba(255,255,255,0.15)", border:"none", borderRadius:8, color:"#fff", fontSize:18, cursor:"pointer", padding:"4px 10px", fontWeight:700 }}>←</button>
        <div style={{ width:36,height:36,borderRadius:"50%",background:"rgba(255,255,255,0.25)",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:900,fontSize:15,flexShrink:0 }}>
          {(thread.from_name||"?")[0].toUpperCase()}
        </div>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:800,fontSize:15,color:"#fff" }}>{thread.from_name||"Unknown"}</div>
          <div style={{ fontSize:13,color:"rgba(255,255,255,0.7)" }}>{thread.from_email||""} · {msgs.length} msgs</div>
        </div>
        <button onClick={toggleClose}
          style={{ background:isClosed?"#43A047":"#E53935",border:"none",borderRadius:8,color:"#fff",fontSize:12,fontWeight:800,cursor:"pointer",padding:"6px 12px" }}>
          {isClosed ? "Reopen" : "End Chat"}
        </button>
        <button onClick={async()=>{ if(!window.confirm("Delete conversation?")) return; await sb.from("support_messages").delete().eq("from_uid",thread.from_uid); onClose(); }}
          style={{ background:"rgba(255,255,255,0.1)",border:"none",borderRadius:8,color:"rgba(255,255,255,0.8)",fontSize:16,cursor:"pointer",padding:"6px 8px" }}>&#E8962E;</button>
      </div>

      {isClosed && (
        <div style={{ background:"rgba(0,0,0,0.05)",padding:"7px 14px",textAlign:"center",fontSize:13,fontWeight:600,color:C.textDarkMut,flexShrink:0 }}>
          — Conversation paused · full history preserved —
        </div>
      )}

      <div style={{ flex:1,overflowY:"auto",padding:"14px",background:"#F5F6F8",display:"flex",flexDirection:"column",gap:8 }}>
        {msgs.length===0 && <div style={{ textAlign:"center",color:C.textDarkMut,padding:40 }}>No messages yet</div>}
        {msgs.map((m,i) => {
          // If admin is viewing their OWN thread (they are the user), flip perspective
          const viewingOwnThread = thread.from_uid === session?.uid;
          const isMine = viewingOwnThread ? m.from === "user" : m.from === "admin";
          const senderName = viewingOwnThread
            ? (m.from === "user" ? (thread.from_name || "You") : "Customer Support")
            : (isMine ? "Customer Support" : (thread.from_name || "User"));
          const senderColor = isMine ? (viewingOwnThread ? C.blue : "#E8962E") : (viewingOwnThread ? "#E8962E" : C.blue);
          return (
            <div key={m.id||i} style={{ display:"flex", justifyContent:isMine?"flex-end":"flex-start", alignItems:"flex-end", gap:8, marginBottom:4 }}>
              {/* Left avatar — user messages */}
              {!isMine && (
                <div style={{ width:32,height:32,borderRadius:"50%",background:senderColor,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:800,fontSize:13,flexShrink:0 }}>
                  {senderName[0].toUpperCase()}
                </div>
              )}
              <div style={{ maxWidth:"72%" }}>
                <div style={{ fontSize:13,fontWeight:800,color:senderColor,marginBottom:3,textAlign:isMine?"right":"left" }}>{senderName}</div>
                {m.image && <img src={m.image} alt="" onClick={()=>window.open(m.image,"_blank")} style={{ maxWidth:"100%",borderRadius:12,marginBottom:m.text?4:0,display:"block",cursor:"pointer",border:"2px solid rgba(255,255,255,0.5)" }} />}
                {m.text && (
                  <div style={{ background:isMine?"linear-gradient(135deg,#E8962E,#E8962E)":"#fff", color:isMine?"#fff":C.textDark, borderRadius:isMine?"18px 18px 4px 18px":"18px 18px 18px 4px", padding:"10px 14px", fontSize:13, lineHeight:1.5, boxShadow:"0 1px 4px rgba(0,0,0,0.55)" }}>
                    {m.text}
                  </div>
                )}
                <div style={{ fontSize:12,color:C.textDarkMut,marginTop:3,textAlign:isMine?"right":"left" }}>
                  {new Date(m.time).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}
                </div>
              </div>
              {/* Right avatar — admin messages */}
              {isMine && (
                <div style={{ width:32,height:32,borderRadius:"50%",background:senderColor,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:800,fontSize:13,flexShrink:0 }}>
                  A
                </div>
              )}
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {imgB64 && (
        <div style={{ padding:"6px 12px",background:"#fff",borderTop:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:8,flexShrink:0 }}>
          <img src={imgB64} alt="" style={{ height:50,borderRadius:6,border:`1px solid ${C.border}` }} />
          <button onClick={()=>setImgB64(null)} style={{ color:C.red,background:"none",border:"none",cursor:"pointer",fontWeight:700 }}>Remove</button>
        </div>
      )}
      {sendErr && <div style={{ padding:"8px 14px",background:"#FFEBEE",borderTop:`1px solid ${C.red}`,fontSize:12,color:C.red,fontWeight:700,flexShrink:0 }}>{sendErr}</div>}

      {isClosed ? (
        <div style={{ padding:"14px",background:"#fff",borderTop:`1px solid ${C.border}`,textAlign:"center",flexShrink:0 }}>
          <button onClick={toggleClose} style={{ padding:"10px 24px",background:C.blue,color:"#fff",border:"none",borderRadius:10,fontWeight:800,fontSize:14,cursor:"pointer" }}>Reopen Chat</button>
        </div>
      ) : (
        <div style={{ padding:"8px 12px 10px",background:"#fff",borderTop:`1px solid ${C.border}`,display:"flex",gap:8,alignItems:"flex-end",flexShrink:0 }}>
          <input ref={fileRef} type="file" accept="image/*" style={{ display:"none" }} onChange={async e=>{ const f=e.target.files?.[0]; if(f) setImgB64(await compressImg(f)); }} />
          <button onClick={()=>fileRef.current?.click()} style={{ width:40,height:40,borderRadius:10,border:`1px solid ${C.border}`,background:"#F5F6F8",fontSize:18,cursor:"pointer",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center" }}>&#128247;</button>
          <textarea ref={inputRef} value={input} onChange={e=>setInput(e.target.value)}
            onKeyDown={e=>{ if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();} }}
            placeholder="Type reply... (Enter to send)" rows={2}
            style={{ flex:1,padding:"9px 12px",borderRadius:10,border:`1.5px solid ${C.border}`,fontSize:14,fontFamily:"'Barlow',sans-serif",resize:"none",outline:"none",lineHeight:1.4 }}
            onFocus={e=>e.target.style.borderColor=C.blue} onBlur={e=>e.target.style.borderColor=C.border} />
          <button onClick={send} disabled={sending||(!input.trim()&&!imgB64)}
            style={{ width:42,height:42,borderRadius:10,border:"none",background:sending||(!input.trim()&&!imgB64)?"#ccc":C.blue,color:"#fff",fontSize:20,cursor:"pointer",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center" }}>
            {sending?"...":">"}
          </button>
        </div>
      )}
    </div>
  );
}

function SupportInboxTab({ session, embedded = false }) {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openThread, setOpenThread] = useState(null);
  const [filter, setFilter] = useState("all");
  const pollRef = useRef(null);

  const load = async () => {
    const data = await chatGetAll();
    setThreads(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 6000);
    return () => clearInterval(pollRef.current);
  }, []);

  const openModal = (t) => {
    clearInterval(pollRef.current);
    if (!t.read) { chatMarkRead(t.from_uid); setThreads(prev=>prev.map(x=>x.from_uid===t.from_uid?{...x,read:true}:x)); }
    setOpenThread(t);
  };

  const closeModal = () => {
    setOpenThread(null);
    load();
    pollRef.current = setInterval(load, 6000);
  };

  const handleUpdate = (fresh) => {
    setOpenThread(fresh);
    setThreads(prev=>prev.map(x=>x.from_uid===fresh.from_uid?fresh:x));
  };

  const unread = threads.filter(t=>!t.read).length;
  const filtered = threads.filter(t=>{
    if(filter==="unread") return !t.read;
    if(filter==="open") return !t.closed;
    if(filter==="closed") return t.closed;
    return true;
  });

  const body = (
    <div style={{ padding: embedded?"0":"0 16px 40px" }}>
      <div style={{ display:"flex",gap:6,flexWrap:"wrap",padding:embedded?"10px 0":"14px 0 10px" }}>
        {[["all","All",threads.length],["unread","Unread",unread],["open","Open",threads.filter(t=>!t.closed).length],["closed","Closed",threads.filter(t=>t.closed).length]].map(([v,l,c])=>(
          <button key={v} onClick={()=>setFilter(v)}
            style={{ padding:"5px 12px",borderRadius:20,border:`1.5px solid ${filter===v?"#E8962E":C.border}`,background:filter===v?"#E8962E":"#fff",color:filter===v?"#fff":C.textMed,fontWeight:700,fontSize:12,cursor:"pointer" }}>
            {l} ({c})
          </button>
        ))}
        <button onClick={load} style={{ marginLeft:"auto",padding:"5px 10px",borderRadius:20,border:`1.5px solid ${C.border}`,background:"#fff",fontSize:14,cursor:"pointer" }}>&#8635;</button>
      </div>
      {loading && <div style={{ textAlign:"center",padding:40,color:C.textDarkMut }}>Loading...</div>}
      {!loading && filtered.length===0 && (
        <div className="slt-card" style={{ textAlign:"center",padding:40 }}>
          <div style={{ fontSize:14,fontWeight:700 }}>{filter==="all"?"No conversations yet":"No " + filter + " conversations"}</div>
        </div>
      )}
      {filtered.map(t=>{
        const last=t.msgs?.[t.msgs.length-1];
        return (
          <div key={t.from_uid} onClick={()=>openModal(t)} className="slt-card"
            style={{ marginBottom:10,borderLeft:`4px solid ${!t.read?C.blue:t.closed?"#888":C.orange}`,cursor:"pointer",padding:"12px 14px" }}>
            <div style={{ display:"flex",alignItems:"center",gap:10 }}>
              <div style={{ width:40,height:40,borderRadius:"50%",background:!t.read?C.blue:t.closed?"#888":C.orange,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:900,fontSize:16,flexShrink:0 }}>
                {(t.from_name||"?")[0].toUpperCase()}
              </div>
              <div style={{ flex:1,minWidth:0 }}>
                <div style={{ display:"flex",alignItems:"center",gap:6,marginBottom:2,flexWrap:"wrap" }}>
                  <span style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:14 }}>{t.from_name||"Unknown"}</span>
                  {!t.read&&<span style={{ background:C.blue,color:"#fff",borderRadius:20,padding:"1px 7px",fontSize:12,fontWeight:800 }}>NEW</span>}
                  {t.closed&&<span style={{ background:"#888",color:"#fff",borderRadius:20,padding:"1px 7px",fontSize:12,fontWeight:800 }}>CLOSED</span>}
                  <span style={{ background:"#EEEEEE",color:C.textDarkMed,borderRadius:20,padding:"1px 7px",fontSize:12,fontWeight:700 }}>{t.msgs?.length||0} msgs</span>
                </div>
                {t.from_email&&<div style={{ fontSize:13,color:C.textDarkMed,marginBottom:1 }}>{t.from_email}</div>}
                <div style={{ fontSize:12,color:C.textDarkMut,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>
                  {last?.image?"Photo":last?.text?.slice(0,80)||"No messages"}
                </div>
              </div>
              <div style={{ fontSize:13,color:C.textDarkMut,flexShrink:0,textAlign:"right" }}>
                <div>{t.created_at?.slice(0,10)}</div>
                <div style={{ fontSize:16,marginTop:4 }}>&#9658;</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  if(embedded) return (
    <>{body}{openThread&&<MessageDetailModal thread={openThread} onClose={closeModal} onThreadUpdate={handleUpdate} session={session}/>}</>
  );
  return (
    <div className="slt-page" style={{background:"#070B14",color:"#F0F0F0"}}>
      <div className="slt-hero">
        <div className="slt-hero-title">Support Inbox</div>
        <div className="slt-hero-sub">{threads.length} conversations · {unread} unread</div>
      </div>
      <div className="slt-container">{body}</div>
      {openThread&&<MessageDetailModal thread={openThread} onClose={closeModal} onThreadUpdate={handleUpdate} session={session}/>}
    </div>
  );
}

function ContactUsTab({ session, onBack }) {
  const [thread, setThread] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState(null);
  const [imgB64, setImgB64] = useState(null);
  const [loading, setLoading] = useState(true);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const bottomRef = useRef(null);
  const scrollAreaRef = useRef(null);
  const pollRef = useRef(null);
  const msgsLen = useRef(0);

  // Reliable scroll-to-bottom: use direct scrollTop manipulation (works on iOS Safari)
  const scrollToBottom = () => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    } else if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const loadThread = async () => {
    if(!session?.uid) return;
    const t = await chatGetThread(session.uid);
    setThread(t);
    msgsLen.current = t?.msgs?.length||0;
    setLoading(false);
    setTimeout(scrollToBottom, 80);
  };

  useEffect(()=>{
    loadThread();
    pollRef.current = setInterval(async()=>{
      if(!session?.uid) return;
      const t = await chatGetThread(session.uid);
      if(t && t.msgs.length!==msgsLen.current){
        msgsLen.current=t.msgs.length;
        setThread(t);
      }
    },5000);
    return ()=>clearInterval(pollRef.current);
  },[session?.uid]);

  useEffect(()=>{ setTimeout(scrollToBottom, 80); },[thread?.msgs?.length]);

  const compressImg=(file)=>new Promise(res=>{
    const r=new FileReader();
    r.onload=e=>{
      const img=new Image();
      img.onload=()=>{
        const c=document.createElement("canvas");
        let[w,h]=[img.width,img.height];
        if(w>800){h=h*800/w;w=800;}
        c.width=w;c.height=h;
        c.getContext("2d").drawImage(img,0,0,w,h);
        res(c.toDataURL("image/jpeg",0.65));
      };
      img.src=e.target.result;
    };
    r.readAsDataURL(file);
  });

  const send=async()=>{
    if((!input.trim()&&!imgB64)||sending) return;
    if(thread?.closed){setSendErr("Chat is closed. Start a new conversation.");return;}
    setSending(true);setSendErr(null);
    const msg={id:Date.now().toString(),from:"user",text:input.trim(),image:imgB64||null,time:new Date().toISOString()};
    const err=await chatSendMsg(session.uid,session.fullName||session.name||"User",session.email||"",msg);
    if(err){setSendErr("Could not send.");setSending(false);return;}
    const userText = input.trim();
    setInput("");setImgB64(null);
    // AI auto-responds first if it's a text message
    if(userText && !imgB64) {
      try {
        const aiSystem = `You are TruckPilot Support AI — a helpful assistant for TruckPilot, a Canadian trucking app.
Answer support questions clearly and briefly (2-3 sentences max). 
If the issue requires human help (billing, account deletion, bugs, payment), end with: "If you need further help, a human agent will follow up shortly."
Common topics: logging loads, fleet management, pay calculation, IFTA taxes, expenses, documents, app navigation.
User: ${session.fullName||session.name}, Role: ${session.role}.`;
        const aiReply = await fetch("/api/ai-chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ system: aiSystem, message: userText, maxTokens: 200 })
        });
        const aiData = await aiReply.json();
        if (aiData.text && !aiData.error) {
          const aiMsg = {id: Date.now().toString()+"ai", from:"admin", text:"🤖 TruckPilot AI: " + aiData.text, time: new Date().toISOString()};
          await chatSendMsg(session.uid, session.fullName||session.name||"User", session.email||"", aiMsg, true);
          loadThread();
        }
      } catch(e) { /* AI failed silently, human will respond */ }
    }
    await loadThread();
    setSending(false);
    setTimeout(()=>inputRef.current?.focus(),80);
  };

  const endChat=async()=>{
    if(!window.confirm("End this conversation?")) return;
    await chatSetClosed(session.uid,true);
    await loadThread();
  };

  const newChat=async()=>{
    // Just reopen — never delete history, all messages stay for reference
    await chatSetClosed(session.uid,false);
    await loadThread();
    setTimeout(()=>inputRef.current?.focus(),150);
  };

  const msgs=thread?.msgs||[];
  const isClosed=thread?.closed;

  return(
    <div style={{display:"flex",flexDirection:"column",height:"calc(100dvh - 60px - env(safe-area-inset-bottom, 0px))",background:"#F5F6F8",position:"relative",paddingBottom:"calc(60px + env(safe-area-inset-bottom, 0px))"}}>
      <div style={{background:"linear-gradient(135deg,#0A1628,#1A1A1A)",padding:"12px 14px",display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
        <button onClick={onBack} style={{background:"rgba(255,255,255,0.15)",border:"none",borderRadius:8,color:"#fff",fontSize:18,cursor:"pointer",padding:"4px 10px",fontWeight:700,flexShrink:0}}>←</button>
        <div style={{width:38,height:38,borderRadius:"50%",background:"linear-gradient(135deg,#E8962E,#E8962E)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>&#E8962E;</div>
        <div style={{flex:1}}>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:15,color:"#fff"}}>TruckPilot Support</div>
          <div style={{fontSize:13,color:isClosed?"#FF8A65":"#E8962E",fontWeight:600}}>{isClosed?"Chat ended":"Online · Avg reply < 3 min"}</div>
        </div>
        <a href={"tel:"+COMPANY_PHONE.replace(/-/g,"")} style={{textDecoration:"none"}}>
          <button style={{background:"rgba(255,255,255,0.1)",border:"none",borderRadius:8,color:"#fff",fontSize:12,cursor:"pointer",padding:"6px 10px",fontWeight:700}}>Call</button>
        </a>
        <a href={"https://wa.me/"+WHATSAPP_NUMBER} target="_blank" rel="noreferrer" style={{textDecoration:"none"}}>
          <button style={{background:"#E8962E",border:"none",borderRadius:8,color:"#fff",fontSize:12,cursor:"pointer",padding:"6px 10px",fontWeight:700}}>WhatsApp</button>
        </a>
      </div>

      <div ref={scrollAreaRef} style={{flex:1,overflowY:"auto",padding:"14px 12px",display:"flex",flexDirection:"column",gap:8,WebkitOverflowScrolling:"touch",overscrollBehavior:"contain"}}>
        <div style={{display:"flex",justifyContent:"flex-start",alignItems:"flex-end",gap:6}}>
          <div style={{width:28,height:28,borderRadius:"50%",background:"linear-gradient(135deg,#E8962E,#E8962E)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14}}>&#E8962E;</div>
          <div style={{maxWidth:"78%",background:"#fff",borderRadius:"14px 14px 14px 4px",padding:"9px 13px",fontSize:13,color:C.textDark,boxShadow:"0 1px 3px rgba(0,0,0,0.55)"}}>
            Hello <strong>{session.fullName||session.name||"there"}</strong>, welcome to TruckPilot Support. We are recording this conversation for future reference and training purposes.
          </div>
        </div>
        {loading&&<div style={{textAlign:"center",padding:20,color:C.textDarkMut}}>Loading...</div>}
        {msgs.map((m,i)=>{
          // From user perspective: user = right (outgoing/mine), admin = left (incoming)
          const isMine = m.from === "user";
          // Detect AI-generated admin messages vs human support messages
          const isAIMsg = !isMine && m.text && m.text.startsWith("🤖 TruckPilot AI:");
          const senderName = isMine ? (session.fullName||session.name||"You") : (isAIMsg ? "TruckPilot AI" : "Customer Support");
          const senderColor = isMine ? C.blue : (isAIMsg ? "#E8962E" : "#1C2B4A");
          // Strip the redundant "🤖 TruckPilot AI: " prefix — sender label already shows it
          const displayText = isAIMsg ? m.text.replace(/^🤖 TruckPilot AI:\s*/, "") : m.text;
          const avatarLabel = isAIMsg ? "🤖" : (isMine ? senderName[0].toUpperCase() : "A");
          return(
            <div key={m.id||i} style={{display:"flex", justifyContent:isMine?"flex-end":"flex-start", alignItems:"flex-end", gap:8, marginBottom:4}}>
              {/* Left avatar — admin messages */}
              {!isMine && (
                <div style={{width:32,height:32,borderRadius:"50%",background:senderColor,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:800,fontSize:isAIMsg?16:13,flexShrink:0}}>{avatarLabel}</div>
              )}
              <div style={{maxWidth:"72%"}}>
                <div style={{fontSize:13,fontWeight:800,color:senderColor,marginBottom:3,textAlign:isMine?"right":"left"}}>{senderName}</div>
                {m.image&&<img src={m.image} alt="" onClick={()=>window.open(m.image,"_blank")} style={{maxWidth:"100%",borderRadius:12,marginBottom:m.text?4:0,display:"block",cursor:"pointer"}}/>}
                {displayText&&(
                  <div style={{background:isMine?"linear-gradient(135deg,#E8962E,#E8962E)":(isAIMsg?"linear-gradient(135deg,#1C2B4A,#243655)":"#fff"), color:isMine?"#fff":(isAIMsg?"#fff":"#1a1a2e"), borderRadius:isMine?"18px 18px 4px 18px":"18px 18px 18px 4px", padding:"10px 14px", fontSize:13, lineHeight:1.5, boxShadow:"0 1px 4px rgba(0,0,0,0.55)"}}>
                    {displayText}
                  </div>
                )}
                <div style={{fontSize:12,color:C.textDarkMut,marginTop:3,textAlign:isMine?"right":"left"}}>{new Date(m.time).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</div>
              </div>
              {/* Right avatar — user messages */}
              {isMine && (
                <div style={{width:32,height:32,borderRadius:"50%",background:senderColor,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:800,fontSize:13,flexShrink:0}}>
                  {avatarLabel}
                </div>
              )}
            </div>
          );
        })}
        {isClosed&&<div style={{textAlign:"center",padding:"8px 14px",background:"rgba(0,0,0,0.05)",borderRadius:12,fontSize:13,color:C.textDarkMut,fontWeight:600,margin:"4px 0"}}>— Conversation paused · tap Continue to resume —</div>}
        <div ref={bottomRef}/>
      </div>

      {imgB64&&(
        <div style={{padding:"6px 12px",background:"#fff",borderTop:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
          <img src={imgB64} alt="" style={{height:48,borderRadius:6,border:`1px solid ${C.border}`}}/>
          <button onClick={()=>setImgB64(null)} style={{color:C.red,background:"none",border:"none",cursor:"pointer",fontWeight:700}}>Remove</button>
        </div>
      )}
      {sendErr&&<div style={{padding:"6px 12px",background:"#FFEBEE",borderTop:`1px solid ${C.red}`,fontSize:12,color:C.red,fontWeight:700,flexShrink:0}}>{sendErr}</div>}

      {isClosed?(
        <div style={{padding:"14px",background:"#fff",borderTop:`1px solid ${C.border}`,flexShrink:0}}>
          <button onClick={newChat} style={{width:"100%",padding:"12px",background:C.blue,color:"#fff",border:"none",borderRadius:12,fontWeight:800,fontSize:14,cursor:"pointer"}}>Continue Conversation</button>
        </div>
      ):(
        <div style={{flexShrink:0}}>
          <div style={{padding:"2px 12px 0",background:"#fff",display:"flex",justifyContent:"flex-end"}}>
            <button onClick={endChat} style={{background:"none",border:"none",color:C.textDarkMut,fontSize:13,fontWeight:700,cursor:"pointer",padding:"4px 0"}}>End Conversation</button>
          </div>
          <div style={{padding:"6px 12px 10px",background:"#fff",borderTop:`1px solid ${C.border}`,display:"flex",gap:8,alignItems:"flex-end"}}>
            <input ref={fileRef} type="file" accept="image/*" style={{display:"none"}} onChange={async e=>{const f=e.target.files?.[0];if(f)setImgB64(await compressImg(f));}}/>
            <button onClick={()=>fileRef.current?.click()} style={{width:40,height:40,borderRadius:10,border:`1px solid ${C.border}`,background:"#F5F6F8",fontSize:18,cursor:"pointer",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>&#128247;</button>
            <textarea ref={inputRef} value={input} onChange={e=>setInput(e.target.value)}
              onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}}
              placeholder="Type a message... (Enter to send)" rows={2}
              style={{flex:1,padding:"9px 12px",borderRadius:10,border:`1.5px solid ${C.border}`,fontSize:14,fontFamily:"'Barlow',sans-serif",resize:"none",outline:"none",lineHeight:1.4}}
              onFocus={e=>e.target.style.borderColor=C.blue} onBlur={e=>e.target.style.borderColor=C.border}/>
            <button onClick={send} disabled={sending||(!input.trim()&&!imgB64)}
              style={{width:42,height:42,borderRadius:10,border:"none",background:sending||(!input.trim()&&!imgB64)?"#ccc":"linear-gradient(135deg,#E8962E,#E8962E)",color:"#fff",fontSize:20,cursor:"pointer",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
              {sending?"...":">"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SLTLogo({ size = 44 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Smartphone — bright cyan/white */}
      <rect x="44" y="10" width="28" height="44" rx="5" fill="#E8962E" opacity="0.15" stroke="#E8962E" strokeWidth="2"/>
      <rect x="47" y="15" width="22" height="30" rx="2.5" fill="#E8962E"/>
      {/* Phone screen glow */}
      <rect x="50" y="18" width="14" height="2" rx="1" fill="#fff" opacity="0.9"/>
      <rect x="50" y="22" width="16" height="1.5" rx="1" fill="#fff" opacity="0.6"/>
      <rect x="50" y="26" width="11" height="1.5" rx="1" fill="#fff" opacity="0.5"/>
      <rect x="50" y="30" width="13" height="1.5" rx="1" fill="#fff" opacity="0.4"/>
      {/* Phone home button */}
      <circle cx="58" cy="50" r="2.5" fill="#E8962E" opacity="0.9"/>
      {/* Truck body — bright orange/yellow */}
      <rect x="4" y="34" width="32" height="22" rx="4" fill="#E8962E"/>
      {/* Truck cab */}
      <path d="M26 34 L36 34 L36 46 Q36 50 32 50 L26 50 Z" fill="#FFD600"/>
      {/* Windshield */}
      <rect x="27.5" y="36.5" width="7" height="6" rx="1.5" fill="#1A1A1A" opacity="0.8"/>
      {/* Truck wheels */}
      <circle cx="13" cy="57" r="5.5" fill="#1A1A1A" stroke="#FFD600" strokeWidth="2"/>
      <circle cx="13" cy="57" r="2.2" fill="#FFD600"/>
      <circle cx="29" cy="57" r="5.5" fill="#1A1A1A" stroke="#FFD600" strokeWidth="2"/>
      <circle cx="29" cy="57" r="2.2" fill="#FFD600"/>
      {/* Speed lines */}
      <line x1="1" y1="40" x2="9" y2="40" stroke="#FFD600" strokeWidth="2.5" strokeLinecap="round" opacity="0.9"/>
      <line x1="1" y1="45" x2="7" y2="45" stroke="#E8962E" strokeWidth="1.8" strokeLinecap="round" opacity="0.7"/>
      <line x1="1" y1="50" x2="5" y2="50" stroke="#FFD600" strokeWidth="1.2" strokeLinecap="round" opacity="0.5"/>
      {/* WiFi/signal arc — truck to phone */}
      <path d="M37 28 Q32 18 24 26" stroke="#E8962E" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.9" strokeDasharray="3 2"/>
      <path d="M37 33 Q28 20 20 30" stroke="#E8962E" strokeWidth="1.3" fill="none" strokeLinecap="round" opacity="0.5" strokeDasharray="2 2"/>
      <circle cx="37" cy="28" r="2" fill="#E8962E"/>
    </svg>
  );
}

// ─── Design Tokens ────────────────────────────────────────────────────────────
const C = {
  navy:      "#1C2B4A",
  navyMid:   "#243655",
  blue:      "#2563EB",
  blueBright:"#3B82F6",
  blueLight: "#EFF6FF",
  teal:      "#0891B2",
  white:     "#FFFFFF",
  offWhite:  "#F5F6F8",
  border:    "#E2E2E2",
  textDark:  "#0B1120",
  textMed:   "#4B5563",
  textLight: "#6B7280",
  green:     "#16A34A",
  red:       "#DC2626",
  orange:    "#E8962E",
  purple:    "#7C3AED",
};

// ─── Global Styles ────────────────────────────────────────────────────────────
const GlobalCSS = ({ darkMode, timeTheme }) => {
  const isDark = darkMode; // Only manual dark toggle — no time-based dark switching
  const themes = {
    morning:   { bg:"#F5F6F8", body:"#1C1C1E", card:"#FFFFFF", nav:"#1C2B4A", accent:"#E8962E", page:"#F5F6F8", border:"rgba(0,0,0,.08)", input:"#FFFFFF", inputText:"#1C1C1E", inputBorder:"rgba(0,0,0,.15)" },
    afternoon: { bg:"#F5F6F8", body:"#1C1C1E", card:"#FFFFFF", nav:"#1C2B4A", accent:"#E8962E", page:"#F5F6F8", border:"rgba(0,0,0,.08)", input:"#FFFFFF", inputText:"#1C1C1E", inputBorder:"rgba(0,0,0,.15)" },
    evening:   { bg:"#F5F6F8", body:"#1C1C1E", card:"#FFFFFF", nav:"#1C2B4A", accent:"#E8962E", page:"#F5F6F8", border:"rgba(0,0,0,.08)", input:"#FFFFFF", inputText:"#1C1C1E", inputBorder:"rgba(0,0,0,.15)" },
    night:     { bg:"#F5F6F8", body:"#1C1C1E", card:"#FFFFFF", nav:"#1C2B4A", accent:"#E8962E", page:"#F5F6F8", border:"rgba(0,0,0,.08)", input:"#FFFFFF", inputText:"#1C1C1E", inputBorder:"rgba(0,0,0,.15)" },
  };
  const t = themes[timeTheme] || themes.afternoon;
  return (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@600;700;800;900&family=Barlow:wght@400;500;600;700&display=swap');
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html { margin: 0; padding: 0; width: 100%; max-width: 100%; overflow-x: hidden; -webkit-text-size-adjust: 100%; touch-action: manipulation; height: 100%; }
    body {
      margin: 0; padding: 0; width: 100%; max-width: 100vw; overflow-x: hidden;
      font-family: 'Barlow', sans-serif;
      background: ${t.bg} !important;
      color: ${t.body} !important;
      position: relative; -webkit-overflow-scrolling: touch;
      min-height: 100dvh; min-height: -webkit-fill-available;
    }
    /* ── Design Token Application ── */
    /* Light mode */
    .slt-page { min-height: 100vh; background: ${isDark ? "#1A1A1A" : "#F5F6F8"} !important; }
    .slt-card { background: ${isDark ? "#0D1523" : "#FFFFFF"} !important; border-color: ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)"} !important; color: ${isDark ? "#F0EDE8" : "#1A1A1A"} !important; }
    .slt-card-sm { background: ${isDark ? "#0D1523" : "#FFFFFF"} !important; border-color: ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)"} !important; }
    .slt-container { background: ${isDark ? "#070B14" : "#F5F6F8"} !important; }
    /* Hero: navy in light, deeper navy in dark */
    .slt-hero { background: ${isDark ? "linear-gradient(135deg,#0F2140 0%,#162B55 50%,#0F2140 100%)" : "linear-gradient(135deg,#1C2B4A,#243655)"} !important; border-bottom: ${isDark ? "1px solid rgba(255,255,255,0.08)" : "none"} !important; }
    .slt-hero-title { font-family: 'Barlow Condensed', sans-serif !important; font-weight: 900 !important; font-size: 22px !important; color: #FFFFFF !important; }
    .slt-hero-sub { font-size: 13px !important; color: rgba(255,255,255,0.6) !important; font-weight: 500 !important; }
    .slt-input { background: ${isDark ? "#111C2E" : "#FFFFFF"} !important; border-color: ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.15)"} !important; color: ${isDark ? "#F0EDE8" : "#1A1A1A"} !important; font-size: 16px !important; }
    .slt-label { color: ${isDark ? "rgba(240,237,232,0.55)" : "rgba(60,60,67,0.6)"} !important; font-size: 13px !important; font-weight: 600 !important; }
    /* Navbar — always navy in light, always near-black in dark */
    .slt-nav { background: ${isDark ? "#0B1120" : "#1C2B4A"} !important; border-bottom: 3px solid #E8962E !important; box-shadow: 0 4px 16px ${isDark ? "rgba(0,0,0,0.4)" : "rgba(28,43,74,0.25)"} !important; }
    /* Bottom tab bar */
    .slt-bottom-bar { background: ${isDark ? "#0B1120" : "#FFFFFF"} !important; border-color: ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"} !important; }
    .slt-btn-ghost { background: rgba(255,255,255,0.07) !important; color: ${isDark ? "#F0EDE8" : "#1A1A1A"} !important; border-color: ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.15)"} !important; }
    .slt-btn-primary { font-size: 15px !important; font-weight: 700 !important; }
    body.slt-dark .slt-btn-primary { background: #3B82F6 !important; box-shadow: 0 4px 16px rgba(59,130,246,0.35) !important; }
    body.slt-dark input, body.slt-dark select, body.slt-dark textarea { background: #111C2E !important; color: #F0F0F0 !important; border-color: rgba(59,130,246,0.2) !important; }
    body.slt-dark input::placeholder, body.slt-dark textarea::placeholder { color: rgba(255,255,255,0.25) !important; }
    .slt-btn-dark-text { color: #FFFFFF !important; font-weight: 800 !important; }
    /* Section title text in cards */
    .slt-card .slt-section-title, .slt-section-title { font-family: 'Barlow Condensed', sans-serif !important; font-weight: 800 !important; color: ${isDark ? "#F0EDE8" : "#1A1A1A"} !important; }
    /* Orange accent text */
    .slt-orange { color: #E8962E !important; }
    /* Filter / toggle rows */
    .slt-filter-btn { background: ${isDark ? "#0D1523" : "#FFFFFF"} !important; border-color: ${isDark ? "rgba(255,255,255,0.1)" : "#E2E2E2"} !important; color: ${isDark ? "rgba(255,255,255,0.5)" : "#4B5563"} !important; }
    .slt-filter-btn.active { background: ${isDark ? "#3B82F6" : "#1C2B4A"} !important; border-color: ${isDark ? "#3B82F6" : "#1C2B4A"} !important; color: #FFFFFF !important; }
    /* Report hero */
    .slt-report-hero { background: ${isDark ? "linear-gradient(135deg,#0F2140 0%,#162B55 50%,#0F2140 100%)" : "linear-gradient(135deg,#1C2B4A,#243655)"} !important; border: ${isDark ? "1px solid rgba(255,255,255,0.08)" : "none"} !important; }
    /* Analytics hero */
    .slt-analytics-hero { background: ${isDark ? "linear-gradient(135deg,#0F2140 0%,#162B55 50%,#0F2140 100%)" : "linear-gradient(135deg,#1C2B4A,#243655)"} !important; border: ${isDark ? "1px solid rgba(255,255,255,0.08)" : "none"} !important; }
    /* Payday card */
    .slt-payday-card { background: ${isDark ? "#0D1E3A" : "#1C2B4A"} !important; border: ${isDark ? "1px solid rgba(255,255,255,0.08)" : "none"} !important; }
    /* Invite card */
    .slt-invite-card { background: ${isDark ? "rgba(37,99,235,0.08)" : "rgba(37,99,235,0.06)"} !important; border-color: ${isDark ? "rgba(37,99,235,0.25)" : "rgba(37,99,235,0.2)"} !important; }
    /* Stat mini cards */
    .slt-stat-mini { background: ${isDark ? "#0D1523" : "#FFFFFF"} !important; border-color: ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)"} !important; }
    /* Text overrides for dark mode */
    body.slt-dark .slt-text-primary { color: #F0EDE8 !important; }
    body.slt-dark .slt-text-muted { color: rgba(240,237,232,0.45) !important; }
    body.slt-dark h1, body.slt-dark h2, body.slt-dark h3, body.slt-dark h4 { color: #F0EDE8 !important; }

    .slt-auth-bg { background: linear-gradient(160deg, #0d1525, #1A1A1A, #0a1220) !important; }
    /* Ensure all text is readable */
    .slt-page * { -webkit-font-smoothing: antialiased; }
    p, span, div, label, td, th, li { color: inherit; }
    h1,h2,h3,h4 { color: #1C1C1E; font-weight: 700; }
    #root { width: 100%; max-width: 100vw; overflow-x: hidden; position: relative; min-height: 100dvh; }
  `}</style>
  );
};

const StaticCSS = () => (
  <style>{`
    /* NAV */
    .slt-nav {
      background: #0B1120;
      height: 56px;
      display: flex;
      align-items: center;
      padding: 0 18px;
      position: sticky;
      top: 0;
      left: 0;
      right: 0;
      width: 100%;
      z-index: 200;
      box-shadow: 0 2px 20px rgba(0,0,0,0.5);
      border-bottom: 2px solid rgba(59,130,246,0.3);
      gap: 12px;
      box-sizing: border-box;
    }
    /* Live pulse animation */
    @keyframes tp-pulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.5; transform: scale(0.8); }
    }
    /* Skeleton loader */
    @keyframes tp-skeleton {
      0% { background-position: -200px 0; }
      100% { background-position: calc(200px + 100%) 0; }
    }
    .tp-skeleton {
      background: linear-gradient(90deg, rgba(255,255,255,0.04) 25%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.04) 75%);
      background-size: 400px 100%;
      animation: tp-skeleton 1.4s ease-in-out infinite;
      border-radius: 8px;
    }

    /* ── GLOBAL SCROLL LOCK ── */
    html { overscroll-behavior: none; }
    body { overscroll-behavior: none; touch-action: pan-y; }
    /* ── BOTTOM TAB BAR ── */
    .modal-open { overflow: hidden !important; position: fixed !important; width: 100% !important; }
    /* All fixed modals - prevent background scroll */
    [style*="position:fixed"][style*="zIndex"] > div,
    [style*="position: fixed"][style*="z-index"] > div {
      -webkit-overflow-scrolling: touch;
      overscroll-behavior: contain;
    }
    /* Desktop nav visible only on desktop */
    .slt-desktop-nav { display: flex !important; }
    /* Hide bottom tab bar on desktop */
    @media (min-width: 768px) {
      .slt-bottom-nav { display: none !important; }
    }
    /* Hide desktop nav on mobile */
    @media (max-width: 767px) {
      .slt-desktop-nav { display: none !important; }
    }
    .slt-bottom-nav {
      display: none;
    }
    @media (max-width: 640px) {
      .slt-bottom-nav {
        display: flex;
        align-items: center;
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 9000;
        background: #FFFFFF;
        border-top: 1px solid rgba(0,0,0,.08);
        padding: 4px 0;
        min-height: 56px;
        box-shadow: 0 -4px 16px rgba(0,0,0,0.4);
      }
      .slt-bottom-tab {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
        gap: 2px;
        padding: 4px 2px 4px;
        cursor: pointer;
        background: none;
        border: none;
        position: relative;
      }
      .slt-bottom-tab::before {
        content: '';
        display: block;
        height: 3px;
        width: 0;
        border-radius: 2px;
        background: #E8962E;
        transition: width 0.2s;
        margin-bottom: 2px;
      }
      .slt-bottom-tab.active::before {
        width: 22px;
      }
      .slt-bottom-tab-icon {
        font-size: 16px;
        line-height: 1;
        transition: transform 0.2s;
      }
      .slt-bottom-tab.active .slt-bottom-tab-icon {
        transform: scale(1.1);
        filter: drop-shadow(0 0 6px rgba(59,130,246,0.7));
      }
      .slt-bottom-tab-label {
        font-size: 9px;
        font-weight: 700;
        font-family: 'Barlow', sans-serif;
        color: rgba(255,255,255,0.3);
        transition: color 0.2s;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      .slt-bottom-tab.active .slt-bottom-tab-label {
        color: #3B82F6;
        font-weight: 800;
      }
      .slt-bottom-tab-badge {
        position: absolute;
        top: 0;
        right: calc(50% - 18px);
        background: #DC2626;
        color: #fff;
        border-radius: 10px;
        font-size: 9px;
        font-weight: 800;
        padding: 1px 5px;
        min-width: 16px;
        text-align: center;
      }
      /* Centre FAB wrapper */
      .slt-center-tab {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-end;
        align-self: stretch;
        padding-bottom: 4px;
        background: none;
        border: none;
        cursor: default;
        position: relative;
      }
      /* The big circle button */
      .slt-fab-btn {
        width: 42px;
        height: 42px;
        border-radius: 50%;
        background: linear-gradient(135deg, #1C2B4A, #243655);
        border: 2px solid #FFFFFF;
        box-shadow: 0 4px 18px rgba(28, 43, 74, 0.45);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        position: absolute;
        bottom: 4px;
        transition: transform 0.15s, box-shadow 0.15s;
      }
      .slt-fab-btn:active {
        transform: scale(0.92);
        box-shadow: 0 2px 10px rgba(28, 43, 74, 0.35);
      }
      /* Push content above bottom nav */
      .slt-page {
        padding-bottom: 80px !important;
      }
    }

    /* ── SKELETON LOADER ── */
    .slt-skeleton {
      background: linear-gradient(90deg, #E5E5EA 25%, #F2F2F7 50%, #E5E5EA 75%);
      background-size: 200% 100%;
      animation: slt-shimmer 1.5s infinite;
      border-radius: 8px;
    }
    @keyframes slt-shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }

    /* ── PAGE TRANSITIONS ── */
    .slt-page-enter {
      animation: slt-fade-in 0.25s ease;
    }
    @keyframes slt-fade-in {
      from { opacity: 0; transform: translateY(8px); }
      to { opacity: 1; transform: translateY(0); }
    }

    /* ══════════════════════════════════════════
       TP ANIMATION SYSTEM — navy light / amber dark
    ══════════════════════════════════════════ */
    :root { --tp-anim: #1C2B4A; }
    body.slt-dark { --tp-anim: #E8962E; }

    /* ── App entrance (post-login) ── */
    @keyframes tp-app-enter {
      from { opacity:0; transform:translateY(18px) scale(.98); }
      to   { opacity:1; transform:translateY(0)    scale(1);   }
    }
    .tp-app-enter { animation: tp-app-enter .55s cubic-bezier(.22,1,.36,1) both; }

    /* ── App exit (logout) ── */
    @keyframes tp-app-exit {
      from { opacity:1; transform:scale(1); }
      to   { opacity:0; transform:scale(.95); }
    }
    .tp-app-exit { animation: tp-app-exit .38s ease forwards; pointer-events:none; }

    /* ── Splash / boot ── */
    @keyframes tp-splash-ring {
      0%   { transform:scale(.4); opacity:.7; }
      100% { transform:scale(1.6); opacity:0; }
    }
    .tp-splash-ring { animation: tp-splash-ring 3s ease-out infinite; }
    .tp-splash-ring:nth-child(2) { animation-delay:.55s; }
    .tp-splash-ring:nth-child(3) { animation-delay:1.1s; }
    .tp-splash-ring:nth-child(4) { animation-delay:1.65s; }
    @keyframes tp-splash-glow {
      0%,100% { box-shadow:0 0 40px rgba(232,150,46,.4); }
      50%      { box-shadow:0 0 80px rgba(232,150,46,.8); }
    }
    .tp-splash-glow { animation: tp-splash-glow 2s ease-in-out infinite; }
    @keyframes tp-shimmer {
      0%   { background-position:200%; }
      100% { background-position:-200%; }
    }
    .tp-shimmer {
      background: linear-gradient(90deg,#E8962E,#fff,#E8962E);
      background-size: 200%;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      animation: tp-shimmer 3s linear infinite;
    }
    @keyframes tp-dot-wave {
      0%,80%,100% { transform:scale(.6); opacity:.4; }
      40%          { transform:scale(1.1); opacity:1; }
    }
    .tp-dot-wave span { display:inline-block; width:7px; height:7px; border-radius:50%; background:#E8962E; animation:tp-dot-wave 1.2s ease-in-out infinite; margin:0 3px; }
    .tp-dot-wave span:nth-child(2) { animation-delay:.15s; }
    .tp-dot-wave span:nth-child(3) { animation-delay:.3s; }

    /* ── Login logo pop ── */
    @keyframes tp-logo-pop {
      0%   { transform:scale(0) rotate(-15deg); opacity:0; }
      60%  { transform:scale(1.18) rotate(4deg); }
      100% { transform:scale(1) rotate(0deg); opacity:1; }
    }
    .tp-logo-pop { animation: tp-logo-pop .6s cubic-bezier(.34,1.56,.64,1) both; }

    /* ── Stagger slide-up ── */
    @keyframes tp-slide-up {
      from { opacity:0; transform:translateY(16px); }
      to   { opacity:1; transform:translateY(0); }
    }
    .tp-su-1 { animation: tp-slide-up .4s .28s ease both; }
    .tp-su-2 { animation: tp-slide-up .4s .40s ease both; }
    .tp-su-3 { animation: tp-slide-up .4s .52s ease both; }
    .tp-su-4 { animation: tp-slide-up .4s .64s ease both; }
    .tp-su-5 { animation: tp-slide-up .4s .76s ease both; }

    /* ── Wrong-password shake ── */
    @keyframes tp-shake {
      0%,100% { transform:translateX(0); }
      18%     { transform:translateX(-10px); }
      36%     { transform:translateX(10px); }
      54%     { transform:translateX(-7px); }
      72%     { transform:translateX(7px); }
      90%     { transform:translateX(-3px); }
    }
    .tp-shake { animation: tp-shake .5s cubic-bezier(.36,.07,.19,.97) both; }

    /* ── Tab bar — active icon spring-up ── */
    .slt-bottom-tab-icon {
      font-size: 20px;
      line-height: 1;
      transition: transform .28s cubic-bezier(.34,1.56,.64,1) !important;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 34px;
      height: 28px;
      border-radius: 8px;
      background: transparent;
      transition: transform .28s cubic-bezier(.34,1.56,.64,1), background .2s !important;
    }
    .slt-bottom-tab.active .slt-bottom-tab-icon {
      transform: translateY(-4px) scale(1.2) !important;
      background: rgba(28,43,74,.12) !important;
    }
    body.slt-dark .slt-bottom-tab.active .slt-bottom-tab-icon {
      background: rgba(232,150,46,.18) !important;
    }
    /* Active indicator uses --tp-anim */
    .slt-bottom-tab::before {
      background: var(--tp-anim) !important;
    }
    .slt-bottom-tab.active .slt-bottom-tab-label {
      color: var(--tp-anim) !important;
    }
    /* ── Sign Out Push Button ── */
    .tp-signout-push {
      display: block;
      width: 100%;
      padding: 14px;
      margin-top: 4px;
      border-radius: 14px;
      background: #DC2626;
      color: #fff;
      font-family: 'Barlow', sans-serif;
      font-size: 15px;
      font-weight: 800;
      letter-spacing: .04em;
      border: none;
      cursor: pointer;
      position: relative;
      box-shadow: 0 6px 0 #7f1d1d;
      top: 0;
      transition: top .1s, box-shadow .1s;
      overflow: hidden;
    }
    .tp-signout-push:active {
      top: 4px;
      box-shadow: 0 2px 0 #7f1d1d;
    }
    /* dot appears beneath active tab */
    .slt-bottom-tab.active::after {
      content: '';
      position: absolute;
      bottom: 2px;
      left: 50%;
      transform: translateX(-50%);
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: var(--tp-anim);
      animation: tp-dot-appear .3s cubic-bezier(.34,1.56,.64,1) both;
    }
    @keyframes tp-dot-appear {
      from { width:0; height:0; opacity:0; }
      to   { width:5px; height:5px; opacity:1; }
    }

    /* ── Bell / icon shake ── */
    @keyframes tp-bell-ring {
      0%,100% { transform:rotate(0) scale(1); }
      15%     { transform:rotate(-16deg) scale(1.22); }
      30%     { transform:rotate(16deg) scale(1.22); }
      45%     { transform:rotate(-10deg) scale(1.1); }
      60%     { transform:rotate(10deg) scale(1.1); }
      75%     { transform:rotate(-5deg); }
      90%     { transform:rotate(5deg); }
    }
    .tp-bell-ring { animation: tp-bell-ring .7s ease-in-out; }

    /* ── FAB spin on press ── */
    .slt-fab-btn {
      transition: transform .22s cubic-bezier(.34,1.56,.64,1), box-shadow .2s !important;
    }
    .slt-fab-btn:active {
      transform: scale(.9) rotate(90deg) !important;
    }

    /* ── Card hover lift ── */
    .slt-load-card {
      transition: transform .22s cubic-bezier(.34,1.56,.64,1), box-shadow .22s !important;
    }
    .slt-load-card:hover {
      transform: translateY(-4px) scale(1.012) !important;
      box-shadow: 0 14px 36px rgba(28,43,74,.14) !important;
    }
    .slt-load-card:active { transform: scale(.97) !important; }
    .slt-card {
      border-radius: 20px !important;
      transition: transform .22s cubic-bezier(.34,1.56,.64,1), box-shadow .22s, border-color .2s !important;
    }
    .slt-card:hover {
      transform: translateY(-3px) !important;
      box-shadow: 0 10px 28px rgba(28,43,74,.1) !important;
    }

    /* ── Card stagger entrance ── */
    @keyframes tp-card-enter {
      from { opacity:0; transform:translateY(22px); }
      to   { opacity:1; transform:translateY(0); }
    }
    .tp-stagger > * { animation: tp-card-enter .38s ease both; }
    .tp-stagger > *:nth-child(1) { animation-delay:.04s; }
    .tp-stagger > *:nth-child(2) { animation-delay:.11s; }
    .tp-stagger > *:nth-child(3) { animation-delay:.18s; }
    .tp-stagger > *:nth-child(4) { animation-delay:.25s; }
    .tp-stagger > *:nth-child(5) { animation-delay:.32s; }
    .tp-stagger > *:nth-child(6) { animation-delay:.39s; }

    /* ── Button ripple ── */
    .tp-ripple-wrap { position:relative; overflow:hidden; }
    .tp-ripple {
      position: absolute;
      border-radius: 50%;
      background: rgba(255,255,255,.28);
      transform: scale(0);
      animation: tp-ripple-go .65s linear;
      pointer-events: none;
    }
    @keyframes tp-ripple-go { to { transform:scale(5); opacity:0; } }

    /* ── Toast notifications ── */
    @keyframes tp-toast-in {
      from { transform:translateY(90px) scale(.95); opacity:0; }
      to   { transform:translateY(0)    scale(1);   opacity:1; }
    }
    @keyframes tp-toast-out {
      from { transform:translateY(0)    scale(1);   opacity:1; }
      to   { transform:translateY(90px) scale(.95); opacity:0; }
    }
    .tp-toast     { animation: tp-toast-in  .38s cubic-bezier(.34,1.56,.64,1) both; }
    .tp-toast-out { animation: tp-toast-out .28s ease forwards; }

    /* ── KPI pop-in ── */
    @keyframes tp-kpi-pop {
      from { opacity:0; transform:translateY(10px) scale(.92); }
      to   { opacity:1; transform:translateY(0)    scale(1); }
    }
    .tp-kpi-pop { animation: tp-kpi-pop .45s cubic-bezier(.34,1.56,.64,1) both; }

    /* dark overrides so var() also works where we override explicitly */
    body.slt-dark .slt-bottom-tab.active .slt-bottom-tab-label { color: #3B82F6 !important; }
    body.slt-dark .slt-bottom-tab::before { background: #3B82F6 !important; }

    /* ── SWIPE LOAD CARD ── */
    .slt-swipeable {
      position: relative;
      overflow: hidden;
      touch-action: pan-y;
    }
    .slt-swipe-hint {
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;
      width: 80px;
      background: linear-gradient(135deg, #43A047, #2E7D32);
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-size: 24px;
      border-radius: 0 12px 12px 0;
      transform: translateX(100%);
      transition: transform 0.3s;
    }



    /* ══════════════════════════════════════════════════════════
       DARK MODE — Deep Blue Steel (Option A)
       ══════════════════════════════════════════════════════════ */
    body.slt-dark {
      background: #070B14 !important;
      color: #F0F0F0 !important;
    }

    /* ── Page & container ── */
    body.slt-dark .slt-page,
    body.slt-dark .slt-container { background: #070B14 !important; color: #F0F0F0 !important; }

    /* ── Cards ── */
    body.slt-dark .slt-card,
    body.slt-dark .slt-card-sm,
    body.slt-dark .slt-auth-card,
    body.slt-dark .slt-load-card {
      background: #0D1523 !important;
      border-color: rgba(59,130,246,0.12) !important;
      color: #F0F0F0 !important;
    }
    body.slt-dark .slt-card:hover { box-shadow: 0 10px 32px rgba(59,130,246,0.12) !important; border-color: rgba(59,130,246,0.22) !important; }

    /* ── Nav ── */
    body.slt-dark .slt-nav { background: #0B1120 !important; border-bottom: 2px solid rgba(59,130,246,0.3) !important; }

    /* ── Bottom bar ── */
    body.slt-dark .slt-bottom-nav,
    body.slt-dark .slt-bottom-bar { background: #0B1120 !important; border-top: 1px solid rgba(59,130,246,0.15) !important; }
    body.slt-dark .slt-bottom-tab-label { color: rgba(255,255,255,0.3) !important; }
    body.slt-dark .slt-bottom-tab.active .slt-bottom-tab-label { color: #3B82F6 !important; }
    body.slt-dark .slt-bottom-tab::before { background: #3B82F6 !important; }
    body.slt-dark .slt-bottom-tab.active .slt-bottom-tab-icon { filter: drop-shadow(0 0 6px rgba(59,130,246,0.7)) !important; }
    body.slt-dark .slt-fab-btn { background: linear-gradient(135deg,#E8962E,#F5B660) !important; border-color: #0B1120 !important; box-shadow: 0 4px 18px rgba(232,150,46,0.45) !important; }
    body.slt-dark .slt-fab-btn .slt-fab-plus { color: #0B1120 !important; }

    /* ── Inputs & selects ── */
    body.slt-dark .slt-input,
    body.slt-dark input:not([type="range"]),
    body.slt-dark textarea,
    body.slt-dark select { background: #111C2E !important; border-color: rgba(59,130,246,0.2) !important; color: #F0F0F0 !important; }
    body.slt-dark select option { background: #111C2E; color: #F0F0F0; }
    body.slt-dark input::placeholder, body.slt-dark textarea::placeholder { color: rgba(255,255,255,0.25) !important; }
    body.slt-dark .slt-label { color: rgba(255,255,255,0.4) !important; }

    /* ── Hero ── */
    body.slt-dark .slt-hero {
      background: linear-gradient(135deg,#0F2140 0%,#162B55 50%,#0F2140 100%) !important;
      border-bottom: 1px solid rgba(59,130,246,0.2) !important;
    }
    body.slt-dark .slt-hero-title { color: #FFFFFF !important; }
    body.slt-dark .slt-hero-sub { color: rgba(255,255,255,0.5) !important; }

    /* ── Badges ── */
    body.slt-dark .slt-badge-orange { background: rgba(232,150,46,.15) !important; color: #E8962E !important; }
    body.slt-dark .slt-badge-green  { background: rgba(34,197,94,.15) !important; color: #4ade80 !important; }
    body.slt-dark .slt-badge-red    { background: rgba(239,68,68,.15) !important; color: #f87171 !important; }

    /* ── Buttons ── */
    body.slt-dark .slt-btn-ghost,
    body.slt-dark .slt-btn-secondary { background: rgba(255,255,255,0.06) !important; color: #F0F0F0 !important; border-color: rgba(255,255,255,0.12) !important; }
    body.slt-dark .slt-btn-primary { background: #3B82F6 !important; box-shadow: 0 4px 16px rgba(59,130,246,0.35) !important; }

    /* ── Dividers ── */
    body.slt-dark .slt-divider { border-color: rgba(59,130,246,0.1) !important; }

    /* ════════════════════════════════════════════════════════
       INLINE STYLE OVERRIDES
       These beat hardcoded light colors in component JSX
       ════════════════════════════════════════════════════════ */

    /* White / near-white → deep card */
    body.slt-dark [style*="background:#fff"],
    body.slt-dark [style*="background:#FFF"],
    body.slt-dark [style*="background:#FFFFFF"],
    body.slt-dark [style*="background: #fff"],
    body.slt-dark [style*="background: #FFFFFF"],
    body.slt-dark [style*="background:white"],
    body.slt-dark [style*="background: white"] { background: #0D1523 !important; color: #F0F0F0 !important; }

    /* Light grey page bg → deep page bg */
    body.slt-dark [style*="background:#F5F6F8"],
    body.slt-dark [style*="background: #F5F6F8"],
    body.slt-dark [style*="background:#F2F2F7"],
    body.slt-dark [style*="background:#F8FAFC"],
    body.slt-dark [style*="background:#F5F7FA"],
    body.slt-dark [style*="background:#F3F4F6"],
    body.slt-dark [style*="background:#EEEEEE"],
    body.slt-dark [style*="background:#f8faff"],
    body.slt-dark [style*="background:#EEF2FB"],
    body.slt-dark [style*="background:#F9FAFB"],
    body.slt-dark [style*="background:#F1F5F9"],
    body.slt-dark [style*="background:#F0F4FF"] { background: #0D1523 !important; color: #F0F0F0 !important; }

    /* Warm amber backgrounds → dark amber tint */
    body.slt-dark [style*="background:#FFF3EB"],
    body.slt-dark [style*="background:#FFF3E0"],
    body.slt-dark [style*="background:#FFF8E1"],
    body.slt-dark [style*="background:#FFFBF0"],
    body.slt-dark [style*="background:#FEF9C3"] { background: rgba(232,150,46,0.08) !important; }

    /* Light red → dark red tint */
    body.slt-dark [style*="background:#FFF8F8"],
    body.slt-dark [style*="background:#fee2e2"],
    body.slt-dark [style*="background:#FEE2E2"],
    body.slt-dark [style*="background:#FFEBEE"],
    body.slt-dark [style*="background:#fff5f5"],
    body.slt-dark [style*="background:#FFF5F5"],
    body.slt-dark [style*="background:#FFF0F0"] { background: rgba(239,68,68,0.08) !important; }

    /* Light green → dark green tint */
    body.slt-dark [style*="background:#E8F5E9"],
    body.slt-dark [style*="background:#F0FDF4"],
    body.slt-dark [style*="background:#dcfce7"],
    body.slt-dark [style*="background:#DCFCE7"] { background: rgba(34,197,94,0.08) !important; }

    /* Light blue → dark blue tint */
    body.slt-dark [style*="background:#e8f0fe"],
    body.slt-dark [style*="background:#EFF6FF"],
    body.slt-dark [style*="background:#dbeafe"],
    body.slt-dark [style*="background:#F0F4FF"] { background: rgba(59,130,246,0.08) !important; }

    /* Dark text on light bg → white */
    body.slt-dark [style*="color:#1A1A1A"],
    body.slt-dark [style*="color:#1a1a1a"],
    body.slt-dark [style*="color:#111827"],
    body.slt-dark [style*="color:#0A1628"],
    body.slt-dark [style*="color:#374151"],
    body.slt-dark [style*="color:#1C1C1E"],
    body.slt-dark [style*="color:#1a2a3a"] { color: #F0F0F0 !important; }

    body.slt-dark [style*="color:#4B5563"],
    body.slt-dark [style*="color:#6B7280"],
    body.slt-dark [style*="color:#9CA3AF"] { color: rgba(255,255,255,0.5) !important; }

    /* Keep brand/status colors as-is */
    body.slt-dark [style*="color:#E8962E"] { color: #E8962E !important; }
    body.slt-dark [style*="color:#22C55E"],
    body.slt-dark [style*="color:#22c55e"] { color: #22C55E !important; }
    body.slt-dark [style*="color:#EF4444"],
    body.slt-dark [style*="color:#ef4444"] { color: #EF4444 !important; }
    body.slt-dark [style*="color:#3B82F6"] { color: #3B82F6 !important; }


    @keyframes slt-star-twinkle {
      0%, 100% { opacity: 0.2; transform: scale(0.8); }
      50%       { opacity: 1;   transform: scale(1.2); }
    }
    @keyframes slt-dot-bounce {
      0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
      40%           { transform: scale(1);   opacity: 1;   }
    }
    /* ── HERO: light theme ── */
    .slt-hero {
      background: #F5F6F8;
      animation: none;
    }

    /* ── GLOWING NUMBERS ── */
    .slt-glow-green {
      color: #69F0AE;
      text-shadow: 0 0 20px rgba(105,240,174,0.6), 0 0 40px rgba(105,240,174,0.3);
    }
    .slt-glow-blue {
      color: #E8962E;
      text-shadow: 0 0 20px rgba(64,196,255,0.6), 0 0 40px rgba(64,196,255,0.3);
    }

    /* ── GRADIENT STAT CARDS ── */
    .slt-stat-card {
      border-radius: 16px;
      padding: 18px 16px;
      position: relative;
      overflow: hidden;
      cursor: pointer;
      transition: transform 0.2s, box-shadow 0.2s;
    }
    .slt-stat-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.55); }
    .slt-stat-card::before {
      content: '';
      position: absolute;
      top: -30%;
      right: -10%;
      width: 80px;
      height: 80px;
      border-radius: 50%;
      background: rgba(255,255,255,0.08);
    }
    .slt-stat-card::after {
      content: '';
      position: absolute;
      bottom: -20%;
      left: -5%;
      width: 60px;
      height: 60px;
      border-radius: 50%;
      background: rgba(255,255,255,0.05);
    }

    /* ── CONFETTI ── */
    .slt-confetti-piece {
      position: fixed;
      width: 10px;
      height: 10px;
      border-radius: 2px;
      animation: slt-confetti-fall 1.2s ease-out forwards;
      z-index: 99999;
      pointer-events: none;
    }
    @keyframes slt-confetti-fall {
      0%   { transform: translateY(-20px) rotate(0deg); opacity: 1; }
      100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
    }

    /* ── BUTTON RIPPLE ── */
    .slt-ripple {
      position: relative;
      overflow: hidden;
    }
    .slt-ripple::after {
      content: '';
      position: absolute;
      border-radius: 50%;
      background: rgba(255,255,255,0.3);
      width: 100px;
      height: 100px;
      margin-top: -50px;
      margin-left: -50px;
      top: 50%;
      left: 50%;
      animation: slt-ripple-anim 0.6s linear;
      opacity: 0;
    }
    @keyframes slt-ripple-anim {
      0%   { transform: scale(0); opacity: 0.5; }
      100% { transform: scale(4); opacity: 0; }
    }

    /* ── CARD SLIDE IN ── */
    .slt-slide-in {
      animation: slt-slide-up 0.3s ease both;
    }
    @keyframes slt-slide-up {
      from { opacity: 0; transform: translateY(16px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .slt-slide-in:nth-child(1) { animation-delay: 0.05s; }
    .slt-slide-in:nth-child(2) { animation-delay: 0.1s; }
    .slt-slide-in:nth-child(3) { animation-delay: 0.15s; }
    .slt-slide-in:nth-child(4) { animation-delay: 0.2s; }
    .slt-slide-in:nth-child(5) { animation-delay: 0.25s; }

    /* ── PULSE LOG LOAD BUTTON ── */
    .slt-pulse-btn {
      animation: slt-pulse-btn 2.5s infinite;
    }
    @keyframes slt-pulse-btn {
      0%, 100% { box-shadow: 0 4px 20px rgba(0,188,212,0.5); }
      50%       { box-shadow: 0 4px 32px rgba(0,188,212,0.85), 0 0 0 8px rgba(0,188,212,0.15); }
    }

    /* ── GLASSMORPHISM CARD ── */
    .slt-glass {
      background: rgba(255,255,255,0.12);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border: 1px solid rgba(255,255,255,0.2);
      border-radius: 16px;
    }

    /* ── COUNT UP NUMBER ── */
    .slt-countup {
      font-variant-numeric: tabular-nums;
      transition: all 0.05s;
    }
    /* Safe area spacer sits above the nav on notched iPhones */
    .slt-nav-safe {
      background: #1C2B4A;
      height: env(safe-area-inset-top, 0px);
      position: sticky;
      top: 0;
      left: 0;
      right: 0;
      width: 100%;
      z-index: 201;
    }
    .slt-logo-area {
      display: flex;
      align-items: center;
      gap: 12px;
      text-decoration: none;
      flex-shrink: 0;
    }
    .slt-brand {
      display: flex;
      flex-direction: column;
      line-height: 1.1;
    }
    .slt-brand-main {
      font-family: 'Barlow Condensed', sans-serif;
      font-size: 18px;
      font-weight: 800;
      color: #fff;
      letter-spacing: -0.3px;
    }
    .slt-brand-sub {
      font-family: 'Barlow', sans-serif;
      font-size: 10px;
      font-weight: 600;
      color: ${C.teal};
      letter-spacing: 1.5px;
      text-transform: uppercase;
    }

    /* MENU TRIGGER */
    .slt-menu-trigger {
      display: flex;
      align-items: center;
      gap: 8px;
      background: rgba(255,255,255,0.06);
      border: 1px solid rgba(255,255,255,0.12);
      border-radius: 10px;
      padding: 8px 16px;
      cursor: pointer;
      color: rgba(255,255,255,0.9);
      font-family: 'Barlow', sans-serif;
      font-size: 13.5px;
      font-weight: 600;
      letter-spacing: 0.2px;
      transition: all 0.2s;
      flex-shrink: 0;
    }
    .slt-menu-trigger:hover {
      background: rgba(255,255,255,0.12);
      border-color: ${C.teal};
      color: #fff;
    }
    .slt-menu-trigger.open {
      background: ${C.teal}22;
      border-color: ${C.teal};
      color: ${C.teal};
    }
    .slt-menu-chevron {
      transition: transform 0.25s ease;
      opacity: 0.7;
    }
    .slt-menu-chevron.open { transform: rotate(180deg); }

    /* ACTIVE TAB PILL */
    @media (max-width: 600px) {
      .slt-msg-grid { grid-template-columns: 1fr !important; }
    }
    .truckpilot-chat-fab {
      position: fixed;
      bottom: 16px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 8888;
      display: flex;
      align-items: center;
      gap: 10px;
      background: linear-gradient(135deg, #E8962E, #E8962E, #E8962E);
      color: #fff;
      border: 2px solid rgba(255,255,255,0.3);
      border-radius: 50px;
      padding: 13px 22px 13px 16px;
      cursor: pointer;
      box-shadow: 0 4px 24px rgba(156,39,176,0.7), 0 0 0 0 rgba(156,39,176,0.4), 0 0 40px rgba(224,64,251,0.3);
      animation: truckpilot-pulse 2.2s infinite;
      font-family: 'Barlow', sans-serif;
      font-weight: 800;
      font-size: 14px;
      letter-spacing: 0.2px;
      transition: box-shadow 0.15s;
      text-decoration: none;
      white-space: nowrap;
    }
    .truckpilot-chat-fab:hover {
      box-shadow: 0 6px 32px rgba(156,39,176,0.9), 0 0 60px rgba(224,64,251,0.5);
    }
    .truckpilot-chat-fab:active { transform: translateX(-50%) scale(0.97); }
    .truckpilot-chat-fab .fab-icon {
      width: 30px; height: 30px;
      background: rgba(255,255,255,0.2);
      border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      font-size: 16px;
      flex-shrink: 0;
    }
    .truckpilot-chat-fab .fab-dot {
      width: 9px; height: 9px;
      background: #69F0AE;
      border-radius: 50%;
      position: absolute;
      top: 8px; right: 12px;
      border: 2px solid #fff;
      animation: truckpilot-blink 1.4s infinite;
    }
    .truckpilot-chat-fab .fab-label { line-height: 1.2; }
    .truckpilot-chat-fab .fab-sub { font-size: 10px; font-weight: 600; opacity: 0.85; display: block; }
    @keyframes truckpilot-pulse {
      0%   { box-shadow: 0 4px 24px rgba(156,39,176,0.7), 0 0 0 0 rgba(156,39,176,0.4); }
      60%  { box-shadow: 0 4px 24px rgba(156,39,176,0.7), 0 0 0 16px rgba(156,39,176,0); }
      100% { box-shadow: 0 4px 24px rgba(156,39,176,0.7), 0 0 0 0 rgba(156,39,176,0); }
    }
    @keyframes truckpilot-blink {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.3; }
    }
    @media (max-width: 480px) {
      .truckpilot-chat-fab { bottom: 12px; font-size: 13px; padding: 11px 18px 11px 13px; }
      .truckpilot-chat-fab .fab-icon { width: 26px; height: 26px; font-size: 14px; }
    }
    .slt-active-pill {
      display: flex;
      align-items: center;
      gap: 6px;
      background: ${C.teal}20;
      border: 1px solid ${C.teal}50;
      border-radius: 20px;
      padding: 5px 14px;
      font-size: 12.5px;
      font-weight: 700;
      color: ${C.teal};
      font-family: 'Barlow', sans-serif;
    }

    /* DROPDOWN PANEL */
    .slt-dropdown-overlay {
      position: fixed;
      inset: 0;
      z-index: 290;
    }
    .slt-dropdown {
      position: absolute;
      top: calc(100% + 8px);
      left: 0;
      width: 360px;
      background: ${C.navyMid};
      border: 1px solid rgba(255,255,255,0.1);
      border-radius: 16px;
      box-shadow: 0 24px 60px rgba(0,0,0,0.5);
      overflow: hidden;
      animation: dropIn 0.22s cubic-bezier(0.34,1.56,0.64,1) forwards;
      z-index: 300;
    }
    @keyframes dropIn {
      from { opacity: 0; transform: translateY(-10px) scale(0.97); }
      to   { opacity: 1; transform: translateY(0) scale(1); }
    }
    .slt-dropdown-header {
      padding: 16px 20px 12px;
      border-bottom: 1px solid rgba(255,255,255,0.07);
      font-family: 'Barlow Condensed', sans-serif;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 1.8px;
      text-transform: uppercase;
      color: ${C.textLight};
    }
    .slt-dropdown-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 6px;
      padding: 12px;
    }
    .slt-menu-item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px 14px;
      border-radius: 10px;
      cursor: pointer;
      background: transparent;
      border: 1px solid transparent;
      transition: all 0.16s;
      font-family: 'Barlow', sans-serif;
      color: rgba(255,255,255,0.75);
      font-size: 13.5px;
      font-weight: 600;
      position: relative;
    }
    .slt-menu-item:hover {
      background: rgba(255,255,255,0.06);
      border-color: rgba(255,255,255,0.1);
      color: #fff;
    }
    .slt-menu-item.active {
      background: linear-gradient(135deg, ${C.teal}25, ${C.blue}20);
      border-color: ${C.teal}50;
      color: ${C.teal};
    }
    .slt-menu-item.active .slt-item-icon {
      filter: drop-shadow(0 0 6px ${C.teal}80);
    }
    .slt-item-icon { font-size: 18px; flex-shrink: 0; }
    .slt-item-label { font-size: 13px; font-weight: 600; }
    .slt-item-badge {
      position: absolute;
      top: 6px;
      right: 6px;
      background: ${C.red};
      color: #fff;
      border-radius: 50%;
      width: 16px;
      height: 16px;
      font-size: 9px;
      font-weight: 800;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .slt-dropdown-footer {
      padding: 10px 12px 12px;
      border-top: 1px solid rgba(255,255,255,0.07);
      display: flex;
      gap: 8px;
    }

    /* RIGHT SIDE NAV */
    .slt-nav-right {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-left: auto;
    }
    .slt-user-chip {
      display: none;
      display: flex;
      align-items: center;
      gap: 8px;
      background: rgba(255,255,255,0.06);
      border: 1px solid rgba(255,255,255,0.1);
      border-radius: 24px;
      padding: 5px 14px 5px 8px;
    }
    .slt-user-avatar {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      background: linear-gradient(135deg, ${C.teal}, ${C.blue});
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      font-weight: 800;
      color: #fff;
      font-family: 'Barlow Condensed', sans-serif;
      flex-shrink: 0;
    }
    .slt-user-name { font-size: 13px; font-weight: 700; color: #fff; font-family: 'Barlow', sans-serif; }
    .slt-user-role { font-size: 10px; color: ${C.textLight}; font-family: 'Barlow', sans-serif; }
    .slt-settings-btn {
      background: rgba(255,255,255,0.06);
      border: 1px solid rgba(255,255,255,0.1);
      border-radius: 8px;
      padding: 7px 12px;
      color: rgba(255,255,255,0.75);
      font-size: 13px;
      cursor: pointer;
      font-family: 'Barlow', sans-serif;
      font-weight: 600;
      transition: all 0.15s;
    }
    .slt-settings-btn:hover { background: rgba(255,255,255,0.12); color: #fff; }
    .slt-logout-btn {
      display: none;
      background: rgba(229,57,53,0.12);
      border: 1px solid rgba(229,57,53,0.3);
      border-radius: 8px;
      padding: 7px 12px;
      color: #ff8a80;
      font-size: 13px;
      cursor: pointer;
      font-family: 'Barlow', sans-serif;
      font-weight: 700;
      transition: all 0.15s;
      flex-shrink: 0;
    }
    .slt-logout-btn:hover { background: rgba(229,57,53,0.22); }
    .slt-logout-icon { display: none; }
    @media (max-width: 640px) {
      .slt-logout-text { display: none; }
      .slt-logout-icon { display: inline; font-size: 16px; }
      .slt-logout-btn { padding: 8px 10px; }
    }

    /* PAGE SHELLS */
    .slt-page { min-height: 100vh; background: #070B14; color: #F0F0F0; }
    /* ── HERO: Deep blue gradient with radial glow ── */
    .slt-hero {
      background: linear-gradient(135deg, #0F2140 0%, #162B55 50%, #0F2140 100%);
      padding: 22px 18px 20px;
      position: relative;
      overflow: hidden;
      border-bottom: 1px solid rgba(59,130,246,0.2);
    }
    .slt-hero::before {
      content: '';
      position: absolute;
      top: -60px;
      right: -60px;
      width: 200px;
      height: 200px;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 70%);
      pointer-events: none;
    }
    .slt-hero::after {
      content: '';
      position: absolute;
      bottom: -40px;
      left: -40px;
      width: 140px;
      height: 140px;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(232,150,46,0.07) 0%, transparent 70%);
      pointer-events: none;
    }
    .slt-hero-title {
      font-family: 'Barlow Condensed', sans-serif;
      font-size: 24px;
      font-weight: 900;
      color: #FFFFFF;
      margin-bottom: 4px;
      letter-spacing: -0.3px;
      position: relative;
    }
    .slt-hero-sub {
      font-size: 13px;
      color: rgba(255,255,255,0.5);
      position: relative;
      font-weight: 500;
    }
    /* Back button inside hero */
    .slt-hero-back {
      font-size: 13px;
      font-weight: 700;
      color: #E8962E;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 3px;
      margin-bottom: 10px;
      background: none;
      border: none;
      padding: 0;
    }
    .slt-page { min-height: 100vh; background: #F5F6F8; width: 100%; overflow-x: hidden; }
    .slt-container { max-width: 980px; margin: 0 auto; padding: 32px 20px 64px; width: 100%; }
    .slt-container-sm { max-width: 600px; margin: 0 auto; padding: 32px 20px 64px; width: 100%; }

    /* CARDS */
    .slt-card {
      background: ${C.white};
      border-radius: 14px;
      padding: 26px;
      box-shadow: 0 1px 6px rgba(10,22,40,0.07);
      border: 1px solid ${C.border};
      margin-bottom: 18px;
    }
    .slt-card-sm {
      background: ${C.white};
      border-radius: 12px;
      padding: 18px;
      box-shadow: 0 1px 4px rgba(10,22,40,0.06);
      border: 1px solid ${C.border};
    }

    /* STAT CARDS */
    .slt-stat {
      background: ${C.white};
      border-radius: 14px;
      padding: 20px 22px;
      border: 1px solid ${C.border};
      box-shadow: 0 1px 6px rgba(10,22,40,0.06);
      transition: all 0.2s;
    }
    .slt-stat:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(30,136,229,0.12); }

    /* INPUTS */
    .slt-input {
      width: 100%;
      padding: 13px 16px;
      border: 1.5px solid ${C.border};
      border-radius: 12px;
      font-size: 15px;
      color: ${C.textDark};
      background: ${C.white};
      outline: none;
      font-family: 'Barlow', sans-serif;
      transition: border-color 0.2s, box-shadow 0.2s;
      min-height: 48px;
      box-sizing: border-box;
    }
    .slt-input:focus { border-color: ${C.blue}; box-shadow: 0 0 0 3px ${C.blue}18; }
    .ld-input:focus { border-color: #E8962E !important; box-shadow: 0 0 0 3px rgba(232,150,46,0.18) !important; outline: none; }

    /* BUTTONS */
    .slt-btn-primary {
      background: linear-gradient(135deg, #1C2B4A, #243655);
      color: #fff;
      border: none;
      border-radius: 12px;
      padding: 14px 24px;
      font-size: 15px;
      font-weight: 800;
      cursor: pointer;
      font-family: 'Barlow', sans-serif;
      transition: all 0.2s;
      letter-spacing: 0.2px;
      min-height: 48px;
    }
    .slt-btn-primary:hover { opacity: 0.92; transform: translateY(-1px); box-shadow: 0 4px 16px rgba(30,136,229,0.4); }
    .slt-btn-secondary {
      background: ${C.white};
      color: ${C.blue};
      border: 1.5px solid ${C.blue};
      border-radius: 12px;
      padding: 12px 20px;
      font-size: 14px;
      font-weight: 700;
      cursor: pointer;
      font-family: 'Barlow', sans-serif;
      transition: all 0.18s;
      min-height: 44px;
    }
    .slt-btn-secondary:hover { background: ${C.blueLight}; }
    .slt-btn-danger {
      background: ${C.white};
      color: ${C.red};
      border: 1.5px solid ${C.red};
      border-radius: 12px;
      padding: 10px 16px;
      font-size: 13px;
      font-weight: 700;
      cursor: pointer;
      font-family: 'Barlow', sans-serif;
      min-height: 44px;
    }
    .slt-btn-ghost {
      background: transparent;
      color: ${C.textMed};
      border: 1.5px solid ${C.border};
      border-radius: 9px;
      padding: 8px 14px;
      font-size: 13px;
      cursor: pointer;
      font-family: 'Barlow', sans-serif;
    }
    .slt-btn-complete {
      background: linear-gradient(135deg, ${C.green}, #E8962E);
      color: #fff;
      border: none;
      border-radius: 8px;
      padding: 8px 14px;
      font-size: 12.5px;
      font-weight: 800;
      cursor: pointer;
      font-family: 'Barlow', sans-serif;
      letter-spacing: 0.2px;
      transition: all 0.18s;
    }
    .slt-btn-complete:hover { opacity: 0.88; transform: scale(1.03); }
    .slt-btn-reopen {
      background: ${C.orange}18;
      color: ${C.orange};
      border: 1.5px solid ${C.orange}60;
      border-radius: 8px;
      padding: 6px 12px;
      font-size: 12px;
      font-weight: 700;
      cursor: pointer;
      font-family: 'Barlow', sans-serif;
    }

    /* LABEL */
    .slt-label { display: block; font-size: 12.5px; font-weight: 700; color: ${C.textMed}; margin-bottom: 5px; font-family: 'Barlow', sans-serif; letter-spacing: 0.2px; }

    /* BADGES */
    .slt-badge-green  { display:inline-block; background:${C.green}18;  color:${C.green};  border-radius:20px; padding:3px 11px; font-size:11.5px; font-weight:700; font-family:'Barlow',sans-serif; }
    .slt-badge-orange { display:inline-block; background:${C.orange}18; color:${C.orange}; border-radius:20px; padding:3px 11px; font-size:11.5px; font-weight:700; font-family:'Barlow',sans-serif; }
    .slt-badge-blue   { display:inline-block; background:${C.blue}18;   color:${C.blue};   border-radius:20px; padding:3px 11px; font-size:11.5px; font-weight:700; font-family:'Barlow',sans-serif; }
    .slt-badge-red    { display:inline-block; background:${C.red}18;    color:${C.red};    border-radius:20px; padding:3px 11px; font-size:11.5px; font-weight:700; font-family:'Barlow',sans-serif; }

    /* DIVIDER */
    .slt-divider { border: none; border-top: 1px solid ${C.border}; margin: 18px 0; }

    /* SECTION TITLE */
    .slt-section-title { font-family: 'Barlow Condensed', sans-serif; font-size: 18px; font-weight: 800; color: ${C.textDark}; margin-bottom: 4px; }
    .slt-section-sub { font-size: 13.5px; color: ${C.textMed}; margin-bottom: 20px; }

    /* ACTIVE BANNER */
    .slt-active-banner {
      background: linear-gradient(90deg, #FFF8E1, #FFFBF0);
      border: 1.5px solid #FFB300;
      border-radius: 12px;
      padding: 12px 18px;
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 20px;
    }

    /* LOAD CARD */
    .slt-load-card {
      background: ${C.white};
      border-radius: 14px;
      padding: 18px 20px;
      box-shadow: 0 1px 6px rgba(10,22,40,0.07);
      border: 1.5px solid rgba(28,43,74,0.15);
      margin-bottom: 12px;
      cursor: pointer;
      transition: all 0.18s;
    }
    .slt-load-card:hover { box-shadow: 0 5px 20px rgba(30,136,229,0.12); transform: translateY(-1px); }

    /* CHAT BUBBLES */
    .slt-bubble-me    { background: linear-gradient(135deg, ${C.blue}, ${C.teal}); color: #fff; border-radius: 14px 14px 4px 14px; padding: 10px 14px; font-size: 13px; line-height: 1.5; }
    .slt-bubble-other { background: #F5F6F8; color: ${C.textDark}; border: 1px solid ${C.border}; border-radius: 14px 14px 14px 4px; padding: 10px 14px; font-size: 13px; line-height: 1.5; }

    /* ANIMATIONS */
    @keyframes fadeUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
    .slt-fade-up { animation: fadeUp 0.28s ease forwards; }

    /* AUTH */
    .slt-auth-bg {
      min-height: 100vh;
      background: linear-gradient(135deg, #1A1A1A 0%, #2a2a2a 50%, #1A1A1A 100%);
      display: flex;
      align-items: flex-start;
      justify-content: center;
      padding: 24px 24px calc(24px + env(safe-area-inset-bottom, 0px));
      font-family: 'Barlow', sans-serif;
      position: relative;
      overflow-y: auto;
      overflow-x: hidden;
      -webkit-overflow-scrolling: touch;
    }
    .slt-auth-bg::before {
      content: '';
      position: absolute;
      inset: 0;
      background: radial-gradient(ellipse at 20% 80%, ${C.teal}18 0%, transparent 50%),
                  radial-gradient(ellipse at 80% 20%, ${C.blue}20 0%, transparent 50%);
      pointer-events: none;
    }
    .slt-auth-card {
      background: rgba(255,255,255,0.04);
      backdrop-filter: blur(20px);
      border: 1px solid rgba(255,255,255,0.12);
      border-radius: 20px;
      padding: 36px 32px;
      width: 100%;
      max-width: 440px;
      position: relative;
      margin: auto 0;
    }

    @media (max-width: 640px) {
      .slt-nav { height: 52px; padding: 0 12px; gap: 8px; width: 100%; }
      .slt-brand { display: flex; }
      .slt-brand-main { font-size: 13px; }
      .slt-logo-area { gap: 7px; }
      .slt-menu-trigger { padding: 7px 10px; font-size: 13px; gap: 4px; }
      .slt-menu-label { display: none; }
      .slt-menu-chevron { display: none; }
      .slt-active-pilot { display: none; }
      .slt-active-pill { display: none; }
      .slt-user-name, .slt-user-role { display: none; }
      .slt-user-chip { display: none !important; }
      .slt-container, .slt-container-sm { padding: 12px 14px 90px; max-width: 100%; width: 100%; box-sizing: border-box; }
      .slt-card { padding: 13px 14px; }
      .slt-card-sm { padding: 11px 12px; }
      .slt-hero { padding: 18px 16px 16px; }
      .slt-hero-title { font-size: 20px; color: #FFFFFF; }
      .slt-hero-sub { font-size: 13px; color: rgba(255,255,255,0.6); }
      .slt-page { width: 100%; max-width: 100vw; overflow-x: hidden; box-sizing: border-box; }
      .slt-dropdown {
        position: fixed;
        top: calc(52px + env(safe-area-inset-top, 0px));
        left: 0;
        right: 0;
        width: 100%;
        border-radius: 0 0 16px 16px;
        max-height: calc(100dvh - 52px - env(safe-area-inset-top, 0px) - 16px);
        overflow-y: scroll;
        -webkit-overflow-scrolling: touch;
        overscroll-behavior: contain;
        touch-action: pan-y;
        padding-bottom: env(safe-area-inset-bottom, 16px);
      }
      .slt-dropdown-grid { grid-template-columns: 1fr 1fr; }
      .slt-logout-btn { padding: 7px 9px; font-size: 12px; }
    }

    /* ══════════════════════════════════════════════════════
       DARK MODE — Design System Token Overrides
       These !important rules beat inline style="" attributes
       and standardise ALL screens to the HTML reference:
       BG #1A1A1A · Cards #252525 · Nav/BottomBar #0B1120
       Hero linear-gradient(135deg,#0B1120,#1F2937)
    ══════════════════════════════════════════════════════ */
    body.slt-dark .slt-page { background: #1A1A1A !important; color: #F0EDE8 !important; }
    body.slt-dark .slt-container { background: #070B14 !important; }
    body.slt-dark .slt-hero {
      background: linear-gradient(135deg,#0B1120 0%,#1F2937 100%) !important;
      border-bottom: 1px solid rgba(255,255,255,0.08) !important;
    }
    body.slt-dark .slt-hero-title { color: #FFFFFF !important; }
    body.slt-dark .slt-hero-sub { color: rgba(255,255,255,0.6) !important; }
    body.slt-dark .slt-hero-back { color: #E8962E !important; }
    body.slt-dark .slt-nav { background: #0B1120 !important; border-bottom: 2px solid rgba(59,130,246,0.3) !important; }
    body.slt-dark .slt-bottom-bar,
    body.slt-dark .slt-bottom-nav { background: #0B1120 !important; border-top: 1px solid rgba(59,130,246,0.15) !important; z-index: 9000 !important; }
    body.slt-dark .slt-card { background: #0D1523 !important; border-color: rgba(59,130,246,0.12) !important; color: #F0F0F0 !important; }
    body.slt-dark .slt-card:hover { box-shadow: 0 10px 32px rgba(59,130,246,0.12) !important; border-color: rgba(59,130,246,0.22) !important; }
    body.slt-dark .slt-card-sm { background: #252525 !important; border-color: rgba(255,255,255,0.07) !important; }
    /* All inputs and form elements */
    body.slt-dark input,
    body.slt-dark textarea,
    body.slt-dark select { background: #2A2A2A !important; border-color: rgba(255,255,255,0.12) !important; color: #F0EDE8 !important; }
    body.slt-dark select option { background: #2A2A2A; color: #F0EDE8; }
    body.slt-dark label { color: rgba(240,237,232,0.55) !important; }
    /* Override hardcoded #F5F6F8 white backgrounds on ALL screens */
    body.slt-dark [class*="slt-page"] { background: #1A1A1A !important; }
    /* Inline white backgrounds on divs inside slt-page — catch-all */
    body.slt-dark .slt-page > div > div[style*="background:#FFFFFF"],
    body.slt-dark .slt-page > div > div[style*="background: #FFFFFF"],
    body.slt-dark .slt-page div[style*="background:\"#FFFFFF\""] { background: #252525 !important; }
    /* Stat cards, section cards, load items in all secondary screens */
    body.slt-dark .slt-stat,
    body.slt-dark .slt-stat-card,
    body.slt-dark .slt-load-card { background: #112233 !important; border-color: rgba(255,255,255,0.08) !important; color: #eaeaea !important; }
    /* Load Details Modal dark mode */
    body.slt-dark [style*="background:\"#fff\""][style*="border-radius:\"18px 18px 0 0\""],
    body.slt-dark [style*="background: #fff"][style*="border-radius: 18px 18px 0 0"] { background: #0b1a2a !important; }
    body.slt-dark [style*="blueLight"] { background: rgba(28,43,74,0.4) !important; }
    /* Typography — make text readable in dark mode on all screens */
    body.slt-dark h1, body.slt-dark h2, body.slt-dark h3, body.slt-dark h4 { color: #F0EDE8 !important; }
    /* Dividers and borders */
    body.slt-dark hr { border-color: rgba(255,255,255,0.08) !important; }
    /* Buttons that are white bg */
    body.slt-dark button[style*="background:\"#fff\""],
    body.slt-dark button[style*="background:#fff"],
    body.slt-dark button[style*="background:\"#FFFFFF\""],
    body.slt-dark button[style*="background:#FFFFFF"] { background: #2A2A2A !important; color: #F0EDE8 !important; border-color: rgba(255,255,255,0.1) !important; }
  `}</style>
);

// ─── NAV BAR with Dropdown ────────────────────────────────────────────────────
function EditProfileModal({ session, onClose, onSave }) {
  const [name, setName] = useState(session.fullName||session.name||"");
  const [companyName, setCompanyName] = useState(session.companyName||"");
  const [username, setUsername] = useState(session.username||"");
  const [usernameMsg, setUsernameMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if(!name.trim()) return;
    setSaving(true);
    // Check username availability if changed
    if(username.trim() && username.trim() !== session.username) {
      const { data: existing } = await sb.from("profiles").select("id").eq("username", username.trim()).neq("id", session.uid).single();
      if(existing) { setUsernameMsg("Username already taken"); setSaving(false); return; }
    }
    await sbSaveProfile({ id: session.uid, name: name.trim(), company_name: companyName.trim()||null, role: session.role, plan: session.plan||"free", username: username.trim()||null });
    onSave(name.trim(), companyName.trim()||null);
    setSaving(false);
    onClose();
  };
  return (
    <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.55)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{background:"#fff",borderRadius:18,padding:28,width:"100%",maxWidth:360,boxShadow:"0 8px 40px rgba(0,0,0,0.55)",maxHeight:"calc(100dvh - 32px)",overflowY:"auto"}}>
        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:18,marginBottom:20}}>✏️ Edit Profile</div>
        <div style={{marginBottom:16}}>
          <label style={{fontSize:12,fontWeight:700,color:"#374151",display:"block",marginBottom:6}}>Full Name *</label>
          <input value={name} onChange={e=>setName(e.target.value)} className="slt-input" placeholder="Your full name" style={{fontSize:16}}/>
        </div>
        <div style={{marginBottom:16}}>
          <label style={{fontSize:12,fontWeight:700,color:"#374151",display:"block",marginBottom:6}}>Company Name</label>
          <input value={companyName} onChange={e=>setCompanyName(e.target.value)} className="slt-input" placeholder="e.g. ABC Trucking Ltd." style={{fontSize:16}}/>
          <div style={{fontSize:13,color:"#6B7280",marginTop:4}}>Used on invoices and statements</div>
        </div>
        <div style={{marginBottom:16}}>
          <label style={{fontSize:12,fontWeight:700,color:"#374151",display:"block",marginBottom:6}}>Username <span style={{fontWeight:500,color:"#6B7280"}}>(for quick login)</span></label>
          <input value={username} onChange={e=>{setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g,"")); setUsernameMsg("");}} className="slt-input" placeholder="e.g. john_driver" style={{fontSize:16}}/>
          {usernameMsg && <div style={{fontSize:13,color:"#EF4444",marginTop:4}}>{usernameMsg}</div>}
          <div style={{fontSize:13,color:"#6B7280",marginTop:4}}>Letters, numbers and _ only</div>
        </div>
        <div style={{marginBottom:16}}>
          <label style={{fontSize:12,fontWeight:700,color:"#374151",display:"block",marginBottom:6}}>Email</label>
          <div style={{padding:"12px 14px",borderRadius:10,background:"#F5F6F8",fontSize:14,color:"#4B5563"}}>{session.email||session.uid}</div>
          <div style={{fontSize:13,color:"#6B7280",marginTop:4}}>Email cannot be changed here</div>
        </div>
        <div style={{marginBottom:20}}>
          <label style={{fontSize:12,fontWeight:700,color:"#374151",display:"block",marginBottom:6}}>Role</label>
          <div style={{padding:"10px 14px",borderRadius:10,background:session.role==="owner"?"#FFF3EB":"#F5F6F8",fontSize:13,fontWeight:800,color:session.role==="owner"?"#E8962E":"#E8962E"}}>{session.role==="owner"?"⭐ Owner":"🚛 Driver"}</div>
        </div>
        {/* Join / Leave Fleet for drivers */}
        {session.role === "driver" && (
          <div style={{marginBottom:20, padding:"14px", borderRadius:12, border:`1.5px solid ${C.border}`, background:"#F5F6F8"}}>
            <div style={{fontWeight:800, fontSize:13, marginBottom:8}}>🚛 Fleet Management</div>
            <JoinFleetForm session={session} onClose={onClose} />
          </div>
        )}

        <div style={{display:"flex",gap:10}}>
          <button onClick={onClose} style={{flex:1,padding:"12px",borderRadius:10,border:"1.5px solid #ddd",background:"#fff",fontWeight:700,cursor:"pointer"}}>Cancel</button>
          <button onClick={save} disabled={saving} style={{flex:2,padding:"12px",borderRadius:10,border:"none",background:"#E8962E",color:"#1C1C1E",fontWeight:800,cursor:"pointer",fontSize:14}}>{saving?"Saving…":"Save Changes"}</button>
        </div>
      </div>
    </div>
  );
}

function JoinFleetForm({ session, onClose }) {
  const [code, setCode] = useState("");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  // Seed immediately from session so fleet names show even if sbGetMyFleets is slow or RLS-blocked.
  // session.allFleetOwnerUids + fleetOwnerUid tell us which fleets we're in; we show those right away
  // and then enrich with full names once sbGetMyFleets resolves.
  const seedFleets = () => {
    const uids = session.allFleetOwnerUids || (session.fleetOwnerUid && session.fleetOwnerUid !== session.uid ? [session.fleetOwnerUid] : []);
    return uids.map(uid => ({ owner_uid: uid, owner_name: "Loading…", ownerCompany: null, joined_at: null, driverRating: 0, _seeded: true }));
  };
  const [myFleets, setMyFleets] = useState(seedFleets);

  useEffect(() => {
    sbGetMyFleets(session.uid)
      .then(fleets => { setMyFleets(fleets.length > 0 ? fleets : seedFleets()); })
      .catch(() => { /* keep seeded fleets */ });
  }, [session.uid]);

  const joinFleet = async () => {
    if (!code.trim()) return;
    setLoading(true); setStatus(null);
    const result = await sbJoinFleet(session.uid, session.fullName||session.name, code.trim().toUpperCase());
    if (result.error) {
      setStatus({ type:"error", msg: "❌ " + result.error });
      setLoading(false);
    } else {

      const updatedFleets = await sbGetMyFleets(session.uid);
      const allOwnerUids = updatedFleets.map(f => f.owner_uid);
      const updatedSession = {
        ...session,

        ownerUid: allOwnerUids[0] || result.ownerUid,
        allFleetOwnerUids: allOwnerUids,
        inFleet: true,
      };
      saveSession(updatedSession);
      setStatus({ type:"success", msg: `✅ Joined ${result.ownerName}'s fleet! Reloading…` });
      setTimeout(() => window.location.reload(), 1200);
    }
  };

  const leaveFleet = async (ownerUid) => {
    if (!window.confirm("Leave this fleet?")) return;
    await sbLeaveFleet(session.uid, ownerUid);
    const updatedFleets = await sbGetMyFleets(session.uid);
    setMyFleets(updatedFleets);
    if (updatedFleets.length === 0) {
      // Left all fleets — reload so session resets to solo driver
      window.location.reload();
    } else {
      // Still in other fleets — update session allFleetOwnerUids and reload
      const allOwnerUids = updatedFleets.map(f => f.owner_uid);
      const updatedSession = {
        ...session,
        fleetOwnerUid: allOwnerUids[0],
        ownerUid: allOwnerUids[0],
        allFleetOwnerUids: allOwnerUids,
        inFleet: true,
      };
      saveSession(updatedSession);
      window.location.reload();
    }
  };

  return (
    <div>
      {myFleets !== null && myFleets.length > 0 && (
        <div style={{marginBottom:12}}>
          <div style={{fontSize:12, fontWeight:700, color:C.textDarkMed, marginBottom:6}}>YOUR FLEETS</div>
          {myFleets.map(f => (
            <div key={f.owner_uid} style={{padding:"10px 0", borderBottom:`1px solid ${C.border}`}}>
              <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6}}>
                <div>
                  <div style={{fontWeight:800, fontSize:14, color:C.text}}>{f.ownerCompany || f.owner_name}</div>
                  {f.ownerCompany && f.ownerCompany !== f.owner_name && (
                    <div style={{fontSize:12, color:C.textDarkMut}}>{f.owner_name}</div>
                  )}
                  {f.joined_at && <div style={{fontSize:12, color:C.textDarkMut}}>Joined {f.joined_at.slice(0,10)}</div>}
                </div>
                <button onClick={()=>leaveFleet(f.owner_uid)}
                  style={{padding:"5px 10px", borderRadius:8, border:`1px solid ${C.red}`, background:"#fff", color:C.red, fontSize:13, fontWeight:700, cursor:"pointer"}}>
                  Leave
                </button>
              </div>
              <div style={{display:"flex", alignItems:"center", gap:4}}>
                <span style={{fontSize:13, color:C.textDarkMed, marginRight:4}}>Rate owner:</span>
                {[1,2,3,4,5].map(star=>(
                  <button key={star} onClick={async()=>{
                    // driver rating removed
                    setMyFleets(prev=>prev.map(x=>x.owner_uid===f.owner_uid?{...x,driverRating:star}:x));
                  }} style={{fontSize:20,background:"none",border:"none",cursor:"pointer",padding:"0 1px",
                    color:(f.driverRating||0)>=star?"#FFD700":"#ddd"}}>★</button>
                ))}
                {f.driverRating && <span style={{fontSize:13, color:C.textDarkMed}}>{f.driverRating}.0</span>}
              </div>
            </div>
          ))}
        </div>
      )}
      {myFleets !== null && myFleets.length === 0 && (
        <div style={{fontSize:13, color:C.textDarkMed, marginBottom:8}}>You are not connected to any fleet.</div>
      )}
      <div style={{fontSize:12, color:C.textDarkMed, marginBottom:8, marginTop:(myFleets&&myFleets.length>0)?8:0}}>
        {(myFleets&&myFleets.length > 0) ? "Join an additional fleet:" : "Enter an owner's invite code to join their fleet."}
      </div>
      <input value={code} onChange={e=>setCode(e.target.value.toUpperCase())}
        placeholder="6-LETTER CODE"
        style={{width:"100%", padding:"10px 14px", borderRadius:9, border:`1.5px solid ${C.border}`, fontSize:16, fontWeight:800, letterSpacing:6, textAlign:"center", marginBottom:8, boxSizing:"border-box"}} />
      {status && (
        <div style={{padding:"8px 12px", borderRadius:8, background:status.type==="success"?"#E8F5E9":"#FFEBEE", color:status.type==="success"?C.green:C.red, fontSize:12, fontWeight:700, marginBottom:8}}>
          {status.msg}
        </div>
      )}
      <button onClick={joinFleet} disabled={loading || !code.trim()}
        style={{width:"100%", padding:"10px", borderRadius:9, border:"none", background:loading||!code.trim()?"#ccc":C.blue, color:"#fff", fontWeight:800, fontSize:13, cursor:"pointer"}}>
        {loading ? "Joining..." : "Join Fleet Instantly"}
      </button>
    </div>
  );
}

// ─── CONFETTI ────────────────────────────────────────────────────────────────
function fireConfetti() {
  const colors = ["#FF6B6B","#FFD93D","#6BCB77","#E8962E","#E8962E","#E8962E"];
  for (let i = 0; i < 60; i++) {
    const el = document.createElement("div");
    el.className = "slt-confetti-piece";
    el.style.left = Math.random() * 100 + "vw";
    el.style.top = "-10px";
    el.style.background = colors[Math.floor(Math.random() * colors.length)];
    el.style.width = (Math.random() * 8 + 6) + "px";
    el.style.height = (Math.random() * 8 + 6) + "px";
    el.style.borderRadius = Math.random() > 0.5 ? "50%" : "2px";
    el.style.animationDelay = Math.random() * 0.5 + "s";
    el.style.animationDuration = (Math.random() * 0.8 + 0.8) + "s";
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 2000);
  }
}

// ─── COUNT UP HOOK ────────────────────────────────────────────────────────────
function useCountUp(target, duration=800) {
  const [value, setValue] = useState(0);
  const prev = useRef(0);
  useEffect(() => {
    const start = prev.current;
    const end = Number(target) || 0;
    if (start === end) return;
    const startTime = Date.now();
    const tick = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease out cubic
      setValue(start + (end - start) * eased);
      if (progress < 1) requestAnimationFrame(tick);
      else { setValue(end); prev.current = end; }
    };
    requestAnimationFrame(tick);
  }, [target]);
  return value;
}

// ─── ANIMATED STAT CARD ───────────────────────────────────────────────────────
function AnimatedStatCard({ label, value, icon, gradient, onClick, delay=0 }) {
  const isNumber = !isNaN(parseFloat(String(value).replace(/[$,]/g,"")));
  const numVal = isNumber ? parseFloat(String(value).replace(/[$,]/g,"")) : 0;
  const counted = useCountUp(numVal, 900);
  const displayValue = isNumber
    ? (String(value).startsWith("$") ? "$" + counted.toFixed(2) : Math.round(counted).toString())
    : value;

  return (
    <div className="slt-stat-card slt-slide-in" onClick={onClick}
      style={{ background:gradient, animationDelay:`${delay}s`, cursor:onClick?"pointer":"default" }}>
      <div style={{ fontSize:13, fontWeight:700, color:"rgba(255,255,255,0.7)", letterSpacing:1.5, textTransform:"uppercase", marginBottom:6 }}>{label}</div>
      <div className="slt-countup" style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:28, color:"#fff", lineHeight:1 }}>
        {displayValue}
      </div>
      <div style={{ position:"absolute", bottom:10, right:14, fontSize:28, opacity:0.2 }}>{icon}</div>
    </div>
  );
}

// ─── WEATHER ALERT BANNER ────────────────────────────────────────────────────
function WeatherAlertBanner() {
  const [weather, setWeather] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    fetch("https://api.open-meteo.com/v1/forecast?latitude=53.5461&longitude=-113.4938&current=weather_code,temperature_2m,wind_speed_10m,wind_gusts_10m,apparent_temperature,visibility,precipitation&timezone=America/Edmonton")
      .then(r=>r.json())
      .then(d=>{
        const code = d.current?.weather_code || 0;
        const temp = Math.round(d.current?.temperature_2m || 0);
        const feelsLike = Math.round(d.current?.apparent_temperature || temp);
        const wind = Math.round(d.current?.wind_speed_10m || 0);
        const gusts = Math.round(d.current?.wind_gusts_10m || 0);
        const visibility = d.current?.visibility || 10000;
        const precip = d.current?.precipitation || 0;
        const emoji = code<=1?"☀️":code<=3?"⛅":code<=48?"🌫️":code<=67?"🌧️":code<=77?"❄️":"⛈️";
        const label = code<=1?"Clear":code<=3?"Cloudy":code<=48?"Foggy":code<=67?"Rainy":code<=77?"Snowy":"Stormy";

        // Determine alert level
        let alert = null;
        if (temp<=-20 || code===66 || code===67) {
          alert = { level:"danger", msg: temp<=-20?`Extreme cold ${temp}°C — black ice risk`:`Freezing rain — dangerous roads`, icon:"🚨" };
        } else if (gusts>=60||code>=71&&code<=77||visibility<=1000||temp<=-10) {
          const msgs = [];
          if (code>=71&&code<=77) msgs.push("Snow");
          if (gusts>=60) msgs.push(`Gusts ${gusts}km/h`);
          if (visibility<=1000) msgs.push("Low visibility");
          if (temp<=-10) msgs.push(`${temp}°C icy roads`);
          alert = { level:"warning", msg: msgs.join(" · "), icon:"⚠️" };
        }

        setWeather({ temp, feelsLike, wind, gusts, emoji, label, alert, precip, visibility });
      }).catch(()=>{});
  }, []);

  if (!weather || dismissed) return null;

  return (
    <div style={{ marginBottom:12 }}>
      {/* Always show current conditions */}
      <div style={{ background:"linear-gradient(135deg,#E8962E,#E8962E)", borderRadius:14, padding:"12px 16px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:28 }}>{weather.emoji}</span>
          <div>
            <div style={{ fontWeight:800, color:"#fff", fontSize:14 }}>Edmonton, AB · {weather.temp}°C</div>
            <div style={{ fontSize:13, color:"rgba(255,255,255,0.6)" }}>
              {weather.label} · Feels {weather.feelsLike}°C · Wind {weather.wind}km/h
              {weather.gusts>30?` · Gusts ${weather.gusts}`:""}
            </div>
          </div>
        </div>
        <div style={{ fontSize:13, color:"rgba(255,255,255,0.5)", fontWeight:700 }}>
          {new Date().toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}
        </div>
      </div>

      {/* Alert banner if dangerous */}
      {weather.alert && (
        <div style={{ 
          background:weather.alert.level==="danger"?"linear-gradient(135deg,#B71C1C,#C62828)":"linear-gradient(135deg,#E8962E,#F57C00)",
          borderRadius:"0 0 14px 14px", padding:"10px 16px",
          display:"flex", justifyContent:"space-between", alignItems:"center",
          marginTop:-4
        }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontSize:18 }}>{weather.alert.icon}</span>
            <div>
              <div style={{ fontWeight:800, color:"#fff", fontSize:12 }}>
                {weather.alert.level==="danger"?"🚨 HAZARDOUS CONDITIONS":"⚠️ WEATHER ADVISORY"}
              </div>
              <div style={{ fontSize:13, color:"rgba(255,255,255,0.85)" }}>{weather.alert.msg}</div>
            </div>
          </div>
          <button onClick={()=>setDismissed(true)} style={{ background:"rgba(255,255,255,0.2)", border:"none", borderRadius:20, color:"#fff", width:24, height:24, cursor:"pointer", fontSize:12, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>✕</button>
        </div>
      )}
    </div>
  );
}

// ─── DYNAMIC WELCOME SCREEN ──────────────────────────────────────────────────
function WelcomeScreen({ session, loads=[], rates={}, onDone }) {
  const [slide, setSlide] = useState(0);
  const [weather, setWeather] = useState(null);
  const [truckX, setTruckX] = useState(-120);

  const myLoads = loads.filter(l => l.user_id === session.uid || l.addedBy === session.uid || l.addedBy === session.uid);
  const totalPay = myLoads.reduce((s,l)=>s+(Number(l.driverBasePay)>0?Number(l.driverBasePay):Number(l.earnings||0)),0);
  const weekLoads = myLoads.filter(l=>{ const d=new Date(l.date); const now=new Date(); const w=new Date(now); w.setDate(w.getDate()-7); return d>=w; });
  const weekPay = weekLoads.reduce((s,l)=>s+(Number(l.driverBasePay)>0?Number(l.driverBasePay):Number(l.earnings||0)),0);
  const bestDay = (() => {
    const byDay = {};
    myLoads.forEach(l => { const k=l.date; byDay[k]=(byDay[k]||0)+(Number(l.driverBasePay)>0?Number(l.driverBasePay):Number(l.earnings||0)); });
    const best = Object.entries(byDay).sort((a,b)=>b[1]-a[1])[0];
    return best ? { date: new Date(best[0]+'T12:00:00').toLocaleDateString('en-CA',{weekday:'long',month:'short',day:'numeric'}), amount: best[1] } : null;
  })();
  const yearlyPace = myLoads.length > 0 ? (totalPay / Math.max(1, (new Date()-new Date(myLoads[myLoads.length-1]?.date||new Date())) / (1000*60*60*24))) * 365 : 0;
  const streak = (() => { let c=0; const d=new Date(); while(true){ const ds=d.toISOString().slice(0,10); if(myLoads.some(l=>l.date===ds)){c++;d.setDate(d.getDate()-1);}else break; } return c; })();

  // Time of day sky
  const hour = new Date().getHours();
  const sky = hour >= 5 && hour < 7
    ? { bg:"linear-gradient(180deg,#1a0a2e 0%,#ff6b35 40%,#ffd700 70%,#FFD580 100%)", stars:false, label:"sunrise", emoji:"🌅" }
    : hour >= 7 && hour < 17
    ? { bg:"linear-gradient(180deg,#E8962E 0%,#E8962E 40%,#90e0ef 100%)", stars:false, label:"day", emoji:"☀️" }
    : hour >= 17 && hour < 20
    ? { bg:"linear-gradient(180deg,#1a0a2e 0%,#e76f51 35%,#f4a261 60%,#1A1A1A 100%)", stars:false, label:"sunset", emoji:"🌇" }
    : { bg:"linear-gradient(180deg,#020818 0%,#0d1b2a 40%,#1b2838 100%)", stars:true, label:"night", emoji:"🌙" };

  // Fetch real weather + alerts for Alberta (Edmonton area)
  useEffect(() => {
    const lat = 53.5461; // Edmonton AB
    const lon = -113.4938;
    Promise.all([
      // Current weather + hourly forecast + wind + precipitation
      fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=weather_code,temperature_2m,wind_speed_10m,wind_gusts_10m,precipitation,visibility,apparent_temperature&hourly=temperature_2m,precipitation_probability,weather_code&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum,wind_speed_10m_max&timezone=America/Edmonton&forecast_days=2`),
    ]).then(([weatherRes]) => Promise.all([weatherRes.json()])).then(([d]) => {
      const code = d.current?.weather_code || 0;
      const temp = Math.round(d.current?.temperature_2m || 0);
      const feelsLike = Math.round(d.current?.apparent_temperature || temp);
      const wind = Math.round(d.current?.wind_speed_10m || 0);
      const gusts = Math.round(d.current?.wind_gusts_10m || 0);
      const precip = d.current?.precipitation || 0;
      const visibility = d.current?.visibility || 10000;

      // Condition labels
      const conditions = code<=1?"Clear":code<=3?"Partly Cloudy":code<=45?"Foggy":code<=48?"Freezing Fog":code<=57?"Drizzle":code<=67?"Rain":code<=69?"Freezing Rain":code<=77?"Snow":code<=82?"Rain Showers":code<=86?"Snow Showers":"Thunderstorm";
      const emoji = code<=1?"☀️":code<=3?"⛅":code<=48?"🌫️":code<=57?"🌦️":code<=67?"🌧️":code<=69?"🌨️":code<=77?"❄️":code<=82?"🌦️":code<=86?"🌨️":"⛈️";

      // Build real trucker alerts
      const alerts = [];
      
      // Temperature alerts
      if (temp <= -20) alerts.push({ level:"danger", icon:"🥶", msg:`Extreme cold: ${temp}°C (feels ${feelsLike}°C) — risk of engine freeze & black ice` });
      else if (temp <= -10) alerts.push({ level:"warning", icon:"❄️", msg:`Cold conditions: ${temp}°C — watch for black ice on roads` });
      else if (temp >= 35) alerts.push({ level:"warning", icon:"🌡️", msg:`Heat advisory: ${temp}°C — check tire pressure & engine cooling` });

      // Wind alerts
      if (gusts >= 90) alerts.push({ level:"danger", icon:"💨", msg:`Extreme wind gusts: ${gusts} km/h — risk of truck rollover on exposed roads` });
      else if (gusts >= 60 || wind >= 50) alerts.push({ level:"warning", icon:"💨", msg:`Strong winds: ${wind} km/h, gusts to ${gusts} km/h — reduce speed on highways` });
      
      // Visibility alerts
      if (visibility <= 200) alerts.push({ level:"danger", icon:"🌫️", msg:`Near-zero visibility: ${Math.round(visibility)}m — pull over if unsafe` });
      else if (visibility <= 1000) alerts.push({ level:"warning", icon:"🌫️", msg:`Low visibility: ${Math.round(visibility/100)*100}m — use low beams & reduce speed` });

      // Snow/ice alerts
      if (code >= 71 && code <= 77) alerts.push({ level:"warning", icon:"❄️", msg:`Snow falling — roads may be icy, increase following distance` });
      if (code === 66 || code === 67) alerts.push({ level:"danger", icon:"🧊", msg:`Freezing rain — extremely dangerous road conditions` });
      
      // Rain alerts
      if (precip > 5) alerts.push({ level:"warning", icon:"🌧️", msg:`Heavy rain: ${precip.toFixed(1)}mm — reduced traction, slow down` });

      // Tomorrow forecast
      const tomorrowCode = d.daily?.weather_code?.[1] || 0;
      const tomorrowMax = Math.round(d.daily?.temperature_2m_max?.[1] || 0);
      const tomorrowMin = Math.round(d.daily?.temperature_2m_min?.[1] || 0);
      const tomorrowWind = Math.round(d.daily?.wind_speed_10m_max?.[1] || 0);
      const tomorrowEmoji = tomorrowCode<=1?"☀️":tomorrowCode<=3?"⛅":tomorrowCode<=48?"🌫️":tomorrowCode<=67?"🌧️":tomorrowCode<=77?"❄️":"⛈️";

      setWeather({ 
        conditions, temp, feelsLike, wind, gusts, precip, visibility,
        emoji, alerts,
        tomorrow: { emoji:tomorrowEmoji, max:tomorrowMax, min:tomorrowMin, wind:tomorrowWind,
          label: tomorrowCode<=1?"Clear":tomorrowCode<=3?"Cloudy":tomorrowCode<=67?"Rain":tomorrowCode<=77?"Snow":"Storms" }
      });
    }).catch(() => {});
  }, []);

  // Animate truck across screen
  useEffect(() => {
    let x = -120;
    const target = window.innerWidth + 40;
    const interval = setInterval(() => {
      x += 3;
      setTruckX(x);
      if (x > target) x = -120;
    }, 16);
    return () => clearInterval(interval);
  }, []);

  // Auto-swipe slides
  const slides = [
    { type:"welcome" },
    { type:"weather" },
    { type:"money1" },
    { type:"money2" },
    { type:"money3" },
  ].filter(s => {
    if (s.type==="weather" && !weather) return false;
    if (s.type==="money1" && weekPay===0) return false;
    if (s.type==="money2" && yearlyPace<1000) return false;
    if (s.type==="money3" && !bestDay) return false;
    return true;
  });

  useEffect(() => {
    if (slide >= slides.length - 1) {
      const t = setTimeout(onDone, 2000);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setSlide(s => s+1), 2800);
    return () => clearTimeout(t);
  }, [slide, slides.length]);

  const greet = hour<5?"Working late":hour<12?"Good morning":hour<17?"Good afternoon":"Good evening";

  const renderSlide = () => {
    const s = slides[slide];
    if (!s) return null;
    switch(s.type) {
      case "welcome": return (
        <div style={{textAlign:"center",animation:"slt-fade-in 0.5s ease"}}>
          <div style={{fontSize:56,marginBottom:8}}>{sky.emoji}</div>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:28,color:"#fff",marginBottom:8,textShadow:"0 2px 20px rgba(0,0,0,0.5)"}}>
            {greet},<br/>{(session.fullName||session.name).split(" ")[0]}!
          </div>
          {streak>=2&&<div style={{background:"rgba(255,152,0,0.3)",borderRadius:20,padding:"6px 16px",display:"inline-block",fontSize:14,fontWeight:700,color:"#FFD54F"}}>🔥 {streak} day streak!</div>}
        </div>
      );
      case "weather": return (
        <div style={{textAlign:"center",animation:"slt-fade-in 0.5s ease",width:"100%",maxWidth:360}}>
          {/* Current conditions */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:12,marginBottom:8}}>
            <div style={{fontSize:48}}>{weather.emoji}</div>
            <div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:36,color:"#fff",lineHeight:1}}>{weather.temp}°C</div>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.6)"}}>Feels {weather.feelsLike}°C</div>
            </div>
          </div>
          <div style={{fontWeight:700,color:"rgba(255,255,255,0.8)",fontSize:14,marginBottom:4}}>{weather.conditions} · Edmonton, AB</div>
          
          {/* Wind & visibility row */}
          <div style={{display:"flex",justifyContent:"center",gap:16,marginBottom:12,fontSize:12,color:"rgba(255,255,255,0.6)"}}>
            <span>💨 {weather.wind} km/h</span>
            {weather.gusts>0&&<span>⚡ Gusts {weather.gusts}</span>}
            {weather.precip>0&&<span>🌧️ {weather.precip.toFixed(1)}mm</span>}
          </div>

          {/* Alerts */}
          {weather.alerts?.length > 0 ? (
            <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:10}}>
              {weather.alerts.map((a,i)=>(
                <div key={i} style={{
                  background:a.level==="danger"?"rgba(229,57,53,0.3)":"rgba(255,152,0,0.25)",
                  border:`1px solid ${a.level==="danger"?"rgba(229,57,53,0.5)":"rgba(255,152,0,0.4)"}`,
                  borderRadius:10,padding:"8px 12px",fontSize:12,
                  color:a.level==="danger"?"#FF8A80":"#FFD54F",
                  fontWeight:700,textAlign:"left",display:"flex",gap:8,alignItems:"flex-start"
                }}>
                  <span style={{flexShrink:0}}>{a.icon}</span>
                  <span>{a.msg}</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{background:"rgba(105,240,174,0.2)",borderRadius:10,padding:"8px 14px",fontSize:13,color:"#69F0AE",fontWeight:700,marginBottom:10}}>
              ✅ Good road conditions — safe to haul today!
            </div>
          )}

          {/* Tomorrow */}
          {weather.tomorrow&&(
            <div style={{background:"rgba(255,255,255,0.08)",borderRadius:10,padding:"8px 14px",fontSize:12,color:"rgba(255,255,255,0.6)",display:"flex",justifyContent:"center",gap:16}}>
              <span style={{fontWeight:700,color:"rgba(255,255,255,0.4)"}}>Tomorrow:</span>
              <span>{weather.tomorrow.emoji} {weather.tomorrow.label}</span>
              <span>↑{weather.tomorrow.max}° ↓{weather.tomorrow.min}°</span>
              {weather.tomorrow.wind>40&&<span>💨 {weather.tomorrow.wind}km/h</span>}
            </div>
          )}
        </div>
      );
      case "money1": return (
        <div style={{textAlign:"center",animation:"slt-fade-in 0.5s ease"}}>
          <div style={{fontSize:48,marginBottom:8}}>💰</div>
          <div style={{fontSize:14,fontWeight:700,color:"rgba(255,255,255,0.6)",letterSpacing:1,textTransform:"uppercase",marginBottom:8}}>This Week</div>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:42,color:"#69F0AE",textShadow:"0 0 30px rgba(105,240,174,0.6)"}}>
            ${weekPay.toFixed(2)}
          </div>
          <div style={{fontSize:14,color:"rgba(255,255,255,0.7)",marginTop:8}}>{weekLoads.length} load{weekLoads.length!==1?"s":""} this week 📋</div>
        </div>
      );
      case "money2": return (
        <div style={{textAlign:"center",animation:"slt-fade-in 0.5s ease"}}>
          <div style={{fontSize:48,marginBottom:8}}>📈</div>
          <div style={{fontSize:14,fontWeight:700,color:"rgba(255,255,255,0.6)",letterSpacing:1,textTransform:"uppercase",marginBottom:8}}>At This Pace</div>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:36,color:"#FFD54F",textShadow:"0 0 30px rgba(255,213,79,0.5)"}}>
            ${Math.round(yearlyPace).toLocaleString()}/year
          </div>
          <div style={{fontSize:13,color:"rgba(255,255,255,0.6)",marginTop:8}}>Keep logging every load 💪</div>
        </div>
      );
      case "money3": return (
        <div style={{textAlign:"center",animation:"slt-fade-in 0.5s ease"}}>
          <div style={{fontSize:48,marginBottom:8}}>🏆</div>
          <div style={{fontSize:14,fontWeight:700,color:"rgba(255,255,255,0.6)",letterSpacing:1,textTransform:"uppercase",marginBottom:8}}>Your Best Day</div>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:42,color:"#FF8A65",textShadow:"0 0 30px rgba(255,138,101,0.6)"}}>
            ${bestDay.amount.toFixed(2)}
          </div>
          <div style={{fontSize:13,color:"rgba(255,255,255,0.6)",marginTop:8}}>{bestDay.date} 🗓</div>
          <div style={{fontSize:13,color:"rgba(255,255,255,0.5)",marginTop:4}}>Can you beat it today?</div>
        </div>
      );
      default: return null;
    }
  };

  return (
    <div style={{position:"fixed",inset:0,zIndex:99998,background:sky.bg,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
      
      {/* Stars for night */}
      {sky.stars && Array.from({length:40}).map((_,i)=>(
        <div key={i} style={{position:"absolute",width:Math.random()*3+1+"px",height:Math.random()*3+1+"px",borderRadius:"50%",background:"#fff",top:Math.random()*60+"%",left:Math.random()*100+"%",opacity:Math.random()*0.8+0.2,animation:`slt-star-twinkle ${Math.random()*2+1}s infinite`}} />
      ))}

      {/* Road */}
      <div style={{position:"absolute",bottom:0,left:0,right:0,height:"30%",background:"linear-gradient(180deg,transparent,#1a1a2e 60%)"}} />
      <div style={{position:"absolute",bottom:"18%",left:0,right:0,height:8,background:"#333"}} />
      <div style={{position:"absolute",bottom:"22%",left:0,right:0,height:60,background:"linear-gradient(180deg,transparent,rgba(0,0,0,0.55))"}} />
      {/* Road dashes */}
      {Array.from({length:8}).map((_,i)=>(
        <div key={i} style={{position:"absolute",bottom:"22.5%",left:`${i*14}%`,width:"8%",height:4,background:"rgba(255,255,0,0.4)",borderRadius:2}} />
      ))}

      {/* Truck */}
      <div style={{position:"absolute",bottom:"22%",fontSize:48,transform:`translateX(${truckX}px)`,transition:"none",filter:"drop-shadow(0 4px 8px rgba(0,0,0,0.5))"}}>🚛</div>

      {/* Slide content */}
      <div style={{position:"relative",zIndex:2,textAlign:"center",padding:"0 32px",marginBottom:80}}>
        {renderSlide()}
      </div>

      {/* Progress dots */}
      <div style={{position:"absolute",bottom:40,display:"flex",gap:8}}>
        {slides.map((_,i)=>(
          <div key={i} onClick={()=>setSlide(i)} style={{width:i===slide?24:8,height:8,borderRadius:4,background:i===slide?"#E8962E":"rgba(255,255,255,0.3)",transition:"all 0.3s",cursor:"pointer"}} />
        ))}
      </div>

      {/* Skip button */}
      <button onClick={onDone} style={{position:"absolute",top:20,right:20,background:"rgba(255,255,255,0.15)",border:"none",borderRadius:20,color:"rgba(255,255,255,0.7)",padding:"6px 14px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
        Skip →
      </button>
    </div>
  );
}

// ─── AI ENGINE ───────────────────────────────────────────────────────────────
const ANTHROPIC_MODEL = "claude-sonnet-4-20250514";

async function callAI(systemPrompt, userMessage, maxTokens=600) {
  try {
    const res = await fetch("/api/ai-chat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ system: systemPrompt, message: userMessage, maxTokens })
    });
    const data = await res.json();
    if (data.error) throw new Error(data.error);
    return data.text || "Sorry, I couldn't process that.";
  } catch(e) {
    return "Connection error. Please try again.";
  }
}

// ─── AI ASSISTANT MODAL ───────────────────────────────────────────────────────
function AIAssistant({ session, loads=[], rates={}, expenses=[], onClose, initialMode="chat" }) {
  const [mode, setMode] = useState(initialMode);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  const myLoads = loads.filter(l => l.user_id === session.uid || l.addedBy === session.uid);
  const totalPay = myLoads.reduce((s,l) => s + (Number(l.driverBasePay)>0?Number(l.driverBasePay):Number(l.earnings||0)), 0);
  const totalExp = expenses.reduce((s,e) => s+Number(e.amount||0), 0);
  const recentLoads = myLoads.slice(0,5).map(l=>`${l.date}: ${l.location} — $${Number(l.earnings||0).toFixed(2)}`).join("\n");


  const SYSTEM = `You are TruckPilot AI — a smart assistant built into the TruckPilot fleet management app used by Canadian truckers.
You help drivers and fleet owners with: logging loads, tracking pay, understanding CRA tax deductions, fleet management, and app navigation.
Keep responses SHORT and practical — max 3-4 sentences. Use bullet points for lists. Be friendly and direct.
User info: Name: ${session.fullName||session.name}, Role: ${session.role}, Total loads: ${myLoads.length}, Total pay: $${totalPay.toFixed(2)}, Total expenses: $${totalExp.toFixed(2)}.
Recent loads:
${recentLoads||"No loads yet"}
Always give specific, actionable advice. Never say you cannot help.`;

  const modes = [
    { id:"chat",     icon:"💬", label:"Ask AI" },
    { id:"tax",      icon:"🗂", label:"Tax Help" },
    { id:"insights", icon:"📊", label:"Insights" },
    { id:"dispute",  icon:"✍️", label:"Draft Message" },
  ];

  const quickPrompts = {
    chat: ["How do I log a load?", "Where is my tax export?", "How do I join a fleet?", "What is wait pay?"],
    tax:  ["What can I claim as a trucker?", "How does CRA treat fuel expenses?", "What receipts should I keep?", "Is my phone bill deductible?"],
    insights: ["Analyze my earnings", "What are my best paying routes?", "How can I earn more?", "Compare my expenses"],
    dispute: ["Write a message about missing pay", "Draft a late payment reminder", "Help me explain a route issue", "Write a bonus request"],
  };

  const send = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role:"user", text:msg }]);
    setLoading(true);

    let prompt = msg;
    if (mode === "tax") prompt = `Tax question from a Canadian trucker: ${msg}. Give specific CRA line numbers and practical advice.`;
    if (mode === "insights") prompt = `Analyze this trucker's data and answer: ${msg}. Be specific with numbers from their actual data.`;
    if (mode === "dispute") prompt = `Write a professional, respectful message for this situation: ${msg}. Keep it under 100 words.`;

    const reply = await callAI(SYSTEM, prompt, 400);
    setMessages(prev => [...prev, { role:"assistant", text:reply }]);
    setLoading(false);
    setTimeout(() => bottomRef.current?.scrollIntoView({behavior:"smooth"}), 100);
  };

  useEffect(() => {
    // Auto-greet based on mode
    const greetings = {
      chat: `Hi ${session.fullName?.split(" ")[0]||"there"}! 👋 I'm your TruckPilot AI. Ask me anything about the app, your loads, pay, or taxes.`,
      tax: `I'll help you maximize your CRA deductions! 🗂 You have $${totalExp.toFixed(2)} in tracked expenses. Ask me anything about Canadian trucker taxes.`,
      insights: `Let me analyze your data! 📊 You have ${myLoads.length} loads logged totaling $${totalPay.toFixed(2)} in pay. What would you like to know?`,
      dispute: `I'll help you write professional messages. ✍️ Just describe the situation and I'll draft something for you.`,
    };
    setMessages([{ role:"assistant", text:greetings[mode] }]);
  }, [mode]);

  return (
    <div style={{ position:"fixed", inset:0, zIndex:19999, background:"rgba(0,0,0,0.6)", backdropFilter:"blur(4px)", display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
      <div style={{ width:"100%", maxWidth:480, height:"85vh", background:"#fff", borderRadius:"24px 24px 0 0", display:"flex", flexDirection:"column", boxShadow:"0 -8px 40px rgba(0,0,0,0.55)" }}>
        
        {/* Header */}
        <div style={{ background:"linear-gradient(135deg,#E8962E,#E8962E,#E8962E)", padding:"16px 18px", borderRadius:"24px 24px 0 0", flexShrink:0 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ width:36, height:36, borderRadius:"50%", background:"rgba(255,255,255,0.2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18 }}>🤖</div>
              <div>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, color:"#fff", fontSize:16 }}>TruckPilot AI</div>
                <div style={{ fontSize:13, color:"rgba(255,255,255,0.7)" }}>Powered by Claude</div>
              </div>
            </div>
            <button onClick={onClose} style={{ background:"rgba(255,255,255,0.2)", border:"none", borderRadius:20, color:"#fff", width:32, height:32, cursor:"pointer", fontSize:16, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
          </div>
          {/* Mode tabs */}
          <div style={{ display:"flex", gap:6 }}>
            {modes.map(m => (
              <button key={m.id} onClick={()=>setMode(m.id)}
                style={{ flex:1, padding:"6px 4px", borderRadius:20, border:"none", background:mode===m.id?"#fff":"rgba(255,255,255,0.15)", color:mode===m.id?"#E8962E":"#fff", fontWeight:700, fontSize:12, cursor:"pointer", transition:"all 0.2s" }}>
                {m.icon} {m.label}
              </button>
            ))}
          </div>
        </div>

        {/* Messages */}
        <div style={{ flex:1, overflowY:"auto", padding:"14px 16px", display:"flex", flexDirection:"column", gap:10, background:"#F8F9FC" }}>
          {messages.map((m,i) => (
            <div key={i} style={{ display:"flex", justifyContent:m.role==="user"?"flex-end":"flex-start", gap:8, alignItems:"flex-start" }}>
              {m.role==="assistant" && (
                <div style={{ width:30, height:30, borderRadius:"50%", background:"linear-gradient(135deg,#E8962E,#E8962E)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, flexShrink:0 }}>🤖</div>
              )}
              <div style={{ maxWidth:"80%", padding:"10px 14px", borderRadius:m.role==="user"?"18px 18px 4px 18px":"18px 18px 18px 4px", background:m.role==="user"?"linear-gradient(135deg,#E8962E,#E8962E)":"#fff", color:m.role==="user"?"#fff":"#1a1a2e", fontSize:13, lineHeight:1.6, boxShadow:"0 1px 4px rgba(0,0,0,0.55)", whiteSpace:"pre-wrap" }}>
                {m.text}
              </div>
            </div>
          ))}
          {loading && (
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              <div style={{ width:30, height:30, borderRadius:"50%", background:"linear-gradient(135deg,#E8962E,#E8962E)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14 }}>🤖</div>
              <div style={{ padding:"10px 14px", borderRadius:"18px 18px 18px 4px", background:"#fff", boxShadow:"0 1px 4px rgba(0,0,0,0.55)" }}>
                <div style={{ display:"flex", gap:4 }}>
                  {[0,1,2].map(i => <div key={i} style={{ width:7, height:7, borderRadius:"50%", background:"#E8962E", animation:`slt-dot-bounce 1.2s ${i*0.2}s infinite` }} />)}
                </div>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Quick prompts */}
        {messages.length <= 1 && (
          <div style={{ padding:"8px 16px", background:"#F8F9FC", borderTop:"1px solid #eee", flexShrink:0 }}>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              {quickPrompts[mode].map(p => (
                <button key={p} onClick={()=>send(p)}
                  style={{ padding:"6px 12px", borderRadius:20, border:"1.5px solid #E1E8FF", background:"#fff", color:"#E8962E", fontSize:13, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div style={{ padding:"10px 16px 16px", background:"#fff", borderTop:"1px solid #eee", flexShrink:0 }}>
          <div style={{ display:"flex", gap:8, alignItems:"flex-end" }}>
            <textarea value={input} onChange={e=>setInput(e.target.value)}
              onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}}
              placeholder="Ask anything..."
              rows={1}
              style={{ flex:1, padding:"10px 14px", borderRadius:20, border:"1.5px solid #E1E8FF", fontSize:14, resize:"none", outline:"none", fontFamily:"'Barlow',sans-serif", lineHeight:1.5, maxHeight:80, overflowY:"auto" }} />
            <button onClick={()=>send()} disabled={!input.trim()||loading}
              style={{ width:44, height:44, borderRadius:"50%", border:"none", background:input.trim()&&!loading?"linear-gradient(135deg,#E8962E,#E8962E)":"#e0e0e0", color:"#fff", fontSize:18, cursor:input.trim()&&!loading?"pointer":"default", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              ➤
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── SWIPEABLE LOAD CARD ─────────────────────────────────────────────────────
function SwipeableLoadCard({ load, onComplete, onClick, children, darkMode=false }) {
  const [swipeX, setSwipeX] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const startX = useRef(0);
  const cardRef = useRef(null);

  const handleTouchStart = (e) => {
    startX.current = e.touches[0].clientX;
    setSwiping(true);
  };

  const handleTouchMove = (e) => {
    if (!swiping) return;
    const dx = startX.current - e.touches[0].clientX;
    if (dx > 0) setSwipeX(Math.min(dx, 90));
    else setSwipeX(0);
  };

  const handleTouchEnd = () => {
    if (swipeX > 60 && !load.completed) {
      onComplete();
    }
    setSwipeX(0);
    setSwiping(false);
  };

  return (
    <div ref={cardRef} className="slt-swipeable"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ marginBottom:10, borderRadius:16, overflow:"hidden", boxShadow:"0 2px 10px rgba(0,0,0,0.07)" }}>
      {/* Green complete hint behind card */}
      {!load.completed && (
        <div style={{ position:"absolute", right:0, top:0, bottom:0, width:swipeX, background:"linear-gradient(135deg,#43A047,#2E7D32)", display:"flex", alignItems:"center", justifyContent:"center", borderRadius:"0 12px 12px 0", overflow:"hidden" }}>
          {swipeX > 40 && <span style={{ color:"#fff", fontSize:22 }}>✓</span>}
        </div>
      )}
      {/* Card content */}
      <div style={{ transform:`translateX(-${swipeX}px)`, transition:swiping?"none":"transform 0.3s", background:darkMode?"#0D1523":"#fff", borderRadius:16, padding:"16px 18px" }}
        onClick={swipeX < 5 ? onClick : undefined}>
        {children}
      </div>
    </div>
  );
}

// ─── BOTTOM TAB BAR (mobile only) ────────────────────────────────────────────
// ─── PROFILE TAB ──────────────────────────────────────────────────────────────
function PrivacySecurityModal({ session, onClose, onLogout, darkModeOn }) {
  useEffect(()=>{ document.body.style.overflow="hidden"; return()=>{ document.body.style.overflow=""; }; },[]);
  const [section, setSection] = useState("main");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [passMsg, setPassMsg] = useState("");
  const [passLoading, setPassLoading] = useState(false);
  const [notifEmail, setNotifEmail] = useState(true);
  const [notifPush, setNotifPush] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteMsg, setDeleteMsg] = useState("");

  const bg = darkModeOn ? "#1a1a1a" : "#fff";
  const cardBg = darkModeOn ? "#222222" : "#FFFFFF";
  const textPrimary = darkModeOn ? "#F0EDE8" : "#1A1A1A";
  const textMuted = darkModeOn ? "rgba(240,237,232,.5)" : "#888";
  const border = darkModeOn ? "rgba(255,255,255,.08)" : "#EEEEEE";
  const BLUE = "#E8962E";

  const rowStyle = {display:"flex",alignItems:"center",gap:14,padding:"14px 18px",borderBottom:`1px solid ${border}`,cursor:"pointer"};
  const rowLastStyle = {display:"flex",alignItems:"center",gap:14,padding:"14px 18px"};
  const iconStyle = {width:38,height:38,borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0};
  const inputStyle = {width:"100%",padding:"10px 14px",borderRadius:10,border:`1.5px solid ${border}`,fontSize:14,background:bg,color:textPrimary,fontFamily:"inherit",boxSizing:"border-box",marginTop:4,outline:"none"};
  const sectionTitle = {fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:18,color:textPrimary,marginBottom:16};

  const changePassword = async () => {
    if (!newPass || newPass.length < 6) { setPassMsg("Password must be at least 6 characters."); return; }
    if (newPass !== confirmPass) { setPassMsg("Passwords don't match."); return; }
    setPassLoading(true);
    const { error } = await sb.auth.updateUser({ password: newPass });
    setPassLoading(false);
    if (error) { setPassMsg("Error: " + error.message); return; }
    setPassMsg("✅ Password updated successfully!");
    setNewPass(""); setConfirmPass("");
    setTimeout(() => setPassMsg(""), 3000);
  };

  const exportData = () => {
    const loads = getStored(`tp-loads-${session.uid}`);
    const expenses = getStored(expensesKey(session.uid));
    const rows = [["Type","Date","Route/Description","Amount","Status"]];
    loads.forEach(l => rows.push(["Load", l.date, l.location, l.earnings||0, l.completed?"Done":"Active"]));
    expenses.forEach(e => rows.push(["Expense", e.date, e.merchant||e.note||"", e.amount, e.category]));
    const csv = rows.map(r => r.map(v => `"${v}"`).join(",")).join("\n");
    const blob = new Blob([csv], {type:"text/csv"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `TruckPilot-${session.fullName||"data"}-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const requestDelete = async () => {
    if (deleteConfirm !== "DELETE") { setDeleteMsg("Type DELETE to confirm."); return; }
    await sb.from("support_messages").insert([{
      from_uid: session.uid, from_name: session.fullName||session.name,
      text: `⚠️ ACCOUNT DELETION REQUEST from ${session.fullName||session.name} (${session.email}) — User ID: ${session.uid}`,
      created_at: new Date().toISOString(), read: false
    }]);
    setDeleteMsg("✅ Deletion request sent. We'll process it within 48 hours.");
    setDeleteConfirm("");
  };

  const signOutAll = async () => {
    if (!window.confirm("Sign out from all devices?")) return;
    await sb.auth.signOut({ scope: "global" });
    onLogout();
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:2000,display:"flex",alignItems:"flex-end",justifyContent:"center"}} onClick={onClose}>
      <div style={{background:bg,borderRadius:"24px 24px 0 0",width:"100%",maxWidth:540,maxHeight:"92vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
        {/* Header */}
        <div style={{padding:"20px 20px 0",display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          {section!=="main"
            ? <button onClick={()=>setSection("main")} style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",color:BLUE,fontWeight:700,fontSize:15,cursor:"pointer",padding:0}}>‹ Back</button>
            : <div/>}
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:20,color:textPrimary}}>
            {section==="main"?"🔒 Privacy & Security":section==="password"?"🔑 Change Password":section==="data"?"📤 My Data":section==="notifications"?"🔔 Notifications":section==="delete"?"🗑 Delete Account":section==="sessions"?"📱 Sessions":"📄 Legal"}
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:textMuted}}>✕</button>
        </div>

        <div style={{padding:"0 16px 40px"}}>

          {/* MAIN MENU */}
          {section==="main" && (
            <div style={{borderRadius:18,background:cardBg,overflow:"hidden",marginBottom:16}}>
              {[
                {icon:"🔑",label:"Change Password",sub:"Update your login password",id:"password",bg:"rgba(232,150,46,.08)"},
                {icon:"📱",label:"Active Sessions",sub:"See & manage where you're logged in",id:"sessions",bg:"rgba(76,175,80,.1)"},
                {icon:"🔔",label:"Notifications",sub:"Email and push preferences",id:"notifications",bg:"rgba(255,193,7,.1)"},
                {icon:"👁",label:"Data Usage",sub:"What TruckPilot stores about you",id:"usage",bg:"rgba(33,150,243,.1)"},
                {icon:"📤",label:"Export My Data",sub:"Download all your loads & expenses",id:"data",bg:"rgba(0,150,136,.1)"},
                {icon:"📄",label:"Legal & Policies",sub:"Privacy policy, terms of service",id:"legal",bg:"rgba(100,100,100,.1)"},
                {icon:"🗑",label:"Delete Account",sub:"Permanently remove your account",id:"delete",bg:"rgba(229,57,53,.1)"},
              ].map((item,i,arr) => (
                <div key={item.id} style={i===arr.length-1?{...rowLastStyle,cursor:"pointer"}:{...rowStyle}} onClick={()=>setSection(item.id)}>
                  <div style={{...iconStyle,background:item.bg}}>{item.icon}</div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:15,fontWeight:600,color:item.id==="delete"?"#E53935":textPrimary}}>{item.label}</div>
                    <div style={{fontSize:12,color:textMuted,marginTop:1}}>{item.sub}</div>
                  </div>
                  <span style={{fontSize:14,color:textMuted}}>›</span>
                </div>
              ))}
            </div>
          )}

          {/* CHANGE PASSWORD */}
          {section==="password" && (
            <div>
              <div style={{borderRadius:18,background:cardBg,padding:"18px",marginBottom:16}}>
                <div style={{marginBottom:14}}>
                  <label style={{fontSize:12,fontWeight:700,color:textMuted,textTransform:"uppercase",letterSpacing:1}}>New Password</label>
                  <input type="password" value={newPass} onChange={e=>setNewPass(e.target.value)} placeholder="Min 6 characters" style={inputStyle} />
                </div>
                <div style={{marginBottom:16}}>
                  <label style={{fontSize:12,fontWeight:700,color:textMuted,textTransform:"uppercase",letterSpacing:1}}>Confirm Password</label>
                  <input type="password" value={confirmPass} onChange={e=>setConfirmPass(e.target.value)} placeholder="Repeat new password" style={inputStyle} />
                </div>
                {passMsg && <div style={{fontSize:13,fontWeight:600,color:passMsg.startsWith("✅")?"#4CAF50":"#E53935",marginBottom:12}}>{passMsg}</div>}
                <button onClick={changePassword} disabled={passLoading} style={{width:"100%",padding:"13px",borderRadius:12,border:"none",background:BLUE,color:"#fff",fontWeight:800,fontSize:15,cursor:"pointer",fontFamily:"inherit"}}>
                  {passLoading?"Updating...":"Update Password"}
                </button>
              </div>
              <div style={{borderRadius:18,background:cardBg,padding:"16px 18px"}}>
                <div style={{fontSize:13,fontWeight:700,color:textPrimary,marginBottom:8}}>🚪 Sign Out All Devices</div>
                <div style={{fontSize:12,color:textMuted,marginBottom:12}}>This will sign you out everywhere including this device.</div>
                <button onClick={signOutAll} style={{width:"100%",padding:"11px",borderRadius:12,border:"1.5px solid #E53935",background:"transparent",color:"#E53935",fontWeight:700,fontSize:14,cursor:"pointer",fontFamily:"inherit"}}>
                  Sign Out All Devices
                </button>
              </div>
            </div>
          )}

          {/* SESSIONS */}
          {section==="sessions" && (
            <div style={{borderRadius:18,background:cardBg,padding:"18px",marginBottom:16}}>
              <div style={{textAlign:"center",padding:"20px 0"}}>
                <div style={{fontSize:40,marginBottom:12}}>📱</div>
                <div style={{fontWeight:700,fontSize:16,color:textPrimary,marginBottom:8}}>Current Session</div>
                <div style={{fontSize:13,color:textMuted,marginBottom:4}}>{session.email}</div>
                <div style={{fontSize:12,color:textMuted,marginBottom:20}}>Logged in on this device</div>
                <div style={{background:BLUE,borderRadius:12,padding:"10px 18px",display:"inline-block",color:"#fff",fontSize:12,fontWeight:700}}>✅ Active</div>
              </div>
              <button onClick={signOutAll} style={{width:"100%",padding:"11px",borderRadius:12,border:"1.5px solid #E53935",background:"transparent",color:"#E53935",fontWeight:700,fontSize:14,cursor:"pointer",fontFamily:"inherit",marginTop:8}}>
                🚪 Sign Out All Devices
              </button>
            </div>
          )}

          {/* NOTIFICATIONS */}
          {section==="notifications" && (
            <div style={{borderRadius:18,background:cardBg,overflow:"hidden",marginBottom:16}}>
              {[
                {label:"Email Notifications",sub:"Load updates, reports, reminders",val:notifEmail,set:setNotifEmail},
                {label:"Push Notifications",sub:"Alerts on your phone",val:notifPush,set:setNotifPush},
              ].map((item,i,arr) => (
                <div key={item.label} style={i===arr.length-1?rowLastStyle:rowStyle}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:15,fontWeight:600,color:textPrimary}}>{item.label}</div>
                    <div style={{fontSize:12,color:textMuted,marginTop:1}}>{item.sub}</div>
                  </div>
                  <div onClick={()=>item.set(v=>!v)} style={{width:46,height:26,borderRadius:13,background:item.val?BLUE:"#D1D5DB",cursor:"pointer",position:"relative",transition:"background .2s",flexShrink:0}}>
                    <div style={{position:"absolute",top:3,left:item.val?23:3,width:20,height:20,borderRadius:"50%",background:"#fff",transition:"left .2s"}}/>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* EXPORT DATA */}
          {section==="data" && (
            <div>
              <div style={{borderRadius:18,background:cardBg,padding:"18px",marginBottom:16}}>
                <div style={{fontSize:15,fontWeight:700,color:textPrimary,marginBottom:8}}>📊 Export Your Data</div>
                <div style={{fontSize:13,color:textMuted,marginBottom:16}}>Download all your loads, expenses and activity as a CSV file you can open in Excel or Google Sheets.</div>
                <button onClick={exportData} style={{width:"100%",padding:"13px",borderRadius:12,border:"none",background:BLUE,color:"#fff",fontWeight:800,fontSize:15,cursor:"pointer",fontFamily:"inherit"}}>
                  ⬇️ Download My Data (CSV)
                </button>
              </div>
              <div style={{borderRadius:18,background:cardBg,padding:"16px 18px"}}>
                <div style={{fontSize:13,fontWeight:700,color:textPrimary,marginBottom:6}}>📋 What's included</div>
                {["All loads with dates, routes, earnings","All expenses with categories","Driver pay records","Wait time records"].map(item=>(
                  <div key={item} style={{fontSize:13,color:textMuted,padding:"6px 0",borderBottom:`1px solid ${border}`}}>✓ {item}</div>
                ))}
              </div>
            </div>
          )}

          {/* LEGAL */}
          {section==="legal" && (
            <div style={{borderRadius:18,background:cardBg,overflow:"hidden",marginBottom:16}}>
              {[
                {icon:"📄",label:"Privacy Policy",url:"https://truckpilot.ca/privacy"},
                {icon:"📋",label:"Terms of Service",url:"https://truckpilot.ca/terms"},
                {icon:"🍪",label:"Cookie Policy",url:"https://truckpilot.ca/cookies"},
              ].map((item,i,arr)=>(
                <a key={item.label} href={item.url} target="_blank" rel="noreferrer" style={{...(i===arr.length-1?rowLastStyle:rowStyle),textDecoration:"none"}}>
                  <div style={{...iconStyle,background:"rgba(100,100,100,.1)",fontSize:20}}>{item.icon}</div>
                  <div style={{flex:1,fontSize:15,fontWeight:600,color:textPrimary}}>{item.label}</div>
                  <span style={{fontSize:14,color:textMuted}}>›</span>
                </a>
              ))}
            </div>
          )}

          {/* DATA USAGE */}
          {section==="usage" && (
            <div>
              <div style={{borderRadius:18,background:cardBg,overflow:"hidden",marginBottom:16}}>
                {[
                  {icon:"📦",title:"Your Loads",desc:"Route, date, earnings, wait times, truck number, driver name. Stored securely in your account."},
                  {icon:"🧾",title:"Expenses",desc:"Amount, category, merchant name, date and optional receipt photos."},
                  {icon:"👤",title:"Profile Info",desc:"Your name, email, role (owner/driver), invite code and plan level."},
                  {icon:"🔧",title:"Maintenance Records",desc:"Truck service history, dates and costs you manually enter."},
                  {icon:"📲",title:"Device Data",desc:"We do NOT sell your data to third parties. Ever."},
                ].map((item,i,arr)=>(
                  <div key={item.title} style={{padding:"14px 18px",borderBottom:i<arr.length-1?`1px solid ${border}`:"none"}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
                      <span style={{fontSize:18}}>{item.icon}</span>
                      <span style={{fontWeight:700,fontSize:14,color:textPrimary}}>{item.title}</span>
                    </div>
                    <div style={{fontSize:13,color:textMuted,paddingLeft:28}}>{item.desc}</div>
                  </div>
                ))}
              </div>
              <div style={{borderRadius:18,background:"#E8F5E9",padding:"14px 18px",fontSize:13,color:"#2E7D32",fontWeight:600}}>
                🔒 All data is encrypted and stored securely via Supabase. You can export or delete your data at any time.
              </div>
            </div>
          )}

          {/* DELETE ACCOUNT */}
          {section==="delete" && (
            <div>
              <div style={{borderRadius:18,background:"#FFF5F5",border:"1.5px solid #FFCDD2",padding:"18px",marginBottom:16}}>
                <div style={{fontSize:16,fontWeight:800,color:"#E53935",marginBottom:8}}>⚠️ Delete Account</div>
                <div style={{fontSize:13,color:"#374151",marginBottom:16}}>This will send a deletion request to TruckPilot support. Your account and all data will be permanently removed within 48 hours. This cannot be undone.</div>
                <div style={{marginBottom:12}}>
                  <label style={{fontSize:12,fontWeight:700,color:"#E53935",textTransform:"uppercase",letterSpacing:1}}>Type DELETE to confirm</label>
                  <input value={deleteConfirm} onChange={e=>setDeleteConfirm(e.target.value)} placeholder="DELETE" style={{...inputStyle,border:"1.5px solid #E53935",marginTop:6}} />
                </div>
                {deleteMsg && <div style={{fontSize:13,fontWeight:600,color:deleteMsg.startsWith("✅")?"#4CAF50":"#E53935",marginBottom:12}}>{deleteMsg}</div>}
                <button onClick={requestDelete} style={{width:"100%",padding:"13px",borderRadius:12,border:"none",background:"#E53935",color:"#fff",fontWeight:800,fontSize:15,cursor:"pointer",fontFamily:"inherit"}}>
                  Send Deletion Request
                </button>
              </div>
              <div style={{borderRadius:18,background:cardBg,padding:"14px 18px",fontSize:13,color:textMuted}}>
                Need help instead? Contact us at <strong>support@truckpilot.ca</strong> or call <strong>437-700-5835</strong>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

function TruckEditCard({ truck, darkModeOn, cardBg, cardBorder, textPrimary, textMuted, rowBorder, BLUE, session }) {
  const [editing, setEditing] = useState(false);
  const [truckNum, setTruckNum] = useState(truck.truckNumber || "");
  const [trailerNum, setTrailerNum] = useState(truck.trailerNumber || "");
  const [plate, setPlate] = useState(truck.licensePlate || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const ownerUid = session.ownerUid || session.uid;
      const updatedTruck = { ...truck, truckNumber: truckNum, trailerNumber: trailerNum, licensePlate: plate };
      const allTrucks = await sbGetTrucks(ownerUid);
      const updated = allTrucks.map(t => t.id === truck.id ? updatedTruck : t);
      await sbSaveTrucks(updated, ownerUid);
    } catch(e) { console.error(e); }
    setSaving(false);
    setEditing(false);
  };

  const rowStyle = {display:"flex",alignItems:"center",gap:14,padding:"14px 18px",borderBottom:"1px solid "+rowBorder};
  const rowLastStyle = {display:"flex",alignItems:"center",gap:14,padding:"14px 18px"};
  const iconStyle = {width:38,height:38,borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0};
  const inputStyle = {flex:1,padding:"8px 12px",borderRadius:10,border:"1.5px solid "+BLUE,fontSize:14,fontWeight:600,fontFamily:"inherit",background:darkModeOn?"#1A1A1A":"#fff",color:textPrimary,outline:"none"};

  if (editing) return (
    <div style={{borderRadius:18,background:cardBg,border:"2px solid "+BLUE,overflow:"hidden",marginBottom:20}}>
      <div style={{padding:"14px 18px",borderBottom:"1px solid "+rowBorder}}>
        <div style={{fontSize:13,fontWeight:700,color:BLUE,marginBottom:12}}>✏️ Edit Truck & Trailer</div>
        <div style={{marginBottom:10}}>
          <div style={{fontSize:13,fontWeight:700,color:textMuted,marginBottom:4}}>TRUCK NUMBER</div>
          <input value={truckNum} onChange={e=>setTruckNum(e.target.value)} placeholder="e.g. T-101" style={inputStyle} />
        </div>
        <div style={{marginBottom:10}}>
          <div style={{fontSize:13,fontWeight:700,color:textMuted,marginBottom:4}}>TRAILER NUMBER</div>
          <input value={trailerNum} onChange={e=>setTrailerNum(e.target.value)} placeholder="e.g. TRL-88" style={inputStyle} />
        </div>
        <div style={{marginBottom:16}}>
          <div style={{fontSize:13,fontWeight:700,color:textMuted,marginBottom:4}}>LICENSE PLATE</div>
          <input value={plate} onChange={e=>setPlate(e.target.value)} placeholder="e.g. ABJ 4421" style={inputStyle} />
        </div>
        <div style={{display:"flex",gap:10}}>
          <button onClick={()=>setEditing(false)} style={{flex:1,padding:"11px",borderRadius:12,border:"1.5px solid "+rowBorder,background:"transparent",color:textMuted,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
          <button onClick={save} disabled={saving} style={{flex:2,padding:"11px",borderRadius:12,border:"none",background:BLUE,color:"#fff",fontWeight:800,cursor:"pointer",fontFamily:"inherit",fontSize:14}}>
            {saving?"Saving...":"💾 Save"}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{borderRadius:18,background:cardBg,border:"1px solid "+cardBorder,overflow:"hidden",marginBottom:20}}>
      <div style={rowStyle}>
        <div style={{...iconStyle,background:"rgba(232,150,46,.08)"}}>🚛</div>
        <div style={{flex:1,fontSize:15,fontWeight:600,color:textPrimary}}>Truck No.</div>
        <div style={{fontSize:14,fontWeight:700,color:textMuted}}>{truck.truckNumber ? "TRK-"+truck.truckNumber : "—"}</div>
      </div>
      <div style={rowStyle}>
        <div style={{...iconStyle,background:"rgba(100,100,100,.1)"}}>🔗</div>
        <div style={{flex:1,fontSize:15,fontWeight:600,color:textPrimary}}>Trailer No.</div>
        <div style={{fontSize:14,fontWeight:700,color:textMuted}}>{truck.trailerNumber ? "TRL-"+truck.trailerNumber : "—"}</div>
      </div>
      <div style={rowStyle}>
        <div style={{...iconStyle,background:"rgba(100,100,100,.1)"}}>📋</div>
        <div style={{flex:1,fontSize:15,fontWeight:600,color:textPrimary}}>License Plate</div>
        <div style={{fontSize:14,fontWeight:700,color:textMuted}}>{truck.licensePlate || "—"}</div>
      </div>
      <div style={rowLastStyle}>
        <button onClick={()=>setEditing(true)} style={{width:"100%",padding:"11px",borderRadius:12,border:"1.5px solid "+BLUE,background:"transparent",color:BLUE,fontWeight:700,cursor:"pointer",fontFamily:"inherit",fontSize:13}}>
          ✏️ Edit Truck & Trailer
        </button>
      </div>
    </div>
  );
}

function ProfileTab({ session, loads, trucks, plan, isOwner, onLogout, setTab, setShowSettings, onDarkToggle, darkModeOn, onEditProfile, openUpgrade, lang, changeLang, featureFlags = {} }) {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [profileCfg, setProfileCfg] = useState(null);
  const [notificationsOn, setNotificationsOn] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tp-notifs") ?? "true"); } catch { return true; }
  });
  const scrollRef = useRef(null);
  const scrollKey = `tp-profile-scroll-${session.uid}`;

  useEffect(() => {
    const loadCfg = async () => {
      try {
        const { data: userCfg } = await sb.from("app_config").select("profile_items").eq("id", session.uid).maybeSingle();
        if (userCfg?.profile_items) { setProfileCfg(userCfg.profile_items); return; }
        const { data: globalCfg } = await sb.from("app_config").select("profile_items").eq("id", "global").maybeSingle();
        if (globalCfg?.profile_items) setProfileCfg(globalCfg.profile_items);
      } catch(e) {}
    };
    loadCfg();
  }, [session.uid]);

  useEffect(() => {
    const saved = sessionStorage.getItem(scrollKey);
    if (saved && scrollRef.current) scrollRef.current.scrollTop = parseInt(saved);
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => sessionStorage.setItem(scrollKey, el.scrollTop);
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  try {
    loads = loads || [];
    trucks = trucks || [];
    openUpgrade = openUpgrade || function(){};

    const myLoads = isOwner ? loads : loads.filter(l => l.assignedDriverUid === session.uid || l.addedBy === session.uid || l.user_id === session.uid);
    const name = session.fullName || session.name || "Driver";

    const bg = darkModeOn ? "#1A1A1A" : "#F5F6F8";
    const cardBg = darkModeOn ? "#1C1C1E" : "#FFFFFF";
    const cardBorder = darkModeOn ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.06)";
    const textPrimary = darkModeOn ? "#FFFFFF" : "#1C1C1E";
    const textMuted = darkModeOn ? "rgba(255,255,255,.45)" : "rgba(60,60,67,.6)";
    const secLabelColor = darkModeOn ? "rgba(255,255,255,.4)" : "#8E8E93";
    const tap = (id) => { if(setTab) setTab(id); };

    // Stats
    const grossTotal = myLoads.reduce((sum, l) => sum + (parseFloat(l.earnings) || parseFloat(l.rate) || 0), 0);
    const grossStr = grossTotal >= 1000 ? `$${Math.round(grossTotal / 1000)}k` : `$${Math.round(grossTotal)}`;
    const driverUids = isOwner ? [...new Set(loads.filter(l => l.assignedDriverUid).map(l => l.assignedDriverUid))] : [];
    const planInfo = plan === "pro" ? "\uD83D\uDE80 Owner Pro Plan" : plan === "basic" ? "\uD83D\uDCBC Hauler Plan" : "\u2B50 Beta Plan";

    // Styles
    const secLabel = { fontSize: 11, fontWeight: 700, color: secLabelColor, textTransform: "uppercase", letterSpacing: ".08em", padding: "14px 0 8px" };
    const grid4 = { display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 4 };
    const iconCard = { display:"flex",flexDirection:"column",alignItems:"center",gap:6,cursor:"pointer",padding:"12px 6px",background:cardBg,borderRadius:14,border:`1px solid ${cardBorder}` };
    const iconBoxBg = darkModeOn ? "rgba(232,150,46,.15)" : "rgba(28,43,74,.09)";
    const iconBox = { width:42,height:42,borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,marginBottom:2,background:iconBoxBg,filter:darkModeOn?"drop-shadow(0 1px 3px rgba(232,150,46,0.7))":"drop-shadow(0 1px 3px rgba(28,43,74,0.45))" };
    const iconLbl = { fontSize:10,fontWeight:800,color:darkModeOn?"#E8962E":"#1C2B4A",textAlign:"center",lineHeight:1.25 };
    const rowItem = { display:"flex",alignItems:"center",gap:14,padding:"14px 16px",borderBottom:`1px solid ${cardBorder}`,cursor:"pointer" };
    const rowLast = { display:"flex",alignItems:"center",gap:14,padding:"14px 16px",cursor:"pointer" };

    // iOS-style toggle
    const Toggle = ({ on, onToggle }) => (
      <button onClick={onToggle} style={{width:51,height:31,borderRadius:16,background:on?"#34C759":"rgba(120,120,128,.2)",border:"none",cursor:"pointer",position:"relative",flexShrink:0,transition:"background .2s",padding:0}}>
        <div style={{position:"absolute",top:2,left:on?22:2,width:27,height:27,borderRadius:"50%",background:"#fff",transition:"left .2s",boxShadow:"0 2px 6px rgba(0,0,0,.25)"}} />
      </button>
    );

    // Icon grid data
    const fleetItems = isOwner ? [
      {icon:"\uD83D\uDC65",label:"Drivers",id:"drivers",bg:"rgba(232,150,46,.1)"},
      {icon:"\uD83D\uDCB5",label:"Payroll",id:"payroll",bg:"rgba(34,197,94,.12)"},
      {icon:"\uD83D\uDD27",label:"Maintenance",id:"maintenance",bg:"rgba(107,114,128,.12)"},
      {icon:"\uD83E\uDEA3",label:"Fuel Log",id:"fuel_log",bg:"rgba(16,185,129,.1)"},
      {icon:"\uD83E\uDDFE",label:"Expenses",id:"expenses",bg:"rgba(239,68,68,.08)"},
      {icon:"🧾",label:"Pay History",id:"pay_history",bg:"rgba(28,43,74,.1)"},
    ] : [
      {icon:"\uD83D\uDCCB",label:"My Loads",id:"log",bg:"rgba(232,150,46,.08)"},
      {icon:"\u2795",label:"Add Load",id:"new",bg:"rgba(255,101,0,.12)"},
      {icon:"\uD83D\uDCCA",label:"Reports",id:"report",bg:"rgba(232,150,46,.08)"},
      {icon:"\uD83E\uDDFE",label:"Expenses",id:"expenses",bg:"rgba(239,68,68,.08)"},
      {icon:"🧾",label:"Pay History",id:"pay_history",bg:"rgba(28,43,74,.1)"},
    ];

    const financeItems = [
      {icon:"🧮",label:"Calculator",id:"profit",bg:"rgba(99,102,241,.12)"},
      {icon:"\uD83D\uDCC8",label:"Analytics",id:"analytics",bg:"rgba(232,150,46,.1)"},
      {icon:"\uD83E\uDDEE",label:"IFTA Tax",id:"ifta",bg:"rgba(245,158,11,.12)"},
      {icon:"\uD83D\uDDC2",label:"Tax Export",id:"tax",bg:"rgba(139,92,246,.12)"},
      {icon:"\uD83D\uDCCB",label:"Fin. Reports",id:"financial_reports",bg:"rgba(232,150,46,.1)"},
    ];

    const opsItems1 = []; // merged into Fleet & Operations above

    const opsItems2 = [
      {icon:"\uD83D\uDCC1",label:"Documents",id:"documents",bg:"rgba(245,158,11,.12)"},
      {icon:"\uD83D\uDCC5",label:"Doc Expiry",id:"doc_expiry",bg:"rgba(245,158,11,.15)"},
      {icon:"\uD83D\uDEA8",label:"Emergency",id:"emergency",bg:"rgba(239,68,68,.12)"},
      {icon:"\uD83D\uDCAC",label:"Messages",id:"contact",bg:"rgba(99,102,241,.12)"},
    ];

    return (
      <div ref={scrollRef} style={{background:bg,minHeight:"100vh",fontFamily:"'Barlow',sans-serif",color:textPrimary}}>
        <div style={{padding:"20px 16px 140px"}}>

          {/* ── Header ── */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
            <div style={{fontSize:28,fontWeight:800,color:textPrimary,fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:".5px"}}>Profile</div>
            <button onClick={()=>{ if(setShowSettings) setShowSettings(true); }}
              style={{width:36,height:36,borderRadius:10,background:iconBoxBg,border:`1.5px solid ${darkModeOn?"rgba(232,150,46,.3)":"rgba(28,43,74,.2)"}`,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,filter:darkModeOn?"drop-shadow(0 1px 3px rgba(232,150,46,0.5))":"drop-shadow(0 1px 3px rgba(28,43,74,0.3))"}}>
              ⚙️
            </button>
          </div>

          {/* ── Avatar + Name ── */}
          <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:20}}>
            <div style={{width:68,height:68,borderRadius:"50%",background:"linear-gradient(135deg,#1C2B4A,#E8962E)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:30,flexShrink:0}}>
              👤
            </div>
            <div>
              {session.companyName && (
                <div style={{fontSize:12,fontWeight:800,color:"#E8962E",textTransform:"uppercase",letterSpacing:1,marginBottom:2}}>
                  🏢 {session.companyName}
                </div>
              )}
              <div style={{fontSize:22,fontWeight:800,color:textPrimary,lineHeight:1.2}}>{name}</div>
              <div style={{fontSize:13,color:textMuted,marginTop:3}}>
                {isOwner ? "Fleet Owner" : "Driver"} · {planInfo}
              </div>
            </div>
          </div>

          {/* ── Stats Row ── */}
          <div style={{display:"grid",gridTemplateColumns:`repeat(${isOwner?4:3},1fr)`,background:cardBg,borderRadius:16,border:`1px solid ${cardBorder}`,marginBottom:24,overflow:"hidden"}}>
            {[
              {val:myLoads.length, lbl:"Loads"},
              ...(isOwner ? [{val:driverUids.length||"—",lbl:"Drivers"}] : []),
              {val:grossStr, lbl:"Gross"},
              {val:trucks.length, lbl:"Trucks"},
            ].map((s,i,arr)=>(
              <div key={s.lbl} style={{padding:"14px 6px",textAlign:"center",borderRight:i<arr.length-1?`1px solid ${cardBorder}`:"none"}}>
                <div style={{fontSize:20,fontWeight:900,color:textPrimary,fontFamily:"'Barlow Condensed',sans-serif"}}>{s.val}</div>
                <div style={{fontSize:10,fontWeight:700,color:textMuted,textTransform:"uppercase",letterSpacing:".06em",marginTop:2}}>{s.lbl}</div>
              </div>
            ))}
          </div>

          {/* ── Fleet ── */}
          <div style={secLabel}>{isOwner?"Fleet & Operations":"My Work"}</div>
          <div style={{...grid4, gridTemplateColumns:`repeat(${fleetItems.length},1fr)`}}>
            {fleetItems.map(t=>(
              <div key={t.id} style={iconCard} onClick={()=>tap(t.id)}>
                <div style={iconBox}>{t.icon}</div>
                <div style={iconLbl}>{t.label}</div>
              </div>
            ))}
          </div>

          {/* ── Finance ── */}
          <div style={secLabel}>Finance</div>
          <div style={grid4}>
            {financeItems.map(t=>(
              <div key={t.id} style={iconCard} onClick={()=>tap(t.id)}>
                <div style={iconBox}>{t.icon}</div>
                <div style={iconLbl}>{t.label}</div>
              </div>
            ))}
          </div>

          {/* ── Tools & Safety ── */}
          {opsItems2.length>0&&<>
          <div style={secLabel}>Tools & Safety</div>
          <div style={grid4}>
            {opsItems2.map(t=>(
              <div key={t.id} style={iconCard} onClick={()=>tap(t.id)}>
                <div style={iconBox}>{t.icon}</div>
                <div style={iconLbl}>{t.label}</div>
              </div>
            ))}
          </div>
          </>}


          {/* ── Account & Settings ── */}
          <div style={secLabel}>Account & Settings</div>
          <div style={{background:cardBg,borderRadius:16,border:`1px solid ${cardBorder}`,overflow:"hidden",marginBottom:12}}>

            {/* Edit Profile */}
            <div style={rowItem} onClick={()=>{ if(onEditProfile) onEditProfile(); }}>
              <div style={{width:34,height:34,borderRadius:10,background:"rgba(232,150,46,.12)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0,filter:darkModeOn?"drop-shadow(0 1px 2px rgba(232,150,46,0.6))":"drop-shadow(0 1px 2px rgba(28,43,74,0.4))"}}>✏️</div>
              <div style={{flex:1}}>
                <div style={{fontSize:15,fontWeight:600,color:textPrimary}}>Edit Profile</div>
                <div style={{fontSize:12,color:textMuted,marginTop:1}}>Name, username, invite code</div>
              </div>
              <span style={{color:textMuted,fontSize:18,lineHeight:1}}>›</span>
            </div>

            {/* AI Assistant */}
            <div style={rowItem} onClick={()=>tap("contact")}>
              <div style={{width:34,height:34,borderRadius:10,background:"rgba(255,101,0,.12)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0,filter:darkModeOn?"drop-shadow(0 1px 2px rgba(232,150,46,0.6))":"drop-shadow(0 1px 2px rgba(28,43,74,0.4))"}}>🤖</div>
              <div style={{flex:1}}>
                <div style={{fontSize:15,fontWeight:600,color:textPrimary}}>AI Assistant</div>
                <div style={{fontSize:12,color:textMuted,marginTop:1}}>Ask anything, get tax help</div>
              </div>
              <span style={{color:textMuted,fontSize:18,lineHeight:1}}>›</span>
            </div>

            {/* Dark Mode */}
            <div style={rowItem}>
              <div style={{width:34,height:34,borderRadius:10,background:"rgba(232,150,46,.08)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0,filter:darkModeOn?"drop-shadow(0 1px 2px rgba(232,150,46,0.6))":"drop-shadow(0 1px 2px rgba(28,43,74,0.4))"}}>🌙</div>
              <div style={{flex:1}}><div style={{fontSize:15,fontWeight:600,color:textPrimary}}>Dark Mode</div></div>
              <Toggle on={darkModeOn} onToggle={()=>{ if(onDarkToggle) onDarkToggle(); }} />
            </div>

            {/* Settings */}
            <div style={rowItem} onClick={()=>{ if(setShowSettings) setShowSettings(true); }}>
              <div style={{width:34,height:34,borderRadius:10,background:"rgba(107,114,128,.12)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0,filter:darkModeOn?"drop-shadow(0 1px 2px rgba(232,150,46,0.6))":"drop-shadow(0 1px 2px rgba(28,43,74,0.4))"}}>⚙️</div>
              <div style={{flex:1}}>
                <div style={{fontSize:15,fontWeight:600,color:textPrimary}}>Settings</div>
                <div style={{fontSize:12,color:textMuted,marginTop:1}}>Rates, routes, trucks</div>
              </div>
              <span style={{color:textMuted,fontSize:18,lineHeight:1}}>›</span>
            </div>

            {/* Notifications */}
            <div style={rowItem}>
              <div style={{width:34,height:34,borderRadius:10,background:"rgba(255,149,0,.12)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0,filter:darkModeOn?"drop-shadow(0 1px 2px rgba(232,150,46,0.6))":"drop-shadow(0 1px 2px rgba(28,43,74,0.4))"}}>🔔</div>
              <div style={{flex:1}}><div style={{fontSize:15,fontWeight:600,color:textPrimary}}>Notifications</div></div>
              <Toggle on={notificationsOn} onToggle={()=>{ const v=!notificationsOn; setNotificationsOn(v); try{localStorage.setItem("tp-notifs",JSON.stringify(v));}catch{} }} />
            </div>

            {/* Language */}
            <div style={rowItem}>
              <div style={{width:34,height:34,borderRadius:10,background:"rgba(52,199,89,.1)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0,filter:darkModeOn?"drop-shadow(0 1px 2px rgba(232,150,46,0.6))":"drop-shadow(0 1px 2px rgba(28,43,74,0.4))"}}>🌐</div>
              <div style={{flex:1}}><div style={{fontSize:15,fontWeight:600,color:textPrimary}}>Language</div></div>
              <span style={{fontSize:14,color:textMuted,marginRight:4}}>English</span>
              <span style={{color:textMuted,fontSize:18,lineHeight:1}}>›</span>
            </div>

            {/* Privacy & Security */}
            <div style={rowLast} onClick={()=>setShowPrivacy(true)}>
              <div style={{width:34,height:34,borderRadius:10,background:"rgba(107,114,128,.12)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0,filter:darkModeOn?"drop-shadow(0 1px 2px rgba(232,150,46,0.6))":"drop-shadow(0 1px 2px rgba(28,43,74,0.4))"}}>🔒</div>
              <div style={{flex:1}}><div style={{fontSize:15,fontWeight:600,color:textPrimary}}>Privacy & Security</div></div>
              <span style={{color:textMuted,fontSize:18,lineHeight:1}}>›</span>
            </div>
          </div>

          {/* ── Sign Out Push Button ── */}
          <button
            className="tp-signout-push tp-ripple-wrap"
            onClick={(e)=>{ tpRipple(e); if(onLogout) onLogout(); }}
          >
            🚪 Sign Out
          </button>

        </div>

        {showPrivacy && <PrivacySecurityModal session={session} onClose={()=>setShowPrivacy(false)} onLogout={onLogout} darkModeOn={darkModeOn} />}
      </div>
    );
  } catch(err) {
    return (
      <div style={{padding:40,textAlign:"center",color:"#0B1120"}}>
        <div style={{fontSize:40,marginBottom:16}}>⚠️</div>
        <div style={{fontWeight:700,marginBottom:8}}>Something went wrong</div>
        <div style={{fontSize:13,color:"#374151",marginBottom:24}}>{String(err)}</div>
        <button onClick={()=>{ if(onLogout) onLogout(); }} style={{padding:"12px 24px",background:"#EF4444",color:"#fff",border:"none",borderRadius:12,cursor:"pointer",fontWeight:700}}>Log Out</button>
      </div>
    );
  }
}

function BottomTabBar({ tab, setTab, isOwner, unreadMessages, inspectionAlerts=[], darkMode=false }) {
  const [showFabMenu, setShowFabMenu] = useState(false);
  const leftTabs = [
    { id:"dashboard", icon:"🏠", label:"Home" },
    { id:"log",       icon:"📋", label:"Loads" },
  ];
  const rightTabs = isOwner
    ? [
        { id:"report",  icon:"📊", label:"Reports" },
        { id:"profile", icon:"👤", label:"Profile" },
      ]
    : [
        { id:"report",  icon:"📊", label:"Reports" },
        { id:"profile", icon:"👤", label:"Profile" },
      ];

  // Bell shake when unread count increases
  const prevUnread = useRef(unreadMessages);
  const [bellRing, setBellRing] = useState(false);
  useEffect(() => {
    if (unreadMessages > prevUnread.current) {
      setBellRing(true);
      setTimeout(() => setBellRing(false), 750);
    }
    prevUnread.current = unreadMessages;
  }, [unreadMessages]);

  return (
    <div className="slt-bottom-nav">
      {leftTabs.map(t => {
        const isActive = tab === t.id;
        return (
          <button key={t.id} className={`slt-bottom-tab${isActive?" active":""}`} onClick={(e)=>{ tpRipple(e); setTab(t.id); }} style={{position:"relative",overflow:"hidden"}}>
            <span className="slt-bottom-tab-icon">{t.icon}</span>
            <span className="slt-bottom-tab-label">{t.label}</span>
          </button>
        );
      })}

      {/* Big centre + button — shows choice menu */}
      <div className="slt-bottom-tab slt-center-tab" style={{position:"relative"}}>
        {showFabMenu && (
          <>
            {/* Backdrop */}
            <div onClick={()=>setShowFabMenu(false)}
              style={{position:"fixed",inset:0,zIndex:8990,background:"rgba(0,0,0,0.35)"}}/>
            {/* Menu */}
            <div style={{position:"absolute",bottom:72,left:"50%",transform:"translateX(-50%)",zIndex:9000,display:"flex",flexDirection:"column",gap:8,alignItems:"center"}}>
              <button onClick={()=>{ setShowFabMenu(false); setTab("new"); }}
                style={{display:"flex",alignItems:"center",gap:10,padding:"13px 22px",borderRadius:16,background:"#1C2B4A",color:"#fff",fontWeight:800,fontSize:14,border:"none",cursor:"pointer",boxShadow:"0 6px 24px rgba(0,0,0,0.45)",whiteSpace:"nowrap",minWidth:170}}>
                <span style={{fontSize:20}}>📦</span> Add Load
              </button>
              <button onClick={()=>{ setShowFabMenu(false); setTab("expenses"); }}
                style={{display:"flex",alignItems:"center",gap:10,padding:"13px 22px",borderRadius:16,background:"#E8962E",color:"#fff",fontWeight:800,fontSize:14,border:"none",cursor:"pointer",boxShadow:"0 6px 24px rgba(0,0,0,0.35)",whiteSpace:"nowrap",minWidth:170}}>
                <span style={{fontSize:20}}>🧾</span> Add Expense
              </button>
            </div>
          </>
        )}
        <button
          className={`slt-fab-btn tp-ripple-wrap${showFabMenu?" tp-fab-open":""}`}
          onClick={(e)=>{ tpRipple(e); setShowFabMenu(m=>!m); }}
          aria-label="Quick Add"
          style={showFabMenu?{transform:"rotate(45deg)",transition:"transform .2s"}:{transition:"transform .2s"}}
        >
          <span className="slt-fab-plus" style={{ fontSize: 30, lineHeight: 1, color: darkMode ? "#0B1120" : "#fff", fontWeight: 300 }}>+</span>
        </button>
      </div>

      {rightTabs.map(t => {
        const isActive = tab === t.id;
        const badge = (t.id === "support_inbox" || t.id === "contact") && unreadMessages > 0 ? unreadMessages : 0;
        const isMsg = t.id === "contact" || t.id === "support_inbox";
        return (
          <button key={t.id} className={`slt-bottom-tab${isActive?" active":""}`} onClick={(e)=>{ tpRipple(e); setTab(t.id); }} style={{position:"relative",overflow:"hidden"}}>
            {badge > 0 && <span className="slt-bottom-tab-badge">{badge}</span>}
            <span className={`slt-bottom-tab-icon${isMsg && bellRing ? " tp-bell-ring" : ""}`}>{t.icon}</span>
            <span className="slt-bottom-tab-label">{t.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ─── SKELETON LOADER ──────────────────────────────────────────────────────────
function SkeletonCard({ rows=3 }) {
  return (
    <div style={{ background:"#fff", borderRadius:14, padding:"16px 18px", marginBottom:12, boxShadow:"0 1px 6px rgba(0,0,0,0.06)" }}>
      {Array.from({length:rows}).map((_,i) => (
        <div key={i} className="slt-skeleton" style={{ height:i===0?20:14, width:i===0?"60%":i===1?"80%":"40%", marginBottom:i<rows-1?10:0 }} />
      ))}
    </div>
  );
}

function SkeletonDashboard() {
  return (
    <div style={{ padding:"16px" }}>
      {[1,2,3].map(i => <SkeletonCard key={i} rows={3} />)}
    </div>
  );
}

// ─── ONBOARDING SCREEN ────────────────────────────────────────────────────────
function OnboardingScreen({ session, isOwner, onDone }) {
  const [step, setStep] = useState(0);
  
  const steps = isOwner ? [
    {
      icon: "🚛",
      title: "Welcome to TruckPilot!",
      desc: "Your fleet command center. Log loads, track drivers, and maximize your profits.",
      cta: "Let's get started"
    },
    {
      icon: "👥",
      title: "Add Your Drivers",
      desc: "Share your invite code with drivers so they can join your fleet. Go to Drivers → copy your code.",
      cta: "Got it"
    },
    {
      icon: "➕",
      title: "Post Your First Load",
      desc: "Tap Add Load to log a haul. Set the route, earnings, and assign a driver.",
      cta: "I'm ready!"
    }
  ] : [
    {
      icon: "✈️",
      title: `Welcome, ${session.fullName?.split(" ")[0] || "Driver"}!`,
      desc: "TruckPilot helps you log loads, track pay, and save on taxes. Simple and fast.",
      cta: "Let's go"
    },
    {
      icon: "➕",
      title: "Log Your Loads",
      desc: "Every load takes 30 seconds to log. Tap Add Load → pick your route → done.",
      cta: "Easy enough"
    },
    {
      icon: "💰",
      title: "Track Your Pay & Taxes",
      desc: "See your earnings and expenses in Reports. Download your tax summary anytime.",
      cta: "I'm ready!"
    }
  ];

  const current = steps[step];
  const isLast = step === steps.length - 1;

  return (
    <div style={{ position:"fixed", inset:0, background:"linear-gradient(160deg,#0A1628,#E8962E)", zIndex:99999, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:32 }}>
      {/* Progress dots */}
      <div style={{ display:"flex", gap:8, marginBottom:48 }}>
        {steps.map((_, i) => (
          <div key={i} style={{ width: i===step?24:8, height:8, borderRadius:4, background: i===step?"#E8962E":"rgba(255,255,255,0.3)", transition:"all 0.3s" }} />
        ))}
      </div>

      {/* Content */}
      <div style={{ textAlign:"center", maxWidth:340 }}>
        <div style={{ fontSize:80, marginBottom:24 }}>{current.icon}</div>
        <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:26, color:"#fff", marginBottom:16, lineHeight:1.2 }}>
          {current.title}
        </div>
        <div style={{ fontSize:15, color:"rgba(255,255,255,0.7)", lineHeight:1.7, marginBottom:48 }}>
          {current.desc}
        </div>
      </div>

      {/* CTA Button */}
      <button onClick={() => { if (isLast) onDone(); else setStep(s => s+1); }}
        style={{ background:"linear-gradient(135deg,#E8962E,#E8962E)", border:"none", borderRadius:50, color:"#fff", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:16, padding:"16px 48px", cursor:"pointer", boxShadow:"0 4px 24px rgba(0,188,212,0.5)" }}>
        {current.cta} →
      </button>

      {/* Skip */}
      {!isLast && (
        <button onClick={onDone} style={{ marginTop:20, background:"none", border:"none", color:"rgba(255,255,255,0.4)", fontSize:13, cursor:"pointer" }}>
          Skip intro
        </button>
      )}
    </div>
  );
}

function NavBar({ session, tab, setTab, setShowSettings, onLogout, isOwner, isSuperAdmin=false, unreadMessages, navItems, plan, openUpgrade, onEditProfile=()=>{}, onDarkToggle=()=>{}, darkModeOn=false }) {
  const [showProfile,setShowProfile]=useState(false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target) &&
          triggerRef.current && !triggerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Lock background scroll when dropdown is open
  useEffect(() => {
    if (open) {
      // Save scroll position before locking
      const scrollY = window.scrollY;
      document.body.style.overflow = "hidden";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.dataset.scrollY = scrollY;
    } else {
      // Restore scroll position
      const scrollY = parseInt(document.body.dataset.scrollY || "0");
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      window.scrollTo(0, scrollY);
    }
    return () => {
      const scrollY = parseInt(document.body.dataset.scrollY || "0");
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  const items = navItems || [];
  const activeItem = items.find(i => i.id === tab) || items[0];
  useEffect(()=>{
    const close=(e)=>{ if(!e.target.closest(".slt-nav-right")) setShowProfile(false); };
    document.addEventListener("mousedown",close);
    return ()=>document.removeEventListener("mousedown",close);
  },[]);
  const initials = (session.fullName || session.name || "U").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  const avatarGradient = isSuperAdmin
    ? "linear-gradient(135deg,#E8962E,#E8962E)"
    : isOwner
    ? "linear-gradient(135deg,#E8962E,#E8962E)"
    : "linear-gradient(135deg,#E8962E,#FFAB40)";

  return (
    <nav className="slt-nav">
      {/* ── LIVE badge (top-left) ── */}
      <div style={{display:"flex",alignItems:"center",flex:1}}>
        <div style={{display:"flex",alignItems:"center",gap:5,background:"rgba(34,197,94,0.12)",border:"1px solid rgba(34,197,94,0.28)",borderRadius:20,padding:"5px 12px"}}>
          <div style={{width:6,height:6,borderRadius:"50%",background:"#22C55E",boxShadow:"0 0 6px rgba(34,197,94,0.8)",animation:"tp-pulse 2s ease-in-out infinite"}}/>
          <span style={{fontSize:10,fontWeight:800,color:"#22C55E",letterSpacing:"1px",textTransform:"uppercase"}}>LIVE</span>
        </div>
      </div>





      <div className="slt-nav-right" style={{display:"flex",alignItems:"center",gap:10}}>
        {/* Avatar chip */}
        <div onClick={()=>setTab&&setTab("profile")}
          style={{width:34,height:34,borderRadius:"50%",background:"linear-gradient(135deg,#3B82F6,#6366F1)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,color:"#fff",cursor:"pointer",flexShrink:0,letterSpacing:"0.5px",border:"2px solid rgba(59,130,246,0.35)",boxShadow:"0 0 0 2px rgba(59,130,246,0.15)"}}>
          {initials}
        </div>
        {/* Nav links for desktop only */}
        <div className="slt-desktop-nav" style={{display:"flex",gap:4,alignItems:"center"}}>
          {["dashboard","new","log","report","profile"].map(t => {
            const labels = {dashboard:"Home",new:"Add Load",log:"My Loads",report:"Reports",profile:"Profile"};
            const icons = {dashboard:"🏠",new:"➕",log:"📋",report:"📊",profile:"👤"};
            return (
              <button key={t} onClick={()=>setTab(t)}
                style={{padding:"6px 12px",borderRadius:20,border:"none",cursor:"pointer",fontSize:12,fontWeight:700,
                  background:tab===t?"rgba(232,150,46,.15)":"transparent",
                  color:tab===t?"#E8962E":"rgba(255,255,255,.6)",fontFamily:"'Barlow',sans-serif",whiteSpace:"nowrap"}}>
                {icons[t]} {labels[t]}
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
}


// ─── Auth Screen ──────────────────────────────────────────────────────────────
function AuthScreen({ onLogin, loginNotifs, onDismissNotif }) {
  const [mode, setMode] = useState("login");
  const [role, setRole] = useState("owner");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [fullName, setFullName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [pass, setPass] = useState("");
  const [invite, setInvite] = useState("");
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState("error");
  const [loading, setLoading] = useState(false);
  const [shakeKey, setShakeKey] = useState(0); // increments to retrigger shake
  // loginInput can be email or username
  const [loginInput, setLoginInput] = useState("");

  // Check for existing Supabase session on mount
  useEffect(() => {
    sb.auth.getSession().then(({ data: { session } }) => {
      if (session) buildSessionFromSupabase(session);
    });
    const { data: { subscription } } = sb.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) buildSessionFromSupabase(session);
      // Ignore TOKEN_REFRESHED — prevents re-render on every token refresh
    });
    return () => subscription.unsubscribe();
  }, []);

  const buildSessionFromSupabase = async (sbSession) => {
    const uid = sbSession.user.id;
    const meta = sbSession.user.user_metadata || {};
    const profile = await sbGetProfile(uid);
    const sess = {
      uid, email: sbSession.user.email,
      fullName: profile?.name || meta.name || sbSession.user.email,
      name: profile?.name || meta.name || sbSession.user.email,
      role: profile?.role || meta.role || "owner",
      plan: profile?.plan || meta.plan || "free",
      supabase: true,
    };
    saveSession(sess);
    // Update last_seen
    try { await sb.from("profiles").update({ last_seen: new Date().toISOString() }).eq("id", uid); } catch(e) {}
    // Show onboarding for first-time users
    const onboardedKey = `tp-onboarded-${sess.uid}`;
    if (!localStorage.getItem(onboardedKey)) {
      // Will be handled by main app
    }
    onLogin(sess);
  };

  const showMsg = (text, type = "error") => { setMsg(text); setMsgType(type); if (type === "error") setShakeKey(k => k + 1); };

  const resolveEmailFromUsername = async (input) => {
    // If input contains @, it's an email — use directly
    if (input.includes("@")) return input.trim().toLowerCase();
    // Otherwise treat as username — look up email from profiles
    const { data } = await sb.from("profiles").select("id").eq("username", input.trim().toLowerCase()).maybeSingle();
    if (!data) return null;
    // Get email from auth using the profile id
    const { data: authData } = await sb.from("profiles").select("id, name").eq("id", data.id).maybeSingle();
    // We store username→email mapping in profiles.username_email
    const { data: prof } = await sb.from("profiles").select("username_email").eq("username", input.trim().toLowerCase()).maybeSingle();
    return prof?.username_email || null;
  };

  const submit = async () => {
    setMsg(""); setLoading(true);
    try {
      if (mode === "login") {
        const input = loginInput.trim();
        if (!input || !pass.trim()) return showMsg("Enter your email or username and password.");
        let loginEmail = input;
        if (!input.includes("@")) {
          // Username login — look up their email
          const { data: prof } = await sb.from("profiles")
            .select("username_email, name")
            .eq("username", input.toLowerCase())
            .maybeSingle();
          if (!prof?.username_email) return showMsg("Username not found. Try your email instead.");
          loginEmail = prof.username_email;
        }
        const { data, error } = await sb.auth.signInWithPassword({ email: loginEmail, password: pass });
        if (error) return showMsg("Wrong email/username or password. Please try again.");
      } else {
        if (!email.trim() || !pass.trim() || !fullName.trim()) return showMsg("All fields are required.");
        if (pass.length < 6) return showMsg("Password must be at least 6 characters.");
        // Username validation
        const usernameVal = username.trim().toLowerCase();
        if (usernameVal.length < 3) return showMsg("Username must be at least 3 characters.");
        if (!/^[a-z0-9_]+$/.test(usernameVal)) return showMsg("Username can only contain letters, numbers and underscores.");
        // Check username not taken
        const { data: existingUser } = await sb.from("profiles").select("id").eq("username", usernameVal).maybeSingle();
        if (existingUser) return showMsg("Username already taken. Choose another.");
        const { data, error } = await sb.auth.signUp({
          email: email.trim(), password: pass,
          options: { data: { name: fullName.trim(), role, plan: "free" } }
        });
        if (error) return showMsg(error.message);
        console.log("SignUp data:", JSON.stringify({user: data.user?.id, session: data.session?.user?.id, identities: data.user?.identities?.length}));
        const userId = data.user?.id || data.session?.user?.id;
        if (userId) {
          const uid = userId;
          await sbSaveProfile({
            id: uid, name: fullName.trim(), role,
            plan: "free",
            username: usernameVal,
            username_email: email.trim().toLowerCase(),
            company_name: businessName.trim() || null,
          });
          // Send welcome email
          try {
            const emailRes = await fetch(`${SUPABASE_URL}/functions/v1/resend-email`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${SUPABASE_ANON_KEY}`,
              },
              body: JSON.stringify({ to: email.trim(), name: fullName.trim() }),
            });
            const emailData = await emailRes.json();
            console.log("Welcome email response:", JSON.stringify(emailData));
          } catch(e) { console.log("Welcome email failed:", e); }
          const { error: signInErr } = await sb.auth.signInWithPassword({ email: email.trim(), password: pass });
          if (signInErr) {
            showMsg("Account created! Please sign in.", "success");
            setMode("login");
          }
        }
      }
    } catch (e) {
      showMsg("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const authInput = { width: "100%", padding: "12px 15px", border: "1.5px solid rgba(255,255,255,0.15)", borderRadius: 10, fontSize: 14, color: "#fff", background: "rgba(255,255,255,0.07)", outline: "none", fontFamily: "'Barlow',sans-serif", marginBottom: 14, boxSizing: "border-box" };
  const authLabel = { display: "block", fontSize: 12.5, fontWeight: 700, color: "rgba(255,255,255,0.6)", marginBottom: 6, fontFamily: "'Barlow',sans-serif", letterSpacing: 0.3 };

  return (
    <div className="slt-auth-bg">
      {/* Login screen notifications */}
      {loginNotifs && loginNotifs.map((n, idx) => {
        const colors = { update:"#E8962E", maintenance:"#B45309", announcement:"#166534", info:"#E8962E" };
        const icons  = { update:"🚀", maintenance:"🔧", announcement:"📣", info:"💬" };
        return (
          <div key={n.id} style={{
            position:"fixed", top: idx * 52, left:0, right:0, zIndex:9999,
            background: colors[n.type]||"#E8962E", color:"#fff",
            padding:"12px 48px 12px 16px", fontSize:14, fontWeight:700,
            display:"flex", alignItems:"center", gap:10,
            boxShadow:"0 2px 8px rgba(0,0,0,0.55)"
          }}>
            <span style={{fontSize:18}}>{icons[n.type]||"💬"}</span>
            <div style={{flex:1}}>
              {n.title && <span style={{fontWeight:900, marginRight:8}}>{n.title}:</span>}
              {n.message}
            </div>
            <button onClick={()=>onDismissNotif&&onDismissNotif(n.id)} style={{
              position:"absolute", right:12, top:"50%", transform:"translateY(-50%)",
              background:"rgba(255,255,255,0.2)", border:"none", color:"#fff",
              borderRadius:"50%", width:28, height:28, fontSize:16, cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"center", fontWeight:900
            }}>✕</button>
          </div>
        );
      })}
      <div style={{ width: "100%", maxWidth: 440, position: "relative" }}>
        {/* Brand */}
        <div className="tp-su-1" style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 20, padding: "18px 28px", marginBottom: 16, width: "100%" }}>
            {/* Logo */}
            <div className="tp-logo-pop" style={{ display: "flex", alignItems: "center", gap: 12, justifyContent: "center" }}>
              <img src="data:image/png;base64,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" style={{height:"70px",width:"auto",display:"block"}} alt="TruckPilot" />
            </div>
            {/* Tagline */}
            <div style={{ fontFamily: "'Barlow',sans-serif", fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.7)", letterSpacing: 2, textTransform: "uppercase", whiteSpace: "nowrap", textAlign: "center" }}>
              Log Loads · Save Taxes · Stay Compliant
            </div>
          </div>
        </div>

        {/* Card */}
        <div key={shakeKey} className={`slt-auth-card tp-su-2${shakeKey > 0 ? " tp-shake" : ""}`}>
          {/* Mode toggle */}
          <div style={{ display: "flex", background: "rgba(255,255,255,0.06)", borderRadius: 10, padding: 4, marginBottom: 24, gap: 4 }}>
            {[["login","Sign In"],["register","Create Account"]].map(([m, l]) => (
              <button key={m} onClick={() => { setMode(m); setMsg(""); }}
                style={{ flex: 1, padding: "9px", borderRadius: 8, border: "none", background: mode === m ? "#fff" : "transparent", color: mode === m ? C.navy : "rgba(255,255,255,0.6)", fontSize: 13.5, fontWeight: 700, cursor: "pointer", fontFamily: "'Barlow',sans-serif", transition: "all 0.2s" }}>
                {l}
              </button>
            ))}
          </div>

          {mode === "register" && (
            <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
              {[["owner","🔑 Owner"],["driver","🚛 Driver"]].map(([r, l]) => (
                <button key={r} onClick={() => setRole(r)}
                  style={{ flex: 1, padding: "12px", borderRadius: 12, border: `2px solid ${role === r ? "#FFD700" : "rgba(255,255,255,0.25)"}`, background: role === r ? "rgba(255,215,0,0.15)" : "rgba(255,255,255,0.08)", color: role === r ? "#FFD700" : "#fff", fontSize: 15, fontWeight: 800, cursor: "pointer", fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 1 }}>
                  {l}
                </button>
              ))}
            </div>
          )}

          {/* LOGIN — email or username */}
          {mode === "login" && (
            <div><label style={authLabel}>Email or Username</label><input className="slt-input" value={loginInput} onChange={e => setLoginInput(e.target.value)} placeholder="you@email.com or your_username" style={authInput} onKeyDown={e=>{if(e.key==="Enter")submit();}}/></div>
          )}

          {/* REGISTER fields */}
          {mode === "register" && (
            <>
              <div><label style={authLabel}>Full Name</label><input className="slt-input" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Your full name" style={authInput} /></div>
              <div><label style={authLabel}>Business Name <span style={{fontWeight:500,opacity:0.6}}>(optional)</span></label><input className="slt-input" value={businessName} onChange={e => setBusinessName(e.target.value)} placeholder="e.g. ABC Trucking Ltd." style={authInput} /></div>
              <div><label style={authLabel}>Email Address</label><input className="slt-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" style={authInput} /></div>
              <div><label style={authLabel}>Username <span style={{fontWeight:500,opacity:0.6}}>(for quick login)</span></label><input className="slt-input" value={username} onChange={e => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g,""))} placeholder="e.g. john_driver" style={authInput} /></div>
            </>
          )}

          <div><label style={authLabel}>Password</label><input className="slt-input" type="password" value={pass} onChange={e => setPass(e.target.value)} placeholder={mode==="register"?"Min. 6 characters":"Enter password"} style={authInput} onKeyDown={e=>{if(e.key==="Enter")submit();}}/></div>
          {mode === "register" && role === "driver" && (
            <div>
              <label style={authLabel}>Owner Invite Code <span style={{fontWeight:500,opacity:0.6}}>(optional — you can join a fleet later)</span></label>
              <input className="slt-input" value={invite} onChange={e => setInvite(e.target.value.toUpperCase())} placeholder="Leave blank to sign up solo" style={{ ...authInput, textTransform: "uppercase", letterSpacing: invite ? 6 : 1, textAlign: "center", fontSize: invite ? 16 : 13 }} />
            </div>
          )}

          {msg && <div style={{ background: msgType==="success"?"rgba(0,137,123,0.2)":"rgba(229,57,53,0.15)", border: `1px solid ${msgType==="success"?"rgba(0,137,123,0.5)":"rgba(229,57,53,0.35)"}`, borderRadius: 9, padding: "10px 14px", color: msgType==="success"?"#80cbc4":"#ff8a80", fontSize: 13, marginBottom: 14, fontFamily: "'Barlow',sans-serif" }}>{msg}</div>}

          <button className="slt-btn-primary slt-btn-dark-text tp-ripple-wrap tp-su-3" onClick={(e)=>{ tpRipple(e); submit(); }} disabled={loading} style={{ width: "100%", padding: "13px", fontSize: 15, borderRadius: 10, opacity: loading?0.7:1 }}>
            {loading ? "⏳ Please wait…" : mode === "login" ? "→ Sign In" : "→ Create Account"}
          </button>

          {mode === "login" && (
            <div style={{ textAlign: "center", marginTop: 14 }}>
              <button onClick={async () => {
                const emailVal = loginInput.trim();
                if (!emailVal) { showMsg("Enter your email address first."); return; }
                if (!emailVal.includes("@")) { showMsg("Please enter your email address (not username) to reset password."); return; }
                setLoading(true);
                const { error } = await sb.auth.resetPasswordForEmail(emailVal, { redirectTo: window.location.origin });
                setLoading(false);
                if (error) return showMsg(error.message);
                showMsg("✅ Reset link sent! Check your email.", "success");
              }} style={{ background: "transparent", border: "none", color: "rgba(255,255,255,0.55)", fontSize: 13, cursor: "pointer", textDecoration: "underline", fontFamily: "'Barlow',sans-serif" }}>
                Forgot Password?
              </button>
            </div>
          )}
          <div style={{ textAlign: "center", marginTop: 8 }}>
            <span style={{ color: "rgba(255,255,255,0.25)", fontSize: 13, fontFamily: "'Barlow',sans-serif" }}>
              🔒 Secured by Supabase · Works on all devices
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── BACK BUTTON ─────────────────────────────────────────────────────────────
function BackButton({ onBack, label }) {
  return (
    <button onClick={onBack} style={{
      display:"flex", alignItems:"center", gap:6,
      background:"none", border:"none", cursor:"pointer",
      padding:"10px 16px", fontSize:15, fontWeight:700,
      color:"#E8962E", fontFamily:"'Barlow',sans-serif"
    }}>
      <span style={{fontSize:20, lineHeight:1}}>‹</span>
      <span>{label || "Back"}</span>
    </button>
  );
}

// ─── GLOBAL SEARCH MODAL ─────────────────────────────────────────────────────
function GlobalSearchModal({ session, loads = [], allDrivers = [], onNavigate, onClose }) {
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 80); }, []);

  const expenses = (() => {
    try { return JSON.parse(localStorage.getItem(`tp-expenses-${session.uid}`) || localStorage.getItem(`tp-expensesv2-${session.uid}`) || "[]"); }
    catch { return []; }
  })();

  const q = query.toLowerCase().trim();

  const matchLoads = !submitted || !q ? [] : loads.filter(l => {
    const text = [l.location, l.date, l.driverFullName, l.tmwLoadNumber, l.notes, l.commodity, l.customer, l.reference].join(" ").toLowerCase();
    return text.includes(q);
  }).slice(0, 12);

  const matchExpenses = !submitted || !q ? [] : expenses.filter(e => {
    const text = [e.merchant, e.category, e.date, e.note, String(e.amount || ""), e.taxLabel].join(" ").toLowerCase();
    return text.includes(q) && !e.deleted;
  }).slice(0, 8);

  const matchDrivers = !submitted || !q ? [] : allDrivers.filter(d => {
    const text = [d.fullName, d.name, d.username].join(" ").toLowerCase();
    return text.includes(q);
  }).slice(0, 6);

  const total = matchLoads.length + matchExpenses.length + matchDrivers.length;

  const handleSearch = (e) => { e.preventDefault(); setSubmitted(true); };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:9999, display:"flex", flexDirection:"column", alignItems:"stretch", justifyContent:"flex-start", backdropFilter:"blur(4px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:"#fff", borderRadius:"0 0 22px 22px", padding:"16px 16px 20px", boxShadow:"0 8px 32px rgba(0,0,0,0.18)", maxHeight:"85vh", display:"flex", flexDirection:"column" }}>
        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
          <span style={{ fontSize:22 }}>🔍</span>
          <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:20, color:"#E8962E", flex:1 }}>Search Everything</span>
          <button onClick={onClose} style={{ background:"none", border:"none", fontSize:22, cursor:"pointer", color:"#666", padding:"0 4px" }}>✕</button>
        </div>
        {/* Search form */}
        <form onSubmit={handleSearch} style={{ display:"flex", gap:8, marginBottom:14 }}>
          <input
            ref={inputRef}
            value={query}
            onChange={e => { setQuery(e.target.value); setSubmitted(false); }}
            placeholder="Loads, expenses, drivers, dates, routes…"
            style={{ flex:1, padding:"11px 14px", borderRadius:10, border:"2px solid #E8962E", fontSize:14, fontFamily:"'Barlow',sans-serif", outline:"none" }}
          />
          <button type="submit"
            style={{ padding:"11px 18px", borderRadius:10, background:"#E8962E", color:"#fff", border:"none", fontWeight:800, fontSize:14, cursor:"pointer", fontFamily:"'Barlow',sans-serif" }}>
            Search
          </button>
        </form>
        {/* Results */}
        <div style={{ overflowY:"auto", flex:1 }}>
          {submitted && q && total === 0 && (
            <div style={{ textAlign:"center", padding:"32px 16px", color:"#888" }}>
              <div style={{ fontSize:36, marginBottom:8 }}>🔎</div>
              <div style={{ fontWeight:700 }}>No results for "{query}"</div>
              <div style={{ fontSize:13, marginTop:4 }}>Try a location, date, driver name, or amount</div>
            </div>
          )}

          {matchLoads.length > 0 && (
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:12, fontWeight:800, color:"#E8962E", textTransform:"uppercase", letterSpacing:1, marginBottom:8, padding:"0 2px" }}>📦 Loads ({matchLoads.length})</div>
              {matchLoads.map(l => (
                <div key={l.id} onClick={() => { onNavigate("log"); onClose(); }}
                  style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 12px", borderRadius:10, background:"#F5F6F8", marginBottom:6, cursor:"pointer" }}>
                  <div>
                    <div style={{ fontWeight:700, fontSize:14, color:"#1A1A1A" }}>{l.location || "—"}</div>
                    <div style={{ fontSize:12, color:"#666" }}>{l.date}{l.driverFullName ? ` · ${l.driverFullName}` : ""}{l.tmwLoadNumber ? ` · TMW #${l.tmwLoadNumber}` : ""}</div>
                  </div>
                  <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:16, color:"#E8962E" }}>{fmtC(Number(l.earnings||0))}</div>
                </div>
              ))}
            </div>
          )}

          {matchExpenses.length > 0 && (
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:12, fontWeight:800, color:"#E8962E", textTransform:"uppercase", letterSpacing:1, marginBottom:8, padding:"0 2px" }}>🧾 Expenses ({matchExpenses.length})</div>
              {matchExpenses.map(e => (
                <div key={e.id} onClick={() => { onNavigate("expenses"); onClose(); }}
                  style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 12px", borderRadius:10, background:"#F5F6F8", marginBottom:6, cursor:"pointer" }}>
                  <div>
                    <div style={{ fontWeight:700, fontSize:14, color:"#1A1A1A" }}>{e.merchant || e.taxLabel || e.category || "Expense"}</div>
                    <div style={{ fontSize:12, color:"#666" }}>{e.date}{e.note ? ` · ${e.note}` : ""}</div>
                  </div>
                  <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:16, color:"#E53935" }}>{fmtC(Number(e.amount||0))}</div>
                </div>
              ))}
            </div>
          )}

          {matchDrivers.length > 0 && (
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:12, fontWeight:800, color:"#E8962E", textTransform:"uppercase", letterSpacing:1, marginBottom:8, padding:"0 2px" }}>👥 Drivers ({matchDrivers.length})</div>
              {matchDrivers.map(d => (
                <div key={d.uid} onClick={() => { onNavigate("drivers"); onClose(); }}
                  style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", borderRadius:10, background:"#F5F6F8", marginBottom:6, cursor:"pointer" }}>
                  <div style={{ width:36, height:36, borderRadius:"50%", background:"#E8962E", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:800, fontSize:14, flexShrink:0 }}>
                    {(d.fullName||d.name||"?")[0].toUpperCase()}
                  </div>
                  <div style={{ fontWeight:700, fontSize:14 }}>{d.fullName||d.name}</div>
                </div>
              ))}
            </div>
          )}

          {!submitted && (
            <div style={{ textAlign:"center", padding:"20px 16px", color:"#aaa", fontSize:13 }}>
              Type and press <strong>Search</strong> to find loads, expenses, drivers, dates and more
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
/* eslint-disable */
/**
 * TruckPilot — Redesigned DashboardTab
 * Drop-in replacement for the DashboardTab function in your App.jsx
 *
 * HOW TO INTEGRATE:
 *   1. Copy this entire function (and the helper CSS block at the bottom)
 *   2. In your App.jsx, find: function DashboardTab({ ... })
 *   3. Replace that entire function with this one
 *   4. Paste the <style> block into your GlobalCSS component (or a <style> tag)
 */

function DashboardTab({
  session, loads, rates, isOwner, setTab, allDrivers, trucks,
  plan, openUpgrade, inspectionAlerts = [], onClearAlert,
  setShowAI = () => {}, setAIMode = () => {},
  onRefresh = () => {}, onUpdateLoad = () => {}
}) {
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const [showDashPayment, setShowDashPayment] = useState(false);
  const [dashSbExpenses, setDashSbExpenses] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [pullY, setPullY] = useState(0);
  const [pulling, setPulling] = useState(false);
  const touchStartRef = useRef(0);
  const scrollRef = useRef(null);

  const handleTouchStart = (e) => {
    if (scrollRef.current && scrollRef.current.scrollTop === 0) {
      touchStartRef.current = e.touches[0].clientY;
      setPulling(true);
    }
  };
  const handleTouchMove = (e) => {
    if (!pulling) return;
    const delta = e.touches[0].clientY - touchStartRef.current;
    if (delta > 0) setPullY(Math.min(delta * 0.4, 70));
  };
  const handleTouchEnd = async () => {
    if (pullY > 48) {
      setRefreshing(true);
      setPullY(0);
      await onRefresh();
      setRefreshing(false);
    } else {
      setPullY(0);
    }
    setPulling(false);
  };
  const [bonusAlerts, setBonusAlerts] = useState([]);
  const [earningsTab, setEarningsTab] = useState("paid"); // "paid" | "unpaid"
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("tp-dark") !== null ? localStorage.getItem("tp-dark") === "1" : true);
  const [docAlertDismissed, setDocAlertDismissed] = useState(() => sessionStorage.getItem("tp-doc-alert-dismissed") === "1");
  const [expiringDocs, setExpiringDocs] = useState([]);

  useEffect(() => {
    // Check existing docs from localStorage for expiry
    const docsKey = `tp-docs-${session.uid}`;
    try {
      const docs = JSON.parse(localStorage.getItem(docsKey) || "[]");
      const today = new Date();
      const expiring = docs.filter(d => {
        if (!d.expiry) return false;
        const diff = (new Date(d.expiry) - today) / (1000 * 60 * 60 * 24);
        return diff <= 30;
      });
      setExpiringDocs(expiring);
    } catch(e) {}
  }, [session.uid]);

  useEffect(() => {
    sbGetExpenses(session.uid).then(exps => {
      if (!isOwner) setBonusAlerts(exps.filter(e => e.category === "bonus" && e.source === "bonus" && !e.paid));
      setDashSbExpenses(exps || []);
    }).catch(() => {});
  }, [session.uid, isOwner]);

  const toggleDark = () => {
    setDarkMode(d => { localStorage.setItem("tp-dark", d ? "0" : "1"); return !d; });
  };

  const myLoads = isOwner ? loads : loads.filter(l => l.assignedDriverUid === session.uid || l.addedBy === session.uid || l.user_id === session.uid);
  const active = myLoads.filter(l => !l.completed);
  const done = myLoads.filter(l => l.completed);
  const gross = myLoads.reduce((s, l) => {
    const wm = (Number(l.loadWaitMins) || 0) + (Number(l.offloadWaitMins) || 0);
    return s + Number(l.earnings || 0) + wm / 60 * (Number(rates.companyWaitRate) || 0);
  }, 0);
  const drvPay = myLoads.reduce((s, l) => {
    const wm = (Number(l.loadWaitMins) || 0) + (Number(l.offloadWaitMins) || 0);
    const waitPay = wm / 60 * (Number(rates.driverWaitRate) || 0);
    return s + Number(l.driverBasePay||0) + waitPay;
  }, 0);
  const totalExp = (() => {
    // Merge localStorage + Supabase expenses (deduplicate by id)
    const local = getStored(expensesKey(session.uid));
    const merged = [...local];
    dashSbExpenses.forEach(se => { if (!merged.find(e => e.id === se.id)) merged.push(se); });
    return merged.filter(e => {
      if (e.deleted || e.deleted_at) return false;
      if (isOwner) return e.source !== "load"; // owners see all non-load expenses
      return !e.ownerExpense && e.expenseType !== "business" && e.source !== "load";
    }).reduce((s, e) => s + Number(e.amount || 0), 0);
  })();

  // ── Driver Paid vs Unpaid split ──────────────────────────────────────────
  // "Paid" = owner confirmed payment (driverReceived OR payment_status="paid" OR driverPaid)
  // "Unpaid" = completed loads where driver hasn't been paid yet
  const isPaidLoad = (l) => l.driverReceived || l.payment_status === "paid" || (l.driverPaid && l.driverReceived);
  const driverCompletedLoads = !isOwner ? myLoads.filter(l => l.completed && Number(l.driverBasePay||0) > 0) : [];
  const driverPaidLoads = driverCompletedLoads.filter(l => isPaidLoad(l));
  const driverUnpaidLoads = driverCompletedLoads.filter(l => !isPaidLoad(l));
  const driverPaidTotal = driverPaidLoads.reduce((s,l) => {
    const wm = (Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0);
    return s + Number(l.driverBasePay||0) + wm/60*(Number(rates.driverWaitRate)||0);
  }, 0);
  const driverUnpaidTotal = driverUnpaidLoads.reduce((s,l) => {
    const wm = (Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0);
    return s + Number(l.driverBasePay||0) + wm/60*(Number(rates.driverWaitRate)||0);
  }, 0);
  const driverTotalEarnable = driverPaidTotal + driverUnpaidTotal;
  const driverPaidPct = driverTotalEarnable > 0 ? Math.round((driverPaidTotal / driverTotalEarnable) * 100) : 0;
  // Last payment received
  const lastPaidLoad = driverPaidLoads.sort((a,b) => (b.driverReceivedDate||b.driverPaidDate||b.date||"") > (a.driverReceivedDate||a.driverPaidDate||a.date||"") ? 1 : -1)[0];
  const lastPaidDaysAgo = lastPaidLoad ? Math.floor((new Date() - new Date((lastPaidLoad.driverReceivedDate||lastPaidLoad.driverPaidDate||lastPaidLoad.date)+"T12:00:00")) / (1000*60*60*24)) : null;
  // Oldest unpaid load
  const oldestUnpaid = driverUnpaidLoads.sort((a,b) => (a.date||"") < (b.date||"") ? -1 : 1)[0];
  const oldestUnpaidDays = oldestUnpaid ? Math.floor((new Date() - new Date((oldestUnpaid.date||"")+"T12:00:00")) / (1000*60*60*24)) : null;

  const today = todayStr();
  const todayLoads = myLoads.filter(l => l.date === today);
  const recent = [...myLoads].sort((a, b) => b.date > a.date ? 1 : -1).slice(0, 6);

  const todayEarnings = todayLoads.reduce((s, l) => {
    const wm = (Number(l.loadWaitMins)||0) + (Number(l.offloadWaitMins)||0);
    const waitPay = wm / 60 * (Number(rates.driverWaitRate)||0);
    if (isOwner) {
      const ownerWait = wm / 60 * (Number(rates.companyWaitRate)||0);
      return s + Number(l.earnings||0) + ownerWait;
    }
    const basePay = Number(l.driverBasePay) > 0 ? Number(l.driverBasePay) : Number(l.earnings||0);
    return s + basePay + waitPay;
  }, 0);

  const streak = (() => {
    let count = 0;
    const d = new Date();
    while (true) {
      const ds = d.toISOString().slice(0, 10);
      if (myLoads.some(l => l.date === ds)) { count++; d.setDate(d.getDate() - 1); }
      else break;
    }
    return count;
  })();

  const weekBars = (() => {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const now = new Date();
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(now);
      d.setDate(now.getDate() - (6 - i));
      const ds = d.toISOString().slice(0, 10);
      const dayLoads = myLoads.filter(l => l.date === ds);
      const earn = dayLoads.reduce((s, l) => s + Number(l.earnings || 0), 0);
      return { label: days[d.getDay()], earn, isToday: ds === today };
    });
  })();
  const maxEarn = Math.max(...weekBars.map(b => b.earn), 1);

  // ── Color palette: navy header + toned green ──
  const GREEN      = darkMode ? "#43a047" : "#43a047";
  const GREEN_DIM  = darkMode ? "#2e7d32" : "#2e7d32";
  const ORANGE     = darkMode ? "#e8962e" : "#e8962e";
  const HDR_BG     = darkMode ? "#0f2137" : "#1c2b4a";
  const bg         = darkMode ? "#0b1a2a" : "#f5f6f8";
  const cardBg     = darkMode ? "#112233" : "#ffffff";
  const cardBorder = darkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  const textPrimary= darkMode ? "#eaeaea" : "#1a1a1a";
  const textMuted  = darkMode ? "#4a6a8a" : "rgba(26,26,26,0.4)";
  const textSec    = darkMode ? "#a0a0a0" : "#6a7a8a";
  const altBg      = darkMode ? "#0f2137" : "#f5f6f8";
  const hdrTextMuted = "rgba(255,255,255,0.45)";

  const greeting = (() => {
    const h = new Date().getHours();
    return h < 12 ? "Good morning," : h < 17 ? "Good afternoon," : "Good evening,";
  })();
  const firstName = (session.fullName || session.name || "").split(" ")[0];

  const planLabel = isOwner
    ? (plan === "pro" ? "🚀 Owner Pro" : plan === "basic" ? "💼 Hauler" : "⭐ Beta")
    : (plan === "pro" ? "🚀 Fleet Pro" : plan === "basic" ? "💼 Hauler" : "⭐ Beta");

  const S = {
    root: { fontFamily: "'Barlow', sans-serif", background: bg, minHeight: "100vh", color: textPrimary, transition: "background .3s, color .3s" },
    // Navy header — always dark regardless of mode
    header: { background: HDR_BG, padding: "20px 18px 18px", marginBottom: 0 },
    headerGreet: { fontSize: 12, color: hdrTextMuted, marginBottom: 2 },
    headerName: { fontSize: 18, fontWeight: 700, color: "#ffffff", marginBottom: 12, fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 0.3 },
    headerEarnLbl: { fontSize: 11, color: hdrTextMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 },
    headerEarnNum: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 52, fontWeight: 900, color: GREEN, lineHeight: 1 },
    headerEarnSub: { fontSize: 12, color: hdrTextMuted, marginTop: 4, marginBottom: 14 },
    headerPills: { display: "flex", gap: 6, flexWrap: "wrap" },
    pill: { background: "rgba(67,160,71,0.15)", borderRadius: 20, padding: "4px 10px", fontSize: 11, color: GREEN },
    scroll: { padding: "14px 16px 100px" },
    // 2x2 stat grid
    statGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 },
    statTile: { background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 14, padding: "13px 14px" },
    statNum: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 28, fontWeight: 900, color: GREEN, lineHeight: 1 },
    statNumOrange: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 28, fontWeight: 900, color: ORANGE, lineHeight: 1 },
    statNumPlain: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 28, fontWeight: 900, color: textPrimary, lineHeight: 1 },
    statLbl: { fontSize: 10, color: textMuted, marginTop: 3, textTransform: "uppercase", letterSpacing: "0.08em" },
    // Card
    card: { borderRadius: 16, padding: "16px 18px", background: cardBg, border: `1px solid ${cardBorder}`, marginBottom: 12 },
    sectionHead: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 },
    sectionTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 15, fontWeight: 800, color: textPrimary },
    seeAll: { fontSize: 12, color: GREEN, fontWeight: 700, cursor: "pointer", background: "none", border: "none" },
    // Bar chart
    barsWrap: { display: "flex", alignItems: "flex-end", gap: 6, height: 72, marginTop: 8 },
    barLbls: { display: "flex", justifyContent: "space-around", marginTop: 5 },
    // Load row
    loadItem: { display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: `1px solid ${cardBorder}` },
    loadItemLast: { display: "flex", alignItems: "center", gap: 10, padding: "10px 0 0" },
    loadBar: { width: 3, height: 34, borderRadius: 2, background: GREEN, flexShrink: 0 },
    loadBarOrange: { width: 3, height: 34, borderRadius: 2, background: ORANGE, flexShrink: 0 },
    loadBarMuted: { width: 3, height: 34, borderRadius: 2, background: textMuted, flexShrink: 0 },
    loadInfo: { flex: 1, minWidth: 0 },
    loadRoute: { fontSize: 13, fontWeight: 600, color: textPrimary, lineHeight: 1.2 },
    loadSub: { fontSize: 11, color: textMuted, marginTop: 2 },
    loadAmt: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 16, fontWeight: 900, color: GREEN },
    loadAmtOrange: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 16, fontWeight: 900, color: ORANGE },
    badgeDone: { padding: "3px 9px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: `${GREEN}20`, color: GREEN },
    badgeActive: { padding: "3px 9px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: `${ORANGE}18`, color: ORANGE },
    // Payday card
    paydayCard: { background: HDR_BG, border: `1px solid ${cardBorder}`, borderRadius: 16, padding: "14px 16px", marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" },
    paydayDate: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 26, fontWeight: 900, color: GREEN },
    paydayLbl: { fontSize: 10, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 3 },
    paydayDays: { fontSize: 11, color: GREEN_DIM, marginTop: 3 },
    paydayAmt: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 24, fontWeight: 900, color: "#ffffff" },
    paydaySub: { fontSize: 10, color: "rgba(255,255,255,0.35)", marginTop: 2, textAlign: "right" },
    // Alert
    alertBanner: { borderRadius: 14, padding: "14px 16px", background: darkMode ? `${ORANGE}18` : "#FFF3EE", border: `2px solid ${ORANGE}`, marginBottom: 12, display: "flex", alignItems: "center", gap: 12 },
    truckIcon: { width: 36, height: 36, borderRadius: 10, background: GREEN, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 },
  };

  return (
    <div style={S.root}>

      {/* ── Create Payment Modal (owner) ── */}
      {showDashPayment && (
        <CreatePaymentModal
          session={session}
          loads={loads}
          allDrivers={allDrivers}
          onClose={() => setShowDashPayment(false)}
          onUpdateLoad={onUpdateLoad}
          onPaymentSaved={() => { onRefresh(); }}
        />
      )}

      {/* ── Global Search Modal ── */}
      {showGlobalSearch && (
        <GlobalSearchModal
          session={session}
          loads={loads}
          allDrivers={allDrivers}
          onNavigate={(tab) => setTab(tab)}
          onClose={() => setShowGlobalSearch(false)}
        />
      )}

      {/* ── NAVY HEADER ── */}
      <div style={S.header}>
        {/* Pull-to-refresh indicator inside header */}
        {(pullY > 0 || refreshing) && (
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height: refreshing ? 36 : pullY, overflow:"hidden", transition: pulling?"none":"height 0.25s ease", marginBottom: 8 }}>
            <span style={{ fontSize:18, display:"inline-block", animation: refreshing ? "spin 0.7s linear infinite" : "none" }}>🔄</span>
          </div>
        )}
        {/* Top row: greeting + date pill */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <div>
            <div style={S.headerGreet}>{greeting}</div>
            <div style={{...S.headerName,fontSize:26,marginBottom:0}}>{firstName || "Driver"}</div>
          </div>
          {/* ① Date as pill — same family as chips below, no inconsistent box */}
          <div style={{background:"rgba(232,150,46,0.18)",border:"1px solid rgba(232,150,46,0.35)",borderRadius:20,padding:"5px 13px",flexShrink:0}}>
            <span style={{fontSize:12,fontWeight:700,color:ORANGE}}>{new Date().toLocaleString("default",{month:"short"})} {new Date().getDate()}, {new Date().getFullYear()}</span>
          </div>
        </div>

        {/* Revenue */}
        <div style={S.headerEarnLbl}>💰 {isOwner ? "Month revenue" : "Month earnings"}</div>
        <div style={S.headerEarnNum}>{fmtC(isOwner ? gross : drvPay)}</div>
        {/* ③ Net after expenses — driver's most useful number */}
        {!isOwner && totalExp > 0 && (
          <div style={{fontSize:12,color:hdrTextMuted,marginTop:3,marginBottom:2}}>
            Net after expenses · <span style={{color:GREEN,fontWeight:800}}>{fmtC(drvPay - totalExp)}</span>
          </div>
        )}

        {/* Chips row */}
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:10,marginBottom:12}}>
          <div style={{display:"flex",alignItems:"center",gap:4,background:"rgba(255,255,255,0.08)",borderRadius:20,padding:"4px 10px"}}>
            <span style={{fontSize:13}}>💵</span>
            <span style={{fontSize:12,fontWeight:700,color:"rgba(255,255,255,0.85)"}}>+{fmtC(todayEarnings)} today</span>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:4,background:"rgba(255,255,255,0.08)",borderRadius:20,padding:"4px 10px"}}>
            <span style={{fontSize:13}}>📦</span>
            <span style={{fontSize:12,fontWeight:700,color:"rgba(255,255,255,0.85)"}}>{myLoads.length} load{myLoads.length!==1?"s":""}</span>
          </div>
        </div>

        {/* ② 3 stat boxes — Today tile removed (redundant with chip above) */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
          <div style={{background:"rgba(255,255,255,0.07)",borderRadius:10,padding:"10px 12px",display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:20}}>✅</span>
            <div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:900,color:GREEN,lineHeight:1}}>{done.length}</div>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.4)",textTransform:"uppercase",letterSpacing:"0.5px",marginTop:1}}>Done</div>
            </div>
          </div>
          <div style={{background:"rgba(255,255,255,0.07)",borderRadius:10,padding:"10px 12px",display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:20}}>🚛</span>
            <div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:900,color:ORANGE,lineHeight:1}}>{active.length}</div>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.4)",textTransform:"uppercase",letterSpacing:"0.5px",marginTop:1}}>Active</div>
            </div>
          </div>
          <div style={{background:"rgba(255,255,255,0.07)",borderRadius:10,padding:"10px 12px",display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:20}}>🧾</span>
            <div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:900,color:"#FF5252",lineHeight:1}}>{fmtC(totalExp)}</div>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.4)",textTransform:"uppercase",letterSpacing:"0.5px",marginTop:1}}>Expenses</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Driver Earnings Split (Paid vs Unpaid) — only shown for fleet drivers ── */}
      {!isOwner && driverCompletedLoads.length > 0 && (
        <div style={{ background: darkMode ? "#0f2137" : "#fff", borderBottom:`1px solid ${cardBorder}` }}>
          {/* Smart insight banners */}
          {driverUnpaidLoads.length > 0 && (
            <div style={{ margin:"12px 14px 0", background: darkMode?"rgba(232,150,46,0.12)":"#FFF8EE", border:`1px solid ${ORANGE}40`, borderRadius:10, padding:"9px 12px", display:"flex", alignItems:"center", gap:9 }}>
              <span style={{ fontSize:16, flexShrink:0 }}>⏳</span>
              <div style={{ fontSize:12, fontWeight:700, color: darkMode?"#E8962E":"#92400E", lineHeight:1.4 }}>
                {fmtC(driverUnpaidTotal)} waiting across {driverUnpaidLoads.length} load{driverUnpaidLoads.length!==1?"s":""}
                {oldestUnpaidDays !== null && oldestUnpaidDays > 7 && <span style={{ fontWeight:800 }}> · oldest {oldestUnpaidDays}d ago</span>}
              </div>
            </div>
          )}
          {lastPaidLoad && lastPaidDaysAgo !== null && (
            <div style={{ margin:"8px 14px 0", background: darkMode?"rgba(34,197,94,0.1)":"#F0FDF4", border:"1px solid rgba(34,197,94,0.3)", borderRadius:10, padding:"9px 12px", display:"flex", alignItems:"center", gap:9 }}>
              <span style={{ fontSize:16, flexShrink:0 }}>🗓️</span>
              <div style={{ fontSize:12, fontWeight:700, color: darkMode?"#4ade80":"#166534", lineHeight:1.4 }}>
                Last payment received {lastPaidDaysAgo === 0 ? "today" : `${lastPaidDaysAgo} day${lastPaidDaysAgo!==1?"s":""} ago`} · {fmtC(Number(lastPaidLoad.driverBasePay||0))}
              </div>
            </div>
          )}
          {/* Tabs */}
          <div style={{ display:"flex", background:"rgba(0,0,0,0.06)", borderRadius:10, margin:"12px 14px 0", padding:3 }}>
            {[["💰 Paid","paid"],["⏳ Unpaid","unpaid"]].map(([label,val])=>(
              <button key={val} onClick={()=>setEarningsTab(val)}
                style={{ flex:1, textAlign:"center", padding:"8px", borderRadius:8, fontSize:11, fontWeight:800, cursor:"pointer", border:"none", fontFamily:"inherit", background: earningsTab===val?(val==="paid"?GREEN:ORANGE):"transparent", color: earningsTab===val?"#fff": darkMode?"rgba(255,255,255,0.45)":"rgba(0,0,0,0.4)", transition:"all 0.2s" }}>
                {label}
              </button>
            ))}
          </div>
          {/* Metrics */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, padding:"10px 14px 0" }}>
            {earningsTab === "paid" ? (<>
              <div style={{ background: darkMode?"rgba(34,197,94,0.1)":"#F0FDF4", border:"1px solid rgba(34,197,94,0.25)", borderRadius:12, padding:"12px 10px" }}>
                <div style={{ fontSize:9, fontWeight:800, color:"rgba(34,197,94,0.7)", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>✅ Paid This Month</div>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:22, fontWeight:900, color:"#22C55E", lineHeight:1 }}>{fmtC(driverPaidTotal)}</div>
                <div style={{ fontSize:9, color:"rgba(34,197,94,0.6)", marginTop:3 }}>Actually received</div>
              </div>
              <div style={{ background: darkMode?"rgba(239,68,68,0.08)":"#FFF5F5", border:"1px solid rgba(239,68,68,0.2)", borderRadius:12, padding:"12px 10px" }}>
                <div style={{ fontSize:9, fontWeight:800, color:"rgba(239,68,68,0.7)", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>⏳ Still Owed</div>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:22, fontWeight:900, color:"#EF4444", lineHeight:1 }}>{fmtC(driverUnpaidTotal)}</div>
                <div style={{ fontSize:9, color:"rgba(239,68,68,0.6)", marginTop:3 }}>{driverUnpaidLoads.length} load{driverUnpaidLoads.length!==1?"s":""} pending</div>
              </div>
            </>) : (<>
              <div style={{ background: darkMode?"rgba(239,68,68,0.1)":"#FFF5F5", border:"1px solid rgba(239,68,68,0.25)", borderRadius:12, padding:"12px 10px" }}>
                <div style={{ fontSize:9, fontWeight:800, color:"rgba(239,68,68,0.7)", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>⏳ Total Owed</div>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:22, fontWeight:900, color:"#EF4444", lineHeight:1 }}>{fmtC(driverUnpaidTotal)}</div>
                <div style={{ fontSize:9, color:"rgba(239,68,68,0.6)", marginTop:3 }}>{driverUnpaidLoads.length} load{driverUnpaidLoads.length!==1?"s":""}</div>
              </div>
              <div style={{ background: darkMode?"rgba(232,150,46,0.1)":"#FFFBEB", border:`1px solid ${ORANGE}40`, borderRadius:12, padding:"12px 10px" }}>
                <div style={{ fontSize:9, fontWeight:800, color:`${ORANGE}99`, textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>📅 Oldest Unpaid</div>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:22, fontWeight:900, color:ORANGE, lineHeight:1 }}>{oldestUnpaidDays !== null ? `${oldestUnpaidDays}d` : "—"}</div>
                <div style={{ fontSize:9, color:`${ORANGE}80`, marginTop:3 }}>{oldestUnpaid?.date || "—"}</div>
              </div>
            </>)}
          </div>
          {/* Progress bar */}
          {driverTotalEarnable > 0 && (
            <div style={{ padding:"10px 14px" }}>
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color: darkMode?"rgba(255,255,255,0.4)":"rgba(0,0,0,0.4)", marginBottom:5, fontWeight:700 }}>
                <span>Paid</span>
                <span>{fmtC(driverPaidTotal)} / {fmtC(driverTotalEarnable)} total</span>
              </div>
              <div style={{ height:7, borderRadius:99, background: darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.08)", overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${driverPaidPct}%`, borderRadius:99, background:`linear-gradient(90deg,${GREEN},#16A34A)`, transition:"width 0.8s ease" }} />
              </div>
            </div>
          )}
          {/* Unpaid load list (when on unpaid tab) */}
          {earningsTab === "unpaid" && driverUnpaidLoads.length > 0 && (
            <div style={{ margin:"0 14px 12px", background: darkMode?"rgba(255,255,255,0.03)":"#FAFAFA", border:`1px solid ${cardBorder}`, borderRadius:12, overflow:"hidden" }}>
              <div style={{ padding:"10px 13px 6px", borderBottom:`1px solid ${cardBorder}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div style={{ fontSize:12, fontWeight:800, color: darkMode?"#fff":"#1A1A1A" }}>📋 Unpaid Loads</div>
                <div style={{ fontSize:11, color: darkMode?"rgba(255,255,255,0.4)":"#6B7280" }}>Completed · not received</div>
              </div>
              {driverUnpaidLoads.slice(0,5).map((l,i) => {
                const pay = Number(l.driverBasePay||0);
                const daysAgo = l.date ? Math.floor((new Date() - new Date(l.date+"T12:00:00")) / (1000*60*60*24)) : null;
                const isOverdue = daysAgo !== null && daysAgo > 14;
                return (
                  <div key={l.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"9px 13px", borderBottom: i < driverUnpaidLoads.slice(0,5).length-1 ? `1px solid ${cardBorder}` : "none" }}>
                    <div>
                      <div style={{ fontSize:12, fontWeight:700, color: darkMode?"#fff":"#1A1A1A" }}>{l.location||"Load"}</div>
                      <div style={{ fontSize:10, color: darkMode?"rgba(255,255,255,0.4)":"#6B7280", marginTop:1 }}>{l.date}{daysAgo!==null?` · ${daysAgo}d ago`:""}</div>
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:3 }}>
                      <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:14, color:ORANGE }}>{fmtC(pay)}</div>
                      <span style={{ fontSize:8, fontWeight:800, padding:"2px 6px", borderRadius:20, background: isOverdue?"#FEE2E2":"#FEF3C7", color: isOverdue?"#DC2626":"#B45309" }}>{isOverdue?"OVERDUE":"PENDING"}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div
        ref={scrollRef}
        style={S.scroll}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* ── Document Expiry Alert ── */}
        {!docAlertDismissed && expiringDocs.length > 0 && (
          <div style={{ background: cardBg, border:"2px solid #B45309", borderRadius:14, padding:"14px 16px", marginBottom:12, display:"flex", alignItems:"flex-start", gap:12 }}>
            <span style={{ fontSize:22, flexShrink:0 }}>⚠️</span>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:800, color:"#B45309", marginBottom:4, fontSize:14 }}>Document Expiry Alert</div>
              {expiringDocs.map(d => {
                const days = Math.ceil((new Date(d.expiry) - new Date()) / (1000*60*60*24));
                return <div key={d.id} style={{ fontSize:12, color: days < 0 ? "#EF4444" : "#B45309", marginBottom:2 }}>• {d.name} — {days < 0 ? "EXPIRED" : `${days} days left`}</div>;
              })}
              <button onClick={()=>setTab("documents")} style={{ marginTop:8, padding:"6px 14px", background:"#B45309", color:"#fff", border:"none", borderRadius:8, fontWeight:700, fontSize:12, cursor:"pointer" }}>📁 View Documents</button>
            </div>
            <button onClick={()=>{ setDocAlertDismissed(true); sessionStorage.setItem("tp-doc-alert-dismissed","1"); }} style={{ background:"none", border:"none", color:"#B45309", cursor:"pointer", fontSize:18, padding:"0 4px", flexShrink:0 }}>✕</button>
          </div>
        )}

        {/* ── Bonus Alerts (Driver) ── */}
        {!isOwner && bonusAlerts.length > 0 && bonusAlerts.map(b => (
          <div key={b.id} style={S.alertBanner}>
            <span style={{ fontSize:24 }}>🎁</span>
            <div style={{ flex:1 }}>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:14, color:ORANGE }}>You have a bonus!</div>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:20, color:textPrimary }}>${Number(b.amount||0).toFixed(2)}</div>
              <div style={{ fontSize:12, color:textMuted }}>{b.description?.replace("🎁 Bonus: ","")}</div>
            </div>
            <div style={{ background:ORANGE, color:"#fff", borderRadius:10, padding:"6px 12px", fontSize:12, fontWeight:800 }}>Added</div>
          </div>
        ))}

        {/* ── Inspection Alerts (Owner) ── */}
        {isOwner && inspectionAlerts.filter(a => !a.read).length > 0 && (
          <div style={{ ...S.alertBanner, background: "#FFF5F5", borderColor:"#EF4444" }}>
            <span style={{ fontSize:22 }}>🚨</span>
            <div style={{ flex:1 }}>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:14, color:"#EF4444" }}>
                {inspectionAlerts.filter(a=>!a.read).length} Inspection Issue{inspectionAlerts.filter(a=>!a.read).length>1?"s":""}
              </div>
              <div style={{ fontSize:12, color:textMuted }}>Tap to review reported issues</div>
            </div>
            <button onClick={() => setTab("inspection")} style={{ background:"#EF4444", color:"#fff", border:"none", borderRadius:10, padding:"8px 14px", fontSize:12, fontWeight:700, cursor:"pointer" }}>View →</button>
          </div>
        )}

        {/* ── Owner: Pay Drivers Action Card ── */}
        {isOwner && (()=>{
          const unpaidLoads = loads.filter(l =>
            l.completed && !l.driverReceived && l.payment_status !== "paid"
            && l.assignedDriverUid && l.assignedDriverUid !== session.uid
          );
          if (unpaidLoads.length === 0) return null;
          const totalOwed = unpaidLoads.reduce((s,l)=>s+Number(l.driverBasePay||0),0);
          const driversOwed = [...new Set(unpaidLoads.map(l=>l.assignedDriverUid||l.user_id).filter(Boolean))].length;
          return (
            <div style={{ background:HDR_BG, border:`1px solid rgba(232,150,46,0.35)`, borderRadius:16, padding:"14px 16px", marginBottom:12, display:"flex", justifyContent:"space-between", alignItems:"center", gap:12 }}>
              <div>
                <div style={{ fontSize:11, color:"rgba(255,255,255,0.45)", fontWeight:700, textTransform:"uppercase", letterSpacing:0.8, marginBottom:3 }}>⏳ Unpaid Drivers</div>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:26, fontWeight:900, color:ORANGE, lineHeight:1 }}>{fmtC(totalOwed)}</div>
                <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)", marginTop:3 }}>{driversOwed} driver{driversOwed!==1?"s":""} · {unpaidLoads.length} load{unpaidLoads.length!==1?"s":""}</div>
              </div>
              <button
                onClick={() => setShowDashPayment(true)}
                style={{ background:ORANGE, color:"#fff", border:"none", borderRadius:12, padding:"12px 18px", fontWeight:900, fontSize:14, cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:0.5, flexShrink:0 }}>
                💳 Pay Now
              </button>
            </div>
          );
        })()}

        {/* ── Pay Day Banner ── */}
        {(()=>{
          const pp = getPayPeriod(rates);
          const toDate = (v) => !v ? null : (v instanceof Date ? v : new Date(String(v).length === 10 ? v + "T12:00:00" : v));
          const ps = toDate(pp.periodStart);
          const pe = toDate(pp.periodEnd);
          const pd = pp.nextPayDate || null;
          const now = new Date();
          const daysUntilPay = pd ? Math.ceil((pd - now) / (1000*60*60*24)) : null;
          const fmt = d => d.toLocaleDateString("en-CA",{month:"short",day:"numeric"});
          const periodLoads = (ps && pe) ? myLoads.filter(l => {
            if (!l.date) return false;
            const ld = new Date(l.date+"T12:00:00");
            return ld >= ps && ld <= pe;
          }) : myLoads;
          const ownerEarnings = periodLoads.reduce((s,l) => { const wm=(Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0); const wComp=wm/60*(Number(rates.companyWaitRate)||0); return s + Number(l.earnings||0) + wComp; }, 0);
          const totalDriverPay = periodLoads.filter(l => Number(l.driverBasePay||0) > 0 && (l.payMyselfAsDriver === true || (l.assignedDriverUid !== session.uid && l.user_id !== session.uid))).reduce((s,l) => { const wm=(Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0); const wDrv=wm/60*(Number(rates.driverWaitRate)||0); return s + Number(l.driverBasePay||0) + wDrv; }, 0);
          const myDriverPay = periodLoads.reduce((s,l) => { const wm=(Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0); const wDrv=wm/60*(Number(rates.driverWaitRate)||0); return s + Number(l.driverBasePay||0) + wDrv; }, 0);
          return (
            <div style={S.paydayCard}>
              <div>
                <div style={S.paydayLbl}>{isOwner ? "💵 Pay day" : "💰 Your pay day"}</div>
                <div style={S.paydayDate}>{pd ? fmt(pd) : "Not set"}</div>
                <div style={S.paydayDays}>
                  {daysUntilPay !== null ? (daysUntilPay > 0 ? `in ${daysUntilPay} day${daysUntilPay!==1?"s":""}` : daysUntilPay === 0 ? "🎉 Today!" : `${Math.abs(daysUntilPay)} days ago`) : ""}
                </div>
              </div>
              <div>
                <div style={S.paydaySub}>{isOwner ? "You owe drivers" : "You'll get"}</div>
                <div style={S.paydayAmt}>{fmtC(isOwner ? totalDriverPay : myDriverPay)}</div>
                <div style={S.paydaySub}>{periodLoads.length} load{periodLoads.length!==1?"s":""} this period</div>
              </div>
            </div>
          );
        })()}

        {/* ── Truck Dashboard Card removed — stats consolidated into hero card above */}

        {/* ── Daily Earnings Bar Chart ── */}
        <div style={S.card}>
          <div style={S.sectionHead}>
            <div style={S.sectionTitle}>This Week</div>
            <span style={S.seeAll}>7-day →</span>
          </div>
          <div style={S.barsWrap}>
            {weekBars.map((b, i) => (
              <div key={i} style={{
                flex: 1,
                height: `${Math.max(b.earn / maxEarn * 100, b.earn > 0 ? 8 : 4)}%`,
                borderRadius: "6px 6px 0 0",
                background: b.isToday ? GREEN : (darkMode ? `${GREEN}22` : `${GREEN}18`),
                cursor: "pointer",
                transition: "all .2s",
                minHeight: 4,
              }} />
            ))}
          </div>
          <div style={S.barLbls}>
            {weekBars.map((b, i) => (
              <span key={i} style={{ flex:1, textAlign:"center", fontSize:11, fontWeight: b.isToday ? 700 : 400, color: b.isToday ? GREEN : textMuted }}>
                {b.label}
              </span>
            ))}
          </div>
        </div>

        {/* ── Recent Loads ── */}
        <div style={S.card}>
          <div style={S.sectionHead}>
            <div style={S.sectionTitle}>Recent Loads</div>
            <button style={S.seeAll} onClick={() => setTab("log")}>See all →</button>
          </div>
          {recent.length === 0
            ? <div style={{ textAlign:"center", padding:"20px 0", color:textMuted, fontSize:13 }}>No loads yet — start logging!</div>
            : recent.map((l, idx) => {
                const earn = Number(l.driverBasePay||0) > 0 ? Number(l.driverBasePay) : Number(l.earnings||0);
                const isActive = !l.completed;
                return (
                  <div key={l.id} style={idx < recent.length - 1 ? S.loadItem : S.loadItemLast}>
                    <div style={isActive ? S.loadBarOrange : S.loadBar}></div>
                    <div style={S.loadInfo}>
                      <div style={S.loadRoute}>{l.location || "Load"}</div>
                      <div style={S.loadSub}>{l.date}{l.truckNumber ? ` · TRK-${l.truckNumber}` : ""}</div>
                    </div>
                    <div>
                      <div style={isActive ? S.loadAmtOrange : S.loadAmt}>{fmtC(earn)}</div>
                      <div style={isActive ? S.badgeActive : S.badgeDone}>{isActive ? "Active" : "Done"}</div>
                    </div>
                  </div>
                );
              })
          }
          <button onClick={() => setTab("new")} style={{ width:"100%", marginTop:12, padding:"11px", borderRadius:10, background:GREEN, color:"#fff", border:"none", cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:15, letterSpacing:"0.04em" }}>
            🚛 &nbsp;LOG A LOAD
          </button>
        </div>

        {/* ── Today's Expenses ── */}
        {(()=>{
          const EXP_ICONS = {fuel:"⛽",maintenance:"🔧",insurance:"🛡",permits:"📋",meals:"🍔",lodging:"🏨",tolls:"🛣",union_dues:"🤝",tools_supplies:"🧰",safety:"🦺",accounting:"📂",advertising:"📣"};
          const todayExps = (window.__sbExpensesCache||[]).length>0 ? window.__sbExpensesCache : getStored(expensesKey(session.uid))
            .filter(e => !e.deleted && !e.ownerExpense && e.expenseType !== "business" && e.source !== "load" && e.date === today);
          const todayExpTotal = todayExps.reduce((s,e)=>s+Number(e.amount||0),0);
          return (
            <div style={S.card}>
              <div style={S.sectionHead}>
                <div style={S.sectionTitle}>Today's Expenses</div>
                <button style={S.seeAll} onClick={() => setTab("expenses")}>View All</button>
              </div>
              {todayExps.length === 0
                ? <>
                    <div style={{ textAlign:"center", padding:"14px 0 8px", color:textMuted, fontSize:13 }}>No expenses logged today</div>
                    <button onClick={() => setTab("expenses")} style={{ width:"100%", padding:"10px", borderRadius:10, border:`1.5px dashed ${cardBorder}`, background:"none", fontSize:13, fontWeight:700, color:textMuted, cursor:"pointer", fontFamily:"inherit" }}>+ Log an Expense</button>
                  </>
                : <>
                    {todayExps.slice(0,4).map((e,idx)=>(
                      <div key={e.id||idx} style={idx < todayExps.slice(0,4).length - 1 ? S.loadItem : S.loadItemLast}>
                        <div style={{ width:34, height:34, borderRadius:10, background:`${ORANGE}18`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, flexShrink:0 }}>
                          {EXP_ICONS[e.category]||"🧾"}
                        </div>
                        <div style={S.loadInfo}>
                          <div style={S.loadRoute}>{e.merchant||e.description||e.category||"Expense"}</div>
                          <div style={S.loadSub}>{e.taxLabel||e.category||""}</div>
                        </div>
                        <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:16, fontWeight:900, color:"#ef4444" }}>−{fmtC(Number(e.amount||0))}</div>
                      </div>
                    ))}
                    {todayExpTotal > 0 && (
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", paddingTop:10, marginTop:4, borderTop:`1px solid ${cardBorder}` }}>
                        <div style={{ fontSize:12, fontWeight:700, color:textMuted }}>Today's Total</div>
                        <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:17, fontWeight:900, color:"#ef4444" }}>−{fmtC(todayExpTotal)}</div>
                      </div>
                    )}
                  </>
              }
            </div>
          );
        })()}



        {/* ── Quick Actions ── */}
        <div style={S.card}>
          <div style={S.sectionHead}>
            <div style={S.sectionTitle}>⚡ Quick Actions</div>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(80px,1fr))", gap:8 }}>
            {(isOwner
              ? [["Add Load","new","➕"],["Drivers","drivers","👥"],["Reports","report","📊"],["Expenses","expenses","🧾"],["Payroll","payroll","💵"],["Tax","tax","🗂"]]
              : [["Add Load","new","➕"],["Load Log","log","📋"],["Expenses","expenses","🧾"],["Reports","report","📊"]]
            ).map(([label, goTab, icon]) => (
              <button key={goTab} onClick={() => setTab(goTab)}
                style={{ padding:"12px 8px", borderRadius:14, border:`1px solid ${cardBorder}`, background:altBg, cursor:"pointer", textAlign:"center", fontFamily:"inherit" }}>
                <div style={{ fontSize:18, marginBottom:4 }}>{icon}</div>
                <div style={{ fontSize:12, fontWeight:700, color:textPrimary }}>{label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* ── Driver Score Card ── */}
        {(()=>{
          const completedLoads = myLoads.filter(l => l.completed);
          const totalLoads = myLoads.length;
          const acceptanceRate = totalLoads > 0 ? Math.min(100, Math.round((completedLoads.length / totalLoads) * 100)) : 0;
          const onTimeLoads = completedLoads.filter(l => {
            const wm = (Number(l.loadWaitMins)||0) + (Number(l.offloadWaitMins)||0);
            return wm < 60;
          });
          const onTimeRate = completedLoads.length > 0 ? Math.min(100, Math.round((onTimeLoads.length / completedLoads.length) * 100)) : 0;
          const fuelLoads = myLoads.filter(l => Number(l.fuel||0) > 0);
          const avgFuelEff = fuelLoads.length > 0
            ? Math.min(100, Math.round(Math.max(0, 100 - (fuelLoads.reduce((s,l)=>s+Number(l.fuel||0),0)/fuelLoads.length - 50) * 2)))
            : 75;
          const overallScore = Math.round((acceptanceRate * 0.4 + onTimeRate * 0.4 + avgFuelEff * 0.2));
          const scoreColor = overallScore >= 80 ? GREEN : overallScore >= 60 ? ORANGE : "#dc2626";
          const scoreLabel = overallScore >= 80 ? "Excellent" : overallScore >= 60 ? "Good" : "Needs Work";
          const bars = [
            {label:"Load Acceptance",val:acceptanceRate,color:GREEN},
            {label:"On-Time Delivery",val:onTimeRate,color:GREEN},
            {label:"Fuel Efficiency",val:avgFuelEff,color:ORANGE},
          ];
          return (
            <div style={{...S.card,marginBottom:12}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                <div style={S.sectionTitle}>🏆 Driver Score</div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:26,fontWeight:900,color:scoreColor,lineHeight:1}}>{overallScore}</div>
                  <div style={{fontSize:11,fontWeight:700,color:scoreColor,background:`${scoreColor}18`,padding:"3px 8px",borderRadius:12}}>{scoreLabel}</div>
                </div>
              </div>
              {bars.map((b,i) => (
                <div key={i} style={{marginBottom:i<bars.length-1?10:0}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                    <span style={{fontSize:12,color:textMuted}}>{b.label}</span>
                    <span style={{fontSize:12,fontWeight:700,color:b.color}}>{b.val}%</span>
                  </div>
                  <div style={{height:6,borderRadius:3,background:cardBorder,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${b.val}%`,borderRadius:3,background:b.color,transition:"width 0.8s ease"}}/>
                  </div>
                </div>
              ))}
              {totalLoads === 0 && (
                <div style={{textAlign:"center",padding:"8px 0",color:textMuted,fontSize:12,marginTop:8}}>Log some loads to build your score!</div>
              )}
            </div>
          );
        })()}

        {/* ── Weather Alert ── */}
        <WeatherAlertBanner />

        {/* ── Need Help Card ── */}
        <div style={{ borderRadius:16, overflow:"hidden", marginBottom:12, background:HDR_BG, border:`1px solid ${cardBorder}` }}>
          <div style={{ padding:"16px 18px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
              <span style={{ fontSize:20 }}>🆘</span>
              <div>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:15, color:"#fff" }}>Need Help?</div>
                <div style={{ fontSize:11, color:"rgba(255,255,255,.4)", marginTop:1 }}>TruckPilot Support · Mon–Fri 8AM–8PM</div>
              </div>
            </div>
            <div style={{ display:"flex", gap:10 }}>
              <a href="tel:4377005835" style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", gap:8, padding:"11px", borderRadius:10, background:`${GREEN}20`, textDecoration:"none" }}>
                <span style={{ fontSize:16 }}>📞</span>
                <span style={{ fontWeight:700, fontSize:12, color:GREEN }}>437-700-5835</span>
              </a>
              <a href="mailto:support@truckpilot.ca" style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", gap:8, padding:"11px", borderRadius:10, background:`${GREEN}20`, textDecoration:"none" }}>
                <span style={{ fontSize:16 }}>✉️</span>
                <span style={{ fontWeight:700, fontSize:12, color:GREEN }}>Email Us</span>
              </a>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

// ─── HAUL LOG ─────────────────────────────────────────────────────────────────
function HaulLogTab({ session, loads, rates, isOwner, trucks, setTab, setEditLoad, deleteLoad, setDetailLoad, toggleComplete, allDrivers=[], darkMode=false, onUpdateLoad }) {
  const myLoads = isOwner ? loads : loads.filter(l => l.assignedDriverUid===session.uid||l.addedBy===session.uid||l.user_id===session.uid);
  const [filter, setFilter] = useState("all");
  const [payFilter, setPayFilter] = useState("all"); // owner-only: "all" | "pending_pay" | "paid"
  const [driverFilter, setDriverFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const filteredByDriver = isOwner && driverFilter !== "all"
    ? myLoads.filter(l => {
        if (driverFilter === "owner") {
          return !l.assignedDriverUid || l.assignedDriverUid === session.uid;
        }
        const driver = allDrivers.find(d => d.uid === driverFilter);
        return l.assignedDriverUid === driverFilter
          || l.addedBy === driverFilter
          || l.user_id === driverFilter
          || (driver && l.driverFullName === (driver.fullName || driver.name));
      })
    : myLoads;
  const filteredBySearch = searchQuery
    ? filteredByDriver.filter(l => {
        const q = searchQuery.toLowerCase();
        return [l.location, l.date, l.driverFullName, l.tmwLoadNumber, l.notes, l.commodity, l.customer, l.reference, String(l.earnings||""), String(l.driverBasePay||"")].join(" ").toLowerCase().includes(q);
      })
    : filteredByDriver;
  // Payment status filter (owner-only)
  const filteredByPay = isOwner && payFilter !== "all"
    ? filteredBySearch.filter(l => {
        const isPaid = l.driverReceived || l.payment_status === "paid" || (l.driverPaid && l.driverReceived);
        const isPending = l.completed && !l.driverReceived && l.payment_status !== "paid" && (l.assignedDriverUid && l.assignedDriverUid !== session.uid);
        if (payFilter === "paid") return isPaid;
        if (payFilter === "pending_pay") return isPending;
        return true;
      })
    : filteredBySearch;
  const filtered = filteredByPay.filter(l => filter==="active"?!l.completed:filter==="done"?l.completed:true).sort((a,b)=>b.date>a.date?1:-1);
  const activeCount = myLoads.filter(l=>!l.completed).length;
  // Owner payment counts for badges
  const pendingPayCount = isOwner ? myLoads.filter(l => l.completed && !l.driverReceived && l.payment_status !== "paid" && l.assignedDriverUid && l.assignedDriverUid !== session.uid).length : 0;
  const paidCount = isOwner ? myLoads.filter(l => l.driverReceived || l.payment_status === "paid").length : 0;

  // ── Dark mode color palette ──
  const DM = {
    pageBg:   darkMode ? "#070B14" : "#F5F6F8",
    cardBg:   darkMode ? "#0D1523" : "#FFFFFF",
    cardBg2:  darkMode ? "#111C2E" : "#F8FAFC",
    border:   darkMode ? "rgba(255,255,255,0.07)" : "#E2E2E2",
    text:     darkMode ? "#F0F0F0" : "#1A1A1A",
    textMed:  darkMode ? "rgba(255,255,255,0.5)" : "#4B5563",
    textMut:  darkMode ? "rgba(255,255,255,0.35)" : "#6B7280",
    heroBg:   darkMode ? "#070B14" : "#F5F6F8",
    inputBg:  darkMode ? "#111C2E" : "#FFFFFF",
    rowAlt:   darkMode ? "#0D1929" : "#F8FAFC",
    greenBg:  darkMode ? "rgba(34,197,94,0.1)"  : "#E8F5E9",
    redBg:    darkMode ? "rgba(239,68,68,0.1)"  : "#FFF5F5",
    amberBg:  darkMode ? "rgba(232,150,46,0.1)" : "#F5F6F8",
    amberBg2: darkMode ? "rgba(232,150,46,0.06)": "#FFF3EB",
    divider:  darkMode ? "rgba(59,130,246,0.1)": "#F0F0F0",
  };

  return (
    <div className="slt-page" style={{background:DM.pageBg,color:C.textDark}}>
      <div className="slt-hero">
        <div style={{fontSize:10,fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"rgba(255,255,255,0.35)",marginBottom:3}}>TRUCKPILOT</div>
        <div className="slt-hero-title" style={{color:"#fff"}}>{isOwner?"Load Log":"Load Log"}</div>
        <div className="slt-hero-sub">{myLoads.length} total · <span style={{color:"#E8962E",fontWeight:700}}>{activeCount} active</span></div>
      </div>
      <div className="slt-container" style={{padding:"16px 14px 80px"}}>
        {/* ── Load Search Bar ── */}
        <form onSubmit={e=>{e.preventDefault();setSearchQuery(searchInput);}} style={{display:"flex",gap:8,marginBottom:14}}>
          <input
            value={searchInput}
            onChange={e=>{setSearchInput(e.target.value);if(!e.target.value){setSearchQuery("");}}}
            placeholder="🔍 Search by route, date, driver, TMW#…"
            style={{flex:1,padding:"10px 14px",borderRadius:10,border:darkMode?"1.5px solid rgba(255,255,255,0.1)":"1.5px solid #d0d5dd",fontSize:13,fontFamily:"'Barlow',sans-serif",outline:"none",background:DM.inputBg,color:DM.text}}
          />
          <button type="submit"
            style={{padding:"10px 16px",borderRadius:10,background:"#E8962E",color:"#1C1C1E",border:"none",fontWeight:800,fontSize:13,cursor:"pointer",fontFamily:"'Barlow',sans-serif",whiteSpace:"nowrap"}}>
            Search
          </button>
          {searchQuery&&<button type="button" onClick={()=>{setSearchQuery("");setSearchInput("");}}
            style={{padding:"10px 12px",borderRadius:10,background:DM.cardBg2,color:DM.textMed,border:"none",fontWeight:700,fontSize:13,cursor:"pointer"}}>✕</button>}
        </form>
        {searchQuery&&<div style={{fontSize:12,color:"#E8962E",fontWeight:700,marginBottom:10}}>🔍 "{searchQuery}" — {filtered.length} result{filtered.length!==1?"s":""}</div>}

        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:isOwner?10:20,flexWrap:"wrap",gap:12 }}>
          <div style={{ display:"flex",gap:8 }}>
            {[["active","⬤ Active"],["done","✓ Done"],["all","All"]].map(([v,l])=>(
              <button key={v} onClick={()=>setFilter(v)} className="slt-btn-secondary"
                style={{ background:filter===v?(v==="active"?C.orange:v==="done"?C.green:C.blue):DM.cardBg, color:filter===v?"#fff":DM.textMed, borderColor:filter===v?(v==="active"?C.orange:v==="done"?C.green:C.blue):DM.border, padding:"8px 16px" }}>
                {l}{v==="active"&&activeCount>0?` (${activeCount})`:""}
              </button>
            ))}
          </div>
        </div>

        {/* Payment status filter for owners */}
        {isOwner && (
          <div style={{display:"flex",gap:6,marginBottom:14,overflowX:"auto",scrollbarWidth:"none",paddingBottom:2}}>
            {[
              ["all","📋 All Loads", myLoads.length],
              ["done","✅ Completed", myLoads.filter(l=>l.completed).length],
              ["pending_pay","⏳ Pending Pay", pendingPayCount],
              ["paid","💳 Paid", paidCount],
            ].map(([v,l,count])=>(
              <button key={v}
                onClick={()=>{ setPayFilter(v==="done"?"all":v); if(v==="done") setFilter("done"); else setFilter("all"); }}
                style={{
                  padding:"7px 13px", borderRadius:20, border:"none", cursor:"pointer",
                  fontWeight:800, fontSize:11, whiteSpace:"nowrap", fontFamily:"inherit",
                  background: payFilter===v||(v==="done"&&filter==="done"&&payFilter==="all")
                    ? (v==="pending_pay"?"#E8962E":v==="paid"?"#166534":v==="done"?"#1C2B4A":"#1C2B4A")
                    : darkMode?"rgba(255,255,255,0.07)":"rgba(0,0,0,0.05)",
                  color: payFilter===v||(v==="done"&&filter==="done"&&payFilter==="all") ? "#fff" : DM.textMed,
                  transition:"all 0.15s"
                }}>
                {l}{count>0?` (${count})`:""}
              </button>
            ))}
          </div>
        )}
        {isOwner&&allDrivers.length>0&&(
          <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:16}}>
            <span style={{fontSize:12,fontWeight:700,color:C.textDarkMed,alignSelf:"center"}}>Driver:</span>
            {[["all","👥 All"],["owner","👤 Me"],...allDrivers.map(d=>[d.uid,d.fullName||d.name])].map(([v,l])=>(
              <button key={v} onClick={()=>setDriverFilter(v)} className="slt-btn-secondary"
                style={{padding:"6px 12px",fontSize:12,background:driverFilter===v?C.navy:DM.cardBg,color:driverFilter===v?"#fff":DM.textMed,borderColor:driverFilter===v?C.navy:DM.border}}>{l}</button>
            ))}
          </div>
        )}

        {filtered.length===0
          ? <div style={{ textAlign:"center",padding:"48px 24px",background:DM.cardBg,borderRadius:16,margin:"0 0 12px" }}>
              <div style={{fontSize:64,marginBottom:16}}>{filter==="active"?"✅":"🚛"}</div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:18,color:DM.text,marginBottom:8}}>
                {filter==="active"?"You're all caught up!":"No loads yet"}
              </div>
              <div style={{fontSize:13,color:C.textDarkMut,marginBottom:20,lineHeight:1.6}}>
                {filter==="active"
                  ?"All your active loads are complete. Great work! 🎉"
                  :"Start logging your loads to track earnings and stay compliant."}
              </div>
              {filter!=="active"&&(
                <button onClick={()=>setTab("new")} style={{background:"linear-gradient(135deg,#E8962E,#E8962E)",border:"none",borderRadius:50,color:"#fff",fontWeight:800,fontSize:14,padding:"12px 28px",cursor:"pointer"}}>
                  ➕ Log Your First Load
                </button>
              )}
            </div>
          : <div key={filter+driverFilter+searchQuery} className="tp-stagger">
            {/* Column headers */}
            <div style={{display:"grid",gridTemplateColumns:"1fr auto",padding:"6px 18px",marginBottom:4,borderBottom:`2px solid ${darkMode?"#E8962E":"#1C2B4A"}`}}>
              <div style={{fontSize:11,fontWeight:800,color:darkMode?"#E8962E":"#1C2B4A",textTransform:"uppercase",letterSpacing:0.5}}>🗺 Route / Info</div>
              <div style={{fontSize:11,fontWeight:800,color:darkMode?"#E8962E":"#1C2B4A",textTransform:"uppercase",letterSpacing:0.5}}>💰 Pay</div>
            </div>
            {filtered.map(l => {
            const wm=(Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0);
            const loadWm=Number(l.loadWaitMins)||0;
            const offWm=Number(l.offloadWaitMins)||0;
            const truck=trucks.find(t=>t.id===l.truckId);
            const waitOwner=wm/60*(Number(rates.companyWaitRate)||0);
            const waitDrv=wm/60*(Number(rates.driverWaitRate)||0);
            // Owner sees gross earnings only — never driver pay on their own loads
            const amt = isOwner
              ? Number(l.earnings||0) + waitOwner
              : Number(l.driverBasePay||0) + waitDrv;
            const totalWait = waitOwner + waitDrv;
            const waitAmt = isOwner ? waitOwner : waitDrv;
            return (
              <SwipeableLoadCard key={l.id} load={l} onComplete={()=>!l.completed&&toggleComplete(l.id,true)} onClick={()=>setDetailLoad(l)} darkMode={darkMode}>
                {/* Badges row */}
                <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap",marginBottom:6}}>
                  {l.tmwLoadNumber&&<span style={{background:"#E8962E",color:"#1C1C1E",borderRadius:20,padding:"3px 10px",fontSize:12,fontWeight:700}}>TMW #{l.tmwLoadNumber}</span>}
                  <span className={l.completed?"slt-badge-green":"slt-badge-orange"}>{l.completed?"✓ Done":"⬤ Active"}</span>
                </div>

                {/* Option A: Table row layout */}
                <div style={{display:"grid",gridTemplateColumns:"1fr auto",gap:8,alignItems:"flex-start"}}>
                  {/* Left: Route info */}
                  <div>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:17,color:darkMode?"#eaeaea":"#1A1A1A",lineHeight:1.2}}>{l.location}</div>
                    {isOwner&&l.driverFullName&&<div style={{fontSize:12,color:darkMode?"#a0a0a0":"#555",marginTop:3}}>👤 {l.driverFullName}</div>}
                    <div style={{fontSize:11,color:darkMode?"#6a7a8a":"#999",marginTop:2}}>
                      {truck&&<span>🚛 {truck.truckNumber}</span>}
                      {l.tmwLoadNumber&&<span> · #{l.tmwLoadNumber}</span>}
                      {l.date&&<span> · {l.date}</span>}
                    </div>
                    {(()=>{
                      const fmt12=(t)=>{if(!t)return null;const[h,m]=t.split(":").map(Number);return`${h%12||12}:${String(m).padStart(2,"0")} ${h>=12?"PM":"AM"}`;};
                      const arrival=fmt12(l.time);
                      const appt=fmt12(l.appointmentTime);
                      const done=fmt12(l.completedTime);
                      if(!appt&&!arrival&&!done)return null;
                      return(
                        <div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:5}}>
                          {appt&&<span style={{fontSize:10,background:"#EEF2FF",borderRadius:4,padding:"2px 6px",color:"#4338CA",fontWeight:700}}>📅 {appt}</span>}
                          {arrival&&<span style={{fontSize:10,background:"#F0FDF4",borderRadius:4,padding:"2px 6px",color:"#16A34A",fontWeight:700}}>🛬 {arrival}</span>}
                          {done&&<span style={{fontSize:10,background:"#FFF7ED",borderRadius:4,padding:"2px 6px",color:"#E8962E",fontWeight:700}}>✅ {done}</span>}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Right: Pay */}
                  <div style={{textAlign:"right",flexShrink:0}}>
                    {!isOwner&&<div style={{fontSize:10,fontWeight:700,color:C.green,textTransform:"uppercase",letterSpacing:0.5,marginBottom:2}}>💰 Pay</div>}
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:20,fontWeight:900,color:"#E8962E"}}>{fmtC(amt)}</div>
                    {waitAmt>0&&<div style={{fontSize:11,fontWeight:700,color:"#1565C0"}}>⏱ +{fmtC(waitAmt)}</div>}
                  </div>
                </div>

                {l.messages&&l.messages.length>0&&<div style={{fontSize:12,color:C.blue,marginBottom:4}}>💬 {l.messages.length} note{l.messages.length!==1?"s":""}</div>}
                {/* ── Payment status badges ── */}
                {(isOwner ? (l.contractorPaid||(!l.contractorPaid&&l.completed)) : (l.completed&&Number(l.driverBasePay||0)>0))&&(
                  <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:6}}>
                    {/* Owner badges */}
                    {isOwner&&l.contractorPaid&&<span style={{fontSize:10,fontWeight:800,color:"#059669",background:"#D1FAE5",padding:"2px 8px",borderRadius:20}}>💰 Contractor Paid</span>}
                    {isOwner&&!l.contractorPaid&&l.completed&&<span style={{fontSize:10,fontWeight:800,color:"#B45309",background:"#FEF3C7",padding:"2px 8px",borderRadius:20}}>⏳ Awaiting Contractor</span>}
                    {/* Driver badges — self-managed payment status */}
                    {!isOwner&&l.completed&&Number(l.driverBasePay||0)>0&&!l.driverReceived&&<span style={{fontSize:10,fontWeight:800,color:"#DC2626",background:"#FEE2E2",padding:"2px 8px",borderRadius:20}}>⏳ Payment Pending</span>}
                    {!isOwner&&l.driverReceived&&<span style={{fontSize:10,fontWeight:800,color:"#059669",background:"#D1FAE5",padding:"2px 8px",borderRadius:20}}>✅ Pay Received {l.driverReceivedDate||""}</span>}
                  </div>
                )}
                <div style={{display:"flex",gap:8,marginTop:10,borderTop:`1px solid ${DM.divider}`,paddingTop:10,flexWrap:"wrap"}}>
                  {!l.completed
                    ? <button className="slt-btn-complete" style={{flex:1}} onClick={e=>{e.stopPropagation();toggleComplete(l.id,true);}}>✓ Mark Complete</button>
                    : <button className="slt-btn-reopen" style={{flex:1}} onClick={e=>{e.stopPropagation();toggleComplete(l.id,false);}}>↩ Reopen</button>
                  }
                  {/* Driver: self-mark payment received */}
                  {!isOwner&&l.completed&&Number(l.driverBasePay||0)>0&&!l.driverReceived&&onUpdateLoad&&(
                    <button style={{flex:1,padding:"8px",borderRadius:10,background:"#059669",color:"#fff",fontWeight:800,fontSize:12,border:"none",cursor:"pointer"}}
                      onClick={e=>{e.stopPropagation();onUpdateLoad(l.id,{driverReceived:true,driverReceivedDate:new Date().toISOString().slice(0,10)});}}>✅ Mark Paid</button>
                  )}
                  {/* Only the load's original poster can edit or delete it */}
                  {(l.addedBy===session.uid||l.user_id===session.uid||(!l.addedBy&&!l.user_id&&isOwner)) && (
                    <button className="slt-btn-secondary" style={{padding:"8px 16px",fontSize:13}} onClick={e=>{e.stopPropagation();setEditLoad(l);setTab("new");}}>✏️ Edit</button>
                  )}
                  {(l.addedBy===session.uid||l.user_id===session.uid||(!l.addedBy&&!l.user_id&&isOwner)) && (
                    <button className="slt-btn-danger" style={{padding:"8px 14px",fontSize:13}} onClick={e=>{e.stopPropagation();if(window.confirm("Delete this load?"))deleteLoad(l.id);}}>🗑</button>
                  )}
                </div>
            </SwipeableLoadCard>
            );
          })}</div>
        }
      </div>
    </div>
  );
}

// ─── LOAD FORM ────────────────────────────────────────────────────────────────
function LoadFormTab({ session, isOwner, rates, allRoutes, trucks, onSave, editLoad, onCancel, darkMode=false }) {
  // Determine the fleet owner uid from session immediately — don't wait for async
  const sessionFleetOwnerUid = !isOwner
    ? (() => { const u = session.fleetOwnerUid || session.ownerUid; return (u && u !== session.uid) ? u : null; })()
    : null;

  // Initialize with session data immediately so the UI shows on first render
  const [myFleets, setMyFleets] = useState(() =>
    sessionFleetOwnerUid ? [{ owner_uid: sessionFleetOwnerUid, owner_name: "Fleet Owner" }] : []
  );
  const [selectedFleetOwner, setSelectedFleetOwner] = useState(sessionFleetOwnerUid);
  const [fleetTrucks, setFleetTrucks] = useState(trucks);
  const [fleetRoutes, setFleetRoutes] = useState(allRoutes);
  const [fleetRates, setFleetRates] = useState(rates);
  // loadMode: "fleet" = log for Owner Operator; "own" = log as My Own Load (driver working for themselves)
  // When editing an existing load, respect its saved isOwnLoad flag
  const [loadMode, setLoadMode] = useState(() => {
    if (editLoad && editLoad.isOwnLoad === true) return "own";
    return sessionFleetOwnerUid ? "fleet" : "own";
  });
  const [scanningLoad, setScanningLoad] = useState(false);
  const [scanLoadMsg, setScanLoadMsg] = useState("");
  const loadScanRef = useRef(null);

  const loadFleetData = async (ownerUid) => {
    const [t, s] = await Promise.all([sbGetTrucks(ownerUid), sbGetSettings(ownerUid)]);
    if (t && t.length > 0) setFleetTrucks(t);
    if (s?.rates) setFleetRates({ ...DEFAULT_RATES, ...s.rates });
    if (s?.routes && s.routes.length > 0) setFleetRoutes(s.routes);
  };

  useEffect(() => {
    if (!isOwner) {
      // Immediately load fleet data from the known session owner uid
      if (sessionFleetOwnerUid) loadFleetData(sessionFleetOwnerUid);

      // Also refresh from Supabase — get correct owner name + handle multiple fleets
      sbGetMyFleets(session.uid).then(async (fleets) => {
        if (fleets.length > 0) {
          setMyFleets(fleets);
          const first = fleets[0];
          setSelectedFleetOwner(first.owner_uid);
          loadFleetData(first.owner_uid);
        } else if (sessionFleetOwnerUid) {
          // sbGetMyFleets returned empty — fetch owner name for display
          try {
            const { data: p } = await sb.from("profiles").select("name").eq("id", sessionFleetOwnerUid).maybeSingle();
            setMyFleets([{ owner_uid: sessionFleetOwnerUid, owner_name: p?.name || "Fleet Owner" }]);
          } catch {
            // keep the synthetic entry already set in useState initializer
          }
        }
      }).catch(() => {
        // Network error — keep session-derived state, fleet data already loading above
      });
    } else {
      // Owner: refresh their own routes/rates from Supabase
      sbGetSettings(session.uid).then(s => {
        if (s?.routes) setFleetRoutes(s.routes);
        if (s?.rates) setFleetRates({ ...DEFAULT_RATES, ...s.rates });
      });
    }
  }, [session.uid]);

  // isFleetMember: true when driver is logging for fleet owner AND loadMode is "fleet"
  const hasFleet = !isOwner && !!selectedFleetOwner && selectedFleetOwner !== session.uid;
  const isFleetMember = hasFleet && loadMode === "fleet";
  const activeTrucks = isFleetMember ? fleetTrucks : trucks;
  const activeRoutes = isFleetMember ? fleetRoutes : allRoutes;
  const activeRates = isFleetMember ? fleetRates : rates;

  const ownerUid = selectedFleetOwner || session.fleetOwnerUid || session.ownerUid || session.uid;
  const seqKey=`tp-seq-${ownerUid}`;
  // Read next number WITHOUT incrementing — just a preview
  const peekNextNum=()=>{ const last=parseInt(localStorage.getItem(seqKey)||"1000",10); return (last+1).toString(); };
  // Actually consume and increment — called only on submit
  const genNextNum=()=>{ const last=parseInt(localStorage.getItem(seqKey)||"1000",10); const next=last+1; localStorage.setItem(seqKey,next.toString()); return next.toString(); };
  const [previewNum] = useState(()=> editLoad ? null : peekNextNum());

  const blank = { date:todayStr(),appointmentDate:"",time:"",appointmentTime:"",completedTime:"",offloadArrivalTime:"",offloadCompletedTime:"",offloadDate:todayStr(),offloadNote:"",offloadDocImage:"",location:"",loadOrigin:"",loadDestination:"",loadType:"Oil Sands",loadWaitMins:"",offloadWaitMins:"",earnings:"",driverBasePay:"",assignedDriverUid:"",fuelLitres:"",fuelPricePerLitre:"",fuelTotal:"",note:"",truckId:"",manualTruckNumber:"",driverFullName:"",completed:false,quantity:"",billingMethod:"per_load",payMyselfAsDriver:true };
  
  // Smart defaults — remember last used truck and route
  const getSmartDefaults = () => {
    const lastTruck = localStorage.getItem(`tp-last-truck-${session.uid}`) || "";
    const lastRoute = localStorage.getItem(`tp-last-route-${session.uid}`) || "";
    // Restore sequence number from Supabase if localStorage was wiped
    if (!localStorage.getItem(seqKey)) {
      sbGetSettings(ownerUid).then(d=>{ if(d&&d.loadSeq){ localStorage.setItem(seqKey,d.loadSeq.toString()); } }).catch(()=>{});
    }
    return { truckId: lastTruck, location: lastRoute };
  };
  
  const [form, setForm] = useState(() => {
    if (editLoad) return {...blank, ...editLoad};
    const defaults = getSmartDefaults();
    return {...blank, tmwLoadNumber:"", ...defaults};
  });

  // ── Permission guard: only the load creator can edit ──
  if (editLoad) {
    const isCreator = editLoad.user_id === session.uid || editLoad.addedBy === session.uid || (!editLoad.user_id && !editLoad.addedBy && isOwner);
    if (!isCreator) {
      return (
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"48px 24px",textAlign:"center"}}>
          <div style={{fontSize:48,marginBottom:16}}>🔒</div>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:22,color:"#1A1A1A",marginBottom:8}}>Not Authorized</div>
          <div style={{color:"#374151",fontSize:14,marginBottom:24}}>Only the person who created this load can edit it.</div>
          <button className="slt-btn-ghost" onClick={onCancel} style={{padding:"10px 24px"}}>← Go Back</button>
        </div>
      );
    }
  }
  const [activeLoadSubTab, setActiveLoadSubTab] = useState("loading");
  const [offloadDocPreview, setOffloadDocPreview] = useState(() => editLoad?.offloadDocImage || "");
  const [offloadScanMsg, setOffloadScanMsg] = useState("");
  const [offloadScanning, setOffloadScanning] = useState(false);
  const offloadScanRef = useRef(null);
  const [section, setSection] = useState("details");
  const [loadStatus,setLoadStatus]=useState(null); const [offStatus,setOffStatus]=useState(null);
  const [loadElapsed,setLoadElapsed]=useState(0); const [offElapsed,setOffElapsed]=useState(0);
  const loadRef=useRef(null); const loadStart=useRef(0); const offRef=useRef(null); const offStart=useRef(0);

  // Cancel just navigates away — no number was ever generated
  const handleCancel=()=>{ onCancel(); };

  const startTimer=(k)=>{ if(k==="load"){loadStart.current=Date.now()-loadElapsed*1000;setLoadStatus("running");loadRef.current=setInterval(()=>setLoadElapsed(Math.floor((Date.now()-loadStart.current)/1000)),1000);}else{offStart.current=Date.now()-offElapsed*1000;setOffStatus("running");offRef.current=setInterval(()=>setOffElapsed(Math.floor((Date.now()-offStart.current)/1000)),1000);}};
  const stopTimer=(k)=>{ if(k==="load"){clearInterval(loadRef.current);setLoadStatus("stopped");setForm(f=>({...f,loadWaitMins:Math.floor(loadElapsed/60).toString()}));}else{clearInterval(offRef.current);setOffStatus("stopped");setForm(f=>({...f,offloadWaitMins:Math.floor(offElapsed/60).toString()}));}};
  const resetTimer=(k)=>{ if(k==="load"){clearInterval(loadRef.current);setLoadStatus(null);setLoadElapsed(0);}else{clearInterval(offRef.current);setOffStatus(null);setOffElapsed(0);}};
  useEffect(()=>()=>{clearInterval(loadRef.current);clearInterval(offRef.current);},[]);

  const users=getUsers();
  const drivers=Object.values(users).filter(u=>u.role==="driver"&&u.ownerUid===ownerUid);
  const hc=(e)=>setForm(f=>({...f,[e.target.name]:e.target.value}));
  const getRD=(loc)=>(activeRoutes||allRoutes).find(r=>`${r.from} → ${r.to}`===loc);

  // ── Auto-calculate earnings based on billing method ──
  const calcEarnings=(rd,qty)=>{ if(!rd)return""; const m=rd.billingMethod||"per_load"; if(m==="per_load")return(Number(rd.ratePerLoad||rd.rate)||0).toString(); if(m==="per_cubic")return(Number(rd.rateCubic||0)*Number(qty||0)).toFixed(2); if(m==="per_hour")return(Number(rd.rateHour||rd.rate||0)*Number(qty||0)).toFixed(2); if(m==="per_pct")return(Number(rd.rateCubic||rd.rate||0)*Number(qty||0)).toFixed(2); if(m==="per_km")return(Number(rd.ratePerKm||0)*Number(qty||0)).toFixed(2); return""; };
  // Driver pay: per_load/per_cubic = flat rate (override or default); per_hour = driver's hourly rate × hours
  const getDriverRate=(rd,uid)=>{ const overrides=rd.driverOverrides||{}; return uid&&overrides[uid]!==undefined&&overrides[uid]!==""?Number(overrides[uid]):Number(rd.driverPay||rd.pay||0); };
  const calcDriverPay=(rd,qty)=>{
    if(!rd)return"";
    const m=rd.billingMethod||"per_load";
    const uid=form.assignedDriverUid||(isOwner?"":session.uid);
    const dRate=getDriverRate(rd,uid);
    if(m==="per_hour") return (dRate*Number(qty||0)).toFixed(2);
    if(m==="per_pct") return (Number(rd.ratePerLoad||0)*Number(qty||0)*Number(rd.driverPct||0)/100).toFixed(2);
    // per_cubic: driver gets flat fixed pay — only the business earns per yd³
    if(m==="per_cubic") return dRate.toString();
    // per_km: driver pay = their rate per km × km driven
    if(m==="per_km") return (dRate*Number(qty||0)).toFixed(2);
    return dRate.toString();
  };
  const handleRoute=(val)=>{ if(!val){setForm(f=>({...f,location:"",loadOrigin:"",loadDestination:"",driverBasePay:"",earnings:"",quantity:"",billingMethod:"per_load"}));return;} const rd=getRD(val); if(rd){const m=rd.billingMethod||"per_load";const earn=m==="per_load"?calcEarnings(rd,""):"";setForm(f=>{const overrides=rd.driverOverrides||{};const uid=f.assignedDriverUid||(isOwner?"":session.uid);const isCubicPct=m==="per_cubic"&&(rd.cubicDriverMode||"flat")==="pct";const pay=isCubicPct?"0":(uid&&overrides[uid]!==undefined&&overrides[uid]!==""?Number(overrides[uid]).toString():Number(rd.driverPay||rd.pay||0).toString());return{...f,location:val,loadOrigin:rd.from||"",loadDestination:rd.to||"",billingMethod:m,driverBasePay:pay,earnings:earn,quantity:""};});}else{setForm(f=>({...f,location:val,billingMethod:"per_load"}));}};
  // When driver assignment changes, recalculate their pay for the selected route
  const handleDriverChange=(e)=>{ const uid=e.target.value; setForm(f=>{ const rd=getRD(f.location); if(!rd)return{...f,assignedDriverUid:uid}; const m=rd.billingMethod||"per_load"; const overrides=rd.driverOverrides||{}; const dRate=uid&&overrides[uid]!==undefined&&overrides[uid]!==""?Number(overrides[uid]):Number(rd.driverPay||rd.pay||0); const pay=m==="per_hour"?(dRate*Number(f.quantity||0)).toFixed(2):dRate.toString(); return{...f,assignedDriverUid:uid,driverBasePay:pay}; }); };
  const handleQuantity=(val)=>{ const rd=getRD(form.location); if(rd){ const m=rd.billingMethod||"per_load"; const newDriverPay=m==="per_cubic"?Number(rd.driverPay||rd.pay||0).toString():calcDriverPay(rd,val); setForm(f=>({...f,quantity:val,earnings:calcEarnings(rd,val),driverBasePay:newDriverPay}));}else{setForm(f=>({...f,quantity:val}));} };
  useEffect(()=>{ const t=activeTrucks||trucks; if(!form.truckId&&t.length===1)setForm(f=>({...f,truckId:t[0].id})); },[activeTrucks?.length, trucks.length]);

  // Auto-calc waiting time: difference in minutes between appointment and completed time
  const calcWaitMins = (appt, done) => {
    if (!appt || !done) return null;
    const [ah, am] = appt.split(":").map(Number);
    const [dh, dm] = done.split(":").map(Number);
    let diff = (dh * 60 + dm) - (ah * 60 + am);
    if (diff < 0) diff += 1440; // handle crossing midnight
    return diff >= 0 ? diff : null;
  };
  const wm=(Number(form.loadWaitMins)||0)+(Number(form.offloadWaitMins)||0);
  const wComp=parseFloat((wm/60*(Number(rates.companyWaitRate)||0)).toFixed(2));
  const wDrv=parseFloat((wm/60*(Number(rates.driverWaitRate)||0)).toFixed(2));
  const gross=parseFloat(((Number(form.earnings)||0)+wComp).toFixed(2));
  const dPay=parseFloat(((Number(form.driverBasePay)||0)+wDrv).toFixed(2));
  const net=parseFloat((gross-dPay).toFixed(2));

  const scanLoadDocument = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Only images are supported for AI vision scan
    if (!file.type.startsWith("image/")) {
      setScanLoadMsg("⚠️ Please upload an image (JPG, PNG, HEIC). For PDFs, take a photo of the document instead.");
      setTimeout(()=>setScanLoadMsg(""),5000);
      e.target.value="";
      return;
    }
    setScanningLoad(true);
    setScanLoadMsg("🤖 Reading document...");
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const base64Data = ev.target.result;
      const base64 = base64Data.split(",")[1];
      const mediaType = base64Data.split(";")[0].split(":")[1] || "image/jpeg";
      try {
        // Build numbered route list so AI can return an index for fuzzy matching
        const routes = activeRoutes || [];
        const routeList = routes.length
          ? routes.map((r,i)=>`[${i}] ${r.from} → ${r.to}`).join("\n")
          : "none configured";
        const truckList = (activeTrucks||[]).map(t=>t.truckNumber).join(", ");

        // ── Client-side fuzzy route matcher ──────────────────────────────────
        // Normalize known OCR/spelling variants before keyword extraction
        const normalizeOilSites = (str) => (str||"").toUpperCase()
          .replace(/\bCANADIAN NATURAL RESOURCES LI[DT]\b/g,"CNRL")
          .replace(/\bCANADIAN NATURAL RESOURCES\b/g,"CNRL")
          .replace(/\bCDN NAT\b/g,"CNRL")
          .replace(/\bCHECHEM\b/g,"CHEECHAM")
          .replace(/\bCHEEHAM\b/g,"CHEECHAM")
          .replace(/\bCHECHAM\b/g,"CHEECHAM")
          .replace(/\bRAIL BAR\b/g,"RAILBAR")
          .replace(/\bRAIL-BAR\b/g,"RAILBAR")
          .replace(/\bHEARTLAND FT SASK\b/g,"HEARTLAND")
          .replace(/\bHEARTLAND FORT SASK\b/g,"HEARTLAND");

        // Strips LSD grid references, province codes, and numbers, leaving meaningful place-name words
        const routeKeywords = (str) => normalizeOilSites(str)
          .replace(/\d{1,3}-\d{1,3}-\d{1,3}-\d{1,3}[EW]\d*/g,"") // LSD e.g. 10-16-96-11W4
          .replace(/\b(AB|BC|SK|MB|ON|QC|NB|NS|PE|NL|NT|NU|YT|CANADA)\b/g,"")
          .replace(/\b\d+\b/g,"")
          .replace(/[^A-Z\s]/g," ")
          .split(/\s+/).filter(w=>w.length>=3); // meaningful words only

        const fuzzyMatchRoute = (origin, dest) => {
          if(!routes.length) return null;
          const okw = routeKeywords(origin);
          const dkw = routeKeywords(dest);
          let best=0, bestR=null;
          for(const r of routes){
            const F=(r.from||"").toUpperCase(), T=(r.to||"").toUpperCase();
            let score=0;
            okw.forEach(w=>{ if(F.includes(w))score+=3; if(T.includes(w))score+=1; });
            dkw.forEach(w=>{ if(T.includes(w))score+=3; if(F.includes(w))score+=1; });
            if(score>best){best=score;bestR=r;}
          }
          return best>=3 ? bestR : null; // require at least one strong keyword hit
        };

        const response = await fetch("/api/ai-chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            system: `You are an expert at reading Canadian oil sands trucking dispatch sheets, Bills of Lading (BOL), and Trimac/TMW trip sheets. Extract load information and respond ONLY with valid JSON — no explanation, no markdown.

SAVED ROUTES (indexed):
${routeList}

SAVED TRUCKS: ${truckList||"none"}

━━━ CANADIAN OIL COMPANY NAME RECOGNITION ━━━
OCR and handwriting cause company names to appear in many variations. Normalize them:
- "CANADIAN NATURAL RESOURCES LTD" / "CANADIAN NATURAL RESOURCES LID" / "CDN NAT" / "CNRL" / "CANADIAN NATURAL" → keyword: CNRL
  CNRL sites: HORIZON (Fort MacKay), KIRBY, PELICAN LAKE, PRIMROSE, JACKFISH, CHEECHAM
- "SUNCOR ENERGY" / "SUNCOR" → keyword: SUNCOR
  Suncor sites: BASE PLANT, MACKAY RIVER, FIREBAG, VOYAGEUR
- "SYNCRUDE CANADA" / "SYNCRUDE" → keyword: SYNCRUDE
  Syncrude sites: AURORA, MILDRED LAKE
- "IMPERIAL OIL" / "ESSO" / "IMPERIAL" → keyword: IMPERIAL
  Imperial sites: COLD LAKE, KEARL
- "CENOVUS ENERGY" / "CENOVUS" → keyword: CENOVUS
  Cenovus sites: FOSTER CREEK, CHRISTINA LAKE
- "MEG ENERGY" / "MEG" → keyword: MEG, CHRISTINA
- "PETROSUL" / "PETRO-SUL" → keyword: PETROSUL
- "HEARTLAND" / "HEARTLAND FT SASK" / "HEARTLAND FORT SASK" / "HEARTLAND PETROCHEMICALS" → keyword: HEARTLAND (Fort Saskatchewan delivery/processing terminal)
- "CHECHEM" / "CHEECHAM" / "CHEECHAM TERMINAL" / "CHEEHAM" / "CHECHAM" → keyword: CHEECHAM (CNRL terminal near Fort McMurray — OCR commonly misspells as CHECHEM, CHEEHAM, CHECHAM)
- "RAIL BAR" / "RAILBAR" / "RAIL-BAR" / "RAIL BAR TERMINAL" → keyword: RAILBAR (loading/offloading terminal — match saved routes containing RAIL or RAILBAR)

Use this normalization to improve route matching. If Consignor/shipper = "CANADIAN NATURAL RESOURCES LID", treat it as CNRL and look for CNRL or HORIZON in saved routes.

━━━ ROUTE MATCHING ━━━
Canadian dispatch sheets use full legal land descriptions. IGNORE LSD grid numbers (##-##-##-##W#). Extract PLACE NAME keywords (e.g. "HORIZON", "HEARTLAND", "MILDRED LAKE") AND use the normalized company name above as an additional keyword.

Example: Consignor = "CANADIAN NATURAL RESOURCES LID", Pickup = "CDN NAT HORIZON 10-18-96-11 W4 Fort MacKay"
→ keywords: CNRL, HORIZON, FORT MCKAY — match against saved routes for any of these words.

Return matchedRouteIdx = index of best matching saved route, or -1 if no match.

━━━ DATE & TIME — TRIMAC SHEET FORMAT ━━━
Trimac sheets have two date/time stamps:
- "Arrive Date: M-DD-YYYY H:MM a.m./p.m. Mountain Standard Time" → this is when driver ARRIVED at the loading site
  → date = YYYY-MM-DD (convert from M-DD-YYYY)
  → time = HH:MM 24h (convert 12h a.m./p.m. to 24h)
- "Submit Date: M-DD-YYYY H:MM a.m./p.m. Mountain Standard Time" → this is when loading was COMPLETED and driver submitted the paperwork
  → completedTime = HH:MM 24h ← ALWAYS map Submit Date to completedTime

Example: "Arrive Date: 2-23-2026 5:54 p.m." → date: "2026-02-23", time: "17:54"
Example: "Submit Date: 2-23-2026 5:55 p.m." → completedTime: "17:55"

━━━ VOLUME / QUANTITY — CRITICAL ━━━
"quantity" = physical load amount (cubic metres, metric tonnes, KG). Priority order:
1. "Mass/Volume" — PRIMARY field on Trimac sheets. Take this number.
2. "Net Volume", "Net Weight (Tonnes)", "Volume", "Cubic Metres", "m³", "yd³"
3. "Gross Weight" minus "Tare Weight" (only if Net not shown)
4. "Quantity" field only IF UOM is M3/YD3/MTN/KG — NEVER if UOM is HRS/KM

UOM → billingMethod:
- M3 / m³ / yd³ / cubic / CBM → "per_cubic"
- MTN / MT / tonne / metric tonne / kg / KG → "per_cubic"
- HRS / hours → "per_hour" (WAIT TIME only — never put in quantity)
- KM / km / miles → "per_km"
- Flat / per load / trip → "per_load"

━━━ WAITING TIME SECTION ━━━
Trimac "Customer Information" section at bottom:
"Charge Type: Waiting Time", "Quantity: X", "UOM: HRS - Hours"
"Reason: total hrs A-B net hrs C to unload/load"

- "net hrs X to unload" → offloadWaitMins = round(X × 60)
- "net hrs X to load" → loadWaitMins = round(X × 60)
- No Reason text → use Quantity × 60 as offloadWaitMins
- Example: "total hrs 2.5-1.5 net hrs 1 to unload" → offloadWaitMins = 60

━━━ ALL FIELDS ━━━
- tmwLoadNumber: Scan the ENTIRE document for any of these labels and extract the associated number. Priority order:
  1. "TMW #", "TMW#", "TMW Number", "TMW No", "TMW No.", "T.M.W." → use this value directly
  2. "Order # (- Delivery)", "Order Number", "Order No", "Order No." → number only (digits only, strip dashes/spaces)
  3. "Load #", "Load Number", "Load No", "Load No.", "Load ID"
  4. "Trip #", "Trip Number", "Trip No", "Trip No."
  5. "BOL #", "BOL Number", "Bill of Lading #", "Bill of Lading Number", "B/L #", "B/L No"
  6. "Pro #", "Pro Number", "PRO No", "P.R.O."
  7. "Dispatch #", "Dispatch Number", "Dispatch No", "Manifest #", "Manifest Number"
  8. "Reference #", "Ref #", "Ref No", "Reference No"
  9. "Job #", "Job Number", "Work Order #", "WO #"
  IMPORTANT: If multiple numbers exist, prefer the one labelled TMW first, then the first numeric-looking reference number (6–12 digits). Strip all non-digit characters from the final value. If the number appears as "12345-6" extract "123456". Return "" if truly absent.
- consignorName: Normalized company name (e.g. "CNRL", "Suncor", "Syncrude") — used for route matching hint
- loadOrigin: Full Pickup site text from document
- loadDestination: Full Delivery site text from document
- matchedRouteIdx: Best matching route index from SAVED ROUTES (-1 if none)
- date: YYYY-MM-DD from Arrive Date
- time: HH:MM 24h from Arrive Date timestamp → driver arrival at LOADING site
- appointmentTime: Scheduled load appointment HH:MM 24h (if shown separately)
- completedTime: HH:MM 24h from SUBMIT DATE — loading completed/driver left site
- offloadArrivalTime: Arrived at delivery HH:MM 24h
- offloadCompletedTime: Done offloading HH:MM 24h
- truckNumber: "Tractor #", "Unit #", "Truck #", "Power Unit"
- billingMethod: From UOM mapping
- quantity: Mass/Volume number only — no units, no HRS values
- loadWaitMins: Minutes at load site (integer, 0 if none)
- offloadWaitMins: Minutes at offload site (integer, 0 if none)
- note: Commodity, Driver Name, Receipt #, Account (e.g. PETROSUL), trailer numbers, special instructions

Respond ONLY with this JSON:
{"tmwLoadNumber":"","consignorName":"","loadOrigin":"","loadDestination":"","matchedRouteIdx":-1,"date":"","time":"","appointmentTime":"","completedTime":"","offloadArrivalTime":"","offloadCompletedTime":"","truckNumber":"","billingMethod":"per_cubic","quantity":"","loadWaitMins":0,"offloadWaitMins":0,"note":""}

Use "" for missing strings, 0 for missing numbers. Never guess. Never put wait-time hours in quantity.`,
            message: "Extract all load details from this dispatch document. CRITICAL: scan every corner of the document for a TMW #, Load #, Order #, BOL #, Trip #, Pro #, or any reference number — put it in tmwLoadNumber (digits only). Pay special attention to Mass/Volume for the quantity field and any Waiting Time section for offloadWaitMins.",
            maxTokens: 800,
            image: base64,
            mediaType: mediaType
          })
        });
        const data = await response.json();
        const text = data.text || "";
        let jsonStr = text.replace(/```json|```/g,"").trim();
        const jsonMatch = jsonStr.match(/\{[\s\S]*\}/);
        if (!jsonMatch) throw new Error("No JSON object in response");
        const parsed = JSON.parse(jsonMatch[0]);

        // Route matching: try AI index first, then client-side fuzzy fallback
        // Also pass consignorName so "CNRL" can match a route named "Horizon" (CNRL's site)
        let matchedRoute = null;
        const aiIdx = Number(parsed.matchedRouteIdx ?? -1);
        if (aiIdx >= 0 && aiIdx < routes.length) {
          matchedRoute = routes[aiIdx];
        } else {
          // Include consignor name as extra origin hint — CNRL operates Horizon, Suncor=Base Plant etc.
          const originHint = [parsed.loadOrigin, parsed.consignorName].filter(Boolean).join(" ");
          matchedRoute = fuzzyMatchRoute(originHint, parsed.loadDestination);
        }

        const matchedTruck = (activeTrucks||[]).find(t=>
          t.truckNumber === parsed.truckNumber ||
          t.truckNumber?.toUpperCase().includes((parsed.truckNumber||"").toUpperCase()) ||
          (parsed.truckNumber||"").toUpperCase().includes(t.truckNumber?.toUpperCase()||"X")
        );

        // If route matched, auto-fill earnings and driver pay from that route's rates
        const rd = matchedRoute ? getRD(`${matchedRoute.from} → ${matchedRoute.to}`) : null;
        const qty = parsed.quantity ? String(parsed.quantity) : "";
        const autoEarnings = rd && qty ? String(calcEarnings(rd, qty)) : (rd?.rate ? String(rd.rate) : "");
        const autoDriverPay = rd && qty ? String(calcDriverPay(rd, qty)) : (rd ? String(rd.driverPay||rd.pay||"") : "");
        const autoBilling = parsed.billingMethod && parsed.billingMethod!=="per_load" ? parsed.billingMethod : (rd?.billingMethod||"per_load");

        setForm(f=>({
          ...f,
          tmwLoadNumber: parsed.tmwLoadNumber || f.tmwLoadNumber,
          location: matchedRoute ? `${matchedRoute.from} → ${matchedRoute.to}` : f.location,
          loadOrigin: parsed.loadOrigin || (matchedRoute ? matchedRoute.from : f.loadOrigin),
          loadDestination: parsed.loadDestination || (matchedRoute ? matchedRoute.to : f.loadDestination),
          date: parsed.date || f.date,
          time: parsed.time || f.time,
          appointmentTime: parsed.appointmentTime || f.appointmentTime,
          completedTime: parsed.completedTime || f.completedTime,
          offloadArrivalTime: parsed.offloadArrivalTime || f.offloadArrivalTime,
          offloadCompletedTime: parsed.offloadCompletedTime || f.offloadCompletedTime,
          truckId: matchedTruck ? matchedTruck.id : f.truckId,
          manualTruckNumber: !matchedTruck && parsed.truckNumber ? parsed.truckNumber : f.manualTruckNumber,
          billingMethod: autoBilling || f.billingMethod,
          quantity: qty || f.quantity,
          earnings: autoEarnings || f.earnings,
          driverBasePay: autoDriverPay || f.driverBasePay,
          loadWaitMins: parsed.loadWaitMins ? String(parsed.loadWaitMins) : f.loadWaitMins,
          offloadWaitMins: parsed.offloadWaitMins ? String(parsed.offloadWaitMins) : f.offloadWaitMins,
          note: parsed.note || f.note,
        }));
        setScanLoadMsg("✅ Load details filled in — please review!");
        setTimeout(()=>setScanLoadMsg(""),4000);
      } catch(err) {
        setScanLoadMsg("⚠️ Could not read document. Please fill in manually.");
        setTimeout(()=>setScanLoadMsg(""),4000);
      }
      setScanningLoad(false);
    };
    reader.readAsDataURL(file);
    e.target.value="";
  };

  // ── Off Loading AI scan ──
  const scanOffloadDocument = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setOffloadScanMsg("⚠️ Please upload an image (JPG, PNG, HEIC). Take a photo of the document instead.");
      setTimeout(()=>setOffloadScanMsg(""),5000);
      e.target.value="";
      return;
    }
    setOffloadScanning(true);
    setOffloadScanMsg("🤖 Reading offload document…");
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const base64Data = ev.target.result;
      setOffloadDocPreview(base64Data);
      setForm(f=>({...f, offloadDocImage: base64Data}));
      const base64 = base64Data.split(",")[1];
      const mediaType = base64Data.split(";")[0].split(":")[1];
      try {
        const response = await fetch("/api/ai-chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            system: `You are reading a trucking offload/delivery document. Extract ONLY the following fields and respond with valid JSON only — no explanation.

Fields to extract:
- offloadArrivalTime: Time driver arrived at offload/delivery site. HH:MM 24h format. Look for "Arrival", "Arrived", "In Time", "Gate In", "Begin", or any timestamp at start. If "Arrive Date" present convert to HH:MM.
- offloadCompletedTime: Time offloading was completed/done. HH:MM 24h format. Look for "Completed", "Done", "Out Time", "Gate Out", "Submit", "End", "Departed".
- offloadDate: Date of delivery/offload in YYYY-MM-DD format. Look for delivery date, submit date, or arrival date at delivery site.
- tmwConfirm: TMW #, Order #, Load #, BOL #, Trip #, Pro #, or any reference number found. Digits only, strip dashes.
- offloadNote: Delivery terminal name, ticket #, commodity, any remarks on document.

Respond ONLY with JSON: {"offloadArrivalTime":"","offloadCompletedTime":"","offloadDate":"","tmwConfirm":"","offloadNote":""}
Use "" for missing. Convert all times to 24h HH:MM.`,
            message: "Extract offload arrival time, completed time, date, TMW# confirmation, and any notes from this delivery document.",
            maxTokens: 300,
            image: base64,
            mediaType: mediaType
          })
        });
        const data = await response.json();
        const text = data.text || "";
        let jsonStr = text.replace(/```json|```/g,"").trim();
        const jsonMatch = jsonStr.match(/\{[\s\S]*\}/);
        if (!jsonMatch) throw new Error("No JSON");
        const parsed = JSON.parse(jsonMatch[0]);
        setForm(f=>({
          ...f,
          offloadArrivalTime: parsed.offloadArrivalTime || f.offloadArrivalTime,
          offloadCompletedTime: parsed.offloadCompletedTime || f.offloadCompletedTime,
          offloadDate: parsed.offloadDate || f.offloadDate,
          offloadNote: parsed.offloadNote ? (f.offloadNote ? f.offloadNote + " · " + parsed.offloadNote : parsed.offloadNote) : f.offloadNote,
          tmwLoadNumber: (!f.tmwLoadNumber && parsed.tmwConfirm) ? parsed.tmwConfirm : f.tmwLoadNumber,
          offloadDocImage: base64Data,
        }));
        // Auto-calc wait mins from newly filled times
        const filled = {
          offloadArrivalTime: parsed.offloadArrivalTime || form.offloadArrivalTime,
          offloadCompletedTime: parsed.offloadCompletedTime || form.offloadCompletedTime,
        };
        if (filled.offloadArrivalTime && filled.offloadCompletedTime) {
          const [ah,am] = filled.offloadArrivalTime.split(":").map(Number);
          const [dh,dm] = filled.offloadCompletedTime.split(":").map(Number);
          let diff = (dh*60+dm)-(ah*60+am);
          if (diff < 0) diff += 1440;
          if (diff >= 0) setForm(f=>({...f, offloadWaitMins: diff.toString()}));
        }
        setOffloadScanMsg("✅ Offload details filled — please review!");
        setTimeout(()=>setOffloadScanMsg(""),4000);
      } catch(err) {
        setOffloadScanMsg("⚠️ Could not read document. Please fill in manually.");
        setTimeout(()=>setOffloadScanMsg(""),4000);
      }
      setOffloadScanning(false);
    };
    reader.readAsDataURL(file);
    e.target.value="";
  };

  const submit=()=>{
    if(!form.tmwLoadNumber||!form.tmwLoadNumber.trim()){alert("TMW # is required. Please enter a TMW number before saving.");return;}
    if(!form.location)return;
    const rd=getRD(form.location);
    let finalEarn=Number(form.earnings)||(rd?.rate?Number(rd.rate):Number(rates.perLoadRate)||0);
    let drvName=!isOwner?(session.fullName||session.name):form.driverFullName;
    if(isOwner&&form.assignedDriverUid){const d=users[form.assignedDriverUid];drvName=d?(d.fullName||d.name):"";}

    // Owner driving themselves — driverBasePay already set from route's driver pay field
    // Just set assignedDriverUid = owner's uid so they appear in payroll/reports
    let finalDriverBasePay = form.driverBasePay;
    let finalAssignedDriverUid = form.assignedDriverUid;
    let finalDriverFullName = drvName;
    if(isOwner && !form.assignedDriverUid && Number(form.driverBasePay||0) > 0) {
      finalAssignedDriverUid = session.uid;
      finalDriverFullName = session.fullName||session.name||"Owner";
    }

    // Save smart defaults for next time
    if (form.truckId && form.truckId !== "__manual__") localStorage.setItem(`tp-last-truck-${session.uid}`, form.truckId);
    if (form.location) localStorage.setItem(`tp-last-route-${session.uid}`, form.location);
    // Tag "My Own Load" — set by explicit loadMode toggle or when driver has no fleet
    const isOwnLoad = !isOwner && (loadMode === "own" || !hasFleet);
    onSave({...form,earnings:finalEarn,driverFullName:finalDriverFullName,driverBasePay:finalDriverBasePay,assignedDriverUid:finalAssignedDriverUid,id:editLoad?.id||Date.now().toString(),addedBy:session.uid,isOwnLoad});
  };

  const methodLabel = {per_load:"Per Load",per_cubic:"Per Cubic",per_hour:"Per Hour",per_pct:"% Based",per_km:"Per KM"}[form.billingMethod||"per_load"]||"Per Load";
  const LD = {
    pageBg:     darkMode ? "#111111" : "#F5F6F8",
    headerBg:   darkMode ? "#111111" : "linear-gradient(135deg,#1C2B4A,#243655)",
    headerSub:  darkMode ? "rgba(255,255,255,0.4)" : "rgba(255,255,255,0.55)",
    cardBg:     darkMode ? "#1C1C1C" : "#FFFFFF",
    cardBorder: darkMode ? "rgba(255,255,255,0.08)" : "#E2E2E2",
    inputBg:    darkMode ? "#262626" : "#F9FAFB",
    inputBorder:darkMode ? "rgba(255,255,255,0.12)" : "#D1D5DB",
    inputColor: darkMode ? "#FFFFFF" : "#1A1A1A",
    labelColor: darkMode ? "rgba(255,255,255,0.5)" : "#374151",
    divider:    darkMode ? "rgba(255,255,255,0.08)" : "#E5E7EB",
    rowText:    darkMode ? "rgba(255,255,255,0.65)" : "#4B5563",
  };
  const ldInput = {width:"100%",padding:"12px 14px",borderRadius:10,border:`1.5px solid ${LD.inputBorder}`,background:LD.inputBg,color:LD.inputColor,fontSize:15,fontWeight:700,fontFamily:"'Barlow Condensed',sans-serif",outline:"none",boxSizing:"border-box"};
  const ldLabel = {display:"block",fontSize:13,fontWeight:600,color:LD.labelColor,marginBottom:6};

  return (
    <div className="slt-page" style={{background:LD.pageBg,color:LD.inputColor}}>
      <div style={{background:LD.headerBg,padding:"20px 20px 18px"}}>
        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:17,color:"#fff",letterSpacing:0.5}}>{editLoad?"Edit Load":"Log a Load"}</div>
        <div style={{fontSize:13,color:LD.headerSub,marginTop:2}}>Fill in load details below</div>
      </div>

      {/* ── LOADING / OFF LOADING TAB SWITCHER ── */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",background:"#fff",borderBottom:"2px solid #E2E2E2",position:"sticky",top:0,zIndex:10}}>
        <div onClick={()=>setActiveLoadSubTab("loading")} style={{padding:"13px 8px",textAlign:"center",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:14,letterSpacing:0.3,cursor:"pointer",color:activeLoadSubTab==="loading"?"#E8962E":"#9CA3AF",borderBottom:`3px solid ${activeLoadSubTab==="loading"?"#E8962E":"transparent"}`,background:activeLoadSubTab==="loading"?"rgba(232,150,46,0.04)":"transparent",transition:"all 0.15s"}}>⬆️ Loading</div>
        <div onClick={()=>setActiveLoadSubTab("offloading")} style={{padding:"13px 8px",textAlign:"center",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:14,letterSpacing:0.3,cursor:"pointer",color:activeLoadSubTab==="offloading"?"#E8962E":"#9CA3AF",borderBottom:`3px solid ${activeLoadSubTab==="offloading"?"#E8962E":"transparent"}`,background:activeLoadSubTab==="offloading"?"rgba(232,150,46,0.04)":"transparent",transition:"all 0.15s"}}>⬇️ Off Loading</div>
      </div>

      {activeLoadSubTab==="loading" && (
      <div className="slt-container-sm">

        {/* ── LOAD TYPE TOGGLE (fleet drivers only) ── */}
        {!isOwner && hasFleet && (
          <div style={{marginBottom:16}}>
            <div style={{fontSize:13,fontWeight:800,color:"#E8962E",textTransform:"uppercase",letterSpacing:1.5,marginBottom:8}}>🚛 Load Type</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <button
                onClick={()=>{ setLoadMode("fleet"); setForm(f=>({...f,location:"",loadOrigin:"",loadDestination:"",earnings:"",driverBasePay:"",quantity:"",billingMethod:"per_load"})); }}
                style={{padding:"12px 8px",borderRadius:12,border:`2px solid ${loadMode==="fleet"?"#E8962E":"#D1D5DB"}`,background:loadMode==="fleet"?"linear-gradient(135deg,#1C2B4A,#243655)":"#fff",color:loadMode==="fleet"?"#fff":"#374151",fontWeight:800,fontSize:13,cursor:"pointer",transition:"all 0.2s"}}>
                🏢 For Owner Operator
                {loadMode==="fleet"&&<div style={{fontSize:11,fontWeight:600,color:"rgba(255,255,255,0.7)",marginTop:2}}>Owner's routes & rates</div>}
              </button>
              <button
                onClick={()=>{ setLoadMode("own"); setForm(f=>({...f,location:"",loadOrigin:"",loadDestination:"",earnings:"",driverBasePay:"",quantity:"",billingMethod:"per_load"})); }}
                style={{padding:"12px 8px",borderRadius:12,border:`2px solid ${loadMode==="own"?"#E8962E":"#D1D5DB"}`,background:loadMode==="own"?"linear-gradient(135deg,#E8962E,#E8962E)":"#fff",color:loadMode==="own"?"#fff":"#374151",fontWeight:800,fontSize:13,cursor:"pointer",transition:"all 0.2s"}}>
                👤 My Own Load
                {loadMode==="own"&&<div style={{fontSize:11,fontWeight:600,color:"rgba(255,255,255,0.85)",marginTop:2}}>Your routes & rates</div>}
              </button>
            </div>
            {loadMode==="own"&&<div style={{fontSize:12,color:"#374151",marginTop:8,padding:"6px 10px",background:"rgba(232,150,46,0.08)",borderRadius:8,border:"1px solid rgba(232,150,46,0.2)"}}>✅ Private load — not visible to your fleet owner</div>}
            {loadMode==="fleet"&&<div style={{fontSize:12,color:"#374151",marginTop:8,padding:"6px 10px",background:"rgba(28,43,74,0.06)",borderRadius:8,border:"1px solid rgba(28,43,74,0.15)"}}>👁 This load will be visible to your fleet owner</div>}
          </div>
        )}

        {/* ── ROUTE INFO ── */}
        <div style={{fontSize:13,fontWeight:800,color:"#E8962E",textTransform:"uppercase",letterSpacing:1.5,marginBottom:10}}>📍 Route Info</div>
        <div style={{background:LD.cardBg,borderRadius:14,padding:"16px",marginBottom:14,border:`1.5px solid ${LD.cardBorder}`}}>
            <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:12,marginBottom:14}}>
              <div>
                <label style={ldLabel}>Saved Route</label>
                <select value={form.location||""} onChange={e=>handleRoute(e.target.value)} className="ld-input" style={ldInput}>
                  <option value="">— Select Route —</option>
                  {(activeRoutes||allRoutes).map((r,i)=><option key={i} value={`${r.from} → ${r.to}`}>{r.from} → {r.to}</option>)}
                </select>
              </div>
              <div>
                <label style={ldLabel}>TMW #</label>
                <input
                  type="text"
                  value={form.tmwLoadNumber||""}
                  onChange={e=>setForm(f=>({...f,tmwLoadNumber:e.target.value}))}
                  className="ld-input"
                  style={{...ldInput, fontWeight:900, fontSize:16}}
                  placeholder="e.g. 1234567"
                />
              </div>
            </div>
            <div style={{marginBottom:14}}>
              <label style={ldLabel}>Origin / Loading Site</label>
              <input value={form.loadOrigin||""} onChange={e=>setForm(f=>({...f,loadOrigin:e.target.value,location:e.target.value+(f.loadDestination?` → ${f.loadDestination}`:"")}))} className="ld-input" style={ldInput} placeholder="e.g. CNRL Horizon, Suncor Base, Pit 4"/>
            </div>
            <div style={{marginBottom:14}}>
              <label style={ldLabel}>Destination / Offload Site</label>
              <input value={form.loadDestination||""} onChange={e=>setForm(f=>({...f,loadDestination:e.target.value,location:(f.loadOrigin||"")+` → ${e.target.value}`}))} className="ld-input" style={ldInput} placeholder="e.g. Heartland, Upgrader Dock 3, Plant Gate"/>
            </div>
            <div style={{marginBottom:14}}>
              <label style={ldLabel}>Load Date</label>
              <input name="date" type="date" value={form.date} onChange={hc} className="ld-input" style={ldInput}/>
            </div>
            <div style={{marginBottom:14}}>
              <label style={ldLabel}>Arrival Time</label>
              <input
                name="time"
                type="time"
                value={form.time||""}
                onChange={hc}
                className="ld-input"
                style={ldInput}
              />
            </div>
            <div style={{marginBottom:14}}>
              <label style={ldLabel}>Appointment Time</label>
              <input
                name="appointmentTime"
                type="time"
                value={form.appointmentTime||""}
                onChange={e=>{
                  hc(e);
                  const mins = calcWaitMins(e.target.value, form.completedTime);
                  if (mins !== null) setForm(f=>({...f, appointmentTime: e.target.value, loadWaitMins: mins.toString()}));
                }}
                className="ld-input"
                style={ldInput}
              />
            </div>
            <div style={{marginBottom:14}}>
              <label style={ldLabel}>Completed Time</label>
              <input
                name="completedTime"
                type="time"
                value={form.completedTime||""}
                onChange={e=>{
                  hc(e);
                  const mins = calcWaitMins(form.appointmentTime, e.target.value);
                  if (mins !== null) setForm(f=>({...f, completedTime: e.target.value, loadWaitMins: mins.toString()}));
                }}
                className="ld-input"
                style={ldInput}
              />
              {(()=>{
                const mins = calcWaitMins(form.appointmentTime, form.completedTime);
                if (mins === null || mins === 0) return null;
                const h = Math.floor(mins / 60), m = mins % 60;
                return (
                  <div style={{marginTop:6, fontSize:12, fontWeight:700, color:LD.labelColor}}>
                    ⏱ Wait auto-calculated: {h > 0 ? `${h}h ` : ""}{m}min → saved to Wait Time
                  </div>
                );
              })()}
            </div>
            {(()=>{
              return null; // TIMES SECTION HIDDEN (legacy — replaced by fields above)
              const calcMins=(a,b)=>null;
              const loadMins=null; const offMins=null; const fmtMins=m=>"—";
              return (
                <div style={{background:C.offWhite,borderRadius:12,padding:"14px 16px",marginBottom:14}}>
                  <div style={{fontSize:14,fontWeight:900,color:"#E8962E",letterSpacing:0.5,textTransform:"uppercase",marginBottom:12,fontFamily:"'Barlow Condensed',sans-serif"}}>⏰ Times</div>

                  {/* Loading times */}
                  <div style={{fontSize:13,fontWeight:700,color:C.blue,marginBottom:8,textTransform:"uppercase",letterSpacing:0.8}}>🏭 Loading Site</div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:10}}>
                    <div>
                      <label className="slt-label" style={{fontSize:13}}>📅 Appt</label>
                      <input name="appointmentTime" type="time" value={form.appointmentTime||""} onChange={e=>{hc(e);const m=calcMins(e.target.value,form.completedTime);if(m!==null)setForm(f=>({...f,loadWaitMins:m.toString()}));}} className="slt-input" style={{fontSize:13,padding:"8px 10px"}}/>
                    </div>
                    <div>
                      <label className="slt-label" style={{fontSize:13}}>🛬 Arrival</label>
                      <input name="time" type="time" value={form.time} onChange={hc} className="slt-input" style={{fontSize:13,padding:"8px 10px"}}/>
                    </div>
                    <div>
                      <label className="slt-label" style={{fontSize:13}}>✅ Done</label>
                      <input name="completedTime" type="time" value={form.completedTime||""} onChange={e=>{hc(e);const m=calcMins(form.appointmentTime,e.target.value);if(m!==null)setForm(f=>({...f,loadWaitMins:m.toString()}));}} className="slt-input" style={{fontSize:13,padding:"8px 10px"}}/>
                    </div>
                  </div>
                  {loadMins!==null&&(
                    <div style={{background:"#F5F6F8",borderRadius:8,padding:"8px 14px",marginBottom:14,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <span style={{fontSize:12,color:C.blue,fontWeight:700}}>⏱ Load Wait Auto-Calculated</span>
                      <span style={{fontSize:14,fontWeight:800,color:C.blue}}>{fmtMins(loadMins)}</span>
                    </div>
                  )}

                  {/* Offloading times */}
                  <div style={{borderTop:`1px solid ${C.border}`,paddingTop:12,marginTop:4}}>
                    <div style={{fontSize:13,fontWeight:700,color:C.orange,marginBottom:8,textTransform:"uppercase",letterSpacing:0.8}}>🏗 Offloading Site</div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                      <div>
                        <label className="slt-label" style={{fontSize:13}}>🛬 Arrival</label>
                        <input name="offloadArrivalTime" type="time" value={form.offloadArrivalTime||""} onChange={e=>{hc(e);const m=calcMins(e.target.value,form.offloadCompletedTime);if(m!==null)setForm(f=>({...f,offloadWaitMins:m.toString()}));}} className="slt-input" style={{fontSize:13,padding:"8px 10px"}}/>
                      </div>
                      <div>
                        <label className="slt-label" style={{fontSize:13}}>✅ Done</label>
                        <input name="offloadCompletedTime" type="time" value={form.offloadCompletedTime||""} onChange={e=>{hc(e);const m=calcMins(form.offloadArrivalTime,e.target.value);if(m!==null)setForm(f=>({...f,offloadWaitMins:m.toString()}));}} className="slt-input" style={{fontSize:13,padding:"8px 10px"}}/>
                      </div>
                    </div>
                    {offMins!==null&&(
                      <div style={{background:"#FFF3E0",borderRadius:8,padding:"8px 14px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <span style={{fontSize:12,color:C.orange,fontWeight:700}}>⏱ Offload Wait Auto-Calculated</span>
                        <span style={{fontSize:14,fontWeight:800,color:C.orange}}>{fmtMins(offMins)}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Billing method auto-calc — HIDDEN */}
            {false&&form.location&&getRD(form.location)&&(()=>{
              const rd=getRD(form.location);
              const method=rd.billingMethod||"per_load";
              const colors={per_load:C.teal,per_cubic:C.green,per_hour:C.orange,per_pct:"#E8962E",per_km:"#E8962E"};
              const icons={per_load:"📦",per_cubic:"📐",per_hour:"⏱",per_pct:"💯",per_km:"🛣"};
              const labels={per_load:"Per Load — flat rate",per_cubic:"Per Cubic Yard",per_hour:"Per Hour",per_pct:"% of Load Earnings",per_km:"Per KM / Mile"};
              const driverHints={per_load:"Your pay is set for this route",per_cubic:"Enter cubic yards hauled — your pay is a flat rate",per_hour:"Enter your hours — your pay will calculate automatically",per_pct:"Your pay is automatically calculated as a % of load earnings",per_km:"Enter KM driven — your pay calculates automatically"};
              const ownerHints={per_load:"Earnings auto-filled",per_cubic:"Enter cubic yards hauled",per_hour:"Enter hours worked",per_pct:"Driver pay auto-calculated from %",per_km:"Enter KM driven"};
              const col=colors[method];

              // Driver's personal rate for this route
              const driverUid=form.assignedDriverUid||(isOwner?"":session.uid);
              const overrides=rd.driverOverrides||{};
              const driverHourlyRate=driverUid&&overrides[driverUid]!==undefined&&overrides[driverUid]!==""?Number(overrides[driverUid]):Number(rd.driverPay||rd.pay||0);

              return(
                <div style={{marginBottom:14}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,background:`${col}12`,border:`1.5px solid ${col}40`,borderRadius:10,padding:"10px 14px",marginBottom:method!=="per_load"?12:0}}>
                    <span style={{fontSize:20}}>{icons[method]}</span>
                    <div style={{flex:1}}>
                      <div style={{fontSize:12,fontWeight:800,color:col}}>{labels[method]}</div>
                      <div style={{fontSize:13,color:C.textDarkMut}}>{isOwner?ownerHints[method]:driverHints[method]}</div>
                    </div>
                    {/* Per load: owner sees business rate, driver sees flat pay */}
                    {method==="per_load"&&isOwner&&<div style={{fontSize:17,fontWeight:800,color:C.green,fontFamily:"'Barlow Condensed',sans-serif"}}>{fmtC(rd.ratePerLoad||rd.rate||0)}</div>}
                    {method==="per_load"&&!isOwner&&<div style={{fontSize:17,fontWeight:800,color:C.blue,fontFamily:"'Barlow Condensed',sans-serif"}}>{fmtC(rd.driverPay||rd.pay||0)}</div>}
                    {/* Per hour: show driver their hourly rate */}
                    {method==="per_hour"&&!isOwner&&<div style={{textAlign:"right"}}><div style={{fontSize:13,color:"rgba(0,0,0,0.55)"}}>Your rate</div><div style={{fontSize:17,fontWeight:800,color:C.orange,fontFamily:"'Barlow Condensed',sans-serif"}}>{fmtC(driverHourlyRate)}/hr</div></div>}
                    {/* Per km: show driver their km rate */}
                    {method==="per_km"&&!isOwner&&<div style={{textAlign:"right"}}><div style={{fontSize:13,color:"rgba(0,0,0,0.55)"}}>Your rate</div><div style={{fontSize:17,fontWeight:800,color:"#E8962E",fontFamily:"'Barlow Condensed',sans-serif"}}>{fmtC(driverHourlyRate)}/km</div></div>}
                    {/* Per cubic: driver sees flat pay (same as per_load) */}
                    {method==="per_cubic"&&!isOwner&&<div style={{fontSize:17,fontWeight:800,color:C.blue,fontFamily:"'Barlow Condensed',sans-serif"}}>{fmtC(rd.driverPay||rd.pay||0)}</div>}
                  </div>

                  {method!=="per_load"&&(
                    <div>
                      <label className="slt-label">{method==="per_cubic"||method==="per_pct"?"Cubic Yards (yd³)":method==="per_km"?"KM Driven":"Hours Worked"}</label>
                      <input type="number" step="0.1" min="0" value={form.quantity} onChange={e=>handleQuantity(e.target.value)} className="slt-input"
                        placeholder={method==="per_cubic"||method==="per_pct"?"e.g. 150":method==="per_km"?"e.g. 250":"e.g. 8.5"}
                        style={{fontSize:24,fontWeight:800,textAlign:"center"}}/>

                      {form.quantity&&(
                        <div style={{marginTop:8,borderRadius:10,padding:"10px 14px",background:method==="per_hour"?`${C.orange}10`:method==="per_km"?"rgba(124,58,237,0.08)":`${C.green}10`,border:`1px solid ${method==="per_hour"?C.orange:method==="per_km"?"#E8962E":C.green}30`}}>
                          {/* Owner sees business earnings calc */}
                          {isOwner&&<div style={{textAlign:"center"}}>
                            <div style={{fontSize:13,color:C.textDarkMed,marginBottom:4}}>
                              {method==="per_cubic"||method==="per_pct"
                                ?`${form.quantity} yd³ × ${fmtC(rd.rateCubic||0)}/yd³`
                                :method==="per_km"
                                  ?`${form.quantity} km × ${fmtC(rd.ratePerKm||0)}/km`
                                  :`${form.quantity} hrs × ${fmtC(rd.rateHour||rd.rate||0)}/hr`}
                              {" = "}<strong style={{color:C.green,fontSize:15}}>{fmtC(form.earnings)}</strong>
                            </div>
                            {method==="per_cubic"&&form.assignedDriverUid&&(
                              <div style={{fontSize:13,color:"#E8962E",marginTop:4}}>
                                {`Driver flat pay: ${fmtC(form.driverBasePay)}`}
                              </div>
                            )}
                            {method==="per_km"&&form.assignedDriverUid&&(
                              <div style={{fontSize:13,color:"#E8962E",marginTop:4}}>
                                {`Driver: ${form.quantity} km × ${fmtC(rd.driverPay||rd.pay||0)}/km = ${fmtC(form.driverBasePay)}`}
                              </div>
                            )}
                          </div>}
                          {/* Driver sees their own pay calc */}
                          {!isOwner&&method==="per_hour"&&<div style={{textAlign:"center"}}>
                            <div style={{fontSize:13,color:C.textDarkMed,marginBottom:4}}>
                              {form.quantity} hrs × {fmtC(driverHourlyRate)}/hr
                            </div>
                            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:20,fontWeight:900,color:C.orange}}>
                              = {fmtC(form.driverBasePay)}
                            </div>
                          </div>}
                          {!isOwner&&method==="per_km"&&<div style={{textAlign:"center"}}>
                            <div style={{fontSize:13,color:C.textDarkMed,marginBottom:4}}>
                              {form.quantity} km × {fmtC(driverHourlyRate)}/km
                            </div>
                            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:20,fontWeight:900,color:"#E8962E"}}>
                              = {fmtC(form.driverBasePay)}
                            </div>
                          </div>}
                          {!isOwner&&method==="per_cubic"&&(
                            <div style={{textAlign:"center"}}>
                              <div style={{fontSize:12,color:C.textDarkMed,marginBottom:4}}>Your flat pay for this load</div>
                              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:20,fontWeight:900,color:C.green}}>
                                {fmtC(form.driverBasePay)}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

        {/* ── EARNINGS ── */}
        <div style={{fontSize:13,fontWeight:800,color:"#E8962E",textTransform:"uppercase",letterSpacing:1.5,marginBottom:10,marginTop:4}}>💰 Earnings</div>
        <div style={{background:LD.cardBg,borderRadius:14,padding:"16px",marginBottom:14,border:`1.5px solid ${LD.cardBorder}`}}>

          {/* ── Per-Cubic: show Cubics Loaded field + auto-calc gross (owner only sees gross) ── */}
          {(()=>{
            const rd = form.location ? getRD(form.location) : null;
            const isCubic = (rd?.billingMethod || form.billingMethod) === "per_cubic";
            const cubicRate = Number(rd?.rateCubic || rd?.rate || 0);

            if (isCubic) {
              const cubicsLoaded = Number(form.quantity || 0);
              const autoGross = cubicRate > 0 && cubicsLoaded > 0 ? (cubicRate * cubicsLoaded).toFixed(2) : "";
              // sync auto-gross into form.earnings when quantity changes (handled by handleQuantity already)
              return (
                <div style={{marginBottom:14}}>
                  {/* Cubics Loaded input */}
                  <div style={{marginBottom:12}}>
                    <label style={ldLabel}>📐 Cubics Loaded (yd³)</label>
                    <input
                      type="number" step="0.1" min="0"
                      value={form.quantity||""}
                      onChange={e => handleQuantity(e.target.value)}
                      className="ld-input"
                      style={{...ldInput, fontSize:22, fontWeight:800, textAlign:"center"}}
                      placeholder="e.g. 45.5"
                      />
                    {cubicRate > 0 && cubicsLoaded > 0 && (
                      <div style={{marginTop:6,fontSize:12,color:LD.labelColor,textAlign:"center"}}>
                        {cubicsLoaded} yd³ × {fmtC(cubicRate)}/yd³ = <strong style={{color:"#E8962E"}}>{fmtC(autoGross)}</strong>
                      </div>
                    )}
                  </div>

                  {/* Gross Revenue — owner only */}
                  {isOwner && (
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                      <div>
                        <label style={ldLabel}>Gross Revenue ($)</label>
                        <input type="number" step="0.01" min="0" value={form.earnings||""} onChange={e=>{const v=e.target.value;setForm(f=>({...f,earnings:v}));}} className="ld-input" style={ldInput} placeholder="e.g. 2500.00"/>
                      </div>
                      <div>
                        <label style={ldLabel}>Pay Method</label>
                        <div style={{...ldInput,display:"flex",alignItems:"center",color:LD.labelColor,cursor:"default"}}>{methodLabel}</div>
                      </div>
                    </div>
                  )}

                  {/* Driver: show Pay Method only (no gross revenue) */}
                  {!isOwner && (
                    <div>
                      <label style={ldLabel}>Pay Method</label>
                      <div style={{...ldInput,display:"flex",alignItems:"center",color:LD.labelColor,cursor:"default"}}>{methodLabel}</div>
                    </div>
                  )}
                </div>
              );
            }

            // Non-cubic: normal layout — owner sees gross revenue, driver sees pay method only
            if (isOwner) {
              return (
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:form.location ? 14 : 0}}>
                  <div>
                    <label style={ldLabel}>Gross Revenue ($)</label>
                    <input type="number" step="0.01" min="0" value={form.earnings||""} onChange={e=>{const v=e.target.value;setForm(f=>({...f,earnings:v}));}} className="ld-input" style={ldInput}/>
                  </div>
                  <div>
                    <label style={ldLabel}>Pay Method</label>
                    <div style={{...ldInput,display:"flex",alignItems:"center",color:LD.labelColor,cursor:"default"}}>{methodLabel}</div>
                  </div>
                </div>
              );
            }
            return (
              <div style={{marginBottom:Number(form.driverBasePay||0)>0 ? 14 : 0}}>
                <label style={ldLabel}>Pay Method</label>
                <div style={{...ldInput,display:"flex",alignItems:"center",color:LD.labelColor,cursor:"default"}}>{methodLabel}</div>
              </div>
            );
          })()}

          {/* ── Pay Myself As Driver Toggle (owner only, own load) ── */}
          {isOwner && (!form.assignedDriverUid || form.assignedDriverUid === session.uid) && (
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",background:darkMode?"rgba(255,255,255,0.05)":"#F0FDF4",borderRadius:12,padding:"12px 14px",marginBottom:14,border:`1.5px solid ${form.payMyselfAsDriver?"#16A34A":"#E5E7EB"}`}}>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:LD.rowText}}>🧑‍💼 Pay myself as driver?</div>
                <div style={{fontSize:12,color:LD.labelColor,marginTop:2}}>{form.payMyselfAsDriver?"Driver pay deducted from company gross":"No driver deduction — company keeps full gross"}</div>
              </div>
              <div
                onClick={()=>setForm(f=>{
                  const turningOn = !f.payMyselfAsDriver;
                  if(turningOn){
                    // Restore driverBasePay from route definition or keep existing if non-zero
                    const rd = getRD(f.location);
                    const restoredPay = (f._savedDriverBasePay && Number(f._savedDriverBasePay)>0)
                      ? f._savedDriverBasePay
                      : rd ? String(Number(rd.driverPay||rd.pay||0)) : "";
                    return {...f, payMyselfAsDriver:true, driverBasePay:restoredPay, _savedDriverBasePay:undefined};
                  } else {
                    // Turning off: save current pay so we can restore it later
                    return {...f, payMyselfAsDriver:false, _savedDriverBasePay:f.driverBasePay||"", driverBasePay:"0"};
                  }
                })}
                style={{width:44,height:26,borderRadius:13,background:form.payMyselfAsDriver?"#16A34A":"#D1D5DB",cursor:"pointer",position:"relative",transition:"background 0.2s",flexShrink:0}}>
                <div style={{position:"absolute",top:3,left:form.payMyselfAsDriver?20:3,width:20,height:20,borderRadius:"50%",background:"#fff",transition:"left 0.2s"}}/>
              </div>
            </div>
          )}

          {/* Owner earnings summary — highlighted grey card */}
          {isOwner&&form.location&&Number(form.earnings||0)>0&&(
            <div style={{background:darkMode?"rgba(255,255,255,0.05)":"#F3F4F6",borderRadius:12,padding:"4px 12px",border:`1.5px solid ${darkMode?"rgba(255,255,255,0.1)":"#E5E7EB"}`}}>
              {[
                ["Gross Revenue", fmtC(Number(form.earnings||0)+wComp), "#E8962E"],
                ["Driver Base Pay", form.payMyselfAsDriver===false?"$0.00":fmtC(Number(form.driverBasePay||0)), "#E8962E"],
                ["Driver Wait Pay", form.payMyselfAsDriver===false?"$0.00":fmtC(wDrv), "#E8962E"],
                ["Company Wait Pay", fmtC(wComp), "#1C2B4A"],
                ["Net to Company", form.payMyselfAsDriver===false?fmtC(Number(form.earnings||0)+wComp):fmtC((Number(form.earnings||0)+wComp)-Number(form.driverBasePay||0)-wDrv), "#16A34A"]
              ].map(([l,v,c], idx, arr)=>(
                <div key={l} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:idx<arr.length-1?`1px solid ${darkMode?"rgba(255,255,255,0.08)":"#E5E7EB"}`:"none"}}>
                  <span style={{fontSize:14,fontWeight:700,color:LD.rowText}}>{l}</span>
                  <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:17,fontWeight:900,color:c}}>{v}</span>
                </div>
              ))}
            </div>
          )}

          {/* Driver earnings summary */}
          {!isOwner&&Number(form.driverBasePay||0)>0&&(
            <div style={{background:darkMode?"rgba(255,255,255,0.05)":"#F3F4F6",borderRadius:12,padding:"4px 12px",border:`1.5px solid ${darkMode?"rgba(255,255,255,0.1)":"#E5E7EB"}`}}>
              {[
                ["Your Pay", fmtC(Number(form.driverBasePay||0)), "#E8962E", null],
                ["Wait Pay", wDrv>0?fmtC(wDrv):(wm>0&&!(Number(rates.driverWaitRate)||0)?"Rate not set":fmtC(0)), wDrv>0?"#E8962E":"#9CA3AF", wm>0?`${wm} min recorded${!(Number(rates.driverWaitRate)||0)?" — ask owner to set wait rate in Settings":""}`:`${wm} min recorded`],
                ["Total Pay", fmtC(Number(form.driverBasePay||0)+wDrv), "#16A34A", null]
              ].map(([l,v,c,sub], idx, arr)=>(
                <div key={l} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:idx<arr.length-1?`1px solid ${darkMode?"rgba(255,255,255,0.08)":"#E5E7EB"}`:"none"}}>
                  <div>
                    <span style={{fontSize:14,fontWeight:700,color:LD.rowText}}>{l}</span>
                    {sub&&<div style={{fontSize:11,color:wm>0&&!(Number(rates.driverWaitRate)||0)&&l==="Wait Pay"?"#E8962E":"#6B7280",marginTop:1}}>{sub}</div>}
                  </div>
                  <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:17,fontWeight:900,color:c}}>{v}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── TRUCK ── */}
        <div style={{fontSize:13,fontWeight:800,color:"#E8962E",textTransform:"uppercase",letterSpacing:1.5,marginBottom:10,marginTop:4}}>🚛 Truck</div>
        <div style={{background:LD.cardBg,borderRadius:14,padding:"16px",marginBottom:14,border:`1.5px solid ${LD.cardBorder}`}}>
          {(activeTrucks||trucks).length > 0 ? (
            <div>
              <label style={ldLabel}>Select Truck</label>
              <select value={form.truckId||""} onChange={e=>setForm(f=>({...f,truckId:e.target.value,manualTruckNumber:""}))} className="ld-input" style={ldInput}>
                <option value="">— Select Truck —</option>
                {(activeTrucks||trucks).map(t=><option key={t.id} value={t.id}>Truck {t.truckNumber}{t.trailerNumber?` / Trailer ${t.trailerNumber}`:""}</option>)}
                <option value="__manual__">✏️ Enter manually…</option>
              </select>
              {form.truckId==="__manual__"&&(
                <input type="text" value={form.manualTruckNumber||""} onChange={e=>setForm(f=>({...f,manualTruckNumber:e.target.value}))} className="ld-input" style={{...ldInput,marginTop:8}} placeholder="e.g. TRK-217"/>
              )}
            </div>
          ) : (
            <div>
              <label style={ldLabel}>Truck Number</label>
              <input type="text" value={form.manualTruckNumber||""} onChange={e=>setForm(f=>({...f,manualTruckNumber:e.target.value,truckId:""}))} className="ld-input" style={ldInput} placeholder="e.g. TRK-217"/>
              <div style={{fontSize:11,color:LD.labelColor,marginTop:4}}>Add trucks in Settings → Trucks to enable quick selection</div>
            </div>
          )}
        </div>

        {/* ── WAIT TIME ── */}
        <div style={{fontSize:13,fontWeight:800,color:"#E8962E",textTransform:"uppercase",letterSpacing:1.5,marginBottom:10,marginTop:4}}>⏱ Wait Time</div>
        <div style={{background:LD.cardBg,borderRadius:14,padding:"16px",marginBottom:14,border:`1.5px solid ${LD.cardBorder}`}}>
          {/* Loading Wait */}
          <div style={{fontSize:12,fontWeight:800,color:C.blue,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>🏭 Loading Site</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            <div>
              <label style={ldLabel}>Arrival at Load</label>
              <input type="time" value={form.time||""} onChange={e=>setForm(f=>({...f,time:e.target.value}))} className="ld-input" style={{...ldInput,flex:1}}/>
              <button type="button" onClick={()=>{const now=new Date();const hh=String(now.getHours()).padStart(2,"0");const mm=String(now.getMinutes()).padStart(2,"0");setForm(f=>({...f,time:`${hh}:${mm}`}));}}
                style={{marginTop:6,width:"100%",padding:"9px 6px",borderRadius:8,border:"none",background:"linear-gradient(135deg,#1C2B4A,#243655)",color:"#fff",fontWeight:800,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>
                ▶ Push to Start
              </button>
            </div>
            <div>
              <label style={ldLabel}>Done Loading</label>
              <input type="time" value={form.completedTime||""} onChange={e=>{const val=e.target.value;setForm(f=>({...f,completedTime:val}));}} className="ld-input" style={{...ldInput,flex:1}}/>
              <button type="button" onClick={()=>{const now=new Date();const hh=String(now.getHours()).padStart(2,"0");const mm=String(now.getMinutes()).padStart(2,"0");setForm(f=>({...f,completedTime:`${hh}:${mm}`}));}}
                style={{marginTop:6,width:"100%",padding:"9px 6px",borderRadius:8,border:"none",background:"linear-gradient(135deg,#E8962E,#D4791A)",color:"#fff",fontWeight:800,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>
                ⏹ Push to Stop
              </button>
            </div>
          </div>
          {Number(form.loadWaitMins||0)>0&&(
            <div style={{background:"rgba(30,136,229,0.1)",border:"1px solid rgba(30,136,229,0.3)",borderRadius:8,padding:"8px 12px",display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
              <span style={{fontSize:12,fontWeight:700,color:C.blue}}>⏱ Load Wait</span>
              <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:16,fontWeight:900,color:C.blue}}>{Math.floor(Number(form.loadWaitMins)/60)>0?`${Math.floor(Number(form.loadWaitMins)/60)}h `:""}{Number(form.loadWaitMins)%60}min</span>
            </div>
          )}
          <div>
            <label style={{...ldLabel,fontSize:11}}>Manual override (min)</label>
            <input type="number" min="0" value={form.loadWaitMins||""} onChange={e=>setForm(f=>({...f,loadWaitMins:e.target.value}))} className="ld-input" style={{...ldInput,fontSize:13}} placeholder="or type minutes directly"/>
          </div>

          {/* Offloading Wait — with quick-tap Now buttons */}
          <div style={{borderTop:`1px solid ${LD.divider}`,paddingTop:12}}>
            <div style={{fontSize:12,fontWeight:800,color:"#E8962E",textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>🏗 Offloading Site</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
              <div>
                <label style={ldLabel}>Arrival at Offload</label>
                <div style={{display:"flex",gap:6,alignItems:"center"}}>
                  <input type="time" value={form.offloadArrivalTime||""} onChange={e=>{const val=e.target.value;const mins=calcWaitMins(val,form.offloadCompletedTime);setForm(f=>({...f,offloadArrivalTime:val,...(mins!==null?{offloadWaitMins:mins.toString()}:{})}))} } className="ld-input" style={{...ldInput,flex:1}}/>
                </div>
                <button type="button" onClick={()=>{const now=new Date();const hh=String(now.getHours()).padStart(2,"0");const mm=String(now.getMinutes()).padStart(2,"0");const val=`${hh}:${mm}`;const mins=calcWaitMins(val,form.offloadCompletedTime);setForm(f=>({...f,offloadArrivalTime:val,...(mins!==null?{offloadWaitMins:mins.toString()}:{})}));}}
                  style={{marginTop:6,width:"100%",padding:"9px 6px",borderRadius:8,border:"none",background:"linear-gradient(135deg,#1C2B4A,#243655)",color:"#fff",fontWeight:800,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>
                  ▶ Push to Start
                </button>
              </div>
              <div>
                <label style={ldLabel}>Done Offloading</label>
                <div style={{display:"flex",gap:6,alignItems:"center"}}>
                  <input type="time" value={form.offloadCompletedTime||""} onChange={e=>{const val=e.target.value;const mins=calcWaitMins(form.offloadArrivalTime,val);setForm(f=>({...f,offloadCompletedTime:val,...(mins!==null?{offloadWaitMins:mins.toString()}:{})}))} } className="ld-input" style={{...ldInput,flex:1}}/>
                </div>
                <button type="button" onClick={()=>{const now=new Date();const hh=String(now.getHours()).padStart(2,"0");const mm=String(now.getMinutes()).padStart(2,"0");const val=`${hh}:${mm}`;const mins=calcWaitMins(form.offloadArrivalTime,val);setForm(f=>({...f,offloadCompletedTime:val,...(mins!==null?{offloadWaitMins:mins.toString()}:{})}));}}
                  style={{marginTop:6,width:"100%",padding:"9px 6px",borderRadius:8,border:"none",background:"linear-gradient(135deg,#E8962E,#D4791A)",color:"#fff",fontWeight:800,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>
                  ⏹ Push to Stop
                </button>
              </div>
            </div>
            {/* Auto-calc display */}
            {(()=>{const mins=Number(form.offloadWaitMins||0);if(!mins)return null;const h=Math.floor(mins/60),m=mins%60;return(<div style={{background:"rgba(232,150,46,0.1)",border:"1px solid rgba(232,150,46,0.3)",borderRadius:8,padding:"8px 12px",display:"flex",justifyContent:"space-between",alignItems:"center"}}><span style={{fontSize:12,fontWeight:700,color:"#E8962E"}}>⏱ Offload Wait</span><span style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:16,fontWeight:900,color:"#E8962E"}}>{h>0?`${h}h `:""}{m}min</span></div>);})()}
            {/* Manual override */}
            <div style={{marginTop:10}}>
              <label style={{...ldLabel,fontSize:11}}>Manual override (min)</label>
              <input type="number" min="0" value={form.offloadWaitMins||""} onChange={e=>setForm(f=>({...f,offloadWaitMins:e.target.value}))} className="ld-input" style={{...ldInput,fontSize:13}} placeholder="or type minutes directly"/>
            </div>
          </div>
        </div>

        {/* ── Auto Profit Calculator ── HIDDEN */}
        {false && isOwner && form.earnings && (()=>{
          const rate = Number(form.earnings)||0;
          const fuel = Number(form.fuel||0);
          const drvPay = Number(form.driverBasePay||0);
          const waitComp = (()=>{
            const wm = (Number(form.loadWaitMins||0))+(Number(form.offloadWaitMins||0));
            return wm/60*(Number(rates.companyWaitRate)||0);
          })();
          const gross = rate + waitComp;
          const profit = gross - drvPay - fuel;
          const margin = gross > 0 ? ((profit/gross)*100).toFixed(0) : 0;
          return (
            <div style={{marginBottom:16,borderRadius:14,overflow:"hidden",border:"1.5px solid #22c55e30"}}>
              <div style={{background:"linear-gradient(135deg,#16a34a,#22c55e)",padding:"10px 16px",display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:18}}>💰</span>
                <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:15,color:"#fff",letterSpacing:0.5}}>AUTO PROFIT CALCULATION</span>
              </div>
              <div style={{background:"#F0FDF4",padding:"12px 16px"}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6,marginBottom:10}}>
                  <div style={{textAlign:"center",background:"#fff",borderRadius:10,padding:"8px 4px",border:"1px solid #dcfce7"}}>
                    <div style={{fontSize:11,color:"#16a34a",fontWeight:700,textTransform:"uppercase",letterSpacing:0.5}}>Rate</div>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:18,color:"#16a34a"}}>{fmtC(gross)}</div>
                  </div>
                  <div style={{textAlign:"center",background:"#fff",borderRadius:10,padding:"8px 4px",border:"1px solid #fee2e2"}}>
                    <div style={{fontSize:11,color:"#dc2626",fontWeight:700,textTransform:"uppercase",letterSpacing:0.5}}>Costs</div>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:18,color:"#dc2626"}}>{fmtC(drvPay+fuel)}</div>
                  </div>
                  <div style={{textAlign:"center",background:"#fff",borderRadius:10,padding:"8px 4px",border:`1px solid ${profit>=0?"#dcfce7":"#fee2e2"}`}}>
                    <div style={{fontSize:11,color:profit>=0?"#16a34a":"#dc2626",fontWeight:700,textTransform:"uppercase",letterSpacing:0.5}}>Profit</div>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:18,color:profit>=0?"#16a34a":"#dc2626"}}>{profit>=0?"+":""}{fmtC(profit)}</div>
                  </div>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 10px",background:"#fff",borderRadius:8,border:"1px solid #e2e8f0"}}>
                  <span style={{fontSize:12,color:"#64748b",fontWeight:600}}>Margin</span>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <div style={{width:80,height:6,borderRadius:3,background:"#e2e8f0",overflow:"hidden"}}>
                      <div style={{width:`${Math.min(100,Math.max(0,Number(margin)))}%`,height:"100%",background:profit>=0?"#22c55e":"#ef4444",borderRadius:3}}/>
                    </div>
                    <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:14,color:profit>=0?"#16a34a":"#dc2626"}}>{margin}%</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── AI DISPATCH SHEET ── */}
        <div style={{fontSize:13,fontWeight:800,color:"#E8962E",textTransform:"uppercase",letterSpacing:1.5,marginBottom:10,marginTop:4}}>📋 AI Dispatch Sheet</div>
        <div style={{background:LD.cardBg,borderRadius:14,padding:"16px",marginBottom:14,border:`1.5px solid ${LD.cardBorder}`}}>
          <div style={{fontSize:12,color:LD.labelColor,marginBottom:10}}>Scan or upload your dispatch sheet to auto-fill load details.</div>
          <input
            ref={loadScanRef}
            type="file"
            accept="image/*,application/pdf"
            style={{display:"none"}}
            onChange={scanLoadDocument}
          />
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <button
              type="button"
              onClick={()=>{if(loadScanRef.current){loadScanRef.current.removeAttribute("capture");loadScanRef.current.accept="image/*,application/pdf";loadScanRef.current.click();}}}
              style={{padding:"13px 10px",borderRadius:10,border:`1.5px solid ${LD.inputBorder}`,background:LD.inputBg,color:LD.inputColor,fontWeight:700,fontSize:13,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:6,fontFamily:"inherit"}}
              disabled={scanningLoad}
            >📎 Attach / Upload</button>
            <button
              type="button"
              onClick={()=>{if(loadScanRef.current){loadScanRef.current.setAttribute("capture","environment");loadScanRef.current.accept="image/*";loadScanRef.current.click();}}}
              style={{padding:"13px 10px",borderRadius:10,border:`1.5px solid ${LD.inputBorder}`,background:LD.inputBg,color:LD.inputColor,fontWeight:700,fontSize:13,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:6,fontFamily:"inherit"}}
              disabled={scanningLoad}
            >📷 Camera</button>
          </div>
          {scanningLoad&&<div style={{marginTop:10,textAlign:"center",fontSize:13,color:"#E8962E",fontWeight:700}}>🤖 Reading document…</div>}
          {scanLoadMsg&&!scanningLoad&&<div style={{marginTop:10,textAlign:"center",fontSize:13,color:scanLoadMsg.startsWith("✅")?"#16A34A":"#E8962E",fontWeight:700}}>{scanLoadMsg}</div>}
        </div>

        {/* Save / Cancel buttons */}
        <div style={{display:"flex",gap:10,marginTop:20,paddingBottom:28}}>
          <button style={{flex:1,padding:"14px",borderRadius:14,border:`1.5px solid ${LD.inputBorder}`,background:"transparent",color:LD.labelColor,fontWeight:700,fontSize:14,cursor:"pointer",fontFamily:"inherit"}} onClick={onCancel}>Cancel</button>
          <button onClick={(e)=>{tpRipple(e);submit();}}
            className="tp-ripple-wrap"
            style={{flex:2,padding:"16px",borderRadius:14,border:"none",cursor:"pointer",background:"#E8962E",color:"#0B1120",fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:900,letterSpacing:1,textTransform:"uppercase",boxShadow:"0 4px 14px rgba(232,150,46,0.35)",position:"relative",overflow:"hidden"}}>
            {editLoad?"💾 SAVE CHANGES":"✅ SAVE LOAD"}
          </button>
        </div>
      </div>
      )}

      {/* ═══════════════════════════════════════════════════ */}
      {/* ─── OFF LOADING TAB ─────────────────────────────── */}
      {/* ═══════════════════════════════════════════════════ */}
      {activeLoadSubTab==="offloading" && (
      <div className="slt-container-sm">

        {/* ── Pre-filled reference: TMW # + Route ── */}
        <div style={{fontSize:13,fontWeight:800,color:"#E8962E",textTransform:"uppercase",letterSpacing:1.5,marginBottom:10}}>📋 Load Reference</div>
        <div style={{background:LD.cardBg,borderRadius:14,padding:"14px 16px",marginBottom:14,border:`1.5px solid ${LD.cardBorder}`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${LD.divider}`}}>
            <span style={{fontSize:13,fontWeight:700,color:LD.labelColor}}>TMW #</span>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:18,color:"#1C2B4A"}}>{form.tmwLoadNumber||"—"}</span>
              {form.tmwLoadNumber&&<span style={{fontSize:10,fontWeight:800,color:"#16A34A",background:"rgba(34,197,94,0.1)",border:"1px solid rgba(34,197,94,0.3)",padding:"2px 7px",borderRadius:20,textTransform:"uppercase",letterSpacing:0.5}}>Auto ✓</span>}
            </div>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${LD.divider}`}}>
            <span style={{fontSize:13,fontWeight:700,color:LD.labelColor}}>Route</span>
            <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:14,color:LD.inputColor,textAlign:"right",maxWidth:"60%"}}>{form.location||"—"}</span>
          </div>
          {(()=>{const t=activeTrucks||trucks;const tk=t.find(x=>x.id===form.truckId);const tn=tk?`Truck ${tk.truckNumber}`:(form.manualTruckNumber||null);return tn?(<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0"}}><span style={{fontSize:13,fontWeight:700,color:LD.labelColor}}>Truck</span><span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:14,color:LD.inputColor}}>{tn}</span></div>):null;})()}
        </div>

        {/* ── Offload Date & Times ── */}
        <div style={{fontSize:13,fontWeight:800,color:"#E8962E",textTransform:"uppercase",letterSpacing:1.5,marginBottom:10}}>📅 Delivery Details</div>
        <div style={{background:LD.cardBg,borderRadius:14,padding:"16px",marginBottom:14,border:`1.5px solid ${LD.cardBorder}`}}>
          <div style={{marginBottom:14}}>
            <label style={ldLabel}>Date Offloaded</label>
            <input type="date" value={form.offloadDate||todayStr()} onChange={e=>setForm(f=>({...f,offloadDate:e.target.value}))} className="ld-input" style={ldInput}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:10}}>
            <div>
              <label style={ldLabel}>Arrival at Offload</label>
              <input type="time" value={form.offloadArrivalTime||""} onChange={e=>{const val=e.target.value;const mins=calcWaitMins(val,form.offloadCompletedTime);setForm(f=>({...f,offloadArrivalTime:val,...(mins!==null?{offloadWaitMins:mins.toString()}:{})}))} } className="ld-input" style={ldInput}/>
              <button type="button" onClick={()=>{const now=new Date();const hh=String(now.getHours()).padStart(2,"0");const mm=String(now.getMinutes()).padStart(2,"0");const val=`${hh}:${mm}`;const mins=calcWaitMins(val,form.offloadCompletedTime);setForm(f=>({...f,offloadArrivalTime:val,...(mins!==null?{offloadWaitMins:mins.toString()}:{})}));}} style={{marginTop:6,width:"100%",padding:"9px 6px",borderRadius:8,border:"none",background:"linear-gradient(135deg,#1C2B4A,#243655)",color:"#fff",fontWeight:800,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>▶ Push to Start</button>
            </div>
            <div>
              <label style={ldLabel}>Completed Time</label>
              <input type="time" value={form.offloadCompletedTime||""} onChange={e=>{const val=e.target.value;const mins=calcWaitMins(form.offloadArrivalTime,val);setForm(f=>({...f,offloadCompletedTime:val,...(mins!==null?{offloadWaitMins:mins.toString()}:{})}))} } className="ld-input" style={ldInput}/>
              <button type="button" onClick={()=>{const now=new Date();const hh=String(now.getHours()).padStart(2,"0");const mm=String(now.getMinutes()).padStart(2,"0");const val=`${hh}:${mm}`;const mins=calcWaitMins(form.offloadArrivalTime,val);setForm(f=>({...f,offloadCompletedTime:val,...(mins!==null?{offloadWaitMins:mins.toString()}:{})}));}} style={{marginTop:6,width:"100%",padding:"9px 6px",borderRadius:8,border:"none",background:"linear-gradient(135deg,#E8962E,#D4791A)",color:"#fff",fontWeight:800,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>⏹ Push to Stop</button>
            </div>
          </div>
          {(()=>{const mins=Number(form.offloadWaitMins||0);if(!mins)return null;const h=Math.floor(mins/60),m=mins%60;return(<div style={{background:"rgba(232,150,46,0.1)",border:"1px solid rgba(232,150,46,0.3)",borderRadius:8,padding:"9px 12px",display:"flex",justifyContent:"space-between",alignItems:"center"}}><span style={{fontSize:12,fontWeight:700,color:"#E8962E"}}>⏱ Offload Wait Calculated</span><span style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:900,color:"#E8962E"}}>{h>0?`${h}h `:""}{m}min</span></div>);})()}
        </div>

        {/* ── Offload Document Scan ── */}
        <div style={{fontSize:13,fontWeight:800,color:"#E8962E",textTransform:"uppercase",letterSpacing:1.5,marginBottom:10}}>📄 Offload Document</div>
        <input ref={offloadScanRef} type="file" accept="image/*" style={{display:"none"}} onChange={scanOffloadDocument}/>
        {offloadDocPreview ? (
          <div style={{marginBottom:14,borderRadius:14,overflow:"hidden",border:`1.5px solid ${LD.cardBorder}`,position:"relative"}}>
            <img src={offloadDocPreview} alt="Offload doc" style={{width:"100%",objectFit:"cover",maxHeight:160,display:"block"}}/>
            {offloadScanning&&<div style={{position:"absolute",inset:0,background:"rgba(28,43,74,0.88)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:8}}><div style={{fontSize:28}}>🤖</div><div style={{color:"#fff",fontWeight:700,fontSize:13}}>Reading offload document…</div></div>}
            <button type="button" onClick={()=>{setOffloadDocPreview("");setForm(f=>({...f,offloadDocImage:""}));}} style={{position:"absolute",top:8,right:8,background:"rgba(0,0,0,0.55)",border:"none",borderRadius:"50%",width:30,height:30,color:"#fff",fontSize:16,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
            {offloadScanMsg&&!offloadScanning&&<div style={{position:"absolute",bottom:0,left:0,right:0,padding:"8px 12px",background:offloadScanMsg.startsWith("✅")?"rgba(22,163,74,0.92)":"rgba(232,150,46,0.92)",color:"#fff",fontWeight:700,fontSize:12,textAlign:"center"}}>{offloadScanMsg}</div>}
          </div>
        ) : (
          <div style={{border:`2px dashed ${LD.inputBorder}`,borderRadius:14,padding:"18px 14px",textAlign:"center",background:LD.inputBg,marginBottom:14}}>
            <div style={{fontSize:28,marginBottom:6}}>📋</div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:14,color:LD.inputColor,marginBottom:2}}>Upload Offload Dispatch Sheet</div>
            <div style={{fontSize:12,color:LD.labelColor,marginBottom:12}}>AI reads arrival time, completed time & confirms TMW #</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <button type="button" onClick={()=>{if(offloadScanRef.current){offloadScanRef.current.removeAttribute("capture");offloadScanRef.current.accept="image/*,application/pdf";offloadScanRef.current.click();}}} style={{padding:"12px 8px",borderRadius:10,border:`1.5px solid ${LD.inputBorder}`,background:LD.inputBg,color:LD.inputColor,fontWeight:700,fontSize:13,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:6,fontFamily:"inherit"}} disabled={offloadScanning}>📎 Attach / Upload</button>
              <button type="button" onClick={()=>{if(offloadScanRef.current){offloadScanRef.current.setAttribute("capture","environment");offloadScanRef.current.accept="image/*";offloadScanRef.current.click();}}} style={{padding:"12px 8px",borderRadius:10,border:`1.5px solid ${LD.inputBorder}`,background:LD.inputBg,color:LD.inputColor,fontWeight:700,fontSize:13,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:6,fontFamily:"inherit"}} disabled={offloadScanning}>📷 Camera</button>
            </div>
            {offloadScanning&&<div style={{marginTop:10,fontSize:13,color:"#E8962E",fontWeight:700}}>🤖 Reading document…</div>}
            {offloadScanMsg&&!offloadScanning&&<div style={{marginTop:10,fontSize:13,color:offloadScanMsg.startsWith("✅")?"#16A34A":"#E8962E",fontWeight:700}}>{offloadScanMsg}</div>}
          </div>
        )}

        {/* ── Offload Note ── */}
        <div style={{fontSize:13,fontWeight:800,color:"#E8962E",textTransform:"uppercase",letterSpacing:1.5,marginBottom:10}}>📝 Offload Note</div>
        <div style={{background:LD.cardBg,borderRadius:14,padding:"14px 16px",marginBottom:20,border:`1.5px solid ${LD.cardBorder}`}}>
          <textarea
            value={form.offloadNote||""}
            onChange={e=>setForm(f=>({...f,offloadNote:e.target.value}))}
            placeholder="e.g. Offloaded at Heartland dock 3 — ticket #HFS-2892. Signed by J. Smith. No issues."
            style={{width:"100%",padding:"10px 12px",borderRadius:10,border:`1.5px solid ${LD.inputBorder}`,background:LD.inputBg,color:LD.inputColor,fontSize:14,fontFamily:"'Barlow',sans-serif",resize:"none",minHeight:80,outline:"none",boxSizing:"border-box"}}
          />
        </div>

        {/* Save / Cancel buttons (same as Loading tab) */}
        <div style={{display:"flex",gap:10,marginBottom:28,paddingBottom:28}}>
          <button style={{flex:1,padding:"14px",borderRadius:14,border:`1.5px solid ${LD.inputBorder}`,background:"transparent",color:LD.labelColor,fontWeight:700,fontSize:14,cursor:"pointer",fontFamily:"inherit"}} onClick={onCancel}>Cancel</button>
          <button onClick={(e)=>{tpRipple(e);submit();}} className="tp-ripple-wrap" style={{flex:2,padding:"16px",borderRadius:14,border:"none",cursor:"pointer",background:"#E8962E",color:"#0B1120",fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:900,letterSpacing:1,textTransform:"uppercase",boxShadow:"0 4px 14px rgba(232,150,46,0.35)",position:"relative",overflow:"hidden"}}>
            {editLoad?"💾 SAVE CHANGES":"✅ SAVE LOAD"}
          </button>
        </div>
      </div>
      )}

    </div>
  );
}

// ─── LOAD DETAIL MODAL ────────────────────────────────────────────────────────
function LoadDetailModal({ load, onClose, rates, isOwner, trucks, session, allDrivers=[], darkMode=false, onToggleComplete, onGenerateInvoice, onAddNote, onSummary, onViewPhotos, onUpdateLoad }) {
  const DM = {
    bg: darkMode ? "#0b1a2a" : "#fff",
    cardBg: darkMode ? "#112233" : "#fff",
    sectionBg: darkMode ? "#0d1e30" : "#F0F7FF",
    headerBg: darkMode ? "#112233" : "#fff",
    border: darkMode ? "rgba(255,255,255,0.08)" : "#e5e7eb",
    text: darkMode ? "#eaeaea" : "#1A1A1A",
    textMed: darkMode ? "#a0a0a0" : "#4B5563",
    textMut: darkMode ? "#6a7a8a" : "#9CA3AF",
    blueBg: darkMode ? "rgba(28,43,74,0.5)" : "#EFF6FF",
    blueText: darkMode ? "#60A5FA" : "#1565C0",
    greenBg: darkMode ? "rgba(22,163,74,0.15)" : "#F0FDF4",
    greenText: darkMode ? "#86efac" : "#16A34A",
    timeCellBg: darkMode ? "#0f1e2e" : "#F5F6F8",
    timeCellBgFilled: darkMode ? "#0f2137" : "#fff",
    offWhite: darkMode ? "#0d1e30" : "#F5F6F8",
    amberBg: darkMode ? "rgba(255,179,0,0.10)" : "#F5F6F8",
    expenseBg: darkMode ? "#0f1e30" : "#F5F6F8",
    payStatusBg: darkMode ? "rgba(22,163,74,0.12)" : "#F0FFF4",
    payStatusBorder: darkMode ? "rgba(22,163,74,0.3)" : "#BBF7D0",
    footerBg: darkMode ? "#0b1a2a" : "#fff",
    attachBorder: darkMode ? "rgba(255,255,255,0.12)" : "#E2E2E2",
    pdfThumb: darkMode ? "#0f2137" : "#F5F6F8",
  };
  const [note,setNote]=useState("");
  const [showPaySheet,setShowPaySheet]=useState(false);
  const [payMethod,setPayMethod]=useState("eft");
  const [payDate,setPayDate]=useState(()=>new Date().toISOString().slice(0,10));
  const [showDriverPaySheet,setShowDriverPaySheet]=useState(false);
  const [driverPayMethod,setDriverPayMethod]=useState("eft");
  const [driverPayDate,setDriverPayDate]=useState(()=>new Date().toISOString().slice(0,10));
  const [showOwnerStub,setShowOwnerStub]=useState(false);
  const [showDriverStub,setShowDriverStub]=useState(false);
  useEffect(()=>{
    const y=window.scrollY;
    document.body.style.position="fixed";
    document.body.style.top=`-${y}px`;
    document.body.style.width="100%";
    document.body.style.overflow="hidden";
    return ()=>{
      document.body.style.position="";
      document.body.style.top="";
      document.body.style.width="";
      document.body.style.overflow="";
      window.scrollTo(0,y);
    };
  },[]);
  const endRef=useRef(null);
  useEffect(()=>endRef.current?.scrollIntoView({behavior:"smooth"}),[load.messages?.length]);
  const wm=(Number(load.loadWaitMins)||0)+(Number(load.offloadWaitMins)||0);
  const fmt=(m)=>{const h=Math.floor(m/60),mn=m%60;return `${h}h ${mn}m`;};
  const wComp=parseFloat(((wm/60)*(Number(rates.companyWaitRate)||0)).toFixed(2));
  const wDrv=parseFloat(((wm/60)*(Number(rates.driverWaitRate)||0)).toFixed(2));
  const gross=parseFloat(((Number(load.earnings)||0)+wComp).toFixed(2));
  const dPay=parseFloat(((Number(load.driverBasePay)||0)+wDrv).toFixed(2));
  const net=parseFloat((gross-dPay).toFixed(2));
  const handleSend=()=>{if(!note.trim())return;onAddNote(load.id,note.trim(),session);setNote("");};
  return (
    <div
      onClick={onClose}
      style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:9100,display:"flex",alignItems:"flex-end",justifyContent:"center",touchAction:"none"}}
    >
      <div
        onClick={e=>e.stopPropagation()}
        style={{
          background: DM.bg,
          borderRadius:"18px 18px 0 0",
          width:"100%",maxWidth:600,
          height:"100dvh",
          display:"flex",flexDirection:"column",
          boxShadow:"0 -8px 40px rgba(0,0,0,0.55)",
          overflow:"hidden",
        }}
      >
        {/* ── Sticky header ── */}
        <div style={{flexShrink:0,padding:"14px 20px",borderBottom:`1px solid ${DM.border}`,display:"flex",justifyContent:"space-between",alignItems:"center",background:DM.headerBg,zIndex:1}}>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:17,color:DM.text}}>📦 Load Details</div>
          <button className="slt-btn-ghost" style={{padding:"6px 12px",fontSize:13}} onClick={onClose}>✕</button>
        </div>

        {/* ── Scrollable body ── */}
        <div style={{flex:1,overflowY:"auto",WebkitOverflowScrolling:"touch",overscrollBehavior:"contain",padding:"16px 20px 8px",background:DM.bg}}>
          <div style={{background:DM.blueBg,borderRadius:11,padding:14,marginBottom:18}}>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:16,color:DM.blueText}}>{load.location}</div>
            {load.driverFullName&&<div style={{fontSize:12.5,color:DM.textMed,marginTop:2}}>Driver: {load.driverFullName}</div>}
          </div>

          {/* Times row */}
          {(()=>{
            const fmt12=(t)=>{ if(!t)return null; const [h,m]=t.split(":").map(Number); const ampm=h>=12?"PM":"AM"; return `${h%12||12}:${String(m).padStart(2,"0")} ${ampm}`; };
            const fmtW=(mins)=>{ const h=Math.floor(mins/60); const m=mins%60; return `${h>0?h+"h ":""}${m}min`; };
            const lwm=Number(load.loadWaitMins)||0;
            const owm=Number(load.offloadWaitMins)||0;
            return (
              <div style={{background:DM.sectionBg,borderRadius:11,padding:"14px 16px",marginBottom:14}}>
                {/* Loading Site */}
                <div style={{fontSize:12,fontWeight:800,color:C.blue,textTransform:"uppercase",letterSpacing:0.8,marginBottom:10}}>🏭 Loading Site</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6,textAlign:"center",marginBottom:8}}>
                  {[
                    ["📅 Appt", fmt12(load.appointmentTime), C.blue],
                    ["🛬 Arrival", fmt12(load.time), C.green],
                    ["✅ Done", fmt12(load.completedTime), C.orange],
                  ].map(([label,val,col],i)=>(
                    <div key={label} style={{background:val?DM.timeCellBgFilled:DM.timeCellBg,borderRadius:8,padding:"8px 4px",border:val?`1.5px solid ${col}30`:`1px solid ${DM.border}`}}>
                      <div style={{fontSize:12,color:DM.textMed,fontWeight:700,marginBottom:3,textTransform:"uppercase",letterSpacing:0.5}}>{label}</div>
                      <div style={{fontSize:13,fontWeight:800,color:val?col:DM.textMut}}>{val||"—"}</div>
                    </div>
                  ))}
                </div>
                {lwm>0&&<div style={{background:DM.timeCellBg,borderRadius:7,padding:"6px 12px",fontSize:12,fontWeight:700,color:DM.blueText,marginBottom:10,display:"flex",justifyContent:"space-between"}}>
                  <span>⏱ Load Wait</span><span>{fmtW(lwm)}</span>
                </div>}

                {/* Offloading Site */}
                {(load.offloadArrivalTime||load.offloadCompletedTime)&&(
                  <>
                    <div style={{fontSize:12,fontWeight:800,color:C.orange,textTransform:"uppercase",letterSpacing:0.8,marginBottom:10,borderTop:`1px solid ${DM.border}`,paddingTop:10}}>🏗 Offloading Site</div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,textAlign:"center",marginBottom:8}}>
                      {[
                        ["🛬 Arrival", fmt12(load.offloadArrivalTime), C.green],
                        ["✅ Done", fmt12(load.offloadCompletedTime), C.orange],
                      ].map(([label,val,col])=>(
                        <div key={label} style={{background:val?DM.timeCellBgFilled:DM.timeCellBg,borderRadius:8,padding:"8px 4px",border:val?`1.5px solid ${col}30`:`1px solid ${DM.border}`}}>
                          <div style={{fontSize:12,color:C.textDarkMed,fontWeight:700,marginBottom:3,textTransform:"uppercase",letterSpacing:0.5}}>{label}</div>
                          <div style={{fontSize:13,fontWeight:800,color:val?col:C.textLight}}>{val||"—"}</div>
                        </div>
                      ))}
                    </div>
                    {owm>0&&<div style={{background:DM.amberBg,borderRadius:7,padding:"6px 12px",fontSize:12,fontWeight:700,color:C.orange,display:"flex",justifyContent:"space-between"}}>
                      <span>⏱ Offload Wait</span><span>{fmtW(owm)}</span>
                    </div>}
                  </>
                )}
              </div>
            );
          })()}
          {[["Date",load.date],["TMW #",load.tmwLoadNumber||"—"],["Wait",wm>0?fmt(wm):"—"],["Note",load.note||"—"]].map(([l,v])=>(
            <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:`1px solid ${DM.border}`}}>
              <span style={{fontSize:13,color:DM.textMed}}>{l}</span>
              <span style={{fontSize:13,fontWeight:700,color:DM.text}}>{v}</span>
            </div>
          ))}

          {isOwner&&(
            <div style={{background:DM.offWhite,borderRadius:11,padding:14,marginTop:16}}>
              <div style={{fontSize:13,fontWeight:800,color:DM.textMed,letterSpacing:1.5,textTransform:"uppercase",marginBottom:10,fontFamily:"'Barlow',sans-serif"}}>Financials</div>
              {/* Revenue */}
              {[["Earnings",fmtC(load.earnings||0),DM.text],["Wait Co.",fmtC(wComp),C.orange],["Gross",fmtC(gross),C.green]].map(([l,v,color])=>(
                <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${DM.border}`}}>
                  <span style={{fontSize:12.5,color:DM.textMed}}>{l}</span>
                  <span style={{fontSize:13.5,fontWeight:800,color,fontFamily:"'Barlow Condensed',sans-serif"}}>{v}</span>
                </div>
              ))}
              {/* Driver Pay breakdown */}
              {Number(load.driverBasePay||0) > 0 && (<>
                <div style={{fontSize:13,fontWeight:800,color:DM.textMed,letterSpacing:1,textTransform:"uppercase",marginTop:10,marginBottom:6}}>Driver Pay</div>
                {[
                  ["Route Pay", fmtC(Number(load.driverBasePay||0)), C.blue],
                  ...(wDrv > 0 ? [["Wait Pay", fmtC(wDrv), C.orange]] : []),
                  ["Total Driver Pay", fmtC(dPay), C.blue],
                ].map(([l,v,color])=>(
                  <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${DM.border}`,fontWeight:l==="Total Driver Pay"?800:400}}>
                    <span style={{fontSize:12.5,color:l==="Total Driver Pay"?DM.text:DM.textMed}}>{l}</span>
                    <span style={{fontSize:13.5,fontWeight:800,color,fontFamily:"'Barlow Condensed',sans-serif"}}>-{v}</span>
                  </div>
                ))}
              </>)}
              {/* Net */}
              <div style={{display:"flex",justifyContent:"space-between",padding:"8px 0",marginTop:4,borderTop:`2px solid ${DM.border}`}}>
                <span style={{fontSize:13,fontWeight:800,color:DM.text}}>Net</span>
                <span style={{fontSize:15,fontWeight:900,color:net>=0?C.green:C.red,fontFamily:"'Barlow Condensed',sans-serif"}}>{fmtC(net)}</span>
              </div>
            </div>
          )}

          {!isOwner&&(load.assignedDriverUid===session.uid||load.addedBy===session.uid||load.user_id===session.uid)&&(
            <div style={{background:DM.greenBg,borderRadius:11,padding:14,marginTop:16,border:`1.5px solid ${DM.greenText}`}}>
              <div style={{fontSize:13,fontWeight:800,color:C.green,letterSpacing:1.5,textTransform:"uppercase",marginBottom:10}}>💵 Your Pay</div>
              {[
                ["Driver's Base Pay",fmtC(Number(load.driverBasePay)||0),C.blue,null],
                ["Wait Pay", wDrv>0 ? fmtC(wDrv) : (wm>0&&!(Number(rates.driverWaitRate)||0) ? "Rate not set" : fmtC(0)),
                  wDrv>0?"#E8962E":"#9CA3AF",
                  wm>0 ? `${wm} min recorded${!(Number(rates.driverWaitRate)||0)?" — wait rate not configured by owner":""}` : null],
                ["Total Pay",fmtC(dPay),C.green,null]
              ].map(([l,v,color,sub])=>(
                <div key={l} style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",padding:"6px 0",borderBottom:`1px solid ${DM.border}`}}>
                  <div>
                    <span style={{fontSize:12.5,color:DM.textMed}}>{l}</span>
                    {sub&&<div style={{fontSize:11,color:wm>0&&!(Number(rates.driverWaitRate)||0)&&l==="Wait Pay"?"#E8962E":"#9CA3AF",marginTop:1}}>{sub}</div>}
                  </div>
                  <span style={{fontSize:13.5,fontWeight:800,color,fontFamily:"'Barlow Condensed',sans-serif"}}>{v}</span>
                </div>
              ))}
            </div>
          )}

          {/* Same-day Expenses Box */}
          {(()=>{
            const dayExp = getStored(expensesKey(session.uid)).filter(e => e.date === load.date && e.source !== "load");
            if(dayExp.length === 0) return null;
            const dayTotal = dayExp.reduce((s,e)=>s+Number(e.amount||0),0);
            return (
              <div style={{marginTop:16,background:DM.expenseBg,borderRadius:11,padding:14,border:"1.5px solid #FFB300"}}>
                <div style={{fontSize:13,fontWeight:800,color:"#E8962E",letterSpacing:1.5,textTransform:"uppercase",marginBottom:10}}>
                  🧾 Expenses on {load.date}
                </div>
                {dayExp.map(e=>(
                  <div key={e.id} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:`1px solid ${DM.border}`,fontSize:13}}>
                    <span style={{color:DM.textMed}}>{e.category==="fuel"?"⛽":e.category==="meals"?"🍽":e.category==="tolls"?"🛣":"🧾"} {e.description||e.note||e.category}</span>
                    <span style={{fontWeight:800,color:"#E8962E"}}>{fmtC(Number(e.amount||0))}</span>
                  </div>
                ))}
                <div style={{display:"flex",justifyContent:"space-between",marginTop:8,paddingTop:8,borderTop:"2px solid #FFB300"}}>
                  <span style={{fontWeight:800,fontSize:13,color:"#E8962E"}}>Total expenses this day</span>
                  <span style={{fontWeight:900,fontSize:15,color:"#E8962E",fontFamily:"'Barlow Condensed',sans-serif"}}>{fmtC(dayTotal)}</span>
                </div>
                <div style={{fontSize:13,color:"#8D6E63",marginTop:6}}>These are shown for reference only — not deducted from load earnings</div>
              </div>
            );
          })()}

          {/* ── Attached Photos / Dispatch Sheets ── */}
          {load.photos && load.photos.length > 0 && (
            <div style={{marginTop:16}}>
              <div style={{fontWeight:800,fontSize:13,color:DM.textMed,textTransform:"uppercase",letterSpacing:1.2,marginBottom:8}}>📎 Attachments ({load.photos.length})</div>
              <div style={{display:"flex",gap:8,overflowX:"auto",WebkitOverflowScrolling:"touch",paddingBottom:4}}>
                {load.photos.map((p,i)=>{const pUrl=typeof p==="string"?p:(p?.url||"");return(
                  <a key={i} href={pUrl} target="_blank" rel="noopener noreferrer" style={{flexShrink:0,display:"block",borderRadius:10,overflow:"hidden",border:`1.5px solid ${DM.attachBorder}`}}>
                    {pUrl.toLowerCase().endsWith(".pdf") ? (
                      <div style={{width:90,height:90,background:DM.pdfThumb,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:4}}>
                        <span style={{fontSize:28}}>📄</span>
                        <span style={{fontSize:9,fontWeight:700,color:"#E8962E"}}>PDF</span>
                      </div>
                    ) : (
                      <img src={pUrl} alt={`Attachment ${i+1}`} style={{width:90,height:90,objectFit:"cover",display:"block"}}/>
                    )}
                  </a>
                );})}
              </div>
            </div>
          )}

          {/* Notes - READ ONLY. Add notes via Edit Load only. Only load creator can add notes. */}
          {load.messages && load.messages.length > 0 && (
            <div style={{marginTop:20}}>
              <div style={{fontWeight:800,fontSize:14,marginBottom:10,fontFamily:"'Barlow Condensed',sans-serif"}}>💬 Load Notes</div>
              {load.messages.map((m,i)=>{
                const isMe=m.authorUid===session.uid;
                return(
                  <div key={i} style={{display:"flex",justifyContent:isMe?"flex-end":"flex-start",marginBottom:8}}>
                    <div style={{maxWidth:"75%"}}>
                      <div style={{fontSize:12.5,color:DM.textMut,marginBottom:3,textAlign:isMe?"right":"left"}}>{m.authorName} · {m.timestamp?.slice(0,10)}</div>
                      <div className={isMe?"slt-bubble-me":"slt-bubble-other"}>{m.text}</div>
                    </div>
                  </div>
                );
              })}
              <div ref={endRef}/>
            </div>
          )}
        </div>{/* end scrollable body */}

        {/* ── Payment status bar ── */}
        {(load.contractorPaid || load.driverPaid || load.driverReceived || load.payment_status === "paid") && (
          <div style={{flexShrink:0,padding:"8px 20px",background:DM.payStatusBg,borderTop:`1px solid ${DM.payStatusBorder}`,display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
            {isOwner && load.contractorPaid && (
              <span style={{fontSize:11,fontWeight:800,color:"#059669",background:"#D1FAE5",padding:"3px 10px",borderRadius:20}}>
                ✅ Contractor Paid {load.contractorPaidDate||""}{load.contractorPaidMethod?` · ${load.contractorPaidMethod.toUpperCase()}`:""}
              </span>
            )}

            {!isOwner && load.completed && (Number(load.driverBasePay||0) > 0 || Number(load.earnings||0) > 0) && !load.driverReceived && (
              <span style={{fontSize:11,fontWeight:800,color:"#DC2626",background:"#FEE2E2",padding:"3px 10px",borderRadius:20}}>
                ⏳ Payment Pending
              </span>
            )}
            {!isOwner && load.driverReceived && (
              <span style={{fontSize:11,fontWeight:800,color:"#059669",background:"#D1FAE5",padding:"3px 10px",borderRadius:20}}>
                ✅ Pay Received {load.driverReceivedDate||""}
              </span>
            )}
          </div>
        )}

        {/* ── Sticky footer buttons ── */}
        <div style={{flexShrink:0,padding:"10px 16px",borderTop:`1px solid ${DM.border}`,background:DM.footerBg,display:"flex",gap:8,flexWrap:"wrap",paddingBottom:"calc(10px + env(safe-area-inset-bottom, 0px))"}}>

          {/* ── OWNER buttons ── */}
          {(()=>{
            // Detect if assigned driver is a subcontractor corp
            const assignedDriver = allDrivers.find(d => d.uid === load.assignedDriverUid);
            const isSubcontractorDriver = assignedDriver?.driverType === "subcontractor";
            // Detect if owner is driving themselves (no external driver, or driver is owner themselves)
            const ownerDrivingSelf = isOwner && (!load.driverFullName || load.assignedDriverUid === session.uid);
            return (<>
              {/* Mark Contractor Paid */}
              {isOwner && !load.contractorPaid && onUpdateLoad && (
                <button style={{flex:"1 1 40%",padding:"11px 6px",borderRadius:12,background:"#059669",color:"#fff",fontWeight:800,fontSize:12,border:"none",cursor:"pointer"}}
                  onClick={()=>setShowPaySheet(true)}>💰 Mark Contractor Paid</button>
              )}
              {/* Owner driving themselves — after contractor paid, mark corp deposited */}
              {isOwner && load.contractorPaid && ownerDrivingSelf && !load.corpDeposited && onUpdateLoad && (
                <button style={{flex:"1 1 40%",padding:"11px 6px",borderRadius:12,background:"#1C2B4A",color:"#fff",fontWeight:800,fontSize:12,border:"none",cursor:"pointer"}}
                  onClick={()=>onUpdateLoad(load.id,{corpDeposited:true,corpDepositedDate:new Date().toISOString().slice(0,10)})}>
                  🏢 Mark Corp Deposited</button>
              )}
              {isOwner && load.contractorPaid && ownerDrivingSelf && load.corpDeposited && (
                <div style={{flex:"1 1 40%",padding:"11px 6px",borderRadius:12,background:"#D1FAE5",color:"#059669",fontWeight:800,fontSize:12,textAlign:"center"}}>
                  ✅ Corp Deposited {load.corpDepositedDate||""}
                </div>
              )}
              {/* Pay driver / Pay subcontractor invoice */}
              {false && isOwner && load.contractorPaid && !load.driverPaid && load.driverFullName && !ownerDrivingSelf && onUpdateLoad && (
                <button style={{flex:"1 1 40%",padding:"11px 6px",borderRadius:12,background:"#1C2B4A",color:"#fff",fontWeight:800,fontSize:12,border:"none",cursor:"pointer"}}
                  onClick={()=>setShowDriverPaySheet(true)}>
                  {isSubcontractorDriver ? "📋 Pay Contractor Invoice" : "💳 Pay Driver"}
                </button>
              )}
              {/* Payment stub */}
              {isOwner && load.contractorPaid && (
                <button style={{flex:"1 1 40%",padding:"11px 6px",borderRadius:12,background:"#D1FAE5",color:"#059669",fontWeight:800,fontSize:12,border:"none",cursor:"pointer"}}
                  onClick={()=>setShowOwnerStub(true)}>🧾 Payment Stub</button>
              )}
              {isOwner && (
                <button className="slt-btn-primary slt-btn-dark-text" style={{flex:"1 1 40%",background:`linear-gradient(135deg,${C.purple},#E8962E)`}} onClick={()=>onGenerateInvoice(load)}>📄 Invoice</button>
              )}
            </>);
          })()}

          {/* ── DRIVER buttons ── */}
          {(()=>{
            const isSubcontractorSelf = session.driverType === "subcontractor";
            // Driver can pay themselves if load is completed and has any earnings (driverBasePay OR gross earnings)
            const driverHasPay = Number(load.driverBasePay||0) > 0 || Number(load.earnings||0) > 0;
            // The amount the driver should receive (fall back to earnings if no driverBasePay set)
            const selfPayAmt = Number(load.driverBasePay||0) > 0 ? Number(load.driverBasePay) : Number(load.earnings||0);
            return (<>
              {!isOwner && load.completed && driverHasPay && !load.driverReceived && onUpdateLoad && (
                <button style={{flex:"1 1 40%",padding:"11px 6px",borderRadius:12,background:"#E8962E",color:"#fff",fontWeight:800,fontSize:12,border:"none",cursor:"pointer"}}
                  onClick={()=>{ onUpdateLoad(load.id,{driverReceived:true,driverReceivedDate:new Date().toISOString().slice(0,10),driverPaidAmount:selfPayAmt}); }}>
                  {isSubcontractorSelf ? "✅ Mark Invoice Paid" : "✅ Pay Myself"}
                </button>
              )}
              {!isOwner && load.driverReceived && onGenerateInvoice && (
                <button style={{flex:"1 1 40%",padding:"11px 6px",borderRadius:12,background:`linear-gradient(135deg,${C.purple},#E8962E)`,color:"#fff",fontWeight:800,fontSize:12,border:"none",cursor:"pointer"}}
                  onClick={()=>onGenerateInvoice(load)}>
                  📄 Invoice Receipt
                </button>
              )}
              {!isOwner && load.driverReceived && (
                <button style={{flex:"1 1 40%",padding:"11px 6px",borderRadius:12,background:"#D1FAE5",color:"#059669",fontWeight:800,fontSize:12,border:"none",cursor:"pointer"}}
                  onClick={()=>setShowDriverStub(true)}>
                  🧾 My Pay Stub
                </button>
              )}
            </>);
          })()}

          {/* ── Shared ── */}
          {onViewPhotos&&<button className="slt-btn-primary slt-btn-dark-text" style={{flex:"1 1 40%",background:"#166534"}} onClick={()=>onViewPhotos(load)}>📷 Photos {load.photos?.length>0?`(${load.photos.length})`:""}</button>}
          <button className="slt-btn-ghost" style={{flex:"1 1 40%",padding:"12px"}} onClick={onClose}>Close</button>
        </div>

        {/* ── SHEET: Mark Paid by Contractor ── */}
        {showPaySheet && (
          <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"flex-end",zIndex:10}} onClick={()=>setShowPaySheet(false)}>
            <div style={{background:darkMode?"#0D1523":"#fff",borderRadius:"20px 20px 0 0",width:"100%",padding:"20px 18px 32px"}} onClick={e=>e.stopPropagation()}>
              <div style={{width:36,height:4,background:"#E2E2E2",borderRadius:2,margin:"0 auto 16px"}}/>
              <div style={{fontWeight:900,fontSize:17,marginBottom:4}}>💰 Contractor Payment Received</div>
              <div style={{fontSize:12,color:"#6B7280",marginBottom:4}}>{load.location||"Load"}</div>
              <div style={{fontSize:14,fontWeight:800,color:"#059669",marginBottom:16}}>Total: ${((Number(load.earnings)||0)+parseFloat(((((Number(load.loadWaitMins)||0)+(Number(load.offloadWaitMins)||0))/60)*(Number(rates.companyWaitRate)||0)).toFixed(2))).toFixed(2)}</div>
              <div style={{fontSize:11,fontWeight:800,color:"#4B5563",textTransform:"uppercase",letterSpacing:.5,marginBottom:5}}>Date Received</div>
              <input type="date" value={payDate} onChange={e=>setPayDate(e.target.value)}
                style={{width:"100%",padding:"11px 13px",borderRadius:10,border:"1.5px solid #E2E2E2",fontSize:14,fontWeight:700,marginBottom:12,outline:"none"}}/>
              <div style={{fontSize:11,fontWeight:800,color:"#4B5563",textTransform:"uppercase",letterSpacing:.5,marginBottom:8}}>Payment Method</div>
              <div style={{display:"flex",gap:8,marginBottom:16}}>
                {["eft","cheque","cash"].map(m=>(
                  <button key={m} onClick={()=>setPayMethod(m)} style={{flex:1,padding:"9px",borderRadius:10,border:`2px solid ${payMethod===m?"#E8962E":"#E2E2E2"}`,background:payMethod===m?"#FFF3EB":"#fff",color:payMethod===m?"#E8962E":"#6B7280",fontWeight:800,fontSize:12,cursor:"pointer",textTransform:"uppercase"}}>
                    {m==="eft"?"🏦 EFT":m==="cheque"?"💳 Cheque":"💵 Cash"}
                  </button>
                ))}
              </div>
              <button style={{width:"100%",padding:"14px",borderRadius:14,background:"#059669",color:"#fff",fontWeight:900,fontSize:15,border:"none",cursor:"pointer"}}
                onClick={()=>{ onUpdateLoad(load.id,{contractorPaid:true,contractorPaidDate:payDate,contractorPaidMethod:payMethod}); setShowPaySheet(false); }}>
                ✅ Confirm Payment Received
              </button>
            </div>
          </div>
        )}

        {/* ── SHEET: Pay Driver ── */}
        {showDriverPaySheet && (
          <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"flex-end",zIndex:10}} onClick={()=>setShowDriverPaySheet(false)}>
            <div style={{background:darkMode?"#0D1523":"#fff",borderRadius:"20px 20px 0 0",width:"100%",padding:"20px 18px 32px"}} onClick={e=>e.stopPropagation()}>
              <div style={{width:36,height:4,background:"#E2E2E2",borderRadius:2,margin:"0 auto 16px"}}/>
              <div style={{fontWeight:900,fontSize:17,marginBottom:2}}>💳 Pay Driver</div>
              <div style={{fontSize:12,color:"#6B7280",marginBottom:4}}>{load.driverFullName||"Driver"}</div>
              <div style={{background:"#F0F9FF",borderRadius:10,padding:"10px 14px",marginBottom:14}}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#4B5563",marginBottom:4}}>
                  <span>Base Pay</span><span style={{fontWeight:700}}>${(Number(load.driverBasePay)||0).toFixed(2)}</span>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#4B5563",marginBottom:4}}>
                  <span>Wait Pay</span><span style={{fontWeight:700,color:"#E8962E"}}>${wDrv.toFixed(2)}</span>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:14,fontWeight:900,color:"#1C2B4A",borderTop:"1px solid #E2E2E2",paddingTop:6}}>
                  <span>Total to Driver</span><span style={{color:"#059669"}}>${dPay.toFixed(2)}</span>
                </div>
              </div>
              <div style={{fontSize:11,fontWeight:800,color:"#4B5563",textTransform:"uppercase",letterSpacing:.5,marginBottom:5}}>Payment Date</div>
              <input type="date" value={driverPayDate} onChange={e=>setDriverPayDate(e.target.value)}
                style={{width:"100%",padding:"11px 13px",borderRadius:10,border:"1.5px solid #E2E2E2",fontSize:14,fontWeight:700,marginBottom:12,outline:"none"}}/>
              <div style={{fontSize:11,fontWeight:800,color:"#4B5563",textTransform:"uppercase",letterSpacing:.5,marginBottom:8}}>Payment Method</div>
              <div style={{display:"flex",gap:8,marginBottom:16}}>
                {["eft","cheque","cash"].map(m=>(
                  <button key={m} onClick={()=>setDriverPayMethod(m)} style={{flex:1,padding:"9px",borderRadius:10,border:`2px solid ${driverPayMethod===m?"#1C2B4A":"#E2E2E2"}`,background:driverPayMethod===m?"#EEF2FF":"#fff",color:driverPayMethod===m?"#1C2B4A":"#6B7280",fontWeight:800,fontSize:12,cursor:"pointer",textTransform:"uppercase"}}>
                    {m==="eft"?"🏦 EFT":m==="cheque"?"💳 Cheque":"💵 Cash"}
                  </button>
                ))}
              </div>
              <button style={{width:"100%",padding:"14px",borderRadius:14,background:"#1C2B4A",color:"#fff",fontWeight:900,fontSize:15,border:"none",cursor:"pointer"}}
                onClick={()=>{ onUpdateLoad(load.id,{driverPaid:true,driverPaidDate:driverPayDate,driverPaidMethod:driverPayMethod,driverPaidAmount:dPay}); setShowDriverPaySheet(false); }}>
                💳 Confirm Driver Paid
              </button>
            </div>
          </div>
        )}

        {/* ── STUB: Owner Payment Receipt (from Contractor) ── */}
        {showOwnerStub && (
          <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:11,padding:16}} onClick={()=>setShowOwnerStub(false)}>
            <div style={{background:"#fff",borderRadius:16,width:"100%",maxWidth:420,overflow:"hidden",boxShadow:"0 20px 60px rgba(0,0,0,0.4)"}} onClick={e=>e.stopPropagation()}>
              {/* Header */}
              <div style={{background:"#1C2B4A",padding:"18px 20px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:18,color:"#fff",letterSpacing:1}}>PAYMENT RECEIPT</div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.6)",marginTop:2}}>Owner — Received from Contractor</div>
                </div>
                <button onClick={()=>setShowOwnerStub(false)} style={{background:"rgba(255,255,255,0.1)",border:"none",borderRadius:8,color:"#fff",fontWeight:800,fontSize:14,padding:"6px 12px",cursor:"pointer"}}>✕</button>
              </div>
              {/* Amber accent */}
              <div style={{height:4,background:"linear-gradient(90deg,#E8962E,#F5A94A)"}}/>
              {/* Body */}
              <div style={{padding:"18px 20px"}}>
                <div style={{fontSize:13,fontWeight:800,color:"#1C2B4A",marginBottom:2}}>{load.location||"Load"}</div>
                <div style={{fontSize:11,color:"#6B7280",marginBottom:14}}>{load.date||""} · TMW #{load.tmwLoadNumber||"—"}</div>
                {[
                  ["Load Earnings", `$${(Number(load.earnings)||0).toFixed(2)}`],
                  ["Wait Compensation", `$${wComp.toFixed(2)}`],
                  ["Total Received", `$${gross.toFixed(2)}`],
                ].map(([label,val],i)=>(
                  <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"9px 0",borderBottom:"1px solid #F3F4F6",fontWeight:i===2?900:400}}>
                    <span style={{fontSize:13,color:i===2?"#1C2B4A":"#4B5563"}}>{label}</span>
                    <span style={{fontSize:13,fontWeight:800,color:i===2?"#059669":"#1C2B4A"}}>{val}</span>
                  </div>
                ))}
                <div style={{background:"#F0FFF4",borderRadius:10,padding:"12px 14px",marginTop:14}}>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#4B5563",marginBottom:4}}>
                    <span>Paid by Contractor</span><span style={{fontWeight:700}}>{load.contractorPaidDate||"—"}</span>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#4B5563"}}>
                    <span>Method</span><span style={{fontWeight:700,textTransform:"uppercase"}}>{load.contractorPaidMethod||"—"}</span>
                  </div>
                </div>
                {load.driverFullName && (
                  <div style={{background:"#FEF3C7",borderRadius:10,padding:"10px 14px",marginTop:10}}>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#92400E"}}>
                      <span>Driver Pay Out ({load.driverFullName})</span>
                      <span style={{fontWeight:800}}>- ${dPay.toFixed(2)}</span>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:13,fontWeight:900,color:"#1C2B4A",marginTop:6}}>
                      <span>Net to Business</span>
                      <span style={{color:"#059669"}}>$ {net.toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>
              <div style={{padding:"0 20px 20px"}}>
                <button style={{width:"100%",padding:"12px",borderRadius:12,background:"#1C2B4A",color:"#fff",fontWeight:800,fontSize:13,border:"none",cursor:"pointer"}}
                  onClick={()=>setShowOwnerStub(false)}>Close</button>
              </div>
            </div>
          </div>
        )}

        {/* ── STUB: Driver Pay Stub (from Owner) ── */}
        {showDriverStub && (
          <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:11,padding:16}} onClick={()=>setShowDriverStub(false)}>
            <div style={{background:"#fff",borderRadius:16,width:"100%",maxWidth:420,overflow:"hidden",boxShadow:"0 20px 60px rgba(0,0,0,0.4)"}} onClick={e=>e.stopPropagation()}>
              {/* Header */}
              <div style={{background:"#059669",padding:"18px 20px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:18,color:"#fff",letterSpacing:1}}>PAY STUB</div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.7)",marginTop:2}}>Driver — Received from Owner</div>
                </div>
                <button onClick={()=>setShowDriverStub(false)} style={{background:"rgba(255,255,255,0.15)",border:"none",borderRadius:8,color:"#fff",fontWeight:800,fontSize:14,padding:"6px 12px",cursor:"pointer"}}>✕</button>
              </div>
              <div style={{height:4,background:"linear-gradient(90deg,#E8962E,#F5A94A)"}}/>
              <div style={{padding:"18px 20px"}}>
                <div style={{fontSize:13,fontWeight:800,color:"#1C2B4A",marginBottom:2}}>{load.location||"Load"}</div>
                <div style={{fontSize:11,color:"#6B7280",marginBottom:14}}>{load.date||""} · TMW #{load.tmwLoadNumber||"—"}</div>
                {[
                  ["Driver's Base Pay", `$${(Number(load.driverBasePay)||0).toFixed(2)}`, "#1C2B4A"],
                  ["Wait Pay", `$${wDrv.toFixed(2)}`, "#E8962E"],
                  ["Total Pay", `$${dPay.toFixed(2)}`, "#059669"],
                ].map(([label,val,col],i)=>(
                  <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"9px 0",borderBottom:"1px solid #F3F4F6",fontWeight:i===2?900:400}}>
                    <span style={{fontSize:13,color:i===2?"#1C2B4A":"#4B5563"}}>{label}</span>
                    <span style={{fontSize:13,fontWeight:800,color:col}}>{val}</span>
                  </div>
                ))}
                <div style={{background:"#F0FFF4",borderRadius:10,padding:"12px 14px",marginTop:14}}>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#4B5563",marginBottom:4}}>
                    <span>Paid by Owner</span><span style={{fontWeight:700}}>{load.driverPaidDate||"—"}</span>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#4B5563",marginBottom:4}}>
                    <span>Method</span><span style={{fontWeight:700,textTransform:"uppercase"}}>{load.driverPaidMethod||"—"}</span>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#4B5563"}}>
                    <span>Confirmed Received</span><span style={{fontWeight:700,color:"#059669"}}>{load.driverReceivedDate||"—"}</span>
                  </div>
                </div>
              </div>
              <div style={{padding:"0 20px 20px"}}>
                <button style={{width:"100%",padding:"12px",borderRadius:12,background:"#059669",color:"#fff",fontWeight:800,fontSize:13,border:"none",cursor:"pointer"}}
                  onClick={()=>setShowDriverStub(false)}>Close</button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// ─── INVOICE MODAL ────────────────────────────────────────────────────────────
function InvoiceModal({ load, onClose, rates, trucks, session }) {
  useEffect(()=>{
    const y=window.scrollY;
    document.body.style.position='fixed';
    document.body.style.top='-'+y+'px';
    document.body.style.width='100%';
    document.body.style.overflow='hidden';
    return ()=>{
      document.body.style.position='';
      document.body.style.top='';
      document.body.style.width='';
      document.body.style.overflow='';
      window.scrollTo(0,y);
    };
  },[]);
  const isOwner = session.role === "owner";
  const wm=(Number(load.loadWaitMins)||0)+(Number(load.offloadWaitMins)||0);
  const wHrs=wm/60;
  // Owner sees company-side figures; driver sees only their own pay
  const wComp=parseFloat((wHrs*(Number(rates.companyWaitRate)||0)).toFixed(2));
  const wDrv=parseFloat((wHrs*(Number(rates.driverWaitRate)||0)).toFixed(2));
  const gross=parseFloat(((Number(load.earnings)||0)+wComp).toFixed(2));
  const driverBasePay=Number(load.driverBasePay)||0;
  const driverTotal=parseFloat((driverBasePay+wDrv).toFixed(2));
  const truck=trucks?.find(t=>t.id===load.truckId);
  const users=getUsers(); const owner=users[session.ownerUid||session.uid];
  const invNum=`INV-${load.tmwLoadNumber||load.id?.slice(-4)||"0001"}`;

  const handlePrint=()=>{
    const content=document.getElementById("slt-invoice-content").innerHTML;
    downloadPDF(content, `Invoice_${invNum}`, session);
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:400,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{background:"#fff",borderRadius:18,width:"100%",maxWidth:600,maxHeight:"92vh",overflowY:"auto",boxShadow:"0 28px 80px rgba(0,0,0,0.55)"}}>
        <div style={{padding:"18px 24px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <h2 style={{margin:0,fontSize:18,fontWeight:800,fontFamily:"'Barlow Condensed',sans-serif"}}>📄 {isOwner ? "Invoice Preview" : "Pay Stub"}</h2>
          <div style={{display:"flex",gap:10}}>
            <button className="slt-btn-primary slt-btn-dark-text" style={{width:"auto",padding:"9px 18px"}} onClick={handlePrint}>🖨 Print / PDF</button>
            <button className="slt-btn-ghost" style={{padding:"9px 12px"}} onClick={onClose}>✕</button>
          </div>
        </div>
        <div id="slt-invoice-content" style={{padding:28}}>
          <div className="header" style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:24,borderBottom:`3px solid ${isOwner?C.blue:"#059669"}`,paddingBottom:18}}>
            <div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:900,color:isOwner?C.blue:"#059669"}}>
                {isOwner ? (session.companyName||owner?.companyName||session.fullName||"Owner Operator") : (session.fullName||session.name||"Driver")}
              </div>
              {isOwner && (session.companyName||owner?.companyName)&&<div style={{fontSize:13,color:C.textDarkMed,marginTop:2}}>{owner?.fullName||session.fullName||"Owner Operator"}</div>}
              {isOwner && load.driverFullName&&load.driverFullName!==(owner?.fullName||session.fullName)&&<div style={{fontSize:12,color:C.textDarkMut,marginTop:1}}>Driver: {load.driverFullName}</div>}
              {!isOwner && <div style={{fontSize:12,color:C.textDarkMut,marginTop:1}}>Driver Pay Stub</div>}
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:800,color:C.textDark}}>{invNum}</div>
              <div style={{fontSize:12,color:C.textDarkMut,marginTop:4}}>Issued: {todayStr()}</div>
              <div style={{marginTop:8}}><span className={load.completed?"slt-badge-green":"slt-badge-orange"}>{load.completed?"✓ Delivered":"⬤ In Progress"}</span></div>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:24,marginBottom:24}}>
            <div>
              <div style={{fontSize:12,textTransform:"uppercase",letterSpacing:1.5,color:C.textDarkMut,fontWeight:700,marginBottom:8}}>Load Info</div>
              <div style={{fontSize:13.5,color:C.textDark,lineHeight:1.9}}>
                <div><strong>Route:</strong> {load.location}</div><div><strong>Date:</strong> {load.date}</div>
                {load.appointmentTime&&<div><strong>Appt:</strong> {load.appointmentTime}</div>}{load.time&&<div><strong>Arrival:</strong> {load.time}</div>}{load.completedTime&&<div><strong>Completed:</strong> {load.completedTime}</div>}{load.tmwLoadNumber&&<div><strong>TMW #:</strong> {load.tmwLoadNumber}</div>}
              </div>
            </div>
            <div>
              <div style={{fontSize:12,textTransform:"uppercase",letterSpacing:1.5,color:C.textDarkMut,fontWeight:700,marginBottom:8}}>Equipment</div>
              <div style={{fontSize:13.5,color:C.textDark,lineHeight:1.9}}>
                {truck?<><div><strong>Truck:</strong> {truck.truckNumber}</div><div><strong>TMW #:</strong> {truck.tmwNumber}</div></>:<div>—</div>}
                {load.driverFullName&&<div><strong>Driver:</strong> {load.driverFullName}</div>}
              </div>
            </div>
          </div>
          {/* Owner sees contractor invoice; driver sees only their own pay */}
          {isOwner ? (
            <table style={{width:"100%",borderCollapse:"collapse",marginBottom:20,fontSize:13.5}}>
              <thead><tr style={{background:C.offWhite}}>{["Description","Qty","Rate","Amount"].map(h=><th key={h} style={{padding:"9px 12px",textAlign:"left",fontSize:13,textTransform:"uppercase",letterSpacing:0.5,color:C.textDarkMed,fontWeight:700}}>{h}</th>)}</tr></thead>
              <tbody>
                <tr style={{borderBottom:`1px solid ${C.border}`}}><td style={{padding:"11px 12px"}}>{load.location}</td><td>1 load</td><td>{fmtC(load.earnings||0)}</td><td style={{fontWeight:700}}>{fmtC(load.earnings||0)}</td></tr>
                {wComp>0&&<tr style={{borderBottom:`1px solid ${C.border}`}}><td style={{padding:"11px 12px"}}>Wait Time</td><td>{wHrs.toFixed(2)} hrs</td><td>{fmtC(rates.companyWaitRate)}/hr</td><td style={{fontWeight:700}}>{fmtC(wComp)}</td></tr>}
                {load.fuelTotal>0&&<tr style={{borderBottom:`1px solid ${C.border}`,background:"#F5F6F8"}}><td style={{padding:"11px 12px",color:"#E8962E",fontWeight:700}}>⛽ Fuel Expense</td><td style={{color:"#E8962E"}}>{load.fuelLitres?`${load.fuelLitres}L @ $${Number(load.fuelPricePerLitre||0).toFixed(3)}/L`:"—"}</td><td style={{fontSize:13,color:"#4B5563"}}>Business expense<br/>{load.completedTime?`at ${load.completedTime}`:load.date}</td><td style={{fontWeight:800,color:"#E8962E"}}>{fmtC(load.fuelTotal)}</td></tr>}
                <tr className="total" style={{background:C.blueLight}}><td colSpan={3} style={{padding:"11px 12px",fontWeight:800,fontSize:14}}>TOTAL</td><td style={{fontWeight:800,fontSize:17,color:C.blue,fontFamily:"'Barlow Condensed',sans-serif"}}>{fmtC(gross)}</td></tr>
              </tbody>
            </table>
          ) : (
            <table style={{width:"100%",borderCollapse:"collapse",marginBottom:20,fontSize:13.5}}>
              <thead><tr style={{background:"#F0FDF4"}}>{["Description","Qty","Rate","Amount"].map(h=><th key={h} style={{padding:"9px 12px",textAlign:"left",fontSize:13,textTransform:"uppercase",letterSpacing:0.5,color:"#065F46",fontWeight:700}}>{h}</th>)}</tr></thead>
              <tbody>
                <tr style={{borderBottom:`1px solid ${C.border}`}}><td style={{padding:"11px 12px"}}>{load.location}</td><td>1 load</td><td>{fmtC(driverBasePay)}</td><td style={{fontWeight:700}}>{fmtC(driverBasePay)}</td></tr>
                {wDrv>0&&<tr style={{borderBottom:`1px solid ${C.border}`}}><td style={{padding:"11px 12px"}}>Wait Time</td><td>{wHrs.toFixed(2)} hrs</td><td>{fmtC(rates.driverWaitRate)}/hr</td><td style={{fontWeight:700,color:"#E8962E"}}>{fmtC(wDrv)}</td></tr>}
                <tr style={{background:"#F0FDF4"}}><td colSpan={3} style={{padding:"11px 12px",fontWeight:800,fontSize:14}}>TOTAL PAY</td><td style={{fontWeight:800,fontSize:17,color:"#059669",fontFamily:"'Barlow Condensed',sans-serif"}}>{fmtC(driverTotal)}</td></tr>
              </tbody>
            </table>
          )}
          {load.note&&<div style={{background:C.offWhite,borderRadius:9,padding:"11px 14px",marginBottom:20,fontSize:13,color:C.textDarkMed,fontStyle:"italic"}}>📝 {load.note}</div>}
          {isOwner && load.contractorPaid&&<div style={{background:"#D1FAE5",border:"2px solid #059669",borderRadius:10,padding:"14px 18px",marginBottom:20}}>
            <div style={{fontWeight:800,fontSize:14,color:"#065F46",marginBottom:8}}>✅ Payment Confirmed</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,fontSize:13,color:"#065F46"}}>
              <div><strong>Status:</strong> Paid by Contractor</div>
              <div><strong>Amount:</strong> {fmtC(gross)}</div>
              {load.contractorPaidDate&&<div><strong>Date:</strong> {load.contractorPaidDate}</div>}
              {load.contractorPaidMethod&&<div><strong>Method:</strong> {load.contractorPaidMethod.toUpperCase()}</div>}
            </div>
          </div>}
          {!isOwner && load.driverReceived&&<div style={{background:"#D1FAE5",border:"2px solid #059669",borderRadius:10,padding:"14px 18px",marginBottom:20}}>
            <div style={{fontWeight:800,fontSize:14,color:"#065F46",marginBottom:8}}>✅ Pay Confirmed Received</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,fontSize:13,color:"#065F46"}}>
              <div><strong>Status:</strong> Paid by Owner</div>
              <div><strong>Amount:</strong> {fmtC(driverTotal)}</div>
              {load.driverPaidDate&&<div><strong>Date:</strong> {load.driverPaidDate}</div>}
              {load.driverPaidMethod&&<div><strong>Method:</strong> {load.driverPaidMethod.toUpperCase()}</div>}
            </div>
          </div>}
          <div style={{textAlign:"center",color:C.textDarkMut,fontSize:13,marginTop:28,borderTop:`1px solid ${C.border}`,paddingTop:14}}>{todayStr()}</div>
        </div>
      </div>
    </div>
  );
}

// ─── PAY STUB HISTORY TAB ─────────────────────────────────────────────────────
function PayStubHistoryTab({ session, loads, rates, isOwner, setDetailLoad, goBack, darkMode=false }) {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0,10);
  const todayStr2 = today.toISOString().slice(0,10);

  const [dateFrom, setDateFrom] = useState(firstOfMonth);
  const [dateTo, setDateTo]     = useState(todayStr2);
  const [driverFilter, setDriverFilter] = useState("all");
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfHtml, setPdfHtml] = useState("");

  // Collect all drivers visible in loads (for owner filter)
  const driverOptions = isOwner ? (() => {
    const seen = {};
    loads.forEach(l => {
      if (l.driverFullName && l.user_id !== session.uid) seen[l.user_id||l.driverFullName] = l.driverFullName;
    });
    // Include owner self-pay entries
    const hasSelfPay = loads.some(l => l.payMyselfAsDriver === true && Number(l.driverBasePay||0) > 0);
    if (hasSelfPay) seen[session.uid] = (session.fullName || session.name || "Owner") + " (Self)";
    return Object.entries(seen).map(([k,v]) => ({ uid: k, name: v }));
  })() : [];

  // Filter loads to the selected date range + paid status
  const filteredLoads = loads.filter(l => {
    const d = l.date || (l.created_at||"").slice(0,10);
    if (!d) return false;
    if (d < dateFrom || d > dateTo) return false;
    if (isOwner) {
      // Owner history: contractor-paid loads OR payMyselfAsDriver loads the owner confirmed
      const isSelfPay = l.payMyselfAsDriver === true && Number(l.driverBasePay||0) > 0 && l.driverReceived;
      const isContractorPaid = !!l.contractorPaid;
      if (!isContractorPaid && !isSelfPay) return false;
      if (driverFilter !== "all") {
        // For self-pay loads the key is the owner's uid; for others match user_id/driverFullName
        const matchKey = isSelfPay ? session.uid : (l.user_id || l.driverFullName);
        if (matchKey !== driverFilter) return false;
      }
    } else {
      // Driver history: loads where driver received (or payment_status=paid)
      const mine = l.assignedDriverUid === session.uid || l.user_id === session.uid || l.addedBy === session.uid;
      if (!mine) return false;
      if (!l.driverReceived && l.payment_status !== "paid") return false;
    }
    return true;
  }).sort((a,b) => (b.date||"") > (a.date||"") ? 1 : -1);

  // Compute totals
  const totals = filteredLoads.reduce((acc, l) => {
    const wm = (Number(l.loadWaitMins)||0) + (Number(l.offloadWaitMins)||0);
    if (isOwner) {
      const wComp = wm/60 * (Number(rates.companyWaitRate)||0);
      const wDrv  = wm/60 * (Number(rates.driverWaitRate)||0);
      const gross = (Number(l.earnings)||0) + wComp;
      const dPay  = (Number(l.driverBasePay)||0) + wDrv;
      acc.gross  += gross;
      acc.dPay   += dPay;
      acc.net    += gross - dPay;
      acc.loads  += 1;
    } else {
      const wDrv = wm/60 * (Number(rates.driverWaitRate)||0);
      acc.base   += Number(l.driverBasePay)||0;
      acc.wait   += wDrv;
      acc.gross  += (Number(l.driverBasePay)||0) + wDrv;
      acc.loads  += 1;
    }
    return acc;
  }, { gross:0, dPay:0, net:0, base:0, wait:0, loads:0 });

  const fmtC2 = (n) => `$${Number(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g,",")}`;
  const fmtDate = (d) => { if(!d)return"—"; const [y,m,day]=d.split("-"); const months=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]; return `${months[Number(m)-1]} ${Number(day)}, ${y}`; };

  // ── Dark mode palette ──
  const DM = {
    pageBg:  darkMode ? "#070B14" : "#F5F6F8",
    cardBg:  darkMode ? "#0D1523" : "#FFFFFF",
    border:  darkMode ? "rgba(255,255,255,0.08)" : C.border,
    text:    darkMode ? "#F0F0F0" : C.textDark,
    textMed: darkMode ? "rgba(255,255,255,0.5)" : C.textDarkMed,
    inputBg: darkMode ? "#111C2E" : "#FFFFFF",
    pillBg:  darkMode ? "rgba(255,255,255,0.07)" : "#F3F4F6",
  };

  return (
    <div className="slt-page" style={{background:DM.pageBg,color:DM.text,paddingBottom:80}}>
      <div className="slt-hero">
        <button onClick={goBack} style={{background:"none",border:"none",fontSize:22,cursor:"pointer",color:"#fff",marginBottom:6,padding:0}}>←</button>
        <div className="slt-hero-title">🧾 {isOwner ? "Payment History" : "Pay Stub History"}</div>
        <div className="slt-hero-sub">{isOwner ? "Contractor-paid loads with net to business" : "Your confirmed received payments"}</div>
      </div>

      <div className="slt-container">
        {/* ── Filters ── */}
        <div style={{background:DM.cardBg,borderRadius:14,padding:"16px 18px",marginBottom:16,boxShadow:darkMode?"none":"0 1px 4px rgba(0,0,0,0.06)"}}>
          <div style={{fontWeight:800,fontSize:12,color:DM.textMed,textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>📅 Date Range</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
            <div>
              <label style={{fontSize:11,fontWeight:700,color:DM.textMed,display:"block",marginBottom:4}}>From</label>
              <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)}
                style={{width:"100%",padding:"9px 11px",borderRadius:9,border:`1.5px solid ${DM.border}`,fontSize:13,fontWeight:600,outline:"none",boxSizing:"border-box",background:DM.inputBg,color:DM.text}}/>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:700,color:DM.textMed,display:"block",marginBottom:4}}>To</label>
              <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)}
                style={{width:"100%",padding:"9px 11px",borderRadius:9,border:`1.5px solid ${DM.border}`,fontSize:13,fontWeight:600,outline:"none",boxSizing:"border-box",background:DM.inputBg,color:DM.text}}/>
            </div>
          </div>
          {/* Quick-range pills */}
          <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
            {[
              {label:"This Month", fn:()=>{ const n=new Date(); setDateFrom(new Date(n.getFullYear(),n.getMonth(),1).toISOString().slice(0,10)); setDateTo(n.toISOString().slice(0,10)); }},
              {label:"Last Month", fn:()=>{ const n=new Date(); const f=new Date(n.getFullYear(),n.getMonth()-1,1); const t=new Date(n.getFullYear(),n.getMonth(),0); setDateFrom(f.toISOString().slice(0,10)); setDateTo(t.toISOString().slice(0,10)); }},
              {label:"Last 3 Mo",  fn:()=>{ const n=new Date(); const f=new Date(n.getFullYear(),n.getMonth()-2,1); setDateFrom(f.toISOString().slice(0,10)); setDateTo(n.toISOString().slice(0,10)); }},
              {label:"This Year",  fn:()=>{ const n=new Date(); setDateFrom(`${n.getFullYear()}-01-01`); setDateTo(n.toISOString().slice(0,10)); }},
            ].map(({label,fn})=>(
              <button key={label} onClick={fn}
                style={{padding:"5px 12px",borderRadius:20,border:`1.5px solid ${DM.border}`,background:DM.pillBg,fontSize:12,fontWeight:700,cursor:"pointer",color:DM.text}}>
                {label}
              </button>
            ))}
          </div>
          {/* Driver filter (owner only) */}
          {isOwner && driverOptions.length > 0 && (
            <div style={{marginTop:12}}>
              <label style={{fontSize:11,fontWeight:700,color:DM.textMed,display:"block",marginBottom:5}}>Filter by Driver</label>
              <select value={driverFilter} onChange={e=>setDriverFilter(e.target.value)}
                style={{width:"100%",padding:"9px 11px",borderRadius:9,border:`1.5px solid ${DM.border}`,fontSize:13,fontWeight:600,outline:"none",background:DM.inputBg,color:DM.text}}>
                <option value="all">All Drivers</option>
                {driverOptions.map(d=><option key={d.uid} value={d.uid}>{d.name}</option>)}
              </select>
            </div>
          )}
        </div>

        {/* ── Summary Cards ── */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
          {(isOwner ? [
            {label:"Loads", val:totals.loads, color:C.blue, icon:"📦"},
            {label:"Gross Revenue", val:fmtC2(totals.gross), color:"#059669", icon:"💰"},
            {label:"Driver Pay", val:fmtC2(totals.dPay), color:"#E8962E", icon:"👤"},
            {label:"Net to Business", val:fmtC2(totals.net), color:C.blue, icon:"🏢"},
          ] : [
            {label:"Loads Paid", val:totals.loads, color:C.blue, icon:"📦"},
            {label:"Route Pay", val:fmtC2(totals.base), color:C.blue, icon:"🚛"},
            {label:"Wait Pay", val:fmtC2(totals.wait), color:"#E8962E", icon:"⏱"},
            {label:"Total Earned", val:fmtC2(totals.gross), color:"#059669", icon:"💵"},
          ]).map(({label,val,color,icon})=>(
            <div key={label} style={{background:DM.cardBg,borderRadius:12,padding:"14px 16px",boxShadow:darkMode?"none":"0 1px 4px rgba(0,0,0,0.06)",borderTop:`3px solid ${color}`}}>
              <div style={{fontSize:11,fontWeight:700,color:DM.textMed,textTransform:"uppercase",letterSpacing:0.8,marginBottom:4}}>{icon} {label}</div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:900,color}}>{val}</div>
            </div>
          ))}
        </div>

        {/* ── Load List ── */}
        {filteredLoads.length === 0 ? (
          <div style={{background:DM.cardBg,borderRadius:14,padding:"36px 20px",textAlign:"center",boxShadow:darkMode?"none":"0 1px 4px rgba(0,0,0,0.06)"}}>
            <div style={{fontSize:40,marginBottom:10}}>📭</div>
            <div style={{fontWeight:800,fontSize:15,color:DM.text,marginBottom:4}}>No paid loads found</div>
            <div style={{fontSize:13,color:DM.textMed}}>Try a wider date range or check your payment statuses</div>
          </div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {filteredLoads.map(l => {
              const wm = (Number(l.loadWaitMins)||0) + (Number(l.offloadWaitMins)||0);
              const wComp = wm/60 * (Number(rates.companyWaitRate)||0);
              const wDrv  = wm/60 * (Number(rates.driverWaitRate)||0);
              const ownerGross = (Number(l.earnings)||0) + wComp;
              const driverTotal2 = (Number(l.driverBasePay)||0) + wDrv;
              const ownerNet = ownerGross - driverTotal2;
              return (
                <div key={l.id}
                  onClick={()=>{ if(setDetailLoad) setDetailLoad(l); }}
                  style={{background:DM.cardBg,borderRadius:13,padding:"14px 16px",boxShadow:darkMode?"none":"0 1px 4px rgba(0,0,0,0.07)",cursor:setDetailLoad?"pointer":"default",borderLeft:`4px solid ${isOwner?"#059669":"#1C2B4A"}`}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                    <div style={{fontWeight:800,fontSize:14,color:DM.text,flex:1,marginRight:8}}>{l.location||"—"}</div>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:900,color:isOwner?"#059669":C.blue}}>
                      {isOwner ? fmtC2(ownerGross) : fmtC2(driverTotal2)}
                    </div>
                  </div>
                  <div style={{fontSize:12,color:DM.textMed,marginBottom:isOwner?6:0}}>
                    {fmtDate(l.date)}
                    {l.tmwLoadNumber ? ` · TMW #${l.tmwLoadNumber}` : ""}
                    {!isOwner && l.driverReceivedDate ? ` · Received ${fmtDate(l.driverReceivedDate)}` : ""}
                    {isOwner && l.contractorPaidDate ? ` · Paid ${fmtDate(l.contractorPaidDate)}` : ""}
                  </div>
                  {isOwner && (
                    <div style={{display:"flex",gap:12,fontSize:12,color:DM.textMed,flexWrap:"wrap"}}>
                      {l.driverFullName && <span>👤 {l.driverFullName}</span>}
                      {wComp > 0 && <span>⏱ Wait: {fmtC2(wComp)}</span>}
                      <span style={{color:"#E8962E"}}>Driver: -{fmtC2(driverTotal2)}</span>
                      <span style={{color:"#059669",fontWeight:800}}>Net: {fmtC2(ownerNet)}</span>
                    </div>
                  )}
                  {!isOwner && (
                    <div style={{display:"flex",gap:12,fontSize:12,color:DM.textMed,flexWrap:"wrap"}}>
                      <span>🚛 Base: {fmtC2(Number(l.driverBasePay)||0)}</span>
                      {wDrv > 0 && <span style={{color:"#E8962E"}}>⏱ Wait: {fmtC2(wDrv)}</span>}
                      {l.driverPaidMethod && <span style={{textTransform:"uppercase"}}>{l.driverPaidMethod}</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── PDF Buttons ── */}
        {filteredLoads.length > 0 && (()=> {

          const buildPayStubPDF = () => {
            const userName = session.companyName || session.fullName || session.name || (isOwner ? "Owner" : "Driver");
            const generatedDate = new Date().toLocaleDateString("en-CA", { year:"numeric", month:"long", day:"numeric" });
            const rangeLabel = `${dateFrom} to ${dateTo}`;
            const loadRows = filteredLoads.map((l, i) => {
              const wm = (Number(l.loadWaitMins)||0) + (Number(l.offloadWaitMins)||0);
              const wComp = wm/60 * (Number(rates.companyWaitRate)||0);
              const wDrv  = wm/60 * (Number(rates.driverWaitRate)||0);
              const routePay = isOwner ? Number(l.earnings||0) : Number(l.driverBasePay||0);
              const waitAmt  = isOwner ? wComp : wDrv;
              const total    = routePay + waitAmt;
              const paidDate = isOwner ? (l.contractorPaidDate||l.date||"—") : (l.driverReceivedDate||l.date||"—");
              const method   = isOwner ? (l.contractorPaidMethod||"") : (l.driverPaidMethod||"");
              return `<tr style="background:${i%2===0?"#fff":"#F9FAFB"}">
                <td style="padding:9px 12px">${l.date||"—"}</td>
                <td style="padding:9px 12px">${l.tmwLoadNumber?`<strong>#${l.tmwLoadNumber}</strong>`:"—"}</td>
                <td style="padding:9px 12px">${l.location||"—"}</td>
                <td style="padding:9px 12px;text-align:right">$${routePay.toFixed(2)}</td>
                <td style="padding:9px 12px;text-align:right;color:#1565C0">$${waitAmt.toFixed(2)}</td>
                <td style="padding:9px 12px;text-align:right;font-weight:800;color:#16A34A">$${total.toFixed(2)}</td>
                <td style="padding:9px 12px;font-size:11px;color:#6B7280">${fmtDate(paidDate)}${method?" · "+method.toUpperCase():""}</td>
              </tr>`;
            }).join("");

            return `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>Pay Stub History — ${userName}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Segoe UI',Arial,sans-serif;color:#1a1a1a;background:#fff;font-size:13px;line-height:1.5}
  .page{max-width:820px;margin:0 auto;padding:32px 36px}
  h2{font-size:12px;font-weight:800;color:#1C2B4A;margin:24px 0 10px;text-transform:uppercase;letter-spacing:1px;border-bottom:2px solid #E8962E;padding-bottom:5px}
  table{width:100%;border-collapse:collapse;margin-bottom:16px;font-size:13px}
  thead tr{background:#1C2B4A}
  thead th{padding:9px 12px;text-align:left;color:#fff;font-size:11px;text-transform:uppercase;letter-spacing:0.8px;font-weight:700}
  thead th.r{text-align:right}
  tbody td{border-bottom:1px solid #F0F0F0;vertical-align:middle}
  tfoot td{padding:11px 12px;font-weight:900;font-size:14px;border-top:2px solid #1C2B4A;background:#F5F8FF}
  .summary-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin:20px 0}
  .s-card{background:#F5F8FF;border:1.5px solid #E0E8F5;border-radius:8px;padding:14px 12px;text-align:center}
  .s-lbl{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#888;margin-bottom:6px}
  .s-val{font-size:22px;font-weight:900;color:#1C2B4A}
  .s-val.green{color:#16A34A}.s-val.blue{color:#1565C0}.s-val.orange{color:#E8962E}
  .footer{margin-top:24px;padding-top:12px;border-top:1px solid #E8EAF0;font-size:10px;color:#aaa;display:flex;justify-content:space-between}
</style>
</head><body><div class="page">
  <div style="display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:18px;border-bottom:3px solid #E8962E;margin-bottom:22px">
    <div>
      <div style="display:inline-block;background:#1C2B4A;color:#fff;font-size:10px;font-weight:800;letter-spacing:1px;padding:3px 10px;border-radius:20px;text-transform:uppercase;margin-bottom:6px">${isOwner?"Payment History":"Pay Stub History"}</div>
      <div style="font-size:22px;font-weight:900;color:#0A1628;margin-bottom:6px">🧾 ${isOwner?"Payment History":"Pay Stub History"}</div>
      <div style="font-size:12px;color:#666;line-height:1.8">
        <strong>${isOwner?"Owner":"Driver"}:</strong> ${userName}<br>
        <strong>Period:</strong> ${rangeLabel}<br>
        <strong>Generated:</strong> ${generatedDate}
      </div>
    </div>
    <div style="text-align:right">
      <div style="font-size:11px;color:#888;margin-bottom:4px">Total ${isOwner?"Revenue":"Earned"}</div>
      <div style="font-size:36px;font-weight:900;color:#16A34A">$${(isOwner?totals.gross:totals.gross).toFixed(2)}</div>
      <div style="font-size:11px;color:#888">${filteredLoads.length} loads</div>
    </div>
  </div>

  <h2>Summary</h2>
  <div class="summary-grid">
    ${isOwner
      ? `<div class="s-card"><div class="s-lbl">Loads</div><div class="s-val">${totals.loads}</div></div>
         <div class="s-card"><div class="s-lbl">Gross Revenue</div><div class="s-val green">$${totals.gross.toFixed(2)}</div></div>
         <div class="s-card"><div class="s-lbl">Driver Pay</div><div class="s-val orange">$${totals.dPay.toFixed(2)}</div></div>
         <div class="s-card"><div class="s-lbl">Net to Business</div><div class="s-val green">$${totals.net.toFixed(2)}</div></div>`
      : `<div class="s-card"><div class="s-lbl">Loads Paid</div><div class="s-val">${totals.loads}</div></div>
         <div class="s-card"><div class="s-lbl">Route Pay</div><div class="s-val blue">$${totals.base.toFixed(2)}</div></div>
         <div class="s-card"><div class="s-lbl">Wait Pay</div><div class="s-val orange">$${totals.wait.toFixed(2)}</div></div>
         <div class="s-card"><div class="s-lbl">Total Earned</div><div class="s-val green">$${totals.gross.toFixed(2)}</div></div>`
    }
  </div>

  <h2>Load History (${filteredLoads.length} loads)</h2>
  <table>
    <thead><tr>
      <th>Date</th><th>TMW #</th><th>Route</th>
      <th class="r">Route Pay</th>
      <th class="r">Wait Pay</th>
      <th class="r">Total</th>
      <th>${isOwner?"Paid Date":"Received"}</th>
    </tr></thead>
    <tbody>${loadRows}</tbody>
    <tfoot><tr>
      <td colspan="3">TOTAL (${filteredLoads.length} loads)</td>
      <td style="text-align:right">$${(isOwner?totals.gross-totals.dPay-totals.wait:totals.base).toFixed(2)}</td>
      <td style="text-align:right;color:#1565C0">$${(isOwner?(totals.gross-totals.dPay-totals.wait>0?totals.wait:0):totals.wait).toFixed(2)}</td>
      <td style="text-align:right;color:#16A34A">$${(isOwner?totals.gross:totals.gross).toFixed(2)}</td>
      <td></td>
    </tr></tfoot>
  </table>

  <div class="footer">
    <span>TruckPilot · Confidential Payment Record</span>
    <span>Generated ${generatedDate}</span>
  </div>
</div></body></html>`;
          };

          return (
            <>
              {showPdfPreview && (
                <div style={{position:"fixed",inset:0,zIndex:9200,background:"rgba(0,0,0,0.85)",display:"flex",flexDirection:"column"}}>
                  <div style={{background:"#1A1A1A",padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0,gap:10}}>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:15,color:"#fff"}}>
                      🧾 {isOwner?"Payment History":"Pay Stub History"}
                    </div>
                    <div style={{display:"flex",gap:8}}>
                      <button
                        onClick={()=>{ downloadPDF(buildPayStubPDF(), `PayStub_${dateFrom}_${dateTo}`, session); }}
                        style={{background:"#E8962E",border:"none",color:"#fff",borderRadius:8,padding:"8px 14px",fontSize:12,cursor:"pointer",fontWeight:800}}>
                        ⬇ Download PDF
                      </button>
                      <button onClick={()=>setShowPdfPreview(false)}
                        style={{background:"rgba(255,255,255,0.15)",border:"none",color:"#fff",borderRadius:8,padding:"8px 14px",fontSize:13,cursor:"pointer",fontWeight:700}}>
                        ✕ Close
                      </button>
                    </div>
                  </div>
                  <iframe srcDoc={pdfHtml} style={{flex:1,border:"none",background:"#fff"}} title="Pay Stub Preview" />
                </div>
              )}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:20}}>
                <button
                  onClick={()=>{ setPdfHtml(buildPayStubPDF()); setShowPdfPreview(true); }}
                  style={{padding:"13px",borderRadius:12,border:"none",background:"#1C2B4A",color:"#fff",fontWeight:800,fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>
                  👁 Preview PDF
                </button>
                <button
                  onClick={()=>{ downloadPDF(buildPayStubPDF(), `PayStub_${dateFrom}_${dateTo}`, session); }}
                  style={{padding:"13px",borderRadius:12,border:`2px solid ${darkMode?"rgba(255,255,255,0.15)":"#D1D5DB"}`,background:"transparent",color:DM.text,fontWeight:800,fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>
                  ⬇ Download PDF
                </button>
              </div>
            </>
          );
        })()}
      </div>
    </div>
  );
}

// ─── MESSAGES TAB ─────────────────────────────────────────────────────────────
function MessagesTab({ session, loads, isOwner, onAddNote }) {
  const myLoads = isOwner?loads:loads.filter(l=>l.assignedDriverUid===session.uid||l.addedBy===session.uid||l.user_id===session.uid);
  const threaded = myLoads.filter(l=>l.messages&&l.messages.length>0);
  const [selected,setSelected]=useState(null);
  const [note,setNote]=useState(""); useEffect(()=>{
    const y=window.scrollY;
    document.body.style.position='fixed';
    document.body.style.top='-'+y+'px';
    document.body.style.width='100%';
    document.body.style.overflow='hidden';
    return ()=>{
      document.body.style.position='';
      document.body.style.top='';
      document.body.style.width='';
      document.body.style.overflow='';
      window.scrollTo(0,y);
    };
  },[]);
  const [showPicker,setShowPicker]=useState(false);
  const endRef=useRef(null);
  useEffect(()=>endRef.current?.scrollIntoView({behavior:"smooth"}),[selected?.messages?.length]);
  const current=selected?loads.find(l=>l.id===selected.id):null;
  const handleSend=()=>{ if(!note.trim()||!current)return; onAddNote(current.id,note.trim(),session); setNote(""); setSelected(loads.find(l=>l.id===current.id)); };

  return (
    <div className="slt-page" style={{background:"#070B14",color:"#F0F0F0"}}>
      <div className="slt-hero"><div style={{fontSize:10,fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"rgba(255,255,255,0.35)",marginBottom:3}}>TRUCKPILOT</div><div className="slt-hero-title">Messages & Notes</div><div className="slt-hero-sub">Load-level notes shared between owner and driver</div></div>
      <div className="slt-container">
        <div style={{display:"grid",gridTemplateColumns:"min(300px,100%) 1fr",gap:18,minHeight:520,gridTemplateRows:"auto"}} className="slt-msg-grid">
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:14}}>Load Threads</span>
              <button className="slt-btn-secondary" style={{padding:"6px 12px",fontSize:12}} onClick={()=>setShowPicker(!showPicker)}>+ New</button>
            </div>
            {showPicker&&(
              <div className="slt-card" style={{padding:14,marginBottom:12}}>
                <div style={{fontSize:12.5,fontWeight:700,marginBottom:8}}>Pick a load:</div>
                {myLoads.slice(0,10).map(l=>(
                  <div key={l.id} onClick={()=>{setSelected(l);setShowPicker(false);}} style={{padding:"9px 10px",borderRadius:9,cursor:"pointer",fontSize:13,marginBottom:4,background:C.offWhite}} onMouseEnter={e=>e.currentTarget.style.background=C.blueLight} onMouseLeave={e=>e.currentTarget.style.background=C.offWhite}>
                    <div style={{fontWeight:700,fontSize:13}}>{l.location}</div>
                    <div style={{fontSize:13,color:C.textDarkMut}}>#{l.tmwLoadNumber} · {l.date}</div>
                  </div>
                ))}
              </div>
            )}
            {threaded.length===0&&!showPicker&&<div className="slt-card" style={{textAlign:"center",padding:"28px 14px"}}><div style={{fontSize:32,marginBottom:8}}>💬</div><div style={{color:C.textDarkMut,fontSize:13}}>No threads yet.</div></div>}
            {threaded.map(l=>{
              const last=l.messages[l.messages.length-1];
              const isSel=current?.id===l.id;
              return(
                <div key={l.id} onClick={()=>setSelected(l)} className="slt-card-sm" style={{marginBottom:8,cursor:"pointer",border:`1.5px solid ${isSel?C.blue:C.border}`,background:isSel?C.blueLight:C.white}}>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:13.5,marginBottom:2}}>{l.location}</div>
                  <div style={{fontSize:13,color:C.textDarkMut,marginBottom:6}}>#{l.tmwLoadNumber} · {l.date}</div>
                  <div style={{fontSize:12,color:C.textDarkMed,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}><strong>{last?.authorName?.split(" ")[0]}:</strong> {last?.text}</div>
                  <div style={{fontSize:12.5,color:C.textDarkMut,marginTop:3}}>{l.messages.length} note{l.messages.length!==1?"s":""}</div>
                </div>
              );
            })}
          </div>
          <div className="slt-card" style={{display:"flex",flexDirection:"column",padding:0,overflow:"hidden",minHeight:400}}>
            {!current?(
              <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",color:C.textDarkMut,padding:40}}>
                <div style={{fontSize:44,marginBottom:14}}>💬</div>
                <div style={{fontWeight:700,marginBottom:6,fontFamily:"'Barlow Condensed',sans-serif"}}>Select a load to view notes</div>
                <div style={{fontSize:13}}>Notes are shared between owner and driver</div>
              </div>
            ):(
              <>
                <div style={{padding:"14px 18px",borderBottom:`1px solid ${C.border}`,background:C.offWhite}}>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:15}}>{current.location}</div>
                  <div style={{fontSize:13.5,color:C.textDarkMut}}>#{current.tmwLoadNumber} · {current.date} · <span style={{color:current.completed?C.green:C.orange,fontWeight:700}}>{current.completed?"Completed":"Active"}</span></div>
                </div>
                <div style={{flex:1,overflowY:"auto",padding:18,display:"flex",flexDirection:"column",gap:10,minHeight:280}}>
                  {(!current.messages||current.messages.length===0)&&<div style={{textAlign:"center",color:C.textDarkMut,padding:"28px 0",fontSize:13}}>No notes yet.</div>}
                  {current.messages&&current.messages.map((m,i)=>{
                    const isMe=m.authorUid===session.uid;
                    return(<div key={i} style={{display:"flex",justifyContent:isMe?"flex-end":"flex-start"}}>
                      <div style={{maxWidth:"72%"}}>
                        <div style={{fontSize:12.5,color:C.textDarkMut,marginBottom:3,textAlign:isMe?"right":"left"}}>{m.authorName} · {m.timestamp?.slice(0,10)}</div>
                        <div className={isMe?"slt-bubble-me":"slt-bubble-other"}>{m.text}</div>
                      </div>
                    </div>);
                  })}
                  <div ref={endRef}/>
                </div>
                <div style={{padding:"12px 18px",borderTop:`1px solid ${C.border}`,display:"flex",gap:8}}>
                  <input className="slt-input" value={note} onChange={e=>setNote(e.target.value)} placeholder="Type a note…" style={{flex:1}} onKeyDown={e=>{if(e.key==="Enter")handleSend();}}/>
                  <button className="slt-btn-primary slt-btn-dark-text" style={{width:"auto",padding:"11px 18px"}} onClick={handleSend}>Send</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── EXPENSE DETAIL MODAL ─────────────────────────────────────────────────────
function ExpenseDetailModal({ expense, onClose, onEdit, onDelete, CATS }) {
  if (!expense) return null;
  const cat = CATS.find(c => c.id === expense.category) || CATS[CATS.length - 1];
  const receiptSrc = expense.receipt && expense.receipt.startsWith("data:") ? expense.receipt : expense.receiptUrl || null;
  const [imgError, setImgError] = useState(false);
  const isPdf = receiptSrc && (receiptSrc.startsWith("data:application/pdf") || receiptSrc.toLowerCase().includes(".pdf"));
  // Lock body scroll (iOS-safe) when modal is open
  useEffect(() => {
    const scrollY = window.scrollY;
    // Freeze body position to prevent iOS Safari background scroll
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    document.body.style.touchAction = "none";
    // Block touchmove on the backdrop (but allow it on modal inner scroll)
    const blockTouch = (e) => { if (!e.target.closest(".exp-modal-scroll")) e.preventDefault(); };
    document.addEventListener("touchmove", blockTouch, { passive: false });
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.touchAction = "";
      document.removeEventListener("touchmove", blockTouch);
      window.scrollTo(0, scrollY);
    };
  }, []);
  return (
    <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:4000,display:"flex",alignItems:"flex-end",justifyContent:"center",touchAction:"none" }} onClick={onClose}>
      <div style={{ background:"#fff",borderRadius:"20px 20px 0 0",width:"100%",maxWidth:560,maxHeight:"80vh",display:"flex",flexDirection:"column" }} onClick={e=>e.stopPropagation()}>
        <div className="exp-modal-scroll" style={{ overflowY:"auto",flex:1,padding:"20px 20px 8px",WebkitOverflowScrolling:"touch",overscrollBehavior:"contain" }}>
        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14 }}>
          <div>
            <div style={{ fontSize:12,fontWeight:800,color:cat.c,textTransform:"uppercase",letterSpacing:1,marginBottom:3 }}>{cat.i} {cat.l}</div>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:30,color:cat.c,lineHeight:1 }}>{fmtC(expense.amount)}</div>
          </div>
          <button onClick={onClose} style={{ background:"#EEEEEE",border:"none",borderRadius:"50%",width:34,height:34,fontSize:17,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>✕</button>
        </div>
        <div style={{ background:"#FFFFFF",borderRadius:12,padding:"10px 14px",marginBottom:12,display:"flex",flexDirection:"column",gap:8 }}>
          {expense.merchant && (<div style={{ display:"flex",justifyContent:"space-between",alignItems:"center" }}><span style={{ fontSize:12,color:"#4B5563",fontWeight:700 }}>MERCHANT</span><span style={{ fontSize:14,fontWeight:800 }}>{expense.merchant}</span></div>)}
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center" }}><span style={{ fontSize:12,color:"#4B5563",fontWeight:700 }}>DATE</span><span style={{ fontSize:14,fontWeight:800 }}>{expense.date}</span></div>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center" }}><span style={{ fontSize:12,color:"#4B5563",fontWeight:700 }}>TAX LINE</span><span style={{ fontSize:12,fontWeight:800,color:cat.c,background:cat.c+"18",borderRadius:6,padding:"2px 10px" }}>{expense.taxCategory || cat.cra}</span></div>
          {(expense.litres || expense.pricePerLitre) && (<div style={{ display:"flex",justifyContent:"space-between",alignItems:"center" }}><span style={{ fontSize:12,color:"#4B5563",fontWeight:700 }}>FUEL</span><span style={{ fontSize:14,fontWeight:800 }}>{expense.litres}L @ ${Number(expense.pricePerLitre||0).toFixed(3)}/L</span></div>)}
          {(expense.note || expense.description) && (<div><span style={{ fontSize:12,color:"#4B5563",fontWeight:700,display:"block",marginBottom:4 }}>NOTE</span><span style={{ fontSize:13,color:"#0B1120" }}>{expense.description || expense.note}</span></div>)}
          {expense.driverName && (<div style={{ display:"flex",justifyContent:"space-between",alignItems:"center" }}><span style={{ fontSize:12,color:"#4B5563",fontWeight:700 }}>DRIVER</span><span style={{ fontSize:13,fontWeight:800,color:"#E8962E" }}>👤 {expense.driverName}</span></div>)}
          {expense.source === "fuel_log" && <div style={{ background:"#F5F6F8",borderRadius:8,padding:"6px 10px",fontSize:12,color:"#E8962E",fontWeight:700 }}>⛽ From Fuel Log</div>}
          {expense.source === "load" && <div style={{ background:"#F5F6F8",borderRadius:8,padding:"6px 10px",fontSize:12,color:"#E8962E",fontWeight:700 }}>🔗 Auto-logged from Load</div>}
        </div>
        {(receiptSrc && !imgError) ? (
          <div style={{ marginBottom:14 }}>
            <div style={{ fontSize:12,color:"#4B5563",fontWeight:800,textTransform:"uppercase",letterSpacing:1,marginBottom:8 }}>📎 Attachment</div>
            {isPdf ? (
              <a href={receiptSrc} target="_blank" rel="noopener noreferrer" style={{ display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:"#F5F6F8",borderRadius:12,border:"1.5px solid #F5F6F8",textDecoration:"none" }}>
                <span style={{ fontSize:26 }}>📄</span>
                <div><div style={{ fontWeight:800,fontSize:13,color:"#E8962E" }}>PDF Receipt</div><div style={{ fontSize:12,color:"#4B5563" }}>Tap to open</div></div>
                <span style={{ marginLeft:"auto",fontSize:16,color:"#E8962E" }}>↗</span>
              </a>
            ) : (
              <div>
                <img src={receiptSrc} alt="Receipt" onError={()=>setImgError(true)} style={{ width:"100%",borderRadius:12,objectFit:"contain",maxHeight:220,border:"1px solid #e0e0e0",display:"block" }} />
                <a href={receiptSrc} download="receipt.jpg" style={{ display:"block",marginTop:8,textAlign:"center",background:"#E8962E",color:"#1C1C1E",padding:"10px 0",borderRadius:10,fontWeight:700,fontSize:13,textDecoration:"none" }}>⬇️ Download Receipt</a>
              </div>
            )}
          </div>
        ) : (
          <div style={{ display:"flex",alignItems:"center",gap:8,padding:"8px 0",marginBottom:8,color:"#9CA3AF",fontSize:12,borderTop:"1px solid #f0f0f0",borderBottom:"1px solid #f0f0f0" }}>
            <span style={{ fontSize:16 }}>🧾</span><span>No attachment saved</span>
          </div>
        )}
        </div>{/* end scrollable content */}
        {(onEdit || onDelete) && (
          <div style={{ padding:"12px 20px 30px",background:"#fff",borderTop:"1px solid #f0f0f0",flexShrink:0 }}>
            <div style={{ display:"flex",gap:10 }}>
              {onDelete && <button onClick={onDelete} style={{ flex:1,padding:"14px 0",borderRadius:12,border:"2px solid #ef5350",background:"#FFF5F5",color:"#ef5350",fontWeight:800,fontSize:14,cursor:"pointer" }}>🗑 Delete</button>}
              {onEdit && <button onClick={onEdit} style={{ flex:2,padding:"14px 0",borderRadius:12,border:"none",background:"#E8962E",color:"#1C1C1E",fontWeight:800,fontSize:14,cursor:"pointer" }}>✏️ Edit</button>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── EXPENSES TAB ─────────────────────────────────────────────────────────────
function ExpensesTab({ session, isOwner, allLoads=[], goBack, darkMode=false }) {
  const [expSearchQuery, setExpSearchQuery] = useState("");
  const [expSearchInput, setExpSearchInput] = useState("");
  // Full CRA-claimable categories
  const CATS = [
    {id:"fuel",           l:"Fuel & Oil",              i:"⛽", c:C.orange,  cra:"Line 9220"},
    {id:"maintenance",    l:"Repairs & Maintenance",   i:"🔧", c:C.red,     cra:"Line 9270"},
    {id:"insurance",      l:"Insurance",               i:"🛡", c:C.blue,    cra:"Line 9910"},
    {id:"permits",        l:"Licenses & Renewals",     i:"📋", c:C.purple,  cra:"Line 9270"},
    {id:"telephone",      l:"Telephone & Internet",    i:"📱", c:"#E8962E",  cra:"Line 9220"},
    {id:"rent",           l:"Rent / Lease",            i:"🏢", c:"#E8962E",  cra:"Line 9200"},
    {id:"meals",          l:"Meals & Entertainment",   i:"🍽", c:C.green,   cra:"Line 8523 (50%)"},
    {id:"lodging",        l:"Accommodation / Travel",  i:"🏨", c:"#8D6E63",  cra:"Line 9200"},
    {id:"tolls",          l:"Tolls & Parking",         i:"🛣", c:C.teal,    cra:"Line 9281"},
    {id:"union_dues",     l:"Union / Association Dues",i:"🤝", c:"#4B5563",  cra:"Line 9270"},
    {id:"tools_supplies", l:"Tools & Supplies",        i:"🧰", c:C.orange,  cra:"Line 9270"},
    {id:"safety",         l:"Safety Gear & Clothing",  i:"🦺", c:"#EF6C00",  cra:"Line 9270"},
    {id:"accounting",     l:"Accounting / Legal Fees", i:"📂", c:"#6D4C41",  cra:"Line 8860"},
    {id:"advertising",    l:"Advertising / Marketing", i:"📣", c:"#E8962E",  cra:"Line 8520"},
    {id:"bank_fees",      l:"Bank & Interest Charges", i:"🏦", c:"#37474F",  cra:"Line 8710"},
    {id:"medical",        l:"Medical / Drug Plan",     i:"💊", c:"#D32F2F",  cra:"Line 9270"},
    {id:"other",          l:"Other Operating",         i:"📦", c:C.textMed, cra:"Line 9270"},
  ];
  const HIGH_FUEL_THRESHOLD = 300;

  // ── Dark mode color palette ──
  const DM = {
    pageBg:   darkMode ? "#070B14" : "#F5F6F8",
    cardBg:   darkMode ? "#0D1523" : "#FFFFFF",
    cardBg2:  darkMode ? "#111C2E" : "#F8FAFC",
    border:   darkMode ? "rgba(255,255,255,0.07)" : "#E2E2E2",
    text:     darkMode ? "#F0F0F0" : "#1A1A1A",
    textMed:  darkMode ? "rgba(255,255,255,0.5)" : "#4B5563",
    textMut:  darkMode ? "rgba(255,255,255,0.35)" : "#6B7280",
    heroBg:   darkMode ? "#070B14" : "#F5F6F8",
    inputBg:  darkMode ? "#111C2E" : "#FFFFFF",
    rowAlt:   darkMode ? "#0D1929" : "#F8FAFC",
    greenBg:  darkMode ? "rgba(34,197,94,0.1)"  : "#E8F5E9",
    redBg:    darkMode ? "rgba(239,68,68,0.1)"  : "#FFF5F5",
    amberBg:  darkMode ? "rgba(232,150,46,0.1)" : "#F5F6F8",
    amberBg2: darkMode ? "rgba(232,150,46,0.06)": "#FFF3EB",
    divider:  darkMode ? "rgba(59,130,246,0.1)": "#F0F0F0",
  };

  const [expenses,setExpenses]=useState([]);
  const [expView,setExpView]=useState("all");
  const [expPostedFilter,setExpPostedFilter]=useState("all"); // owner-only: "all"|"owner"|"driver"
  const [showAdd,setShowAdd]=useState(false);
  const [form,setForm]=useState({amount:"",category:CATS[0].id,merchant:"",note:"",date:todayStr(),receipt:"",litres:"",pricePerLitre:"",expenseType:"personal"});
  const [receiptPreview,setReceiptPreview]=useState(null);
  const [fuelLogByDriver, setFuelLogByDriver] = useState({});

  useEffect(() => {
    if (!isOwner) return;
    const fetchFuelLogs = async () => {
      try {
        // Strategy 1: batch OR query across all driver UIDs (works if RLS allows fleet-owner reads)
        let all = await sbGetFleetFuelLogs(session.uid);
        // Strategy 2: fallback per-driver loop (also works when batch returns empty due to RLS)
        if (all.length === 0) {
          const own = await sbGetFuelLog(session.uid);
          const fleetDrivers = [];
          all = own.map(f => ({ ...f, driverName: session.fullName || session.name || "Owner" }));
          if (fleetDrivers) {
            for (const d of fleetDrivers) {
              try {
                const logs = await sbGetFuelLog(d.driver_uid);
                all = [...all, ...logs.map(f => ({ ...f, driverName: d.driver_name || "Driver" }))];
              } catch(e2) {}
            }
          }
        }
        // Group by driver name
        const grouped = all.reduce((acc, f) => {
          const name = f.driverName || "Unknown";
          if (!acc[name]) acc[name] = { name, total: 0, loads: 0, litres: 0, entries: [] };
          acc[name].total += Number(f.total || 0);
          acc[name].loads += 1;
          acc[name].litres += Number(f.litres || 0);
          acc[name].entries.push(f);
          return acc;
        }, {});
        setFuelLogByDriver(grouped);
      } catch(e) {}
    };
    fetchFuelLogs();
  }, [session.uid, isOwner]);
  const [alerts,setAlerts]=useState([]);
  const [editingId,setEditingId]=useState(null);
  const [scanning,setScanning]=useState(false);
  const [scanError,setScanError]=useState("");
  const [viewReceiptUrl,setViewReceiptUrl]=useState(null);
  const [selectedExpense,setSelectedExpense]=useState(null);
  const [detailImgErr,setDetailImgErr]=useState(false);

  // Merge localStorage receipts into DB expenses (for old entries saved before cloud storage)
  const mergeLocalReceipts = (dbExpenses, uid) => {
    try {
      const local = getStored(expensesKey(uid));
      const localMap = {};
      local.forEach(e => { if (e.receipt && e.receipt.startsWith("data:")) localMap[e.id] = e.receipt; });
      return dbExpenses.map(e => ({
        ...e,
        receipt: e.receipt || localMap[e.id] || null,
      }));
    } catch { return dbExpenses; }
  };

  useEffect(()=>{
    if (isOwner) {
      sbGetExpenses(session.uid).then(async data => {
        let all = data.length > 0 ? mergeLocalReceipts(data, session.uid) : getStored(expensesKey(session.uid));
        // Fetch business expenses from fleet drivers.
        // Uses two strategies: direct multi-user query (works if RLS allows fleet-owner reads)
        // AND a fallback loop per driver. Both are deduplicated below.
        try {
          // Strategy 1: batch fetch using OR filter across all driver UIDs
          const fleetBizExps = await sbGetFleetDriverBusinessExpenses(session.uid);
          all = [...all, ...fleetBizExps];

          // Strategy 2a: batch fuel log fetch across all driver UIDs + owner
          const fleetFuelLogs = await sbGetFleetFuelLogs(session.uid);
          fleetFuelLogs.forEach(f => {
            all.push({
              id: `fuellog-${f.id}`, category:"fuel",
              amount: Number(f.total||0),
              merchant: `⛽ ${f.truck_number||"Truck"} · ${f.driverName||"Driver"}`,
              note: `${f.litres}L @ $${Number(f.price_per_litre||0).toFixed(3)}/L${f.location?` · ${f.location}`:""}`,
              date: f.date || todayStr(),
              source:"fuel_log", ownerExpense:true,
              taxCategory:"Line 9220", taxLabel:"Fuel & Oil",
              driverName: f.driverName||"Driver",
            });
          });

          // Strategy 2b: loop per driver (fallback when batch returns empty due to RLS)
          const fleetDrivers = [];
          if (fleetDrivers && fleetDrivers.length > 0) {
            for (const d of fleetDrivers) {
              try {
                const driverExps = await sbGetExpenses(d.driver_uid);
                const bizAndFuelExps = driverExps.filter(e => e.ownerExpense || e.expenseType === "business" || e.source === "driver_business" || e.category === "fuel");
                all = [...all, ...bizAndFuelExps];
              } catch(e2) {}
              // Fallback: per-driver fuel log fetch (when batch returned nothing)
              if (fleetFuelLogs.length === 0) {
                try {
                  const fuelLogs = await sbGetFuelLog(d.driver_uid);
                  fuelLogs.forEach(f => {
                    all.push({
                      id: `fuellog-${f.id}`, category:"fuel",
                      amount: Number(f.total||0),
                      merchant: `⛽ ${f.truck_number||"Truck"} · ${d.driver_name||"Driver"}`,
                      note: `${f.litres}L @ $${Number(f.price_per_litre||0).toFixed(3)}/L${f.location?` · ${f.location}`:""}`,
                      date: f.date || todayStr(),
                      source:"fuel_log", ownerExpense:true,
                      taxCategory:"Line 9220", taxLabel:"Fuel & Oil",
                      driverName: d.driver_name||"Driver",
                    });
                  });
                } catch(e2) {}
              }
            }
          }
          // Owner's own fuel logs (fallback when batch returned nothing)
          if (fleetFuelLogs.length === 0) {
            const ownFuelLogs = await sbGetFuelLog(session.uid);
            ownFuelLogs.forEach(f => {
              all.push({
                id: `fuellog-${f.id}`, category:"fuel",
                amount: Number(f.total||0),
                merchant: `⛽ ${f.truck_number||"Truck"} · Owner`,
                note: `${f.litres}L @ $${Number(f.price_per_litre||0).toFixed(3)}/L${f.location?` · ${f.location}`:""}`,
                date: f.date || todayStr(),
                source:"fuel_log", ownerExpense:true,
                taxCategory:"Line 9220", taxLabel:"Fuel & Oil",
                driverName: session.fullName || session.name || "Owner",
              });
            });
          }
        } catch(e) {}
        // Deduplicate by base ID — strip "mirror-" prefix so a mirrored copy and its
        // original don't both appear. First occurrence wins (owner's own row entries come first).
        const seen = new Set();
        const deduped = all.filter(e => {
          const baseId = (e.id || "").replace(/^mirror-/, "");
          if (seen.has(baseId)) return false;
          seen.add(baseId);
          return true;
        });
        setExpenses(deduped);
      }).catch(()=>setExpenses(getStored(expensesKey(session.uid))));
    } else {
      sbGetExpenses(session.uid).then(data=>{
        if(data.length>0) setExpenses(mergeLocalReceipts(data, session.uid));
        else setExpenses(getStored(expensesKey(session.uid)));
      }).catch(()=>setExpenses(getStored(expensesKey(session.uid))));
    }
  },[session.uid, isOwner]);

  // ── Admin-triggered expense cleanup executor ──────────────────────────────
  // Admin cannot delete driver rows directly (RLS). Instead, admin inserts an
  // app_notification with type="expense_cleanup" targeted at the driver UID,
  // with expense IDs as JSON in the message field. This effect runs on load
  // for the driver and self-executes the deletes using their own session.
  useEffect(() => {
    if (!session?.uid || isOwner) return;
    (async () => {
      try {
        const { data: cmds } = await sb
          .from("app_notifications")
          .select("id, message")
          .eq("type", "expense_cleanup")
          .eq("target", "specific")
          .eq("target_uid", session.uid)
          .eq("active", true);
        if (!cmds || cmds.length === 0) return;

        const allIdsToDelete = new Set();
        for (const cmd of cmds) {
          try {
            const ids = JSON.parse(cmd.message);
            if (!Array.isArray(ids)) continue;
            ids.forEach(id => allIdsToDelete.add(id));
          } catch(e2) {}
        }

        if (allIdsToDelete.size === 0) return;

        // Hard-delete each expense row using driver's own session (RLS allows this)
        for (const expId of allIdsToDelete) {
          try {
            await sb.from("expenses").delete().eq("id", expId).eq("user_id", session.uid);
          } catch(e2) {}
        }

        // Purge from React state
        setExpenses(prev => prev.filter(e => !allIdsToDelete.has(e.id)));

        // Purge from localStorage so they don't re-appear from cache on next render
        try {
          const lsKey = expensesKey(session.uid);
          const stored = JSON.parse(localStorage.getItem(lsKey) || "[]");
          const pruned = stored.filter(e => !allIdsToDelete.has(e.id));
          localStorage.setItem(lsKey, JSON.stringify(pruned));
        } catch(e2) {}

        // Mark all cleanup commands as executed
        for (const cmd of cmds) {
          try {
            await sb.from("app_notifications").update({ active: false }).eq("id", cmd.id);
          } catch(e2) {}
        }
      } catch(e) {}
    })();
  }, [session.uid, isOwner]);

  // Auto-detect high fuel alerts from loads
  useEffect(()=>{
    if(!isOwner) return;
    const highFuel = allLoads.filter(l=>Number(l.fuelTotal)>HIGH_FUEL_THRESHOLD).map(l=>({
      id:l.id, driver:l.driverFullName||"Driver", amount:Number(l.fuelTotal),
      location:l.location||"Load", date:l.date, tmw:l.tmwLoadNumber
    }));
    setAlerts(highFuel);
  },[allLoads, isOwner]);

  const save=(arr)=>{
    setExpenses(arr);
    try {
      localStorage.setItem(expensesKey(session.uid),JSON.stringify(arr));
    } catch(e) {
      try {
        const stripped=arr.map(({receipt,...rest})=>rest);
        localStorage.setItem(expensesKey(session.uid),JSON.stringify(stripped));
      } catch(e2){ console.error("localStorage full",e2); }
    }
    // Do NOT bulk-upsert — individual add/edit/delete call Supabase directly.
  };

  const scanReceiptWithAI = async (base64Data) => {
    if (!base64Data || !base64Data.startsWith("data:image")) return;
    setScanning(true);
    setScanError("");
    try {
      const base64 = base64Data.split(",")[1];
      const mediaType = base64Data.split(";")[0].split(":")[1];
      const response = await fetch("/api/scan-receipt", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image: base64, mediaType })
      });
      const parsed = await response.json();
      if (parsed.error) throw new Error(parsed.error);
      setForm(f => ({
        ...f,
        amount:        parsed.amount        ? String(parsed.amount)                              : f.amount,
        merchant:      parsed.merchant      || f.merchant,
        category:      parsed.category      || f.category,
        date:          parsed.date          || f.date,
        litres:        parsed.litres != null ? String(parsed.litres)                             : f.litres,
        pricePerLitre: parsed.pricePerLitre != null ? String(Number(parsed.pricePerLitre).toFixed(3)) : f.pricePerLitre,
        note:          parsed.note          || f.note,
      }));
    } catch(e) {
      setScanError("Could not read receipt — please fill in manually.");
    }
    setScanning(false);
  };


  const handleReceipt=(e)=>{
    const file=e.target.files[0];
    if(!file) return;
    const reader=new FileReader();
    reader.onload=(ev)=>{
      const base64=ev.target.result;
      setForm(f=>({...f,receipt:base64}));
      setReceiptPreview(base64);
      // Trigger AI scan if it's an image
      if(base64.startsWith("data:image")) {
        scanReceiptWithAI(base64);
      } else {
        // Fallback: auto-categorize based on file name
        const name=file.name.toLowerCase();
        if(name.includes("shell")||name.includes("petro")||name.includes("esso")||name.includes("fuel")||name.includes("gas"))
          setForm(f=>({...f,category:"fuel",merchant:name.includes("shell")?"Shell":name.includes("petro")?"Petro-Canada":name.includes("esso")?"Esso":"Gas Station"}));
        else if(name.includes("hotel")||name.includes("inn")||name.includes("motel"))
          setForm(f=>({...f,category:"lodging"}));
        else if(name.includes("food")||name.includes("restaurant")||name.includes("meal"))
          setForm(f=>({...f,category:"meals"}));
      }
    };
    reader.readAsDataURL(file);
  };

  const add=()=>{
    if(!form.amount||isNaN(parseFloat(form.amount))) return;
    const cat=CATS.find(c=>c.id===form.category)||CATS[CATS.length-1];
    if(editingId){
      const updated=expenses.map(e=>e.id===editingId?{...e,...form,amount:parseFloat(form.amount),taxCategory:cat.cra,taxLabel:cat.l}:e);
      save(updated);
      if(session?.supabase) sbSaveExpense(updated.find(e=>e.id===editingId),session.uid).catch(console.error);
      setEditingId(null);
    } else {
      const isSplit = (form.expenseType || "personal") === "split";
      const newExp = {
        ...form,
        amount: parseFloat(form.amount),
        litres: form.litres ? Number(form.litres) : undefined,
        pricePerLitre: form.pricePerLitre ? Number(form.pricePerLitre) : undefined,
        id: Date.now().toString(),
        taxCategory: cat.cra,
        taxLabel: cat.l,
        expenseType: form.expenseType || "personal",
        // split: personal side = 50% only; business = false (not a pure business expense)
        ownerExpense: form.expenseType === "business",
        splitPct: isSplit ? 0.5 : undefined,
        user_id: session.uid,
      };
      save([newExp, ...expenses]);
      if (session?.supabase) sbSaveExpense(newExp, session.uid).catch(console.error);
    }
    setForm({amount:"",category:CATS[0].id,merchant:"",note:"",date:todayStr(),receipt:"",litres:"",pricePerLitre:"",expenseType:"personal"});
    setReceiptPreview(null);
    setShowAdd(false);
  };

  // Each user sees only their own expenses.
  // Owner: also include auto-generated entries (fuel_log, load fuel) without user_id.
  const visibleExpenses = expenses.filter(e => {
    if (e.deleted) return false;
    if (e.source === "load") return false;
    if (e.user_id === session.uid) return true;
    if (isOwner && !e.user_id) return true;
    return false;
  });
  // Driver's submitted business expenses (shown separately)
  const driverBusinessExpenses = !isOwner
    ? expenses.filter(e => !e.deleted && e.user_id === session.uid && (e.ownerExpense || e.expenseType === "business"))
    : [];
  // Driver personal expenses only
  const driverPersonalExpenses = !isOwner
    ? expenses.filter(e => !e.deleted && e.source !== "load" && e.user_id === session.uid && !e.ownerExpense && e.expenseType !== "business" && e.source !== "driver_split_business")
    : [];
  // For split expenses: driver's personal side = 50% of original amount
  // (the other 50% was already mirrored to the owner as a business expense)
  const expAmount = (e) => {
    if (e.expenseType === "split" && !isOwner) return Number(e.amount||0) * (Number(e.splitPct||0.5));
    return Number(e.amount||0);
  };
  // Total and fuel total must match what is actually shown — using all expenses
  // for drivers caused $330 total with "No expenses yet" when all entries were business.
  const total = visibleExpenses.reduce((s,e)=>s+expAmount(e),0);
  const fuelExps = visibleExpenses.filter(e=>e.category==="fuel");
  const fuelTotal = fuelExps.reduce((s,e)=>s+expAmount(e),0);
  const byCat=CATS.map(c=>({...c,total:visibleExpenses.filter(e=>e.category===c.id).reduce((s,e)=>s+expAmount(e),0)})).filter(c=>c.total>0);

  // For owner: fuel by driver from loads
  // Fuel by driver — combine load fuel + fuel log entries
  const fuelByDriver = isOwner ? (() => {
    const acc = { ...fuelLogByDriver };
    // Also add load-based fuel
    allLoads.filter(l=>Number(l.fuelTotal)>0).forEach(l=>{
      const name = l.driverFullName || l.driverName || "Unknown";
      if(!acc[name]) acc[name]={name,total:0,loads:0,litres:0,entries:[]};
      acc[name].total+=Number(l.fuelTotal)||0;
      acc[name].loads+=1;
      acc[name].litres+=Number(l.fuelLitres)||0;
    });
    return acc;
  })() : {};

  // ── INLINE EXPENSE DETAIL PAGE (replaces modal — no iOS scroll issues) ──
  if (selectedExpense) {
    const exp = selectedExpense;
    const cat = CATS.find(c => c.id === exp.category) || CATS[CATS.length - 1];
    const receiptSrc = exp.receipt && exp.receipt.startsWith("data:") ? exp.receipt : exp.receiptUrl || null;
    const isPdf = receiptSrc && (receiptSrc.startsWith("data:application/pdf") || receiptSrc.toLowerCase().includes(".pdf"));
    const canEdit = !exp.source && (exp.user_id === session.uid || (!exp.user_id && !exp.ownerExpense));
    const canDelete = exp.source === "fuel_log" || (!exp.source && (exp.user_id === session.uid || !exp.user_id));
    const handleEdit = canEdit ? () => {
      setForm({amount:String(exp.amount),category:exp.category,merchant:exp.merchant||"",note:exp.note||exp.description||"",date:exp.date,receipt:exp.receipt||""});
      setEditingId(exp.id);
      setReceiptPreview(exp.receipt||null);
      setShowAdd(true);
      setSelectedExpense(null);
    } : null;
    const handleDelete = canDelete ? async () => {
      if (exp.source === "fuel_log") {
        if (!window.confirm("Remove this fuel log entry from expenses?")) return;
        const realId = exp.id.replace("fuellog-", "");
        await sbDeleteFuelEntry(realId).catch(console.error);
        setExpenses(prev => prev.filter(x => x.id !== exp.id));
        setSelectedExpense(null);
      } else {
        const isBiz = exp.ownerExpense || exp.expenseType === "business";
        const confirmMsg = isBiz
          ? "Delete this business expense? It will also be removed from your owner's account."
          : "Delete this expense? It can be recovered by admin within 90 days.";
        if (!window.confirm(confirmMsg)) return;
        const softDeleted = { ...exp, deleted: true, deleted_at: new Date().toISOString() };
        const updated = expenses.filter(x => x.id !== exp.id);
        setExpenses(updated);
        try { localStorage.setItem(expensesKey(session.uid), JSON.stringify(updated)); } catch(e) {}
        if (session?.supabase) {
          sbSoftDeleteExpense(softDeleted, session.uid).catch(console.error);
        }
        setSelectedExpense(null);
      }
    } : null;

    return (
      <div className="slt-page" style={{background:DM.pageBg,color:C.textDark}}>
        {/* Navy hero header — no back button here */}
        <div style={{
  background: darkMode
    ? "linear-gradient(135deg,#1A1A1A,#252525)"
    : "linear-gradient(135deg,#1C2B4A,#243655)",
  padding: "24px 18px 20px",
  borderBottomLeftRadius: 20,
  borderBottomRightRadius: 20
}}>
          <div style={{ display:"flex", alignItems:"center", gap:14, padding:"20px 18px 18px" }}>
            <div>
              <div style={{ color:"rgba(255,255,255,0.65)", fontSize:12, fontWeight:800, textTransform:"uppercase", letterSpacing:1 }}>{cat.i} {cat.l}</div>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:38, color:"#fff", lineHeight:1.1 }}>{fmtC(exp.amount)}</div>
            </div>
          </div>
        </div>

        <div className="slt-container" style={{ paddingTop:16 }}>
          {/* Detail rows */}
          <div className="slt-card" style={{ marginBottom:14 }}>
            {/* Posted by — always show for owner */}
            {isOwner && (
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:`1px solid ${C.border}` }}>
                <span style={{ fontSize:12, color:C.textDarkMed, fontWeight:700 }}>POSTED BY</span>
                <span style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, fontWeight:800,
                  color: (exp.user_id && exp.user_id !== session.uid) || exp.source==="driver_business" || exp.source==="fuel_log" ? "#B45309" : "#1C2B4A" }}>
                  {(exp.user_id && exp.user_id !== session.uid) || exp.source==="driver_business" || exp.source==="fuel_log"
                    ? <>👤 {exp.driverName || "Driver"}</>
                    : <>🏢 You (Owner)</>}
                </span>
              </div>
            )}
            {exp.merchant && (
              <div style={{ display:"flex", justifyContent:"space-between", padding:"10px 0", borderBottom:`1px solid ${C.border}` }}>
                <span style={{ fontSize:12, color:C.textDarkMed, fontWeight:700 }}>MERCHANT</span>
                <span style={{ fontSize:14, fontWeight:800 }}>{exp.merchant}</span>
              </div>
            )}
            <div style={{ display:"flex", justifyContent:"space-between", padding:"10px 0", borderBottom:`1px solid ${C.border}` }}>
              <span style={{ fontSize:12, color:C.textDarkMed, fontWeight:700 }}>DATE</span>
              <span style={{ fontSize:14, fontWeight:800 }}>{exp.date}</span>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", padding:"10px 0", borderBottom:`1px solid ${C.border}` }}>
              <span style={{ fontSize:12, color:C.textDarkMed, fontWeight:700 }}>TAX LINE</span>
              <span style={{ fontSize:12, fontWeight:800, color:cat.c, background:cat.c+"18", borderRadius:6, padding:"3px 10px" }}>{exp.taxCategory || cat.cra}</span>
            </div>
            {(exp.litres || exp.pricePerLitre) && (
              <div style={{ display:"flex", justifyContent:"space-between", padding:"10px 0", borderBottom:`1px solid ${C.border}` }}>
                <span style={{ fontSize:12, color:C.textDarkMed, fontWeight:700 }}>FUEL</span>
                <span style={{ fontSize:14, fontWeight:800 }}>{exp.litres}L @ ${Number(exp.pricePerLitre||0).toFixed(3)}/L</span>
              </div>
            )}
            {(exp.note || exp.description) && (
              <div style={{ padding:"10px 0", borderBottom:`1px solid ${C.border}` }}>
                <span style={{ fontSize:12, color:C.textDarkMed, fontWeight:700, display:"block", marginBottom:4 }}>NOTE</span>
                <span style={{ fontSize:13, color:"#0B1120" }}>{exp.description || exp.note}</span>
              </div>
            )}
            {exp.driverName && (
              <div style={{ display:"flex", justifyContent:"space-between", padding:"10px 0", borderBottom:`1px solid ${C.border}` }}>
                <span style={{ fontSize:12, color:C.textDarkMed, fontWeight:700 }}>DRIVER</span>
                <span style={{ fontSize:13, fontWeight:800, color:"#E8962E" }}>👤 {exp.driverName}</span>
              </div>
            )}
            {exp.source === "fuel_log" && <div style={{ background:"#F5F6F8", borderRadius:8, padding:"8px 12px", marginTop:8, fontSize:12, color:"#E8962E", fontWeight:700 }}>⛽ From Fuel Log</div>}
            {exp.source === "load" && <div style={{ background:DM.amberBg2, borderRadius:8, padding:"8px 12px", marginTop:8, fontSize:12, color:"#E8962E", fontWeight:700 }}>🔗 Auto-logged from Load</div>}
          </div>

          {/* Attachment */}
          <div className="slt-card" style={{ marginBottom:14 }}>
            <div style={{ fontSize:12, color:C.textDarkMed, fontWeight:800, textTransform:"uppercase", letterSpacing:1, marginBottom:10 }}>📎 Attachment</div>
            {(receiptSrc && !detailImgErr) ? (
              isPdf ? (
                <a href={receiptSrc} target="_blank" rel="noopener noreferrer"
                  style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 14px", background:"#F5F6F8", borderRadius:12, border:"1.5px solid #F5F6F8", textDecoration:"none" }}>
                  <span style={{ fontSize:26 }}>📄</span>
                  <div><div style={{ fontWeight:800, fontSize:13, color:"#E8962E" }}>PDF Receipt</div><div style={{ fontSize:12, color:"#4B5563" }}>Tap to open</div></div>
                  <span style={{ marginLeft:"auto", fontSize:16, color:"#E8962E" }}>↗</span>
                </a>
              ) : (
                <div>
                  <img src={receiptSrc} alt="Receipt" onError={()=>setDetailImgErr(true)} style={{ width:"100%", borderRadius:12, objectFit:"contain", maxHeight:300, border:"1px solid #e0e0e0", display:"block" }} />
                  <a href={receiptSrc} download="receipt.jpg" style={{ display:"block", marginTop:10, textAlign:"center", background:"#E8962E", color:"#fff", padding:"11px 0", borderRadius:10, fontWeight:700, fontSize:13, textDecoration:"none" }}>⬇️ Download Receipt</a>
                </div>
              )
            ) : (
              <div style={{ display:"flex", alignItems:"center", gap:10, padding:"16px 0", color:"#9CA3AF", fontSize:13 }}>
                <span style={{ fontSize:24 }}>🧾</span><span>No attachment saved</span>
              </div>
            )}
          </div>

          {/* Back / Delete / Edit buttons — always visible at bottom */}
          <div style={{ display:"flex", gap:10, marginBottom:24 }}>
            <button onClick={() => setSelectedExpense(null)}
              style={{ flex:1, padding:"16px 0", borderRadius:14, border:"2px solid #1C2B4A", background:"transparent", color:"#1C2B4A", fontWeight:900, fontSize:15, cursor:"pointer", fontFamily:"inherit" }}>
              ← Back
            </button>
            {handleDelete && (
              <button onClick={handleDelete}
                style={{ flex:1, padding:"16px 0", borderRadius:14, border:"2px solid #ef5350", background:DM.redBg, color:"#ef5350", fontWeight:900, fontSize:15, cursor:"pointer", fontFamily:"inherit" }}>
                🗑 Delete
              </button>
            )}
            {handleEdit && (
              <button onClick={handleEdit}
                style={{ flex:1.5, padding:"16px 0", borderRadius:14, border:"none", background:"#E8962E", color:"#fff", fontWeight:900, fontSize:15, cursor:"pointer", fontFamily:"inherit" }}>
                ✏️ Edit
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="slt-page" style={{background:DM.pageBg,color:C.textDark}}>
      {goBack && <BackButton onBack={goBack} label="Back" />}
      {/* ── EXPENSES HERO CARD ── */}
      <div style={{
        margin:"0 0 0 0",
        background: darkMode ? "linear-gradient(135deg, #C97820 0%, #E8962E 80%)" : "linear-gradient(135deg, #E8962E 0%, #F5B660 100%)",
        padding:"28px 20px 20px",
        boxShadow: "0 4px 16px rgba(232,150,46,0.25)",
      }}>
        <div style={{maxWidth:600,margin:"0 auto"}}>
          <div style={{fontSize:12,fontWeight:800,color:"rgba(28,28,30,0.6)",textTransform:"uppercase",letterSpacing:2,marginBottom:6}}>
            TOTAL EXPENSES — {new Date().toLocaleString("en-CA",{month:"long"}).toUpperCase()}
          </div>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:54,fontWeight:900,color:"#1C1C1E",lineHeight:1,marginBottom:4}}>
            {fmtC(total)}
          </div>
          <div style={{fontSize:13,color:"rgba(28,28,30,0.55)",marginBottom:16}}>
            {visibleExpenses.length} transaction{visibleExpenses.length!==1?"s":""} this month
          </div>
          {byCat.length>0&&(
            <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
              {byCat.slice(0,4).map(c=>(
                <div key={c.id} style={{background:"rgba(28,28,30,0.1)",borderRadius:20,padding:"5px 12px",fontSize:13,fontWeight:700,color:"rgba(28,28,30,0.7)",display:"flex",alignItems:"center",gap:5}}>
                  <span>{c.i}</span>
                  <span>{c.l.split(" ")[0]} {fmtC(c.total)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="slt-container">

        {/* ── HIGH FUEL ALERTS (owner only) ── */}
        {isOwner&&alerts.length>0&&(
          <div style={{marginBottom:16}}>
            {alerts.map(a=>(
              <div key={a.id} style={{background:"#FFF3E0",border:"2px solid #E8962E",borderRadius:12,padding:"12px 16px",marginBottom:8,display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontSize:22}}>🚨</span>
                <div style={{flex:1}}>
                  <div style={{fontWeight:800,color:"#E8962E",fontSize:13}}>High Fuel Alert — {a.driver}</div>
                  <div style={{fontSize:12,color:"#BF360C"}}>{a.location} · {a.date}{a.tmw?` · TMW #${a.tmw}`:""} · {fmtC(a.amount)}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── VIEW TABS ── */}
        {isOwner&&(
          <div style={{marginBottom:16}}>
            {/* Main view tabs */}
            <div style={{display:"flex",gap:8,marginBottom:10}}>
              {[["all","All Expenses"],["fuel","⛽ Fuel by Driver"]].map(([v,l])=>(
                <button key={v} onClick={()=>setExpView(v)} className="slt-btn-secondary"
                  style={{flex:1,background:expView===v?C.blue:"#fff",color:expView===v?"#fff":C.textMed,borderColor:expView===v?C.blue:C.border,padding:"9px 8px",fontSize:13}}>{l}</button>
              ))}
            </div>
            {/* Posted-by filter pills — only in All Expenses view */}
            {expView==="all"&&(
              <div style={{display:"flex",gap:6,overflowX:"auto",scrollbarWidth:"none",paddingBottom:2}}>
                {(()=>{
                  const ownerExpCount = visibleExpenses.filter(e=>e.user_id===session.uid||(!e.user_id&&e.ownerExpense)||(!e.driverName&&e.source!=="driver_business")).length;
                  const driverExpCount = visibleExpenses.filter(e=>e.user_id&&e.user_id!==session.uid||e.source==="driver_business"||e.source==="fuel_log").length;
                  return [
                    {v:"all",     l:"👥 All",         count:visibleExpenses.length},
                    {v:"owner",   l:"🏢 My Expenses",  count:ownerExpCount},
                    {v:"driver",  l:"👤 Driver Posted", count:driverExpCount},
                  ].map(({v,l,count})=>{
                    const active = (expView==="all") && (expPostedFilter||"all")===v;
                    return(
                      <button key={v}
                        onClick={()=>setExpPostedFilter(v)}
                        style={{padding:"7px 13px",borderRadius:20,border:"none",cursor:"pointer",fontWeight:800,fontSize:11,whiteSpace:"nowrap",fontFamily:"inherit",background:active?"#1C2B4A":"rgba(0,0,0,0.05)",color:active?"#fff":"#6B7280",transition:"all 0.15s"}}>
                        {l}{count>0?` (${count})`:""}
                      </button>
                    );
                  });
                })()}
              </div>
            )}
          </div>
        )}

        {/* ── FUEL BY DRIVER VIEW (owner only) ── */}
        {isOwner&&expView==="fuel"&&(
          <div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:17,marginBottom:12}}>⛽ Fuel Expenses by Driver</div>
            {Object.keys(fuelByDriver).length===0
              ?<div className="slt-card" style={{textAlign:"center",padding:40}}><div style={{fontSize:38,marginBottom:8}}>⛽</div><div style={{color:C.textDarkMed}}>No fuel logged yet</div></div>
              :Object.values(fuelByDriver).sort((a,b)=>b.total-a.total).map(d=>(
                <div key={d.name} className="slt-card" style={{borderLeft:`4px solid ${C.orange}`,padding:"14px 18px",marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:16}}>{d.name}</div>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:20,color:C.orange}}>{fmtC(d.total)}</div>
                  </div>
                  <div style={{display:"flex",gap:20}}>
                    <div><div style={{fontSize:13,color:C.textDarkMut,fontWeight:700}}>LOADS</div><div style={{fontSize:16,fontWeight:800}}>{d.loads}</div></div>
                    <div><div style={{fontSize:13,color:C.textDarkMut,fontWeight:700}}>LITRES</div><div style={{fontSize:16,fontWeight:800}}>{d.litres.toFixed(0)}L</div></div>
                    <div><div style={{fontSize:13,color:C.textDarkMut,fontWeight:700}}>AVG/LOAD</div><div style={{fontSize:16,fontWeight:800}}>{fmtC(d.total/d.loads)}</div></div>
                    <div><div style={{fontSize:13,color:C.textDarkMut,fontWeight:700}}>TAX LINE</div><div style={{fontSize:12,fontWeight:700,color:C.orange}}>CRA 9220</div></div>
                  </div>
                  {allLoads.filter(l=>(l.driverFullName||"Unknown")===d.name&&Number(l.fuelTotal)>0).map(l=>(
                    <div key={l.id} style={{marginTop:8,padding:"8px 12px",background:C.offWhite,borderRadius:8,fontSize:12}}>
                      <div style={{display:"flex",justifyContent:"space-between"}}>
                        <span style={{color:C.textDarkMed}}>{l.date} · {l.location||"—"}{l.tmwLoadNumber?` · TMW #${l.tmwLoadNumber}`:""}</span>
                        <span style={{fontWeight:800,color:Number(l.fuelTotal)>HIGH_FUEL_THRESHOLD?C.red:C.textMed}}>
                          {fmtC(Number(l.fuelTotal))}{Number(l.fuelTotal)>HIGH_FUEL_THRESHOLD?" 🚨":""}
                        </span>
                      </div>
                      {l.fuelLitres&&<div style={{color:C.textDarkMut,marginTop:2}}>{l.fuelLitres}L @ ${Number(l.fuelPricePerLitre||0).toFixed(3)}/L</div>}
                    </div>
                  ))}
                </div>
              ))
            }
          </div>
        )}

        {/* ── ALL EXPENSES VIEW ── */}
        {expView==="all"&&<>
          {/* ── ADD EXPENSE BUTTON ── */}
          <button
            onClick={()=>setShowAdd(!showAdd)}
            style={{
              display:"block", width:"100%", padding:"16px 0",
              borderRadius:14, border:"none", cursor:"pointer", marginBottom:16,
              background: showAdd ? "#E2E2E2" : (darkMode ? "#E8962E" : "#1C2B4A"),
              color: showAdd ? "#4B5563" : "#FFFFFF",
              fontFamily:"'Barlow Condensed',sans-serif",
              fontSize:18, fontWeight:900, letterSpacing:1.5,
              textTransform:"uppercase",
              boxShadow: showAdd ? "none" : (darkMode ? "0 4px 14px rgba(232,150,46,0.35)" : "0 4px 14px rgba(28,43,74,0.3)"),
            }}>
            {showAdd ? "✕ CANCEL" : "+ ADD EXPENSE"}
          </button>

          {byCat.length>0&&<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>{byCat.map(c=><div key={c.id} className="slt-card-sm" style={{display:"flex",alignItems:"center",gap:10,padding:"12px 14px",borderRadius:14}}><span style={{fontSize:22}}>{c.i}</span><div><div style={{fontSize:12,color:C.textDarkMed,fontWeight:700}}>{c.l}</div><div style={{fontSize:18,fontWeight:900,color:c.c,fontFamily:"'Barlow Condensed',sans-serif"}}>{fmtC(c.total)}</div></div></div>)}</div>}

          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:17}}>All Expenses</span>
          </div>
          {/* ── Expense Search Bar ── */}
          <form onSubmit={e=>{e.preventDefault();setExpSearchQuery(expSearchInput);}} style={{display:"flex",gap:8,marginBottom:14}}>
            <input
              value={expSearchInput}
              onChange={e=>{setExpSearchInput(e.target.value);if(!e.target.value){setExpSearchQuery("");}}}
              placeholder="🔍 Search by merchant, category, date, amount…"
              style={{flex:1,padding:"10px 14px",borderRadius:10,border:"1.5px solid #d0d5dd",fontSize:13,fontFamily:"'Barlow',sans-serif",outline:"none"}}
            />
            <button type="submit"
              style={{padding:"10px 16px",borderRadius:10,background:"#E8962E",color:"#1C1C1E",border:"none",fontWeight:800,fontSize:13,cursor:"pointer",fontFamily:"'Barlow',sans-serif",whiteSpace:"nowrap"}}>
              Search
            </button>
            {expSearchQuery&&<button type="button" onClick={()=>{setExpSearchQuery("");setExpSearchInput("");}}
              style={{padding:"10px 12px",borderRadius:10,background:"#F5F6F8",color:"#666",border:"none",fontWeight:700,fontSize:13,cursor:"pointer"}}>✕</button>}
          </form>
          {expSearchQuery&&<div style={{fontSize:12,color:"#E8962E",fontWeight:700,marginBottom:10}}>🔍 "{expSearchQuery}"</div>}

          {showAdd&&<div className="slt-card" style={{border:`2px solid ${C.blue}`}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
              <div><label className="slt-label">Amount ($)</label><input type="number" step="0.01" value={form.amount} onChange={e=>setForm(f=>({...f,amount:e.target.value}))} className="slt-input" placeholder="0.00"/></div>
              <div><label className="slt-label" style={{display:"block",fontSize:14,fontWeight:900,color:"#E8962E",marginBottom:6,fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:0.5,textTransform:"uppercase"}}>Date</label><input type="date" value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value}))} className="slt-input"/></div>
            </div>
            <div style={{marginBottom:14}}>
                <label className="slt-label">Expense Type</label>
                {/* TruckSmarter-style Business / Personal / Split selector */}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginTop:6}}>
                  {[
                    {val:"personal", icon:"👤", label:"Personal",  desc:"Your tax"},
                    {val:"business", icon:"🏢", label:"Business",  desc:"Fleet cost"},
                    {val:"split",    icon:"⚖️", label:"Split",     desc:"50 / 50"},
                  ].map(({val,icon,label,desc})=>(
                    <button key={val} type="button" onClick={()=>setForm(f=>({...f,expenseType:val}))}
                      style={{
                        padding:"12px 8px", borderRadius:14, cursor:"pointer", textAlign:"center",
                        border:`2px solid ${form.expenseType===val?"#E8962E":DM.border}`,
                        background: form.expenseType===val ? (darkMode?"rgba(232,150,46,0.15)":"#FFF3EB") : "transparent",
                        transition:"all 0.15s",
                      }}>
                      <div style={{fontSize:22,marginBottom:4}}>{icon}</div>
                      <div style={{fontWeight:800,fontSize:12,color:form.expenseType===val?"#E8962E":DM.text,lineHeight:1.2}}>{label}</div>
                      <div style={{fontSize:10,color:DM.textMut,marginTop:2}}>{desc}</div>
                    </button>
                  ))}
                </div>
              </div>
            <div style={{marginBottom:12}}>
              <label className="slt-label">Category (Auto Tax Line)</label>
              {/* Quick-pick trucking categories (TruckSmarter-style) */}
              <div style={{display:"flex",flexWrap:"wrap",gap:6,margin:"8px 0 10px"}}>
                {[
                  {id:"fuel",           i:"⛽", l:"Fuel"},
                  {id:"maintenance",    i:"🔧", l:"Maintenance"},
                  {id:"meals",          i:"🍽", l:"Meals"},
                  {id:"safety",         i:"🦺", l:"Safety Gear"},
                  {id:"tools_supplies", i:"🧰", l:"Equipment"},
                  {id:"lodging",        i:"🏨", l:"Lodging"},
                ].map(c=>(
                  <button key={c.id} type="button" onClick={()=>setForm(f=>({...f,category:c.id}))}
                    style={{
                      display:"flex",alignItems:"center",gap:5,
                      padding:"6px 12px",borderRadius:20,
                      border:`1.5px solid ${form.category===c.id?"#E8962E":DM.border}`,
                      background: form.category===c.id?(darkMode?"rgba(232,150,46,0.15)":"#FFF3EB"):(darkMode?"rgba(255,255,255,0.05)":"#fff"),
                      color: form.category===c.id?"#E8962E":DM.textMed,
                      fontSize:12,fontWeight:700,cursor:"pointer",transition:"all 0.15s",
                    }}>
                    <span>{c.i}</span>{c.l}
                  </button>
                ))}
              </div>
              <select value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))} className="slt-input">
                {CATS.map(c=><option key={c.id} value={c.id}>{c.i} {c.l} — {c.cra}</option>)}
              </select>
            </div>
            <div style={{marginBottom:12}}><label className="slt-label">Merchant</label><input value={form.merchant} onChange={e=>setForm(f=>({...f,merchant:e.target.value}))} className="slt-input" placeholder="e.g. Shell"/></div>
            {form.category==="fuel"&&(
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
                <div><label className="slt-label">Litres Pumped</label><input type="number" step="0.1" value={form.litres} onChange={e=>{const l=e.target.value;setForm(f=>({...f,litres:l,amount:l&&f.pricePerLitre?(Number(l)*Number(f.pricePerLitre)).toFixed(2):f.amount}));}} className="slt-input" placeholder="e.g. 150"/></div>
                <div><label className="slt-label">Price Per Litre ($)</label><input type="number" step="0.001" value={form.pricePerLitre} onChange={e=>{const p=e.target.value;setForm(f=>({...f,pricePerLitre:p,amount:p&&f.litres?(Number(f.litres)*Number(p)).toFixed(2):f.amount}));}} className="slt-input" placeholder="e.g. 1.459"/></div>
              </div>
            )}
            <div style={{marginBottom:12}}><label className="slt-label">Note</label><input value={form.note} onChange={e=>setForm(f=>({...f,note:e.target.value}))} className="slt-input" placeholder="Details…"/></div>
            <div style={{marginBottom:16}}>
              <label className="slt-label">📎 Attach Receipt (auto-categorizes)</label>
              <div style={{display:"flex",gap:8,marginBottom:8}}>
                <label style={{flex:1,padding:"10px 14px",borderRadius:10,border:`1.5px solid ${C.border}`,background:C.offWhite,cursor:"pointer",textAlign:"center",fontSize:13,fontWeight:700,color:C.textDarkMed}}>
                  📁 Choose File
                  <input type="file" accept="image/*,application/pdf" onChange={handleReceipt} style={{display:"none"}}/>
                </label>
                <label style={{flex:1,padding:"10px 14px",borderRadius:10,border:`1.5px solid #E8962E`,background:C.blueLight,cursor:"pointer",textAlign:"center",fontSize:13,fontWeight:700,color:C.blue}}>
                  📷 Camera Scan
                  <input type="file" accept="image/*" capture="environment" onChange={handleReceipt} style={{display:"none"}}/>
                </label>
              </div>
              {receiptPreview&&receiptPreview.startsWith("data:image")&&(
                <div style={{marginTop:8,borderRadius:8,overflow:"hidden",border:`1px solid ${C.border}`,maxHeight:160,position:"relative"}}>
                  <img src={receiptPreview} alt="Receipt" style={{width:"100%",objectFit:"cover",maxHeight:160}}/>
                  {scanning&&(
                    <div style={{position:"absolute",inset:0,background:"rgba(36,59,110,0.85)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:8}}>
                      <div style={{fontSize:28}}>🤖</div>
                      <div style={{color:"#fff",fontWeight:700,fontSize:13}}>Reading receipt...</div>
                    </div>
                  )}
                </div>
              )}
              {scanError&&<div style={{marginTop:6,fontSize:12,color:C.red,fontWeight:600}}>⚠️ {scanError}</div>}
              {scanning&&!receiptPreview&&<div style={{marginTop:8,padding:"10px 14px",borderRadius:10,background:DM.amberBg,fontSize:13,fontWeight:600,color:"#E8962E"}}>🤖 Reading receipt with AI...</div>}
              {receiptPreview&&!receiptPreview.startsWith("data:image")&&(
                <div style={{marginTop:8,padding:"8px 12px",background:C.offWhite,borderRadius:8,fontSize:12,color:C.textDarkMed}}>📄 PDF receipt attached</div>
              )}
            </div>
            <div style={{background:DM.amberBg2,borderRadius:8,padding:"8px 12px",marginBottom:12,fontSize:12}}>
              <span style={{fontWeight:800,color:C.blue}}>Tax: </span>
              <span style={{color:C.blue}}>{CATS.find(c=>c.id===form.category)?.cra||"—"} — {CATS.find(c=>c.id===form.category)?.l||"—"}</span>
            </div>
            <button className="slt-btn-primary slt-btn-dark-text" style={{width:"100%"}} onClick={add}>{editingId?"Update Expense":"Save Expense"}</button>
          </div>}

          {/* Full screen receipt viewer */}
          {viewReceiptUrl && (
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.92)",zIndex:3000,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}} onClick={()=>setViewReceiptUrl(null)}>
              <div style={{position:"absolute",top:20,right:20}}>
                <button onClick={()=>setViewReceiptUrl(null)} style={{background:"rgba(255,255,255,.15)",border:"none",color:"#fff",borderRadius:20,padding:"8px 16px",fontSize:14,fontWeight:700,cursor:"pointer"}}>✕ Close</button>
              </div>
              <img src={viewReceiptUrl} alt="Receipt" style={{maxWidth:"95vw",maxHeight:"85vh",objectFit:"contain",borderRadius:12}} onClick={e=>e.stopPropagation()}/>
              <a href={viewReceiptUrl} download="receipt.jpg" style={{marginTop:16,background:"#E8962E",color:"#1C1C1E",padding:"10px 24px",borderRadius:20,textDecoration:"none",fontWeight:700,fontSize:14}}>⬇️ Download</a>
            </div>
          )}

          {/* Driver: show submitted business expenses section */}
          {!isOwner && driverBusinessExpenses.length > 0 && (
            <div className="slt-card" style={{marginBottom:12,border:"1.5px solid #1565C0",background:"#EFF6FF"}}>
              <div style={{fontWeight:800,fontSize:13,color:"#1565C0",marginBottom:8}}>🏢 Submitted to Owner ({driverBusinessExpenses.length})</div>
              <div style={{fontSize:12,color:"#4B5563",marginBottom:10}}>These expenses were reported to your fleet owner's books.</div>
              {driverBusinessExpenses.map(e=>{
                const cat=CATS.find(c=>c.id===e.category)||CATS[CATS.length-1];
                return(
                  <div key={e.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:"1px solid rgba(21,101,192,0.15)"}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:700,color:"#1A1A1A"}}>{cat.icon} {e.merchant||cat.l}</div>
                      <div style={{fontSize:11,color:"#6B7280"}}>{e.date} · {e.note||""}</div>
                    </div>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:16,fontWeight:900,color:"#1565C0"}}>{fmtC(e.amount)}</div>
                  </div>
                );
              })}
            </div>
          )}

          {(()=>{
            // Apply posted-by filter for owners
            let expFiltered = expSearchQuery
              ? visibleExpenses.filter(e=>{const q=expSearchQuery.toLowerCase();return[e.merchant,e.category,e.date,e.note,e.description,String(e.amount||""),e.taxLabel,e.driverName].join(" ").toLowerCase().includes(q);})
              : visibleExpenses;
            if (isOwner && expPostedFilter === "owner") {
              expFiltered = expFiltered.filter(e => (e.user_id===session.uid||(!e.user_id&&e.ownerExpense)||(!e.driverName&&e.source!=="driver_business")) && e.source!=="driver_business");
            } else if (isOwner && expPostedFilter === "driver") {
              expFiltered = expFiltered.filter(e => (e.user_id&&e.user_id!==session.uid)||e.source==="driver_business"||e.source==="fuel_log");
            }
            return expFiltered.length===0
            ?<div className="slt-card" style={{textAlign:"center",padding:"44px"}}><div style={{fontSize:38,marginBottom:10}}>🧾</div><div style={{color:C.textDarkMed}}>{expSearchQuery?"No results found":"No expenses yet"}</div></div>
            :expFiltered.map(e=>{
              const cat=CATS.find(c=>c.id===e.category)||CATS[CATS.length-1];
              const isAutoFuel=e.source==="load";
              const isFuelLog=e.source==="fuel_log";
              const isDriverPosted = isOwner && (e.source==="driver_business"||e.source==="fuel_log"||(e.user_id&&e.user_id!==session.uid));
              const isOwnerPosted = isOwner && !isDriverPosted;
              // Determine posted-by label for owner view
              const postedByName = isDriverPosted
                ? (e.driverName || (isFuelLog ? "Driver (Fuel Log)" : "Driver"))
                : (session.fullName||session.name||"You");
              // Only show delete/edit to person who entered it — not auto-entries
              const canEdit = !isAutoFuel && !isFuelLog && (e.user_id===session.uid || (!e.user_id && !e.ownerExpense));
              const hasAttachment = !!(e.receipt || e.receiptUrl);
              return(
                <div key={e.id} className="slt-card" style={{padding:"14px 18px",borderLeft:`4px solid ${isDriverPosted?"#E8962E":cat.c}`,cursor:"pointer"}} onClick={()=>{setDetailImgErr(false);setSelectedExpense(e);}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                    <div style={{flex:1,minWidth:0}}>
                      {/* Posted-by banner — owner sees who logged this */}
                      {isOwner&&(
                        <div style={{display:"inline-flex",alignItems:"center",gap:5,marginBottom:6,padding:"3px 9px",borderRadius:20,fontSize:11,fontWeight:800,
                          background: isDriverPosted?"rgba(232,150,46,0.12)":"rgba(28,43,74,0.07)",
                          color: isDriverPosted?"#B45309":"#1C2B4A",
                          border: `1px solid ${isDriverPosted?"rgba(232,150,46,0.3)":"rgba(28,43,74,0.15)"}`}}>
                          <span>{isDriverPosted?"👤":"🏢"}</span>
                          <span>{isDriverPosted?`${postedByName}`:"You (Owner)"}</span>
                        </div>
                      )}
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:2,flexWrap:"wrap"}}>
                        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:17,color:cat.c}}>{fmtC(e.amount)}</div>
                        {isAutoFuel&&<span style={{fontSize:12,background:DM.amberBg2,color:C.blue,borderRadius:6,padding:"2px 8px",fontWeight:800}}>🔗 From Load</span>}
                        {isFuelLog&&<span style={{fontSize:12,background:"#F5F6F8",color:"#E8962E",borderRadius:6,padding:"2px 8px",fontWeight:800}}>⛽ Fuel Log</span>}
                        {hasAttachment&&<span style={{fontSize:12,background:DM.greenBg,color:C.green,borderRadius:6,padding:"2px 8px",fontWeight:800}}>📎 Receipt</span>}
                        {Number(e.amount)>HIGH_FUEL_THRESHOLD&&e.category==="fuel"&&<span style={{fontSize:12,background:"#FFF3E0",color:"#E8962E",borderRadius:6,padding:"2px 8px",fontWeight:800}}>🚨 High</span>}
                      </div>
                      <div style={{fontSize:13,color:C.textDarkMed}}>{cat.i} {cat.l}{e.merchant?` · ${e.merchant}`:""}</div>
                      {(e.note||e.description)&&<div style={{fontSize:12,color:C.textDarkMut}}>{e.description||e.note}</div>}
                      <div style={{display:"flex",gap:8,marginTop:3,alignItems:"center",flexWrap:"wrap"}}>
                        <span style={{fontSize:13,color:C.textDarkMut}}>{e.date}</span>
                        <span style={{fontSize:12,background:cat.c+"18",color:cat.c,borderRadius:6,padding:"1px 7px",fontWeight:700}}>{e.taxCategory||cat.cra}</span>
                        {cat.id==="meals"&&<span style={{fontSize:12,background:DM.amberBg2,color:"#F57C00",borderRadius:6,padding:"1px 7px",fontWeight:700}}>50% deductible</span>}
                      </div>
                      {/* Thumbnail preview for local image receipts */}
                      {e.receipt&&e.receipt.startsWith("data:image")&&(
                        <div style={{marginTop:8,borderRadius:8,overflow:"hidden",border:`1px solid ${C.border}`,maxHeight:80,position:"relative",pointerEvents:"none"}}>
                          <img src={e.receipt} alt="Receipt" style={{width:"100%",objectFit:"cover",maxHeight:80}}/>
                          <div style={{position:"absolute",bottom:4,right:4,background:"rgba(0,0,0,0.55)",borderRadius:6,padding:"2px 7px",fontSize:12,color:"#fff",fontWeight:700}}>👁 View</div>
                        </div>
                      )}
                      {/* Show thumbnail for cloud-stored receipt */}
                      {(!e.receipt||!e.receipt.startsWith("data:image"))&&e.receiptUrl&&!e.receiptUrl.endsWith(".pdf")&&(
                        <div style={{marginTop:8,borderRadius:8,overflow:"hidden",border:`1px solid ${C.border}`,maxHeight:80,position:"relative",pointerEvents:"none"}}>
                          <img src={e.receiptUrl} alt="Receipt" style={{width:"100%",objectFit:"cover",maxHeight:80}}/>
                          <div style={{position:"absolute",bottom:4,right:4,background:"rgba(0,0,0,0.55)",borderRadius:6,padding:"2px 7px",fontSize:12,color:"#fff",fontWeight:700}}>👁 View</div>
                        </div>
                      )}
                      {e.receiptUrl&&e.receiptUrl.endsWith(".pdf")&&(
                        <div style={{marginTop:6,fontSize:13,color:"#E8962E",fontWeight:700}}>📄 PDF attached — tap to view</div>
                      )}
                    </div>
                    <div style={{marginLeft:10,flexShrink:0,display:"flex",flexDirection:"column",gap:6,alignItems:"flex-end"}}>
                      <span style={{fontSize:13,color:C.textDarkMut,fontWeight:600}}>›</span>
                    </div>
                  </div>
                </div>
              );
            })
          })()}
        </>}
      </div>

      {/* Expense detail is now handled as an inline page above — no modal */}
    </div>
  );
}

// ─── DRIVERS TAB ──────────────────────────────────────────────────────────────
function DriversTab({ session, loads, rates , goBack}) {
  const [inviteCode, setInviteCode] = useState(session.inviteCode || "");
  const [drivers, setDrivers] = useState([]);
  const [inactiveDrivers, setInactiveDrivers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [copied, setCopied] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [driverSearch, setDriverSearch] = useState("");
  const [driverSearchInput, setDriverSearchInput] = useState("");

  const loadAll = async () => {
    sbGetProfile(session.uid).then(profile => {
      if (profile?.invite_code) setInviteCode(profile.invite_code);
      else {
        const newCode = genCode();
        setInviteCode(newCode);
        sbSaveProfile({ id: session.uid, name: session.fullName, role: "owner", owner_uid: session.uid, plan: "free", invite_code: newCode });
      }
    });

    const [fleetDrivers, legacyDrivers] = await Promise.all([
      sbGetFleetDrivers(session.uid),
      sbGetDrivers(session.uid),
    ]);
    // Merge, deduplicate by uid (including within fleet drivers themselves)
    const seen = new Set();
    const merged = [];
    [...fleetDrivers, ...legacyDrivers].forEach(d => {
      if (!seen.has(d.uid)) { seen.add(d.uid); merged.push(d); }
    });
    setDrivers(merged);
    setRequests([]);
    sbGetInactiveDrivers(session.uid).then(setInactiveDrivers).catch(()=>{});
  };

  useEffect(() => { loadAll(); }, [session.uid]);

  const copyCode = () => {
    if (inviteCode) navigator.clipboard.writeText(inviteCode).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };
  const regen = async () => {
    const newCode = genCode();
    setInviteCode(newCode);
    await sbSaveProfile({ id: session.uid, name: session.fullName, role: "owner", owner_uid: session.uid, plan: "free", invite_code: newCode });
  };
  const approve = async (driverUid) => {
    await sbApproveFleetRequest(driverUid, session.uid);
    setRequests(prev => prev.filter(r => r.id !== driverUid));
    await sbGetDrivers(session.uid).then(setDrivers);
  };
  const reject = async (driverUid) => {
    await sbRejectFleetRequest(driverUid);
    setRequests(prev => prev.filter(r => r.id !== driverUid));
  };
  const remove = async (uid) => {
    if (!window.confirm("Remove this driver from your fleet? Their account stays active.")) return;
    await sbRemoveDriverFromFleet(uid, session.uid);
    // Also update legacy owner_uid back to driver's own uid
    await sb.from("profiles").update({ owner_uid: uid }).eq("id", uid);
    setDrivers(prev => prev.filter(d => d.uid !== uid));
  };

  return (
    <div className="slt-page" style={{background:"#070B14",color:"#F0F0F0"}}>
      {goBack && <BackButton onBack={goBack} label="Back" />}
      <div className="slt-hero">
        <div style={{fontSize:10,fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"rgba(255,255,255,0.35)",marginBottom:3}}>TRUCKPILOT</div>
        <div className="slt-hero-title">Fleet Drivers</div>
        <div className="slt-hero-sub">{drivers.length} driver{drivers.length!==1?"s":""} in fleet{requests.length>0?` · ${requests.length} pending request${requests.length!==1?"s":""}`:""}</div>
      </div>
      <div className="slt-container">

        {/* Invite Code */}
        <div className="slt-card" style={{border:`2px solid ${C.blue}`, marginBottom:16}}>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:17,color:C.blue,marginBottom:6}}>Your Fleet Invite Code</div>
          <div style={{fontSize:13,color:C.textDarkMed,marginBottom:16}}>Share with drivers — they enter this to join your fleet</div>
          <div style={{background:C.blueLight,borderRadius:12,padding:"16px 20px",textAlign:"center",fontFamily:"'Barlow Condensed',sans-serif",fontSize:30,fontWeight:800,color:C.blue,letterSpacing:10,marginBottom:16}}>{inviteCode||"——"}</div>
          <div style={{display:"flex",gap:10}}>
            <button className="slt-btn-primary slt-btn-dark-text" style={{background:copied?C.green:undefined}} onClick={copyCode}>{copied?"✓ Copied!":"Copy Code"}</button>
            <button className="slt-btn-secondary" style={{padding:"12px 16px"}} onClick={regen}>🔄 New Code</button>
          </div>
        </div>

        {/* Pending Requests */}
        {requests.length > 0 && (
          <div className="slt-card" style={{border:`2px solid ${C.orange}`, marginBottom:16}}>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:16,color:C.orange,marginBottom:12}}>
              🔔 {requests.length} Fleet Request{requests.length!==1?"s":""}
            </div>
            {requests.map(r => (
              <div key={r.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:`1px solid ${C.border}`}}>
                <div>
                  <div style={{fontWeight:700,fontSize:14}}>{r.name}</div>
                  {r.username && <div style={{fontSize:12,color:C.textDarkMut}}>@{r.username}</div>}
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>approve(r.id)} style={{padding:"7px 14px",borderRadius:8,border:"none",background:C.green,color:"#fff",fontWeight:800,fontSize:12,cursor:"pointer"}}>✅ Approve</button>
                  <button onClick={()=>reject(r.id)} style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${C.red}`,background:"#fff",color:C.red,fontWeight:800,fontSize:12,cursor:"pointer"}}>❌ Reject</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Driver Profile View */}
        {selectedDriver && (() => {
          const d = selectedDriver;
          const dl = loads.filter(l=>l.assignedDriverUid===d.uid||l.addedBy===d.uid);
          const done = dl.filter(l=>l.completed);
          const active = dl.filter(l=>!l.completed);
          const dp = dl.reduce((s,l)=>{const wm=(Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0);return s+(Number(l.driverBasePay)||0)+wm/60*(Number(rates?.driverWaitRate)||0);},0);
          const initials = (d.fullName||d.name||"?").split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
          return (
            <div className="slt-card" style={{marginBottom:16}}>
              {/* Back button */}
              <button onClick={()=>setSelectedDriver(null)} style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",cursor:"pointer",color:"#E8962E",fontWeight:700,fontSize:14,marginBottom:16,padding:0}}>
                ‹ All Drivers
              </button>
              {/* Avatar + Name */}
              <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:20}}>
                <div style={{width:64,height:64,borderRadius:"50%",background:"#E8962E",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:900,color:"#fff",flexShrink:0}}>
                  {initials}
                </div>
                <div>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:22,color:"#1A1A1A"}}>{d.fullName||d.name}</div>
                  {d.username&&<div style={{fontSize:13,color:"#4B5563"}}>@{d.username}</div>}
                  <div style={{fontSize:12,color:"#4B5563",marginTop:2}}>Joined {d.joined?.slice(0,10)||"—"}</div>
                </div>
              </div>
              {/* Driver Type Toggle */}
              <div style={{background:"#F5F6F8",borderRadius:14,padding:"14px 16px",marginBottom:16}}>
                <div style={{fontSize:11,fontWeight:800,color:"#6B7280",textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>Driver Classification</div>
                <div style={{display:"flex",gap:8}}>
                  {[{val:"employee",label:"👷 Employee",desc:"50% split expenses flow to your books"},{val:"subcontractor",label:"🏢 Subcontractor (Corp)",desc:"50% split stays in driver's own corp"}].map(opt=>{
                    const isActive = (d.driverType||"employee")===opt.val;
                    return (
                      <button key={opt.val} onClick={()=>{
                        setDrivers(prev=>prev.map(x=>x.uid===d.uid?{...x,driverType:opt.val}:x));
                        setSelectedDriver({...d,driverType:opt.val});
                      }} style={{flex:1,padding:"10px 8px",borderRadius:10,border:`2px solid ${isActive?"#E8962E":"#E5E7EB"}`,background:isActive?"#FFF7ED":"#fff",cursor:"pointer",textAlign:"left",transition:"all .15s"}}>
                        <div style={{fontSize:13,fontWeight:800,color:isActive?"#E8962E":"#374151"}}>{opt.label}</div>
                        <div style={{fontSize:11,color:"#6B7280",marginTop:3,lineHeight:1.3}}>{opt.desc}</div>
                      </button>
                    );
                  })}
                </div>
                <button onClick={async()=>{
                  await sb.from("profiles").update({driver_type: d.driverType||"employee"}).eq("id", d.uid);
                  alert("✅ Driver classification saved!");
                }} style={{marginTop:10,width:"100%",padding:"10px",borderRadius:10,border:"none",background:"#E8962E",color:"#fff",fontWeight:800,fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>
                  💾 Save Classification
                </button>
              </div>
              {/* Stats */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:20}}>
                {[{val:dl.length,lbl:"Total Loads"},{val:done.length,lbl:"Completed"},{val:active.length,lbl:"Active"}].map(s=>(
                  <div key={s.lbl} style={{borderRadius:12,padding:"12px",background:"#F5F6F8",textAlign:"center"}}>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:900,color:"#E8962E"}}>{s.val}</div>
                    <div style={{fontSize:12,fontWeight:600,color:"#4B5563",textTransform:"uppercase",marginTop:2}}>{s.lbl}</div>
                  </div>
                ))}
              </div>
              {/* Total Pay */}
              <div style={{background:"#E8962E",borderRadius:14,padding:"14px 18px",marginBottom:20,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{fontSize:13,color:"rgba(255,255,255,.7)",fontWeight:600}}>Total Pay Earned</div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,.5)",marginTop:2}}>Base pay + wait pay</div>
                </div>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:26,fontWeight:900,color:"#fff"}}>{fmtC(dp)}</div>
              </div>
              {/* Recent Loads */}
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:15,marginBottom:12}}>Recent Loads</div>
              {dl.slice(0,5).map((l,i)=>(
                <div key={l.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:"1px solid #f0f0f0"}}>
                  <div>
                    <div style={{fontWeight:700,fontSize:14,color:"#1A1A1A"}}>{l.location}</div>
                    <div style={{fontSize:12,color:"#4B5563"}}>{l.date}{l.time?` · ${l.time}`:""}</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:16,fontWeight:800,color:"#E8962E"}}>{fmtC(Number(l.driverBasePay||0))}</div>
                    <span style={{fontSize:12,fontWeight:700,color:l.completed?"#22C55E":"#E8962E"}}>{l.completed?"✓ Done":"Active"}</span>
                  </div>
                </div>
              ))}
              {dl.length===0&&<div style={{textAlign:"center",color:"#4B5563",padding:"20px 0",fontSize:13}}>No loads yet</div>}
              {/* Owner Rating */}
              <div style={{marginTop:16,borderTop:"1px solid #f0f0f0",paddingTop:16}}>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:14,marginBottom:10}}>⭐ Your Rating for this Driver</div>
                <div style={{display:"flex",gap:8,marginBottom:12}}>
                  {[1,2,3,4,5].map(star=>(
                    <button key={star} onClick={async()=>{
                      await sb.from("profiles").update({owner_rating:star}).eq("id",d.uid);
                      setDrivers(prev=>prev.map(x=>x.uid===d.uid?{...x,ownerRating:star}:x));
                      setSelectedDriver({...d,ownerRating:star});
                    }} style={{fontSize:28,background:"none",border:"none",cursor:"pointer",
                      color:(d.ownerRating||0)>=star?"#FFD700":"#ddd"}}>★</button>
                  ))}
                  {d.ownerRating && <span style={{fontSize:13,color:"#4B5563",alignSelf:"center"}}>{d.ownerRating}.0 / 5</span>}
                </div>
              </div>
              {/* Remove button */}
              <button className="slt-btn-danger" style={{width:"100%",marginTop:8,padding:"12px"}} onClick={()=>{remove(d.uid);setSelectedDriver(null);}}>Remove from Fleet</button>
            </div>
          );
        })()}

        {/* Active Drivers List */}
        {!selectedDriver && (
          <>
            {/* Driver Search Bar */}
            <form onSubmit={e=>{e.preventDefault();setDriverSearch(driverSearchInput);}} style={{display:"flex",gap:8,marginBottom:12}}>
              <input
                value={driverSearchInput}
                onChange={e=>{setDriverSearchInput(e.target.value);if(!e.target.value)setDriverSearch("");}}
                placeholder="🔍 Search by driver name…"
                style={{flex:1,padding:"10px 14px",borderRadius:10,border:"1.5px solid #d0d5dd",fontSize:13,fontFamily:"'Barlow',sans-serif",outline:"none"}}
              />
              <button type="submit"
                style={{padding:"10px 16px",borderRadius:10,background:"#E8962E",color:"#1C1C1E",border:"none",fontWeight:800,fontSize:13,cursor:"pointer",fontFamily:"'Barlow',sans-serif",whiteSpace:"nowrap"}}>
                Search
              </button>
              {driverSearch&&<button type="button" onClick={()=>{setDriverSearch("");setDriverSearchInput("");}}
                style={{padding:"10px 12px",borderRadius:10,background:"#F5F6F8",color:"#666",border:"none",fontWeight:700,fontSize:13,cursor:"pointer"}}>✕</button>}
            </form>
            {drivers.length===0
              ? <div className="slt-card" style={{textAlign:"center",padding:"44px"}}><div style={{fontSize:38,marginBottom:10}}>👥</div><div style={{color:C.textDarkMed}}>No drivers in your fleet yet.</div><div style={{fontSize:13,color:C.textDarkMut,marginTop:6}}>Share your invite code above or wait for join requests.</div></div>
              : drivers.filter(d=>!driverSearch||[d.fullName,d.name,d.username].join(" ").toLowerCase().includes(driverSearch.toLowerCase())).map(d => {
                const dl = loads.filter(l=>l.assignedDriverUid===d.uid||l.addedBy===d.uid);
                const dp = dl.reduce((s,l)=>{const wm=(Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0);return s+(Number(l.driverBasePay)||0)+wm/60*(Number(rates?.driverWaitRate)||0);},0);
                const initials = (d.fullName||d.name||"?").split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
                return (
                  <div key={d.uid} className="slt-card" style={{cursor:"pointer"}} onClick={()=>setSelectedDriver(d)}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <div style={{display:"flex",alignItems:"center",gap:12}}>
                        <div style={{width:44,height:44,borderRadius:"50%",background:"#E8962E",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:900,color:"#fff",flexShrink:0}}>
                          {initials}
                        </div>
                        <div>
                          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:17}}>{d.fullName||d.name}</div>
                          <div style={{fontSize:12,color:C.textDarkMut}}>{dl.length} loads · {fmtC(dp)} total pay</div>
                        </div>
                      </div>
                      <span style={{fontSize:18,color:"#4B5563"}}>›</span>
                    </div>
                  </div>
                );
              })
            }
          </>
        )}
      </div>
    </div>
  );
}

// ─── REPORT TAB ───────────────────────────────────────────────────────────────
function ReportTab({ loads, session, rates, isOwner, allDrivers, goBack, setTab, setDetailLoad, darkMode=false }) {

  // ── Dark mode color palette ──
  const DM = {
    pageBg:   darkMode ? "#070B14" : "#F5F6F8",
    cardBg:   darkMode ? "#0D1523" : "#FFFFFF",
    cardBg2:  darkMode ? "#111C2E" : "#F8FAFC",
    border:   darkMode ? "rgba(255,255,255,0.07)" : "#E2E2E2",
    text:     darkMode ? "#F0F0F0" : "#1A1A1A",
    textMed:  darkMode ? "rgba(255,255,255,0.5)" : "#4B5563",
    textMut:  darkMode ? "rgba(255,255,255,0.35)" : "#6B7280",
    heroBg:   darkMode ? "#070B14" : "#F5F6F8",
    inputBg:  darkMode ? "#111C2E" : "#FFFFFF",
    rowAlt:   darkMode ? "#0D1929" : "#F8FAFC",
    greenBg:  darkMode ? "rgba(34,197,94,0.1)"  : "#E8F5E9",
    redBg:    darkMode ? "rgba(239,68,68,0.1)"  : "#FFF5F5",
    amberBg:  darkMode ? "rgba(232,150,46,0.1)" : "#F5F6F8",
    amberBg2: darkMode ? "rgba(232,150,46,0.06)": "#FFF3EB",
    divider:  darkMode ? "rgba(59,130,246,0.1)": "#F0F0F0",
  };

  const [range,setRange]=useState("month"); const [dFilter,setDFilter]=useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [reportSearch, setReportSearch] = useState("");
  const [reportSearchInput, setReportSearchInput] = useState("");
  const [showCustom, setShowCustom] = useState(false);
  const [reportFuelLogs, setReportFuelLogs] = useState([]);
  const [selectedReportExpense, setSelectedReportExpense] = useState(null);
  const [selectedFuelEntry, setSelectedFuelEntry] = useState(null);
  const reportScrollY = useRef(0);
  const openReportExpense = (e) => { reportScrollY.current = window.scrollY; setSelectedReportExpense(e); };
  const closeReportExpense = () => { setSelectedReportExpense(null); setTimeout(()=>window.scrollTo(0,reportScrollY.current),30); };
  const [drillRow, setDrillRow] = useState(null);
  // Restore expanded sections from sessionStorage so they survive navigation away and back
  const [expanded, setExpanded] = useState(() => {
    try { const s=sessionStorage.getItem("tp-report-expanded"); return s?new Set(JSON.parse(s)):new Set(); } catch{return new Set();}
  });
  const toggleExpand = (key) => setExpanded(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });
  const isExpanded = (key) => expanded.has(key);
  const plRef = useRef(null);
  const dailyRef = useRef(null);

  // Persist expanded state whenever it changes
  useEffect(() => {
    try { sessionStorage.setItem("tp-report-expanded", JSON.stringify([...expanded])); } catch{}
  }, [expanded]);

  // Restore scroll position on mount; save it on unmount
  useEffect(() => {
    const saved = sessionStorage.getItem("tp-report-scroll");
    if (saved) setTimeout(() => window.scrollTo(0, parseInt(saved)||0), 80);
    return () => { try { sessionStorage.setItem("tp-report-scroll", String(window.scrollY)); } catch{} };
  }, []);

  useEffect(() => {
    if (!isOwner) return;
    const fetchFuelLogs = async () => {
      try {
        const own = await sbGetFuelLog(session.uid);
        const fleetDrivers = [];
        let all = own.map(f => ({ ...f, driverName:"Owner" }));
        if (fleetDrivers) {
          for (const d of fleetDrivers) {
            const logs = await sbGetFuelLog(d.driver_uid);
            all = [...all, ...logs.map(f => ({ ...f, driverName: d.driver_name||"Driver" }))];
          }
        }
        setReportFuelLogs(all);
      } catch(e) {}
    };
    fetchFuelLogs();
  }, [session.uid, isOwner]);

  const fd=(d)=>{ 
    if(!d) return false; 
    const dt=new Date(d+"T12:00:00"), now=new Date(); 
    if(range==="custom") {
      const from = customFrom ? new Date(customFrom+"T00:00:00") : null;
      const to = customTo ? new Date(customTo+"T23:59:59") : null;
      if(from && dt < from) return false;
      if(to && dt > to) return false;
      return true;
    }
    if(range==="today") return dt.toDateString()===now.toDateString(); 
    if(range==="week"){const w=new Date(now);w.setDate(w.getDate()-7);return dt>=w;} 
    if(range==="month"){const m=new Date(now);m.setDate(m.getDate()-30);return dt>=m;} 
    // all / year
    return dt.getFullYear()===now.getFullYear();
  };
  const ml=isOwner?loads.filter(l=>fd(l.date)&&(dFilter==="all"||l.assignedDriverUid===dFilter||(!l.assignedDriverUid&&dFilter==="owner"))):loads.filter(l=>fd(l.date)&&(l.assignedDriverUid===session.uid||l.addedBy===session.uid||l.user_id===session.uid));

  // Load financials
  const te=ml.reduce((s,l)=>s+Number(l.earnings||0),0);
  const wc=ml.reduce((s,l)=>s+((Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0))/60*(Number(rates.companyWaitRate)||0),0);
  const gross=te; // Base load earnings only (wait time billed is shown separately)
  // Only count driver pay for loads actually assigned to a driver (not owner)
  // Total driver pay — includes owner when they drive themselves (assignedDriverUid = session.uid with driverBasePay set)
  // Total driver pay — includes owner self-pay and all driver loads
  const totalDrvPay=ml.filter(l=>Number(l.driverBasePay||0)>0
  ).reduce((s,l)=>{const wm=(Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0);return s+(Number(l.driverBasePay)||0)+wm/60*(Number(rates.driverWaitRate)||0);},0);
  const tw=ml.reduce((s,l)=>s+(Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0),0);
  // Driver-specific pay:
  // If load has driverBasePay set (fleet driver) use that
  // If driver logged their own load solo, use earnings as their pay
  const drp = isOwner ? 0 : ml.reduce((s,l) => {
    if (Number(l.driverBasePay) > 0) return s + Number(l.driverBasePay);
    // Solo driver own load — use earnings
    if (!l.assignedDriverUid || l.assignedDriverUid === session.uid) return s + Number(l.earnings||0);
    return s;
  }, 0);
  const dwp = isOwner ? 0 : ml.reduce((s,l)=>s+((Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0))/60*(Number(rates.driverWaitRate)||0),0);

  // Expenses — load fuel is owner/business only, never shown to drivers
  const [sbExpRep, setSbExpRep] = useState([]);
  useEffect(()=>{ sbGetExpenses(session.uid).then(d=>{if(d?.length>0)setSbExpRep(d);}).catch(()=>{}); },[session.uid]);
  const allExpenses=(()=>{
    // Filter deleted entries from localStorage before merging — prevents ghost entries
    // from showing after the user deletes an expense (localStorage may still have it)
    const local=getStored(expensesKey(session.uid)).filter(e => !e.deleted);
    const merged=[...local];
    sbExpRep.forEach(se=>{if(!merged.find(e=>e.id===se.id))merged.push(se);});
    // Add fuel log entries for owner — keyed by date for correct period filtering
    if (isOwner) {
      reportFuelLogs.filter(f => fd(f.date)).forEach(f => {
        const fid = `fuellog-${f.id}`;
        if (!merged.find(e => e.id === fid)) {
          merged.push({
            id: fid, category:"fuel",
            amount: Number(f.total||0),
            merchant: `⛽ Fuel Log · ${f.driverName||"Driver"}`,
            note: `${f.litres}L @ $${Number(f.price_per_litre||0).toFixed(3)}/L${f.location?` · ${f.location}`:""}`,
            date: f.date, source:"fuel_log", ownerExpense:true,
            taxCategory:"Line 9220", taxLabel:"Fuel & Oil",
          });
        }
      });
    }
    return merged;
  })();
  const filteredExp=allExpenses.filter(e=>{
    if(!fd(e.date)) return false;
    if(isOwner) return true;
    // Driver: personal expenses only — business expenses don't deduct from driver pay
    if(e.source==="load" || e.ownerExpense || e.expenseType==="business") return false;
    return true;
  });
  // Business expenses submitted by driver — shown separately in driver report, no deduction.
  // Must filter out soft-deleted entries so deleted expenses disappear immediately.
  const driverSubmittedBizExp = !isOwner ? allExpenses.filter(e => !e.deleted && !e.deleted_at && fd(e.date) && (e.ownerExpense || e.expenseType==="business")) : [];
  // Business expenses (logged by driver but flagged as business) — go to owner report
  const businessExp=allExpenses.filter(e=>fd(e.date) && (e.ownerExpense || e.expenseType==="business"));
  const filteredExpNoFuel=filteredExp.filter(e=>e.category!=="fuel"&&e.source!=="load");
  const filteredFuelOnly=filteredExp.filter(e=>e.category==="fuel"||e.source==="load");
  const expByCategory={};
  filteredExp.forEach(e=>{const cat=e.category||"other";expByCategory[cat]=(expByCategory[cat]||0)+Number(e.amount||0);});
  const expByCategoryNoFuel={};
  filteredExpNoFuel.forEach(e=>{const cat=e.category||"other";expByCategoryNoFuel[cat]=(expByCategoryNoFuel[cat]||0)+Number(e.amount||0);});
  const totalExp=filteredExp.reduce((s,e)=>s+Number(e.amount||0),0);
  const totalExpNoFuel=filteredExpNoFuel.reduce((s,e)=>s+Number(e.amount||0),0);
  const totalFuelExp=filteredFuelOnly.reduce((s,e)=>s+Number(e.amount||0),0);

  // Net after expenses
  // Owner net: gross minus driver pay minus ALL expenses (fuel is owner cost)
  // Owner pay is now included in totalDrvPay via assignedDriverUid = session.uid
  const ownerSelfLoads = isOwner ? ml.filter(l => l.assignedDriverUid === session.uid && (l.addedBy === session.uid || l.user_id === session.uid)) : [];
  const ownerSelfPay = ownerSelfLoads.reduce((s,l) => {
    const wm = (Number(l.loadWaitMins)||0) + (Number(l.offloadWaitMins)||0);
    return s + Number(l.driverBasePay||0) + wm/60*(Number(rates.driverWaitRate)||0);
  }, 0);
  const ownerNet=te+wc-totalDrvPay-totalExp; // Net profit: base earnings + wait billed - driver pay - expenses
  // Driver net: driver pay minus ONLY non-fuel expenses (fuel is NOT driver's cost)
  const driverNet=(drp+dwp-totalExpNoFuel); // Net pay after personal expenses

  const ECATS={fuel:"⛽ Fuel & Oil",maintenance:"🔧 Repairs & Maintenance",insurance:"🛡 Insurance",permits:"📋 Licenses & Renewals",telephone:"📱 Telephone & Internet",rent:"🏢 Rent / Lease",meals:"🍽 Meals & Entertainment",lodging:"🏨 Accommodation",tolls:"🛣 Tolls & Parking",union_dues:"🤝 Union Dues",tools_supplies:"🧰 Tools & Supplies",safety:"🦺 Safety Gear",accounting:"📂 Accounting / Legal",advertising:"📣 Advertising",bank_fees:"🏦 Bank Fees",medical:"💊 Medical",other:"📦 Other"};

  const _pNow = new Date();
  const periodLabel = (()=>{
    const fmtD = d => d.toLocaleDateString("en-CA",{month:"short",day:"numeric"});
    if(range==="week"){const s=new Date(_pNow);s.setDate(s.getDate()-6);return `${fmtD(s)} – ${fmtD(_pNow)}, ${_pNow.getFullYear()}`;}
    if(range==="month"){const s=new Date(_pNow);s.setDate(s.getDate()-29);return `${fmtD(s)} – ${fmtD(_pNow)}`;}
    return `Jan 1 – Dec 31, ${_pNow.getFullYear()}`;
  })();

  return (
    <>
    {/* ── Drill-down overlay ── */}
    {drillRow&&(()=>{
      const _dTitles={"gross":"Gross Revenue — Loads","drvpay":"Driver Pay","waitcomp":"Wait Time Pay (billed)","expenses":"Operating Expenses","drv-route":"Route Pay — Loads","drv-wait":"Wait Pay","drv-expenses":"My Expenses"};
      const _dTitle=_dTitles[drillRow]||"Details";
      const _dItems=(drillRow==="gross")?ml.map(l=>({key:l.id,title:l.location||"—",sub:`${l.date}${isOwner&&l.driverFullName?` · ${l.driverFullName}`:""}`,val:fmtC(Number(l.earnings||0)),color:"#22C55E",prefix:"+",cb:()=>{setDetailLoad&&setDetailLoad(l);setDrillRow(null);}}))
        :(drillRow==="drvpay")?ml.filter(l=>Number(l.driverBasePay||0)>0).map(l=>{const wm=(Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0);const tp=Number(l.driverBasePay||0)+wm/60*(Number(rates.driverWaitRate)||0);return{key:l.id,title:l.driverFullName||"Driver",sub:`${l.date} · ${l.location||"—"}`,val:fmtC(tp),color:"#EF4444",prefix:"-",cb:()=>{setDetailLoad&&setDetailLoad(l);setDrillRow(null);}};})
        :(drillRow==="waitcomp")?ml.filter(l=>(Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0)>0).map(l=>{const wm=(Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0);const wp=wm/60*(Number(rates.companyWaitRate)||0);return{key:l.id,title:l.location||"—",sub:`${l.date} · ${wm}min wait`,val:fmtC(wp),color:"#E8962E",prefix:"+",cb:()=>{setDetailLoad&&setDetailLoad(l);setDrillRow(null);}};})
        :(drillRow==="expenses")?filteredExp.map(e=>({key:e.id||e.date+e.category,title:ECATS[e.category]||e.category||"Expense",sub:`${e.date}${e.merchant?` · ${e.merchant}`:""}`,val:fmtC(e.amount),color:"#EF4444",prefix:"-",cb:()=>openReportExpense(e)}))
        :(drillRow==="drv-route")?ml.map(l=>({key:l.id,title:l.location||"—",sub:l.date,val:fmtC(Number(l.driverBasePay)||Number(l.earnings||0)),color:"#22C55E",prefix:"+",cb:()=>{setDetailLoad&&setDetailLoad(l);setDrillRow(null);}}))
        :(drillRow==="drv-wait")?ml.filter(l=>(Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0)>0).map(l=>{const wm=(Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0);const wp=wm/60*(Number(rates.driverWaitRate)||0);return{key:l.id,title:l.location||"—",sub:`${l.date} · ${wm}min`,val:fmtC(wp),color:"#E8962E",prefix:"+",cb:()=>{setDetailLoad&&setDetailLoad(l);setDrillRow(null);}};})
        :(drillRow==="drv-expenses")?filteredExpNoFuel.map(e=>({key:e.id||e.date+e.category,title:ECATS[e.category]||e.category||"Expense",sub:`${e.date}${e.merchant?` · ${e.merchant}`:""}`,val:fmtC(e.amount),color:"#EF4444",prefix:"-",cb:()=>openReportExpense(e)}))
        :[];
      return (
        <div style={{position:"fixed",inset:0,zIndex:4500,background:DM.pageBg,overflowY:"auto",WebkitOverflowScrolling:"touch"}}>
          <div style={{background:darkMode?"linear-gradient(135deg,#0F2140 0%,#162B55 50%,#0F2140 100%)":"linear-gradient(135deg,#1C2B4A 0%,#243655 100%)",padding:"20px 18px 18px"}}>
            <div style={{color:"rgba(255,255,255,0.55)",fontSize:12,fontWeight:800,textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>{_dTitle}</div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:32,color:"#fff",lineHeight:1.1}}>{_dItems.length} item{_dItems.length!==1?"s":""}</div>
          </div>
          <div style={{padding:"16px"}}>
            {_dItems.length===0&&<div style={{textAlign:"center",padding:"32px",color:DM.textMut,fontSize:14}}>No items in this period</div>}
            {_dItems.map(item=>(
              <div key={item.key} onClick={item.cb}
                style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"13px 14px",borderRadius:12,background:DM.cardBg,marginBottom:8,cursor:"pointer",border:`1px solid ${DM.border}`}}>
                <div>
                  <div style={{fontSize:14,fontWeight:700,color:DM.text}}>{item.title}</div>
                  <div style={{fontSize:12,color:DM.textMut,marginTop:2}}>{item.sub}</div>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:16,fontWeight:900,color:item.color}}>{item.prefix}{item.val}</span>
                  <span style={{color:DM.textMut,fontSize:14}}>›</span>
                </div>
              </div>
            ))}
            <button onClick={()=>setDrillRow(null)} style={{width:"100%",padding:"14px",borderRadius:12,border:`2px solid ${darkMode?"rgba(255,255,255,0.15)":"#1C2B4A"}`,background:"transparent",color:darkMode?"#fff":"#1C2B4A",fontWeight:800,fontSize:15,cursor:"pointer",marginTop:8,fontFamily:"inherit"}}>← Back to Reports</button>
          </div>
        </div>
      );
    })()}
    {selectedReportExpense&&(()=>{
      const rExp=selectedReportExpense;
      const rCatLabel=ECATS[rExp.category]||rExp.category||"Expense";
      const rReceiptSrc=rExp.receipt&&rExp.receipt.startsWith("data:")?rExp.receipt:rExp.receiptUrl||null;
      const rIsPdf=rReceiptSrc&&rReceiptSrc.toLowerCase().includes(".pdf");
      return (
        <div style={{position:"fixed",inset:0,zIndex:9500,background:"#F5F6F8",overflowY:"auto",WebkitOverflowScrolling:"touch",paddingBottom:"env(safe-area-inset-bottom,0px)"}}>
          <div style={{background:"linear-gradient(135deg,#1C2B4A,#243655)",padding:"20px 18px 18px"}}>
            <div style={{color:"rgba(255,255,255,0.65)",fontSize:12,fontWeight:800,textTransform:"uppercase",letterSpacing:1}}>🧾 {rCatLabel}</div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:38,color:"#fff",lineHeight:1.1}}>${Number(rExp.amount||0).toFixed(2)}</div>
          </div>
          <div style={{padding:"16px 16px 0"}}>
            <div className="slt-card" style={{marginBottom:14}}>
              {rExp.merchant&&(<div style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:"1px solid #E2E2E2"}}><span style={{fontSize:12,color:"#6B7280",fontWeight:700}}>MERCHANT</span><span style={{fontSize:14,fontWeight:800}}>{rExp.merchant}</span></div>)}
              <div style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:"1px solid #E2E2E2"}}><span style={{fontSize:12,color:"#6B7280",fontWeight:700}}>DATE</span><span style={{fontSize:14,fontWeight:800}}>{rExp.date}</span></div>
              <div style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:"1px solid #E2E2E2"}}><span style={{fontSize:12,color:"#6B7280",fontWeight:700}}>CATEGORY</span><span style={{fontSize:13,fontWeight:800,color:"#E8962E"}}>{rCatLabel}</span></div>
              {(rExp.litres||rExp.pricePerLitre)&&(<div style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:"1px solid #E2E2E2"}}><span style={{fontSize:12,color:"#6B7280",fontWeight:700}}>FUEL</span><span style={{fontSize:14,fontWeight:800}}>{rExp.litres}L @ ${Number(rExp.pricePerLitre||0).toFixed(3)}/L</span></div>)}
              {(rExp.note||rExp.description)&&(<div style={{padding:"10px 0",borderBottom:"1px solid #E2E2E2"}}><span style={{fontSize:12,color:"#6B7280",fontWeight:700,display:"block",marginBottom:4}}>NOTE</span><span style={{fontSize:13,color:"#0B1120"}}>{rExp.description||rExp.note}</span></div>)}
              {rExp.driverName&&(<div style={{display:"flex",justifyContent:"space-between",padding:"10px 0"}}><span style={{fontSize:12,color:"#6B7280",fontWeight:700}}>DRIVER</span><span style={{fontSize:13,fontWeight:800,color:"#E8962E"}}>👤 {rExp.driverName}</span></div>)}
            </div>
            <div className="slt-card" style={{marginBottom:14}}>
              <div style={{fontSize:12,color:"#6B7280",fontWeight:800,textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>📎 Attachment</div>
              {rReceiptSrc?(rIsPdf?(<a href={rReceiptSrc} target="_blank" rel="noopener noreferrer" style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:"#F5F6F8",borderRadius:12,border:"1.5px solid #E2E2E2",textDecoration:"none"}}><span style={{fontSize:26}}>📄</span><div><div style={{fontWeight:800,fontSize:13,color:"#E8962E"}}>PDF Receipt</div><div style={{fontSize:12,color:"#4B5563"}}>Tap to open</div></div><span style={{marginLeft:"auto",fontSize:16,color:"#E8962E"}}>↗</span></a>):(<img src={rReceiptSrc} alt="Receipt" style={{width:"100%",borderRadius:12,objectFit:"contain",maxHeight:300,border:"1px solid #e0e0e0",display:"block"}}/>)):(<div style={{display:"flex",alignItems:"center",gap:10,padding:"16px 0",color:"#9CA3AF",fontSize:13}}><span style={{fontSize:24}}>🧾</span><span>No attachment saved</span></div>)}
            </div>
            <div style={{marginBottom:32}}>
              <button onClick={closeReportExpense} style={{width:"100%",padding:"16px 0",borderRadius:14,border:"2px solid #1C2B4A",background:"transparent",color:"#1C2B4A",fontWeight:900,fontSize:15,cursor:"pointer",fontFamily:"inherit"}}>← Back to Report</button>
            </div>
          </div>
        </div>
      );
    })()}
    {selectedFuelEntry&&(
      <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:4000,display:"flex",alignItems:"flex-end",justifyContent:"center"}} onClick={()=>setSelectedFuelEntry(null)}>
        <div style={{background:darkMode?"#0D1523":"#fff",borderRadius:"20px 20px 0 0",width:"100%",maxWidth:560,padding:"24px 20px 40px",WebkitOverflowScrolling:"touch"}} onClick={e=>e.stopPropagation()}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:22,color:"#E8962E"}}>⛽ Fuel Entry</div>
            <button onClick={()=>setSelectedFuelEntry(null)} style={{background:"#EEEEEE",border:"none",borderRadius:"50%",width:36,height:36,fontSize:18,cursor:"pointer"}}>✕</button>
          </div>
          <div style={{background:"#F5F6F8",borderRadius:12,padding:"14px 16px",display:"flex",flexDirection:"column",gap:10}}>
            {[["Driver",selectedFuelEntry.driverName||"—"],["Truck",selectedFuelEntry.truck_number||"—"],["Date",selectedFuelEntry.date||"—"],["Location",selectedFuelEntry.location||"—"],["Litres",selectedFuelEntry.litres?`${selectedFuelEntry.litres} L`:"—"],["Price/L",selectedFuelEntry.price_per_litre?`$${Number(selectedFuelEntry.price_per_litre).toFixed(3)}/L`:"—"],["Total",selectedFuelEntry.total?`$${Number(selectedFuelEntry.total).toFixed(2)}`:"—"],["Odometer",selectedFuelEntry.odometer?`${selectedFuelEntry.odometer} km`:"—"],["Notes",selectedFuelEntry.notes||selectedFuelEntry.note||"—"]].filter(([,v])=>v!=="—").map(([label,val])=>(<div key={label} style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}><span style={{fontSize:12,color:"#E8962E",fontWeight:700}}>{label.toUpperCase()}</span><span style={{fontSize:14,fontWeight:800,color:"#E8962E"}}>{val}</span></div>))}
          </div>
          {selectedFuelEntry.receipt&&(<div style={{marginTop:14}}><div style={{fontSize:13,color:"#4B5563",fontWeight:700,marginBottom:6}}>RECEIPT</div><img src={selectedFuelEntry.receipt} alt="receipt" style={{width:"100%",borderRadius:10,maxHeight:200,objectFit:"cover"}}/></div>)}
        </div>
      </div>
    )}
    <div className="slt-page" style={{background:DM.pageBg,paddingBottom:100}}>

      {/* ── Header ── */}
      <div style={{background:"#0B1120",borderBottom:"1px solid rgba(59,130,246,0.25)",padding:"14px 18px 12px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:10,fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"rgba(255,255,255,0.4)",marginBottom:2}}>TruckPilot</div>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:900,color:"#fff",letterSpacing:"-0.3px",lineHeight:1}}>Reports</div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:6,background:"rgba(59,130,246,0.1)",border:"1px solid rgba(59,130,246,0.2)",borderRadius:20,padding:"4px 10px"}}>
          <div style={{width:6,height:6,borderRadius:"50%",background:"#3B82F6",boxShadow:"0 0 6px rgba(59,130,246,0.8)"}}/>
          <span style={{fontSize:10,fontWeight:800,color:"#3B82F6",letterSpacing:"0.8px",textTransform:"uppercase"}}>LIVE</span>
        </div>
      </div>

      {/* ── Period Tabs ── */}
      <div style={{display:"flex",gap:8,padding:"12px 16px 14px",overflowX:"auto"}}>
        {[["week","This Week"],["month","Month"],["all","Year"]].map(([v,l])=>(
          <div key={v} onClick={()=>setRange(v)}
            style={{borderRadius:30,padding:"8px 20px",fontSize:14,fontWeight:700,cursor:"pointer",flexShrink:0,
              background:range===v?(darkMode?"#3B82F6":"#1C2B4A"):"transparent",
              color:range===v?"#fff":DM.textMed,
              border:`2px solid ${range===v?(darkMode?"#3B82F6":"#1C2B4A"):DM.border}`}}>
            {l}
          </div>
        ))}
      </div>

      {/* ── Hero Card ── */}
      <div style={{margin:"0 16px 12px",borderRadius:26,padding:"26px 20px 22px",background:darkMode?"linear-gradient(135deg,#0F2140 0%,#162B55 50%,#0F2140 100%)":"linear-gradient(135deg,#1C2B4A 0%,#243655 100%)",border:darkMode?"1px solid rgba(59,130,246,0.2)":"none",position:"relative",overflow:"hidden"}}>
        {/* Radial glow accents */}
        {darkMode&&<div style={{position:"absolute",top:-50,right:-50,width:180,height:180,borderRadius:"50%",background:"radial-gradient(circle,rgba(59,130,246,0.12) 0%,transparent 70%)",pointerEvents:"none"}}/>}
        {darkMode&&<div style={{position:"absolute",bottom:-40,left:-40,width:120,height:120,borderRadius:"50%",background:"radial-gradient(circle,rgba(232,150,46,0.07) 0%,transparent 70%)",pointerEvents:"none"}}/>}
        <div style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:2,color:"rgba(255,255,255,0.5)",marginBottom:6,position:"relative"}}>
          {isOwner?"GROSS REVENUE":"GROSS EARNINGS"}
        </div>
        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:64,fontWeight:900,color:"#fff",lineHeight:1,marginBottom:6,letterSpacing:"-2px",position:"relative"}}>
          <span style={{color:darkMode?"rgba(255,255,255,0.5)":"rgba(255,255,255,0.7)",fontSize:40,verticalAlign:"top",marginTop:8,display:"inline-block"}}>$</span><span style={{color:darkMode?"#fff":"#fff"}}>{(isOwner?gross:drp+dwp).toLocaleString("en-CA",{minimumFractionDigits:0,maximumFractionDigits:0})}</span>
        </div>
        <div style={{fontSize:13,color:"rgba(255,255,255,0.5)",marginBottom:16,display:"flex",alignItems:"center",gap:8}}>
          <span>{periodLabel} · {ml.length} load{ml.length!==1?"s":""}</span>
        </div>

        {/* CRA-correct income note for drivers */}
        {!isOwner && (() => {
          const craLoads = ml.filter(l => l.driverReceived || l.payment_status === "paid");
          const craIncome = craLoads.reduce((s,l) => {
            const wm = (Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0);
            return s + Number(l.driverBasePay||0) + wm/60*(Number(rates.driverWaitRate)||0);
          }, 0);
          const unpaidCount = ml.filter(l => l.completed && !l.driverReceived && l.payment_status !== "paid" && Number(l.driverBasePay||0) > 0).length;
          if (craLoads.length === 0 && unpaidCount === 0) return null;
          return (
            <div style={{background:"rgba(34,197,94,0.12)",border:"1px solid rgba(34,197,94,0.3)",borderRadius:10,padding:"10px 13px",marginBottom:16}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{fontSize:10,fontWeight:800,color:"#4ade80",textTransform:"uppercase",letterSpacing:0.7,marginBottom:2}}>✅ CRA-Correct Income (Received)</div>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:900,color:"#fff"}}>${craIncome.toLocaleString("en-CA",{minimumFractionDigits:0,maximumFractionDigits:0})}</div>
                </div>
                {unpaidCount > 0 && (
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:10,fontWeight:800,color:"rgba(239,68,68,0.8)",textTransform:"uppercase",letterSpacing:0.7,marginBottom:2}}>⏳ Unpaid</div>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:900,color:"#fca5a5"}}>{unpaidCount} load{unpaidCount!==1?"s":""}</div>
                  </div>
                )}
              </div>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.4)",marginTop:5}}>Use this number for CRA T4A / tax filing — only money actually received.</div>
            </div>
          );
        })()}

        <div style={{height:1,background:"rgba(255,255,255,0.12)",marginBottom:16}}/>
        <div style={{display:"grid",gridTemplateColumns:"1fr auto 1fr auto 1fr",alignItems:"center"}}>
          {(isOwner
            ?[["Net Profit",fmtC(ownerNet),ownerNet>=0?"#22C55E":"#EF4444"],["Driver Pay",fmtC(totalDrvPay),"#E8962E"],["Expenses",fmtC(totalExp),"#EF4444"]]
            :[["Net Pay",fmtC(driverNet),driverNet>=0?"#22C55E":"#EF4444"],["Wait Pay",fmtC(dwp),"#E8962E"],["Expenses",fmtC(totalExpNoFuel),"#EF4444"]]
          ).map(([lbl,val,col],i,arr)=>(
            <div key={lbl} style={{display:"contents"}}>
              <div style={{textAlign:"center"}}>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:900,color:col}}>{val}</div>
                <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.45)",textTransform:"uppercase",letterSpacing:1,marginTop:2}}>{lbl}</div>
              </div>
              {i<arr.length-1&&<div style={{width:1,height:32,background:"rgba(255,255,255,0.12)"}}/>}
            </div>
          ))}
        </div>
      </div>

      {/* ── P&L Breakdown Card ── */}
      <div style={{margin:"0 16px 12px",background:DM.cardBg,borderRadius:18,padding:"20px 18px",border:`1px solid ${DM.border}`}}>
        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:20,fontWeight:800,color:DM.text,marginBottom:16}}>P&L Breakdown</div>
        {(()=>{
          const plRows=isOwner
            ?[
                {dot:"#22C55E",label:"Gross Revenue",sub:"Tap to view loads",val:fmtC(gross),valCol:"#22C55E",prefix:"+",rk:"gross",click:true},
                {dot:"#E8962E",label:"Driver Pay",sub:"Tap to view by driver",val:fmtC(totalDrvPay),valCol:"#EF4444",prefix:"-",rk:"drvpay",click:true},
                ...(wc>0?[{dot:"#E8962E",label:"Wait Time Pay (billed)",sub:"Tap to view wait loads",val:fmtC(wc),valCol:"#E8962E",prefix:"+",rk:"waitcomp",click:true}]:[]),
                ...(totalExp>0?[{dot:"#EF4444",label:"Operating Expenses",sub:"Tap to view expenses",val:fmtC(totalExp),valCol:"#EF4444",prefix:"-",rk:"expenses",click:true}]:[]),
                {dot:DM.text,label:"Net Profit",sub:"",val:fmtC(ownerNet),valCol:ownerNet>=0?"#22C55E":"#EF4444",prefix:"",rk:"net",click:false},
              ]
            :[
                {dot:"#22C55E",label:"Route Pay",sub:"Tap to view loads",val:fmtC(drp),valCol:"#22C55E",prefix:"+",rk:"drv-route",click:true},
                ...(dwp>0?[{dot:"#E8962E",label:"Wait Pay",sub:"Tap to view wait loads",val:fmtC(dwp),valCol:"#E8962E",prefix:"+",rk:"drv-wait",click:true}]:[]),
                {dot:"#6B7280",label:"Gross Earnings",sub:"Before expenses",val:fmtC(drp+dwp),valCol:"#6B7280",prefix:"",rk:"drv-gross",click:false},
                ...(totalExpNoFuel>0?[{dot:"#EF4444",label:"My Expenses",sub:"Tap to view",val:fmtC(totalExpNoFuel),valCol:"#EF4444",prefix:"-",rk:"drv-expenses",click:true}]:[]),
                {dot:DM.text,label:"Net Pay",sub:"After expenses",val:fmtC(driverNet),valCol:driverNet>=0?"#22C55E":"#EF4444",prefix:"",rk:"totalpay",click:false},
              ];
          return plRows.map((row,idx)=>(
            <div key={row.rk} onClick={row.click?()=>setDrillRow(row.rk):undefined}
              style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 0",
                borderBottom:idx<plRows.length-1?`1px solid ${DM.divider}`:"none",
                cursor:row.click?"pointer":"default"}}>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <div style={{width:10,height:10,borderRadius:"50%",background:row.dot,flexShrink:0}}/>
                <div>
                  <div style={{fontSize:15,fontWeight:row.click?600:700,color:DM.text}}>{row.label}</div>
                  {row.sub&&<div style={{fontSize:11,color:DM.textMut,marginTop:1}}>{row.sub}</div>}
                </div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:17,fontWeight:900,color:row.valCol}}>{row.prefix}{row.val}</span>
                {row.click&&<span style={{color:DM.textMut,fontSize:14}}>›</span>}
              </div>
            </div>
          ));
        })()}
      </div>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* ── WIDGET 1: Quick Stats ─────────────────────────────── */}
      {/* ══════════════════════════════════════════════════════════ */}
      {ml.length>0&&(()=>{
        const avgLoad=ml.length>0?(isOwner?gross:drp+dwp)/ml.length:0;
        const waitEarned=isOwner?wc:dwp;
        const uniqueDays=new Set(ml.map(l=>l.date).filter(Boolean)).size;
        const stats=isOwner
          ?[["⚡ Avg Per Load",`$${avgLoad.toLocaleString("en-CA",{minimumFractionDigits:0,maximumFractionDigits:0})}`,"#E8962E"],["📦 Total Loads",ml.length,"#1C2B4A"],["⏱ Wait Pay",`$${waitEarned.toLocaleString("en-CA",{minimumFractionDigits:0,maximumFractionDigits:0})}`,"#22C55E"],["📅 Days Worked",uniqueDays,"#1C2B4A"]]
          :[["⚡ Avg Per Load",`$${avgLoad.toLocaleString("en-CA",{minimumFractionDigits:0,maximumFractionDigits:0})}`,"#E8962E"],["📦 Total Loads",ml.length,"#1C2B4A"],["⏱ Wait Pay",`$${waitEarned.toLocaleString("en-CA",{minimumFractionDigits:0,maximumFractionDigits:0})}`,"#22C55E"],["📅 Days Worked",uniqueDays,"#1C2B4A"]];
        return(
          <div style={{margin:"0 16px 12px",background:DM.cardBg,borderRadius:18,padding:"18px 16px",border:`1px solid ${DM.border}`}}>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:800,color:DM.text,marginBottom:14}}>⚡ Quick Stats</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              {stats.map(([lbl,val,col])=>(
                <div key={lbl} style={{background:DM.cardBg2,borderRadius:12,padding:"12px 10px",textAlign:"center",border:`1px solid ${DM.border}`}}>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:900,color:col}}>{val}</div>
                  <div style={{fontSize:10,fontWeight:700,color:DM.textMut,textTransform:"uppercase",letterSpacing:0.5,marginTop:3}}>{lbl}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ══════════════════════════════════════════════════════════ */}
      {/* ── WIDGET 2: Top Routes ──────────────────────────────── */}
      {/* ══════════════════════════════════════════════════════════ */}
      {ml.length>0&&(()=>{
        const byRoute={};
        ml.forEach(l=>{
          const r=l.location||"Unknown";
          if(!byRoute[r])byRoute[r]={earn:0,count:0};
          byRoute[r].earn+=isOwner?Number(l.earnings||0):(Number(l.driverBasePay||0)||Number(l.earnings||0));
          byRoute[r].count++;
        });
        const sorted=Object.entries(byRoute).sort((a,b)=>b[1].earn-a[1].earn).slice(0,4);
        if(!sorted.length)return null;
        const ranks=["🥇","🥈","🥉","4️⃣"];
        return(
          <div style={{margin:"0 16px 12px",background:DM.cardBg,borderRadius:18,padding:"18px 16px",border:`1px solid ${DM.border}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:800,color:DM.text}}>🏆 Top Routes</div>
              <div style={{fontSize:11,fontWeight:700,color:DM.textMut}}>By Revenue</div>
            </div>
            {sorted.map(([route,{earn,count}],i)=>(
              <div key={route} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:i<sorted.length-1?`1px solid ${DM.divider}`:"none"}}>
                <div style={{fontSize:18,width:24,textAlign:"center",flexShrink:0}}>{ranks[i]}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:700,color:DM.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{route}</div>
                  <div style={{fontSize:11,color:DM.textMut,marginTop:1}}>{count} load{count!==1?"s":""}</div>
                </div>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:17,fontWeight:900,color:"#22C55E",flexShrink:0}}>{fmtC(earn)}</div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* ══════════════════════════════════════════════════════════ */}
      {/* ── WIDGET 3: Expense Breakdown ───────────────────────── */}
      {/* ══════════════════════════════════════════════════════════ */}
      {totalExp>0&&(()=>{
        const catSource=isOwner?expByCategory:expByCategoryNoFuel;
        const catTotal=Object.values(catSource).reduce((s,v)=>s+v,0);
        const sorted=Object.entries(catSource).sort((a,b)=>b[1]-a[1]).slice(0,6);
        if(!sorted.length)return null;
        const barColors=["#EF4444","#E8962E","#1C2B4A","#6B7280","#9CA3AF","#D1D5DB"];
        return(
          <div style={{margin:"0 16px 12px",background:DM.cardBg,borderRadius:18,padding:"18px 16px",border:`1px solid ${DM.border}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:800,color:DM.text}}>🧾 Expense Breakdown</div>
              <div style={{fontSize:11,fontWeight:700,color:DM.textMut}}>{fmtC(catTotal)} Total</div>
            </div>
            {sorted.map(([cat,amt],i)=>{
              const pct=catTotal>0?Math.round((amt/catTotal)*100):0;
              return(
                <div key={cat} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:i<sorted.length-1?`1px solid ${DM.divider}`:"none"}}>
                  <div style={{fontSize:15,width:22,textAlign:"center",flexShrink:0}}>{(ECATS[cat]||cat).split(" ")[0]}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:700,color:DM.text,marginBottom:4}}>{(ECATS[cat]||cat).replace(/^.+?\s/,"")}</div>
                    <div style={{height:5,background:DM.border,borderRadius:99,overflow:"hidden"}}>
                      <div style={{width:`${pct}%`,height:"100%",borderRadius:99,background:barColors[i]||"#E8962E"}}/>
                    </div>
                  </div>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:14,fontWeight:800,color:"#EF4444",flexShrink:0,minWidth:58,textAlign:"right"}}>{fmtC(amt)}</div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* ══════════════════════════════════════════════════════════ */}
      {/* ── WIDGET 3b: GST/ITC Summary (driver gets same as owner) */}
      {/* ══════════════════════════════════════════════════════════ */}
      {!isOwner&&(()=>{
        const GST=0.05;
        const expForITC=filteredExp;
        const fuelAmt=expForITC.filter(e=>e.category==="fuel").reduce((s,e)=>s+Number(e.amount||0),0);
        const repAmt=expForITC.filter(e=>e.category==="repairs"||e.category==="maintenance").reduce((s,e)=>s+Number(e.amount||0),0);
        const otherAmt=Math.max(0,expForITC.reduce((s,e)=>s+Number(e.amount||0),0)-fuelAmt-repAmt);
        const itcFuel=parseFloat((fuelAmt*GST).toFixed(2));
        const itcRep=parseFloat((repAmt*GST).toFixed(2));
        const itcOther=parseFloat((otherAmt*GST).toFixed(2));
        const totalITC=parseFloat(((fuelAmt+repAmt+otherAmt)*GST).toFixed(2));
        if(totalITC===0)return null;
        return(
          <div style={{margin:"0 16px 12px",background:DM.cardBg,borderRadius:18,padding:"18px 16px",border:`1px solid ${DM.border}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:800,color:DM.text}}>🧾 GST/HST — Input Tax Credits</div>
              <div style={{fontSize:10,fontWeight:800,color:"#22C55E",background:darkMode?"rgba(34,197,94,0.1)":"#E8F5E9",padding:"3px 9px",borderRadius:30,border:"1px solid rgba(34,197,94,0.3)"}}>REFUND</div>
            </div>
            <div style={{background:darkMode?"rgba(34,197,94,0.07)":"#F0FDF4",borderRadius:12,padding:"12px 14px",border:"1px solid rgba(34,197,94,0.2)",marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:800,textTransform:"uppercase",letterSpacing:0.8,color:"#22C55E",marginBottom:8}}>Zero-Rated Trucking Supply (ETA Sched. VI)</div>
              {[
                ["Line 101 — Freight Revenue (Zero-Rated)",`$${(drp+dwp).toFixed(2)}`,DM.textMed],
                ["Line 105 — GST/HST Collected","$0.00","#22C55E"],
                [null,null,null],
                ["ITC — Fuel & Oil (5%)",`$${itcFuel.toFixed(2)}`,"#22C55E"],
                ...(itcRep>0?[["ITC — Repairs & Maintenance (5%)",`$${itcRep.toFixed(2)}`,"#22C55E"]]:[]),
                ...(itcOther>0?[["ITC — Other Eligible Expenses (5%)",`$${itcOther.toFixed(2)}`,"#22C55E"]]:[]),
              ].map((r,i)=>{
                if(!r[0])return <div key={i} style={{height:1,background:darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.08)",margin:"6px 0"}}/>;
                return(
                  <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:12}}>
                    <span style={{color:DM.textMed,fontWeight:600}}>{r[0]}</span>
                    <span style={{fontWeight:800,color:r[2]||DM.text}}>{r[1]}</span>
                  </div>
                );
              })}
            </div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:darkMode?"rgba(34,197,94,0.12)":"#DCFCE7",borderRadius:12,padding:"14px 16px",border:"1.5px solid rgba(34,197,94,0.4)"}}>
              <div>
                <div style={{fontSize:10,fontWeight:800,textTransform:"uppercase",letterSpacing:1,color:"#22C55E",marginBottom:2}}>LINE 109 — REFUND FROM CRA</div>
                <div style={{fontSize:11,color:darkMode?"rgba(255,255,255,0.5)":"#4B5563"}}>File GST return to claim this</div>
              </div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:26,fontWeight:900,color:"#22C55E"}}>${totalITC.toFixed(2)}</div>
            </div>
            <div style={{fontSize:10,color:DM.textMut,marginTop:10,lineHeight:1.5}}>As a self-employed driver or corporation, your freight revenue is zero-rated — you collect $0 GST but can claim ITCs on eligible expenses. File a GST return to receive your refund.</div>
          </div>
        );
      })()}

      {/* ══════════════════════════════════════════════════════════ */}
      {/* ── WIDGET 4: Recent Loads ────────────────────────────── */}
      {/* ══════════════════════════════════════════════════════════ */}
      {ml.length>0&&(()=>{
        const recent=[...ml].sort((a,b)=>(b.date||"").localeCompare(a.date||"")).slice(0,5);
        return(
          <div style={{margin:"0 16px 12px",background:DM.cardBg,borderRadius:18,padding:"18px 16px",border:`1px solid ${DM.border}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:800,color:DM.text}}>📋 Recent Loads</div>
              <div style={{fontSize:11,fontWeight:700,color:DM.textMut}}>Tap to view</div>
            </div>
            {recent.map((l,i)=>{
              const earn=isOwner?Number(l.earnings||0):Number(l.driverBasePay||0)||Number(l.earnings||0);
              return(
                <div key={l.id||i} onClick={()=>{setDetailLoad&&setDetailLoad(l);}}
                  style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:i<recent.length-1?`1px solid ${DM.divider}`:"none",cursor:"pointer"}}>
                  <div style={{width:8,height:8,borderRadius:"50%",background:l.completed?"#22C55E":"#E8962E",flexShrink:0}}/>
                  <div style={{fontSize:11,fontWeight:700,color:DM.textMut,width:46,flexShrink:0}}>{(l.date||"").slice(5).replace("-","/")}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:700,color:DM.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.location||"—"}</div>
                    <div style={{display:"flex",alignItems:"center",gap:6,marginTop:2,flexWrap:"wrap"}}>
                      {l.tmwLoadNumber&&<span style={{fontSize:10,color:DM.textMut}}>#{l.tmwLoadNumber}</span>}
                      <span style={{fontSize:9,fontWeight:800,letterSpacing:"0.6px",textTransform:"uppercase",padding:"1px 6px",borderRadius:20,
                        background:l.completed?"rgba(34,197,94,0.1)":l.driverReceived?"rgba(59,130,246,0.1)":"rgba(232,150,46,0.1)",
                        color:l.completed?"#22C55E":l.driverReceived?"#3B82F6":"#E8962E",
                        border:`1px solid ${l.completed?"rgba(34,197,94,0.2)":l.driverReceived?"rgba(59,130,246,0.2)":"rgba(232,150,46,0.2)"}`}}>
                        {l.completed?"✓ Done":l.driverReceived?"Paid":"● Active"}
                      </span>
                    </div>
                  </div>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:15,fontWeight:900,color:"#22C55E",flexShrink:0}}>{fmtC(earn)}</div>
                  <div style={{color:DM.textMut,fontSize:13,flexShrink:0}}>›</div>
                </div>
              );
            })}
            {ml.length>5&&(
              <div onClick={()=>setTab&&setTab("log")} style={{textAlign:"center",paddingTop:10,fontSize:12,fontWeight:700,color:"#E8962E",cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:0.5}}>
                View All {ml.length} Loads ›
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Download PDF Report Button ── */}
      <div style={{margin:"0 16px 32px"}}>
        <button
          className="tp-ripple-wrap"
          style={{width:"100%",padding:"16px",borderRadius:14,background:darkMode?"#E8962E":"#1C2B4A",color:"#fff",border:"none",cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:16,letterSpacing:"0.08em",boxShadow:darkMode?"0 4px 16px rgba(232,150,46,0.35)":"0 4px 16px rgba(28,43,74,0.3)",position:"relative",overflow:"hidden"}}
          onClick={(e)=>{tpRipple(e);{
            const rangeLabel=range==="custom"?`${customFrom||"Start"}_to_${customTo||"End"}`:{today:"Today",week:"Last 7 Days",month:"Last 30 Days",all:"This Year"}[range]||"Report";
            const displayLoads=ml.slice(0,50);
            const loadRows=displayLoads.map((l,idx)=>{
              const ownerEarn=Number(l.earnings||0);
              // Drivers see their own pay, not the owner's gross revenue
              const driverEarn=Number(l.driverBasePay||0)||Number(l.earnings||0);
              const earn=isOwner?ownerEarn:driverEarn;
              const drv=Number(l.driverBasePay||0);
              const wm=(Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0);
              const waitPay=isOwner?(wm/60*(Number(rates.companyWaitRate)||0)):(wm/60*(Number(rates.driverWaitRate)||0));
              const status=l.completed?"✓ Done":l.driverReceived?"Paid":"Active";
              const statusColor=l.completed?"#2E7D32":l.driverReceived?"#1565C0":"#E8962E";
              // Column order: #, Date, TMW#, Route, Route Pay, Wait Pay, Total, Status
              const total=earn+waitPay;
              return `<tr style="background:${idx%2===0?"#fff":"#F9FAFB"}">
                <td style="text-align:center;color:#888;font-size:11px;border-right:1px solid #eee">${idx+1}</td>
                <td>${l.date||"—"}</td>
                <td>${l.tmwLoadNumber?`<strong>#${l.tmwLoadNumber}</strong>`:"—"}</td>
                <td>${l.location||"—"}</td>
                <td style="text-align:right">$${earn.toFixed(2)}</td>
                <td style="text-align:right;color:#1565C0">$${waitPay.toFixed(2)}</td>
                <td style="text-align:right;font-weight:900;color:#2E7D32">$${total.toFixed(2)}</td>
                <td style="text-align:center;color:${statusColor};font-weight:700">${status}</td>
              </tr>`;
            }).join("");
            const reportCSS=`<style>
              body{font-family:Arial,sans-serif;color:#1A1A1A;font-size:13px}
              h2{font-size:13px;font-weight:800;color:#1C2B4A;margin:22px 0 8px;text-transform:uppercase;letter-spacing:1px;border-bottom:2px solid #E8962E;padding-bottom:5px}
              .summary-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:18px}
              .summary-card{background:#F5F8FF;border:1.5px solid #E0E8F5;border-radius:8px;padding:12px 14px;text-align:center}
              .s-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#888;margin-bottom:3px}
              .s-value{font-size:20px;font-weight:900;color:#1C2B4A}
              .s-value.green{color:#2E7D32}.s-value.red{color:#C62828}.s-value.blue{color:#1565C0}.s-value.orange{color:#E8962E}
              table{width:100%;border-collapse:collapse;margin-bottom:18px;font-size:12px;table-layout:fixed}
              thead tr{background:#1C2B4A}
              thead th{padding:9px 10px;text-align:left;color:#fff;font-size:10px;text-transform:uppercase;letter-spacing:0.8px;font-weight:700;white-space:nowrap}
              thead th.right{text-align:right}
              tbody tr:nth-child(even){background:#F9FAFB}
              tbody td{padding:8px 10px;border-bottom:1px solid #EEF0F2;vertical-align:middle;overflow:hidden;text-overflow:ellipsis}
              tbody td.right{text-align:right;font-weight:700;font-variant-numeric:tabular-nums}
              tfoot tr{background:#F0F4FF}
              tfoot td{padding:10px 10px;font-weight:900;font-size:13px;border-top:2px solid #E8962E;text-align:right;font-variant-numeric:tabular-nums;white-space:nowrap}
              tfoot td:first-child{text-align:left}
              .calc-box{background:#F0FDF4;border:1.5px solid #A5D6A7;border-radius:8px;padding:14px 18px;margin-bottom:16px}
              .calc-row{display:flex;justify-content:space-between;padding:5px 0;font-size:13px}
              .calc-row.total{border-top:2px solid #4CAF50;margin-top:8px;padding-top:10px;font-weight:900;font-size:15px}
              .brand,.footer,.header,.report-logo{display:none!important}
            </style>`;
            const calcSection=`
              <div class="calc-box">
                <div style="font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:1px;color:#2E7D32;margin-bottom:10px">Financial Summary — ${rangeLabel}</div>
                ${isOwner?`
                <div class="calc-row"><span>Gross Revenue</span><span style="color:#2E7D32;font-weight:700">+ $${gross.toFixed(2)}</span></div>
                <div class="calc-row"><span>Driver Pay</span><span style="color:#C62828;font-weight:700">− $${totalDrvPay.toFixed(2)}</span></div>
                <div class="calc-row"><span>Expenses</span><span style="color:#C62828;font-weight:700">− $${totalExp.toFixed(2)}</span></div>
                <div class="calc-row"><span>Wait Time Pay (billed)</span><span style="color:#1565C0;font-weight:700">+ $${wc.toFixed(2)}</span></div>
                <div class="calc-row total"><span>Net Profit</span><span style="color:${ownerNet>=0?"#2E7D32":"#C62828"}">$${ownerNet.toFixed(2)}</span></div>
                `:`
                <div class="calc-row"><span>Route Pay (all loads)</span><span style="color:#2E7D32;font-weight:700">+ $${drp.toFixed(2)}</span></div>
                <div class="calc-row"><span>Wait Pay</span><span style="color:#1565C0;font-weight:700">+ $${dwp.toFixed(2)}</span></div>
                <div class="calc-row"><span>Gross Earnings</span><span style="color:#1A1A1A;font-weight:700">$${(drp+dwp).toFixed(2)}</span></div>
                <div class="calc-row"><span>Personal Expenses</span><span style="color:#C62828;font-weight:700">− $${totalExpNoFuel.toFixed(2)}</span></div>
                <div class="calc-row total"><span>Net Pay</span><span style="color:${driverNet>=0?"#2E7D32":"#C62828"}">$${driverNet.toFixed(2)}</span></div>
                <div class="calc-row"><span>GST ITC Refund (Line 109)</span><span style="color:#2E7D32;font-weight:700">+ $${(totalExpNoFuel*0.05).toFixed(2)}</span></div>
                <div style="margin-top:14px;padding:10px 14px;background:#E8F5E9;border-radius:6px;border-left:3px solid #4CAF50">
                  <div style="font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:1px;color:#2E7D32;margin-bottom:4px">✅ CRA-Correct Income (Cash Received Only)</div>
                  <div style="font-size:18px;font-weight:900;color:#1A1A1A">$${(()=>{const cl=ml.filter(l=>l.driverReceived||l.payment_status==="paid");return cl.reduce((s,l)=>{const wm=(Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0);return s+Number(l.driverBasePay||0)+wm/60*(Number(rates.driverWaitRate)||0);},0);})().toFixed(2)}</div>
                  <div style="font-size:10px;color:#4B5563;margin-top:4px">Use this amount for T4A / CRA income reporting. Only includes loads where payment was confirmed received.</div>
                </div>
                `}
              </div>`;
            const summaryCards=`
              <div class="summary-grid">
                <div class="summary-card"><div class="s-label">Total Loads</div><div class="s-value">${ml.length}</div></div>
                <div class="summary-card"><div class="s-label">${isOwner?"Gross Revenue":"Route Pay"}</div><div class="s-value green">$${isOwner?gross.toFixed(2):drp.toFixed(2)}</div></div>
                <div class="summary-card"><div class="s-label">${isOwner?"Net Profit":"Net Pay"}</div><div class="s-value" style="color:${isOwner?(ownerNet>=0?"#2E7D32":"#C62828"):(driverNet>=0?"#2E7D32":"#C62828")}">$${isOwner?ownerNet.toFixed(2):driverNet.toFixed(2)}</div></div>
                ${!isOwner?`<div class="summary-card"><div class="s-label">GST ITC Refund</div><div class="s-value green">$${(totalExpNoFuel*0.05).toFixed(2)}</div></div>`:""}
                ${isOwner?`<div class="summary-card"><div class="s-label">Driver Pay</div><div class="s-value red">$${totalDrvPay.toFixed(2)}</div></div>
                <div class="summary-card"><div class="s-label">Expenses</div><div class="s-value red">$${totalExp.toFixed(2)}</div></div>
                <div class="summary-card"><div class="s-label">Wait Time Pay</div><div class="s-value blue">$${wc.toFixed(2)}</div></div>`:""}
              </div>`;
            const loadTable=`
              <h2>Load History (${ml.length} loads${ml.length>50?" · showing first 50":""})</h2>
              <table>
                <thead><tr>
                  <th style="width:30px;text-align:center">#</th>
                  <th>Date</th><th>TMW #</th><th>Route</th>
                  <th class="right">Route Pay</th>
                  <th class="right">Wait Pay</th>
                  <th class="right">Total</th>
                  <th style="text-align:center">Status</th>
                </tr></thead>
                <tbody>${loadRows}</tbody>
                <tfoot><tr>
                  <td colspan="4" style="text-align:right">TOTAL (${displayLoads.length} loads)</td>
                  <td style="text-align:right">$${(isOwner?gross:drp).toFixed(2)}</td>
                  <td style="text-align:right;color:#1565C0">$${wc.toFixed(2)}</td>
                  <td style="text-align:right;font-weight:900;color:#2E7D32">$${(isOwner?(gross+wc):(drp+wc)).toFixed(2)}</td>
                  <td></td>
                </tr></tfoot>
              </table>`;
            const expTable=Object.keys(expByCategory).length>0?`
              <h2>Expenses by Category</h2>
              <table>
                <thead><tr><th>#</th><th>Category</th><th class="right">Amount</th></tr></thead>
                <tbody>${Object.entries(expByCategory).map(([cat,amt],i)=>`<tr style="background:${i%2===0?"#fff":"#F9FAFB"}"><td style="text-align:center;color:#888;font-size:11px">${i+1}</td><td>${ECATS[cat]||cat}</td><td style="text-align:right;font-weight:700">$${amt.toFixed(2)}</td></tr>`).join("")}</tbody>
                <tfoot><tr><td colspan="2" style="text-align:right">Total Expenses</td><td style="text-align:right;color:#C62828">$${totalExp.toFixed(2)}</td></tr></tfoot>
              </table>`:"";
            const html=`${reportCSS}${summaryCards}${calcSection}${loadTable}${expTable}`;
            downloadPDF(html,`Report_${rangeLabel.replace(/ /g,"_")}_${todayStr()}`,session);
          }}}>
          📥 &nbsp;DOWNLOAD PDF REPORT
        </button>
      </div>
    </div>

    {/* ══════════════════════════════════════════════════════════════════ */}
    {/* ── OWNER: Contractor Payment History ─────────────────────────── */}
    {/* ══════════════════════════════════════════════════════════════════ */}
    {isOwner&&(()=>{
      const fmtPer=(d)=>{if(!d)return"";const dt=new Date(d+"T12:00:00");return dt.toLocaleDateString("en-CA",{month:"short",day:"numeric",year:"numeric"});};
      const paidLoads=loads.filter(l=>l.contractorPaid&&l.date);
      const pendingLoads=loads.filter(l=>l.completed&&!l.contractorPaid&&Number(l.earnings||0)>0);
      const pendingTotal=pendingLoads.reduce((s,l)=>s+Number(l.earnings||0),0);
      if(paidLoads.length===0&&pendingLoads.length===0)return null;
      // Group paid loads by month
      const byMonth={};
      paidLoads.forEach(l=>{
        const mk=(l.contractorPaidDate||l.date||"").slice(0,7);
        if(!byMonth[mk])byMonth[mk]={total:0,count:0,loads:[]};
        byMonth[mk].total+=Number(l.earnings||0);
        byMonth[mk].count++;
        byMonth[mk].loads.push(l);
      });
      const sortedMonths=Object.entries(byMonth).sort((a,b)=>b[0].localeCompare(a[0]));
      const allTimePaid=paidLoads.reduce((s,l)=>s+Number(l.earnings||0),0);
      return(
        <div style={{margin:"0 16px 12px",background:DM.cardBg,borderRadius:18,padding:"18px 16px",border:`1px solid ${DM.border}`}}>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:800,color:DM.text,marginBottom:4}}>💼 Contractor Payments Received</div>
          <div style={{fontSize:11,color:DM.textMut,marginBottom:14}}>Track when contractors pay your business for completed loads</div>
          {/* Summary boxes */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14}}>
            <div style={{background:"#1C2B4A",borderRadius:12,padding:"12px 10px",textAlign:"center"}}>
              <div style={{fontSize:9,fontWeight:800,color:"rgba(255,255,255,.55)",textTransform:"uppercase",letterSpacing:.7,marginBottom:4}}>All-Time Received</div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:20,fontWeight:900,color:"#E8962E"}}>{fmtC(allTimePaid)}</div>
              <div style={{fontSize:9,color:"rgba(255,255,255,.4)",marginTop:2}}>{paidLoads.length} load{paidLoads.length!==1?"s":""}</div>
            </div>
            <div style={{background:pendingTotal>0?"#92400E":"#166534",borderRadius:12,padding:"12px 10px",textAlign:"center"}}>
              <div style={{fontSize:9,fontWeight:800,color:"rgba(255,255,255,.55)",textTransform:"uppercase",letterSpacing:.7,marginBottom:4}}>Outstanding</div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:20,fontWeight:900,color:"#fff"}}>{fmtC(pendingTotal)}</div>
              <div style={{fontSize:9,color:"rgba(255,255,255,.4)",marginTop:2}}>{pendingLoads.length} invoice{pendingLoads.length!==1?"s":""} unpaid</div>
            </div>
          </div>
          {/* Outstanding invoices */}
          {pendingLoads.length>0&&(
            <div style={{background:"#FEF3C7",border:"1.5px solid #F59E0B",borderRadius:12,padding:"12px",marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:800,color:"#B45309",marginBottom:8}}>⏳ AWAITING PAYMENT</div>
              {pendingLoads.slice(0,5).map(l=>(
                <div key={l.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderBottom:"1px solid #FDE68A"}}>
                  <div>
                    <div style={{fontSize:12,fontWeight:700,color:"#1A1A1A"}}>{l.location||"Load"}</div>
                    <div style={{fontSize:10,color:"#6B7280"}}>{l.date}{l.driverFullName?` · ${l.driverFullName}`:""}</div>
                  </div>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:14,fontWeight:900,color:"#B45309"}}>{fmtC(Number(l.earnings||0))}</div>
                </div>
              ))}
              {pendingLoads.length>5&&<div style={{fontSize:11,color:"#B45309",textAlign:"center",paddingTop:6,fontWeight:700}}>+{pendingLoads.length-5} more</div>}
            </div>
          )}
          {/* Paid history by month */}
          {sortedMonths.map(([mk,data])=>{
            const [yr,mo]=mk.split("-");
            const mLabel=new Date(Number(yr),Number(mo)-1,1).toLocaleString("default",{month:"long",year:"numeric"});
            return(
              <div key={mk} style={{marginBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:`linear-gradient(135deg,${darkMode?"#0D1523":"#1C2B4A"},${darkMode?"#333":"#243655"})`,borderRadius:10,padding:"10px 14px",marginBottom:6}}>
                  <div>
                    <div style={{color:"rgba(255,255,255,.55)",fontSize:10,fontWeight:800,textTransform:"uppercase",letterSpacing:.7}}>{mLabel}</div>
                    <div style={{color:"rgba(255,255,255,.5)",fontSize:10}}>{data.count} payment{data.count!==1?"s":""}</div>
                  </div>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:900,color:"#E8962E"}}>{fmtC(data.total)}</div>
                </div>
                {data.loads.map(l=>(
                  <div key={l.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 10px",borderRadius:8,background:DM.cardBg2,marginBottom:4,border:`1px solid ${DM.border}`}}>
                    <div>
                      <div style={{fontSize:12,fontWeight:700,color:DM.text}}>{l.location||"Load"}</div>
                      <div style={{fontSize:10,color:DM.textMut}}>{fmtPer(l.contractorPaidDate)}{l.contractorPaidMethod?` · ${l.contractorPaidMethod.toUpperCase()}`:""}{l.driverFullName?` · ${l.driverFullName}`:""}</div>
                    </div>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:14,fontWeight:900,color:"#22C55E"}}>{fmtC(Number(l.earnings||0))}</div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      );
    })()}

    {/* ══════════════════════════════════════════════════════════════════ */}
    {/* ── DRIVER: My Payment History ─────────────────────────────────── */}
    {/* ══════════════════════════════════════════════════════════════════ */}
    {!isOwner&&(()=>{
      const myPaidLoads=loads.filter(l=>(l.assignedDriverUid===session.uid||l.addedBy===session.uid||l.user_id===session.uid)&&Number(l.driverBasePay||0)>0);
      const receivedLoads=myPaidLoads.filter(l=>l.driverReceived);
      const pendingLoads=myPaidLoads.filter(l=>l.driverPaid&&!l.driverReceived);
      const awaitingLoads=myPaidLoads.filter(l=>l.completed&&!l.driverPaid&&!l.driverReceived);
      const allTimePay=myPaidLoads.reduce((s,l)=>s+Number(l.driverBasePay||0),0);
      const receivedTotal=receivedLoads.reduce((s,l)=>s+Number(l.driverBasePay||0),0);
      if(myPaidLoads.length===0)return null;
      // Group confirmed-received loads by month
      const byMonth={};
      receivedLoads.forEach(l=>{
        const mk=(l.driverReceivedDate||l.date||"").slice(0,7);
        if(!byMonth[mk])byMonth[mk]={total:0,loads:[]};
        byMonth[mk].total+=Number(l.driverBasePay||0);
        byMonth[mk].loads.push(l);
      });
      const sortedMonths=Object.entries(byMonth).sort((a,b)=>b[0].localeCompare(a[0]));
      return(
        <div style={{margin:"0 16px 12px",background:DM.cardBg,borderRadius:18,padding:"18px 16px",border:`1px solid ${DM.border}`}}>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:800,color:DM.text,marginBottom:4}}>📜 My Payment History</div>
          <div style={{fontSize:11,color:DM.textMut,marginBottom:14}}>Payments from your owner for completed loads</div>
          {/* Summary boxes */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14}}>
            <div style={{background:"#1C2B4A",borderRadius:12,padding:"12px 10px",textAlign:"center"}}>
              <div style={{fontSize:9,fontWeight:800,color:"rgba(255,255,255,.55)",textTransform:"uppercase",letterSpacing:.7,marginBottom:4}}>Total Earned</div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:20,fontWeight:900,color:"#E8962E"}}>{fmtC(allTimePay)}</div>
              <div style={{fontSize:9,color:"rgba(255,255,255,.4)",marginTop:2}}>{myPaidLoads.length} load{myPaidLoads.length!==1?"s":""}</div>
            </div>
            <div style={{background:"#166534",borderRadius:12,padding:"12px 10px",textAlign:"center"}}>
              <div style={{fontSize:9,fontWeight:800,color:"rgba(255,255,255,.55)",textTransform:"uppercase",letterSpacing:.7,marginBottom:4}}>Confirmed Received</div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:20,fontWeight:900,color:"#fff"}}>{fmtC(receivedTotal)}</div>
              <div style={{fontSize:9,color:"rgba(255,255,255,.4)",marginTop:2}}>{receivedLoads.length} payment{receivedLoads.length!==1?"s":""}</div>
            </div>
          </div>
          {/* Pending confirmation */}
          {pendingLoads.length>0&&(
            <div style={{background:"#FEF3C7",border:"1.5px solid #F59E0B",borderRadius:12,padding:"12px",marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:800,color:"#B45309",marginBottom:8}}>⏳ PAYMENT SENT — TAP TO CONFIRM</div>
              {pendingLoads.map(l=>(
                <div key={l.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderBottom:"1px solid #FDE68A"}}>
                  <div>
                    <div style={{fontSize:12,fontWeight:700,color:"#1A1A1A"}}>{l.location||"Load"}</div>
                    <div style={{fontSize:10,color:"#6B7280"}}>{l.date}{l.driverPaidDate?` · Sent ${l.driverPaidDate}`:""}</div>
                  </div>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:14,fontWeight:900,color:"#E8962E"}}>{fmtC(Number(l.driverBasePay||0))}</div>
                </div>
              ))}
            </div>
          )}
          {/* Awaiting payment */}
          {awaitingLoads.length>0&&(
            <div style={{background:darkMode?"rgba(239,68,68,.1)":"#FFF5F5",border:`1px solid ${darkMode?"rgba(239,68,68,.2)":"#FCA5A5"}`,borderRadius:12,padding:"12px",marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:800,color:"#EF4444",marginBottom:8}}>📋 COMPLETED — AWAITING PAYMENT ({awaitingLoads.length} load{awaitingLoads.length!==1?"s":""})</div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:900,color:"#EF4444"}}>{fmtC(awaitingLoads.reduce((s,l)=>s+Number(l.driverBasePay||0),0))}</div>
            </div>
          )}
          {/* Confirmed payments by month */}
          {sortedMonths.map(([mk,data])=>{
            const [yr,mo]=mk.split("-");
            const mLabel=new Date(Number(yr),Number(mo)-1,1).toLocaleString("default",{month:"long",year:"numeric"});
            return(
              <div key={mk} style={{marginBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:`linear-gradient(135deg,${darkMode?"#0D1523":"#1C2B4A"},${darkMode?"#333":"#243655"})`,borderRadius:10,padding:"10px 14px",marginBottom:6}}>
                  <div>
                    <div style={{color:"rgba(255,255,255,.55)",fontSize:10,fontWeight:800,textTransform:"uppercase",letterSpacing:.7}}>{mLabel}</div>
                    <div style={{color:"rgba(255,255,255,.5)",fontSize:10}}>{data.loads.length} payment{data.loads.length!==1?"s":""}</div>
                  </div>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:900,color:"#22C55E"}}>{fmtC(data.total)}</div>
                </div>
                {data.loads.map(l=>(
                  <div key={l.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 10px",borderRadius:8,background:DM.cardBg2,marginBottom:4,border:`1px solid ${DM.border}`}}>
                    <div>
                      <div style={{fontSize:12,fontWeight:700,color:DM.text}}>{l.location||"Load"}</div>
                      <div style={{fontSize:10,color:DM.textMut}}>{l.date}{l.driverReceivedDate?` · Received ${l.driverReceivedDate}`:""}</div>
                    </div>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:14,fontWeight:900,color:"#22C55E"}}>{fmtC(Number(l.driverBasePay||0))}</div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      );
    })()}

    </>
  );
}


// ─── RESTAURANT FINDER ───────────────────────────────────────────────────────
function ProfitTab({ isOwner }) {
  const [form,setForm]=useState({gross:"",fuel:"",driverPay:"",maintenance:"",tolls:"",other:"",routePay:"",waitPay:"",meals:"",lodging:""});
  const hc=e=>setForm(f=>({...f,[e.target.name]:e.target.value}));
  const reset=()=>setForm({gross:"",fuel:"",driverPay:"",maintenance:"",tolls:"",other:"",routePay:"",waitPay:"",meals:"",lodging:""});
  if(isOwner){
    const g=Number(form.gross)||0;const costs=(Number(form.fuel)||0)+(Number(form.driverPay)||0)+(Number(form.maintenance)||0)+(Number(form.tolls)||0)+(Number(form.other)||0);const profit=g-costs;const margin=g>0?((profit/g)*100).toFixed(1):0;
    return(<div className="slt-page" style={{background:"#070B14",color:"#F0F0F0"}}><div className="slt-hero"><div className="slt-hero-title">Profit Calculator</div><div className="slt-hero-sub">True take-home after all costs</div></div>
      <div className="slt-container-sm">
        {g>0&&<div style={{background:profit>=500?`linear-gradient(135deg,${C.green},#1B5E20)`:profit>=0?`linear-gradient(135deg,${C.orange},#BF360C)`:`linear-gradient(135deg,${C.red},#B71C1C)`,borderRadius:16,padding:"24px 28px",marginBottom:20,color:"#fff"}}>
          <div style={{fontSize:13,opacity:0.85,marginBottom:4}}>Owner Take-Home</div>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:40,fontWeight:800,marginBottom:8}}>{fmtC(profit)}</div>
          <div style={{fontSize:16,fontWeight:700}}>{profit>=500?"✅ Take It!":profit>=0?"⚠️ Marginal":"❌ Leave It!"}</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginTop:16}}>{[["Gross",fmtC(g)],["Costs",fmtC(costs)],["Margin",margin+"%"]].map(([l,v])=><div key={l} style={{background:"rgba(255,255,255,0.15)",borderRadius:9,padding:"10px 12px",textAlign:"center"}}><div style={{fontSize:12,opacity:0.8,marginBottom:2}}>{l}</div><div style={{fontSize:16,fontWeight:800}}>{v}</div></div>)}</div>
        </div>}
        <div className="slt-card">
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,color:C.green,marginBottom:14,fontSize:16}}>Revenue</div>
          <div style={{marginBottom:14}}><label className="slt-label">Gross Revenue ($)</label><input name="gross" type="number" placeholder="2500" value={form.gross} onChange={hc} className="slt-input" style={{fontSize:18}}/></div>
          <hr className="slt-divider"/>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,color:C.red,marginBottom:14,fontSize:16}}>Costs</div>
          {[["fuel","⛽ Fuel"],["driverPay","👤 Driver Pay"],["maintenance","🔧 Maintenance"],["tolls","🛣 Tolls"],["other","📦 Other"]].map(([n,l])=><div key={n} style={{marginBottom:12}}><label className="slt-label">{l} ($)</label><input name={n} type="number" value={form[n]} onChange={hc} className="slt-input"/></div>)}
          <button className="slt-btn-secondary" style={{width:"100%",marginTop:4}} onClick={reset}>Reset</button>
        </div>
      </div>
    </div>);
  }
  const gp=(Number(form.routePay)||0)+(Number(form.waitPay)||0);const te=(Number(form.meals)||0)+(Number(form.lodging)||0)+(Number(form.tolls)||0)+(Number(form.other)||0);const nh=gp-te;
  return(<div className="slt-page" style={{background:"#070B14",color:"#F0F0F0"}}><div className="slt-hero"><div className="slt-hero-title">Pay Calculator</div><div className="slt-hero-sub">Take-home after trip expenses</div></div>
    <div className="slt-container-sm">
      {gp>0&&<div style={{background:nh>=300?`linear-gradient(135deg,${C.green},#1B5E20)`:`linear-gradient(135deg,${C.red},#B71C1C)`,borderRadius:16,padding:"24px 28px",marginBottom:20,color:"#fff"}}><div style={{fontSize:13,opacity:0.85}}>Your Take-Home</div><div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:40,fontWeight:800}}>{fmtC(nh)}</div></div>}
      <div className="slt-card">
        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,color:C.blue,marginBottom:14,fontSize:16}}>Your Pay</div>
        {[["routePay","Route Pay"],["waitPay","Wait Pay"]].map(([n,l])=><div key={n} style={{marginBottom:12}}><label className="slt-label">{l} ($)</label><input name={n} type="number" value={form[n]} onChange={hc} className="slt-input" style={{fontSize:18}}/></div>)}
        <hr className="slt-divider"/>
        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,color:C.red,marginBottom:14,fontSize:16}}>Expenses</div>
        {[["meals","🍽 Meals"],["lodging","🏨 Lodging"],["tolls","🛣 Tolls"],["other","📦 Other"]].map(([n,l])=><div key={n} style={{marginBottom:12}}><label className="slt-label">{l} ($)</label><input name={n} type="number" value={form[n]} onChange={hc} className="slt-input"/></div>)}
        <button className="slt-btn-secondary" style={{width:"100%",marginTop:4}} onClick={reset}>Reset</button>
      </div>
    </div>
  </div>);
}

// ─── MAINTENANCE TAB ──────────────────────────────────────────────────────────
function MaintenanceTab({ session, trucks, goBack }) {
  const key=maintenanceKey(session.ownerUid||session.uid);
  const [records,setRecords]=useState([]);
  useEffect(()=>{
    const ownerUid = session.ownerUid||session.uid;
    sbGetMaintenance(ownerUid).then(data=>{
      if(data.length>0) {
        setRecords(data);
        localStorage.setItem(key, JSON.stringify(data));
      } else {
        setRecords(getStored(key));
      }
    }).catch(()=>setRecords(getStored(key)));
  },[session.uid]);
  const [showAdd,setShowAdd]=useState(false);
  const [form,setForm]=useState({truckId:"",type:"oil_change",date:todayStr(),odometer:"",cost:"",notes:"",nextDueKm:""});
  const hc=e=>setForm(f=>({...f,[e.target.name]:e.target.value}));
  const TYPES=[["oil_change","🛢","Oil Change",C.orange],["tires","🔄","Tires",C.blue],["brakes","🛑","Brakes",C.red],["repair","🔧","Repair",C.purple],["service","⚙","Service",C.green],["inspection","📋","Inspection",C.textMed]];
  const ti=t=>TYPES.find(([id])=>id===t)||["","🔧","Service",C.textMed];
  const saveR=()=>{ if(!form.type)return; const record={...form,id:Date.now().toString()}; const u=[record,...records]; setRecords(u); localStorage.setItem(key,JSON.stringify(u)); sbSaveMaintenance(record, session.ownerUid||session.uid).catch(console.error); setShowAdd(false); };
  const delR=(id)=>{ const u=records.filter(x=>x.id!==id); setRecords(u); localStorage.setItem(key,JSON.stringify(u)); sbDeleteMaintenance(id).catch(console.error); };
  return(
    <div className="slt-page" style={{background:"#070B14",color:"#F0F0F0"}}>
      {goBack && <BackButton onBack={goBack} label="Back" />}
      <div className="slt-hero"><div style={{fontSize:10,fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"rgba(255,255,255,0.35)",marginBottom:3}}>TRUCKPILOT</div><div className="slt-hero-title">Maintenance</div><div className="slt-hero-sub">Oil changes, tires, brakes & service records</div></div>
      <div className="slt-container">
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:18}}>
          {TYPES.map(([id,icon,label,color])=><div key={id} className="slt-card-sm" style={{textAlign:"center",borderTop:`3px solid ${color}`,padding:"12px 8px"}}><div style={{fontSize:22}}>{icon}</div><div style={{fontSize:13,color:C.textDarkMed,fontWeight:700,marginTop:4}}>{label}</div><div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:800,color,marginTop:2}}>{records.filter(r=>r.type===id).length}</div></div>)}
        </div>
        <button className="slt-btn-primary slt-btn-dark-text" style={{marginBottom:16}} onClick={()=>setShowAdd(!showAdd)}>{showAdd?"Cancel":"+ Add Record"}</button>
        {showAdd&&<div className="slt-card" style={{border:`2px solid ${C.blue}`}}>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:16}}>
            {TYPES.map(([id,icon,label,color])=><button key={id} onClick={()=>setForm(f=>({...f,type:id}))} style={{padding:"10px 6px",borderRadius:9,border:`2px solid ${form.type===id?color:C.border}`,background:form.type===id?color+"18":C.white,cursor:"pointer",fontFamily:"'Barlow',sans-serif"}}><div style={{fontSize:20}}>{icon}</div><div style={{fontSize:13,fontWeight:700,color:form.type===id?color:C.textDarkMed,marginTop:3}}>{label}</div></button>)}
          </div>
          {trucks.length>0&&<div style={{marginBottom:12}}><label className="slt-label" style={{display:"block",fontSize:14,fontWeight:900,color:"#E8962E",marginBottom:6,fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:0.5,textTransform:"uppercase"}}>Truck</label><select name="truckId" value={form.truckId} onChange={hc} className="slt-input"><option value="">— Select —</option>{trucks.map(t=><option key={t.id} value={t.id}>Truck {t.truckNumber}</option>)}</select></div>}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            <div><label className="slt-label" style={{display:"block",fontSize:14,fontWeight:900,color:"#E8962E",marginBottom:6,fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:0.5,textTransform:"uppercase"}}>Date</label><input name="date" type="date" value={form.date} onChange={hc} className="slt-input"/></div>
            <div><label className="slt-label">Odometer (km)</label><input name="odometer" type="number" value={form.odometer} onChange={hc} className="slt-input"/></div>
            <div><label className="slt-label">Cost ($)</label><input name="cost" type="number" value={form.cost} onChange={hc} className="slt-input"/></div>
            <div><label className="slt-label">Next Due (km)</label><input name="nextDueKm" type="number" value={form.nextDueKm} onChange={hc} className="slt-input"/></div>
          </div>
          <div style={{marginBottom:14}}><label className="slt-label">Notes</label><input name="notes" value={form.notes} onChange={hc} className="slt-input" placeholder="e.g. Full synthetic 5W-30"/></div>
          <button className="slt-btn-primary slt-btn-dark-text" style={{width:"100%"}} onClick={saveR}>Save Record</button>
        </div>}
        {records.length===0&&!showAdd?<div className="slt-card" style={{textAlign:"center",padding:"44px"}}><div style={{fontSize:38,marginBottom:10}}>🔧</div><div style={{color:C.textDarkMed}}>No records yet</div></div>
        :[...records].sort((a,b)=>b.date>a.date?1:-1).map(r=>{ const [,icon,label,color]=ti(r.type); const truck=trucks.find(t=>t.id===r.truckId); const kl=(Number(r.nextDueKm)>0)?Number(r.nextDueKm)-(Number(r.odometer)||0):null; return(
          <div key={r.id} className="slt-card" style={{borderLeft:`4px solid ${color}`}}>
            <div style={{display:"flex",justifyContent:"space-between"}}>
              <div>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}><span style={{fontSize:20}}>{icon}</span><span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:15}}>{label}</span>{kl!==null&&kl<=2000&&<span className="slt-badge-red">⚠ Due Soon</span>}</div>
                {truck&&<div style={{fontSize:13,color:C.orange,marginBottom:2}}>🚛 Truck {truck.truckNumber}</div>}
                <div style={{fontSize:12.5,color:C.textDarkMut}}>{r.date}{r.odometer?` · ${Number(r.odometer).toLocaleString()} km`:""}</div>
                {r.notes&&<div style={{fontSize:13,color:C.textDarkMed,marginTop:4,fontStyle:"italic"}}>{r.notes}</div>}
                {kl!==null&&<div style={{fontSize:12,color:kl<=2000?C.red:C.green,marginTop:4}}>{kl<=2000?`⚠ Due in ${kl.toLocaleString()} km`:`✓ Next in ${kl.toLocaleString()} km`}</div>}
              </div>
              <div style={{textAlign:"right"}}>
                {r.cost&&<div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:800,color:C.red}}>{fmtC(r.cost)}</div>}
                <button className="slt-btn-danger" style={{padding:"6px 12px",marginTop:8,fontSize:12}} onClick={()=>delR(r.id)}>Delete</button>
              </div>
            </div>
          </div>
        );})}
      </div>
    </div>
  );
}

// ─── SETTINGS MODAL ───────────────────────────────────────────────────────────
function SettingsModal({ session, rates, setRates, customRoutes, setCustomRoutes, trucks, setTrucks, onClose, isOwner, darkMode=false }) {
  const DM = {
    pageBg:  darkMode ? "#070B14" : "#F5F6F8",
    cardBg:  darkMode ? "#0D1523" : "#FFFFFF",
    border:  darkMode ? "rgba(255,255,255,0.07)" : "#E2E2E2",
    text:    darkMode ? "#F0F0F0" : "#1A1A1A",
    textMed: darkMode ? "rgba(255,255,255,0.5)" : "#4B5563",
    textMut: darkMode ? "rgba(255,255,255,0.35)" : "#6B7280",
    inputBg: darkMode ? "#111C2E" : "#FFFFFF",
  };
  useEffect(()=>{
    const y=window.scrollY;
    document.body.style.position='fixed';
    document.body.style.top='-'+y+'px';
    document.body.style.width='100%';
    document.body.style.overflow='hidden';
    return ()=>{
      document.body.style.position='';
      document.body.style.top='';
      document.body.style.width='';
      document.body.style.overflow='';
      window.scrollTo(0,y);
    };
  },[]);
  const [lr,setLr]=useState({...DEFAULT_RATES,...rates});
  const [lRoutes,setLRoutes]=useState([...customRoutes]);
  const [lTrucks,setLTrucks]=useState([...trucks]);
  const [sec,setSec]=useState("rates");
  const [nr,setNr]=useState({from:"",to:"",billingMethod:"per_load",ratePerLoad:"",rateCubic:"",rateHour:"",driverPay:"",driverPct:"",ratePerKm:"",cubicDriverMode:"flat"});
  const [nt,setNt]=useState({truckNumber:"",trailerNumber:""});
  const [expandedRoute,setExpandedRoute]=useState(null);
  const [editingRoute,setEditingRoute]=useState(null);
  // Fleet drivers see their OWN settings (saved to their own UID, separate from owner)
  const isFleetDriver = !isOwner && session.role === "driver";
  // All hooks are above this line — safe to return early now
  const [editingTruck,setEditingTruck]=useState(null);

  // Lock background scroll while modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    };
  }, []);

  // Always save to the current user's own UID — never the owner's
  const saveUid = session.uid;

  const save=async()=>{
    setRates(lr); setCustomRoutes(lRoutes); setTrucks(lTrucks);
    // Always overwrite localStorage with latest data — never let stale cache win
    localStorage.setItem(ratesKey(saveUid),JSON.stringify(lr));
    localStorage.setItem(routesKey(saveUid),JSON.stringify(lRoutes));
    localStorage.setItem(trucksKey(saveUid),JSON.stringify(lTrucks));
    // Save to Supabase and WAIT for it to complete before closing
    try {
      await Promise.all([
        sbSaveSettings(saveUid, lr, lRoutes),
        lTrucks.length > 0 ? sbSaveTrucks(lTrucks, saveUid) : sb.from("trucks").delete().eq("user_id", saveUid),
      ]);
    } catch(e) { console.error("Settings save error:", e); }
    onClose();
  };

  const BILLING=[{id:"per_load",label:"Per Load",icon:"📦",desc:"Flat rate per trip"},{id:"per_cubic",label:"Per Cubic",icon:"📐",desc:"$/yd³ × quantity"},{id:"per_hour",label:"Per Hour",icon:"⏱",desc:"$/hr × hours worked"},{id:"per_pct",label:"% of Earnings",icon:"💯",desc:"Company % & driver % of load earnings"},{id:"per_km",label:"Per KM/Mile",icon:"🛣",desc:"Rate × distance driven"}];

  const addRoute=()=>{
    if(!nr.from.trim()||!nr.to.trim())return;
    setLRoutes(r=>[...r,{id:Date.now().toString(),from:nr.from.trim(),to:nr.to.trim(),billingMethod:nr.billingMethod,ratePerLoad:Number(nr.ratePerLoad)||0,rateCubic:Number(nr.rateCubic)||0,rateHour:Number(nr.rateHour)||0,driverPay:Number(nr.driverPay)||0,driverPct:Number(nr.driverPct)||0,cubicDriverMode:nr.cubicDriverMode||"flat",driverOverrides:{},rate:nr.billingMethod==="per_load"?Number(nr.ratePerLoad)||0:nr.billingMethod==="per_cubic"?Number(nr.rateCubic)||0:nr.billingMethod==="per_pct"?Number(nr.ratePerLoad)||0:Number(nr.rateHour)||0,pay:nr.billingMethod==="per_pct"?(Number(nr.ratePerLoad)||0)*(Number(nr.driverPct)||0)/100:(nr.billingMethod==="per_cubic"&&(nr.cubicDriverMode||"flat")==="pct")?0:Number(nr.driverPay)||0}]);
    setNr({from:"",to:"",billingMethod:"per_load",ratePerLoad:"",rateCubic:"",rateHour:"",driverPay:"",driverPct:"",cubicDriverMode:"flat"});
  };
  const addTruck=()=>{ if(!nt.truckNumber.trim())return; const ex=lTrucks.map(t=>parseInt(t.tmwNumber)||0); const tmw=(Math.max(1000,...ex)+1).toString(); setLTrucks(t=>[...t,{...nt,tmwNumber:tmw,id:Date.now().toString()}]); setNt({truckNumber:"",trailerNumber:""}); };

  const rateDisplay=(r)=>{ if((r.billingMethod||"per_load")==="per_cubic"){const cubicRate=`$${Number(r.rateCubic||r.rate||0).toFixed(2)}/yd³`;return(r.cubicDriverMode||"flat")==="pct"?`${cubicRate} · ${r.driverPct||0}% driver`:cubicRate;} if((r.billingMethod||"per_load")==="per_hour")return`$${Number(r.rateHour||r.rate||0).toFixed(2)}/hr`; if((r.billingMethod||"per_load")==="per_pct")return`$${Number(r.ratePerLoad||r.rate||0).toFixed(2)} · ${r.driverPct||0}% driver`; return`$${Number(r.ratePerLoad||r.rate||0).toFixed(2)}/load`; };

  const setDriverOverride=(routeIdx,driverUid,val)=>{
    setLRoutes(rs=>rs.map((r,i)=>i===routeIdx?{...r,driverOverrides:{...(r.driverOverrides||{}), [driverUid]:val}}:r));
  };

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:300,display:"flex",alignItems:"stretch",justifyContent:"center"}} onClick={onClose}>
      <div style={{background:DM.cardBg,width:"100%",maxWidth:540,height:"100%",display:"flex",flexDirection:"column",boxShadow:"0 -8px 40px rgba(0,0,0,0.55)"}} onClick={e=>e.stopPropagation()}>
        {/* Sticky header */}
        <div style={{paddingTop:"env(safe-area-inset-top, 20px)",paddingLeft:"24px",paddingRight:"24px",paddingBottom:"14px",borderBottom:`1px solid ${DM.border}`,flexShrink:0,background:DM.cardBg}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:18,color:DM.text}}>⚙ Settings</div>
            <button className="slt-btn-ghost" style={{padding:"6px 12px",color:DM.textMed}} onClick={onClose}>✕</button>
          </div>
          {isFleetDriver && (
            <div style={{background:"#F5F6F8",border:"1px solid #FFD580",borderRadius:8,padding:"8px 12px",marginBottom:12,fontSize:12,color:"#E8962E"}}>
              👤 <strong>Your Personal Settings</strong> — these apply to your "My Own Load" entries only. Your fleet owner's settings load automatically for fleet loads.
            </div>
          )}
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {[["rates","Rates"],["routes","Routes"],["trucks","Trucks"],["schedule","Pay Schedule"]].map(([v,l])=>(
              <button key={v} onClick={()=>setSec(v)} className="slt-btn-secondary" style={{background:sec===v?C.blue:C.white,color:sec===v?"#fff":C.textMed,borderColor:sec===v?C.blue:C.border,padding:"8px 14px",fontSize:13}}>{l}</button>
            ))}
          </div>
        </div>
        {/* Scrollable content */}
        <div style={{flex:1,overflowY:"auto",WebkitOverflowScrolling:"touch",minHeight:0,background:DM.pageBg}}>
        <div style={{padding:"20px 24px 100px"}}>
          {sec==="rates"&&(<div>
            {isFleetDriver ? (<>
              <div style={{fontSize:12,color:C.textDarkMed,marginBottom:16}}>Used when you log "My Own Load". Fleet loads use your owner's rates automatically.</div>
              <div style={{marginBottom:14}}>
                <label className="slt-label">💵 My Pay Per Load ($)</label>
                <input type="number" value={lr.perLoadRate||""} onChange={e=>setLr(r=>({...r,perLoadRate:e.target.value}))} className="slt-input" placeholder="e.g. 500"/>
              </div>
              <div style={{marginBottom:14}}>
                <label className="slt-label">⏱ My Wait Rate ($/hr)</label>
                <input type="number" value={lr.driverWaitRate||""} onChange={e=>setLr(r=>({...r,driverWaitRate:e.target.value}))} className="slt-input" placeholder="e.g. 40"/>
              </div>
            </>) : (<>
              <div style={{marginBottom:14}}>
                <label className="slt-label">🏢 Company Wait Rate ($/hr)</label>
                <input type="number" value={lr.companyWaitRate||""} onChange={e=>setLr(r=>({...r,companyWaitRate:e.target.value}))} className="slt-input" placeholder="e.g. 85"/>
              </div>
              <div style={{marginBottom:14}}>
                <label className="slt-label">👤 Driver Wait Rate ($/hr)</label>
                <input type="number" value={lr.driverWaitRate||""} onChange={e=>setLr(r=>({...r,driverWaitRate:e.target.value}))} className="slt-input" placeholder="e.g. 40"/>
              </div>
            </>)}
          </div>)}

          {sec==="schedule"&&(<div>
            <div style={{fontSize:12,color:C.textDarkMed,marginBottom:16}}>{isFleetDriver?"Your pay period and upcoming pay date from your fleet owner.":"Set your pay period and when drivers get paid."}</div>
            <div style={{marginBottom:14}}>
              <label className="slt-label">📅 Period Start</label>
              <input type="date" value={lr.periodStart||""} onChange={e=>setLr(r=>({...r,periodStart:e.target.value}))} className="slt-input"/>
            </div>
            <div style={{marginBottom:14}}>
              <label className="slt-label">🏁 Period End</label>
              <input type="date" value={lr.periodEnd||""} onChange={e=>setLr(r=>({...r,periodEnd:e.target.value}))} className="slt-input"/>
              {lr.periodStart&&lr.periodEnd&&<div style={{fontSize:13,color:C.blue,marginTop:4,fontWeight:700}}>
                {Math.max(0,Math.round((new Date(lr.periodEnd+"T12:00:00")-new Date(lr.periodStart+"T12:00:00"))/(1000*60*60*24)))} day period
              </div>}
            </div>
            <div style={{marginBottom:16}}>
              <label className="slt-label">💸 Pay Date</label>
              <input type="date" value={lr.payDate||""} onChange={e=>setLr(r=>({...r,payDate:e.target.value}))} className="slt-input"/>
              {lr.payDate&&<div style={{fontSize:13,color:C.green,marginTop:4,fontWeight:700}}>{isFleetDriver?"You get paid on":"Drivers paid on"} {new Date(lr.payDate+"T12:00:00").toLocaleDateString("en-CA",{weekday:"long",month:"short",day:"numeric"})}</div>}
            </div>
            {lr.periodStart&&lr.periodEnd&&lr.payDate&&(()=>{
              const start=new Date(lr.periodStart+"T12:00:00");
              const end=new Date(lr.periodEnd+"T12:00:00");
              const pay=new Date(lr.payDate+"T12:00:00");
              const cycleLen=Math.round((end-start)/(1000*60*60*24))+1;
              const nextStart=new Date(end); nextStart.setDate(end.getDate()+1);
              const nextEnd=new Date(nextStart); nextEnd.setDate(nextStart.getDate()+cycleLen-1);
              const nextPay=new Date(pay); nextPay.setDate(pay.getDate()+cycleLen);
              const fmt=d=>d.toISOString().split("T")[0];
              const daysUntilPay=Math.max(0,Math.round((pay-new Date())/(1000*60*60*24)));
              return(
                <div style={{background:"linear-gradient(135deg,#E8962E,#E8962E)",borderRadius:12,padding:16}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
                    <div style={{background:"rgba(255,255,255,0.08)",borderRadius:8,padding:10}}>
                      <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",marginBottom:3,textTransform:"uppercase"}}>Current Period</div>
                      <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:13,color:"#fff"}}>{start.toLocaleDateString("en-CA",{month:"short",day:"numeric"})} → {end.toLocaleDateString("en-CA",{month:"short",day:"numeric"})}</div>
                    </div>
                    <div style={{background:"rgba(255,215,0,0.15)",borderRadius:8,padding:10,border:"1px solid rgba(255,215,0,0.3)"}}>
                      <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",marginBottom:3,textTransform:"uppercase"}}>Pay Date</div>
                      <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:15,color:"#FFD700"}}>{pay.toLocaleDateString("en-CA",{month:"short",day:"numeric"})}</div>
                      <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginTop:1}}>in {daysUntilPay} day{daysUntilPay!==1?"s":""}</div>
                    </div>
                  </div>
                  <button onClick={()=>setLr(r=>({...r,periodStart:fmt(nextStart),periodEnd:fmt(nextEnd),payDate:fmt(nextPay)}))}
                    style={{width:"100%",padding:"10px",borderRadius:10,background:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.3)",color:"#fff",fontWeight:800,fontSize:13,cursor:"pointer"}}>
                    ➡️ Advance to Next Cycle
                  </button>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.55)",marginTop:8,textAlign:"center",lineHeight:1.4}}>
                    Moves the pay period forward by {cycleLen} day{cycleLen!==1?"s":""} — next period: {nextStart.toLocaleDateString("en-CA",{month:"short",day:"numeric"})} → {nextEnd.toLocaleDateString("en-CA",{month:"short",day:"numeric"})}, pay date: {nextPay.toLocaleDateString("en-CA",{month:"short",day:"numeric"})}
                  </div>
                </div>
              );
            })()}
          </div>)}

          {sec==="routes"&&(<div>
            {/* Driver routes — with billing methods same as owner */}
            {isFleetDriver ? (<>
              {lRoutes.map((r,i)=>(
                <div key={i} className="slt-card-sm" style={{borderLeft:'4px solid #E8962E',marginBottom:8}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div>
                      <div style={{fontWeight:700,fontSize:14}}>{r.from} → {r.to}</div>
                      <div style={{fontSize:12,color:C.textDarkMed,marginTop:2}}>
                        <span style={{background:C.blueLight,color:C.blue,borderRadius:10,padding:"1px 8px",fontSize:13,fontWeight:700,marginRight:6}}>{(r.billingMethod||"per_load").replace(/_/g," ")}</span>
                        {(r.billingMethod||"per_load")==="per_load"&&<span style={{color:C.blue}}>Your Pay: ${Number(r.driverPay||r.pay||0).toFixed(2)}/load{r.ratePerLoad>0&&<span style={{color:C.textDarkMed,fontWeight:400}}> · Co. ${Number(r.ratePerLoad).toFixed(2)}</span>}</span>}
                        {r.billingMethod==="per_cubic"&&<span style={{color:C.blue}}>Your Pay: ${Number(r.driverPay||r.pay||0).toFixed(2)} flat{r.rateCubic>0&&<span style={{color:C.textDarkMed,fontWeight:400}}> · Co. ${Number(r.rateCubic).toFixed(2)}/yd³</span>}</span>}
                        {r.billingMethod==="per_hour"&&<span style={{color:C.blue}}>Your Pay: ${Number(r.driverPay||r.pay||0).toFixed(2)}/hr{r.rateHour>0&&<span style={{color:C.textDarkMed,fontWeight:400}}> · Co. ${Number(r.rateHour).toFixed(2)}</span>}</span>}
                        {r.billingMethod==="per_pct"&&<span style={{color:C.blue}}>Your Rate: {r.driverPct||0}%{r.companyPct>0&&<span style={{color:C.textDarkMed,fontWeight:400}}> · Co. {r.companyPct}%</span>}</span>}
                        {r.billingMethod==="per_km"&&<span style={{color:C.blue}}>Your Pay: ${Number(r.driverPay||r.pay||0).toFixed(2)}/km{r.ratePerKm>0&&<span style={{color:C.textDarkMed,fontWeight:400}}> · Co. ${Number(r.ratePerKm).toFixed(2)}</span>}</span>}
                      </div>
                    </div>
                    <button onClick={()=>setLRoutes(rs=>rs.filter((_,j)=>j!==i))} style={{background:"none",border:"none",color:C.red,cursor:"pointer",fontSize:18,padding:"4px 8px"}}>🗑</button>
                  </div>
                </div>
              ))}
              {/* Add new route form */}
              <div style={{background:C.offWhite,borderRadius:12,padding:"14px",marginTop:10,border:`1.5px dashed ${C.border}`}}>
                <div style={{fontWeight:700,fontSize:13,color:C.textDark,marginBottom:10}}>+ Add Route</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                  <div><label className="slt-label">From (Origin)</label><input value={nr.from} onChange={e=>setNr(r=>({...r,from:e.target.value}))} className="slt-input" placeholder="e.g. CNRL Horizon, Suncor Base, Pit 4"/></div>
                  <div><label className="slt-label">To (Destination)</label><input value={nr.to} onChange={e=>setNr(r=>({...r,to:e.target.value}))} className="slt-input" placeholder="e.g. Heartland, Upgrader Dock, Plant Gate"/></div>
                </div>
                <div style={{marginBottom:10}}>
                  <label className="slt-label">Billing Method</label>
                  <select value={nr.billingMethod||"per_load"} onChange={e=>setNr(r=>({...r,billingMethod:e.target.value}))} className="slt-input">
                    <option value="per_load">📦 Per Load — flat rate per trip</option>
                    <option value="per_cubic">📐 Per Cubic Yard — rate × quantity</option>
                    <option value="per_hour">⏱ Per Hour — rate × hours worked</option>
                    <option value="per_pct">💯 % of Earnings — percentage of load value</option>
                    <option value="per_km">🛣 Per KM/Mile — rate × distance</option>
                  </select>
                </div>
                {(nr.billingMethod||"per_load")==="per_load"&&(
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                    <div><label className="slt-label" style={{color:C.textDarkMed}}>Company Earning/Load ($) <span style={{fontWeight:400,fontSize:10,opacity:0.7}}>(optional)</span></label><input type="number" step="0.01" value={nr.ratePerLoad} onChange={e=>setNr(r=>({...r,ratePerLoad:e.target.value}))} className="slt-input" placeholder="e.g. 1200"/></div>
                    <div><label className="slt-label" style={{color:C.blue,fontWeight:800}}>Your Pay per Load ($)</label><input type="number" step="0.01" value={nr.driverPay} onChange={e=>setNr(r=>({...r,driverPay:e.target.value}))} className="slt-input" placeholder="e.g. 500"/></div>
                  </div>
                )}
                {nr.billingMethod==="per_cubic"&&(
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                    <div><label className="slt-label" style={{color:C.textDarkMed}}>Company Rate/yd³ ($) <span style={{fontWeight:400,fontSize:10,opacity:0.7}}>(optional)</span></label><input type="number" step="0.01" value={nr.rateCubic} onChange={e=>setNr(r=>({...r,rateCubic:e.target.value}))} className="slt-input" placeholder="e.g. 40"/></div>
                    <div><label className="slt-label" style={{color:C.blue,fontWeight:800}}>Your Pay (flat $)</label><input type="number" step="0.01" value={nr.driverPay} onChange={e=>setNr(r=>({...r,driverPay:e.target.value}))} className="slt-input" placeholder="e.g. 500"/></div>
                  </div>
                )}
                {nr.billingMethod==="per_hour"&&(
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                    <div><label className="slt-label" style={{color:C.textDarkMed}}>Company Rate/hr ($) <span style={{fontWeight:400,fontSize:10,opacity:0.7}}>(optional)</span></label><input type="number" step="0.01" value={nr.rateHour} onChange={e=>setNr(r=>({...r,rateHour:e.target.value}))} className="slt-input" placeholder="e.g. 80"/></div>
                    <div><label className="slt-label" style={{color:C.blue,fontWeight:800}}>Your Pay per Hour ($)</label><input type="number" step="0.01" value={nr.driverPay} onChange={e=>setNr(r=>({...r,driverPay:e.target.value}))} className="slt-input" placeholder="e.g. 45"/></div>
                  </div>
                )}
                {nr.billingMethod==="per_pct"&&(
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                    <div><label className="slt-label" style={{color:C.textDarkMed}}>Company Rate (%) <span style={{fontWeight:400,fontSize:10,opacity:0.7}}>(optional)</span></label><input type="number" step="1" value={nr.companyPct} onChange={e=>setNr(r=>({...r,companyPct:e.target.value}))} className="slt-input" placeholder="e.g. 30"/></div>
                    <div><label className="slt-label" style={{color:C.blue,fontWeight:800}}>Your Rate (%)</label><input type="number" step="1" value={nr.driverPct} onChange={e=>setNr(r=>({...r,driverPct:e.target.value}))} className="slt-input" placeholder="e.g. 25"/></div>
                  </div>
                )}
                {nr.billingMethod==="per_km"&&(
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                    <div><label className="slt-label" style={{color:C.textDarkMed}}>Company Rate/KM ($) <span style={{fontWeight:400,fontSize:10,opacity:0.7}}>(optional)</span></label><input type="number" step="0.001" value={nr.ratePerKm} onChange={e=>setNr(r=>({...r,ratePerKm:e.target.value}))} className="slt-input" placeholder="e.g. 2.50"/></div>
                    <div><label className="slt-label" style={{color:C.blue,fontWeight:800}}>Your Pay per KM ($)</label><input type="number" step="0.001" value={nr.driverPay} onChange={e=>setNr(r=>({...r,driverPay:e.target.value}))} className="slt-input" placeholder="e.g. 1.80"/></div>
                  </div>
                )}
                <button className="slt-btn-primary slt-btn-dark-text" style={{width:"100%"}} onClick={()=>{
                  if(!nr.from.trim()||!nr.to.trim())return;
                  const bm=nr.billingMethod||"per_load";
                  const companyRate=bm==="per_load"?Number(nr.ratePerLoad)||0:bm==="per_hour"?Number(nr.rateHour)||0:bm==="per_cubic"?Number(nr.rateCubic)||0:bm==="per_km"?Number(nr.ratePerKm)||0:0;
                  const dPay=Number(nr.driverPay)||0;
                  setLRoutes(r=>[...r,{
                    id:Date.now().toString(),from:nr.from.trim(),to:nr.to.trim(),
                    billingMethod:bm,rate:companyRate,ratePerLoad:Number(nr.ratePerLoad)||0,
                    rateCubic:Number(nr.rateCubic)||0,
                    rateHour:Number(nr.rateHour)||0,ratePerKm:Number(nr.ratePerKm)||0,
                    driverPct:Number(nr.driverPct)||0,companyPct:Number(nr.companyPct)||0,
                    pay:dPay,driverPay:dPay
                  }]);
                  setNr({from:"",to:"",billingMethod:"per_load",ratePerLoad:"",rateCubic:"",rateHour:"",driverPay:"",driverPct:"",companyPct:"",ratePerKm:""});
                }}>+ Add Route</button>
              </div>
            </>) : (<>
            {lRoutes.map((r,i)=>(
              <div key={i} style={{marginBottom:10}}>
                <div className="slt-card-sm" style={{borderLeft:'4px solid #E8962E'}}>
                  {editingRoute===i ? (
                    <div>
                      <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,color:C.blue,fontSize:14,marginBottom:12}}>✏️ Edit Route</div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                        <div><label className="slt-label">From</label><input value={r.from} onChange={e=>setLRoutes(rs=>rs.map((x,j)=>j===i?{...x,from:e.target.value}:x))} className="slt-input"/></div>
                        <div><label className="slt-label">To</label><input value={r.to} onChange={e=>setLRoutes(rs=>rs.map((x,j)=>j===i?{...x,to:e.target.value}:x))} className="slt-input"/></div>
                      </div>
                      <div style={{marginBottom:10}}>
                        <label className="slt-label">Billing Method</label>
                        <select value={r.billingMethod||"per_load"} onChange={e=>setLRoutes(rs=>rs.map((x,j)=>j===i?{...x,billingMethod:e.target.value}:x))} className="slt-input">
                          <option value="per_load">📦 Per Load</option>
                          <option value="per_cubic">📐 Per Cubic Yard</option>
                          <option value="per_hour">⏱ Per Hour</option>
                          <option value="per_pct">💯 % of Earnings</option>
                          <option value="per_km">🛣 Per KM/Mile</option>
                        </select>
                      </div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                        {(r.billingMethod||"per_load")==="per_load"&&<div><label className="slt-label">Rate/Load ($)</label><input type="number" step="0.01" value={r.ratePerLoad||r.rate||""} onChange={e=>setLRoutes(rs=>rs.map((x,j)=>j===i?{...x,ratePerLoad:e.target.value,rate:Number(e.target.value)}:x))} className="slt-input"/></div>}
                        {(r.billingMethod||"per_load")==="per_cubic"&&<div><label className="slt-label">Rate/yd³ ($)</label><input type="number" step="0.01" value={r.rateCubic||""} onChange={e=>setLRoutes(rs=>rs.map((x,j)=>j===i?{...x,rateCubic:e.target.value,rate:Number(e.target.value)}:x))} className="slt-input"/></div>}
                        {(r.billingMethod||"per_load")==="per_hour"&&<div><label className="slt-label">Rate/hr ($)</label><input type="number" step="0.01" value={r.rateHour||""} onChange={e=>setLRoutes(rs=>rs.map((x,j)=>j===i?{...x,rateHour:e.target.value,rate:Number(e.target.value)}:x))} className="slt-input"/></div>}
                        <div><label className="slt-label">Driver Pay ($)</label><input type="number" step="0.01" value={r.driverPay||r.pay||""} onChange={e=>setLRoutes(rs=>rs.map((x,j)=>j===i?{...x,driverPay:e.target.value,pay:Number(e.target.value)}:x))} className="slt-input"/></div>
                      </div>
                      <div style={{display:"flex",gap:8}}>
                        <button className="slt-btn-primary slt-btn-dark-text" style={{flex:2}} onClick={()=>setEditingRoute(null)}>✓ Done</button>
                        <button className="slt-btn-danger" style={{flex:1}} onClick={()=>{setLRoutes(rs=>rs.filter((_,j)=>j!==i));setEditingRoute(null);}}>Delete</button>
                      </div>
                    </div>
                  ) : (
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:700}}>{r.from} → {r.to}</div>
                      <div style={{fontSize:12,color:C.textDarkMed,marginTop:2}}>
                        <span style={{background:C.blueLight,color:C.blue,borderRadius:10,padding:"1px 8px",fontSize:13,fontWeight:700,marginRight:6}}>{(r.billingMethod||"per_load").replace("_"," ")}</span>
                        Rate: {rateDisplay(r)} · Driver: {fmtC(r.driverPay||r.pay||0)}
                      </div>
                    </div>
                    <div style={{display:"flex",gap:6}}>
                      <button className="slt-btn-secondary" style={{padding:"6px 12px",fontSize:12}} onClick={()=>setEditingRoute(i)}>✏️ Edit</button>
                      <button className="slt-btn-danger" style={{padding:"6px 12px",fontSize:12}} onClick={()=>setLRoutes(rs=>rs.filter((_,j)=>j!==i))}>Remove</button>
                    </div>
                  </div>)}

                  {/* Per-driver pay overrides */}
                  {expandedRoute===i&&allDrivers.length>0&&(
                    <div style={{marginTop:12,borderTop:`1px solid ${C.border}`,paddingTop:12}}>
                      <div style={{fontSize:13,fontWeight:800,color:C.textDarkMed,letterSpacing:1,textTransform:"uppercase",marginBottom:6}}>
                        Driver Pay Overrides
                      </div>
                      <div style={{fontSize:13,color:C.textDarkMut,marginBottom:10}}>
                        {(r.billingMethod||"per_load")==="per_hour"
                          ?`Set each driver's hourly rate ($/hr). Default: $${Number(r.driverPay||r.pay||0).toFixed(2)}/hr`
                          :`Leave blank to use default (${fmtC(r.driverPay||r.pay||0)})`
                        }
                      </div>
                      {allDrivers.map(d=>(
                        <div key={d.uid} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                          <div style={{width:28,height:28,borderRadius:"50%",background:C.blue,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,flexShrink:0}}>
                            {(d.fullName||d.name||"?")[0].toUpperCase()}
                          </div>
                          <div style={{flex:1}}>
                            <div style={{fontSize:13,fontWeight:600}}>{d.fullName||d.name}</div>
                            {(r.billingMethod||"per_load")==="per_hour"&&<div style={{fontSize:12,color:C.textDarkMut}}>$/hr rate</div>}
                          </div>
                          <div style={{display:"flex",alignItems:"center",gap:4}}>
                            <div style={{position:"relative"}}>
                              <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:C.textDarkMed,fontSize:13}}>$</span>
                              <input
                                type="number" step="0.01" min="0"
                                value={(r.driverOverrides||{})[d.uid]||""}
                                onChange={e=>setDriverOverride(i,d.uid,e.target.value)}
                                className="slt-input"
                                placeholder={(r.driverPay||r.pay||0).toString()}
                                style={{width:100,paddingLeft:22,fontSize:13}}
                              />
                            </div>
                            {(r.billingMethod||"per_load")==="per_hour"&&<span style={{fontSize:12,color:C.textDarkMed}}>/hr</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Add New Route */}
            <div className="slt-card-sm" style={{border:`2px dashed ${C.border}`,marginTop:10}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,color:C.blue,marginBottom:12}}>+ Add Route</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                <div><label className="slt-label">From (Origin)</label><input value={nr.from} onChange={e=>setNr(r=>({...r,from:e.target.value}))} className="slt-input" placeholder="e.g. CNRL Horizon, Suncor Base, Pit 4"/></div>
                <div><label className="slt-label">To (Destination)</label><input value={nr.to} onChange={e=>setNr(r=>({...r,to:e.target.value}))} className="slt-input" placeholder="e.g. Heartland, Upgrader Dock, Plant Gate"/></div>
              </div>
              <div style={{marginBottom:10}}>
                <label className="slt-label">Billing Method</label>
                <select value={nr.billingMethod||"per_load"} onChange={e=>setNr(r=>({...r,billingMethod:e.target.value}))} className="slt-input">
                  <option value="per_load">📦 Per Load — flat rate per trip</option>
                  <option value="per_cubic">📐 Per Cubic Yard — rate × yd³</option>
                  <option value="per_hour">⏱ Per Hour — rate × hours</option>
                  <option value="per_pct">💯 % of Earnings — driver percentage</option>
                  <option value="per_km">🛣 Per KM/Mile — rate × distance</option>
                </select>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                {(nr.billingMethod||"per_load")==="per_load"&&<><div><label className="slt-label">Rate/Load ($)</label><input type="number" step="0.01" value={nr.ratePerLoad} onChange={e=>setNr(r=>({...r,ratePerLoad:e.target.value}))} className="slt-input" placeholder="e.g. 1900"/></div><div><label className="slt-label">Driver Pay ($)</label><input type="number" step="0.01" value={nr.driverPay} onChange={e=>setNr(r=>({...r,driverPay:e.target.value}))} className="slt-input" placeholder="e.g. 420"/></div></>}
                {nr.billingMethod==="per_cubic"&&<><div><label className="slt-label">Rate/yd³ ($)</label><input type="number" step="0.01" value={nr.rateCubic} onChange={e=>setNr(r=>({...r,rateCubic:e.target.value}))} className="slt-input" placeholder="e.g. 40"/></div><div><label className="slt-label">Driver Pay ($)</label><input type="number" step="0.01" value={nr.driverPay} onChange={e=>setNr(r=>({...r,driverPay:e.target.value}))} className="slt-input" placeholder="e.g. 450"/></div></>}
                {nr.billingMethod==="per_hour"&&<><div><label className="slt-label">Rate/hr ($)</label><input type="number" step="0.01" value={nr.rateHour} onChange={e=>setNr(r=>({...r,rateHour:e.target.value}))} className="slt-input" placeholder="e.g. 120"/></div><div><label className="slt-label">Driver Pay/hr ($)</label><input type="number" step="0.01" value={nr.driverPay} onChange={e=>setNr(r=>({...r,driverPay:e.target.value}))} className="slt-input" placeholder="e.g. 50"/></div></>}
                {nr.billingMethod==="per_pct"&&<><div><label className="slt-label">Rate/Load ($)</label><input type="number" step="0.01" value={nr.ratePerLoad} onChange={e=>setNr(r=>({...r,ratePerLoad:e.target.value}))} className="slt-input" placeholder="e.g. 1900"/></div><div><label className="slt-label">Driver % of Earnings</label><input type="number" step="1" value={nr.driverPct} onChange={e=>setNr(r=>({...r,driverPct:e.target.value}))} className="slt-input" placeholder="e.g. 25"/></div></>}
              </div>
              <button className="slt-btn-primary slt-btn-dark-text" style={{width:"100%"}} onClick={()=>{
                if(!nr.from.trim()||!nr.to.trim()) return;
                setLRoutes(r=>[...r,{id:Date.now().toString(),from:nr.from.trim(),to:nr.to.trim(),billingMethod:nr.billingMethod||"per_load",ratePerLoad:Number(nr.ratePerLoad)||0,rate:nr.billingMethod==="per_load"||nr.billingMethod==="per_pct"?Number(nr.ratePerLoad)||0:nr.billingMethod==="per_cubic"?Number(nr.rateCubic)||0:Number(nr.rateHour)||0,rateCubic:Number(nr.rateCubic)||0,rateHour:Number(nr.rateHour)||0,driverPay:Number(nr.driverPay)||0,pay:Number(nr.driverPay)||0,driverPct:Number(nr.driverPct)||0,cubicDriverMode:"flat",driverOverrides:{}}]);
                setNr({from:"",to:"",billingMethod:"per_load",ratePerLoad:"",rateCubic:"",rateHour:"",driverPay:"",driverPct:"",cubicDriverMode:"flat"});
              }}>+ Add Route</button>
            </div>
          </>)}
          </div>)}

          {sec==="trucks"&&(
            <div>
            {lTrucks.map(t=>(
              <div key={t.id} className="slt-card-sm" style={{marginBottom:10}}>
                {editingTruck===t.id ? (
                  <div>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,color:C.blue,fontSize:14,marginBottom:12}}>✏️ Edit Truck</div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                      <div><label className="slt-label">Truck # *</label><input value={t.truckNumber} onChange={e=>setLTrucks(ts=>ts.map(x=>x.id===t.id?{...x,truckNumber:e.target.value}:x))} className="slt-input"/></div>
                      <div><label className="slt-label">Trailer #</label><input value={t.trailerNumber||""} onChange={e=>setLTrucks(ts=>ts.map(x=>x.id===t.id?{...x,trailerNumber:e.target.value}:x))} className="slt-input" placeholder="Optional"/></div>
                    </div>
                    <div style={{marginBottom:10}}><label className="slt-label">License Plate</label><input value={t.licensePlate||""} onChange={e=>setLTrucks(ts=>ts.map(x=>x.id===t.id?{...x,licensePlate:e.target.value}:x))} className="slt-input" placeholder="Optional"/></div>
                    <div style={{display:"flex",gap:8}}>
                      <button className="slt-btn-primary slt-btn-dark-text" style={{flex:2}} onClick={()=>setEditingTruck(null)}>✓ Done</button>
                      <button className="slt-btn-danger" style={{flex:1}} onClick={()=>{setLTrucks(ts=>ts.filter(x=>x.id!==t.id));setEditingTruck(null);}}>Delete</button>
                    </div>
                  </div>
                ) : (
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div>
                      <div style={{fontWeight:700}}>Truck {t.truckNumber}</div>
                      <div style={{fontSize:12,color:C.textDarkMed}}>{t.trailerNumber?`Trailer ${t.trailerNumber}`:""}{t.licensePlate?` · ${t.licensePlate}`:""}</div>
                    </div>
                    <div style={{display:"flex",gap:6}}>
                      <button className="slt-btn-secondary" style={{padding:"6px 12px",fontSize:12}} onClick={()=>setEditingTruck(t.id)}>✏️ Edit</button>
                      <button className="slt-btn-danger" style={{padding:"6px 12px",fontSize:12}} onClick={()=>setLTrucks(ts=>ts.filter(x=>x.id!==t.id))}>Remove</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {lTrucks.length===0&&<div style={{textAlign:"center",padding:"24px",color:C.textDarkMut,fontSize:13}}>No trucks yet.</div>}
            <div className="slt-card-sm" style={{border:`2px dashed ${C.border}`,marginTop:10}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,color:C.orange,marginBottom:12}}>+ Add Truck/Trailer</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                <div><label className="slt-label">Truck # *</label><input value={nt.truckNumber} onChange={e=>setNt(t=>({...t,truckNumber:e.target.value}))} className="slt-input" placeholder="e.g. T-17"/></div>
                <div><label className="slt-label">Trailer #</label><input value={nt.trailerNumber||""} onChange={e=>setNt(t=>({...t,trailerNumber:e.target.value}))} className="slt-input" placeholder="Optional"/></div>
              </div>
              <div style={{marginBottom:10}}><label className="slt-label">License Plate</label><input value={nt.licensePlate||""} onChange={e=>setNt(t=>({...t,licensePlate:e.target.value}))} className="slt-input" placeholder="Optional"/></div>
              <button className="slt-btn-primary slt-btn-dark-text" style={{width:"100%"}} onClick={()=>{
                if(!nt.truckNumber.trim()) return;
                const ex=lTrucks.map(t=>parseInt(t.tmwNumber)||0);
                const tmw=(Math.max(1000,...ex)+1).toString();
                setLTrucks(t=>[...t,{...nt,tmwNumber:tmw,id:Date.now().toString()}]);
                setNt({truckNumber:"",trailerNumber:"",licensePlate:""});
              }}>+ Add Truck</button>
            </div>
          </div>
        )}
        {/* Save button — always visible regardless of active tab */}
        <button className="slt-btn-primary slt-btn-dark-text" style={{width:"100%",padding:"14px",fontSize:15,marginTop:24}} onClick={save}>💾 Save All Settings</button>
        </div>
        </div>{/* end scrollable content */}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// NEW FEATURE MODULES — TruckPilot v3
// All 10 new features appended/integrated
// ═══════════════════════════════════════════════════════════════════

// ─── IFTA Tab ─────────────────────────────────────────────────────
// Feature 1: IFTA Tax Reporting
function IFTATab({ session, loads }) {
  const iftaKey = `tp-ifta-${session.uid}`;
  const [entries, setEntries] = useState(getStored(iftaKey));
  const [quarter, setQuarter] = useState(() => {
    const m = new Date().getMonth();
    return `Q${Math.floor(m / 3) + 1}-${new Date().getFullYear()}`;
  });
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ date: todayStr(), jurisdiction: "AB", km: "", fuelLitres: "", fuelCost: "", truckId: "", note: "" });

  // Fix: Load from Supabase fuel_log, merge with manual IFTA entries, normalize litres field
  useEffect(() => {
    sbGetFuelLog(session.uid).then(fuelLogs => {
      const local = getStored(iftaKey);
      const fromFuelLog = fuelLogs.map(f => {
        const d = new Date(f.date);
        const q = `Q${Math.floor(d.getMonth() / 3) + 1}-${d.getFullYear()}`;
        return {
          id: `fuellog-ifta-${f.id || f.date}`,
          date: f.date,
          jurisdiction: f.jurisdiction || "AB",
          km: 0,
          fuelLitres: Number(f.litres || f.fuelLitres || 0),
          fuelCost: Number(f.total || 0),
          note: `Auto: ${f.business_name || f.location || "Fuel Log"}`,
          quarter: q,
          fromFuelLog: true,
        };
      });
      const manualEntries = local.filter(e => !e.fromFuelLog);
      const merged = [...fromFuelLog, ...manualEntries];
      setEntries(merged);
      localStorage.setItem(iftaKey, JSON.stringify(merged));
    }).catch(() => {});
  }, [session.uid]);

  const CA_PROVINCES = ["AB","BC","MB","NB","NL","NS","NT","NU","ON","PE","QC","SK","YT"];
  const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];
  const ALL_JURISDICTIONS = [...CA_PROVINCES, ...US_STATES];

  // IFTA fuel tax rates (approx CAD cents/L for Canada, USD cents/gallon for US)
  const TAX_RATES = {
    AB:11.0, BC:15.0, MB:14.0, NB:15.2, NL:16.5, NS:15.5, NT:10.7, NU:10.7,
    ON:14.7, PE:14.2, QC:19.2, SK:15.0, YT:7.2,
    AL:19.0, AK:8.0, AZ:18.0, AR:21.5, CA:53.9, CO:22.0, CT:25.0, DE:22.0,
    FL:35.7, GA:31.2, ID:32.0, IL:38.2, IN:53.0, IA:30.5, KS:26.0, KY:21.6,
    LA:20.0, ME:31.2, MD:36.7, MA:24.0, MI:30.0, MN:28.5, MS:18.0, MO:17.0,
    MT:29.0, NE:24.9, NV:27.0, NH:22.2, NJ:41.4, NM:21.0, NY:40.5, NC:36.1,
    ND:23.0, OH:38.5, OK:16.0, OR:36.0, PA:74.1, RI:34.0, SC:26.0, SD:28.0,
    TN:27.4, TX:20.0, UT:31.9, VT:29.5, VA:27.2, WA:49.4, WV:35.7, WI:32.9, WY:24.0,
  };

  const save = (arr) => { setEntries(arr); localStorage.setItem(iftaKey, JSON.stringify(arr)); };
  const add = () => {
    if (!form.km || !form.jurisdiction) return;
    const newEntry = { ...form, km: Number(form.km), fuelLitres: Number(form.fuelLitres) || 0, fuelCost: Number(form.fuelCost) || 0, id: Date.now().toString(), quarter };
    save([newEntry, ...entries]);
    // If litres entered, also save to fuel_log in Supabase
    if (newEntry.fuelLitres > 0) {
      sbSaveFuelEntry({ user_id: session.uid, date: newEntry.date, jurisdiction: newEntry.jurisdiction, litres: newEntry.fuelLitres, total: newEntry.fuelCost, notes: newEntry.note || "Manual IFTA entry" }).catch(console.error);
    }
    setForm({ date: todayStr(), jurisdiction: "AB", km: "", fuelLitres: "", fuelCost: "", truckId: "", note: "" });
    setShowAdd(false);
  };

  const qEntries = entries.filter(e => e.quarter === quarter);
  const totalKm = qEntries.reduce((s, e) => s + Number(e.km || 0), 0);
  const totalFuel = qEntries.reduce((s, e) => s + Number(e.fuelLitres || 0), 0);
  const avgMpg = totalFuel > 0 ? (totalKm / totalFuel).toFixed(2) : "—";

  // Group by jurisdiction
  const byJur = {};
  qEntries.forEach(e => {
    if (!byJur[e.jurisdiction]) byJur[e.jurisdiction] = { km: 0, fuel: 0 };
    byJur[e.jurisdiction].km += Number(e.km || 0);
    byJur[e.jurisdiction].fuel += Number(e.fuelLitres || 0);
  });

  // IFTA calculation: tax owed = (km / total km * total fuel - fuel purchased in jur) * tax rate
  const iftaRows = Object.entries(byJur).map(([jur, data]) => {
    const allocated = totalFuel > 0 ? (data.km / totalKm) * totalFuel : 0;
    const diff = allocated - data.fuel; // positive = owe tax, negative = refund
    const rate = TAX_RATES[jur] || 0;
    const taxOwed = diff * rate / 100; // cents to dollars
    return { jur, km: data.km, fuel: data.fuel, allocated: allocated.toFixed(1), diff: diff.toFixed(1), taxOwed: taxOwed.toFixed(2), isRefund: diff < 0 };
  }).sort((a, b) => Math.abs(Number(b.taxOwed)) - Math.abs(Number(a.taxOwed)));

  const totalTax = iftaRows.reduce((s, r) => s + Number(r.taxOwed), 0);

  const printIFTA = () => {
    const rows = iftaRows.map(r => `<tr><td>${r.jur}</td><td>${r.km.toLocaleString()}</td><td>${r.allocated}</td><td>${r.fuel.toFixed(1)}</td><td>${Number(r.diff) > 0 ? "+" : ""}${r.diff}</td><td style="color:${r.isRefund ? "green" : "red"};font-weight:800">${r.isRefund ? "REFUND" : "OWED"} $${Math.abs(r.taxOwed).toFixed(2)}</td></tr>`).join("");
    const html = `
      <div style="font-size:20px;font-weight:800;margin-bottom:4px">IFTA Tax Report</div><div style="color:#666;margin-bottom:16px">${quarter}</div>
      <div class="summary">
        <div class="summary-card"><div class="label">Total KM</div><div class="value">${totalKm.toLocaleString()}</div></div>
        <div class="summary-card"><div class="label">Total Fuel (L)</div><div class="value">${totalFuel.toFixed(1)}</div></div>
        <div class="summary-card"><div class="label">Net Tax ${totalTax >= 0 ? "Owed" : "Refund"}</div><div class="value" style="color:${totalTax >= 0 ? "#C62828" : "#2E7D32"}">$${Math.abs(totalTax).toFixed(2)}</div></div>
      </div>
      <table><thead><tr><th>Jurisdiction</th><th>KM Driven</th><th>Fuel Allocated (L)</th><th>Fuel Purchased (L)</th><th>Difference</th><th>Tax Status</th></tr></thead>
      <tbody>${rows}</tbody></table>
      <p style="font-size:11px;color:#888">Avg efficiency: ${avgMpg} km/L</p>`;
    downloadPDF(html, `IFTA_${quarter}`, session);
  };

  const quarters = [];
  const now = new Date();
  for (let y = now.getFullYear(); y >= now.getFullYear() - 1; y--) {
    for (let q = 4; q >= 1; q--) quarters.push(`Q${q}-${y}`);
  }

  return (
    <div className="slt-page" style={{background:"#070B14",color:"#F0F0F0"}}>
      <div className="slt-hero">
        <div className="slt-hero-title">📋 IFTA Tax Reporting</div>
        <div className="slt-hero-sub">Track fuel by jurisdiction · Auto-calculate tax owed or refund</div>
      </div>
      <div className="slt-container">
        {/* Quarter selector */}
        <div className="slt-card" style={{ marginBottom: 18, padding: "16px 20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <div style={{ flex: 1 }}>
              <label className="slt-label">Quarter</label>
              <select value={quarter} onChange={e => setQuarter(e.target.value)} className="slt-input" style={{ maxWidth: 200 }}>
                {quarters.map(q => <option key={q} value={q}>{q}</option>)}
              </select>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="slt-btn-secondary" style={{ padding: "10px 18px" }} onClick={printIFTA}>🖨 Print IFTA Report</button>
              <button className="slt-btn-primary slt-btn-dark-text" style={{ width: "auto", padding: "10px 18px" }} onClick={() => setShowAdd(!showAdd)}>+ Add Entry</button>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12, marginBottom: 20 }}>
          {[
            ["Total KM", totalKm.toLocaleString() + " km", C.blue],
            ["Total Fuel", totalFuel.toFixed(1) + " L", C.orange],
            ["Avg Efficiency", avgMpg + " km/L", C.green],
            ["Net Tax", `${totalTax >= 0 ? "Owed" : "Refund"} $${Math.abs(totalTax).toFixed(2)}`, totalTax > 0 ? C.red : C.green],
          ].map(([l, v, color]) => (
            <div key={l} className="slt-card-sm" style={{ borderTop: `4px solid ${color}`, textAlign: "center" }}>
              <div style={{ fontSize: 13, color: C.textLight, fontWeight: 700, marginBottom: 4 }}>{l}</div>
              <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: 18, fontWeight: 800, color }}>{v}</div>
            </div>
          ))}
        </div>

        {showAdd && (
          <div className="slt-card" style={{ border: `2px solid ${C.blue}`, marginBottom: 18 }}>
            <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800, color: C.blue, fontSize: 16, marginBottom: 16 }}>Add Jurisdiction Entry</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div><label className="slt-label" style={{display:"block",fontSize:14,fontWeight:900,color:"#E8962E",marginBottom:6,fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:0.5,textTransform:"uppercase"}}>Date</label><input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="slt-input" /></div>
              <div>
                <label className="slt-label">Jurisdiction</label>
                <select value={form.jurisdiction} onChange={e => setForm(f => ({ ...f, jurisdiction: e.target.value }))} className="slt-input">
                  <optgroup label="🇨🇦 Canada">{CA_PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}</optgroup>
                  <optgroup label="🇺🇸 United States">{US_STATES.map(s => <option key={s} value={s}>{s}</option>)}</optgroup>
                </select>
              </div>
              <div><label className="slt-label">KM Driven in {form.jurisdiction}</label><input type="number" placeholder="0" value={form.km} onChange={e => setForm(f => ({ ...f, km: e.target.value }))} className="slt-input" /></div>
              <div><label className="slt-label">Fuel Purchased (L)</label><input type="number" step="0.1" placeholder="0" value={form.fuelLitres} onChange={e => setForm(f => ({ ...f, fuelLitres: e.target.value }))} className="slt-input" /></div>
              <div><label className="slt-label">Fuel Cost ($)</label><input type="number" step="0.01" placeholder="0.00" value={form.fuelCost} onChange={e => setForm(f => ({ ...f, fuelCost: e.target.value }))} className="slt-input" /></div>
              <div><label className="slt-label">Note</label><input placeholder="Optional…" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} className="slt-input" /></div>
            </div>
            <div style={{ background: C.blueLight, borderRadius: 9, padding: "10px 14px", marginBottom: 14, fontSize: 13, color: C.blue }}>
              💡 Estimated tax rate for {form.jurisdiction}: <strong>{TAX_RATES[form.jurisdiction] || 0}¢/L</strong>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="slt-btn-primary slt-btn-dark-text" style={{ flex: 1 }} onClick={add}>Save Entry</button>
              <button className="slt-btn-ghost" style={{ padding: "12px 18px" }} onClick={() => setShowAdd(false)}>Cancel</button>
            </div>
          </div>
        )}

        {/* IFTA calculation table */}
        {iftaRows.length > 0 ? (
          <div className="slt-card">
            <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800, fontSize: 16, marginBottom: 14 }}>IFTA Summary — {quarter}</div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: C.offWhite }}>
                    {["Jurisdiction", "KM", "Fuel Alloc. (L)", "Fuel Purch. (L)", "Diff (L)", "Est. Tax"].map(h => (
                      <th key={h} style={{ padding: "9px 12px", textAlign: "left", fontWeight: 700, color: C.textMed, borderBottom: `2px solid ${C.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {iftaRows.map((r, i) => (
                    <tr key={r.jur} style={{ background: i % 2 === 0 ? C.white : C.offWhite }}>
                      <td style={{ padding: "9px 12px", fontWeight: 700 }}>{CA_PROVINCES.includes(r.jur) ? "🇨🇦" : "🇺🇸"} {r.jur}</td>
                      <td style={{ padding: "9px 12px" }}>{r.km.toLocaleString()}</td>
                      <td style={{ padding: "9px 12px" }}>{r.allocated}L</td>
                      <td style={{ padding: "9px 12px" }}>{r.fuel.toFixed(1)}L</td>
                      <td style={{ padding: "9px 12px", color: Number(r.diff) > 0 ? C.red : C.green, fontWeight: 700 }}>{Number(r.diff) > 0 ? "+" : ""}{r.diff}L</td>
                      <td style={{ padding: "9px 12px", fontWeight: 800, color: r.isRefund ? C.green : C.red }}>
                        {r.isRefund ? "↩ " : "→ "}{r.isRefund ? "Refund" : "Owed"} ${Math.abs(Number(r.taxOwed)).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ background: C.blueLight }}>
                    <td colSpan={5} style={{ padding: "11px 12px", fontWeight: 800, fontFamily: "'Barlow Condensed',sans-serif" }}>NET IFTA {totalTax >= 0 ? "TAX OWED" : "REFUND"}</td>
                    <td style={{ padding: "11px 12px", fontWeight: 800, fontFamily: "'Barlow Condensed',sans-serif", fontSize: 16, color: totalTax > 0 ? C.red : C.green }}>${Math.abs(totalTax).toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: 13.5, color: C.textLight, marginTop: 10 }}>
              ⚠️ Rates are approximate. Always verify with official IFTA jurisdictional rates before filing.
            </div>
          </div>
        ) : (
          <div className="slt-card" style={{ textAlign: "center", padding: "52px 24px" }}>
            <div style={{ fontSize: 48, marginBottom: 14 }}>📋</div>
            <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, marginBottom: 8 }}>No entries for {quarter}</div>
            <div style={{ color: C.textMed, fontSize: 13 }}>Add your KM by jurisdiction and fuel purchases to generate your IFTA report</div>
          </div>
        )}

        {/* Raw entries */}
        {qEntries.length > 0 && (
          <div className="slt-card" style={{ marginTop: 18 }}>
            <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800, fontSize: 15, marginBottom: 14 }}>Trip Entries</div>
            {[...qEntries].sort((a, b) => b.date > a.date ? 1 : -1).map(e => (
              <div key={e.id} style={{ display: "flex", justifyContent: "space-between", padding: "9px 0", borderBottom: `1px solid ${C.border}`, alignItems: "center" }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{CA_PROVINCES.includes(e.jurisdiction) ? "🇨🇦" : "🇺🇸"} {e.jurisdiction} · {e.km} km</div>
                  <div style={{ fontSize: 13.5, color: C.textLight }}>{e.date}{e.note ? ` · ${e.note}` : ""}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  {e.fuelLitres > 0 && <div style={{ fontSize: 12.5, color: C.orange, fontWeight: 700 }}>{e.fuelLitres}L fuel</div>}
                  <button onClick={() => save(entries.filter(x => x.id !== e.id))} style={{ fontSize: 13, color: C.red, background: "none", border: "none", cursor: "pointer", marginTop: 2 }}>✕ Remove</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Payroll Tab ──────────────────────────────────────────────────
// Feature 2: Driver Payroll Automation
// ══════════════════════════════════════════════════════════════════════════════
// CreatePaymentModal — Owner bulk payment tool
// Lets owner pick a driver, select multiple unpaid loads, auto-sum, confirm.
// Writes a row to the `payments` table and marks loads as payment_status="paid".
// ══════════════════════════════════════════════════════════════════════════════
function CreatePaymentModal({ session, loads, allDrivers, onClose, onPaymentSaved, onUpdateLoad }) {
  const [step, setStep] = useState("select"); // "select" | "confirm"
  const [selectedDriverUid, setSelectedDriverUid] = useState("");
  const [selectedLoadIds, setSelectedLoadIds] = useState(new Set());
  const [payMethod, setPayMethod] = useState("e-Transfer");
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const ownerUid = session.ownerUid || session.uid;

  // Drivers who have unpaid completed loads
  const driversWithLoads = (() => {
    const driverMap = {};
    loads.forEach(l => {
      if (!l.completed) return;
      if (l.driverReceived || l.payment_status === "paid") return;
      const dUid = l.assignedDriverUid || (l.user_id !== session.uid ? l.user_id : null);
      if (!dUid || dUid === session.uid) return;
      if (!driverMap[dUid]) {
        const driverInfo = allDrivers.find(d => d.uid === dUid) || { uid: dUid, fullName: l.driverFullName || "Driver", name: l.driverFullName || "Driver" };
        driverMap[dUid] = { ...driverInfo, unpaidLoads: [] };
      }
      driverMap[dUid].unpaidLoads.push(l);
    });
    return Object.values(driverMap);
  })();

  const selectedDriver = driversWithLoads.find(d => d.uid === selectedDriverUid);
  const unpaidLoads = selectedDriver?.unpaidLoads || [];

  const toggleLoad = (id) => {
    setSelectedLoadIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedLoadIds(new Set(unpaidLoads.map(l => l.id)));
  const deselectAll = () => setSelectedLoadIds(new Set());

  const selectedLoads = unpaidLoads.filter(l => selectedLoadIds.has(l.id));
  const totalAmount = selectedLoads.reduce((s, l) => {
    const pay = Number(l.driverBasePay || 0) || Number(l.earnings || 0);
    return s + pay;
  }, 0);

  const handleDriverSelect = (uid) => {
    setSelectedDriverUid(uid);
    setSelectedLoadIds(new Set()); // reset selection when driver changes
  };

  const handleConfirm = async () => {
    if (!selectedDriverUid || selectedLoads.length === 0) return;
    setSaving(true);
    try {
      const payment = {
        owner_uid: ownerUid,
        driver_uid: selectedDriverUid,
        amount: totalAmount,
        date: payDate,
        method: payMethod,
        loads_included: selectedLoads.map(l => l.id),
        created_at: new Date().toISOString(),
        driver_name: selectedDriver?.fullName || selectedDriver?.name || "Driver",
      };
      await sbSavePayment(payment);
      // Update each selected load's payment_status
      for (const load of selectedLoads) {
        if (onUpdateLoad) {
          await onUpdateLoad(load.id, {
            driverPaid: true,
            driverPaidDate: payDate,
            driverPaidMethod: payMethod,
            payment_status: "paid",
          });
        }
      }
      if (onPaymentSaved) onPaymentSaved();
      onClose();
    } catch (e) {
      console.error("Payment save error:", e);
    }
    setSaving(false);
  };

  const METHODS = ["e-Transfer", "Cash", "Direct Deposit", "Cheque", "Other"];

  const overlayStyle = { position:"fixed", inset:0, zIndex:9800, background:"rgba(0,0,0,0.65)", display:"flex", alignItems:"flex-end" };
  const sheetStyle = { background:"#fff", borderRadius:"20px 20px 0 0", width:"100%", maxHeight:"90vh", overflowY:"auto", WebkitOverflowScrolling:"touch" };

  return (
    <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={sheetStyle}>
        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"18px 20px 14px", borderBottom:"1px solid #F0F0F0", position:"sticky", top:0, background:"#fff", zIndex:1 }}>
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:20, color:"#1A1A1A" }}>💳 Create Payment</div>
          <button onClick={onClose} style={{ background:"none", border:"none", fontSize:22, cursor:"pointer", color:"#9CA3AF", lineHeight:1 }}>✕</button>
        </div>

        <div style={{ padding:"16px 20px 40px" }}>

          {/* Step 1: Select Driver */}
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:10, fontWeight:800, color:"#6B7280", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Select Driver</div>
            {driversWithLoads.length === 0 ? (
              <div style={{ textAlign:"center", padding:"24px", background:"#F9FAFB", borderRadius:12, color:"#6B7280", fontSize:13 }}>
                ✅ All drivers are paid up — no pending loads
              </div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {driversWithLoads.map(d => {
                  const isSelected = selectedDriverUid === d.uid;
                  const dTotal = d.unpaidLoads.reduce((s,l) => s + (Number(l.driverBasePay||0) || Number(l.earnings||0)), 0);
                  return (
                    <div key={d.uid}
                      onClick={() => handleDriverSelect(d.uid)}
                      style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"13px 14px", borderRadius:12, cursor:"pointer", border:`2px solid ${isSelected?"#E8962E":"#E5E7EB"}`, background: isSelected?"rgba(232,150,46,0.06)":"#fff", transition:"all 0.15s" }}>
                      <div>
                        <div style={{ fontWeight:800, fontSize:14, color:"#1A1A1A" }}>{d.fullName || d.name}</div>
                        <div style={{ fontSize:12, color:"#6B7280", marginTop:2 }}>{d.unpaidLoads.length} unpaid load{d.unpaidLoads.length!==1?"s":""}</div>
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:18, color:isSelected?"#E8962E":"#1A1A1A" }}>${dTotal.toFixed(2)}</div>
                        {isSelected && <div style={{ fontSize:10, color:"#E8962E", fontWeight:800 }}>SELECTED ✓</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Step 2: Select Loads */}
          {selectedDriverUid && unpaidLoads.length > 0 && (
            <div style={{ marginBottom:16 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                <div style={{ fontSize:10, fontWeight:800, color:"#6B7280", textTransform:"uppercase", letterSpacing:1 }}>Select Loads to Include</div>
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={selectAll} style={{ fontSize:11, fontWeight:700, color:"#E8962E", background:"none", border:"none", cursor:"pointer" }}>All</button>
                  <button onClick={deselectAll} style={{ fontSize:11, fontWeight:700, color:"#9CA3AF", background:"none", border:"none", cursor:"pointer" }}>None</button>
                </div>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {unpaidLoads.map(l => {
                  const isChecked = selectedLoadIds.has(l.id);
                  const pay = Number(l.driverBasePay || 0) || Number(l.earnings || 0);
                  return (
                    <div key={l.id}
                      onClick={() => toggleLoad(l.id)}
                      style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"11px 13px", borderRadius:10, cursor:"pointer", border:`1.5px solid ${isChecked?"#E8962E":"#E5E7EB"}`, background: isChecked?"rgba(232,150,46,0.07)":"#FAFAFA", transition:"all 0.15s" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <div style={{ width:18, height:18, borderRadius:5, border:`2px solid ${isChecked?"#E8962E":"#D1D5DB"}`, background: isChecked?"#E8962E":"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontSize:11, color:"#fff", fontWeight:900 }}>
                          {isChecked ? "✓" : ""}
                        </div>
                        <div>
                          <div style={{ fontWeight:700, fontSize:13, color:"#1A1A1A" }}>{l.location || "Load"}</div>
                          <div style={{ fontSize:11, color:"#6B7280", marginTop:1 }}>{l.date}{l.driverBasePay ? "" : " · Rate-based"}</div>
                        </div>
                      </div>
                      <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:15, color: isChecked?"#E8962E":"#6B7280" }}>${pay.toFixed(2)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Total bar */}
          {selectedLoads.length > 0 && (
            <div style={{ background:"rgba(232,150,46,0.1)", border:"1.5px solid rgba(232,150,46,0.35)", borderRadius:12, padding:"12px 16px", display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
              <div>
                <div style={{ fontSize:12, fontWeight:800, color:"#E8962E" }}>Total ({selectedLoads.length} load{selectedLoads.length!==1?"s":""})</div>
                <div style={{ fontSize:11, color:"#9CA3AF", marginTop:1 }}>Auto-calculated</div>
              </div>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:26, color:"#1A1A1A" }}>${totalAmount.toFixed(2)}</div>
            </div>
          )}

          {/* Payment Method + Date */}
          {selectedLoads.length > 0 && (
            <>
              <div style={{ marginBottom:12 }}>
                <div style={{ fontSize:10, fontWeight:800, color:"#6B7280", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Payment Method</div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  {METHODS.map(m => (
                    <button key={m} onClick={() => setPayMethod(m)}
                      style={{ padding:"7px 14px", borderRadius:20, border:`1.5px solid ${payMethod===m?"#E8962E":"#E5E7EB"}`, background: payMethod===m?"#E8962E":"#fff", color: payMethod===m?"#fff":"#374151", fontWeight:700, fontSize:12, cursor:"pointer", fontFamily:"inherit", transition:"all 0.15s" }}>
                      {m}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom:18 }}>
                <div style={{ fontSize:10, fontWeight:800, color:"#6B7280", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Payment Date</div>
                <input type="date" value={payDate} onChange={e => setPayDate(e.target.value)}
                  style={{ width:"100%", padding:"11px 13px", borderRadius:10, border:"1.5px solid #E5E7EB", fontSize:14, background:"#FAFAFA", color:"#1A1A1A", fontFamily:"inherit" }} />
              </div>

              <button
                onClick={handleConfirm}
                disabled={saving || selectedLoads.length === 0}
                style={{ width:"100%", padding:"15px", borderRadius:13, background: saving?"#ccc":"#E8962E", color:"#fff", border:"none", fontWeight:900, fontSize:15, cursor: saving?"not-allowed":"pointer", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:0.5, marginBottom:10 }}>
                {saving ? "Saving..." : `✅ Confirm Payment — $${totalAmount.toFixed(2)}`}
              </button>
              <button onClick={onClose}
                style={{ width:"100%", padding:"12px", borderRadius:13, background:"transparent", color:"#6B7280", border:"1.5px solid #E5E7EB", fontWeight:700, fontSize:14, cursor:"pointer", fontFamily:"inherit" }}>
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PayrollTab({ session, loads, rates, allDrivers: allDriversProp, onUpdateLoad, onRefresh, goBack}) {
  const payrollKey = `tp-payroll-${session.ownerUid || session.uid}`;
  const [payPeriod, setPayPeriod] = useState(rates?.payFrequency || "biweekly");
  const pp = getPayPeriod(rates);
  const [bonuses, setBonuses] = useState(getStored(payrollKey));
  // Load bonuses from Supabase settings table
  useEffect(() => {
    sbGetSettings(session.ownerUid||session.uid).then(d => {
      if (d && d.bonuses && d.bonuses.length > 0) {
        setBonuses(d.bonuses);
        localStorage.setItem(payrollKey, JSON.stringify(d.bonuses));
      }
    }).catch(()=>{});
  }, [session.uid]);
  const [showBonus, setShowBonus] = useState(false);
  const [showCreatePayment, setShowCreatePayment] = useState(false);
  const [bonusForm, setBonusForm] = useState({ driverUid: "", amount: "", reason: "", date: todayStr() });
  const [expandDriver, setExpandDriver] = useState(null);
  const [sbDrivers, setSbDrivers] = useState([]);

  useEffect(() => {
    sbGetDrivers(session.ownerUid || session.uid).then(d => {
      if (d && d.length > 0) setSbDrivers(d);
    }).catch(() => {});
  }, [session.uid]);

  // Merge Supabase drivers with localStorage drivers, deduplicate by uid
  const allDrivers = (() => {
    const merged = [...(allDriversProp || [])];
    sbDrivers.forEach(sd => {
      if (!merged.find(d => d.uid === sd.uid)) merged.push(sd);
    });
    // Also add any drivers found in loads who aren't in the list yet
    loads.forEach(l => {
      const dUid = l.assignedDriverUid || (l.addedBy !== session.uid ? l.addedBy : null) || (l.user_id !== session.uid ? l.user_id : null);
      if (dUid && dUid !== session.uid && !merged.find(d => d.uid === dUid)) {
        merged.push({ uid: dUid, fullName: l.driverFullName || "Unknown Driver", name: l.driverFullName || "Unknown Driver" });
      }
    });
    // Add owner as a driver entry if they have any payMyselfAsDriver loads
    const hasSelfPay = loads.some(l => l.payMyselfAsDriver === true && Number(l.driverBasePay||0) > 0);
    if (hasSelfPay && !merged.find(d => d.uid === session.uid)) {
      merged.push({ uid: session.uid, fullName: (session.fullName || session.name || "Owner") + " (Self)", name: (session.fullName || session.name || "Owner") + " (Self)", _isSelfPay: true });
    }
    return merged;
  })();

  const now = new Date();
  const periodStart = new Date(now);
  if (payPeriod === "weekly") periodStart.setDate(now.getDate() - 7);
  else if (payPeriod === "biweekly") periodStart.setDate(now.getDate() - 14);
  else periodStart.setDate(1); // monthly
  const calcPeriodStart = pp?.periodStart || periodStart;
  const calcPeriodEnd = pp?.periodEnd || now;

  const inPeriod = (dateStr) => {
    if (!dateStr) return false;
    const now = new Date();
    // Only use manual period dates if they are current (end date is within last 90 days or in future)
    if (pp?.periodStart && pp?.periodEnd) {
      const end = pp.periodEnd instanceof Date ? pp.periodEnd : new Date((pp.periodEnd||"") + "T23:59:59");
      const start = pp.periodStart instanceof Date ? pp.periodStart : new Date((pp.periodStart||"") + "T00:00:00");
      const ninetyDaysAgo = new Date(now); ninetyDaysAgo.setDate(now.getDate() - 90);
      if (end >= ninetyDaysAgo) {
        const d = new Date(dateStr + "T12:00:00");
        return d >= start && d <= end;
      }
    }
    // Fallback: use payPeriod selector
    const d = new Date(dateStr + "T12:00:00");
    if (payPeriod === "weekly") { const w = new Date(now); w.setDate(w.getDate()-7); return d >= w; }
    if (payPeriod === "biweekly") { const bw = new Date(now); bw.setDate(bw.getDate()-14); return d >= bw; }
    // monthly
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  };

  const saveBonus = (arr) => {
    setBonuses(arr);
    localStorage.setItem(payrollKey, JSON.stringify(arr));
    // Persist bonuses to Supabase settings so they survive localStorage wipe
    sbGetSettings(session.ownerUid||session.uid).then(existing => {
      sbSaveSettings(session.ownerUid||session.uid, {...(existing||{}), bonuses: arr}, []);
    }).catch(()=>{});
    // Save each bonus to Supabase so driver can see it
    arr.forEach(b => sbSaveExpense({
      id: `bonus-${b.id}`, category: "bonus", amount: Number(b.amount),
      description: `🎁 Bonus: ${b.reason||"Bonus"}`, date: b.date,
      reason: b.reason, driverUid: b.driverUid, source: "bonus", paid: b.paid||false
    }, b.driverUid).catch(console.error));
  };
  const addBonus = () => {
    if (!bonusForm.driverUid || !bonusForm.amount) return;
    const newBonus = { ...bonusForm, amount: Number(bonusForm.amount), id: Date.now().toString(), paid: false };
    saveBonus([newBonus, ...bonuses]);
    setBonusForm({ driverUid: "", amount: "", reason: "", date: todayStr() });
    setShowBonus(false);
  };

  const getDriverPayroll = (driver) => {
    const dLoads = loads.filter(l => {
      if (!inPeriod(l.date)) return false;
      // For the owner driving themselves: match payMyselfAsDriver loads
      if (driver._isSelfPay || driver.uid === session.uid) {
        return l.payMyselfAsDriver === true && Number(l.driverBasePay||0) > 0;
      }
      return l.assignedDriverUid === driver.uid || l.addedBy === driver.uid || l.user_id === driver.uid;
    });
    const routePay = dLoads.reduce((s, l) => {
      const dbp = Number(l.driverBasePay||0);
      if (dbp > 0) return s + dbp;
      // fall back: driverPct of earnings, or raw earnings if driver logged own load
      const earn = Number(l.earnings||0);
      const pct = Number(l.driverPct||0);
      if (pct > 0 && earn > 0) return s + earn * pct / 100;
      // if driver added this load themselves (not assigned by owner), use their earnings
      if (l.addedBy === driver.uid || l.user_id === driver.uid) return s + earn;
      return s;
    }, 0);
    const waitPay = dLoads.reduce((s, l) => {
      const wm = (Number(l.loadWaitMins) || 0) + (Number(l.offloadWaitMins) || 0);
      return s + wm / 60 * (Number(rates.driverWaitRate) || 0);
    }, 0);
    const driverBonuses = bonuses.filter(b => b.driverUid === driver.uid && inPeriod(b.date));
    const bonusTotal = driverBonuses.reduce((s, b) => s + Number(b.amount || 0), 0);
    const total = routePay + waitPay + bonusTotal;
    return { dLoads, routePay, waitPay, bonusTotal, total, driverBonuses };
  };

  const exportPayroll = () => {
    // Include ALL payees: external drivers + owner self-pay entry
    const rows = allDrivers.map(d => {
      const p = getDriverPayroll(d);
      const label = d._isSelfPay ? `${d.fullName || d.name} (Owner Draw)` : (d.fullName || d.name);
      return `<tr><td>${label}</td><td>${p.dLoads.length}</td><td>$${p.routePay.toFixed(2)}</td><td>$${p.waitPay.toFixed(2)}</td><td>$${p.bonusTotal.toFixed(2)}</td><td style="font-weight:800;color:#E8962E">$${p.total.toFixed(2)}</td></tr>`;
    }).join("");
    const grandTotal = allDrivers.reduce((s, d) => s + getDriverPayroll(d).total, 0);
    const html = `
      <div style="font-size:20px;font-weight:800;margin-bottom:4px">Driver Payroll Report</div><div style="color:#666;margin-bottom:16px">${payPeriod.charAt(0).toUpperCase()+payPeriod.slice(1)} · ${periodStart.toDateString()} to ${now.toDateString()}</div>
      <div class="summary">
        <div class="summary-card"><div class="label">Payees</div><div class="value">${allDrivers.length}</div></div>
        <div class="summary-card"><div class="label">Total Loads</div><div class="value">${allDrivers.reduce((s,d)=>s+getDriverPayroll(d).dLoads.length,0)}</div></div>
        <div class="summary-card"><div class="label">Grand Total</div><div class="value blue">$${grandTotal.toFixed(2)}</div></div>
      </div>
      <table><thead><tr><th>Driver</th><th>Loads</th><th>Route Pay</th><th>Wait Pay</th><th>Bonuses</th><th>Total</th></tr></thead>
      <tbody>${rows}
      <tr class="total"><td colspan="5"><strong>GRAND TOTAL</strong></td><td><strong>$${grandTotal.toFixed(2)}</strong></td></tr>
      </tbody></table>`;
    downloadPDF(html, `Payroll_${payPeriod}_${todayStr()}`, session);
  };

  return (
    <div className="slt-page" style={{background:"#070B14",color:"#F0F0F0"}}>
      {goBack && <BackButton onBack={goBack} label="Back" />}

      {/* ── Create Payment Modal ── */}
      {showCreatePayment && (
        <CreatePaymentModal
          session={session}
          loads={loads}
          allDrivers={allDrivers}
          onClose={() => setShowCreatePayment(false)}
          onUpdateLoad={onUpdateLoad}
          onPaymentSaved={() => { if (onRefresh) onRefresh(); }}
        />
      )}

      <div className="slt-hero">
        <div className="slt-hero-title">💵 Driver Payroll</div>
        <div className="slt-hero-sub">Automated pay calculation · Export for accounting</div>
      </div>
      <div className="slt-container">

        {/* ── Create Payment CTA ── */}
        {(() => {
          const unpaidDriverCount = allDrivers.filter(d => loads.some(l => l.completed && !l.driverReceived && l.payment_status !== "paid" && (l.assignedDriverUid === d.uid || l.user_id === d.uid) && l.user_id !== session.uid)).length;
          const totalUnpaid = loads.filter(l => l.completed && !l.driverReceived && l.payment_status !== "paid" && l.user_id !== session.uid).reduce((s,l) => s + Number(l.driverBasePay||0), 0);
          if (unpaidDriverCount === 0) return null;
          return (
            <div style={{ background:"linear-gradient(135deg,#1C2B4A,#243655)", borderRadius:14, padding:"14px 16px", marginBottom:16, display:"flex", justifyContent:"space-between", alignItems:"center", gap:12 }}>
              <div>
                <div style={{ fontSize:12, color:"rgba(255,255,255,0.5)", fontWeight:700, textTransform:"uppercase", letterSpacing:0.8, marginBottom:3 }}>💡 Pending Payments</div>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:20, fontWeight:900, color:"#fff", lineHeight:1 }}>{fmtC(totalUnpaid)}</div>
                <div style={{ fontSize:11, color:"rgba(255,255,255,0.45)", marginTop:3 }}>{unpaidDriverCount} driver{unpaidDriverCount!==1?"s":""} waiting</div>
              </div>
              <button onClick={() => setShowCreatePayment(true)}
                style={{ background:"#E8962E", color:"#fff", border:"none", borderRadius:12, padding:"12px 18px", fontWeight:900, fontSize:13, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap", flexShrink:0 }}>
                💳 Pay Drivers
              </button>
            </div>
          );
        })()}

        {/* Pay Period Banner */}
        <div style={{background:"linear-gradient(135deg,#E8962E,#E8962E)",borderRadius:16,padding:"16px 18px",marginBottom:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:10}}>
            <div>
              <div style={{fontSize:13,color:"rgba(255,255,255,0.5)",fontWeight:700,textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>Current Pay Period</div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:20,color:"#fff"}}>{pp.label}</div>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.6)",marginTop:3}}>Cut-off: {pp.cutoffLabel} · {pp.freqLabel}</div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:13,color:"rgba(255,255,255,0.5)",fontWeight:700,textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>Next Pay Date</div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:22,color:"#FFD700"}}>{pp.nextPayLabel}</div>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",marginTop:2}}>in {pp.daysUntilNext} day{pp.daysUntilNext!==1?"s":""}</div>
            </div>
          </div>
        </div>

        <div className="slt-card" style={{ marginBottom: 18 }}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ flex: 1 }}>
              <label className="slt-label">Pay Period</label>
              <div style={{ display: "flex", gap: 8 }}>
                {[["weekly", "Weekly"], ["biweekly", "Bi-Weekly"], ["monthly", "Monthly"]].map(([v, l]) => (
                  <button key={v} onClick={() => setPayPeriod(v)} className="slt-btn-secondary"
                    style={{ background: payPeriod === v ? C.blue : "#fff", color: payPeriod === v ? "#fff" : C.textMed, borderColor: payPeriod === v ? C.blue : C.border, padding: "8px 16px" }}>{l}</button>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="slt-btn-secondary" style={{ padding: "10px 16px" }} onClick={() => setShowBonus(!showBonus)}>+ Bonus</button>
              <button className="slt-btn-primary slt-btn-dark-text" style={{ width: "auto", padding: "10px 18px" }} onClick={exportPayroll}>📄 Export</button>
            </div>
          </div>
          <div style={{ fontSize: 12, color: C.textLight, marginTop: 10 }}>Period: {pp?.periodStart ? new Date(pp.periodStart+"T12:00:00").toLocaleDateString("en-CA",{month:"short",day:"numeric",year:"numeric"}) : periodStart.toDateString()} — {pp?.periodEnd ? new Date(pp.periodEnd+"T12:00:00").toLocaleDateString("en-CA",{month:"short",day:"numeric",year:"numeric"}) : now.toDateString()}</div>
        </div>

        {showBonus && (
          <div className="slt-card" style={{ border: `2px solid ${C.green}`, marginBottom: 18 }}>
            <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800, color: C.green, marginBottom: 14 }}>Add Bonus / Adjustment</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div><label className="slt-label">Driver</label>
                <select value={bonusForm.driverUid} onChange={e => setBonusForm(f => ({ ...f, driverUid: e.target.value }))} className="slt-input">
                  <option value="">— Select Driver —</option>
                  {allDrivers.map(d => <option key={d.uid} value={d.uid}>{d.fullName || d.name}</option>)}
                </select>
              </div>
              <div><label className="slt-label">Amount ($)</label><input type="number" step="0.01" placeholder="0.00" value={bonusForm.amount} onChange={e => setBonusForm(f => ({ ...f, amount: e.target.value }))} className="slt-input" /></div>
              <div><label className="slt-label">Reason</label><input placeholder="e.g. Safety bonus" value={bonusForm.reason} onChange={e => setBonusForm(f => ({ ...f, reason: e.target.value }))} className="slt-input" /></div>
              <div><label className="slt-label" style={{display:"block",fontSize:14,fontWeight:900,color:"#E8962E",marginBottom:6,fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:0.5,textTransform:"uppercase"}}>Date</label><input type="date" value={bonusForm.date} onChange={e => setBonusForm(f => ({ ...f, date: e.target.value }))} className="slt-input" /></div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="slt-btn-primary slt-btn-dark-text" style={{ flex: 1, background: `linear-gradient(135deg,${C.green},#1B5E20)` }} onClick={addBonus}>Save Bonus</button>
              <button className="slt-btn-ghost" style={{ padding: "12px 18px" }} onClick={() => setShowBonus(false)}>Cancel</button>
            </div>
          </div>
        )}

        {allDrivers.filter(d => !d._isSelfPay).length === 0 ? (
          <div className="slt-card" style={{ textAlign: "center", padding: "52px 24px" }}>
            <div style={{ fontSize: 48, marginBottom: 14 }}>👥</div>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>No External Drivers Yet</div>
            <div style={{ color: C.textMed }}>Assign loads to drivers to view their payroll</div>
          </div>
        ) : null}

        {/* ── Owner Pay Card ── shows when owner has driven loads with driverBasePay set */}
        {(() => {
          // Owner loads = loads where payMyselfAsDriver is true, OR owner is assigned driver
          const ownerLoads = loads.filter(l => {
            if (!inPeriod(l.date)) return false;
            if (Number(l.driverBasePay||0) <= 0) return false;
            return l.payMyselfAsDriver === true
              || (l.assignedDriverUid === session.uid && (l.addedBy === session.uid || l.user_id === session.uid));
          });
          if (ownerLoads.length === 0) return null;
          const routePay = ownerLoads.reduce((s,l) => s + Number(l.driverBasePay||0), 0);
          const waitPay = ownerLoads.reduce((s,l) => {
            const wm = (Number(l.loadWaitMins)||0) + (Number(l.offloadWaitMins)||0);
            return s + wm/60 * (Number(rates.driverWaitRate)||0);
          }, 0);
          const total = routePay + waitPay;
          const isOpen = expandDriver === "owner-self";
          return (
            <div className="slt-card" style={{ cursor:"pointer", border:"2px solid #166534", marginBottom:12 }} onClick={() => setExpandDriver(isOpen ? null : "owner-self")}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                  <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:17 }}>
                    👤 {session.fullName||session.name||"Owner"} <span style={{ fontSize:12, color:"#166534", fontWeight:700 }}>(You)</span>
                  </div>
                  <div style={{ fontSize:12, color:C.textDarkMut }}>{ownerLoads.length} load{ownerLoads.length!==1?"s":""} this period</div>
                </div>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:24, fontWeight:800, color:"#166534" }}>{fmtC(total)}</div>
                  <div style={{ fontSize:13, color:C.textDarkMut }}>{isOpen?"▲ Hide":"▼ Details"}</div>
                </div>
              </div>
              {isOpen && (
                <div style={{ marginTop:16, paddingTop:16, borderTop:`1px solid ${C.border}` }}>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:10, marginBottom:14 }}>
                    {[["Route Pay", fmtC(routePay), "#166534"], ["Wait Pay", fmtC(waitPay), C.orange]].map(([l,v,color])=>(
                      <div key={l} style={{ background:"#F0FDF4", borderRadius:9, padding:"12px", textAlign:"center" }}>
                        <div style={{ fontSize:13, color:C.textDarkMut, fontWeight:700 }}>{l}</div>
                        <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:18, fontWeight:800, color, marginTop:3 }}>{v}</div>
                      </div>
                    ))}
                  </div>
                  {ownerLoads.map(l=>(
                    <div key={l.id} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:`1px solid ${C.border}`, fontSize:13 }}>
                      <span>{l.location} · {l.date}</span>
                      <span style={{ fontWeight:700, color:"#166534" }}>{fmtC(Number(l.driverBasePay||0))}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {allDrivers.length > 0 && allDrivers.map(driver => {
            const p = getDriverPayroll(driver);
            const isOpen = expandDriver === driver.uid;
            return (
              <div key={driver.uid} className="slt-card" style={{ cursor: "pointer" }} onClick={() => setExpandDriver(isOpen ? null : driver.uid)}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800, fontSize: 17 }}>{driver.fullName || driver.name}</div>
                    <div style={{ fontSize: 12, color: C.textLight }}>{p.dLoads.length} load{p.dLoads.length !== 1 ? "s" : ""} this period</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: 24, fontWeight: 800, color: C.green }}>{fmtC(p.total)}</div>
                    <div style={{ fontSize: 13, color: C.textLight }}>{isOpen ? "▲ Hide" : "▼ Details"}</div>
                  </div>
                </div>
                {isOpen && (
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 14 }}>
                      {[["Route Pay", fmtC(p.routePay), C.blue], ["Wait Pay", fmtC(p.waitPay), C.orange], ["Bonuses", fmtC(p.bonusTotal), C.green]].map(([l, v, color]) => (
                        <div key={l} style={{ background: C.offWhite, borderRadius: 9, padding: "12px", textAlign: "center" }}>
                          <div style={{ fontSize: 13, color: C.textLight, fontWeight: 700 }}>{l}</div>
                          <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: 18, fontWeight: 800, color, marginTop: 3 }}>{v}</div>
                        </div>
                      ))}
                    </div>
                    {p.dLoads.map(l => (
                      <div key={l.id} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${C.border}`, fontSize: 13 }}>
                        <span>{l.location} · {l.date}</span>
                        <span style={{ fontWeight: 700, color: C.blue }}>{fmtC(l.driverBasePay)}</span>
                      </div>
                    ))}
                    {p.driverBonuses.map(b => (
                      <div key={b.id} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${C.border}`, fontSize: 13 }}>
                        <span style={{ color: C.green }}>🎁 {b.reason || "Bonus"} · {b.date}</span>
                        <span style={{ fontWeight: 700, color: C.green }}>+{fmtC(b.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

        {/* ── Payment History ── */}
        {(() => {
          // Build history: all loads with driverBasePay > 0, grouped into "paid" (past period) and "upcoming"
          const allPaidLoads = loads.filter(l => Number(l.driverBasePay||0) > 0);
          // Owner's own draws: loads where owner is driver
          const ownerDrawLoads = loads.filter(l => (l.assignedDriverUid === session.uid || l.addedBy === session.uid || l.user_id === session.uid) && Number(l.driverBasePay||0) > 0);
          const ownerDrawTotal = ownerDrawLoads.reduce((s,l) => {
            const wm = (Number(l.loadWaitMins)||0) + (Number(l.offloadWaitMins)||0);
            const wDrv = wm / 60 * (Number(rates.driverWaitRate)||0);
            return s + Number(l.driverBasePay||0) + wDrv;
          }, 0);
          // Driver paid out totals (all time)
          const driverHistMap = {};
          allPaidLoads.forEach(l => {
            const dUid = l.assignedDriverUid;
            if (!dUid || dUid === session.uid) return;
            if (!driverHistMap[dUid]) driverHistMap[dUid] = { name: l.driverFullName||"Driver", total: 0, count: 0 };
            driverHistMap[dUid].total += Number(l.driverBasePay||0);
            driverHistMap[dUid].count++;
          });
          const driverHistEntries = Object.entries(driverHistMap);
          if (driverHistEntries.length === 0 && ownerDrawLoads.length === 0) return null;
          return (
            <div className="slt-card" style={{ marginTop: 24 }}>
              <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900, fontSize: 20, marginBottom: 16 }}>📜 Payment History (All Time)</div>
              {driverHistEntries.length > 0 && <>
                <div style={{ fontSize: 11, fontWeight: 800, color: C.textLight, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Paid to Drivers</div>
                {driverHistEntries.map(([uid, d]) => (
                  <div key={uid} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>👤 {d.name}</div>
                      <div style={{ fontSize: 12, color: C.textLight }}>{d.count} load{d.count !== 1 ? "s" : ""} paid</div>
                    </div>
                    <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900, fontSize: 20, color: C.orange }}>{fmtC(d.total)}</div>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${C.border}`, marginBottom: 16 }}>
                  <span style={{ fontWeight: 800, fontSize: 13 }}>Total Paid to All Drivers</span>
                  <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900, fontSize: 18, color: C.red }}>{fmtC(driverHistEntries.reduce((s,[,d])=>s+d.total,0))}</span>
                </div>
              </>}
              {ownerDrawLoads.length > 0 && <>
                <div style={{ fontSize: 11, fontWeight: 800, color: C.textLight, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, marginTop: driverHistEntries.length > 0 ? 4 : 0 }}>Paid to Yourself (Owner Draws)</div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0" }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>🏢 {session.companyName || session.fullName || "Owner"}</div>
                    <div style={{ fontSize: 12, color: C.textLight }}>{ownerDrawLoads.length} load{ownerDrawLoads.length !== 1 ? "s" : ""} driven</div>
                  </div>
                  <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900, fontSize: 20, color: C.green }}>{fmtC(ownerDrawTotal)}</div>
                </div>
              </>}
            </div>
          );
        })()}

      </div>
    </div>
  );
}

// ─── Analytics Tab ────────────────────────────────────────────────
// Feature 3: Trip History & Analytics (Charts using SVG)
function AnalyticsTab({ session, loads, isOwner, rates , goBack}) {
  const myLoads = isOwner ? loads : loads.filter(l => l.assignedDriverUid === session.uid || l.addedBy === session.uid || l.user_id === session.uid);
  const [view, setView] = useState("income");
  const expenses = getStored(expensesKey(session.uid));

  // Helper: driver pay for a single load
  const getDriverPay = (l) => {
    const wm = (Number(l.loadWaitMins) || 0) + (Number(l.offloadWaitMins) || 0);
    return Number(l.driverBasePay || 0) + wm / 60 * (Number(rates.driverWaitRate) || 0);
  };
  const getOwnerGross = (l) => {
    const wm = (Number(l.loadWaitMins) || 0) + (Number(l.offloadWaitMins) || 0);
    return Number(l.earnings || 0) + wm / 60 * (Number(rates.companyWaitRate) || 0);
  };

  // Build monthly data (last 6 months)
  const months = [];
  const now = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleString("default", { month: "short" });
    const mLoads = myLoads.filter(l => l.date && l.date.startsWith(key));
    const monthExp = expenses.filter(e => e.date && e.date.startsWith(key)).reduce((s,e) => s + Number(e.amount||0), 0);
    if (isOwner) {
      const gross = mLoads.reduce((s, l) => s + getOwnerGross(l), 0);
      const drvPay = mLoads.reduce((s, l) => s + getDriverPay(l), 0);
      months.push({ key, label, count: mLoads.length, gross, net: gross - drvPay - monthExp, drvPay, exp: monthExp });
    } else {
      const pay = mLoads.reduce((s, l) => s + getDriverPay(l), 0);
      months.push({ key, label, count: mLoads.length, gross: pay, net: pay - monthExp, exp: monthExp });
    }
  }

  // Route performance — owner sees business earnings, driver sees their pay per route
  const routeMap = {};
  myLoads.forEach(l => {
    if (!l.location) return;
    if (!routeMap[l.location]) routeMap[l.location] = { count: 0, totalEarnings: 0, totalPay: 0 };
    routeMap[l.location].count++;
    routeMap[l.location].totalEarnings += getOwnerGross(l);
    routeMap[l.location].totalPay += getDriverPay(l);
  });
  const topRoutes = Object.entries(routeMap).map(([route, d]) => ({
    route, ...d,
    displayTotal: isOwner ? d.totalEarnings : d.totalPay,
    avg: isOwner ? d.totalEarnings / d.count : d.totalPay / d.count,
  })).sort((a, b) => b.displayTotal - a.displayTotal).slice(0, 8);

  // Fuel efficiency
  const loadsWithFuel = myLoads.filter(l => l.fuelLitres > 0 && l.km > 0);
  const avgEfficiency = loadsWithFuel.length > 0
    ? loadsWithFuel.reduce((s, l) => s + Number(l.km) / Number(l.fuelLitres), 0) / loadsWithFuel.length
    : null;

  const totalLoads = myLoads.length;
  const completedLoads = myLoads.filter(l => l.completed).length;
  // KPI totals: owner = gross/net, driver = their pay only
  const totalDriverPay = myLoads.reduce((s, l) => s + getDriverPay(l), 0);
  const totalGross = myLoads.reduce((s, l) => s + getOwnerGross(l), 0);
  const totalOwnerNet = totalGross - myLoads.reduce((s, l) => s + (isOwner ? getDriverPay(l) : 0), 0);
  const displayTotal = isOwner ? totalGross : totalDriverPay;
  const avgPerLoad = totalLoads > 0 ? displayTotal / totalLoads : 0;

  const BarChart = ({ data, valueKey, colorFn, height = 140 }) => {
    const max = Math.max(...data.map(d => d[valueKey]), 1);
    return (
      <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height, padding: "0 4px" }}>
      {goBack && <BackButton onBack={goBack} label="Back" />}
        {data.map((d, i) => {
          const barH = Math.max(4, (d[valueKey] / max) * (height - 30));
          const val = d[valueKey];
          return (
            <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%" }}>
              <div style={{ fontSize: 12, color: C.textLight, marginBottom: 3, fontWeight: 700 }}>
                {val > 1000 ? `$${(val / 1000).toFixed(1)}k` : val > 0 ? fmtC(val) : "—"}
              </div>
              <div style={{ width: "100%", height: barH, background: colorFn ? colorFn(i) : `linear-gradient(180deg,${C.teal},${C.blue})`, borderRadius: "5px 5px 0 0", transition: "height 0.4s" }} />
              <div style={{ fontSize: 12, color: C.textMed, marginTop: 4, fontWeight: 600 }}>{d.label}</div>
            </div>
          );
        })}
      </div>
    );
  };


  // ── AI Insights ──────────────────────────────────────────────────
  const insights = [];
  const expensesAll = getStored(expensesKey(session.uid));
  const lastMonth = months[months.length - 1];
  const prevMonth = months[months.length - 2];
  if (lastMonth && prevMonth && prevMonth.exp > 0) {
    const expChange = ((lastMonth.exp - prevMonth.exp) / prevMonth.exp) * 100;
    if (expChange > 20) insights.push({ type:"warning", icon:"⚠️", title:"Expense Spike", msg:`Expenses up ${expChange.toFixed(0)}% vs last month ($${lastMonth.exp.toFixed(0)} vs $${prevMonth.exp.toFixed(0)}). Review your spending.` });
    else if (expChange < -15) insights.push({ type:"success", icon:"✅", title:"Expenses Down", msg:`Great job! Expenses dropped ${Math.abs(expChange).toFixed(0)}% vs last month.` });
  }
  if (topRoutes.length > 0) {
    const best = topRoutes[0];
    insights.push({ type:"info", icon:"🏆", title:"Best Route", msg:`${best.route} is your most profitable route — averaging ${fmtC(best.avg)} per load across ${best.count} loads.` });
  }
  const pendingLoads = myLoads.filter(l => !l.completed);
  if (pendingLoads.length > 2) insights.push({ type:"warning", icon:"🔄", title:"Pending Loads", msg:`You have ${pendingLoads.length} loads not marked complete. Update them to keep records accurate.` });
  const driverStats = {};
  if (isOwner) {
    myLoads.forEach(l => {
      // include loads assigned to any driver including owner-driven
      const n = l.driverFullName || l.assignedDriverName || (l.assignedDriverUid && l.assignedDriverUid !== session.uid ? "Driver" : null);
      if (!n) return;
      const uid = l.assignedDriverUid || n;
      const key = uid;
      if (!driverStats[key]) driverStats[key] = { loads:0, pay:0, name:n, uid };
      driverStats[key].loads++;
      driverStats[key].pay += getDriverPay(l);
    });
    const sorted = Object.entries(driverStats).sort((a,b) => b[1].loads - a[1].loads);
    if (sorted.length > 0) {
      const [, topData] = sorted[0];
      insights.push({ type:"success", icon:"🚛", title:"Top Driver", msg:`${topData.name} is your hardest working driver — ${topData.loads} loads, ${fmtC(topData.pay)} earned this period.` });
    }
    if (sorted.length > 1) {
      const [, lowData] = sorted[sorted.length-1];
      if (lowData.loads < 3) insights.push({ type:"info", icon:"💡", title:"Low Activity", msg:`${lowData.name} only has ${lowData.loads} load${lowData.loads!==1?"s":""} this period. Consider assigning more loads.` });
    }
  }
  if (months.length >= 2) {
    const last2 = months[months.length-1], prev2 = months[months.length-2];
    if (prev2.gross > 0) {
      const ch = ((last2.gross - prev2.gross)/prev2.gross)*100;
      if (ch > 10) insights.push({ type:"success", icon:"📈", title:"Revenue Up", msg:`Revenue up ${ch.toFixed(0)}% this month — ${last2.count} loads vs ${prev2.count} last month.` });
      else if (ch < -10) insights.push({ type:"warning", icon:"📉", title:"Revenue Down", msg:`Revenue down ${Math.abs(ch).toFixed(0)}% this month. ${last2.count} loads vs ${prev2.count} last month.` });
    }
  }
  const actions = [];
  if (pendingLoads.length > 0) actions.push({ icon:"🔄", text:`${pendingLoads.length} loads not marked complete — tap to go to My Loads`, urgent:pendingLoads.length > 3, tab:"log" });
  const uncategorized = expensesAll.filter(e => !e.category || e.category === "other").length;
  if (uncategorized > 0) actions.push({ icon:"🧾", text:`${uncategorized} expenses need categories for better tax reporting`, urgent:false, tab:"expenses" });
  if (isOwner && Object.keys(driverStats).length === 0 && myLoads.length > 0) actions.push({ icon:"👥", text:"No drivers found — make sure loads have drivers assigned", urgent:false, tab:"drivers" });
  const noTruck = myLoads.filter(l => !l.truckId && !l.manualTruckNumber).length;
  if (noTruck > 3) actions.push({ icon:"🚛", text:`${noTruck} loads have no truck assigned — add truck numbers for better tracking`, urgent:false, tab:"log" });

  return (
    <div className="slt-page" style={{background:"#070B14",color:"#F0F0F0"}}>
      {goBack && <BackButton onBack={goBack} label="Back" />}
      <div className="slt-hero">
        <div className="slt-hero-title">{isOwner ? "📈 Business Analytics" : "📈 My Analytics"}</div>
        <div className="slt-hero-sub">{isOwner ? "Fleet revenue · Driver performance · Smart insights" : "Your pay · Expenses · Route performance"}</div>
      </div>
      <div className="slt-container">

        {/* ── Spending Insights Card (TruckSmarter-style) ── */}
        {months.length > 0 && (() => {
          const cur = months[months.length - 1];
          const maxVal = Math.max(...months.map(m => Math.max(m.gross, m.exp)), 1);
          return (
            <div style={{borderRadius:20,overflow:"hidden",marginBottom:20,background:"linear-gradient(135deg,#0D1E3A,#0F2A50)",border:"1px solid rgba(59,130,246,0.25)",boxShadow:"0 8px 32px rgba(0,0,0,0.5)"}}>
              {/* Header */}
              <div style={{padding:"18px 18px 10px"}}>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:20,color:"#fff",display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                  📊 Spending Insights
                  <span style={{background:"rgba(255,255,255,0.1)",color:"rgba(255,255,255,0.5)",border:"1px solid rgba(255,255,255,0.15)",borderRadius:8,padding:"2px 8px",fontSize:11,fontWeight:700}}>BETA</span>
                </div>
                {/* Legend */}
                <div style={{display:"flex",gap:14,marginBottom:14}}>
                  <div style={{display:"flex",alignItems:"center",gap:5,fontSize:11,fontWeight:700,color:"#fff"}}>
                    <div style={{width:10,height:10,borderRadius:3,background:"#E8962E"}}></div> Revenue
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:5,fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.6)"}}>
                    <div style={{width:10,height:10,borderRadius:3,background:"rgba(255,255,255,0.25)"}}></div> Expenses
                  </div>
                </div>
                {/* Bar chart */}
                <div style={{display:"flex",alignItems:"flex-end",gap:6,height:80,marginBottom:8}}>
                  {months.map((m, i) => {
                    const revH = Math.max(4, (m.gross / maxVal) * 72);
                    const expH = Math.max(4, (m.exp   / maxVal) * 72);
                    const isLast = i === months.length - 1;
                    return (
                      <div key={m.key} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:3}}>
                        <div style={{display:"flex",gap:2,alignItems:"flex-end"}}>
                          <div style={{width:10,borderRadius:"3px 3px 0 0",height:revH,background:isLast?"#FFD580":"#E8962E",transition:"height 0.4s"}}/>
                          <div style={{width:10,borderRadius:"3px 3px 0 0",height:expH,background:isLast?"rgba(255,255,255,0.4)":"rgba(255,255,255,0.2)",transition:"height 0.4s"}}/>
                        </div>
                        <div style={{fontSize:9,fontWeight:700,color:isLast?"#E8962E":"rgba(255,255,255,0.45)",borderBottom:isLast?"2px solid #E8962E":"none",paddingBottom:isLast?1:0}}>{m.label}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Summary strip */}
              <div style={{background:"rgba(255,255,255,0.06)",borderTop:"1px solid rgba(255,255,255,0.1)",padding:"14px 18px"}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
                  <div style={{textAlign:"center"}}>
                    <div style={{fontSize:9,fontWeight:800,letterSpacing:"0.1em",textTransform:"uppercase",color:"rgba(255,255,255,0.35)",marginBottom:3}}>Revenue</div>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:900,color:"#fff"}}>{fmtC(cur.gross)}</div>
                  </div>
                  <div style={{textAlign:"center"}}>
                    <div style={{fontSize:9,fontWeight:800,letterSpacing:"0.1em",textTransform:"uppercase",color:"rgba(255,255,255,0.35)",marginBottom:3}}>Expenses</div>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:900,color:"#ef4444"}}>{fmtC(cur.exp)}</div>
                  </div>
                </div>
                <div style={{height:1,background:"rgba(255,255,255,0.1)",marginBottom:10}}/>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:9,fontWeight:800,letterSpacing:"0.1em",textTransform:"uppercase",color:"rgba(255,255,255,0.35)",marginBottom:4}}>{cur.label} Profit</div>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:30,fontWeight:900,color:"#E8962E"}}>{fmtC(cur.net)}</div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* KPI row */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))", gap:12, marginBottom:20 }}>
          {(isOwner ? [
            ["Total Loads", totalLoads, C.blue],
            ["Gross Revenue", fmtC(totalGross), C.green],
            ["Net Income", fmtC(totalOwnerNet), C.teal],
            ["Avg/Load", fmtC(avgPerLoad), C.orange],
            ["Completion", `${(totalLoads>0?(completedLoads/totalLoads)*100:100).toFixed(0)}%`, C.green],
          ] : [
            ["Total Loads", totalLoads, C.blue],
            ["Total Pay", fmtC(totalDriverPay), C.green],
            ["Avg/Load", fmtC(avgPerLoad), C.teal],
            ["Completed", completedLoads, C.green],
            ["Pending", pendingLoads.length, pendingLoads.length > 0 ? C.orange : C.green],
          ]).map(([l,v,c]) => (
            <div key={l} className="slt-card-sm" style={{ borderTop:`3px solid ${c}`, textAlign:"center", padding:"12px 8px" }}>
              <div style={{ fontSize:12, color:C.textDarkMut, fontWeight:700, marginBottom:2, textTransform:"uppercase" }}>{l}</div>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:22, fontWeight:900, color:c }}>{v}</div>
            </div>
          ))}
        </div>

        {/* 🤖 AI INSIGHTS */}
        {insights.length > 0 && (
          <div className="slt-card" style={{ marginBottom:20, background:"linear-gradient(135deg,#E8962E,#E8962E)", border:"none" }}>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:16, color:"#fff", marginBottom:14, display:"flex", alignItems:"center", gap:8 }}>
              🤖 AI Insights <span style={{ fontSize:13, fontWeight:600, color:"rgba(255,255,255,0.5)" }}>Based on your data</span>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {insights.map((ins, i) => (
                <div key={i} style={{ background:"rgba(255,255,255,0.08)", borderRadius:12, padding:"12px 14px", borderLeft:`4px solid ${ins.type==="success"?"#4ade80":ins.type==="warning"?"#FFD700":"rgba(255,255,255,0.4)"}` }}>
                  <div style={{ fontWeight:800, fontSize:13, color:"#fff", marginBottom:3 }}>{ins.icon} {ins.title}</div>
                  <div style={{ fontSize:13, color:"rgba(255,255,255,0.75)", lineHeight:1.5 }}>{ins.msg}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ⚠️ ACTION REQUIRED */}
        {actions.length > 0 && (
          <div className="slt-card" style={{ marginBottom:20, border:`1.5px solid ${C.orange}` }}>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:15, color:C.orange, marginBottom:12 }}>⚠️ Action Required</div>
            {actions.map((a, i) => (
              <button key={i} onClick={()=>{ if(a.tab && goBack){ goBack(); setTimeout(()=>{ const ev=new CustomEvent("tp-nav",{detail:{tab:a.tab}}); window.dispatchEvent(ev); },100); }}}
                style={{ width:"100%", display:"flex", alignItems:"center", gap:10, padding:"11px 0", borderBottom: i < actions.length-1 ? `1px solid ${C.border}` : "none", background:"none", border:"none", cursor: a.tab ? "pointer" : "default", textAlign:"left" }}>
                <span style={{ fontSize:20 }}>{a.icon}</span>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, color:a.urgent ? C.red : C.textMed, fontWeight: a.urgent ? 700 : 600 }}>{a.text}</div>
                  {a.tab && <div style={{ fontSize:13, color:C.blue, fontWeight:600, marginTop:2 }}>Tap to fix →</div>}
                </div>
                {a.urgent && <span style={{ fontSize:13, background:C.red, color:"#fff", padding:"2px 8px", borderRadius:10, fontWeight:700 }}>URGENT</span>}
              </button>
            ))}
          </div>
        )}

        {/* 📊 REVENUE CHART */}
        <div className="slt-card" style={{ marginBottom:20 }}>
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:15, marginBottom:4 }}>📊 Monthly Revenue</div>
          <div style={{ fontSize:12, color:C.textDarkMut, marginBottom:16 }}>Last 6 months</div>
          <BarChart data={months} valueKey="gross" colorFn={(i) => i === months.length-1 ? "#E8962E" : "#FFD580"} height={140} />
        </div>

        {/* 💸 EXPENSE BREAKDOWN */}
        {(() => {
          const expBycat = {};
          expensesAll.forEach(e => { const cat = e.category||"other"; if(!expBycat[cat])expBycat[cat]=0; expBycat[cat]+=Number(e.amount||0); });
          const expEntries = Object.entries(expBycat).sort((a,b)=>b[1]-a[1]).slice(0,6);
          const totalExp = expEntries.reduce((s,[,v])=>s+v,0);
          if (expEntries.length === 0) return null;
          const colors = ["#E8962E","#E53935","#F57C00","#2E7D32","#E8962E","#E8962E"];
          return (
            <div className="slt-card" style={{ marginBottom:20 }}>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:15, marginBottom:4 }}>💸 Where You Spend Most</div>
              <div style={{ fontSize:12, color:C.textDarkMut, marginBottom:16 }}>All time expense breakdown</div>
              {expEntries.map(([cat, amt], i) => {
                const pct = totalExp > 0 ? (amt/totalExp)*100 : 0;
                return (
                  <div key={cat} style={{ marginBottom:10 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                      <span style={{ fontSize:13, fontWeight:600, color:C.textDarkMed, textTransform:"capitalize" }}>{cat.replace(/_/g," ")}</span>
                      <span style={{ fontSize:13, fontWeight:700 }}>{fmtC(amt)} <span style={{ color:C.textDarkMut, fontWeight:500 }}>({pct.toFixed(0)}%)</span></span>
                    </div>
                    <div style={{ height:8, background:C.border, borderRadius:4, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${pct}%`, background:colors[i%colors.length], borderRadius:4 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* 🏆 DRIVER LEADERBOARD */}
        {isOwner && Object.keys(driverStats).length > 0 && (
          <div className="slt-card" style={{ marginBottom:20 }}>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:15, marginBottom:4 }}>🏆 Driver Leaderboard</div>
            <div style={{ fontSize:12, color:C.textDarkMut, marginBottom:14 }}>Ranked by loads completed</div>
            {Object.values(driverStats).sort((a,b)=>b.loads-a.loads).map((data, i) => {
              const initials = (data.name||"?").split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
              const medals = ["🥇","🥈","🥉"];
              const medalColors = ["#FFD700","#C0C0C0","#CD7F32"];
              return (
                <div key={data.uid} style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 0", borderBottom:`1px solid ${C.border}` }}>
                  <div style={{ width:32, height:32, borderRadius:"50%", background:i<3?medalColors[i]:C.blue, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:900, fontSize:i<3?18:13, color:i<3?"#E8962E":"#fff", flexShrink:0 }}>
                    {i < 3 ? medals[i] : initials}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:14 }}>{data.name}</div>
                    <div style={{ fontSize:12, color:C.textDarkMut }}>{data.loads} load{data.loads!==1?"s":""} · {fmtC(data.pay)} earned</div>
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:24, fontWeight:900, color: i===0?C.orange:C.green }}>{data.loads}</div>
                    <div style={{ fontSize:12, color:C.textDarkMut }}>loads</div>
                  </div>
                </div>
              );
            })}
            {Object.keys(driverStats).length === 0 && (
              <div style={{ textAlign:"center", padding:"20px 0", color:C.textDarkMut, fontSize:13 }}>No driver data yet — assign loads to drivers to see leaderboard</div>
            )}
          </div>
        )}
        {isOwner && Object.keys(driverStats).length === 0 && myLoads.length > 0 && (
          <div className="slt-card" style={{ marginBottom:20, border:`1px dashed ${C.border}`, textAlign:"center", padding:28 }}>
            <div style={{ fontSize:32, marginBottom:8 }}>🏆</div>
            <div style={{ fontWeight:700, marginBottom:4 }}>Driver Leaderboard</div>
            <div style={{ fontSize:13, color:C.textDarkMut }}>Assign loads to drivers to see rankings here</div>
          </div>
        )}

        {/* 🗺 ROUTE PERFORMANCE */}
        {topRoutes.length > 0 && (
          <div className="slt-card" style={{ marginBottom:20 }}>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:15, marginBottom:14 }}>🗺 Route Performance</div>
            {topRoutes.map((r, i) => (
              <div key={r.route} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 0", borderBottom:`1px solid ${C.border}` }}>
                <div style={{ width:28, height:28, borderRadius:8, background:`${C.blue}18`, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:900, fontSize:13, color:C.blue, flexShrink:0 }}>{i+1}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:13 }}>{r.route}</div>
                  <div style={{ fontSize:12, color:C.textDarkMut }}>{r.count} loads · avg {fmtC(r.avg)}</div>
                </div>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:18, fontWeight:900, color:C.green }}>{fmtC(r.displayTotal)}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 🎯 MONTHLY GOAL */}
        {(() => {
          const curMonth = months[months.length-1];
          const goalKey = `tp-monthly-goal-${session.uid}`;
          const defaultGoal = isOwner ? 20000 : 5000;
          const [goal, setGoal] = useState(() => Number(localStorage.getItem(goalKey)) || defaultGoal);
          useEffect(()=>{ sbGetSettings(session.uid).then(d=>{ if(d&&d.monthlyGoal){ localStorage.setItem(goalKey,d.monthlyGoal); setGoal(Number(d.monthlyGoal)); } }).catch(()=>{}); },[session.uid]);
          const [editingGoal, setEditingGoal] = useState(false);
          const [goalInput, setGoalInput] = useState(String(goal));
          const saveGoal = () => {
            const val = Number(goalInput);
            if (val > 0) { localStorage.setItem(goalKey, val); setGoal(val); sbGetSettings(session.uid).then(ex=>sbSaveSettings(session.uid,{...(ex||{}),monthlyGoal:val},[])).catch(()=>{}); }
            setEditingGoal(false);
          };
          const pct = Math.min((curMonth?.gross||0)/goal*100, 100);
          return (
            <div className="slt-card" style={{ marginBottom:20, background:"linear-gradient(135deg,#f0f4ff,#e8efff)", border:"1.5px solid rgba(36,59,110,0.15)" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:15, color:"#E8962E" }}>🎯 Monthly Goal</div>
                <button onClick={()=>{ setGoalInput(String(goal)); setEditingGoal(true); }} style={{ fontSize:12, color:"#E8962E", background:"rgba(36,59,110,0.1)", border:"none", borderRadius:8, padding:"4px 10px", cursor:"pointer", fontWeight:700 }}>✏️ Edit</button>
              </div>
              {editingGoal ? (
                <div style={{ display:"flex", gap:8, marginBottom:12 }}>
                  <input type="number" value={goalInput} onChange={e=>setGoalInput(e.target.value)} className="slt-input" style={{ flex:1 }} placeholder="Enter goal amount" />
                  <button onClick={saveGoal} style={{ padding:"8px 16px", background:"#E8962E", color:"#fff", border:"none", borderRadius:8, fontWeight:700, cursor:"pointer" }}>Save</button>
                  <button onClick={()=>setEditingGoal(false)} style={{ padding:"8px 12px", background:"#eee", border:"none", borderRadius:8, cursor:"pointer" }}>✕</button>
                </div>
              ) : (
                <div style={{ fontSize:12, color:C.textDarkMut, marginBottom:14 }}>Target: {fmtC(goal)} this month</div>
              )}
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                <span style={{ fontSize:13, fontWeight:700, color:"#E8962E" }}>{fmtC(curMonth?.gross||0)}</span>
                <span style={{ fontSize:13, color:C.textDarkMut }}>{pct.toFixed(0)}% of goal</span>
              </div>
              <div style={{ height:12, background:"rgba(36,59,110,0.1)", borderRadius:6, overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${pct}%`, background:pct>=100?C.green:"linear-gradient(90deg,#E8962E,#E8962E)", borderRadius:6, transition:"width 0.6s" }} />
              </div>
              {pct >= 100 
                ? <div style={{ fontSize:13, color:C.green, fontWeight:700, marginTop:8 }}>🎉 Goal achieved this month!</div>
                : <div style={{ fontSize:13, color:C.textDarkMut, marginTop:8 }}>{fmtC(goal-(curMonth?.gross||0))} more to reach your goal</div>
              }
            </div>
          );
        })()}

      </div>
    </div>
  );
}

function DocumentsTab({ session , goBack}) {
  const docsKey = `tp-docs-${session.uid}`;
  const [docs, setDocs] = useState(getStored(docsKey));
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", category: "bol", note: "", expiry: "", files: [] });
  const [viewDoc, setViewDoc] = useState(null);
  const [filterCat, setFilterCat] = useState("all");
  const [nameError, setNameError] = useState(false);
  const inputRef = useRef(null);

  // Fix: Load from Supabase driver_documents, merge with localStorage
  useEffect(() => {
    sbGetDriverDocuments(session.uid).then(sbDocs => {
      if (sbDocs && sbDocs.length > 0) {
        const local = getStored(docsKey);
        const merged = [...sbDocs];
        local.forEach(l => { if (!merged.find(d => d.id === l.id)) merged.push(l); });
        setDocs(merged);
        localStorage.setItem(docsKey, JSON.stringify(merged));
      }
    }).catch(() => {});
  }, [session.uid]);

  const CATS = [
    { id: "bol", label: "Bill of Lading", icon: "📦", color: C.blue },
    { id: "insurance", label: "Insurance", icon: "🛡", color: C.green },
    { id: "permit", label: "Permits", icon: "📋", color: C.orange },
    { id: "inspection", label: "Inspection", icon: "🔍", color: C.purple },
    { id: "license", label: "License / CVOR", icon: "🪪", color: C.teal },
    { id: "other", label: "Other", icon: "📁", color: C.textMed },
  ];
  const getCat = (id) => CATS.find(c => c.id === id) || CATS[5];

  const save = (arr) => {
    setDocs(arr);
    try { localStorage.setItem(docsKey, JSON.stringify(arr)); } catch {
      const stripped = arr.map(d => ({ ...d, files: (d.files || []).map(f => ({ ...f, data: "[stored]" })) }));
      localStorage.setItem(docsKey, JSON.stringify(stripped));
      setDocs(arr);
    }
  };

  const handleFile = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = ev => setForm(f => ({ ...f, files: [...f.files, { id: Date.now().toString() + Math.random(), name: file.name, data: ev.target.result, type: file.type }] }));
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const add = () => {
    if (!form.name.trim()) { setNameError(true); setTimeout(()=>setNameError(false), 2500); return; }
    const newDoc = { ...form, id: Date.now().toString(), uploadedAt: todayStr() };
    save([newDoc, ...docs]);
    // Fix: also save to Supabase driver_documents
    sbSaveDriverDocument({ user_id: session.uid, ...newDoc }).catch(console.error);
    setForm({ name: "", category: "bol", note: "", expiry: "", files: [] });
    setNameError(false);
    setShowAdd(false);
  };

  const isExpired = (d) => d.expiry && d.expiry < todayStr();
  const isExpiringSoon = (d) => {
    if (!d.expiry || isExpired(d)) return false;
    const diff = (new Date(d.expiry) - new Date()) / (1000 * 60 * 60 * 24);
    return diff <= 30;
  };

  const filtered = filterCat === "all" ? docs : docs.filter(d => d.category === filterCat);
  const expiredCount = docs.filter(isExpired).length;
  const soonCount = docs.filter(isExpiringSoon).length;

  return (
    <div className="slt-page" style={{background:"#070B14",color:"#F0F0F0"}}>
      {goBack && <BackButton onBack={goBack} label="Back" />}
      <div className="slt-hero">
        <div className="slt-hero-title">📁 Document Storage</div>
        <div className="slt-hero-sub">BOL · Insurance · Permits · Inspections — all in one place</div>
      </div>
      <div className="slt-container">
        {(expiredCount > 0 || soonCount > 0) && (
          <div style={{ background: "#FFF8E1", border: "1.5px solid #FFB300", borderRadius: 12, padding: "12px 18px", marginBottom: 18, display: "flex", gap: 12, alignItems: "center" }}>
            <span style={{ fontSize: 22 }}>⚠️</span>
            <div>
              {expiredCount > 0 && <div style={{ fontWeight: 800, color: C.red }}>{expiredCount} document{expiredCount > 1 ? "s" : ""} expired!</div>}
              {soonCount > 0 && <div style={{ fontWeight: 700, color: C.orange }}>{soonCount} expiring within 30 days</div>}
            </div>
          </div>
        )}

        {/* Category filter */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18, alignItems: "center" }}>
          <button onClick={() => setFilterCat("all")} className="slt-btn-secondary"
            style={{ background: filterCat === "all" ? C.navy : "#fff", color: filterCat === "all" ? "#fff" : C.textMed, borderColor: filterCat === "all" ? C.navy : C.border, padding: "7px 14px" }}>All ({docs.length})</button>
          {CATS.map(c => {
            const cnt = docs.filter(d => d.category === c.id).length;
            if (!cnt && filterCat !== c.id) return null;
            return (
              <button key={c.id} onClick={() => setFilterCat(c.id)} className="slt-btn-secondary"
                style={{ background: filterCat === c.id ? c.color : "#fff", color: filterCat === c.id ? "#fff" : C.textMed, borderColor: filterCat === c.id ? c.color : C.border, padding: "7px 14px" }}>
                {c.icon} {c.label} ({cnt})
              </button>
            );
          })}
          <button className="slt-btn-primary slt-btn-dark-text" style={{ marginLeft: "auto", width: "auto", padding: "10px 18px" }} onClick={() => setShowAdd(!showAdd)}>+ Upload Doc</button>
        </div>

        {showAdd && (
          <div className="slt-card" style={{ border: `2px solid ${C.blue}`, marginBottom: 18 }}>
            <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800, color: C.blue, fontSize: 16, marginBottom: 14 }}>Upload Document</div>
            <div style={{ marginBottom: 12 }}>
              <label className="slt-label">Document Name *</label>
              <input value={form.name} onChange={e => { setForm(f => ({ ...f, name: e.target.value })); setNameError(false); }} className="slt-input" placeholder="e.g. Insurance Certificate 2024" style={{border: nameError ? "2px solid #E53935" : undefined}} />
              {nameError && <div style={{color:"#E53935",fontSize:12,fontWeight:600,marginTop:4}}>⚠️ Please enter a document name to save</div>}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div><label className="slt-label">Category</label>
                <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="slt-input">
                  {CATS.map(c => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
                </select>
              </div>
              <div><label className="slt-label">Expiry Date</label><input type="date" value={form.expiry} onChange={e => setForm(f => ({ ...f, expiry: e.target.value }))} className="slt-input" /></div>
            </div>
            <div style={{ marginBottom: 14 }}><label className="slt-label">Notes</label><input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} className="slt-input" placeholder="Optional notes…" /></div>
            <div style={{ marginBottom: 14 }}>
              <label className="slt-label">Attach Files</label>
              <div style={{ border: `2px dashed ${C.border}`, borderRadius: 12, padding: 20, textAlign: "center", cursor: "pointer", background: C.offWhite }} onClick={() => inputRef.current?.click()}>
                <input ref={inputRef} type="file" accept="image/*,application/pdf" multiple style={{ display: "none" }} onChange={handleFile} />
                <div style={{ fontSize: 28, marginBottom: 6 }}>📎</div>
                <div style={{ fontSize: 13, color: C.textMed }}>Click to attach files (images, PDFs)</div>
              </div>
              {form.files.length > 0 && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                  {form.files.map(f => (
                    <div key={f.id} style={{ position: "relative" }}>
                      {f.type?.startsWith("image")
                        ? <img src={f.data} alt={f.name} style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 8, border: `1px solid ${C.border}` }} />
                        : <div style={{ width: 64, height: 64, borderRadius: 8, border: `1px solid ${C.border}`, background: C.offWhite, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>📄</div>
                      }
                      <button onClick={() => setForm(prev => ({ ...prev, files: prev.files.filter(x => x.id !== f.id) }))}
                        style={{ position: "absolute", top: -6, right: -6, background: C.red, color: "#fff", border: "none", borderRadius: "50%", width: 18, height: 18, fontSize: 12, cursor: "pointer", fontWeight: 800 }}>×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="slt-btn-primary slt-btn-dark-text" style={{ flex: 1 }} onClick={add}>Save Document</button>
              <button className="slt-btn-ghost" style={{ padding: "12px 18px" }} onClick={() => setShowAdd(false)}>Cancel</button>
            </div>
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="slt-card" style={{ textAlign: "center", padding: "52px 24px" }}>
            <div style={{ fontSize: 48, marginBottom: 14 }}>📁</div>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>No documents yet</div>
            <div style={{ color: C.textMed }}>Upload BOL, insurance, permits and keep everything organized</div>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 14 }}>
            {filtered.map(d => {
              const cat = getCat(d.category);
              const expired = isExpired(d);
              const soon = isExpiringSoon(d);
              return (
                <div key={d.id} className="slt-card" style={{ borderTop: `4px solid ${expired ? C.red : soon ? C.orange : cat.color}`, cursor: "pointer" }} onClick={() => setViewDoc(d)}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                    <span style={{ fontSize: 28 }}>{cat.icon}</span>
                    <div style={{ display: "flex", gap: 6 }}>
                      {expired && <span className="slt-badge-red" style={{ fontSize: 12 }}>EXPIRED</span>}
                      {soon && !expired && <span className="slt-badge-orange" style={{ fontSize: 12 }}>⚠ Expiring</span>}
                    </div>
                  </div>
                  <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{d.name}</div>
                  <div style={{ fontSize: 12, color: C.textLight, marginBottom: 6 }}>{cat.label}{d.expiry ? ` · Expires ${d.expiry}` : ""}</div>
                  {d.note && <div style={{ fontSize: 12, color: C.textMed, marginBottom: 8, fontStyle: "italic" }}>{d.note}</div>}
                  {(d.files || []).length > 0 && <div style={{ fontSize: 12, color: C.blue }}>📎 {d.files.length} file{d.files.length !== 1 ? "s" : ""}</div>}
                  <button onClick={e => { e.stopPropagation(); if (window.confirm("Delete?")) { save(docs.filter(x => x.id !== d.id)); sbDeleteDriverDocument(d.id).catch(console.error); } }}
                    style={{ marginTop: 10, background: "none", border: "none", color: C.red, cursor: "pointer", fontSize: 12 }}>🗑 Delete</button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Doc viewer modal */}
      {viewDoc && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "#fff", borderRadius: 18, width: "100%", maxWidth: 500, maxHeight: "92vh", overflowY: "auto", boxShadow: "0 28px 80px rgba(0,0,0,0.55)" }}>
            <div style={{ padding: "16px 22px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 style={{ margin: 0, fontSize: 17, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800 }}>{viewDoc.name}</h2>
              <button className="slt-btn-ghost" style={{ padding: "6px 12px" }} onClick={() => setViewDoc(null)}>✕</button>
            </div>
            <div style={{ padding: 22 }}>
              <div style={{ fontSize: 13, color: C.textMed, marginBottom: 12 }}>Category: {getCat(viewDoc.category).label}{viewDoc.expiry ? ` · Expires: ${viewDoc.expiry}` : ""}</div>
              {viewDoc.note && <div style={{ background: C.offWhite, borderRadius: 9, padding: "10px 14px", marginBottom: 14, fontSize: 13, color: C.textMed }}>{viewDoc.note}</div>}
              {(viewDoc.files || []).length === 0 ? <div style={{ color: C.textLight, textAlign: "center", padding: "20px 0" }}>No files attached</div> : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {viewDoc.files.map(f => (
                    <div key={f.id}>
                      {f.type?.startsWith("image") && f.data !== "[stored]"
                        ? <img src={f.data} alt={f.name} style={{ width: "100%", borderRadius: 10, border: `1px solid ${C.border}` }} />
                        : <div style={{ background: C.offWhite, borderRadius: 10, padding: "18px 20px", display: "flex", alignItems: "center", gap: 12 }}>
                          <span style={{ fontSize: 32 }}>📄</span>
                          <div><div style={{ fontWeight: 700 }}>{f.name}</div>{f.data !== "[stored]" && <a href={f.data} download={f.name} style={{ color: C.blue, fontSize: 13 }}>Download</a>}</div>
                        </div>
                      }
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Load Board Tab ───────────────────────────────────────────────
// Feature 5: Load Board (simulated + external links)
function FinancialReportsTab({ session, loads=[], rates={}, isOwner, allDrivers=[], goBack, darkMode=false }) {
  const [period, setPeriod] = useState("year");
  const yearWithData = (() => { const y = new Date().getFullYear().toString(); const hasData = loads.some(l=>l.date&&l.date.startsWith(y)); if(hasData) return y; const prev = (new Date().getFullYear()-1).toString(); return prev; })(); const [year, setYear] = useState(yearWithData);
  // Auto-correct year when loads load async (prevents showing wrong year on first render)
  useEffect(() => { const y = new Date().getFullYear().toString(); if (loads.some(l=>l.date&&l.date.startsWith(y))) setYear(y); }, [loads.length]); // eslint-disable-line
  // ── Dark mode palette ──
  const DM = {
    pageBg:  darkMode ? "#070B14" : "#F5F6F8",
    cardBg:  darkMode ? "#0D1523" : "#FFFFFF",
    border:  darkMode ? "rgba(255,255,255,0.07)" : "#E2E2E2",
    text:    darkMode ? "#F0F0F0" : "#1A1A1A",
    textMed: darkMode ? "rgba(255,255,255,0.5)" : "#4B5563",
    inputBg: darkMode ? "#111C2E" : "#FFFFFF",
    debugBg: darkMode ? "#2A2A2A" : "#EEEEEE",
    amberBg: darkMode ? "rgba(232,150,46,0.12)" : "#F5F6F8",
  };
  const [generating, setGenerating] = useState(null);
  const [sbExpenses, setSbExpenses] = useState([]);

  useEffect(() => {
    sbGetExpenses(session.uid).then(d => { if(d?.length>0) setSbExpenses(d); }).catch(()=>{});
    // For owners, also fetch driver business expenses
    if (session.role === "owner") {
      sbGetFleetDriverBusinessExpenses(session.uid).then(d => {
        if(d?.length>0) setSbExpenses(prev => {
          const merged = [...prev];
          d.forEach(e => { if(!merged.find(x=>x.id===e.id)) merged.push(e); });
          return merged;
        });
      }).catch(()=>{});
    }
  }, [session.uid]);

  const allExpenses = (() => {
    const local = getStored(expensesKey(session.uid));
    const merged = [...local];
    sbExpenses.forEach(se => { if(!merged.find(e=>e.id===se.id)) merged.push(se); });
    return merged;
  })();

  const inRange = (d) => {
    if(!d) return false;
    if(period==="year") return d.startsWith(year);
    if(period==="q1") return d>=`${year}-01-01`&&d<=`${year}-03-31`;
    if(period==="q2") return d>=`${year}-04-01`&&d<=`${year}-06-30`;
    if(period==="q3") return d>=`${year}-07-01`&&d<=`${year}-09-30`;
    if(period==="q4") return d>=`${year}-10-01`&&d<=`${year}-12-31`;
    return true;
  };

  const filteredLoads = loads.filter(l => inRange(l.date));
  const filteredExp = allExpenses.filter(e => !e.deleted && inRange(e.date));
  const myLoads = isOwner ? filteredLoads : filteredLoads.filter(l=>l.assignedDriverUid===session.uid||l.addedBy===session.uid||l.user_id===session.uid);

  // ── Owner calculations ──
  const grossRevenue = isOwner ? myLoads.reduce((s,l)=>s+Number(l.earnings||0),0) : 0;
  const companyWaitPay = isOwner ? myLoads.reduce((s,l)=>s+((Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0))/60*(Number(rates.companyWaitRate)||0),0) : 0;
  const driverPay = isOwner ? myLoads.filter(l=> {
    // Include loads where payMyselfAsDriver is explicitly true (owner driving self)
    if(l.payMyselfAsDriver === true){
      const dbp = Number(l.driverBasePay||0);
      const pct = Number(l.driverPct||0);
      const earn = Number(l.earnings||0);
      return dbp > 0 || (pct > 0 && earn > 0);
    }
    // Must be a load driven by someone other than the owner
    const dUid = l.assignedDriverUid || l.addedBy || l.user_id;
    if(!dUid || dUid === session.uid) return false;
    // Must have some driver pay (flat OR percentage-based)
    const dbp = Number(l.driverBasePay||0);
    const pct = Number(l.driverPct||0);
    const earn = Number(l.earnings||0);
    return dbp > 0 || (pct > 0 && earn > 0);
  }).reduce((s,l)=>{
    const wm=(Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0);
    const wDrv=wm/60*(Number(rates.driverWaitRate)||0);
    const dbp = Number(l.driverBasePay||0);
    const pct = Number(l.driverPct||0);
    const earn = Number(l.earnings||0);
    const calcPay = dbp > 0 ? dbp : (pct > 0 && earn > 0 ? earn * pct / 100 : 0);
    return s + calcPay + wDrv;
  },0) : 0;

  // ── Driver calculations ──
  // Route pay (base driver pay per load)
  const driverRoutePay = !isOwner ? myLoads.reduce((s,l)=>s+Number(l.driverBasePay||0),0) : 0;
  // Driver wait pay (driver's share of wait time)
  const driverWaitPay = !isOwner ? myLoads.reduce((s,l)=>s+((Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0))/60*(Number(rates.driverWaitRate)||0),0) : 0;
  // Total driver earned = route + wait
  const driverTotalEarned = driverRoutePay + driverWaitPay;

  // Personal expenses (not business/owner expenses)
  const personalExp = filteredExp.filter(e=>!e.ownerExpense && e.expenseType!=="business" && e.source!=="load");
  const businessExpSubmitted = !isOwner ? filteredExp.filter(e=>e.ownerExpense||e.expenseType==="business") : [];
  const personalExpTotal = personalExp.reduce((s,e)=>s+Number(e.amount||0),0);
  const businessExpTotal = businessExpSubmitted.reduce((s,e)=>s+Number(e.amount||0),0);

  const waitPay = companyWaitPay; // backward compat alias for owner PDF
  const myPay = !isOwner ? driverTotalEarned : grossRevenue - driverPay;
  const totalIncome = isOwner ? grossRevenue + companyWaitPay - driverPay : driverTotalEarned;
  const totalExpenses = isOwner ? filteredExp.reduce((s,e)=>s+Number(e.amount||0),0) : personalExpTotal;
  const netProfit = totalIncome - totalExpenses;
  const periodLabel = period==="year"?`Full Year ${year}`:period==="q1"?`Q1 ${year}`:period==="q2"?`Q2 ${year}`:period==="q3"?`Q3 ${year}`:`Q4 ${year}`;

  // Expense breakdown by category (personal only for drivers, all for owners)
  const expByCategory = {};
  (isOwner ? filteredExp : personalExp).forEach(e=>{
    const cat = e.category||"other";
    if(!expByCategory[cat]) expByCategory[cat]={total:0,items:[]};
    expByCategory[cat].total += Number(e.amount||0);
    expByCategory[cat].items.push(e);
  });

  const generatePDF = async (type) => {
    setGenerating(type);
    try {
      // ── Load jsPDF ─────────────────────────────────────────────────────────
      const loadjsPDF = () => new Promise((resolve, reject) => {
        if (window.jspdf?.jsPDF) { resolve(window.jspdf.jsPDF); return; }
        const existing = document.getElementById('jspdf-script');
        if (existing) { existing.onload = () => resolve(window.jspdf.jsPDF); return; }
        const script = document.createElement("script");
        script.id = 'jspdf-script';
        script.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
        script.onload = () => { setTimeout(() => resolve(window.jspdf?.jsPDF), 100); };
        script.onerror = () => reject(new Error("Failed to load jsPDF"));
        document.head.appendChild(script);
      });
      const jsPDFClass = await loadjsPDF();
      if (!jsPDFClass) throw new Error("jsPDF not available");
      const doc = new jsPDFClass({ orientation:"portrait", unit:"mm", format:"letter" });

      // ── Constants & helpers ────────────────────────────────────────────────
      const W = 215.9, MX = 18, CW = W - MX * 2;
      const NAVY  = [28, 43, 74];
      const AMBER = [232, 150, 46];
      const GRN   = [22, 101, 52];
      const RED   = [185, 28, 28];
      const GREY  = [210, 215, 225];
      let y = 0, _pageNum = 1;
      const ROW_H = 7;

      const setFill  = (rgb) => doc.setFillColor(rgb[0], rgb[1], rgb[2]);
      const setTxt   = (rgb) => doc.setTextColor(rgb[0], rgb[1], rgb[2]);
      const setDraw  = (rgb) => doc.setDrawColor(rgb[0], rgb[1], rgb[2]);
      const tx       = (s, x, yy, opts) => doc.text(String(s), x, yy, opts);

      const fmt$ = (v) =>
        "$" + Number(v || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const fmtN = (v, d = 0) =>
        Number(v || 0).toLocaleString("en-CA", { minimumFractionDigits: d, maximumFractionDigits: d });

      const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      const fmtDate = (d) => MONTHS_SHORT[d.getMonth()] + " " + d.getDate();

      // ── Report title lookup ────────────────────────────────────────────────
      const TITLES = {
        income:         isOwner ? "Income & Expense Statement"             : "Earnings & Expense Statement",
        tax:            isOwner ? "CRA T2125 — Business Income Statement"  : "CRA Tax Summary — Self-Employed",
        payroll:        isOwner ? "Payroll & Contractor Pay Summary"       : "Pay Period Statement",
        ifta:           "IFTA Fuel Tax Report",
        gst_return:     "GST/HST Return — Zero-Rated Trucking",
        driver_paystub: isOwner ? "Driver Earnings Statements"             : "Earnings Statement — Proof of Income",
        monthly_pl:     "Monthly P&L Statement",
        t4a_summary:    isOwner ? "T4A Subcontractor Summary"              : "T4A Income Summary (Box 048)",
        mileage_log:    "CRA Motor Vehicle / Mileage Log",
        cashflow:       "Cash Flow Forecast — 4 Week",
        route_profit:   "Route Profitability Analysis",
      };
      const reportTitle = TITLES[type] || "Financial Report";
      const companyStr = session.companyName || session.fullName || session.name || "TruckPilot";
      const personStr  = session.companyName ? (session.fullName || session.name || "") : "";

      // ── Page header ────────────────────────────────────────────────────────
      const drawHeader = (firstPage) => {
        if (firstPage) {
          setFill(NAVY); doc.rect(0, 0, W, 38, "F");
          setFill(AMBER); doc.rect(0, 38, W, 2, "F");
          doc.setFontSize(14); doc.setFont("helvetica", "bold"); setTxt([255,255,255]);
          tx(companyStr, MX, 13);
          if (personStr) {
            doc.setFontSize(8); doc.setFont("helvetica", "normal"); setTxt([180,205,250]);
            tx(personStr, MX, 21);
          }
          doc.setFontSize(9.5); doc.setFont("helvetica", "normal"); setTxt([210,225,255]);
          tx(reportTitle, MX, personStr ? 30 : 23);
          doc.setFontSize(8); setTxt([160,190,240]);
          tx(periodLabel,                                    W - MX, 13, { align: "right" });
          tx("Generated: " + new Date().toLocaleDateString("en-CA"), W - MX, 21, { align: "right" });
          tx("Role: " + (isOwner ? "Fleet Owner" : "Driver"),        W - MX, 29, { align: "right" });
          y = 50;
        } else {
          setFill(NAVY); doc.rect(0, 0, W, 12, "F");
          setFill(AMBER); doc.rect(0, 12, W, 1, "F");
          doc.setFontSize(7.5); doc.setFont("helvetica", "bold"); setTxt([255,255,255]);
          tx(companyStr + "  —  " + reportTitle, MX, 8);
          doc.setFont("helvetica", "normal"); setTxt([180,205,250]);
          tx(periodLabel + "   Page " + _pageNum, W - MX, 8, { align: "right" });
          y = 20;
        }
        setTxt([30,30,30]);
      };

      // ── Page overflow check ────────────────────────────────────────────────
      const checkPage = (needed = 15) => {
        if (y + needed > 263) {
          _pageNum++;
          doc.addPage();
          drawHeader(false);
        }
      };

      // ── Section band ───────────────────────────────────────────────────────
      const section = (label, sub = "") => {
        checkPage(14);
        setFill(NAVY); doc.rect(MX - 2, y - 3, CW + 4, 9, "F");
        doc.setFontSize(8); doc.setFont("helvetica", "bold"); setTxt([255,255,255]);
        tx(label.toUpperCase(), MX, y + 2);
        if (sub) {
          doc.setFont("helvetica", "normal"); doc.setFontSize(7); setTxt([170,200,245]);
          tx(sub, W - MX, y + 2, { align: "right" });
        }
        y += 11;
        setTxt([30,30,30]);
        _rowIdx = 0;
      };

      // ── Data row ───────────────────────────────────────────────────────────
      let _rowIdx = 0;
      const row = (label, value, opts = {}) => {
        // opts: bold, indent, noteText, valRgb
        checkPage(ROW_H + 3);
        const alt = _rowIdx % 2 === 0;
        if (alt) { doc.setFillColor(246, 248, 252); doc.rect(MX - 2, y - 3.5, CW + 4, ROW_H + 1, "F"); }
        _rowIdx++;
        doc.setFont("helvetica", opts.bold ? "bold" : "normal");
        doc.setFontSize(8.5);
        setTxt([30,30,30]);
        const lx = opts.indent ? MX + 5 : MX;
        tx(String(label), lx, y);
        if (value !== undefined && value !== null) {
          doc.setFont("helvetica", "bold");
          if (opts.valRgb) setTxt(opts.valRgb); else setTxt([30,30,30]);
          tx(String(value), W - MX, y, { align: "right" });
          setTxt([30,30,30]);
        }
        y += ROW_H;
        if (opts.noteText) {
          doc.setFont("helvetica", "italic"); doc.setFontSize(7); setTxt([130,100,20]);
          tx(opts.noteText, lx + 2, y);
          y += 5;
          setTxt([30,30,30]);
        }
        setDraw(GREY);
        if (!alt) doc.line(MX, y, W - MX, y);
      };

      // ── Total bar ──────────────────────────────────────────────────────────
      const totalBar = (label, value, opts = {}) => {
        checkPage(13);
        const clr = opts.green ? GRN : opts.red ? RED : NAVY;
        setFill(clr); doc.rect(MX - 2, y - 3, CW + 4, 11, "F");
        doc.setFont("helvetica", "bold"); doc.setFontSize(opts.large ? 10.5 : 9);
        setTxt([255,255,255]);
        tx(String(label), MX, y + 4);
        tx(String(value), W - MX, y + 4, { align: "right" });
        y += 14;
        setTxt([30,30,30]);
        _rowIdx = 0;
      };

      // ── Divider line ───────────────────────────────────────────────────────
      const divider = (gap = 5) => {
        checkPage(gap + 3);
        setDraw([190,200,220]);
        doc.line(MX, y, W - MX, y);
        y += gap;
      };

      // ── Note / disclaimer box ──────────────────────────────────────────────
      const note = (txt) => {
        checkPage(10);
        doc.setFillColor(255, 251, 235);
        doc.rect(MX - 2, y - 2, CW + 4, 9, "F");
        doc.setFontSize(7); doc.setFont("helvetica", "italic"); setTxt([140, 80, 10]);
        tx(txt, MX, y + 4);
        y += 12;
        setTxt([30,30,30]);
      };

      // ── Column table header ────────────────────────────────────────────────
      const tableHeader = (cols) => {
        // cols: [{label, x, align}]
        checkPage(12);
        doc.setFillColor(45, 65, 105);
        doc.rect(MX - 2, y - 3, CW + 4, 9, "F");
        doc.setFontSize(7.5); doc.setFont("helvetica", "bold"); setTxt([255,255,255]);
        cols.forEach(c => tx(c.label, c.x, y + 2, { align: c.align || "left" }));
        y += 11;
        setTxt([30,30,30]);
        _rowIdx = 0;
      };

      // ── Table data row ─────────────────────────────────────────────────────
      const tableRow = (cols, opts = {}) => {
        checkPage(9);
        const alt = _rowIdx % 2 === 0;
        doc.setFillColor(alt ? 255 : 247, alt ? 255 : 249, alt ? 255 : 253);
        doc.rect(MX - 2, y - 3, CW + 4, 7.5, "F");
        _rowIdx++;
        doc.setFont("helvetica", opts.bold ? "bold" : "normal"); doc.setFontSize(8);
        setTxt([30,30,30]);
        cols.forEach(c => {
          if (c.rgb) setTxt(c.rgb);
          doc.setFont("helvetica", (c.bold || opts.bold) ? "bold" : "normal");
          tx(String(c.label), c.x, y, { align: c.align || "left" });
          setTxt([30,30,30]);
        });
        y += 7.5;
      };

      // ── Driver-as-corporation detection ───────────────────────────────────
      // A driver is a corp-owner when they log loads with l.earnings (no driverBasePay)
      const driverOwnLoads = !isOwner
        ? myLoads.filter(l => Number(l.earnings || 0) > 0 && !(Number(l.driverBasePay || 0) > 0))
        : [];
      const driverIsCorpOwner = driverOwnLoads.length > 0;
      const driverCorpGross   = driverIsCorpOwner
        ? myLoads.reduce((s, l) => s + Number(l.earnings || 0), 0) + driverWaitPay
        : 0;
      const effectiveDrvIncome = driverIsCorpOwner ? driverCorpGross : driverTotalEarned;
      const driverAllExp       = driverIsCorpOwner ? filteredExp : personalExp;
      const driverAllExpTotal  = driverAllExp.reduce((s, e) => s + Number(e.amount || 0), 0);
      const GST = 0.05;

      // ── CRA expense line references ────────────────────────────────────────
      const CRA_LINE = {
        fuel: "9220", maintenance: "9281", repairs: "9281", insurance: "9221",
        office: "9270", phone: "8220", meals: "8520", tools: "9224",
        lodging: "9200", other: "9270",
      };
      const craLbl = (cat) => {
        const l = cat.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
        return l + " (Line " + (CRA_LINE[cat] || "9270") + ")";
      };

      // ── GST helper: ITC calc from expense list ─────────────────────────────
      const calcITC = (expList) => {
        const fuel   = expList.filter(e => e.category === "fuel").reduce((s,e)=>s+Number(e.amount||0),0);
        const repair = expList.filter(e => e.category==="repairs"||e.category==="maintenance").reduce((s,e)=>s+Number(e.amount||0),0);
        const other  = Math.max(0, expList.reduce((s,e)=>s+Number(e.amount||0),0) - fuel - repair);
        return { fuel, repair, other,
          itcFuel:  parseFloat((fuel  * GST).toFixed(2)),
          itcRep:   parseFloat((repair* GST).toFixed(2)),
          itcOther: parseFloat((other * GST).toFixed(2)),
          total:    parseFloat(((fuel+repair+other)*GST).toFixed(2)) };
      };

      // ══════════════════════════════════════════════════════════════════════
      // DRAW PAGE 1 HEADER
      // ══════════════════════════════════════════════════════════════════════
      drawHeader(true);

      // ══════════════════════════════════════════════════════════════════════
      // REPORT 1 — INCOME & EXPENSE STATEMENT
      // ══════════════════════════════════════════════════════════════════════
      if (type === "income") {

        if (isOwner) {
          // ── OWNER ──────────────────────────────────────────────────────────
          section("Revenue");
          row("Gross Load Revenue", fmt$(grossRevenue));
          row("Company Wait Pay Billed to Clients", fmt$(companyWaitPay));
          row("Total Gross Revenue", fmt$(grossRevenue + companyWaitPay), { bold: true });
          divider(3);
          row("Less: Driver / Subcontractor Pay (T4A)",
              "(" + fmt$(driverPay) + ")", { bold: true, valRgb: RED,
              noteText: "Deductible expense — T4A Box 048 required when paid >$500/year per contractor" });
          totalBar("NET OWNER INCOME  (" + periodLabel + ")", fmt$(totalIncome), { large: true });

          section("Operating Expenses");
          if (Object.keys(expByCategory).length === 0) {
            row("No expenses recorded for this period", "—");
          } else {
            Object.entries(expByCategory).forEach(([cat, data]) => {
              row(craLbl(cat), fmt$(data.total));
            });
          }
          totalBar("TOTAL EXPENSES", fmt$(totalExpenses));
          divider(2);
          totalBar("NET PROFIT", fmt$(netProfit), {
            green: netProfit >= 0, red: netProfit < 0, large: true });

          // GST section — zero-rated trucking
          const itc = calcITC(filteredExp);
          section("GST / HST Summary — Zero-Rated Trucking Supply", "ETA Schedule VI, Part VII");
          note("Trucking is zero-rated. You do NOT charge GST on loads. You DO claim ITCs (refunds) on fuel, repairs and other business expenses.");
          row("Line 101 — Total Revenue (zero-rated)", fmt$(grossRevenue + companyWaitPay));
          row("Line 105 — GST Collected on Revenue", "$0.00",
              { noteText: "Zero-rated supply — no GST charged to customers" });
          row("Line 106 — Fuel & Oil ITC  (" + fmt$(itc.fuel) + " × 5%)",         fmt$(itc.itcFuel),  { indent: true });
          row("Line 106 — Repairs & Maintenance ITC  (" + fmt$(itc.repair) + " × 5%)", fmt$(itc.itcRep), { indent: true });
          row("Line 106 — Other Eligible ITC  (" + fmt$(itc.other) + " × 5%)",    fmt$(itc.itcOther), { indent: true });
          totalBar("LINE 109 — REFUND FROM CRA", fmt$(itc.total), { green: true });
          note("File your GST/HST return to claim this refund. Consult your accountant for the filing due date.");

          // Load detail
          const ownerLoads = myLoads.filter(l => l.date && inRange(l.date)).sort((a, b) => a.date > b.date ? 1 : -1);
          if (ownerLoads.length > 0) {
            section("Load Detail — " + ownerLoads.length + " Loads");
            ownerLoads.forEach(l => {
              const earn = Number(l.earnings || 0);
              const wm   = (Number(l.loadWaitMins)||0) + (Number(l.offloadWaitMins)||0);
              const wait = parseFloat((wm / 60 * (Number(rates.companyWaitRate)||0)).toFixed(2));
              row((l.date||"—") + "  " + (l.location||"Load").substring(0, 52) + (l.completed?" ✓":" (active)"),
                  fmt$(earn + wait));
            });
          }

        } else {
          // ── DRIVER ─────────────────────────────────────────────────────────
          if (driverIsCorpOwner) {
            note("Reporting as Independent Operator / Corporation. Revenue is your gross freight billing.");
          }

          section("Earnings");
          if (driverIsCorpOwner) {
            row("Gross Load Revenue (Corporate)", fmt$(myLoads.reduce((s,l)=>s+Number(l.earnings||0),0)));
            row("Wait Time Revenue", fmt$(driverWaitPay));
          } else {
            row("Route Pay", fmt$(driverRoutePay));
            row("Wait Time Pay", fmt$(driverWaitPay));
          }
          totalBar("TOTAL INCOME  (" + periodLabel + ")", fmt$(effectiveDrvIncome), { large: true });

          section(driverIsCorpOwner ? "Business Expenses" : "Personal Expenses");
          const drvByCat = {};
          driverAllExp.forEach(e => {
            const c = e.category || "other";
            drvByCat[c] = (drvByCat[c] || 0) + Number(e.amount || 0);
          });
          if (Object.keys(drvByCat).length === 0) {
            row("No expenses recorded for this period", "—");
          } else {
            Object.entries(drvByCat).forEach(([cat, amt]) => {
              row(driverIsCorpOwner ? craLbl(cat) : cat.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase()), fmt$(amt));
            });
          }
          totalBar("TOTAL EXPENSES", fmt$(driverAllExpTotal));
          divider(2);
          const drvNet = effectiveDrvIncome - driverAllExpTotal;
          totalBar("NET INCOME", fmt$(drvNet), { green: drvNet >= 0, red: drvNet < 0, large: true });

          // Expenses submitted to owner (subcontractors only)
          if (!driverIsCorpOwner && businessExpSubmitted.length > 0) {
            section("Business Expenses Submitted to Owner (Info Only)");
            note("These expenses go to your fleet owner's account. They do NOT reduce your pay.");
            businessExpSubmitted.forEach(e => {
              const lbl = (e.category||"expense").replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase());
              row((e.date||"—") + "  " + (e.merchant||lbl), fmt$(e.amount));
            });
            totalBar("TOTAL SUBMITTED TO OWNER", fmt$(businessExpTotal));
          }

          // GST for drivers (same as owner)
          const drvItc = calcITC(driverAllExp);
          section("GST / HST Summary" + (driverIsCorpOwner ? " — Zero-Rated Trucking" : " — ITC Claims"),
                  "ETA Schedule VI, Part VII");
          if (driverIsCorpOwner) {
            note("Your corporation's trucking supply is zero-rated. No GST on loads. Claim ITCs on business expenses.");
            row("Line 101 — Total Revenue (zero-rated)", fmt$(driverCorpGross));
            row("Line 105 — GST Collected on Revenue", "$0.00",
                { noteText: "Zero-rated supply — $0 charged to clients" });
          } else {
            note("Subcontract trucking services are zero-rated. No GST on your pay. Claim ITCs on eligible expenses.");
            row("Line 105 — GST Collected", "$0.00", { noteText: "Zero-rated subcontract services" });
          }
          row("Line 106 — Fuel & Oil ITC  (" + fmt$(drvItc.fuel) + " × 5%)",         fmt$(drvItc.itcFuel),  { indent: true });
          row("Line 106 — Repairs & Maint. ITC  (" + fmt$(drvItc.repair) + " × 5%)", fmt$(drvItc.itcRep),  { indent: true });
          row("Line 106 — Other Eligible ITC  (" + fmt$(drvItc.other) + " × 5%)",    fmt$(drvItc.itcOther), { indent: true });
          totalBar("LINE 109 — REFUND FROM CRA", fmt$(drvItc.total), { green: true });
          note("File your GST/HST return to claim this refund from CRA.");

          // Load detail
          const drvLoads = myLoads.filter(l=>l.date&&inRange(l.date)).sort((a,b)=>a.date>b.date?1:-1);
          if (drvLoads.length > 0) {
            section("Load Detail — " + drvLoads.length + " Loads");
            drvLoads.forEach(l => {
              const earn = driverIsCorpOwner ? Number(l.earnings||0) : Number(l.driverBasePay||0);
              const wm   = (Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0);
              const wait = parseFloat((wm/60*(Number(rates.driverWaitRate)||0)).toFixed(2));
              row((l.date||"—")+"  "+(l.location||"Load").substring(0,52)+(l.completed?" ✓":" (active)"),
                  fmt$(earn+wait));
            });
          }
        }
      }

      // ══════════════════════════════════════════════════════════════════════
      // REPORT 2 — CRA T2125 TAX REPORT
      // ══════════════════════════════════════════════════════════════════════
      else if (type === "tax") {

        if (isOwner) {
          const t2125Gross  = grossRevenue + companyWaitPay;
          const t2125DrvPay = driverPay;
          const t2125Exp    = totalExpenses;
          const t2125TotExp = t2125Exp + t2125DrvPay;
          const t2125Net    = t2125Gross - t2125TotExp;

          note("CRA Form T2125 — Statement of Business Activities. For use with your T1 personal income tax return. Consult a CRA-registered accountant before filing.");

          section("Part 1 — Gross Revenue", "Lines 8000 – 8299");
          row("Gross Freight Revenue (Line 8000)", fmt$(grossRevenue), { bold: true });
          row("Wait Time Billed to Clients", fmt$(companyWaitPay));
          row("Returns and Allowances (Line 8290)", "$0.00");
          totalBar("Total Gross Revenue (Line 8299)", fmt$(t2125Gross));

          section("Part 2 — Cost of Goods Sold", "Lines 8300 – 8519");
          row("Opening Inventory (Line 8300)", "$0.00");
          row("Purchases during the period (Line 8320)", "$0.00");
          row("Closing Inventory (Line 8500)", "$0.00");
          totalBar("Cost of Goods Sold (Line 8519)", "$0.00");

          section("Part 3 — Gross Profit");
          row("Gross Profit (Line 8299 − Line 8519)", fmt$(t2125Gross), { bold: true });

          section("Part 4 — Business Expenses", "Lines 9000 – 9368");
          if (Object.keys(expByCategory).length > 0) {
            Object.entries(expByCategory).forEach(([cat, data]) => {
              row(craLbl(cat), fmt$(data.total));
            });
          }
          if (t2125DrvPay > 0) {
            row("Subcontractor / Driver Pay — T4A Box 048 (Line 9270)", fmt$(t2125DrvPay), {
              bold: true,
              noteText: "T4A slips required for every contractor paid >$500 in calendar year. Due end of February."
            });
          }
          totalBar("Total Expenses (Line 9368)", fmt$(t2125TotExp));

          section("Part 5 — Net Income");
          row("Net Income before adjustments (Line 9369)", fmt$(t2125Net));
          row("Business-use-of-home (Line 9270)", "$0.00",
              { noteText: "Enter if applicable — consult your accountant" });
          divider(3);
          totalBar("NET INCOME (Line 9946)", fmt$(t2125Net),
              { green: t2125Net >= 0, red: t2125Net < 0, large: true });

        } else {
          // ── DRIVER: CRA Tax Summary ─────────────────────────────────────────
          const drvGross  = effectiveDrvIncome;
          const drvExpTot = driverAllExpTotal;
          const drvNet    = drvGross - drvExpTot;

          if (driverIsCorpOwner) {
            note("Filing as a corporate owner-operator. Revenue is your corporation's gross freight billing. Consult a CRA accountant for T2 corporate filing.");
          } else {
            note("Filing as a self-employed subcontractor. If you received a T4A (Box 048) from your fleet owner, that is your gross business income (T1 Line 13500).");
          }

          section(driverIsCorpOwner ? "Part 1 — Gross Revenue (Corporate)" : "Part 1 — Self-Employment Income",
                  driverIsCorpOwner ? "Lines 8000–8299" : "T1 Line 13500");
          if (driverIsCorpOwner) {
            row("Gross Load Revenue (Line 8000)", fmt$(myLoads.reduce((s,l)=>s+Number(l.earnings||0),0)), { bold: true });
            row("Wait Time Revenue", fmt$(driverWaitPay));
            totalBar("Total Gross Revenue (Line 8299)", fmt$(driverCorpGross));
          } else {
            row("Route Pay (from fleet owner)", fmt$(driverRoutePay));
            row("Wait Time Pay", fmt$(driverWaitPay));
            totalBar("Total Self-Employment Income", fmt$(driverTotalEarned));
          }

          section("Part 2 — Business Expenses");
          const drvByCat2 = {};
          driverAllExp.forEach(e => {
            const c = e.category || "other";
            drvByCat2[c] = (drvByCat2[c] || 0) + Number(e.amount || 0);
          });
          if (Object.keys(drvByCat2).length === 0) {
            row("No deductible expenses recorded for this period", "—");
          } else {
            Object.entries(drvByCat2).forEach(([cat, amt]) => row(craLbl(cat), fmt$(amt)));
          }
          totalBar("Total Deductible Expenses", fmt$(drvExpTot));

          section("Part 3 — Net Income");
          row("Gross Income", fmt$(drvGross));
          row("Less: Total Expenses", "(" + fmt$(drvExpTot) + ")", { valRgb: RED });
          divider(3);
          totalBar("NET INCOME (Line 9946)", fmt$(drvNet),
              { green: drvNet >= 0, red: drvNet < 0, large: true });

          // GST for drivers
          const drvItc2 = calcITC(driverAllExp);
          section("GST / HST Summary", "Zero-Rated Trucking — ETA Schedule VI");
          note("Trucking is zero-rated. No GST on loads. Claim ITCs on fuel, repairs and other eligible expenses.");
          row("Line 101 — Total Revenue (zero-rated)", fmt$(drvGross));
          row("Line 105 — GST Collected on Sales", "$0.00",
              { noteText: "Zero-rated — $0 charged to clients" });
          row("Line 106 — Fuel ITC  (" + fmt$(drvItc2.fuel) + " × 5%)",         fmt$(drvItc2.itcFuel),  { indent: true });
          row("Line 106 — Repairs ITC  (" + fmt$(drvItc2.repair) + " × 5%)",    fmt$(drvItc2.itcRep),   { indent: true });
          row("Line 106 — Other ITC  (" + fmt$(drvItc2.other) + " × 5%)",       fmt$(drvItc2.itcOther), { indent: true });
          totalBar("LINE 109 — REFUND FROM CRA", fmt$(drvItc2.total), { green: true });
        }
      }

      // ══════════════════════════════════════════════════════════════════════
      // REPORT 3 — PAYROLL / PAY PERIOD SUMMARY
      // ══════════════════════════════════════════════════════════════════════
      else if (type === "payroll") {

        if (isOwner) {
          // Build driver stats — includes payMyselfAsDriver (owner driving self)
          const drvStats = {};
          const ownerDisplayName = session.fullName || session.name || "Owner (Self)";
          filteredLoads.forEach(l => {
            const isSelfPay = l.payMyselfAsDriver === true;
            const dUid = l.assignedDriverUid || l.addedBy || l.user_id;
            // Skip loads with no driver pay that aren't self-pay
            if (!isSelfPay && (!dUid || dUid === session.uid)) return;
            const dbp  = Number(l.driverBasePay || 0);
            const pct  = Number(l.driverPct || 0);
            const earn = Number(l.earnings || 0);
            const cp   = dbp > 0 ? dbp : (pct > 0 && earn > 0 ? earn * pct / 100 : 0);
            if (cp <= 0) return;
            const wp   = ((Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0))/60*(Number(rates.driverWaitRate)||0);
            const name = isSelfPay
              ? ownerDisplayName + " (Owner Draw)"
              : (l.driverFullName || l.assignedDriverName || "Unknown Driver");
            if (!drvStats[name]) drvStats[name] = { loads: 0, routePay: 0, waitPay: 0, done: 0, ytd: 0, isSelfPay };
            drvStats[name].loads++;
            drvStats[name].routePay += cp;
            drvStats[name].waitPay  += wp;
            if (l.completed) drvStats[name].done++;
          });
          // YTD — also include payMyselfAsDriver
          loads.filter(l => l.date && l.date.startsWith(year)).forEach(l => {
            const isSelfPay = l.payMyselfAsDriver === true;
            const dUid = l.assignedDriverUid||l.addedBy||l.user_id;
            if (!isSelfPay && (!dUid||dUid===session.uid)) return;
            const name = isSelfPay
              ? ownerDisplayName + " (Owner Draw)"
              : (l.driverFullName||l.assignedDriverName||"Unknown Driver");
            if (!drvStats[name]) return;
            const dbp=Number(l.driverBasePay||0),pct=Number(l.driverPct||0),earn=Number(l.earnings||0);
            const cp=dbp>0?dbp:(pct>0&&earn>0?earn*pct/100:0);
            const wp=((Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0))/60*(Number(rates.driverWaitRate)||0);
            drvStats[name].ytd += cp + wp;
          });

          if (Object.keys(drvStats).length === 0) {
            section("No Payroll Data");
            note("No driver pay data found for " + periodLabel + ". Assign loads to drivers with a driver pay amount.");
          } else {
            Object.entries(drvStats).forEach(([name, d]) => {
              const total = d.routePay + d.waitPay;
              section("Driver: " + name);
              row("Period", periodLabel);
              row("Loads Assigned", String(d.loads));
              row("Loads Completed", d.done + " / " + d.loads);
              row("Route Pay", fmt$(d.routePay));
              row("Wait Time Pay", fmt$(d.waitPay));
              row("YTD Total Pay (" + year + ")", fmt$(d.ytd), { bold: true });
              totalBar("TOTAL PERIOD PAY — " + name, fmt$(total), { large: true });
              if (d.ytd >= 500) {
                note("T4A REQUIRED — YTD pay " + fmt$(d.ytd) + " exceeds $500 threshold. Issue T4A Box 048 by last day of February.");
              }
            });
            divider(4);
            const grand = Object.values(drvStats).reduce((s, d) => s + d.routePay + d.waitPay, 0);
            totalBar("GRAND TOTAL PAYROLL — " + periodLabel, fmt$(grand), { large: true });
          }

        } else {
          // ── DRIVER: Pay Period Statement ─────────────────────────────────────
          const pp = getPayPeriod(rates);
          section("Pay Period Summary — " + periodLabel);
          row("Period", periodLabel);
          row("Total Loads", String(myLoads.length));
          row("Completed Loads", String(myLoads.filter(l=>l.completed).length));
          if (driverIsCorpOwner) {
            row("Gross Load Revenue", fmt$(myLoads.reduce((s,l)=>s+Number(l.earnings||0),0)));
            row("Wait Time Revenue", fmt$(driverWaitPay));
          } else {
            row("Route Pay", fmt$(driverRoutePay));
            row("Wait Time Pay", fmt$(driverWaitPay));
          }
          // YTD
          const ytdDrv = loads.filter(l=>l.date&&l.date.startsWith(year)&&(l.assignedDriverUid===session.uid||l.addedBy===session.uid||l.user_id===session.uid));
          const ytdRoute = driverIsCorpOwner
            ? ytdDrv.reduce((s,l)=>s+Number(l.earnings||0),0)
            : ytdDrv.reduce((s,l)=>s+Number(l.driverBasePay||0),0);
          const ytdWait = ytdDrv.reduce((s,l)=>s+((Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0))/60*(Number(rates.driverWaitRate)||0),0);
          row("YTD Total Earned (" + year + ")", fmt$(ytdRoute + ytdWait), { bold: true });
          totalBar("TOTAL THIS PERIOD", fmt$(effectiveDrvIncome), { large: true });

          if (pp && pp.nextPayLabel) {
            section("Pay Schedule");
            row("Pay Frequency", pp.freqLabel || "—");
            row("Next Pay Date", pp.nextPayLabel || "—");
            row("Pay Period Cutoff", pp.cutoffLabel || "—");
            row("Expected Amount", fmt$(effectiveDrvIncome));
          }

          // Load detail
          section("Load Detail — " + periodLabel);
          const ppLoads = myLoads.filter(l=>l.date&&inRange(l.date)).sort((a,b)=>a.date>b.date?1:-1);
          if (ppLoads.length === 0) {
            row("No loads found for this period", "—");
          } else {
            ppLoads.forEach(l => {
              const earn = driverIsCorpOwner ? Number(l.earnings||0) : Number(l.driverBasePay||0);
              const wm   = (Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0);
              const wait = parseFloat((wm/60*(Number(rates.driverWaitRate)||0)).toFixed(2));
              row((l.date||"—")+"  "+(l.location||"Load").substring(0,48)+(l.completed?" ✓":" (active)"), fmt$(earn+wait));
            });
          }
        }
      }

      // ══════════════════════════════════════════════════════════════════════
      // REPORT 4 — IFTA FUEL TAX
      // ══════════════════════════════════════════════════════════════════════
      else if (type === "ifta") {
        note("IFTA — International Fuel Tax Agreement. Report km driven and fuel purchased per jurisdiction each quarter. File with your provincial carrier authority.");

        const iftaStored = getStored("tp-ifta-" + session.uid);
        const expFuel = filteredExp.filter(e=>e.category==="fuel"&&(Number(e.litres||0)>0||Number(e.fuelLitres||0)>0)).map(e=>({
          date: e.date, jurisdiction: e.jurisdiction||"AB", km: 0,
          fuelLitres: Number(e.litres||e.fuelLitres||0),
          quarter: (() => { const d=new Date(e.date); return "Q"+(Math.floor(d.getMonth()/3)+1)+"-"+d.getFullYear(); })()
        }));
        const iftaData = [...iftaStored, ...expFuel].filter(e => inRange(e.date));
        const byJur = {};
        iftaData.forEach(e => {
          if (!byJur[e.jurisdiction]) byJur[e.jurisdiction] = { km: 0, fuel: 0 };
          byJur[e.jurisdiction].km   += Number(e.km || 0);
          byJur[e.jurisdiction].fuel += Number(e.fuelLitres || 0);
        });
        const totalKm   = iftaData.reduce((s,e)=>s+Number(e.km||0),0);
        const totalFuel = iftaData.reduce((s,e)=>s+Number(e.fuelLitres||0),0);

        section("Jurisdiction Summary — " + periodLabel);
        if (Object.keys(byJur).length === 0) {
          row("No IFTA data for this period", "—");
          note("Log fuel purchases with litres in the Expenses screen to populate this report.");
        } else {
          tableHeader([
            { label: "Jurisdiction", x: MX },
            { label: "Km Driven",    x: MX + 72, align: "right" },
            { label: "Fuel Purchased", x: MX + 112, align: "right" },
            { label: "Allocated Fuel", x: W - MX, align: "right" },
          ]);
          Object.entries(byJur).sort((a,b)=>a[0].localeCompare(b[0])).forEach(([jur, d]) => {
            const alloc = (totalFuel>0&&totalKm>0) ? (d.km/totalKm)*totalFuel : d.fuel;
            tableRow([
              { label: jur,                       x: MX },
              { label: fmtN(d.km) + " km",        x: MX + 72,  align: "right" },
              { label: d.fuel.toFixed(1) + " L",  x: MX + 112, align: "right" },
              { label: alloc.toFixed(1) + " L",   x: W - MX,   align: "right" },
            ]);
          });
          divider(2);
          totalBar(
            "TOTALS: " + fmtN(totalKm) + " km  |  " + totalFuel.toFixed(1) + " L",
            "Avg: " + (totalFuel > 0 ? (totalKm/totalFuel).toFixed(2) : "—") + " km/L"
          );
        }

        // Fuel ITC
        const fuelAmt = filteredExp.filter(e=>e.category==="fuel").reduce((s,e)=>s+Number(e.amount||0),0);
        section("Fuel Expense & ITC");
        row("Total Fuel Cost (CAD)", fmt$(fuelAmt));
        row("ITC Claim on Fuel (5%)", fmt$(parseFloat((fuelAmt*GST).toFixed(2))), { bold: true });
        note("Claim fuel ITCs on your GST/HST return. Zero-rated trucking means $0 GST on revenue, but you reclaim 5% paid on fuel purchases.");
      }

      // ══════════════════════════════════════════════════════════════════════
      // REPORT 5 — GST/HST QUARTERLY RETURN
      // ══════════════════════════════════════════════════════════════════════
      else if (type === "gst_return") {
        const revBase = isOwner ? (grossRevenue + companyWaitPay) : effectiveDrvIncome;
        const gstExp  = isOwner ? filteredExp : driverAllExp;
        const itc     = calcITC(gstExp);
        // Zero-rated trucking — Line 109 is ALWAYS a refund (no GST collected)
        const netGst  = parseFloat((0 - itc.total).toFixed(2));

        note("Canadian trucking is zero-rated (ETA Schedule VI, Part VII). You do NOT charge GST on loads. You DO claim ITCs on fuel, repairs and other business expenses. Line 109 = refund.");

        section("Part A — Identification & Revenue");
        row("Reporting Period", periodLabel);
        row("Business / Registrant Name", session.companyName || session.fullName || session.name || "—");
        row("Role", isOwner ? "Fleet Owner / Operator" : (driverIsCorpOwner ? "Owner-Operator (Corporation)" : "Driver / Subcontractor"));
        divider(3);
        row("Line 101 — Total Revenue (zero-rated trucking supply)", fmt$(revBase), {
          bold: true,
          noteText: "ETA Schedule VI, Part VII — GST rate on these supplies = 0%"
        });

        section("Part B — GST / HST Collected");
        row("Line 105 — GST / HST Collected on Taxable Sales", "$0.00", { bold: true });
        note("$0.00 — Trucking services are zero-rated. You do NOT collect GST from customers.");

        section("Part C — Input Tax Credits (ITCs)");
        row("Fuel & Oil  (" + fmt$(itc.fuel) + " × 5%)",          fmt$(itc.itcFuel),  { indent: true });
        row("Repairs & Maintenance  (" + fmt$(itc.repair) + " × 5%)", fmt$(itc.itcRep),  { indent: true });
        row("Other Eligible Expenses  (" + fmt$(itc.other) + " × 5%)", fmt$(itc.itcOther), { indent: true });
        totalBar("Line 106 — Total ITCs Claimed", fmt$(itc.total));

        section("Part D — Net Tax (Line 109)");
        row("Line 105 — GST Collected", "$0.00");
        row("Line 106 — ITCs Claimed (refundable)", "(" + fmt$(itc.total) + ")", { valRgb: GRN });
        divider(3);
        checkPage(16);
        setFill(GRN); doc.rect(MX - 2, y - 3, CW + 4, 13, "F");
        doc.setFont("helvetica", "bold"); doc.setFontSize(11); setTxt([255,255,255]);
        tx("LINE 109 — REFUND FROM CRA", MX, y + 5);
        tx(fmt$(itc.total), W - MX, y + 5, { align: "right" });
        y += 16; setTxt([30,30,30]);
        note("File your GST/HST return to receive this refund. CRA will deposit to your registered account.");

        // Expense detail for audit
        section("Expense Detail — Audit Reference");
        tableHeader([
          { label: "Date",     x: MX },
          { label: "Category / Description", x: MX + 22 },
          { label: "Amount",   x: MX + 122, align: "right" },
          { label: "ITC (5%)", x: W - MX,   align: "right" },
        ]);
        gstExp.slice(0, 40).forEach(e => {
          const cat = (e.category||"other").replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase());
          const desc = e.merchant || e.description || "";
          tableRow([
            { label: e.date || "—",                               x: MX },
            { label: cat + (desc ? " — " + desc.substring(0,26) : ""), x: MX + 22 },
            { label: fmt$(e.amount),                              x: MX + 122, align: "right" },
            { label: fmt$(Number(e.amount||0) * GST),             x: W - MX,   align: "right" },
          ]);
        });
        if (gstExp.length > 40) note("+ " + (gstExp.length - 40) + " more expenses — see full Income & Expense report.");
      }

      // ══════════════════════════════════════════════════════════════════════
      // REPORT 6 — DRIVER PAY STUB / EARNINGS STATEMENT
      // ══════════════════════════════════════════════════════════════════════
      else if (type === "driver_paystub") {

        if (!isOwner) {
          // ── DRIVER: own earnings statement ───────────────────────────────────
          const pLoads = myLoads.filter(l=>l.date&&inRange(l.date));
          const pRoute = driverIsCorpOwner
            ? pLoads.reduce((s,l)=>s+Number(l.earnings||0),0)
            : pLoads.reduce((s,l)=>s+Number(l.driverBasePay||0),0);
          const pWait  = pLoads.reduce((s,l)=>s+((Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0))/60*(Number(rates.driverWaitRate)||0),0);
          const pTotal = pRoute + pWait;
          const ytdL   = loads.filter(l=>l.date&&l.date.startsWith(year)&&(l.assignedDriverUid===session.uid||l.addedBy===session.uid||l.user_id===session.uid));
          const ytdR   = driverIsCorpOwner ? ytdL.reduce((s,l)=>s+Number(l.earnings||0),0) : ytdL.reduce((s,l)=>s+Number(l.driverBasePay||0),0);
          const ytdW   = ytdL.reduce((s,l)=>s+((Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0))/60*(Number(rates.driverWaitRate)||0),0);
          const ytdT   = ytdR + ytdW;
          const ytdDone = ytdL.filter(l=>l.completed).length;

          section("Current Period — " + periodLabel);
          row("Loads Completed", pLoads.filter(l=>l.completed).length + " / " + pLoads.length + " total");
          if (driverIsCorpOwner) {
            row("Gross Load Revenue", fmt$(pLoads.reduce((s,l)=>s+Number(l.earnings||0),0)));
          } else {
            row("Route Pay", fmt$(pRoute));
          }
          row("Wait Time Pay", fmt$(pWait));
          totalBar("PERIOD TOTAL", fmt$(pTotal), { large: true });

          section("Year-to-Date — Jan 1 to " + new Date().toLocaleDateString("en-CA", { month: "short", day: "numeric" }) + ", " + year);
          row("Loads Completed YTD", ytdDone + " / " + ytdL.length + " total");
          row("YTD Route Pay", fmt$(ytdR));
          row("YTD Wait Time Pay", fmt$(ytdW));
          row("Average per Completed Load", fmt$(ytdDone > 0 ? ytdT / ytdDone : 0));
          totalBar("YTD TOTAL EARNED", fmt$(ytdT), { large: true });

          note("This statement is issued by TruckPilot (truckpilot.ca). Suitable for income verification for loan applications, housing rentals and immigration files. Figures are as of the date generated.");

          section("Load Detail — " + periodLabel);
          pLoads.slice().sort((a,b)=>a.date>b.date?1:-1).forEach(l => {
            const earn = driverIsCorpOwner ? Number(l.earnings||0) : Number(l.driverBasePay||0);
            const wm   = (Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0);
            const wait = parseFloat((wm/60*(Number(rates.driverWaitRate)||0)).toFixed(2));
            row((l.date||"—")+"  "+(l.location||"Load").substring(0,50)+(l.completed?" ✓":" (active)"), fmt$(earn+wait));
          });

        } else {
          // ── OWNER: one pay stub per driver ─────────────────────────────────
          const dMap = {};
          filteredLoads.forEach(l => {
            const dUid = l.assignedDriverUid||l.addedBy||l.user_id;
            if (!dUid||dUid===session.uid) return;
            const name = l.driverFullName||l.assignedDriverName||"Unknown Driver";
            if (!dMap[name]) dMap[name] = { loads: [], routePay: 0, waitPay: 0, done: 0 };
            const dbp=Number(l.driverBasePay||0),pct=Number(l.driverPct||0),earn=Number(l.earnings||0);
            const cp=dbp>0?dbp:(pct>0&&earn>0?earn*pct/100:0);
            const wp=parseFloat((((Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0))/60*(Number(rates.driverWaitRate)||0)).toFixed(2));
            dMap[name].loads.push(l);
            dMap[name].routePay += cp;
            dMap[name].waitPay  += wp;
            if (l.completed) dMap[name].done++;
          });

          if (Object.keys(dMap).length === 0) {
            section("No Driver Data");
            note("No drivers with pay data found for " + periodLabel + ".");
          } else {
            Object.entries(dMap).forEach(([dName, dData]) => {
              const dTotal  = dData.routePay + dData.waitPay;
              const avgLoad = dData.done > 0 ? dTotal / dData.done : 0;
              section("Earnings Statement — " + dName);
              row("Employer / Fleet Owner", companyStr);
              row("Period", periodLabel);
              row("Loads Assigned", String(dData.loads.length));
              row("Loads Completed", dData.done + " / " + dData.loads.length);
              row("Route Pay", fmt$(dData.routePay));
              row("Wait Time Pay", fmt$(dData.waitPay));
              row("Average per Completed Load", fmt$(avgLoad));
              totalBar("TOTAL EARNINGS — " + dName, fmt$(dTotal), { large: true });
              note("Issued by " + companyStr + " via TruckPilot (truckpilot.ca). Suitable for income verification.");

              section("Load Detail — " + dName);
              dData.loads.slice().sort((a,b)=>a.date>b.date?1:-1).forEach(l => {
                const dbp=Number(l.driverBasePay||0),pct=Number(l.driverPct||0),earn=Number(l.earnings||0);
                const cp=dbp>0?dbp:(pct>0&&earn>0?earn*pct/100:0);
                const wp=parseFloat((((Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0))/60*(Number(rates.driverWaitRate)||0)).toFixed(2));
                row((l.date||"—")+"  "+(l.location||"Load").substring(0,50)+(l.completed?" ✓":" (active)"), fmt$(cp+wp));
              });
            });
          }
        }
      }

      // ══════════════════════════════════════════════════════════════════════
      // REPORT 7 — MONTHLY P&L
      // ══════════════════════════════════════════════════════════════════════
      else if (type === "monthly_pl") {
        const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
        const monthlyData = MONTH_NAMES.map((mName, mi) => {
          const mStr = year + "-" + String(mi + 1).padStart(2, "0");
          const mL   = isOwner
            ? loads.filter(l=>l.date&&l.date.startsWith(mStr))
            : loads.filter(l=>l.date&&l.date.startsWith(mStr)&&(l.assignedDriverUid===session.uid||l.addedBy===session.uid||l.user_id===session.uid));
          const mE = allExpenses.filter(e=>!e.deleted&&e.date&&e.date.startsWith(mStr));
          let rev, exp;
          if (isOwner) {
            const gr = mL.reduce((s,l)=>s+Number(l.earnings||0),0);
            const wp = mL.reduce((s,l)=>s+((Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0))/60*(Number(rates.companyWaitRate)||0),0);
            const dp = mL.reduce((s,l)=>{
              const dUid=l.assignedDriverUid||l.addedBy||l.user_id;
              if(!dUid||dUid===session.uid) return s;
              const dbp=Number(l.driverBasePay||0),pct=Number(l.driverPct||0),earn=Number(l.earnings||0);
              const cp=dbp>0?dbp:(pct>0&&earn>0?earn*pct/100:0);
              return s+cp+((Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0))/60*(Number(rates.driverWaitRate)||0);
            },0);
            rev = gr + wp - dp;
            exp = mE.reduce((s,e)=>s+Number(e.amount||0),0);
          } else {
            const rp = driverIsCorpOwner
              ? mL.reduce((s,l)=>s+Number(l.earnings||0),0)
              : mL.reduce((s,l)=>s+Number(l.driverBasePay||0),0);
            const wp = mL.reduce((s,l)=>s+((Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0))/60*(Number(rates.driverWaitRate)||0),0);
            rev = rp + wp;
            exp = driverIsCorpOwner
              ? mE.reduce((s,e)=>s+Number(e.amount||0),0)
              : mE.filter(e=>!e.ownerExpense&&e.expenseType!=="business"&&e.source!=="load").reduce((s,e)=>s+Number(e.amount||0),0);
          }
          const net = rev - exp;
          const mg  = rev > 0 ? parseFloat(((net / rev) * 100).toFixed(1)) : null;
          return { mName, rev, exp, net, mg, loads: mL.length };
        });

        section("Monthly P&L Statement — " + year);
        const hasData = monthlyData.some(m => m.rev > 0 || m.exp > 0);

        if (!hasData) {
          note("No data found for " + year + ". Ensure you have loads and expenses recorded for this year.");
        } else {
          tableHeader([
            { label: "Month",      x: MX },
            { label: "Loads",      x: MX + 38,  align: "right" },
            { label: "Revenue",    x: MX + 75,  align: "right" },
            { label: "Expenses",   x: MX + 112, align: "right" },
            { label: "Net Income", x: MX + 148, align: "right" },
            { label: "Margin",     x: W - MX,   align: "right" },
          ]);

          let ytdR = 0, ytdE = 0, ytdL = 0;
          monthlyData.forEach((m) => {
            if (m.rev === 0 && m.exp === 0) return;
            ytdR += m.rev; ytdE += m.exp; ytdL += m.loads;
            tableRow([
              { label: m.mName,                     x: MX },
              { label: String(m.loads),              x: MX + 38,  align: "right" },
              { label: fmt$(m.rev),                  x: MX + 75,  align: "right" },
              { label: fmt$(m.exp),                  x: MX + 112, align: "right" },
              { label: fmt$(m.net),                  x: MX + 148, align: "right", rgb: m.net >= 0 ? GRN : RED, bold: true },
              { label: m.mg !== null ? m.mg + "%" : "—", x: W - MX, align: "right" },
            ]);
          });
          divider(2);
          const ytdNet = ytdR - ytdE;
          const ytdMg  = ytdR > 0 ? parseFloat(((ytdNet / ytdR) * 100).toFixed(1)) + "%" : "—";
          checkPage(13);
          setFill(NAVY); doc.rect(MX - 2, y - 3, CW + 4, 11, "F");
          doc.setFont("helvetica", "bold"); doc.setFontSize(8.5); setTxt([255,255,255]);
          tx("YEAR-TO-DATE",  MX,      y + 4);
          tx(String(ytdL),   MX + 38, y + 4, { align: "right" });
          tx(fmt$(ytdR),     MX + 75, y + 4, { align: "right" });
          tx(fmt$(ytdE),     MX + 112,y + 4, { align: "right" });
          tx(fmt$(ytdNet),   MX + 148,y + 4, { align: "right" });
          tx(ytdMg,          W - MX,  y + 4, { align: "right" });
          y += 14; setTxt([30,30,30]);

          const active = monthlyData.filter(m => m.rev > 0);
          if (active.length > 0) {
            const bestM  = active.reduce((a, b) => b.net > a.net ? b : a);
            const worstM = active.reduce((a, b) => b.net < a.net ? b : a);
            checkPage(12);
            doc.setFillColor(240,253,244); doc.rect(MX-2,y-2,CW+4,10,"F");
            doc.setFont("helvetica","bold"); doc.setFontSize(8.5); setTxt(GRN);
            tx("Best Month: " + bestM.mName + " — Net " + fmt$(bestM.net) + (bestM.mg!==null?" ("+bestM.mg+"% margin)":""), MX, y+5);
            y += 12; setTxt([30,30,30]);
            if (worstM !== bestM) {
              checkPage(12);
              doc.setFillColor(255,241,242); doc.rect(MX-2,y-2,CW+4,10,"F");
              doc.setFont("helvetica","bold"); doc.setFontSize(8.5); setTxt(RED);
              tx("Weakest Month: " + worstM.mName + " — Net " + fmt$(worstM.net) + (worstM.mg!==null?" ("+worstM.mg+"% margin)":""), MX, y+5);
              y += 12; setTxt([30,30,30]);
            }
          }
        }
      }

      // ══════════════════════════════════════════════════════════════════════
      // REPORT 8 — T4A SUMMARY
      // ══════════════════════════════════════════════════════════════════════
      else if (type === "t4a_summary") {

        if (isOwner) {
          note("CRA requires T4A Box 048 slips when total fees paid to a subcontractor exceed $500 in a calendar year. Due: last day of February of the following year.");
          const t4aMap = {};
          loads.filter(l=>l.date&&l.date.startsWith(year)).forEach(l => {
            const dUid = l.assignedDriverUid||l.addedBy||l.user_id;
            if (!dUid||dUid===session.uid) return;
            const name = l.driverFullName||l.assignedDriverName||"Unknown Driver";
            if (!t4aMap[name]) t4aMap[name] = { route: 0, wait: 0, loads: 0 };
            const dbp=Number(l.driverBasePay||0),pct=Number(l.driverPct||0),earn=Number(l.earnings||0);
            const cp=dbp>0?dbp:(pct>0&&earn>0?earn*pct/100:0);
            const wp=parseFloat((((Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0))/60*(Number(rates.driverWaitRate)||0)).toFixed(2));
            t4aMap[name].route += cp;
            t4aMap[name].wait  += wp;
            t4aMap[name].loads++;
          });

          section("T4A Summary — Tax Year " + year);
          tableHeader([
            { label: "Driver / Subcontractor", x: MX },
            { label: "Loads",        x: MX + 82,  align: "right" },
            { label: "T4A Required", x: MX + 115, align: "right" },
            { label: "Box 048 — Fees for Services", x: W - MX, align: "right" },
          ]);
          Object.entries(t4aMap).sort((a,b)=>(b[1].route+b[1].wait)-(a[1].route+a[1].wait)).forEach(([name, d]) => {
            const total = d.route + d.wait;
            const need  = total >= 500;
            tableRow([
              { label: name.substring(0, 42),           x: MX },
              { label: String(d.loads),                 x: MX + 82,  align: "right" },
              { label: need ? "YES — Required" : "No (<$500)", x: MX + 115, align: "right", rgb: need ? GRN : [160,160,160] },
              { label: fmt$(total),                     x: W - MX,   align: "right", bold: true },
            ]);
          });
          divider(2);
          const grandT4a = Object.values(t4aMap).reduce((s,d)=>s+d.route+d.wait,0);
          totalBar("TOTAL CONTRACTOR PAY — Box 048", fmt$(grandT4a));

        } else {
          // ── DRIVER: T4A income summary ────────────────────────────────────
          note("T4A Box 048 — Fees for services paid to you as a subcontractor or independent operator. Report this on T1 Line 13500 (self-employment income).");
          section("T4A Income Summary — Tax Year " + year);
          const ytdT4a  = loads.filter(l=>l.date&&l.date.startsWith(year)&&(l.assignedDriverUid===session.uid||l.addedBy===session.uid||l.user_id===session.uid));
          const ytdRoute = driverIsCorpOwner
            ? ytdT4a.reduce((s,l)=>s+Number(l.earnings||0),0)
            : ytdT4a.reduce((s,l)=>s+Number(l.driverBasePay||0),0);
          const ytdWait  = ytdT4a.reduce((s,l)=>s+((Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0))/60*(Number(rates.driverWaitRate)||0),0);
          const ytdTotal = ytdRoute + ytdWait;

          row("Tax Year", year);
          row("Name", session.fullName || session.name || "—");
          row("Corporation", session.companyName || "—");
          row(driverIsCorpOwner ? "Gross Load Revenue" : "Route Pay (Box 048)", fmt$(ytdRoute));
          row("Wait Time Pay", fmt$(ytdWait));
          totalBar("T4A Box 048 — Total Fees for Services", fmt$(ytdTotal), { large: true });
          note("T4A Box 048 = " + fmt$(ytdTotal) + ". Report on T1 Line 13500. Deduct eligible business expenses on CRA Form T2125.");
        }
      }

      // ══════════════════════════════════════════════════════════════════════
      // REPORT 9 — MILEAGE LOG
      // ══════════════════════════════════════════════════════════════════════
      else if (type === "mileage_log") {
        note("CRA motor vehicle log: record dates, destinations, business purpose and km driven. Keep all fuel receipts. Log for 6 years in case of audit.");

        const mileLoads = myLoads.filter(l=>l.date&&inRange(l.date)).sort((a,b)=>a.date>b.date?1:-1);
        let totalKmLog = 0;

        section("Motor Vehicle Log — " + periodLabel);
        tableHeader([
          { label: "Date",              x: MX },
          { label: "Route / Destination", x: MX + 22 },
          { label: "Truck",             x: MX + 115 },
          { label: "Km",                x: W - MX, align: "right" },
        ]);

        if (mileLoads.length === 0) {
          note("No loads found for this period. Log loads with locations to populate this report.");
        } else {
          mileLoads.forEach(l => {
            const orig = (l.loadOrigin || l.location || "—").substring(0, 20);
            const dest = (l.loadDestination || "—").substring(0, 18);
            const km   = Number(l.km || l.distanceKm || l.distance || 0);
            totalKmLog += km;
            tableRow([
              { label: l.date || "—",      x: MX },
              { label: orig + " → " + dest, x: MX + 22 },
              { label: l.truckNumber || "—", x: MX + 115 },
              { label: km > 0 ? fmtN(km) + " km" : "—", x: W - MX, align: "right" },
            ]);
          });
          divider(2);
          totalBar("TOTAL BUSINESS KM — " + periodLabel,
            totalKmLog > 0 ? fmtN(totalKmLog) + " km" : "Log km per load to total");
          section("Summary & CRA Tips");
          row("Total Business Trips", String(mileLoads.length));
          row("Total Business KM", totalKmLog > 0 ? fmtN(totalKmLog) + " km" : "—");
          row("Vehicle Use Classification", "100% Business (all trips are load-related)");
          note("CRA 2024 auto rate: $0.70/km for first 5,000 km; $0.64/km after. Business-use % = Business KM ÷ Total Annual KM. Record odometer on Jan 1 and Dec 31 each year.");
        }
      }

      // ══════════════════════════════════════════════════════════════════════
      // REPORT 10 — CASH FLOW FORECAST
      // ══════════════════════════════════════════════════════════════════════
      else if (type === "cashflow") {
        const payFreqWeeks = rates.payFrequency === "biweekly" ? 2 : rates.payFrequency === "monthly" ? 4 : 1;
        const pp3          = getPayPeriod(rates);
        const weeksActive  = Math.max(1, Math.ceil(myLoads.filter(l=>l.date).length / 7));
        const today3       = new Date();

        note("4-week cash flow projection based on your average weekly activity from " + periodLabel + ". Estimates only.");

        if (isOwner) {
          const avgWkRev    = (grossRevenue + companyWaitPay) / weeksActive;
          const avgWkDrvPay = driverPay / weeksActive;
          const avgWkExp    = totalExpenses / weeksActive;

          section("4-Week Revenue & Cash Flow Forecast");
          tableHeader([
            { label: "Week",            x: MX },
            { label: "Proj. Revenue",   x: MX + 55,  align: "right" },
            { label: "Driver Pay",      x: MX + 100, align: "right" },
            { label: "Expenses",        x: MX + 140, align: "right" },
            { label: "Net Cash",        x: W - MX,   align: "right" },
          ]);
          [1, 2, 3, 4].forEach((w, i) => {
            const wStart = new Date(today3); wStart.setDate(today3.getDate() + (w - 1) * 7);
            const wEnd   = new Date(wStart); wEnd.setDate(wStart.getDate() + 6);
            const isPay  = w % payFreqWeeks === 0 || w === payFreqWeeks;
            const pRev   = parseFloat((avgWkRev    * (0.90 + i * 0.025)).toFixed(2));
            const pDrv   = isPay ? parseFloat((avgWkDrvPay * payFreqWeeks).toFixed(2)) : 0;
            const pExp   = parseFloat((avgWkExp    * 0.90).toFixed(2));
            const net    = pRev - pDrv - pExp;
            const wLabel = MONTHS_SHORT[wStart.getMonth()]+" "+wStart.getDate()+" – "+MONTHS_SHORT[wEnd.getMonth()]+" "+wEnd.getDate();
            tableRow([
              { label: wLabel + (isPay ? "  ★ PAYDAY" : ""), x: MX, bold: isPay },
              { label: fmt$(pRev),                x: MX + 55,  align: "right" },
              { label: isPay ? "(" + fmt$(pDrv) + ")" : "—", x: MX + 100, align: "right" },
              { label: "(" + fmt$(pExp) + ")",    x: MX + 140, align: "right" },
              { label: fmt$(net), x: W - MX, align: "right", rgb: net >= 0 ? GRN : RED, bold: true },
            ]);
          });
          divider(2);
          const t4r = parseFloat((avgWkRev    * 4).toFixed(2));
          const t4d = parseFloat((avgWkDrvPay * 4).toFixed(2));
          const t4e = parseFloat((avgWkExp    * 4).toFixed(2));
          totalBar("4-WEEK PROJECTED NET: " + fmt$(t4r - t4d - t4e),
                   "Rev: " + fmt$(t4r) + "  Drv: (" + fmt$(t4d) + ")  Exp: (" + fmt$(t4e) + ")");

        } else {
          const avgWkEarn = effectiveDrvIncome / weeksActive;
          const avgWkExp2 = driverAllExpTotal  / weeksActive;

          if (pp3 && pp3.nextPayLabel) {
            section("Pay Schedule");
            row("Pay Frequency",  pp3.freqLabel   || "—");
            row("Next Pay Date",  pp3.nextPayLabel || "—");
            row("Pay Cutoff",     pp3.cutoffLabel  || "—");
            row("Expected Amount", fmt$(avgWkEarn * payFreqWeeks));
          }

          section("4-Week Earnings Forecast");
          tableHeader([
            { label: "Week",             x: MX },
            { label: "Proj. Earnings",   x: MX + 65,  align: "right" },
            { label: "Est. Expenses",    x: MX + 120, align: "right" },
            { label: "Take-Home",        x: W - MX,   align: "right" },
          ]);
          [1, 2, 3, 4].forEach((w, i) => {
            const wStart = new Date(today3); wStart.setDate(today3.getDate() + (w - 1) * 7);
            const wEnd   = new Date(wStart); wEnd.setDate(wStart.getDate() + 6);
            const isPay  = w % payFreqWeeks === 0 || w === payFreqWeeks;
            const pEarn  = parseFloat((avgWkEarn * (0.90 + i * 0.025)).toFixed(2));
            const pExp2  = parseFloat((avgWkExp2 * 0.90).toFixed(2));
            const net    = pEarn - pExp2;
            const wLabel = MONTHS_SHORT[wStart.getMonth()]+" "+wStart.getDate()+" – "+MONTHS_SHORT[wEnd.getMonth()]+" "+wEnd.getDate();
            tableRow([
              { label: wLabel + (isPay ? "  ★ PAY WEEK" : ""), x: MX, bold: isPay },
              { label: fmt$(pEarn),                x: MX + 65,  align: "right" },
              { label: "(" + fmt$(pExp2) + ")",    x: MX + 120, align: "right" },
              { label: fmt$(net), x: W - MX, align: "right", rgb: net >= 0 ? GRN : RED, bold: true },
            ]);
          });
          divider(2);
          const t4e2 = parseFloat((avgWkEarn * 4).toFixed(2));
          const t4x2 = parseFloat((avgWkExp2 * 4).toFixed(2));
          totalBar("4-WEEK PROJECTED: Earn " + fmt$(t4e2) + "  /  Take-Home " + fmt$(t4e2 - t4x2),
                   "Expenses: (" + fmt$(t4x2) + ")");
        }
      }

      // ══════════════════════════════════════════════════════════════════════
      // REPORT 11 — ROUTE PROFITABILITY
      // ══════════════════════════════════════════════════════════════════════
      else if (type === "route_profit") {
        const rpLoads = isOwner ? filteredLoads : myLoads;

        if (isOwner) {
          const rMap = {};
          rpLoads.forEach(l => {
            const route = (l.location || "Unlabelled Route").substring(0, 50);
            if (!rMap[route]) rMap[route] = { loads: 0, grossRev: 0, drvPay: 0, waitRev: 0, waitPay: 0 };
            rMap[route].loads++;
            rMap[route].grossRev += Number(l.earnings || 0);
            rMap[route].waitRev  += ((Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0))/60*(Number(rates.companyWaitRate)||0);
            const dUid = l.assignedDriverUid||l.addedBy||l.user_id;
            if (dUid && dUid !== session.uid) {
              const dbp=Number(l.driverBasePay||0),pct=Number(l.driverPct||0),earn=Number(l.earnings||0);
              const cp=dbp>0?dbp:(pct>0&&earn>0?earn*pct/100:0);
              const wp=((Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0))/60*(Number(rates.driverWaitRate)||0);
              rMap[route].drvPay += cp + wp;
            }
          });
          const rList = Object.entries(rMap).map(([name,d]) => {
            const totRev = d.grossRev + d.waitRev;
            const netRev = totRev - d.drvPay;
            const mg     = totRev > 0 ? parseFloat(((netRev / totRev) * 100).toFixed(1)) : 0;
            return { name, loads: d.loads, totRev, netRev, mg,
                     avgRev: d.loads > 0 ? totRev / d.loads : 0,
                     avgNet: d.loads > 0 ? netRev / d.loads : 0 };
          }).sort((a, b) => b.mg - a.mg);

          section("Route Profitability — " + periodLabel);
          if (rList.length === 0) {
            note("No loads found for this period.");
          } else {
            tableHeader([
              { label: "Route",        x: MX },
              { label: "Loads",        x: MX + 77,  align: "right" },
              { label: "Avg Revenue",  x: MX + 108, align: "right" },
              { label: "Avg Net",      x: MX + 143, align: "right" },
              { label: "Margin",       x: W - MX,   align: "right" },
            ]);
            rList.forEach(r => {
              const good = r.mg >= 60, med = r.mg >= 40 && r.mg < 60;
              tableRow([
                { label: r.name.substring(0, 40),  x: MX },
                { label: String(r.loads),           x: MX + 77,  align: "right" },
                { label: fmt$(r.avgRev),             x: MX + 108, align: "right" },
                { label: fmt$(r.avgNet),             x: MX + 143, align: "right" },
                { label: r.mg.toFixed(1) + "%",      x: W - MX,   align: "right",
                  rgb: good ? GRN : med ? [180,83,9] : RED, bold: true },
              ]);
            });
            divider(2);
            const allLoads = rList.reduce((s,r)=>s+r.loads,0);
            const allNet   = rList.reduce((s,r)=>s+r.netRev,0);
            const allRev   = rList.reduce((s,r)=>s+r.totRev,0);
            const overMg   = allRev > 0 ? parseFloat(((allNet/allRev)*100).toFixed(1)) : 0;
            totalBar("ALL ROUTES: " + allLoads + " loads  |  Overall Margin: " + overMg + "%",
                     "Total Net: " + fmt$(allNet));
            const best = rList[0], worst = rList[rList.length-1];
            checkPage(12);
            doc.setFillColor(240,253,244); doc.rect(MX-2,y-2,CW+4,10,"F");
            doc.setFont("helvetica","bold"); doc.setFontSize(8.5); setTxt(GRN);
            tx("Best: " + best.name.substring(0,55) + " — " + best.mg.toFixed(1) + "% margin  (" + best.loads + " loads, avg " + fmt$(best.avgNet) + ")", MX, y+5);
            y += 12; setTxt([30,30,30]);
            if (worst !== best) {
              checkPage(12);
              doc.setFillColor(255,241,242); doc.rect(MX-2,y-2,CW+4,10,"F");
              doc.setFont("helvetica","bold"); doc.setFontSize(8.5); setTxt(RED);
              tx("Lowest: " + worst.name.substring(0,55) + " — " + worst.mg.toFixed(1) + "% margin  (" + worst.loads + " loads, avg " + fmt$(worst.avgNet) + ")", MX, y+5);
              y += 12; setTxt([30,30,30]);
            }
          }

        } else {
          // ── DRIVER: earnings per route ──────────────────────────────────────
          const drvRMap = {};
          rpLoads.forEach(l => {
            const route = (l.location || "Unlabelled Route").substring(0, 50);
            if (!drvRMap[route]) drvRMap[route] = { loads:0, earn:0, wait:0, waitMins:0, done:0 };
            drvRMap[route].loads++;
            drvRMap[route].earn += driverIsCorpOwner ? Number(l.earnings||0) : Number(l.driverBasePay||0);
            const wm = (Number(l.loadWaitMins)||0)+(Number(l.offloadWaitMins)||0);
            drvRMap[route].wait     += wm / 60 * (Number(rates.driverWaitRate)||0);
            drvRMap[route].waitMins += wm;
            if (l.completed) drvRMap[route].done++;
          });
          const drvRList = Object.entries(drvRMap).map(([name,d]) => ({
            name, loads: d.loads, done: d.done,
            totEarn: d.earn + d.wait,
            avgEarn: d.loads > 0 ? (d.earn + d.wait) / d.loads : 0,
            avgWaitMins: d.loads > 0 ? d.waitMins / d.loads : 0,
          })).sort((a,b) => b.avgEarn - a.avgEarn);

          section("Route Earnings Analysis — " + periodLabel);
          if (drvRList.length === 0) {
            note("No loads found for this period.");
          } else {
            const topAvg = drvRList[0]?.avgEarn || 1;
            tableHeader([
              { label: "Route",           x: MX },
              { label: "Loads",           x: MX + 80,  align: "right" },
              { label: "Avg Earnings",    x: MX + 115, align: "right" },
              { label: "Avg Wait",        x: MX + 148, align: "right" },
              { label: "Total Earned",    x: W - MX,   align: "right" },
            ]);
            drvRList.forEach(r => {
              const isTop = r.avgEarn >= topAvg * 0.85;
              const isMid = r.avgEarn >= topAvg * 0.60 && !isTop;
              tableRow([
                { label: r.name.substring(0,42),  x: MX },
                { label: String(r.loads),          x: MX + 80,  align: "right" },
                { label: fmt$(r.avgEarn),           x: MX + 115, align: "right", rgb: isTop ? GRN : isMid ? [180,83,9] : RED, bold: true },
                { label: r.avgWaitMins > 0 ? Math.round(r.avgWaitMins) + " min" : "—", x: MX + 148, align: "right" },
                { label: fmt$(r.totEarn),           x: W - MX,   align: "right" },
              ]);
            });
            divider(2);
            totalBar("ALL ROUTES: " + drvRList.reduce((s,r)=>s+r.loads,0) + " loads",
                     "Total: " + fmt$(drvRList.reduce((s,r)=>s+r.totEarn,0)));
            const best2  = drvRList[0];
            const worst2 = drvRList[drvRList.length-1];
            checkPage(12);
            doc.setFillColor(240,253,244); doc.rect(MX-2,y-2,CW+4,10,"F");
            doc.setFont("helvetica","bold"); doc.setFontSize(8.5); setTxt(GRN);
            tx("Best: " + best2.name.substring(0,55) + " — " + fmt$(best2.avgEarn) + "/load  (" + best2.loads + " loads)", MX, y+5);
            y += 12; setTxt([30,30,30]);
            if (worst2 !== best2) {
              checkPage(12);
              doc.setFillColor(255,241,242); doc.rect(MX-2,y-2,CW+4,10,"F");
              doc.setFont("helvetica","bold"); doc.setFontSize(8.5); setTxt(RED);
              tx("Lowest: " + worst2.name.substring(0,55) + " — " + fmt$(worst2.avgEarn) + "/load  (" + worst2.loads + " loads)", MX, y+5);
              y += 12; setTxt([30,30,30]);
            }
          }
        }
      }

      // ── Branded footer on every page ─────────────────────────────────────
      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFillColor(245, 246, 249);
        doc.rect(0, 267, W, 15, "F");
        setFill(AMBER); doc.rect(0, 267, W, 1, "F");
        doc.setFontSize(7.5); doc.setFont("helvetica", "normal"); setTxt([130,130,140]);
        doc.text("TruckPilot  ·  truckpilot.ca  ·  Generated " + new Date().toLocaleDateString("en-CA"), MX, 274);
        doc.text("Page " + i + " of " + totalPages, W - MX, 274, { align: "right" });
      }

      const fileName = "TruckPilot-" + type + "-" + periodLabel.replace(/ /g, "-") + ".pdf";
      doc.save(fileName);

    } catch (e) {
      alert("Error generating PDF: " + e.message);
    }
    setGenerating(null);
  };


  const money = (v) => `$${Number(v||0).toLocaleString("en-CA",{minimumFractionDigits:2,maximumFractionDigits:2})}`;

  const REPORTS = [
    {
      id:"income", icon:"📊",
      title: isOwner ? "Income & Expense Statement" : "Driver Pay Statement",
      desc: isOwner ? "Revenue, expenses and net profit summary" : "Your route pay, wait pay, personal expenses and take-home total",
      color:"#E8962E"
    },
    {
      id:"tax", icon:"🗂",
      title: isOwner ? "CRA T2125 Tax Report" : "CRA Tax Statement",
      desc: isOwner ? "Statement of business income for CRA filing" : "Statement of employment/contract income and deductible expenses",
      color:"#166534"
    },
    { id:"payroll", icon:"💵", title: isOwner ? "Payroll Summary" : "Pay Period Summary", desc: isOwner ? "Driver pay breakdown for the period" : "Your pay breakdown for the period", color:"#E8962E" },
    { id:"ifta", icon:"⛽", title:"IFTA Fuel Tax Report", desc:"Jurisdiction fuel summary for IFTA filing", color:"#92400e" },
    // ── NEW REPORTS ─────────────────────────────────────────────────────────────
    { id:"gst_return", icon:"🧾",
      title:"GST/HST Quarterly Return",
      desc: isOwner
        ? "Zero-rated trucking supply, ITC refunds on fuel & repairs — Lines 101, 105, 106, 109"
        : "ITC refund claims on eligible expenses — Lines 106 & 109",
      color:"#166534" },
    { id:"driver_paystub", icon:"🪪",
      title: isOwner ? "Driver Earnings Statements" : "Earnings Statement (Pay Stub)",
      desc: isOwner
        ? "Official pay stub per driver with YTD totals — suitable for income verification"
        : "Official proof-of-income for loan applications, rentals and immigration",
      color:"#1D4ED8" },
    { id:"monthly_pl", icon:"📈",
      title:"Monthly P&L Statement",
      desc:"Month-by-month revenue, expenses and net income with gross margin % and YTD totals",
      color:"#1C2B4A" },
    { id:"t4a_summary", icon:"📋",
      title: isOwner ? "T4A Subcontractor Summary" : "T4A Income Summary (Box 048)",
      desc: isOwner
        ? "CRA-required T4A filing data for every owner-operator you paid (Box 048)"
        : "Your total fees earned as a corporation/subcontractor — Box 048 income for CRA filing",
      color:"#7C3AED" },
    { id:"mileage_log", icon:"🗺",
      title:"CRA Motor Vehicle / Mileage Log",
      desc:"Auto-generated from load records — business-use % for CRA motor vehicle expense deduction",
      color:"#92400E" },
    { id:"cashflow", icon:"💰",
      title:"Cash Flow Forecast (4-Week)",
      desc: isOwner
        ? "Projected weekly inflows and driver pay outflows based on current load activity"
        : "Projected weekly earnings vs personal expenses — know your take-home before payday",
      color:"#065F46" },
    { id:"route_profit", icon:"🏆",
      title:"Route Profitability Analysis",
      desc: isOwner
        ? "Net margin per route after driver pay — flag low-margin routes for renegotiation"
        : "Your earnings per route — see which loads pay best and which aren't worth your time",
      color:"#B45309" },
  ];

  return (
    <div className="slt-page" style={{background:DM.pageBg,color:DM.text}}>
      {goBack && <BackButton onBack={goBack} label="Back" />}
      <div className="slt-hero">
        <div className="slt-hero-title" style={{display:"flex",alignItems:"center",gap:10}}>📋 Financial Reports <span style={{fontSize:10,fontWeight:800,padding:"2px 8px",borderRadius:20,background:"rgba(59,130,246,0.15)",color:"#3B82F6",border:"1px solid rgba(59,130,246,0.25)",letterSpacing:"0.5px",textTransform:"uppercase",verticalAlign:"middle"}}>Pro</span></div>
        <div className="slt-hero-sub">{isOwner ? "Professional PDF reports for tax, payroll and compliance" : "Your pay statements, expense records and tax exports"}</div>
      </div>
      <div className="slt-container">

        {/* Period selector */}
        <div className="slt-card" style={{marginBottom:16,background:DM.cardBg}}>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:14,marginBottom:12,color:"#E8962E"}}>📅 Report Period</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:10}}>
            {[["year","Full Year"],["q1","Q1"],["q2","Q2"],["q3","Q3"],["q4","Q4"]].map(([v,l])=>(
              <button key={v} onClick={()=>setPeriod(v)}
                style={{padding:"7px 14px",borderRadius:20,border:"none",cursor:"pointer",fontSize:12,fontWeight:700,
                  background:period===v?"#E8962E":DM.amberBg,color:period===v?"#fff":"#E8962E"}}>
                {l}
              </button>
            ))}
          </div>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:13,fontWeight:600,color:DM.textMed}}>Year:</span>
            <select value={year} onChange={e=>setYear(e.target.value)}
              style={{padding:"6px 12px",borderRadius:8,border:darkMode?"1.5px solid rgba(255,255,255,0.1)":"1.5px solid #e0e0e0",fontSize:13,fontWeight:600,background:DM.inputBg,color:DM.text}}>
              {["2026","2025","2024","2023"].map(y=><option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        {/* Debug info */}
        <div style={{fontSize:13,color:DM.textMed,marginBottom:8,padding:"8px 12px",background:DM.debugBg,borderRadius:8}}>
          Loads in range: {filteredLoads.length} · Expenses: {filteredExp.length} · Gross: {money(grossRevenue)} · DriverPay: {money(driverPay)} · WaitPay: {money(waitPay)} · Expenses$: {money(totalExpenses)}
        </div>

        {/* Summary card */}
        <div style={{borderRadius:18,
          background: darkMode ? "linear-gradient(135deg,#0f2137,#1a2e4a)" : "#1C2B4A",
          padding:"18px 20px",marginBottom:16,color:"#fff",
          boxShadow: darkMode ? "none" : "0 4px 16px rgba(28,43,74,0.18)"}}>
          <div style={{fontSize:11,fontWeight:800,color:"rgba(255,255,255,.4)",textTransform:"uppercase",letterSpacing:1.5,marginBottom:12}}>
            {periodLabel} Summary
          </div>
          <div style={{display:"grid",gridTemplateColumns:isOwner?"1fr 1fr 1fr 1fr":"1fr 1fr 1fr",gap:8}}>
            {[
              {label:"Gross Revenue",val:money(isOwner?grossRevenue+waitPay:totalIncome)},
              ...(isOwner?[{label:"Driver Pay",val:`(${money(driverPay)})`,red:true}]:[]),
              {label:"Expenses",val:money(totalExpenses),red:totalExpenses>0},
              {label:"Net Profit",val:money(netProfit),green:netProfit>=0,red:netProfit<0},
            ].map(s=>(
              <div key={s.label} style={{textAlign:"center",background:"rgba(255,255,255,.06)",borderRadius:10,padding:"10px 6px"}}>
                <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,.45)",marginBottom:5,textTransform:"uppercase",letterSpacing:0.5}}>{s.label}</div>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:900,
                  color:s.red?"#E8962E":s.green?"#4ade80":"#fff"}}>
                  {s.val}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Report cards */}
        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:14,marginBottom:10,color:"#E8962E"}}>
          📥 Download Reports
        </div>
        {REPORTS.map(r=>(
          <div key={r.id} className="slt-card" style={{marginBottom:10,borderLeft:`4px solid ${r.color}`,background:DM.cardBg}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <div style={{width:44,height:44,borderRadius:12,background:r.color+"18",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>
                  {r.icon}
                </div>
                <div>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:15,color:DM.text}}>{r.title}</div>
                  <div style={{fontSize:12,color:DM.textMed,marginTop:2}}>{r.desc}</div>
                </div>
              </div>
              <button onClick={()=>generatePDF(r.id)} disabled={!!generating}
                style={{padding:"10px 16px",borderRadius:12,border:"none",background:r.color,color:"#fff",
                  fontWeight:800,fontSize:12,cursor:generating?"wait":"pointer",whiteSpace:"nowrap",opacity:generating===r.id?0.7:1}}>
                {generating===r.id?"⏳ Generating...":"⬇️ PDF"}
              </button>
            </div>
          </div>
        ))}

        <div style={{borderRadius:12,background:DM.amberBg,padding:"12px 16px",marginTop:8,fontSize:12,color:"#F57C00",fontWeight:600}}>
          ⚠️ These reports are for reference only. Always consult a CRA-certified accountant before filing taxes.
        </div>

      </div>
    </div>
  );
}

function TaxTab({ session, isOwner, allLoads=[], rates={}, goBack, darkMode=false}) {
  // ── Light / dark token palette (TX) ──────────────────────────────
  const TX = {
    pageBg:      darkMode ? "#070B14"                   : "#F5F6F8",
    cardBg:      darkMode ? "#0D1523"                   : "#FFFFFF",
    heroBg:      darkMode ? "linear-gradient(135deg,#0f2137,#0b1a2a)" : "linear-gradient(135deg,#1C2B4A,#243655)",
    border:      darkMode ? "rgba(255,255,255,0.08)"    : "rgba(0,0,0,0.08)",
    text:        darkMode ? "#F0F0F0"                   : "#1A1A1A",
    textMed:     darkMode ? "rgba(255,255,255,0.55)"    : "#4B5563",
    textMut:     darkMode ? "rgba(255,255,255,0.35)"    : "#9CA3AF",
    labelColor:  darkMode ? "rgba(255,255,255,0.45)"    : "#6B7280",
    sectionTitle:darkMode ? "#E8962E"                   : "#1C2B4A",
    // summary cards
    greenCardBg: darkMode ? "rgba(22,163,74,0.12)"      : "#F0FDF4",
    greenCardLbl:darkMode ? "#86efac"                   : "#166534",
    greenCardVal:darkMode ? "#22C55E"                   : "#16A34A",
    redCardBg:   darkMode ? "rgba(239,68,68,0.12)"      : "#FEF2F2",
    redCardLbl:  darkMode ? "#fca5a5"                   : "#991B1B",
    redCardVal:  darkMode ? "#EF4444"                   : "#EF4444",
    // T2125 table
    t2125HeaderColor: darkMode ? "rgba(255,255,255,0.45)" : "#6B7280",
    t2125Border:      darkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)",
    t2125LineColor:   darkMode ? "rgba(255,255,255,0.45)" : "#9CA3AF",
    t2125TextColor:   darkMode ? "#F0F0F0"                : "#1A1A1A",
    // GST row
    gstTextColor:     darkMode ? "#F0F0F0"                : "#1A1A1A",
    gstBorder:        darkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)",
    gstNoteBg:        darkMode ? "rgba(255,179,0,0.12)"   : "#FFFBEB",
    gstNoteBorder:    darkMode ? "#FFB300"                 : "#FCD34D",
    gstNoteText:      darkMode ? "#FFD54F"                 : "#92400E",
    // Deductible breakdown section
    breakdownTitle:   darkMode ? "#E8962E"                : "#1C2B4A",
    // range picker buttons
    btnActiveBg:      darkMode ? C.blue                   : "#1C2B4A",
    btnInactiveBg:    darkMode ? "rgba(255,255,255,0.08)" : "#FFFFFF",
    btnInactiveColor: darkMode ? "rgba(255,255,255,0.5)"  : "#4B5563",
    btnInactiveBorder:darkMode ? "rgba(255,255,255,0.12)" : "#D1D5DB",
  };
  const curYear = new Date().getFullYear().toString();
  const [year, setYear] = useState(curYear);
  const [useCustomRange, setUseCustomRange] = useState(false);
  const [rangeStart, setRangeStart] = useState(`${curYear}-01-01`);
  const [rangeEnd, setRangeEnd] = useState(`${curYear}-12-31`);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [sbExpenses, setSbExpenses] = useState([]);
  const [expandedCat, setExpandedCat] = useState(null);
  const [fuelLogEntries, setFuelLogEntries] = useState([]);

  useEffect(() => {
    sbGetExpenses(session.uid).then(data => {
      if (data && data.length > 0) setSbExpenses(data);
    }).catch(() => {});

  }, [session.uid, isOwner]);

  // Merge localStorage + Supabase expenses, deduplicate by id
  const allExpenses = (() => {
    const local = getStored(expensesKey(session.uid));
    const merged = [...local];
    sbExpenses.forEach(se => {
      if (!merged.find(e => e.id === se.id)) merged.push(se);
    });
    // Load fuel = owner/business expense only — never shown to drivers
    if (isOwner) {
      allLoads.filter(l => Number(l.fuelTotal) > 0).forEach(l => {
        const fuelId = `fuel-${l.id}`;
        if (!merged.find(e => e.id === fuelId)) {
          merged.push({
            id: fuelId, loadRef: l.id, category: "fuel",
            amount: Number(l.fuelTotal),
            description: `Fuel – ${l.location||"Load"} (${l.fuelLitres||"?"}L @ $${Number(l.fuelPricePerLitre||0).toFixed(3)}/L)`,
            date: l.date || todayStr(), source: "load",
            taxCategory: "Line 9220", taxLabel: "Fuel & Oil", ownerExpense: true
          });
        }
      });

    }
    return merged;
  })();

  const expenses = allExpenses;
  const inRange = (dateStr) => {
    if (!dateStr) return false;
    if (useCustomRange) return dateStr >= rangeStart && dateStr <= rangeEnd;
    return dateStr.startsWith(year);
  };

  // Driver-only view: simplified personal expense summary
  // Drivers only see expenses THEY manually added — load fuel is owner/business only
  const driverExpenses = allExpenses.filter(e => e.source !== "load" && !e.ownerExpense && e.expenseType !== "business");

  if (!isOwner) {
    const TAX_CATS_DRIVER = [
      { id:"fuel",           label:"Fuel & Oil",              icon:"⛽", taxLine:"Line 9220 – Fuel & oil",         color:C.orange },
      { id:"meals",          label:"Meals (50% deductible)",  icon:"🍽", taxLine:"Line 8523 – Meals",              color:C.green  },
      { id:"lodging",        label:"Accommodation / Travel",  icon:"🏨", taxLine:"Line 9200 – Travel",             color:"#8D6E63"},
      { id:"tolls",          label:"Tolls & Parking",         icon:"🛣", taxLine:"Line 9281 – Other expenses",     color:"#E8962E"},
      { id:"tools_supplies", label:"Tools & Supplies",        icon:"🧰", taxLine:"Line 9270 – Supplies",           color:C.blue   },
      { id:"safety",         label:"Safety Gear",             icon:"🦺", taxLine:"Line 9270 – Protective clothing",color:"#EF6C00"},
      { id:"union_dues",     label:"Union / Dues",            icon:"🤝", taxLine:"Line 9270 – Professional fees",  color:"#4B5563"},
      { id:"telephone",      label:"Phone & Internet",        icon:"📱", taxLine:"Line 9220 – Phone / internet",   color:"#E8962E"},
      { id:"medical",        label:"Medical / Drug Plan",     icon:"💊", taxLine:"Line 9270 – Medical premiums",   color:"#D32F2F"},
      { id:"other",          label:"Other",                   icon:"📦", taxLine:"Line 9270 – Other expenses",     color:"#4B5563"},
    ];
    const yearExp = allExpenses.filter(e => e.date && inRange(e.date) && !e.ownerExpense && e.expenseType !== "business" && e.source !== "load");
    const byCategory = TAX_CATS_DRIVER.map(cat => ({
      ...cat,
      total: yearExp.filter(e => e.category === cat.id).reduce((s, e) => s + Number(e.amount || 0), 0),
      count: yearExp.filter(e => e.category === cat.id).length,
    }));
    const grandTotal = byCategory.reduce((s, c) => s + c.total, 0);
    const mealsAdj = (byCategory.find(c => c.id === "meals")?.total || 0) * 0.5;
    const adjustedDriverTotal = grandTotal - mealsAdj;
    // ITC on eligible expenses (fuel, tools, other — not meals/lodging)
    const driverItcBase = byCategory.filter(c => !["meals","lodging"].includes(c.id)).reduce((s,c) => s+c.total, 0);
    const driverItc = parseFloat((driverItcBase * 0.05).toFixed(2));

    // T777 lines (driver equivalent of T2125)
    const t777Lines = [
      { line:"T1 13500", label:"Self-Employment Income (from T4A / Route Pay)", amount: allLoads.filter(l => inRange(l.date) && (l.assignedDriverUid === session.uid || l.user_id === session.uid || l.addedBy === session.uid)).reduce((s,l) => s + Number(l.driverBasePay||0), 0), color:"#22C55E", bold:true },
      ...byCategory.filter(c => c.total > 0).map(c => ({
        line: c.taxLine.split(" – ")[0],
        label: c.label + (c.id==="meals" ? " (50% rule)" : ""),
        amount: c.id==="meals" ? c.total * 0.5 : c.total,
        color: "#E8962E",
      })),
      { line:"Net", label:"Net Self-Employment Income", amount: allLoads.filter(l => inRange(l.date) && (l.assignedDriverUid === session.uid || l.user_id === session.uid || l.addedBy === session.uid)).reduce((s,l) => s + Number(l.driverBasePay||0), 0) - adjustedDriverTotal, color:"#22C55E", bold:true },
    ];
    const driverIncome = t777Lines[0].amount;
    const driverNet = driverIncome - adjustedDriverTotal;

    const buildDriverPDF = () => {
      const driverName = session.fullName || session.name || "Driver";
      const companyName = session.companyName || "";
      const rows = byCategory.filter(c=>c.total>0).map((c,i) => `
        <tr style="background:${i%2===0?"#fff":"#F9FAFB"}">
          <td style="padding:9px 12px">${c.icon} ${c.label}</td>
          <td style="padding:9px 12px;color:#6B7280;font-size:12px">${c.taxLine}</td>
          <td style="padding:9px 12px;text-align:right;font-weight:700;color:#E8962E">$${(c.id==="meals"?c.total*0.5:c.total).toFixed(2)}</td>
          <td style="padding:9px 12px;font-size:11px;color:#9CA3AF">${c.id==="meals"?`50% rule applies`:"—"}</td>
        </tr>`).join("");
      const noExpenses = byCategory.filter(c=>c.total>0).length === 0;
      return `
        <style>
          *{box-sizing:border-box;margin:0;padding:0}
          body{font-family:'Segoe UI',Arial,sans-serif;color:#1a1a1a;background:#fff;font-size:13px;line-height:1.5}
          .page{max-width:800px;margin:0 auto;padding:32px 36px}
          h2{font-size:13px;font-weight:800;color:#1C2B4A;margin:24px 0 10px;text-transform:uppercase;letter-spacing:1px;border-bottom:2px solid #E8962E;padding-bottom:5px}
          table{width:100%;border-collapse:collapse;margin-bottom:16px;font-size:13px}
          thead tr{background:#1C2B4A}
          thead th{padding:9px 12px;text-align:left;color:#fff;font-size:11px;text-transform:uppercase;letter-spacing:0.8px;font-weight:700}
          thead th.r{text-align:right}
          tbody td{padding:9px 12px;border-bottom:1px solid #F0F0F0;vertical-align:middle}
          tfoot td{padding:11px 12px;font-weight:900;font-size:14px;border-top:2px solid #1C2B4A;background:#F5F8FF}
          .summary-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin:20px 0}
          .s-card{background:#F5F8FF;border:1.5px solid #E0E8F5;border-radius:8px;padding:14px 12px;text-align:center}
          .s-lbl{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#888;margin-bottom:6px}
          .s-val{font-size:22px;font-weight:900;color:#1C2B4A;font-family:'Segoe UI',Arial,sans-serif}
          .s-val.green{color:#16A34A}.s-val.red{color:#DC2626}.s-val.blue{color:#1565C0}
          .calc-box{background:#F0FDF4;border:1.5px solid #A5D6A7;border-radius:8px;padding:16px 18px;margin:20px 0}
          .calc-row{display:flex;justify-content:space-between;padding:6px 0;font-size:13px;border-bottom:1px solid rgba(0,0,0,0.05)}
          .calc-row:last-child{border-bottom:none;font-weight:900;font-size:15px;padding-top:12px;margin-top:4px;border-top:2px solid #4CAF50}
          .gst-box{background:#E8F5E9;border-radius:8px;border-left:4px solid #4CAF50;padding:14px 16px;margin:20px 0}
          .warn-box{background:#FFF8E1;border:1.5px solid #FFB300;border-radius:8px;padding:14px 16px;font-size:12px;color:#7a5f00;margin-top:16px}
        </style>
        <div class="page">

          <h2>Financial Summary</h2>
          <div class="summary-grid">
            <div class="s-card"><div class="s-lbl">Route Income</div><div class="s-val green">$${driverIncome.toFixed(2)}</div></div>
            <div class="s-card"><div class="s-lbl">Total Deductions</div><div class="s-val red">$${adjustedDriverTotal.toFixed(2)}</div></div>
            <div class="s-card"><div class="s-lbl">Net Self-Employment</div><div class="s-val green">$${driverNet.toFixed(2)}</div></div>
            <div class="s-card"><div class="s-lbl">ITC Refund (5%)</div><div class="s-val blue">$${driverItc.toFixed(2)}</div></div>
          </div>

          <div class="calc-box">
            <div style="font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:1px;color:#2E7D32;margin-bottom:10px">Income &amp; Deductions Breakdown</div>
            <div class="calc-row"><span>Route Pay (self-employment income)</span><span style="color:#16A34A;font-weight:700">+ $${driverIncome.toFixed(2)}</span></div>
            <div class="calc-row"><span>Personal Business Expenses</span><span style="color:#DC2626;font-weight:700">− $${adjustedDriverTotal.toFixed(2)}</span></div>
            <div class="calc-row"><span>Net Self-Employment Income</span><span style="color:${driverNet>=0?"#16A34A":"#DC2626"}">$${driverNet.toFixed(2)}</span></div>
          </div>

          <h2>T777 — Expense Deductions by Category</h2>
          ${noExpenses
            ? `<div style="padding:20px;background:#F9FAFB;border-radius:8px;text-align:center;color:#9CA3AF;font-size:13px">No expenses recorded for ${year}</div>`
            : `<table>
                <thead><tr>
                  <th>Category</th>
                  <th>CRA Line</th>
                  <th class="r">Amount</th>
                  <th>Notes</th>
                </tr></thead>
                <tbody>${rows}</tbody>
                <tfoot><tr>
                  <td colspan="2">NET DEDUCTIBLE TOTAL</td>
                  <td style="text-align:right">$${adjustedDriverTotal.toFixed(2)}</td>
                  <td></td>
                </tr></tfoot>
              </table>`
          }

          <div class="gst-box">
            <div style="font-weight:800;font-size:14px;margin-bottom:8px">GST/HST Input Tax Credits (ITC)</div>
            <div style="font-size:13px;color:#2E7D32;line-height:1.6">
              Your trucking income is <strong>zero-rated</strong> — you do NOT collect GST from your fleet owner or clients.<br/>
              You CAN claim ITCs (refunds) on eligible business expenses you paid GST on.<br/>
              <strong>Eligible expenses × 5% = $${driverItc.toFixed(2)} refund from CRA (Line 109)</strong>
            </div>
          </div>

          <div class="warn-box">
            ⚠️ <strong>Tax Disclaimer:</strong> This report is for reference only. Retain all receipts for 6 years as required by CRA. Meals are 50% deductible. If you received a T4A from your fleet owner, report that income on T1 Line 13500. Always consult a qualified CPA before filing.
          </div>

        </div>
      `;
    };

    // TX tokens for driver view (reuse same light/dark logic)
    const dTX = {
      pageBg:       darkMode ? "#070B14"                 : "#F5F6F8",
      cardBg:       darkMode ? "#0D1523"                 : "#FFFFFF",
      text:         darkMode ? "#F0F0F0"                 : "#1A1A1A",
      textMed:      darkMode ? "rgba(255,255,255,0.5)"   : "#4B5563",
      textMut:      darkMode ? "rgba(255,255,255,0.35)"  : "#9CA3AF",
      border:       darkMode ? "rgba(255,255,255,0.08)"  : "rgba(0,0,0,0.07)",
      sectionTitle: darkMode ? "#22C55E"                 : "#1B5E20",
      greenCardBg:  darkMode ? "rgba(22,163,74,0.12)"    : "#F0FDF4",
      greenCardLbl: darkMode ? "#86efac"                 : "#166534",
      greenCardVal: darkMode ? "#22C55E"                 : "#16A34A",
      redCardBg:    darkMode ? "rgba(239,68,68,0.12)"    : "#FEF2F2",
      redCardLbl:   darkMode ? "#fca5a5"                 : "#991B1B",
      redCardVal:   darkMode ? "#EF4444"                 : "#EF4444",
      t2125Hdr:     darkMode ? "rgba(255,255,255,0.45)"  : "#6B7280",
      t2125Border:  darkMode ? "rgba(255,255,255,0.08)"  : "rgba(0,0,0,0.07)",
      t2125Line:    darkMode ? "rgba(255,255,255,0.45)"  : "#9CA3AF",
      t2125Text:    darkMode ? "#F0F0F0"                 : "#1A1A1A",
      gstBg:        darkMode ? "rgba(22,163,74,0.10)"    : "#F0FDF4",
      gstBorder:    darkMode ? "rgba(22,163,74,0.3)"     : "#BBF7D0",
      noteBg:       darkMode ? "rgba(255,179,0,0.12)"    : "#FFFBEB",
      noteBorder:   darkMode ? "#FFB300"                 : "#FCD34D",
      noteText:     darkMode ? "#FFD54F"                 : "#92400E",
      btnActive:    darkMode ? "#1B5E20"                 : "#1B5E20",
      btnInactive:  darkMode ? "rgba(255,255,255,0.08)"  : "#FFFFFF",
      btnInactiveC: darkMode ? "rgba(255,255,255,0.5)"   : "#4B5563",
      btnInactiveB: darkMode ? "rgba(255,255,255,0.12)"  : "#D1D5DB",
    };

    return (
      <div className="slt-page slt-page-enter" style={{background:dTX.pageBg,color:dTX.text}}>
        {goBack && <BackButton onBack={goBack} label="Back" />}
        <div className="slt-hero" style={{ background: `linear-gradient(135deg, #1B5E20, #2E7D32)` }}>
          <div className="slt-hero-title">🗂 My Tax Summary</div>
          <div className="slt-hero-sub">Your personal deductible expenses — {year}</div>
        </div>
        <div className="slt-container">

          {/* ── Date Range Picker ── */}
          <div className="slt-card" style={{ marginBottom:16 }}>
            <div style={{ display:"flex", gap:8, marginBottom:12 }}>
              <button onClick={() => setUseCustomRange(false)} className="slt-btn-secondary"
                style={{ flex:1, background:!useCustomRange?dTX.btnActive:dTX.btnInactive, color:!useCustomRange?"#fff":dTX.btnInactiveC, borderColor:!useCustomRange?dTX.btnActive:dTX.btnInactiveB, padding:"8px" }}>By Year</button>
              <button onClick={() => setUseCustomRange(true)} className="slt-btn-secondary"
                style={{ flex:1, background:useCustomRange?dTX.btnActive:dTX.btnInactive, color:useCustomRange?"#fff":dTX.btnInactiveC, borderColor:useCustomRange?dTX.btnActive:dTX.btnInactiveB, padding:"8px" }}>Custom Range</button>
            </div>
            {!useCustomRange
              ? <div><label className="slt-label">Tax Year</label>
                  <select className="slt-input" value={year} onChange={e => setYear(e.target.value)}>
                    {["2026","2025","2024","2023","2022"].map(y => <option key={y}>{y}</option>)}
                  </select></div>
              : <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                  <div><label className="slt-label">From</label><input type="date" className="slt-input" value={rangeStart} onChange={e => setRangeStart(e.target.value)}/></div>
                  <div><label className="slt-label">To</label><input type="date" className="slt-input" value={rangeEnd} onChange={e => setRangeEnd(e.target.value)}/></div>
                </div>}
          </div>

          {/* ── Summary Cards ── */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16 }}>
            <div className="slt-card-sm" style={{ borderTop:`4px solid ${C.green}`, background:dTX.greenCardBg }}>
              <div style={{ fontSize:11, color:dTX.greenCardLbl, fontWeight:800, textTransform:"uppercase", letterSpacing:0.8, marginBottom:4 }}>Route Income</div>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:24, fontWeight:900, color:dTX.greenCardVal }}>{fmtC(driverIncome)}</div>
            </div>
            <div className="slt-card-sm" style={{ borderTop:`4px solid ${C.red}`, background:dTX.redCardBg }}>
              <div style={{ fontSize:11, color:dTX.redCardLbl, fontWeight:800, textTransform:"uppercase", letterSpacing:0.8, marginBottom:4 }}>Total Deductions</div>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:24, fontWeight:900, color:dTX.redCardVal }}>{fmtC(adjustedDriverTotal)}</div>
            </div>
          </div>
          <div className="slt-card-sm" style={{ borderTop:`4px solid ${driverNet>=0?C.green:C.red}`, marginBottom:16, background:driverNet>=0?dTX.greenCardBg:dTX.redCardBg }}>
            <div style={{ fontSize:11, color:driverNet>=0?dTX.greenCardLbl:dTX.redCardLbl, fontWeight:800, textTransform:"uppercase", letterSpacing:0.8, marginBottom:4 }}>Net Self-Employment Income</div>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:28, fontWeight:900, color:driverNet>=0?dTX.greenCardVal:dTX.redCardVal }}>{fmtC(driverNet)}</div>
          </div>

          {/* ── T777 Table ── */}
          <div className="slt-card" style={{ marginBottom:16 }}>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:16, marginBottom:14, color:dTX.sectionTitle }}>T777 / T1 — Employment &amp; Self-Employment Expenses</div>
            <div style={{ display:"grid", gridTemplateColumns:"60px 1fr auto", gap:0 }}>
              <div style={{ fontSize:11, fontWeight:800, color:dTX.t2125Hdr, padding:"6px 0", borderBottom:`2px solid ${dTX.t2125Border}` }}>LINE</div>
              <div style={{ fontSize:11, fontWeight:800, color:dTX.t2125Hdr, padding:"6px 8px", borderBottom:`2px solid ${dTX.t2125Border}` }}>DESCRIPTION</div>
              <div style={{ fontSize:11, fontWeight:800, color:dTX.t2125Hdr, padding:"6px 0", textAlign:"right", borderBottom:`2px solid ${dTX.t2125Border}` }}>AMOUNT</div>
              {t777Lines.map((row, i) => (
                row.amount !== 0 || row.bold ? [
                  <div key={`line-${i}`} style={{ fontSize:12, fontWeight:row.bold?800:600, color:row.bold?dTX.t2125Text:dTX.t2125Line, padding:"10px 0", borderBottom:`1px solid ${dTX.t2125Border}` }}>{row.line}</div>,
                  <div key={`label-${i}`} style={{ fontSize:13, fontWeight:row.bold?800:500, color:dTX.t2125Text, padding:"10px 8px", borderBottom:`1px solid ${dTX.t2125Border}` }}>{row.label}</div>,
                  <div key={`amt-${i}`} style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:14, fontWeight:row.bold?900:700, color:row.color, padding:"10px 0", textAlign:"right", borderBottom:`1px solid ${dTX.t2125Border}` }}>{fmtC(row.amount)}</div>,
                ] : null
              ))}
            </div>
          </div>

          {/* ── GST / ITC Section ── */}
          <div className="slt-card" style={{ marginBottom:16 }}>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:16, marginBottom:14, color:dTX.sectionTitle }}>GST / HST — Input Tax Credits</div>
            {[
              { label:"GST on Subcontract Income (zero-rated)", amount:0, color:dTX.greenCardVal },
              { label:"ITC on Eligible Expenses (5%)", amount:-driverItc, color:dTX.redCardVal },
              { label:"ITC Refund from CRA", amount:-driverItc, color:dTX.greenCardVal, bold:true },
            ].map((row, i, arr) => (
              <div key={row.label} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:i<arr.length-1?`1px solid ${dTX.t2125Border}`:"none" }}>
                <span style={{ fontSize:13, fontWeight:row.bold?800:500, color:dTX.t2125Text }}>{row.label}</span>
                <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:15, fontWeight:900, color:row.color }}>
                  {row.amount<0?`(${fmtC(-row.amount)})`:fmtC(row.amount)}
                </span>
              </div>
            ))}
            <div style={{ background:dTX.noteBg, border:`1px solid ${dTX.noteBorder}`, borderRadius:8, padding:"9px 12px", marginTop:12, fontSize:12, color:dTX.noteText }}>
              ℹ️ As a subcontractor, your income is zero-rated — you don't collect GST from your fleet owner. Register for GST to claim ITC refunds on your business expenses.
            </div>
          </div>

          {/* ── Deductible Breakdown Mini Cards ── */}
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:15, marginBottom:10, color:dTX.sectionTitle }}>Deductible Expense Breakdown</div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))", gap:12, marginBottom:20 }}>
            <div className="slt-card-sm" style={{ borderTop:`4px solid ${C.red}`, textAlign:"center" }}>
              <div style={{ fontSize:11, color:dTX.textMed, fontWeight:700, marginBottom:4, textTransform:"uppercase" }}>Total Expenses</div>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:22, fontWeight:800, color:C.red }}>{fmtC(grandTotal)}</div>
            </div>
            <div className="slt-card-sm" style={{ borderTop:`4px solid ${C.green}`, textAlign:"center" }}>
              <div style={{ fontSize:11, color:dTX.textMed, fontWeight:700, marginBottom:4, textTransform:"uppercase" }}>Tax Deductible</div>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:22, fontWeight:800, color:C.green }}>{fmtC(adjustedDriverTotal)}</div>
            </div>
            <div className="slt-card-sm" style={{ borderTop:`4px solid ${C.orange}`, textAlign:"center" }}>
              <div style={{ fontSize:11, color:dTX.textMed, fontWeight:700, marginBottom:4, textTransform:"uppercase" }}>Meals (50%)</div>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:22, fontWeight:800, color:C.orange }}>{fmtC(mealsAdj)}</div>
            </div>
            <div className="slt-card-sm" style={{ borderTop:`4px solid ${C.blue}`, textAlign:"center" }}>
              <div style={{ fontSize:11, color:dTX.textMed, fontWeight:700, marginBottom:4, textTransform:"uppercase" }}>Entries</div>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:22, fontWeight:800, color:C.blue }}>{yearExp.length}</div>
            </div>
          </div>

          {/* ── Category rows (clickable) ── */}
          <div className="slt-card" style={{ marginBottom:16 }}>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:16, marginBottom:14, color:dTX.text }}>
              Expense Breakdown — {useCustomRange?`${rangeStart} → ${rangeEnd}`:year}
            </div>
            {byCategory.map(cat => {
              const catItems2 = yearExp.filter(e => e.category === cat.id);
              const isOpen2 = expandedCat === cat.id;
              return (
                <div key={cat.id}>
                  <div onClick={() => cat.count > 0 && setExpandedCat(isOpen2 ? null : cat.id)}
                    style={{ display:"flex", alignItems:"center", gap:14, padding:"12px 0", borderBottom:`1px solid ${dTX.border}`, cursor:cat.count>0?"pointer":"default" }}>
                    <div style={{ width:38, height:38, borderRadius:9, background:cat.color+"18", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>{cat.icon}</div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, fontSize:13.5, color:dTX.text }}>{cat.label}</div>
                      <div style={{ fontSize:12, color:dTX.textMut, marginTop:1 }}>{cat.taxLine}</div>
                      {cat.id==="meals"&&cat.total>0&&<div style={{ fontSize:12, color:C.orange }}>50% deductible = {fmtC(cat.total*0.5)}</div>}
                    </div>
                    <div style={{ textAlign:"right", flexShrink:0 }}>
                      <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:16, color:cat.total>0?cat.color:dTX.textMut }}>{fmtC(cat.total)}</div>
                      <div style={{ fontSize:12, color:cat.count>0?cat.color:dTX.textMut }}>{cat.count} entries{cat.count>0?(isOpen2?" ▲":" ▼"):""}</div>
                    </div>
                  </div>
                  {isOpen2 && catItems2.length > 0 && (
                    <div style={{ background:cat.color+"08", borderRadius:10, padding:"10px 12px", marginBottom:8, border:`1px solid ${cat.color}20` }}>
                      {catItems2.map((e, i) => (
                        <div key={e.id||i} style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:i<catItems2.length-1?`1px solid ${cat.color}15`:"none" }}>
                          <div>
                            <div style={{ fontSize:13, fontWeight:600, color:dTX.text }}>{e.description||e.note||e.merchant||cat.label}</div>
                            <div style={{ fontSize:12, color:dTX.textMut, marginTop:2 }}>{e.date}{e.merchant?` · ${e.merchant}`:""}</div>
                          </div>
                          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:14, color:cat.color, marginLeft:12 }}>{fmtC(Number(e.amount||0))}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {byCategory.length === 0 && (
              <div style={{ textAlign:"center", padding:"32px 0", color:dTX.textMut }}>No expenses logged for {useCustomRange?`${rangeStart} – ${rangeEnd}`:year}</div>
            )}
            {byCategory.length > 0 && (
              <div style={{ display:"flex", justifyContent:"space-between", padding:"14px 0 4px", borderTop:`2px solid ${dTX.border}`, marginTop:4 }}>
                <span style={{ fontWeight:800, fontSize:14, color:dTX.text }}>Adjusted Deductible Total</span>
                <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:16, color:C.green }}>{fmtC(adjustedDriverTotal)}</span>
              </div>
            )}
          </div>

          {/* ── PDF Download + Disclaimer ── */}
          <div className="slt-card" style={{ marginBottom:16 }}>
            <button onClick={() => downloadPDF(buildDriverPDF(), `DriverTax_${(session.fullName||session.name||"Driver").replace(/\s+/g,"_")}_${year}`, session)}
              style={{ width:"100%", padding:"13px", border:"none", borderRadius:10, background:"#1B5E20", color:"#fff", cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:14 }}>
              ⬇ Download PDF Report
            </button>
          </div>

          <div className="slt-card" style={{ marginBottom:24, background:dTX.noteBg, border:`1px solid ${dTX.noteBorder}` }}>
            <div style={{ fontWeight:800, fontSize:13, color:dTX.noteText, marginBottom:6 }}>⚠️ Tax Disclaimer</div>
            <div style={{ fontSize:13, color:dTX.t2125Text }}>This tool provides a summary for your accountant. Always work with a CPA for your actual tax filing. Keep all original receipts for a minimum of 6 years as required by CRA.</div>
          </div>

        </div>
      </div>
    );
  }

  // ── Date Range Picker (replaces year-only selector) ────────────────────────
  const DateRangePicker = () => (
    <div className="slt-card" style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <button onClick={() => setUseCustomRange(false)} className="slt-btn-secondary"
          style={{ flex:1, background: !useCustomRange ? TX.btnActiveBg : TX.btnInactiveBg, color: !useCustomRange ? "#fff" : TX.btnInactiveColor, borderColor: !useCustomRange ? TX.btnActiveBg : TX.btnInactiveBorder, padding:"8px" }}>
          By Year
        </button>
        <button onClick={() => setUseCustomRange(true)} className="slt-btn-secondary"
          style={{ flex:1, background: useCustomRange ? TX.btnActiveBg : TX.btnInactiveBg, color: useCustomRange ? "#fff" : TX.btnInactiveColor, borderColor: useCustomRange ? TX.btnActiveBg : TX.btnInactiveBorder, padding:"8px" }}>
          Custom Range
        </button>
      </div>
      {!useCustomRange
        ? <div><label className="slt-label">Tax Year</label>
            <select className="slt-input" value={year} onChange={e => setYear(e.target.value)}>
              {["2026","2025","2024","2023","2022"].map(y => <option key={y}>{y}</option>)}
            </select>
          </div>
        : <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <div><label className="slt-label">From</label><input type="date" className="slt-input" value={rangeStart} onChange={e => setRangeStart(e.target.value)}/></div>
            <div><label className="slt-label">To</label><input type="date" className="slt-input" value={rangeEnd} onChange={e => setRangeEnd(e.target.value)}/></div>
          </div>
      }
    </div>
  );

  const TAX_CATS = [
    { id:"fuel",           label:"Fuel & Oil",               icon:"⛽", taxLine:"Line 9220 – Fuel costs",          color:C.orange  },
    { id:"maintenance",    label:"Repairs & Maintenance",    icon:"🔧", taxLine:"Line 9270 – Repairs",             color:C.red     },
    { id:"insurance",      label:"Insurance",                icon:"🛡", taxLine:"Line 9910 – Insurance premiums",  color:C.blue    },
    { id:"permits",        label:"Licenses & Renewals",      icon:"📋", taxLine:"Line 9270 – Licences & fees",    color:'#E8962E'  },
    { id:"telephone",      label:"Telephone & Internet",     icon:"📱", taxLine:"Line 9220 – Phone / internet",   color:"#E8962E" },
    { id:"rent",           label:"Rent / Lease",             icon:"🏢", taxLine:"Line 9200 – Rent & leasing",     color:"#E8962E" },
    { id:"meals",          label:"Meals & Entertainment",    icon:"🍽", taxLine:"Line 8523 – Meals (50% rule)",   color:C.green   },
    { id:"lodging",        label:"Accommodation / Travel",   icon:"🏨", taxLine:"Line 9200 – Travel costs",       color:"#8D6E63" },
    { id:"tolls",          label:"Tolls & Parking",          icon:"🛣", taxLine:"Line 9281 – Other expenses",     color:'#E8962E'    },
    { id:"union_dues",     label:"Union / Association Dues", icon:"🤝", taxLine:"Line 9270 – Professional fees",  color:"#4B5563" },
    { id:"tools_supplies", label:"Tools & Supplies",         icon:"🧰", taxLine:"Line 9270 – Supplies",           color:C.orange  },
    { id:"safety",         label:"Safety Gear & Clothing",   icon:"🦺", taxLine:"Line 9270 – Protective clothing",color:"#EF6C00" },
    { id:"accounting",     label:"Accounting / Legal Fees",  icon:"📂", taxLine:"Line 8860 – Professional fees",  color:"#6D4C41" },
    { id:"advertising",    label:"Advertising & Marketing",  icon:"📣", taxLine:"Line 8520 – Advertising",        color:"#E8962E" },
    { id:"bank_fees",      label:"Bank & Interest Charges",  icon:"🏦", taxLine:"Line 8710 – Interest & bank",    color:"#37474F" },
    { id:"medical",        label:"Medical / Drug Plan",      icon:"💊", taxLine:"Line 9270 – Medical premiums",   color:"#D32F2F" },
    { id:"other",          label:"Other Operating",          icon:"📦", taxLine:"Line 9270 – Other expenses",     color:"#4B5563" },
  ];

  const yearExp = allExpenses.filter(e => e.date && inRange(e.date));
  const byCategory = TAX_CATS.map(cat => ({
    ...cat,
    total: yearExp.filter(e => e.category === cat.id).reduce((s, e) => s + Number(e.amount || 0), 0),
    count: yearExp.filter(e => e.category === cat.id).length,
  }));
  const grandTotal = byCategory.reduce((s, c) => s + c.total, 0);
  const mealsDeductible = byCategory.find(c => c.id === "meals")?.total * 0.5 || 0;
  const adjustedTotal = grandTotal - (byCategory.find(c => c.id === "meals")?.total * 0.5 || 0);

  // ── CRA T2125 Summary Calculations ───────────────────────────────
  const periodLoads = allLoads.filter(l => l.date && inRange(l.date) && (l.completed || Number(l.earnings||0) > 0));
  const baseEarnings = periodLoads.reduce((s,l) => s + Number(l.earnings||0), 0);
  const waitBilled = periodLoads.reduce((s,l) => {
    const wm = (Number(l.loadWaitMins)||0) + (Number(l.offloadWaitMins)||0);
    return s + wm/60*(Number(rates.companyWaitRate)||0);
  }, 0);
  const grossBusinessIncome = baseEarnings + waitBilled;
  // driverPayTotal: wages paid to drivers (including owner paying themselves as driver)
  // Must include payMyselfAsDriver loads — these are deductible salaries on T2125 line 9220
  const driverPayTotal = periodLoads.filter(l => {
    if (Number(l.driverBasePay||0) <= 0) return false;
    // Include owner-pays-self loads
    if (l.payMyselfAsDriver === true) return true;
    // Include loads assigned to external drivers
    const dUid = l.assignedDriverUid || l.addedBy || l.user_id;
    return dUid && dUid !== session.uid;
  }).reduce((s,l) => {
    const wm = (Number(l.loadWaitMins)||0) + (Number(l.offloadWaitMins)||0);
    return s + Number(l.driverBasePay||0) + wm/60*(Number(rates.driverWaitRate)||0);
  }, 0);
  const netSelfEmployment = grossBusinessIncome - adjustedTotal - driverPayTotal;
  // GST/HST — Canadian trucking is ZERO-RATED (ETA Schedule VI, Part VII)
  // Owners do NOT charge GST on loads — GST collected = $0
  // They DO claim ITCs (refunds) on eligible expenses
  const gstRate = 0.05;
  const gstCollected = 0; // Zero-rated supply — no GST charged to customers
  const fuelAmt = byCategory.find(c => c.id === "fuel")?.total || 0;
  const repairAmt = (byCategory.find(c => c.id === "maintenance")?.total || 0) + (byCategory.find(c => c.id === "repairs")?.total || 0);
  const otherEligible = Math.max(0, grandTotal - fuelAmt - repairAmt);
  const itcCredits = parseFloat(((fuelAmt + repairAmt + otherEligible) * gstRate).toFixed(2));
  const netGstOwing = parseFloat((gstCollected - itcCredits).toFixed(2)); // negative = refund

  // T2125 line items
  const t2125Lines = [
    { line:"8000", label:"Gross Business Income",            amount: grossBusinessIncome,  color:"#22C55E", bold:true },
    { line:"9270", label:"Fuel & Oil Costs",                 amount: byCategory.find(c=>c.id==="fuel")?.total||0,        color:"#E8962E" },
    { line:"9281", label:"Motor Vehicle Expenses (Maintenance)", amount: byCategory.find(c=>c.id==="maintenance")?.total||0, color:"#E8962E" },
    { line:"9130", label:"Insurance Premiums",               amount: byCategory.find(c=>c.id==="insurance")?.total||0,   color:"#E8962E" },
    { line:"9220", label:"Salaries / Wages Paid",            amount: driverPayTotal,        color:"#E8962E" },
    { line:"9368", label:"Meals (50% deductible)",           amount: (byCategory.find(c=>c.id==="meals")?.total||0)*0.5, color:"#E8962E" },
    { line:"9270", label:"Other Business Expenses",          amount: byCategory.filter(c=>!["fuel","maintenance","insurance","meals"].includes(c.id)).reduce((s,c)=>s+c.total,0), color:"#E8962E" },
    { line:"9946", label:"Net Self-Employment Income",       amount: netSelfEmployment,     color: netSelfEmployment >= 0 ? "#22C55E" : "#EF4444", bold:true },
  ];

  const buildHtml = () => {
    const ownerName = session.companyName || session.fullName || session.name || "Owner Operator";
    const generatedDate = new Date().toLocaleDateString("en-CA", { year:"numeric", month:"long", day:"numeric" });
    const itemizedSections = byCategory.filter(c => c.total > 0).map(cat => {
      const items = yearExp.filter(e => e.category === cat.id);
      const itemRows = items.map((e,i) => `<tr style="background:${i%2===0?"#fff":"#f9fafb"}"><td style="padding:6px 10px;color:#666;font-size:12px">${e.date||"—"}</td><td style="padding:6px 10px;font-size:12px">${e.description||"—"}</td><td style="padding:6px 10px;text-align:right;font-weight:600;font-size:12px">$${Number(e.amount||0).toFixed(2)}</td></tr>`).join("");
      return `<div style="margin-bottom:24px;page-break-inside:avoid"><div style="background:${cat.color}18;border-left:4px solid ${cat.color};padding:8px 14px;display:flex;justify-content:space-between;align-items:center"><div><span style="font-size:15px;margin-right:8px">${cat.icon}</span><strong style="font-size:13px;color:#1a2a3a">${cat.label}</strong><span style="font-size:11px;color:#888;margin-left:10px">${cat.taxLine}</span></div><strong style="font-size:15px;color:${cat.color}">$${cat.total.toFixed(2)}</strong></div><table style="width:100%;border-collapse:collapse;border:1px solid #e8eaed;border-top:none"><thead><tr style="background:#f1f3f5"><th style="padding:6px 10px;text-align:left;font-size:11px;color:#666;text-transform:uppercase;width:100px">Date</th><th style="padding:6px 10px;text-align:left;font-size:11px;color:#666;text-transform:uppercase">Description</th><th style="padding:6px 10px;text-align:right;font-size:11px;color:#666;text-transform:uppercase;width:90px">Amount</th></tr></thead><tbody>${itemRows}</tbody><tfoot><tr style="background:#f8f9fa;border-top:2px solid #dee2e6"><td colspan="2" style="padding:7px 10px;font-weight:800;font-size:12px">${cat.label} Subtotal${cat.id==="meals"?` (50% deductible = $${(cat.total*0.5).toFixed(2)})`:"" }</td><td style="padding:7px 10px;text-align:right;font-weight:800;font-size:13px;color:${cat.color}">$${cat.total.toFixed(2)}</td></tr></tfoot></table></div>`;
    }).join("");
    const summaryRows = [
      ...byCategory.filter(c => c.total > 0).map(c =>
        `<tr><td style="padding:8px 12px">${c.icon} ${c.label}</td><td style="padding:8px 12px;color:#666;font-size:12px">${c.taxLine}</td><td style="padding:8px 12px;text-align:center;color:#888">${c.count}</td><td style="padding:8px 12px;text-align:right;font-weight:700;color:${c.color}">$${c.total.toFixed(2)}</td></tr>`
      ),
      driverPayTotal > 0 ? `<tr><td style="padding:8px 12px">💼 Salaries / Wages Paid (Driver)</td><td style="padding:8px 12px;color:#666;font-size:12px">Line 9220</td><td style="padding:8px 12px;text-align:center;color:#888">—</td><td style="padding:8px 12px;text-align:right;font-weight:700;color:#E8962E">$${driverPayTotal.toFixed(2)}</td></tr>` : ""
    ].join("");
    return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Tax Summary ${year} — ${ownerName}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Segoe UI',Arial,sans-serif;color:#1a1a1a;background:#fff;font-size:13px;line-height:1.5}
  .page{max-width:820px;margin:0 auto;padding:32px 36px}
  @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}.no-print{display:none}.page{padding:20px 24px}}
  h2{font-size:12px;font-weight:800;color:#1C2B4A;margin:24px 0 10px;text-transform:uppercase;letter-spacing:1px;border-bottom:2px solid #E8962E;padding-bottom:5px}
  table{width:100%;border-collapse:collapse;margin-bottom:16px;font-size:13px}
  thead tr{background:#1C2B4A}
  thead th{padding:9px 12px;text-align:left;color:#fff;font-size:11px;text-transform:uppercase;letter-spacing:0.8px;font-weight:700}
  thead th.r{text-align:right}
  tbody tr:nth-child(even){background:#F9FAFB}
  tbody td{padding:9px 12px;border-bottom:1px solid #F0F0F0;vertical-align:middle}
  tfoot td{padding:11px 12px;font-weight:900;font-size:14px;border-top:2px solid #1C2B4A;background:#F5F8FF}
  .summary-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin:20px 0}
  .s-card{background:#F5F8FF;border:1.5px solid #E0E8F5;border-radius:8px;padding:14px 12px;text-align:center}
  .s-lbl{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#888;margin-bottom:6px}
  .s-val{font-size:22px;font-weight:900;color:#1C2B4A}
  .s-val.green{color:#16A34A}.s-val.red{color:#DC2626}.s-val.blue{color:#1565C0}.s-val.orange{color:#E8962E}
  .calc-box{background:#F0FDF4;border:1.5px solid #A5D6A7;border-radius:8px;padding:16px 18px;margin:20px 0}
  .calc-row{display:flex;justify-content:space-between;padding:6px 0;font-size:13px;border-bottom:1px solid rgba(0,0,0,0.05)}
  .calc-row.total{border-bottom:none;font-weight:900;font-size:15px;padding-top:12px;margin-top:4px;border-top:2px solid #4CAF50}
  .cat-header{display:flex;justify-content:space-between;align-items:center;padding:9px 14px;border-radius:6px 6px 0 0;margin-top:16px}
  .gst-box{background:#E8F5E9;border-radius:8px;border-left:4px solid #4CAF50;padding:14px 16px;margin:20px 0}
  .sig-block{margin-top:36px;padding-top:20px;border-top:2px dashed #ccc;display:grid;grid-template-columns:1fr 1fr;gap:40px}
  .sig-line{border-bottom:1px solid #333;height:40px;margin-bottom:6px}
  .sig-label{font-size:11px;color:#666}
  .warn-box{background:#FFF8E1;border:1.5px solid #FFB300;border-radius:8px;padding:14px 16px;font-size:12px;color:#7a5f00;margin-top:16px;line-height:1.6}
  .footer{margin-top:24px;padding-top:12px;border-top:1px solid #E8EAF0;font-size:10px;color:#aaa;display:flex;justify-content:space-between}
</style>
</head><body><div class="page">

  <!-- Header -->
  <div style="display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:20px;border-bottom:3px solid #E8962E;margin-bottom:24px">
    <div>
      <div style="display:inline-block;background:#E8962E;color:#fff;font-size:10px;font-weight:800;letter-spacing:1px;padding:3px 10px;border-radius:20px;text-transform:uppercase;margin-bottom:6px">Tax Export</div>
      <div style="font-size:22px;font-weight:900;color:#0A1628;margin-bottom:6px">🚛 Tax Expense Summary</div>
      <div style="font-size:12px;color:#666;line-height:1.8">
        <strong>Owner Operator:</strong> ${ownerName}<br>
        <strong>Tax Year:</strong> ${year}<br>
        <strong>Report Type:</strong> CRA T2125 / T777<br>
        <strong>Generated:</strong> ${generatedDate}
      </div>
    </div>
    <div style="text-align:right">
      <div style="font-size:11px;color:#888;margin-bottom:4px">Total Deductible</div>
      <div style="font-size:36px;font-weight:900;color:#E8962E">$${(adjustedTotal + driverPayTotal).toFixed(2)}</div>
      <div style="font-size:11px;color:#888">${yearExp.length} entries · ${year}</div>
    </div>
  </div>

  <!-- Summary Cards -->
  <h2>Financial Summary</h2>
  <div class="summary-grid">
    <div class="s-card"><div class="s-lbl">Gross Revenue</div><div class="s-val green">$${grossBusinessIncome.toFixed(2)}</div></div>
    <div class="s-card"><div class="s-lbl">Driver / Wages Paid</div><div class="s-val red">$${driverPayTotal.toFixed(2)}</div></div>
    <div class="s-card"><div class="s-lbl">Operating Expenses</div><div class="s-val red">$${grandTotal.toFixed(2)}</div></div>
    <div class="s-card"><div class="s-lbl">Net Self-Employment</div><div class="s-val ${netSelfEmployment>=0?"green":"red"}">$${netSelfEmployment.toFixed(2)}</div></div>
  </div>

  <!-- Income & Deductions Calc Box -->
  <div class="calc-box">
    <div style="font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:1px;color:#2E7D32;margin-bottom:10px">Income &amp; Deductions Breakdown</div>
    <div class="calc-row"><span>Gross Load Revenue</span><span style="color:#16A34A;font-weight:700">+ $${grossBusinessIncome.toFixed(2)}</span></div>
    ${driverPayTotal > 0 ? `<div class="calc-row"><span>Driver / Wages Paid (T4A Line 9220)</span><span style="color:#DC2626;font-weight:700">− $${driverPayTotal.toFixed(2)}</span></div>` : ""}
    <div class="calc-row"><span>Operating Expenses</span><span style="color:#DC2626;font-weight:700">− $${grandTotal.toFixed(2)}</span></div>
    ${mealsDeductible > 0 ? `<div class="calc-row" style="padding-left:16px;font-size:12px;color:#666"><span>↳ Meals adjustment (50% rule)</span><span>+ $${mealsDeductible.toFixed(2)}</span></div>` : ""}
    <div class="calc-row total"><span>Net Self-Employment Income (Line 9946)</span><span style="color:${netSelfEmployment>=0?"#16A34A":"#DC2626"}">$${netSelfEmployment.toFixed(2)}</span></div>
  </div>

  <!-- T2125 Table -->
  <h2>T2125 — Expense Deductions by Category</h2>
  <table>
    <thead><tr>
      <th>Category</th>
      <th>CRA Line</th>
      <th class="r" style="text-align:center">Entries</th>
      <th class="r">Amount</th>
    </tr></thead>
    <tbody>${summaryRows}</tbody>
    <tfoot><tr>
      <td colspan="3">NET DEDUCTIBLE TOTAL</td>
      <td style="text-align:right">$${(adjustedTotal + driverPayTotal).toFixed(2)}</td>
    </tr></tfoot>
  </table>

  <!-- GST / ITC Section -->
  <div class="gst-box">
    <div style="font-weight:800;font-size:14px;margin-bottom:8px">GST / HST — Zero-Rated Trucking (ETA Schedule VI, Part VII)</div>
    <div style="font-size:13px;color:#2E7D32;line-height:1.6">
      Canadian trucking is <strong>zero-rated</strong> — you do NOT charge GST on loads.<br>
      You DO claim ITCs (refunds) on fuel, repairs, and eligible business expenses.<br><br>
      <strong>Line 101 — Total Revenue (zero-rated):</strong> $${grossBusinessIncome.toFixed(2)}<br>
      <strong>Line 105 — GST Collected:</strong> $0.00 (zero-rated supply)<br>
      <strong>Line 106 — ITCs Claimed (5% on eligible expenses):</strong> $${((grandTotal) * 0.05).toFixed(2)}<br>
      <strong>Line 109 — Refund from CRA: $${((grandTotal) * 0.05).toFixed(2)}</strong>
    </div>
  </div>

  <!-- Itemized Expense Detail -->
  <h2>Itemized Expense Detail</h2>
  ${itemizedSections || '<p style="color:#888;font-size:13px;padding:12px 0">No expenses recorded for this year.</p>'}

  <!-- Signature Block -->
  <div class="sig-block">
    <div><div class="sig-line"></div><div class="sig-label">Owner Operator Signature &amp; Date</div></div>
    <div><div class="sig-line"></div><div class="sig-label">Accountant / CPA Signature &amp; Date</div></div>
  </div>

  <div class="warn-box">
    ⚠️ <strong>Tax Disclaimer:</strong> This report is for your accountant's reference only. Meals are subject to the 50% limitation rule. Work with a qualified CPA for your actual CRA filing. Retain all original receipts for 6 years as required by CRA.
  </div>

  <div class="footer">
    <span>TruckPilot · Confidential Tax Document</span>
    <span>Generated ${generatedDate} · Tax Year ${year}</span>
  </div>

</div></body></html>`;
  };

  const exportTax = () => {
    const html = buildHtml();
    setPreviewHtml(html);
    setShowPreview(true);
  };

  const downloadTax = () => {
    const html = buildHtml();
    // Extract body content from full HTML for the universal helper
    const bodyContent = html.match(/<body[^>]*>([\s\S]*)<\/body>/i)?.[1] || html;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `TaxSummary_${year}_${(session.fullName||session.name||"Owner").replace(/\s+/g,"_")}.html`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const years = [new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() - 2].map(y => y.toString());

  return (
    <div className="slt-page" style={{background:TX.pageBg,color:TX.text}}>
      {showPreview && (
        <div style={{position:"fixed",inset:0,zIndex:500,background:"rgba(0,0,0,0.85)",display:"flex",flexDirection:"column"}}>
          <div style={{background:'#1A1A1A',padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0,gap:10}}>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:15,color:"#fff"}}>🧾 Tax Summary {year}</div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={downloadTax} className="slt-btn-primary slt-btn-dark-text" style={{padding:"8px 14px",fontSize:12}}>⬇ Download</button>
              <button onClick={()=>setShowPreview(false)} style={{background:"rgba(255,255,255,0.15)",border:"none",color:"#fff",borderRadius:8,padding:"8px 14px",fontSize:13,cursor:"pointer",fontWeight:700}}>✕ Close</button>
            </div>
          </div>
          <iframe srcDoc={previewHtml} style={{flex:1,border:"none",background:"#fff"}} title="Tax Export Preview" />
        </div>
      )}
      <div className="slt-hero">
        <div className="slt-hero-title">🧾 Tax Export</div>
        <div className="slt-hero-sub">Auto-categorized expenses · CRA T2125 ready</div>
      </div>
      <div className="slt-container">
        <DateRangePicker />

        {/* ── Top CRA Summary Cards ── */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16 }}>
          <div className="slt-card-sm" style={{ borderTop:`4px solid ${C.green}`, background:TX.greenCardBg }}>
            <div style={{ fontSize:11, color:TX.greenCardLbl, fontWeight:800, textTransform:"uppercase", letterSpacing:0.8, marginBottom:4 }}>Gross Business Income</div>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:24, fontWeight:900, color:TX.greenCardVal }}>{fmtC(grossBusinessIncome)}</div>
          </div>
          <div className="slt-card-sm" style={{ borderTop:`4px solid ${C.red}`, background:TX.redCardBg }}>
            <div style={{ fontSize:11, color:TX.redCardLbl, fontWeight:800, textTransform:"uppercase", letterSpacing:0.8, marginBottom:4 }}>Total Deductions</div>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:24, fontWeight:900, color:TX.redCardVal }}>{fmtC(adjustedTotal + driverPayTotal)}</div>
          </div>
        </div>
        <div className="slt-card-sm" style={{ borderTop:`4px solid ${netSelfEmployment>=0?C.green:C.red}`, marginBottom:16, background: netSelfEmployment>=0?TX.greenCardBg:TX.redCardBg }}>
          <div style={{ fontSize:11, color: netSelfEmployment>=0?TX.greenCardLbl:TX.redCardLbl, fontWeight:800, textTransform:"uppercase", letterSpacing:0.8, marginBottom:4 }}>Net Self-Employment Income</div>
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:28, fontWeight:900, color: netSelfEmployment>=0?TX.greenCardVal:TX.redCardVal }}>{fmtC(netSelfEmployment)}</div>
        </div>

        {/* ── T2125 Business Income & Expenses Table ── */}
        <div className="slt-card" style={{ marginBottom:16 }}>
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:16, marginBottom:14, color:TX.sectionTitle }}>T2125 — Business Income &amp; Expenses</div>
          <div style={{ display:"grid", gridTemplateColumns:"60px 1fr auto", gap:0 }}>
            <div style={{ fontSize:11, fontWeight:800, color:TX.t2125HeaderColor, padding:"6px 0", borderBottom:`2px solid ${TX.t2125Border}` }}>LINE</div>
            <div style={{ fontSize:11, fontWeight:800, color:TX.t2125HeaderColor, padding:"6px 8px", borderBottom:`2px solid ${TX.t2125Border}` }}>DESCRIPTION</div>
            <div style={{ fontSize:11, fontWeight:800, color:TX.t2125HeaderColor, padding:"6px 0", textAlign:"right", borderBottom:`2px solid ${TX.t2125Border}` }}>AMOUNT</div>
            {t2125Lines.map((row, i) => (
              row.amount !== 0 || row.bold ? [
                <div key={`line-${i}`} style={{ fontSize:12, fontWeight:row.bold?800:600, color:row.bold?TX.t2125TextColor:TX.t2125LineColor, padding:"10px 0", borderBottom:`1px solid ${TX.t2125Border}` }}>{row.line}</div>,
                <div key={`label-${i}`} style={{ fontSize:13, fontWeight:row.bold?800:500, color:TX.t2125TextColor, padding:"10px 8px", borderBottom:`1px solid ${TX.t2125Border}` }}>{row.label}</div>,
                <div key={`amt-${i}`} style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:14, fontWeight:row.bold?900:700, color:row.color, padding:"10px 0", textAlign:"right", borderBottom:`1px solid ${TX.t2125Border}` }}>{fmtC(row.amount)}</div>,
              ] : null
            ))}
          </div>
        </div>

        {/* ── GST/HST Summary ── */}
        <div className="slt-card" style={{ marginBottom:16 }}>
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:16, marginBottom:14, color:TX.sectionTitle }}>GST / HST Summary</div>
          {[
            { label:"GST Collected on Loads (zero-rated)", amount:gstCollected, color:"#22C55E" },
            { label:"Input Tax Credits (ITC) — Refundable", amount:-itcCredits, color:"#EF4444" },
            { label:"Net GST/HST (Refund)", amount:netGstOwing, color: netGstOwing>=0?"#1D4ED8":"#22C55E", bold:true },
          ].map((row,i,arr) => (
            <div key={row.label} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom: i<arr.length-1?`1px solid ${TX.gstBorder}`:"none" }}>
              <span style={{ fontSize:13, fontWeight:row.bold?800:500, color:TX.gstTextColor }}>{row.label}</span>
              <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:15, fontWeight:900, color:row.color }}>{row.amount<0?`(${fmtC(-row.amount)})`:fmtC(row.amount)}</span>
            </div>
          ))}
          <div style={{ background:TX.gstNoteBg, border:`1px solid ${TX.gstNoteBorder}`, borderRadius:8, padding:"9px 12px", marginTop:12, fontSize:12, color:TX.gstNoteText }}>
            ℹ️ Estimates only. Consult your accountant before filing.
          </div>
        </div>

        <div className="slt-card" style={{ marginBottom: 18 }}>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
            <button className="slt-btn-primary slt-btn-dark-text" style={{ flex:1, padding:"11px 20px" }} onClick={exportTax}>👁 Preview PDF</button>
            <button className="slt-btn-secondary" style={{ flex:1, padding:"11px 18px" }} onClick={downloadTax}>⬇ Download</button>
          </div>
        </div>

        {/* ── Deductible Expense Breakdown (existing summary cards) ── */}
        <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:15, marginBottom:10, color:TX.breakdownTitle }}>Deductible Expense Breakdown</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 12, marginBottom: 20 }}>
          <div className="slt-card-sm" style={{ borderTop: `4px solid ${C.red}`, textAlign: "center" }}>
            <div style={{ fontSize: 13, color: C.textLight, fontWeight: 700, marginBottom: 4 }}>TOTAL EXPENSES</div>
            <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: 22, fontWeight: 800, color: C.red }}>{fmtC(grandTotal)}</div>
          </div>
          <div className="slt-card-sm" style={{ borderTop: `4px solid ${C.green}`, textAlign: "center" }}>
            <div style={{ fontSize: 13, color: C.textLight, fontWeight: 700, marginBottom: 4 }}>TAX DEDUCTIBLE</div>
            <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: 22, fontWeight: 800, color: C.green }}>{fmtC(adjustedTotal)}</div>
          </div>
          <div className="slt-card-sm" style={{ borderTop: `4px solid ${C.orange}`, textAlign: "center" }}>
            <div style={{ fontSize: 13, color: C.textLight, fontWeight: 700, marginBottom: 4 }}>MEALS (50%)</div>
            <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: 22, fontWeight: 800, color: C.orange }}>{fmtC(mealsDeductible)}</div>
          </div>
          <div className="slt-card-sm" style={{ borderTop: `4px solid ${C.blue}`, textAlign: "center" }}>
            <div style={{ fontSize: 13, color: C.textLight, fontWeight: 700, marginBottom: 4 }}>ENTRIES</div>
            <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: 22, fontWeight: 800, color: C.blue }}>{yearExp.length}</div>
          </div>
        </div>

        {/* Category breakdown — clickable rows */}
        <div className="slt-card">
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:16, marginBottom:14 }}>
            Expense Breakdown — {useCustomRange?`${rangeStart} → ${rangeEnd}`:year}
          </div>
          {byCategory.map(cat => {
            const catItems = yearExp.filter(e=>e.category===cat.id);
            const isOpen = expandedCat===cat.id;
            return (
              <div key={cat.id}>
                <div onClick={()=>cat.count>0&&setExpandedCat(isOpen?null:cat.id)}
                  style={{ display:"flex", alignItems:"center", gap:14, padding:"12px 0", borderBottom:`1px solid ${C.border}`, cursor:cat.count>0?"pointer":"default", background:isOpen?cat.color+"08":"transparent", borderRadius:isOpen?8:0, paddingLeft:isOpen?6:0, transition:"background 0.15s" }}>
                  <div style={{ width:38, height:38, borderRadius:9, background:cat.color+"18", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>{cat.icon}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:13.5 }}>{cat.label}</div>
                    <div style={{ fontSize:13.5, color:C.textDarkMut }}>{cat.taxLine}</div>
                    {cat.id==="meals"&&cat.total>0&&<div style={{ fontSize:13.5, color:C.orange }}>50% deductible = {fmtC(cat.total*0.5)}</div>}
                  </div>
                  <div style={{ textAlign:"right", flexShrink:0 }}>
                    <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:16, color:cat.total>0?cat.color:C.textDarkMut }}>{fmtC(cat.total)}</div>
                    <div style={{ fontSize:13, color:cat.count>0?cat.color:C.textDarkMut }}>{cat.count} entries {cat.count>0?(isOpen?"▲":"▼"):""}</div>
                  </div>
                </div>
                {isOpen&&catItems.length>0&&(
                  <div style={{ background:cat.color+"08", borderRadius:10, padding:"10px 12px", marginBottom:8, border:`1px solid ${cat.color}20` }}>
                    {catItems.map((e,i)=>(
                      <div key={e.id||i} style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", padding:"8px 0", borderBottom:i<catItems.length-1?`1px solid ${cat.color}15`:"none" }}>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:13, fontWeight:600, color:C.textDark }}>{e.description||e.note||e.merchant||cat.label}</div>
                          <div style={{ fontSize:13, color:C.textDarkMut, marginTop:2 }}>{e.date}{e.merchant?` · ${e.merchant}`:""}</div>
                          {e.source==="load"&&<span style={{ fontSize:12, color:C.blue, background:C.blueLight, borderRadius:5, padding:"1px 6px", marginTop:2, display:"inline-block" }}>🔗 From Load</span>}
                        </div>
                        <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:14, color:cat.color, marginLeft:12, flexShrink:0 }}>{fmtC(Number(e.amount||0))}</div>
                      </div>
                    ))}
                    <div style={{ display:"flex", justifyContent:"space-between", marginTop:10, paddingTop:8, borderTop:`2px solid ${cat.color}30` }}>
                      <span style={{ fontSize:12, fontWeight:800, color:cat.color }}>Subtotal — {cat.count} entries</span>
                      <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:15, color:cat.color }}>{fmtC(cat.total)}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          <div style={{ display:"flex", justifyContent:"space-between", padding:"14px 0", marginTop:4 }}>
            <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:16 }}>Adjusted Deductible Total</span>
            <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:20, color:C.green }}>{fmtC(adjustedTotal)}</span>
          </div>
        </div>

        <div className="slt-card" style={{ background: "#FFF8E1", border: "1.5px solid #FFB300" }}>
          <div style={{ fontWeight: 800, color: C.orange, marginBottom: 6 }}>⚠️ Tax Disclaimer</div>
          <div style={{ fontSize: 13, color: C.textMed }}>This tool provides a summary for your accountant. Always work with a CPA for your actual tax filing. Keep all original receipts for a minimum of 6 years as required by CRA.</div>
        </div>
      </div>
    </div>
  );
}

// ─── Emergency Tab ────────────────────────────────────────────────
// Feature 7: Emergency Roadside Help
function EmergencyTab({ goBack }) {
  const [loc, setLoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [searchType, setSearchType] = useState("mechanic");
  const [error, setError] = useState("");
  const [searched, setSearched] = useState(false);

  const SEARCH_TYPES = [
    { id: "mechanic", label: "Mechanic", icon: "🔧", query: "truck+repair+mechanic" },
    { id: "tire", label: "Tire Shop", icon: "🔄", query: "truck+tire+repair" },
    { id: "tow", label: "Tow Truck", icon: "🚨", query: "tow+truck+service" },
    { id: "rest", label: "Truck Stop", icon: "🛑", query: "truck+stop" },
    { id: "fuel", label: "Fuel", icon: "⛽", query: "diesel+fuel+station" },
    { id: "hospital", label: "Hospital", icon: "🏥", query: "hospital+emergency" },
  ];

  const find = () => {
    setLoading(true); setError(""); setResults([]);
    if (!navigator.geolocation) { setError("Geolocation not supported."); setLoading(false); return; }
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude: lat, longitude: lng } = pos.coords;
      setLoc({ lat, lng });
      const st = SEARCH_TYPES.find(s => s.id === searchType);
      try {
        const q = `[out:json][timeout:25];(node["shop"="car_repair"](around:20000,${lat},${lng});node["amenity"="fuel"](around:20000,${lat},${lng}););out body 15;`;
        const r = await fetch(`https://overpass-api.de/api/interpreter?data=${encodeURIComponent(q)}`);
        const d = await r.json();
        const s = (d.elements || []).filter(e => e.lat && e.lon).slice(0, 10).map(e => ({
          id: e.id, name: e.tags?.name || "Service Provider", lat: e.lat, lng: e.lon,
          phone: e.tags?.phone || e.tags?.["contact:phone"] || null,
          dist: Math.round(Math.sqrt(Math.pow((e.lat - lat) * 111, 2) + Math.pow((e.lon - lng) * 111 * Math.cos(lat * Math.PI / 180), 2)) * 10) / 10,
        })).sort((a, b) => a.dist - b.dist);
        setResults(s); setSearched(true);
      } catch { setError("Search failed. Tap a card below to call for help."); }
      setLoading(false);
    }, () => { setError("Location unavailable."); setLoading(false); });
  };

  const EMERGENCY_CONTACTS = [
    { name: "BCAA Roadside", phone: "1-800-222-4357", area: "BC", icon: "🆘" },
    { name: "CAA Roadside", phone: "1-800-222-4357", area: "ON/QC", icon: "🆘" },
    { name: "AMA Roadside (AB)", phone: "1-800-222-4357", area: "AB", icon: "🆘" },
    { name: "Police/Emergency", phone: "911", area: "All", icon: "🚨" },
    { name: "Transport Canada", phone: "1-800-333-0371", area: "All", icon: "📋" },
  ];

  return (
    <div className="slt-page" style={{background:"#070B14",color:"#F0F0F0"}}>
      {goBack && <BackButton onBack={goBack} label="Back" />}
      {goBack && <BackButton onBack={goBack} label="Back" />}
      <div className="slt-hero" style={{ background: `linear-gradient(135deg,#B71C1C,#D32F2F,#E53935)` }}>
        <div className="slt-hero-title">🚨 Emergency Roadside Help</div>
        <div className="slt-hero-sub">Find mechanics, tire shops, tow trucks near you</div>
      </div>
      <div className="slt-container">
        {/* Emergency contacts — always visible */}
        <div className="slt-card" style={{ borderTop: `4px solid ${C.red}`, marginBottom: 18 }}>
          <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800, fontSize: 16, color: C.red, marginBottom: 14 }}>📞 Emergency Contacts</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 10 }}>
            {EMERGENCY_CONTACTS.map(c => (
              <a key={c.name} href={`tel:${c.phone.replace(/\D/g, "")}`}
                style={{ display: "flex", alignItems: "center", gap: 10, background: C.offWhite, borderRadius: 10, padding: "12px 14px", textDecoration: "none", border: `1px solid ${C.border}`, transition: "all 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.background = "#FFEBEE"}
                onMouseLeave={e => e.currentTarget.style.background = C.offWhite}>
                <span style={{ fontSize: 22 }}>{c.icon}</span>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 13, color: C.textDark }}>{c.name}</div>
                  <div style={{ fontSize: 13, color: C.red, fontWeight: 700 }}>{c.phone}</div>
                  <div style={{ fontSize: 13, color: C.textLight }}>{c.area}</div>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Find nearby services */}
        <div className="slt-card" style={{ marginBottom: 18 }}>
          <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800, fontSize: 16, marginBottom: 14 }}>📍 Find Nearby Services</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
            {SEARCH_TYPES.map(s => (
              <button key={s.id} onClick={() => setSearchType(s.id)} className="slt-btn-secondary"
                style={{ background: searchType === s.id ? C.red : "#fff", color: searchType === s.id ? "#fff" : C.textMed, borderColor: searchType === s.id ? C.red : C.border, padding: "9px 14px" }}>
                {s.icon} {s.label}
              </button>
            ))}
          </div>
          <button onClick={find} style={{ width: "100%", padding: "14px", borderRadius: 10, border: "none", background: `linear-gradient(135deg,${C.red},#B71C1C)`, color: "#fff", fontSize: 15, fontWeight: 800, cursor: "pointer", fontFamily: "'Barlow',sans-serif" }}>
            📍 Find {SEARCH_TYPES.find(s => s.id === searchType)?.label} Near Me
          </button>
        </div>

        {loading && <div className="slt-card" style={{ textAlign: "center", padding: "32px", color: C.blue, fontWeight: 700 }}>🔍 Locating services…</div>}
        {error && <div style={{ background: "#FFEBEE", border: `1px solid ${C.red}30`, borderRadius: 12, padding: "14px 18px", color: C.red, marginBottom: 14 }}>{error}</div>}

        {results.map((r, i) => (
          <div key={r.id} className="slt-card" style={{ padding: "14px 18px", borderLeft: `4px solid ${C.red}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <div>
                <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: 15 }}>{i + 1}. {r.name}</div>
                {r.phone && <div style={{ fontSize: 13, color: C.blue, marginTop: 2 }}>📞 {r.phone}</div>}
              </div>
              <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: 18, fontWeight: 800, color: C.orange }}>{r.dist} km</div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <a href={`https://www.google.com/maps/dir/?api=1&destination=${r.lat},${r.lng}&travelmode=driving`} target="_blank" rel="noreferrer"
                className="slt-btn-primary slt-btn-dark-text" style={{ flex: 1, textAlign: "center", textDecoration: "none", padding: "9px 0", borderRadius: 9, fontSize: 13 }}>🗺 Directions</a>
              {r.phone && <a href={`tel:${r.phone.replace(/\D/g, "")}`} className="slt-btn-secondary" style={{ flex: 1, textAlign: "center", textDecoration: "none", padding: "9px 0", borderRadius: 9, fontSize: 13 }}>📞 Call</a>}
            </div>
          </div>
        ))}

        {/* Safety checklist */}
        <div className="slt-card" style={{ background: `linear-gradient(135deg,${C.navy},${C.navyMid})`, border: "none" }}>
          <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800, fontSize: 16, color: "#fff", marginBottom: 12 }}>🦺 Breakdown Safety Checklist</div>
          {["Pull completely off the road and onto the shoulder", "Turn on hazard lights immediately", "Place triangles/flares 30m, 60m, 90m behind truck", "Stay away from traffic — exit on the passenger side", "Call 911 if you feel unsafe or there's an injury", "Alert dispatch and document everything with photos"].map((tip, i) => (
            <div key={i} style={{ display: "flex", gap: 10, marginBottom: 8, alignItems: "flex-start" }}>
              <span style={{ color: C.teal, fontWeight: 800, flexShrink: 0 }}>✓</span>
              <span style={{ color: "rgba(255,255,255,0.85)", fontSize: 13 }}>{tip}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// NEW FEATURE MODULES — TruckPilot v3
// All 10 new features appended/integrated
// ═══════════════════════════════════════════════════════════════════

// ─── IFTA Tab ─────────────────────────────────────────────────────
// Feature 1: IFTA Tax Reporting
function TripSummaryModal({ load, onClose, rates, session, trucks }) {
  useEffect(()=>{
    const y=window.scrollY;
    document.body.style.position='fixed';
    document.body.style.top='-'+y+'px';
    document.body.style.width='100%';
    document.body.style.overflow='hidden';
    return ()=>{
      document.body.style.position='';
      document.body.style.top='';
      document.body.style.width='';
      document.body.style.overflow='';
      window.scrollTo(0,y);
    };
  },[]);
  const truck = trucks?.find(t => t.id === load.truckId);
  const truckLabel = truck ? `Truck ${truck.truckNumber}` : (load.manualTruckNumber || null);
  const wm = (Number(load.loadWaitMins)||0) + (Number(load.offloadWaitMins)||0);
  const loadWm = Number(load.loadWaitMins)||0;
  const offWm = Number(load.offloadWaitMins)||0;
  const drvWaitPay = wm / 60 * (Number(rates?.driverWaitRate) || 0);
  const basePay = Number(load.driverBasePay) || 0;
  const totalPay = basePay + drvWaitPay;
  const fmtTime = (t) => { if(!t) return null; const [h,m]=t.split(":"); const hr=Number(h); return `${hr>12?hr-12:hr||12}:${m} ${hr>=12?"PM":"AM"}`; };
  const fmtMins = (m) => { if(!m) return "0 min"; const h=Math.floor(m/60),mn=m%60; return h>0?`${h}h ${mn}min`:`${mn}min`; };
  const wHrs = Math.floor(wm/60); const wMins = wm%60;

  const download = () => {
    const html = `
      <div class="header"><div class="brand">${session.companyName||session.fullName||session.name||"TruckPilot"}<br><span style="font-size:13px;font-weight:400;color:#666">${session.fullName||session.name}</span></div><div><div style="font-size:20px;font-weight:800">Trip Summary</div><div style="color:#666">${load.date}${load.tmwLoadNumber?` · TMW #${load.tmwLoadNumber}`:""}</div></div></div>
      <div class="summary">
        <div class="summary-card"><div class="label">Route</div><div class="value" style="font-size:14px">${load.location||"—"}</div></div>
        <div class="summary-card"><div class="label">Total Pay</div><div class="value green">$${totalPay.toFixed(2)}</div></div>
        <div class="summary-card"><div class="label">Total Wait</div><div class="value blue">${wm>0?`${wHrs>0?wHrs+"h ":""}${wMins}min`:"None"}</div></div>
      </div>
      <table><thead><tr><th>Item</th><th>Detail</th></tr></thead><tbody>
        <tr><td>Driver</td><td>${session.fullName||session.name}</td></tr>
        ${truckLabel?`<tr><td>Truck</td><td>${truckLabel}</td></tr>`:""}
        <tr><td colspan="2" style="background:#F5F6F8;font-weight:800;color:#1C2B4A;padding:8px 12px">🏭 Loading Site</td></tr>
        ${load.appointmentTime?`<tr><td>Appointment</td><td>${fmtTime(load.appointmentTime)}</td></tr>`:""}
        ${load.time?`<tr><td>Arrived</td><td>${fmtTime(load.time)}</td></tr>`:""}
        ${load.completedTime?`<tr><td>Done Loading</td><td>${fmtTime(load.completedTime)}</td></tr>`:""}
        ${loadWm>0?`<tr><td>Load Wait</td><td>${fmtMins(loadWm)}</td></tr>`:""}
        ${(load.offloadArrivalTime||load.offloadCompletedTime||offWm)?`<tr><td colspan="2" style="background:#FFF3E0;font-weight:800;color:#E8962E;padding:8px 12px">🏗 Offloading Site</td></tr>`:""}
        ${load.offloadArrivalTime?`<tr><td>Arrived at Offload</td><td>${fmtTime(load.offloadArrivalTime)}</td></tr>`:""}
        ${load.offloadCompletedTime?`<tr><td>Done Offloading</td><td>${fmtTime(load.offloadCompletedTime)}</td></tr>`:""}
        ${offWm>0?`<tr><td>Offload Wait</td><td>${fmtMins(offWm)}</td></tr>`:""}
        <tr><td colspan="2" style="background:#F5F6F8;font-weight:800;color:#1C2B4A;padding:8px 12px">💵 Pay</td></tr>
        <tr><td>Base Pay</td><td>$${basePay.toFixed(2)}</td></tr>
        ${wm>0?`<tr><td>Wait Pay (${fmtMins(wm)})</td><td>$${drvWaitPay.toFixed(2)}</td></tr>`:""}
        <tr style="font-weight:800;background:#E8F5E9"><td>TOTAL PAY</td><td style="color:#2E7D32;font-size:15px">$${totalPay.toFixed(2)}</td></tr>
      </tbody></table>
      ${load.note?`<div style="margin-top:16px;padding:12px;background:#F7F9FC;border-radius:8px;font-size:12px;color:#666"><strong>Notes:</strong> ${load.note}</div>`:""}`;
    downloadPDF(html, `TripSummary_${load.date}_${(load.location||"trip").replace(/[^a-z0-9]/gi,"_")}`, session);
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.65)", zIndex:500, display:"flex", alignItems:"flex-end", justifyContent:"center", padding:0 }} onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{ background:"#fff", borderRadius:"20px 20px 0 0", width:"100%", maxWidth:500, maxHeight:"92vh", display:"flex", flexDirection:"column", paddingBottom:"env(safe-area-inset-bottom,16px)" }}>
        {/* Sticky Header */}
        <div style={{ background:`linear-gradient(135deg,${C.navy},#1A1A1A)`, borderRadius:"20px 20px 0 0", padding:"20px 24px 24px", flexShrink:0 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
            <div>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:20, color:"#fff" }}>🚛 Trip Summary</div>
              <div style={{ color:"rgba(255,255,255,0.7)", fontSize:13, marginTop:3 }}>{load.date} · {load.location}</div>
            </div>
            <button onClick={onClose} style={{ background:"rgba(255,255,255,0.15)", border:"none", color:"#fff", borderRadius:10, padding:"8px 12px", cursor:"pointer", fontSize:16, fontWeight:800 }}>✕ Close</button>
          </div>
          {/* Big pay amount */}
          <div style={{ marginTop:20, background:"rgba(255,255,255,0.1)", borderRadius:14, padding:"16px 20px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div style={{ color:"rgba(255,255,255,0.8)", fontSize:13 }}>Your Total Pay</div>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:32, color:"#69F0AE" }}>${totalPay.toFixed(2)}</div>
          </div>
        </div>

        <div style={{ padding:"20px 24px", overflowY:"auto", flex:1 }}>
          {/* Times strip — Loading */}
          {(load.appointmentTime||load.time||load.completedTime) && (
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:11, fontWeight:800, color:C.blue, textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>🏭 Loading Site</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, textAlign:"center" }}>
                {[["📅 Appt", fmtTime(load.appointmentTime), C.blue],["🛬 Arrived", fmtTime(load.time), C.green],["✅ Done", fmtTime(load.completedTime), C.orange]].map(([l,v,col]) => (
                  <div key={l} style={{ background:C.offWhite, borderRadius:10, padding:"10px 6px" }}>
                    <div style={{ fontSize:11, color:C.textDarkMed, fontWeight:700, marginBottom:3 }}>{l}</div>
                    <div style={{ fontSize:12, fontWeight:800, color:v?col:C.textLight }}>{v||"—"}</div>
                  </div>
                ))}
              </div>
              {loadWm>0&&<div style={{fontSize:11,color:C.blue,fontWeight:700,textAlign:"center",marginTop:4}}>⏱ Load wait: {fmtMins(loadWm)}</div>}
            </div>
          )}
          {/* Times strip — Offloading */}
          {(load.offloadArrivalTime||load.offloadCompletedTime||offWm>0) && (
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:11, fontWeight:800, color:"#E8962E", textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>🏗 Offloading Site</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, textAlign:"center" }}>
                {[["🛬 Arrived", fmtTime(load.offloadArrivalTime), C.green],["✅ Done", fmtTime(load.offloadCompletedTime), C.orange]].map(([l,v,col]) => (
                  <div key={l} style={{ background:"#FFF3E0", borderRadius:10, padding:"10px 6px" }}>
                    <div style={{ fontSize:11, color:C.textDarkMed, fontWeight:700, marginBottom:3 }}>{l}</div>
                    <div style={{ fontSize:12, fontWeight:800, color:v?col:C.textLight }}>{v||"—"}</div>
                  </div>
                ))}
              </div>
              {offWm>0&&<div style={{fontSize:11,color:"#E8962E",fontWeight:700,textAlign:"center",marginTop:4}}>⏱ Offload wait: {fmtMins(offWm)}</div>}
            </div>
          )}

          {/* Pay breakdown */}
          <div style={{ background:C.offWhite, borderRadius:12, padding:"14px 16px", marginBottom:16 }}>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:13, marginBottom:12, color:'#1A1A1A' }}>💵 Pay Breakdown</div>
            {[
              ["Base Pay", `$${basePay.toFixed(2)}`, C.blue],
              ...(wm > 0 ? [[`Wait Pay (${wHrs>0?wHrs+"h ":""}${wMins}min)`, `$${drvWaitPay.toFixed(2)}`, C.orange]] : []),
            ].map(([l,v,col]) => (
              <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:`1px solid ${C.border}` }}>
                <span style={{ fontSize:13, color:C.textDarkMed }}>{l}</span>
                <span style={{ fontSize:13, fontWeight:700, color:col }}>{v}</span>
              </div>
            ))}
            <div style={{ display:"flex", justifyContent:"space-between", padding:"10px 0 0" }}>
              <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:14 }}>TOTAL</span>
              <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:18, color:C.green }}>${totalPay.toFixed(2)}</span>
            </div>
          </div>

          {/* Details */}
          <div style={{ background:C.offWhite, borderRadius:12, padding:"14px 16px", marginBottom:20 }}>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:13, marginBottom:12, color:'#1A1A1A' }}>📋 Trip Details</div>
            {[
              ["Driver", session.fullName||session.name],
              truck && ["Truck", `Truck ${truck.truckNumber}`],
              ["Route", load.location||"—"],
              wm > 0 && ["Total Wait", `${wHrs>0?wHrs+"h ":""}${wMins} min`],
              load.note && ["Notes", load.note],
            ].filter(Boolean).map(([l,v]) => (
              <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:`1px solid ${C.border}` }}>
                <span style={{ fontSize:12, color:C.textDarkMed }}>{l}</span>
                <span style={{ fontSize:12, fontWeight:700, maxWidth:"55%", textAlign:"right" }}>{v}</span>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div style={{ display:"flex", gap:10 }}>
            <button onClick={download} className="slt-btn-primary slt-btn-dark-text" style={{ flex:1, padding:"13px" }}>⬇ Download PDF</button>
            <button onClick={() => { if(navigator.share){ navigator.share({ title:"Trip Summary", text:`Route: ${load.location} | Pay: $${totalPay.toFixed(2)} | Date: ${load.date}` }); } else { navigator.clipboard.writeText(`🚛 Trip Summary\nRoute: ${load.location}\nDate: ${load.date}\nPay: $${totalPay.toFixed(2)}\nWait: ${wm>0?`${wHrs>0?wHrs+"h ":""}${wMins}min`:"None"}`); }}} className="slt-btn-secondary" style={{ flex:1, padding:"13px" }}>📤 Share</button>
          </div>
        </div>
      </div>
    </div>
  );
}



// ─── UPGRADE MODAL ────────────────────────────────────────────────────────────
function UpgradeModal({ session, onClose, onUpgrade }) {
  useEffect(()=>{
    const y=window.scrollY;
    document.body.style.position='fixed';
    document.body.style.top='-'+y+'px';
    document.body.style.width='100%';
    document.body.style.overflow='hidden';
    return ()=>{
      document.body.style.position='';
      document.body.style.top='';
      document.body.style.width='';
      document.body.style.overflow='';
      window.scrollTo(0,y);
    };
  },[]);
  const currentPlan = session.plan || "free";
  const [selected, setSelected] = useState(currentPlan);

  const handleUpgrade = (planId) => {
    const users = getUsers();
    users[session.uid].plan = planId;
    // Set upgrade timestamp for referral tracking
    users[session.uid].planUpgradedAt = new Date().toISOString();
    users[session.uid].planHistory = [...(users[session.uid].planHistory || []), { plan: planId, date: new Date().toISOString() }];
    saveUsers(users);
    // Handle referral commission
    const refs = getReferrals();
    const referrerUid = users[session.uid].referredBy;
    if (referrerUid && planId !== "free") {
      if (!refs[referrerUid]) refs[referrerUid] = { commissions: [], totalEarned: 0, pendingPayout: 0 };
      const monthlyPrice = PLANS[planId]?.price || 0;
      const commission = (monthlyPrice * REFERRAL_COMMISSION_PCT / 100) * REFERRAL_MONTHS;
      refs[referrerUid].commissions.push({
        fromUid: session.uid,
        fromName: session.fullName,
        plan: planId,
        amount: commission,
        date: new Date().toISOString(),
        status: "pending",
      });
      refs[referrerUid].totalEarned += commission;
      refs[referrerUid].pendingPayout += commission;
      saveReferrals(refs);
    }
    onUpgrade(planId);
    onClose();
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:2000, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
      <div style={{ background:"#fff", borderRadius:20, width:"100%", maxWidth:480, maxHeight:"90vh", overflowY:"auto", boxShadow:"0 24px 64px rgba(0,0,0,0.55)" }}>
        {/* Header */}
        <div style={{ background:`linear-gradient(135deg,#0A1628,#1A1A1A)`, padding:"28px 24px 24px", borderRadius:"20px 20px 0 0", textAlign:"center" }}>
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:22, fontWeight:800, color:"#fff", marginBottom:6 }}>🚀 Upgrade TruckPilot</div>
          <div style={{ fontSize:13, color:"rgba(255,255,255,0.6)" }}>Choose the plan that fits your fleet</div>
          <div style={{ marginTop:10, background:"rgba(255,255,255,0.1)", borderRadius:8, padding:"6px 14px", display:"inline-block", fontSize:12, color:"#FFD600", fontWeight:700 }}>Current: {currentPlan==="pro"?"🚀 Pro":currentPlan==="basic"?"💼 Basic":"⭐ Beta"}</div>
        </div>
        {/* Plans */}
        <div style={{ padding:"20px 16px" }}>
          {Object.values(PLANS).map(plan => (
            <div key={plan.id} onClick={() => setSelected(plan.id)}
              style={{ border:`2.5px solid ${selected===plan.id ? plan.color : "#e8ecf0"}`, borderRadius:14, padding:"16px 18px", marginBottom:12, cursor:"pointer", transition:"all 0.2s", background: selected===plan.id ? plan.color+"11" : "#fff", position:"relative" }}>
              {plan.id === "pro" && <div style={{ position:"absolute", top:-10, right:16, background:"#E8962E", color:"#fff", fontSize:12, fontWeight:800, padding:"3px 10px", borderRadius:20, letterSpacing:1 }}>BEST VALUE</div>}
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                <div>
                  <span style={{ fontSize:20, marginRight:8 }}>{plan.emoji}</span>
                  <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:16, color:"#1A1A1A" }}>{plan.id==="pro"?(session.role==="owner"?"🚀 Owner Pro":"🚀 Fleet Pro"):plan.label}</span>
                </div>
                <div style={{ textAlign:"right" }}>
                  {plan.price === 0
                    ? <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:20, color:plan.color }}>FREE</div>
                    : <div><span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:22, color:plan.color }}>${plan.price}</span><span style={{ fontSize:13, color:"#4B5563" }}>/mo</span></div>
                  }
                </div>
              </div>
              <div style={{ fontSize:12, color:"#374151", marginBottom:10 }}>{plan.desc}</div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                {plan.features.map(f => <span key={f} style={{ fontSize:13, background:plan.color+"22", color:plan.color, padding:"3px 9px", borderRadius:20, fontWeight:600 }}>✓ {f}</span>)}
              </div>
              {currentPlan === plan.id && <div style={{ marginTop:10, fontSize:12, color:plan.color, fontWeight:700 }}>✓ Current plan</div>}
            </div>
          ))}
          {/* Action buttons */}
          <div style={{ display:"flex", gap:10, marginTop:4 }}>
            <button onClick={onClose} style={{ flex:1, padding:"12px", border:"1.5px solid #ddd", borderRadius:10, background:"#fff", cursor:"pointer", fontFamily:"'Barlow',sans-serif", fontWeight:700, fontSize:14 }}>‹ Back</button>
            <button disabled
              style={{ flex:2, padding:"12px", border:"none", borderRadius:10, background:"#ccc", color:"#fff", cursor:"not-allowed", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:14 }}>
              Coming Soon
            </button>
          </div>
          <div style={{ textAlign:"center", fontSize:12, color:"#374151", marginTop:10, fontWeight:600 }}>
            Your current plan: {currentPlan==="pro"?"🚀 Pro":currentPlan==="basic"?"💼 Basic":"⭐ Beta"}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── REFERRAL TAB ──────────────────────────────────────────────────────────────
function PlanGate({ feature, plan, onUpgrade }) {
  const needed = canAccessFeature("basic", feature) ? "basic" : "pro";
  const neededPlan = PLANS[needed];
  return (
    <div className="slt-page" style={{background:"#070B14",color:"#F0F0F0"}}>
      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:"60vh", padding:24, textAlign:"center" }}>
        <div style={{ fontSize:60, marginBottom:16 }}>🔒</div>
        <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:22, fontWeight:800, color:"#1A1A1A", marginBottom:8 }}>
          {neededPlan.emoji} {neededPlan.label} Required
        </div>
        <div style={{ fontSize:14, color:"#374151", marginBottom:24, maxWidth:300 }}>
          This feature is available on the {neededPlan.label} plan and above.
        </div>
        <div style={{ background:`${neededPlan.color}11`, border:`2px solid ${neededPlan.color}`, borderRadius:16, padding:"20px 24px", marginBottom:24, width:"100%", maxWidth:320 }}>
          <div style={{ fontWeight:700, fontSize:15, color:neededPlan.color, marginBottom:10 }}>{neededPlan.label} — ${neededPlan.price}/mo</div>
          {neededPlan.features.map(f => <div key={f} style={{ fontSize:13, color:"#0B1120", textAlign:"left", marginBottom:5 }}>✓ {f}</div>)}
        </div>
        <button onClick={onUpgrade} style={{ background:neededPlan.color, color:"#fff", border:"none", borderRadius:12, padding:"14px 32px", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:15, cursor:"pointer", width:"100%", maxWidth:320 }}>
          Upgrade to {neededPlan.label} →
        </button>
      </div>
    </div>
  );
}

// ─── MAIN APP v3 ─────────────────────────────────────────────────────────────

// ─── RESET PASSWORD SCREEN ────────────────────────────────────────────────────
function ResetPasswordScreen({ onDone }) {
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState("error");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleReset = async () => {
    if (!newPass || !confirmPass) { setMsg("Please fill in both fields."); return; }
    if (newPass.length < 6) { setMsg("Password must be at least 6 characters."); return; }
    if (newPass !== confirmPass) { setMsg("Passwords do not match."); return; }
    setLoading(true);
    const { error } = await sb.auth.updateUser({ password: newPass });
    setLoading(false);
    if (error) { setMsg(error.message); setMsgType("error"); return; }
    setDone(true);
    setTimeout(() => onDone(), 2000);
  };

  const authInput = { width:"100%", padding:"12px 15px", border:"1.5px solid rgba(255,255,255,0.15)", borderRadius:10, fontSize:14, color:"#fff", background:"rgba(255,255,255,0.07)", outline:"none", fontFamily:"'Barlow',sans-serif", marginBottom:14, boxSizing:"border-box" };
  const authLabel = { display:"block", fontSize:12.5, fontWeight:700, color:"rgba(255,255,255,0.6)", marginBottom:6, fontFamily:"'Barlow',sans-serif" };

  return (
    <div className="slt-auth-bg">
      <div style={{ width:"100%", maxWidth:440 }}>
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <div style={{ marginBottom:16 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"center" }}>
              <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:26,color:"#FFFFFF",letterSpacing:"1px"}}>TRUCK</span><span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:26,color:"#E8962E",letterSpacing:"1px"}}>PILOT</span>
            </div>
            <div style={{ fontFamily:"'Barlow',sans-serif", fontSize:12, fontWeight:700, color:"rgba(255,255,255,0.7)", letterSpacing:3, textTransform:"uppercase", marginTop:8, textAlign:"center" }}>Log Loads · Save Taxes · Stay Compliant</div>
          </div>
        </div>
        <div className="slt-auth-card">
          {done ? (
            <div style={{ textAlign:"center", padding:"20px 0" }}>
              <div style={{ fontSize:48, marginBottom:12 }}>✅</div>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:20, color:'#E8962E', marginBottom:8 }}>Password Updated!</div>
              <div style={{ fontSize:14, color:"rgba(255,255,255,0.6)" }}>Redirecting you to login...</div>
            </div>
          ) : (
            <>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:20, color:"#fff", marginBottom:6, textAlign:"center" }}>🔐 Set New Password</div>
              <div style={{ fontSize:13, color:"rgba(255,255,255,0.5)", marginBottom:24, textAlign:"center" }}>Enter your new password below</div>
              <div><label style={authLabel}>New Password</label>
              <input type="password" className="slt-input" value={newPass} onChange={e => setNewPass(e.target.value)} placeholder="Min. 6 characters" style={authInput} /></div>
              <div><label style={authLabel}>Confirm New Password</label>
              <input type="password" className="slt-input" value={confirmPass} onChange={e => setConfirmPass(e.target.value)} placeholder="Repeat your password" style={authInput} onKeyDown={e => { if(e.key==="Enter") handleReset(); }} /></div>
              {msg && <div style={{ background:msgType==="success"?"rgba(0,137,123,0.2)":"rgba(229,57,53,0.15)", border:"1px solid rgba(229,57,53,0.35)", borderRadius:9, padding:"10px 14px", color:msgType==="success"?"#80cbc4":"#ff8a80", fontSize:13, marginBottom:14, fontFamily:"'Barlow',sans-serif" }}>{msg}</div>}
              <button className="slt-btn-primary slt-btn-dark-text" onClick={handleReset} disabled={loading} style={{ width:"100%", padding:"13px", fontSize:15, borderRadius:10, opacity:loading?0.7:1 }}>
                {loading ? "⏳ Updating..." : "✅ Set New Password"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Admin Login Screen ───────────────────────────────────────────────────────
function AdminLoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const login = async () => {
    if (!email || !password) return;
    setLoading(true);
    setError("");
    const { data, error: err } = await sb.auth.signInWithPassword({ email, password });
    if (err) { setError("Invalid credentials"); setLoading(false); return; }
    const profile = await sbGetProfile(data.user.id);
    if (!profile || profile.role !== "superadmin") {
      await sb.auth.signOut();
      setError("Access denied — not an admin account");
      setLoading(false);
      return;
    }
    onLogin({ uid: data.user.id, email: data.user.email, fullName: profile.name, name: profile.name, role: "superadmin", plan: "pro", supabase: true });
    setLoading(false);
  };

  return (
    <div style={{ minHeight:"100vh", background:"linear-gradient(160deg,#0F1C31,#1C2B4A)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <GlobalCSS darkMode={false} timeTheme="afternoon" /><StaticCSS />
      <div style={{ background:"#fff", borderRadius:20, padding:36, width:"100%", maxWidth:400, boxShadow:"0 20px 60px rgba(0,0,0,0.55)" }}>
        <div style={{ textAlign:"center", marginBottom:28 }}>
          {/* TruckPilot wordmark — same as main login */}
          <div style={{ display:"inline-flex", alignItems:"baseline", gap:0, marginBottom:10 }}>
            <span style={{ fontFamily:"'Barlow Condensed','Arial Black',sans-serif", fontWeight:900, fontSize:34, color:"#1C2B4A", letterSpacing:1 }}>TRUCK</span>
            <span style={{ fontFamily:"'Barlow Condensed','Arial Black',sans-serif", fontWeight:900, fontSize:34, color:"#E8962E", letterSpacing:1 }}>PILOT</span>
          </div>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:6, marginBottom:4 }}>
            <span style={{ fontSize:14 }}>👑</span>
            <span style={{ fontSize:12, fontWeight:800, color:"#1C2B4A", letterSpacing:2, textTransform:"uppercase" }}>Admin Portal</span>
          </div>
          <div style={{ fontSize:12, color:"#6B7280" }}>Authorized personnel only</div>
        </div>
        <div style={{ marginBottom:14 }}>
          <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:6 }}>Admin Email</label>
          <input value={email} onChange={e=>setEmail(e.target.value)} type="email" placeholder="truckpilot.ca@gmail.com"
            style={{ width:"100%", padding:"12px 14px", borderRadius:10, border:"1.5px solid #ddd", fontSize:14, outline:"none", boxSizing:"border-box" }}
            onKeyDown={e=>e.key==="Enter"&&login()}/>
        </div>
        <div style={{ marginBottom:20 }}>
          <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:6 }}>Password</label>
          <input value={password} onChange={e=>setPassword(e.target.value)} type="password" placeholder="••••••••"
            style={{ width:"100%", padding:"12px 14px", borderRadius:10, border:"1.5px solid #ddd", fontSize:14, outline:"none", boxSizing:"border-box" }}
            onKeyDown={e=>e.key==="Enter"&&login()}/>
        </div>
        {error && <div style={{ background:"#FFEBEE", color:"#C62828", borderRadius:8, padding:"10px 14px", fontSize:13, marginBottom:14, fontWeight:600 }}>⚠️ {error}</div>}
        <button onClick={login} disabled={loading}
          style={{ width:"100%", padding:"14px", borderRadius:12, border:"none", background:"linear-gradient(135deg,#E8962E,#E8962E)", color:"#fff", fontWeight:800, fontSize:15, cursor:"pointer" }}>
          {loading ? "Signing in..." : "👑 Sign In as Admin"}
        </button>
        <div style={{ textAlign:"center", marginTop:16, fontSize:12, color:"#6B7280" }}>
          This page is for TruckPilot administrators only
        </div>
      </div>
    </div>
  );
}

// ─── RECURRING ROUTES TAB ─────────────────────────────────────────────────────
function RecurringRoutesTab({ session, isOwner, goBack }) {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name:"", location:"", earnings:"", driver_pay:"", billing_method:"per_load" });
  const [saving, setSaving] = useState(false);
  const ownerUid = session.ownerUid || session.uid;
  useEffect(() => { sbGetRecurringRoutes(ownerUid).then(d => { setRoutes(d); setLoading(false); }); }, [ownerUid]);
  const openNew = () => { setForm({ name:"", location:"", earnings:"", driver_pay:"", billing_method:"per_load" }); setEditing(null); setShowForm(true); };
  const openEdit = (r) => { setForm({ name:r.name, location:r.location, earnings:r.earnings||"", driver_pay:r.driver_pay||"", billing_method:r.billing_method||"per_load" }); setEditing(r.id); setShowForm(true); };
  const save = async () => {
    if (!form.name.trim() || !form.location.trim()) return alert("Name and route are required.");
    setSaving(true);
    await sbSaveRecurringRoute({ id: editing||undefined, owner_uid: ownerUid, ...form, earnings: Number(form.earnings)||0, driver_pay: Number(form.driver_pay)||0 });
    setRoutes(await sbGetRecurringRoutes(ownerUid)); setShowForm(false); setSaving(false);
  };
  const del = async (id) => {
    if (!window.confirm("Delete this route template?")) return;
    await sbDeleteRecurringRoute(id); setRoutes(prev => prev.filter(r => r.id !== id));
  };
  return (
    <div className="slt-page" style={{background:"#070B14",color:"#F0F0F0"}}>
      <div className="slt-hero"><div style={{fontSize:10,fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"rgba(255,255,255,0.35)",marginBottom:3}}>TRUCKPILOT</div><div className="slt-hero-title">🔁 Recurring Routes</div><div className="slt-hero-sub">Save route templates to fill load forms instantly</div></div>
      <div className="slt-container">
        <button onClick={openNew} className="slt-btn-primary slt-btn-dark-text" style={{width:"100%",marginBottom:16}}>+ Add Route Template</button>
        {showForm && (
          <div className="slt-card" style={{marginBottom:16,border:"2px solid #E8962E"}}>
            <div style={{fontWeight:800,fontSize:15,marginBottom:14,color:"#E8962E"}}>{editing?"✏️ Edit Route":"➕ New Route Template"}</div>
            {[{key:"name",label:"Template Name",placeholder:"e.g. CNRL → Heartland Daily"},{key:"location",label:"Route (From → To)",placeholder:"e.g. CNRL → Heartland"},{key:"earnings",label:"Company Earnings ($)",placeholder:"0.00",type:"number"},{key:"driver_pay",label:"Driver Pay ($)",placeholder:"0.00",type:"number"}].map(({key,label,placeholder,type})=>(
              <div key={key} style={{marginBottom:10}}><label className="slt-label">{label}</label><input type={type||"text"} value={form[key]} placeholder={placeholder} onChange={e=>setForm(p=>({...p,[key]:e.target.value}))} className="slt-input"/></div>
            ))}
            <div style={{marginBottom:14}}><label className="slt-label">Billing Method</label><select value={form.billing_method} onChange={e=>setForm(p=>({...p,billing_method:e.target.value}))} className="slt-input"><option value="per_load">Per Load</option><option value="per_hour">Per Hour</option><option value="per_cubic">Per Cubic</option></select></div>
            <div style={{display:"flex",gap:10}}><button onClick={save} disabled={saving} className="slt-btn-primary slt-btn-dark-text" style={{flex:2}}>{saving?"Saving...":"💾 Save Template"}</button><button onClick={()=>setShowForm(false)} className="slt-btn-ghost" style={{flex:1}}>Cancel</button></div>
          </div>
        )}
        {loading && <div style={{textAlign:"center",padding:40,color:"#999"}}>Loading...</div>}
        {!loading && routes.length===0 && <div className="slt-card" style={{textAlign:"center",padding:40}}><div style={{fontSize:40,marginBottom:12}}>🔁</div><div style={{fontWeight:700,marginBottom:6}}>No route templates yet</div><div style={{fontSize:13,color:"#999"}}>Add your common routes to speed up load logging</div></div>}
        {routes.map(r=>(
          <div key={r.id} className="slt-card" style={{marginBottom:12}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
              <div style={{flex:1}}>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:17,color:"#E8962E",marginBottom:4}}>{r.name}</div>
                <div style={{fontSize:13,color:"#374151",marginBottom:4}}>📍 {r.location}</div>
                <div style={{display:"flex",gap:12,fontSize:12,color:"#4B5563"}}>
                  {r.earnings>0&&<span>💰 ${r.earnings} company</span>}
                  {r.driver_pay>0&&<span>🧑‍✈️ ${r.driver_pay} driver</span>}
                  <span>📋 {r.billing_method}</span>
                </div>
              </div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>openEdit(r)} style={{padding:"6px 12px",borderRadius:8,border:"1px solid #E8962E",background:"#fff",color:"#E8962E",fontWeight:700,fontSize:12,cursor:"pointer"}}>✏️</button>
                <button onClick={()=>del(r.id)} style={{padding:"6px 12px",borderRadius:8,border:"1px solid #EF4444",background:"#fff",color:"#EF4444",fontWeight:700,fontSize:12,cursor:"pointer"}}>🗑</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── DRIVER RATINGS TAB ───────────────────────────────────────────────────────
function FuelLogTab2({ session, trucks, goBack }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({date:"",truck_number:"",litres:"",price_per_litre:"",total:"",odometer:"",location:"",notes:"",business_name:"",receipt:"",jurisdiction:"AB"});
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState("");
  const [receiptPreview, setReceiptPreview] = useState("");
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [viewReceiptUrl, setViewReceiptUrl] = useState(null);
  const todayStr2 = () => new Date().toISOString().slice(0,10);

  useEffect(() => { sbGetFuelLog(session.uid).then(d=>{setEntries(d);setLoading(false);}); }, [session.uid]);

  const calcTotal = (l,p) => ((parseFloat(l)||0)*(parseFloat(p)||0)).toFixed(2);

  const openNew = () => {
    setEditingId(null);
    setForm({date:todayStr2(),truck_number:"",litres:"",price_per_litre:"",total:"",odometer:"",location:"",notes:"",business_name:"",receipt:"",jurisdiction:"AB"});
    setReceiptPreview(""); setScanError("");
    setShowForm(true);
  };

  const openEdit = (e) => {
    setEditingId(e.id);
    setForm({date:e.date||"",truck_number:e.truck_number||"",litres:e.litres||"",price_per_litre:e.price_per_litre||"",total:e.total||"",odometer:e.odometer||"",location:e.location||"",notes:e.notes||"",business_name:e.business_name||"",receipt:e.receipt||"",jurisdiction:e.jurisdiction||"AB"});
    setReceiptPreview(e.receipt||"");
    setScanError(""); setSelectedEntry(null); setShowForm(true);
  };

  const scanReceipt = async (base64) => {
    setScanning(true); setScanError("");
    try {
      const imageBase64 = base64.split(",")[1];
      const mediaType = base64.split(";")[0].split(":")[1] || "image/jpeg";
      const resp = await fetch("/api/scan-receipt", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          image: imageBase64, 
          mediaType,
          prompt: 'Extract fuel receipt info. Return ONLY JSON: {"business_name":"","date":"YYYY-MM-DD","litres":0,"price_per_litre":0,"total":0,"location":"","odometer":""} — litres = total litres/gallons pumped, price_per_litre = price per litre/gallon shown on receipt. Use null for missing fields.'
        })
      });
      if (!resp.ok) throw new Error("Server error " + resp.status);
      const parsed = await resp.json();
      if (parsed.error) throw new Error(parsed.error);
      setForm(p => ({
        ...p,
        business_name: parsed.business_name || parsed.merchant || p.business_name,
        date:          parsed.date          || p.date,
        litres:        parsed.litres        ? String(parsed.litres)        : p.litres,
        price_per_litre: parsed.price_per_litre || parsed.pricePerLitre
                       ? String(Number(parsed.price_per_litre || parsed.pricePerLitre).toFixed(3))
                       : p.price_per_litre,
        total:         parsed.total || parsed.amount
                       ? String(parsed.total || parsed.amount)
                       : calcTotal(parsed.litres || p.litres, parsed.price_per_litre || p.price_per_litre),
        location:      parsed.location      || p.location,
        receipt:       base64,
      }));
    } catch(e) {
      console.error("Fuel scan error:", e);
      setScanError("Could not read receipt: " + (e.message||"Unknown error") + ". Please fill in manually.");
    }
    setScanning(false);
  };
  const handlePhoto = (e) => {
    const file = e.target.files?.[0]; if(!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => { setReceiptPreview(ev.target.result); scanReceipt(ev.target.result); };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.date||!form.litres) return alert("Date and litres are required.");
    setSaving(true);
    const entry = {
      ...(editingId?{id:editingId}:{}),
      user_id:session.uid,
      date:form.date, truck_number:form.truck_number, business_name:form.business_name,
      litres:parseFloat(form.litres), price_per_litre:parseFloat(form.price_per_litre)||0,
      total:parseFloat(form.total)||parseFloat(calcTotal(form.litres,form.price_per_litre)),
      odometer:form.odometer?parseFloat(form.odometer):null,
      location:form.location, notes:form.notes, receipt:form.receipt||null,
      jurisdiction: form.jurisdiction || "AB",
    };
    await sbSaveFuelEntry(entry);

    // Auto-sync to IFTA localStorage so fuel log entries appear in IFTA tab
    const iftaKey = `tp-ifta-${session.uid}`;
    const iftaEntries = JSON.parse(localStorage.getItem(iftaKey) || "[]");
    const iftaId = `fuellog-ifta-${editingId || entry.date + "-" + entry.litres}`;
    const entryDate = new Date(entry.date);
    const entryQuarter = `Q${Math.floor(entryDate.getMonth() / 3) + 1}-${entryDate.getFullYear()}`;
    const iftaEntry = {
      id: iftaId,
      date: entry.date,
      jurisdiction: entry.jurisdiction,
      km: 0,
      fuelLitres: entry.litres,
      fuelCost: entry.total,
      note: `Auto: ${entry.business_name || entry.location || "Fuel Log"}`,
      quarter: entryQuarter,
      fromFuelLog: true,
    };
    const updatedIfta = iftaEntries.filter(e => e.id !== iftaId);
    updatedIfta.unshift(iftaEntry);
    localStorage.setItem(iftaKey, JSON.stringify(updatedIfta));
    const expId = "fuellog-"+(editingId||entry.date+"-"+entry.litres+"-"+session.uid);
    const fuelExpData = {
      id:expId, category:"fuel", source:"fuel_log",
      amount:entry.total, date:entry.date,
      description:"Fuel - "+(entry.business_name||entry.location||"Fuel Station")+" - "+entry.litres+"L @ $"+(entry.price_per_litre||0).toFixed(3)+"/L",
      merchant:entry.business_name||entry.location||"Fuel Station",
      taxCategory:"Line 9220", taxLabel:"Fuel & Oil",
      receiptUrl:entry.receipt||null, ownerExpense:true,
      truck_number:entry.truck_number||null,
      litres: entry.litres, price_per_litre: entry.price_per_litre||0,
    };
    await sbSaveExpense(fuelExpData, session.uid);
    // No fleet mirroring — fuel log stays with this user only
    setEntries(await sbGetFuelLog(session.uid));
    setShowForm(false); setSaving(false); setEditingId(null);
  };

  const del = async (id) => {
    if(!window.confirm("Delete this fuel entry?"))return;
    await sbDeleteFuelEntry(id);
    // Also delete the synced expense entry
    const expId = "fuellog-"+id;
    await sbDeleteExpense(expId).catch(()=>{});
    // Remove from IFTA localStorage
    const iftaKey = `tp-ifta-${session.uid}`;
    const iftaEntries = JSON.parse(localStorage.getItem(iftaKey)||"[]");
    localStorage.setItem(iftaKey, JSON.stringify(iftaEntries.filter(e=>e.id!==`fuellog-ifta-${id}`)));
    setEntries(prev=>prev.filter(e=>e.id!==id));
    setSelectedEntry(null);
  };

  const totalL=entries.reduce((a,b)=>a+(b.litres||0),0);
  const totalC=entries.reduce((a,b)=>a+(b.total||0),0);

  return (
    <div className="slt-page" style={{background:"#070B14",color:"#F0F0F0"}}>
      <div className="slt-hero"><div style={{fontSize:10,fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"rgba(255,255,255,0.35)",marginBottom:3}}>TRUCKPILOT</div><div className="slt-hero-title">Fuel Log</div><div className="slt-hero-sub">Track fuel - syncs to Tax, IFTA and Reports</div></div>
      <div className="slt-container">
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:16}}>
          {[{label:"Total Litres",value:totalL.toFixed(0)+"L",color:"#E8962E"},{label:"Total Cost",value:"$"+totalC.toFixed(2),color:"#166534"},{label:"Avg $/L",value:totalL>0?"$"+(totalC/totalL).toFixed(3):"--",color:"#B45309"}].map(function(s){return(
            <div key={s.label} className="slt-card" style={{textAlign:"center",padding:"14px 8px"}}><div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:20,color:s.color}}>{s.value}</div><div style={{fontSize:13,color:"#999",marginTop:2}}>{s.label}</div></div>
          );})
          }
        </div>
        <button onClick={openNew} className="slt-btn-primary slt-btn-dark-text" style={{width:"100%",marginBottom:16}}>+ Log Fuel</button>
        {showForm&&(
          <div className="slt-card" style={{marginBottom:16,border:"2px solid #E8962E"}}>
            <div style={{fontWeight:800,fontSize:15,marginBottom:14,color:"#E8962E"}}>{editingId?"Edit":"New"} Fuel Entry</div>
            <div style={{display:"flex",gap:10,marginBottom:12}}>
              <label style={{flex:1,padding:"10px 14px",borderRadius:10,border:"1.5px solid #166534",background:"#F0FDF4",cursor:"pointer",textAlign:"center",fontSize:13,fontWeight:700,color:"#166534"}}>
                Camera
                <input type="file" accept="image/*" capture="environment" onChange={handlePhoto} style={{display:"none"}}/>
              </label>
              <label style={{flex:1,padding:"10px 14px",borderRadius:10,border:"1.5px solid #E8962E",background:"#F5F6F8",cursor:"pointer",textAlign:"center",fontSize:13,fontWeight:700,color:"#E8962E"}}>
                Upload Receipt
                <input type="file" accept="image/*,application/pdf" onChange={handlePhoto} style={{display:"none"}}/>
              </label>
            </div>
            {receiptPreview&&receiptPreview.startsWith("data:image")&&(
              <div style={{marginBottom:12,borderRadius:8,overflow:"hidden",border:"1px solid #ddd",maxHeight:160,position:"relative"}}>
                <img src={receiptPreview} alt="Receipt" style={{width:"100%",objectFit:"cover",maxHeight:160}}/>
                {scanning&&<div style={{position:"absolute",inset:0,background:"rgba(36,59,110,0.85)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:8}}><div style={{fontSize:28}}>AI</div><div style={{color:"#fff",fontWeight:700,fontSize:13}}>Reading receipt...</div></div>}
              </div>
            )}
            {scanning&&!receiptPreview&&<div style={{marginBottom:10,padding:"10px 14px",borderRadius:10,background:"#F5F6F8",fontSize:13,fontWeight:600,color:"#E8962E"}}>Reading receipt with AI...</div>}
            {scanError&&<div style={{marginBottom:8,fontSize:12,color:"#EF4444",fontWeight:600}}>{scanError}</div>}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
              <div><label className="slt-label">Date</label><input type="date" value={form.date} onChange={e=>setForm(p=>({...p,date:e.target.value}))} className="slt-input"/></div>
              <div><label className="slt-label">Truck #</label><input value={form.truck_number} placeholder="T-247" onChange={e=>setForm(p=>({...p,truck_number:e.target.value}))} className="slt-input"/></div>
              <div><label className="slt-label">Litres</label><input type="number" value={form.litres} placeholder="0.0" onChange={e=>setForm(p=>({...p,litres:e.target.value,total:calcTotal(e.target.value,p.price_per_litre)}))} className="slt-input"/></div>
              <div><label className="slt-label">Price/L ($)</label><input type="number" step="0.001" value={form.price_per_litre} placeholder="1.500" onChange={e=>setForm(p=>({...p,price_per_litre:e.target.value,total:calcTotal(p.litres,e.target.value)}))} className="slt-input"/></div>
              <div><label className="slt-label">Total ($)</label><input type="number" value={form.total} placeholder="0.00" onChange={e=>setForm(p=>({...p,total:e.target.value}))} className="slt-input"/></div>
              <div><label className="slt-label">Odometer (km)</label><input type="number" value={form.odometer} placeholder="Optional" onChange={e=>setForm(p=>({...p,odometer:e.target.value}))} className="slt-input"/></div>
            </div>
            <div style={{marginBottom:10}}><label className="slt-label">Business Name</label><input value={form.business_name} placeholder="e.g. Petro-Canada" onChange={e=>setForm(p=>({...p,business_name:e.target.value}))} className="slt-input"/></div>
            <div style={{marginBottom:10}}><label className="slt-label">Location</label><input value={form.location} placeholder="e.g. Fort McMurray" onChange={e=>setForm(p=>({...p,location:e.target.value}))} className="slt-input"/></div>
            <div style={{marginBottom:10}}>
              <label className="slt-label">Jurisdiction (Province/State)</label>
              <select value={form.jurisdiction} onChange={e=>setForm(p=>({...p,jurisdiction:e.target.value}))} className="slt-input">
                <optgroup label="🇨🇦 Canada">{["AB","BC","MB","NB","NL","NS","NT","NU","ON","PE","QC","SK","YT"].map(p=><option key={p} value={p}>{p}</option>)}</optgroup>
                <optgroup label="🇺🇸 United States">{["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"].map(s=><option key={s} value={s}>{s}</option>)}</optgroup>
              </select>
            </div>
            <div style={{marginBottom:14}}><label className="slt-label">Notes</label><input value={form.notes} placeholder="Optional" onChange={e=>setForm(p=>({...p,notes:e.target.value}))} className="slt-input"/></div>
            <div style={{background:"#F5F6F8",borderRadius:8,padding:"8px 12px",marginBottom:12,fontSize:12,color:"#E8962E",fontWeight:700}}>Syncs to Tax Report (Line 9220), IFTA and Financial Reports</div>
            <div style={{display:"flex",gap:10}}>
              <button onClick={save} disabled={saving} className="slt-btn-primary slt-btn-dark-text" style={{flex:2}}>{saving?"Saving...":"Save"}</button>
              <button onClick={function(){setShowForm(false);setEditingId(null);}} className="slt-btn-ghost" style={{flex:1}}>Cancel</button>
            </div>
          </div>
        )}
        {loading&&<div style={{textAlign:"center",padding:40,color:"#999"}}>Loading...</div>}
        {!loading&&entries.length===0&&!showForm&&<div className="slt-card" style={{textAlign:"center",padding:40}}><div style={{fontSize:40,marginBottom:12}}>F</div><div style={{fontWeight:700}}>No fuel entries yet</div><div style={{fontSize:13,color:"#999",marginTop:4}}>Tap Log Fuel or scan a receipt</div></div>}
        {entries.map(function(e){return(
          <div key={e.id} className="slt-card" style={{marginBottom:10,cursor:"pointer",borderLeft:"4px solid #E8962E"}} onClick={function(){setSelectedEntry(e);}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
              <div style={{flex:1}}>
                <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:4,flexWrap:"wrap"}}>
                  <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:18,color:"#E8962E"}}>${(e.total||0).toFixed(2)}</span>
                  {e.truck_number&&<span style={{fontSize:12,background:"#F5F6F8",color:"#E8962E",padding:"2px 8px",borderRadius:20,fontWeight:700}}>{e.truck_number}</span>}
                  {e.receipt&&<span style={{fontSize:13,background:"#E8F5E9",color:"#166534",padding:"2px 8px",borderRadius:20,fontWeight:700}}>Receipt</span>}
                </div>
                {e.business_name&&<div style={{fontSize:13,fontWeight:700,color:"#E8962E"}}>{e.business_name}</div>}
                <div style={{fontSize:13,color:"#374151"}}>{e.litres}L @ ${(e.price_per_litre||0).toFixed(3)}/L</div>
                <div style={{fontSize:12,color:"#999",marginTop:2}}>{e.date}{e.location?" - "+e.location:""}{e.odometer?" - "+e.odometer+"km":""}</div>
              </div>
              <span style={{color:"#6B7280",fontSize:18,marginLeft:8}}>></span>
            </div>
          </div>
        );})}
      </div>
      {selectedEntry&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:400,display:"flex",alignItems:"flex-end",justifyContent:"center"}} onClick={function(){setSelectedEntry(null);}}>
          <div style={{background:"#fff",borderRadius:"18px 18px 0 0",width:"100%",maxWidth:600,maxHeight:"95vh",overflowY:"auto"}} onClick={function(e){e.stopPropagation();}}>
            <div style={{padding:"14px 20px",borderBottom:"1px solid #eee",display:"flex",justifyContent:"space-between",alignItems:"center",position:"sticky",top:0,background:"#fff",zIndex:1}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:17,color:"#E8962E"}}>Fuel Entry</div>
              <button onClick={function(){setSelectedEntry(null);}} style={{background:"#F5F6F8",border:"none",borderRadius:20,padding:"6px 12px",fontSize:13,cursor:"pointer"}}>X</button>
            </div>
            <div style={{padding:"16px 20px"}}>
              {selectedEntry.receipt&&selectedEntry.receipt.startsWith("data:image")&&(
                <div style={{marginBottom:16,borderRadius:12,overflow:"hidden",border:"1px solid #eee",cursor:"pointer"}} onClick={function(){setViewReceiptUrl(selectedEntry.receipt);}}>
                  <img src={selectedEntry.receipt} alt="Receipt" style={{width:"100%",objectFit:"cover",maxHeight:200}}/>
                  <div style={{padding:"6px 12px",background:"#F5F6F8",fontSize:12,fontWeight:700,color:"#E8962E",textAlign:"center"}}>Tap to view full receipt</div>
                </div>
              )}
              <div style={{background:"#F0FDF4",borderRadius:11,padding:14,marginBottom:16,border:"1.5px solid #E8962E"}}>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:26,color:"#E8962E",marginBottom:4}}>${(selectedEntry.total||0).toFixed(2)}</div>
                {selectedEntry.business_name&&<div style={{fontSize:14,fontWeight:700,color:"#E8962E"}}>{selectedEntry.business_name}</div>}
              </div>
              {[["Date",selectedEntry.date],["Litres",selectedEntry.litres+"L"],["Price/L","$"+(selectedEntry.price_per_litre||0).toFixed(3)+"/L"],["Truck",selectedEntry.truck_number||"--"],["Jurisdiction",selectedEntry.jurisdiction||"--"],["Location",selectedEntry.location||"--"],["Odometer",selectedEntry.odometer?selectedEntry.odometer+" km":"--"],["Notes",selectedEntry.notes||"--"]].map(function(row){return(
                <div key={row[0]} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid #f0f0f0",fontSize:13}}>
                  <span style={{color:"#374151"}}>{row[0]}</span>
                  <span style={{fontWeight:700,color:"#E8962E"}}>{row[1]}</span>
                </div>
              );})}
              <div style={{background:"#F5F6F8",borderRadius:8,padding:"8px 12px",marginTop:12,fontSize:12,color:"#E8962E",fontWeight:700}}>Synced to Tax (Line 9220) - IFTA - Financial Reports</div>
              <div style={{display:"flex",gap:10,marginTop:16}}>
                <button onClick={function(){openEdit(selectedEntry);}} className="slt-btn-primary slt-btn-dark-text" style={{flex:2}}>Edit</button>
                <button onClick={function(){del(selectedEntry.id);}} style={{flex:1,padding:"12px",borderRadius:10,border:"1.5px solid #EF4444",background:"#fff",color:"#EF4444",fontWeight:700,cursor:"pointer",fontSize:13}}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {viewReceiptUrl&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.95)",zIndex:500,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}} onClick={function(){setViewReceiptUrl(null);}}>
          <button onClick={function(){setViewReceiptUrl(null);}} style={{position:"absolute",top:20,right:20,background:"rgba(255,255,255,.15)",border:"none",color:"#fff",borderRadius:20,padding:"8px 16px",fontSize:14,fontWeight:700,cursor:"pointer"}}>Close</button>
          <img src={viewReceiptUrl} alt="Receipt" style={{maxWidth:"95vw",maxHeight:"85vh",objectFit:"contain",borderRadius:12}} onClick={function(e){e.stopPropagation();}}/>
          <a href={viewReceiptUrl} download="fuel-receipt.jpg" style={{marginTop:16,background:"#E8962E",color:"#1C1C1E",padding:"10px 24px",borderRadius:20,textDecoration:"none",fontWeight:700,fontSize:14}}>Download</a>
        </div>
      )}
    </div>
  );
}

// ─── DOCUMENT EXPIRY TAB ──────────────────────────────────────────────────────
function DocExpiryTab({ session, isOwner, goBack }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({name:"",type:"license",expiry_date:"",notes:""});
  const [saving, setSaving] = useState(false);
  const ownerUid = session.ownerUid||session.uid;
  useEffect(() => {
    const fn=isOwner?sbGetFleetDocuments:sbGetDriverDocuments;
    fn(isOwner?ownerUid:session.uid).then(d=>{setDocs(d);setLoading(false);});
  }, [session.uid]);
  const daysUntil = (d) => Math.ceil((new Date(d)-new Date())/(1000*60*60*24));
  const getStatus = (d) => { const days=daysUntil(d); if(days<0)return{label:"EXPIRED",color:"#EF4444",bg:"#FFF0F0"}; if(days<=30)return{label:`${days}d left`,color:"#B45309",bg:"#FFF8E1"}; if(days<=90)return{label:`${days}d left`,color:"#166534",bg:"#F0FDF4"}; return{label:`${days}d left`,color:"#E8962E",bg:"#F5F6F8"}; };
  const save = async () => {
    if (!form.name.trim()||!form.expiry_date) return alert("Name and expiry date are required.");
    setSaving(true);
    await sbSaveDriverDocument({user_id:session.uid,owner_uid:ownerUid,...form});
    const fn=isOwner?sbGetFleetDocuments:sbGetDriverDocuments;
    setDocs(await fn(isOwner?ownerUid:session.uid)); setShowForm(false); setSaving(false);
    setForm({name:"",type:"license",expiry_date:"",notes:""});
  };
  const del = async (id) => { if(!window.confirm("Delete?"))return; await sbDeleteDriverDocument(id); setDocs(prev=>prev.filter(d=>d.id!==id)); };
  const expired=docs.filter(d=>daysUntil(d.expiry_date)<0);
  const soon=docs.filter(d=>{const x=daysUntil(d.expiry_date);return x>=0&&x<=30;});
  const typeIcons={license:"🪪",insurance:"🛡",permit:"📄",registration:"🚛",medical:"🏥",other:"📋"};
  return (
    <div className="slt-page" style={{background:"#070B14",color:"#F0F0F0"}}>
      <div className="slt-hero"><div style={{fontSize:10,fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"rgba(255,255,255,0.35)",marginBottom:3}}>TRUCKPILOT</div><div className="slt-hero-title">📋 Document Expiry</div><div className="slt-hero-sub">Track licenses, insurance and permits</div></div>
      <div className="slt-container">
        {expired.length>0&&<div style={{background:"#FFF0F0",border:"2px solid #EF4444",borderRadius:14,padding:14,marginBottom:14}}><div style={{fontWeight:800,color:"#EF4444",marginBottom:8}}>🚨 {expired.length} Expired</div>{expired.map(d=><div key={d.id} style={{fontSize:13,color:"#EF4444",marginBottom:2}}>• {d.name}</div>)}</div>}
        {soon.length>0&&<div style={{background:"#F5F6F8",border:"2px solid #B45309",borderRadius:14,padding:14,marginBottom:14}}><div style={{fontWeight:800,color:"#B45309",marginBottom:8}}>⚠️ {soon.length} Expiring Soon</div>{soon.map(d=><div key={d.id} style={{fontSize:13,color:"#B45309",marginBottom:2}}>• {d.name} — {daysUntil(d.expiry_date)} days</div>)}</div>}
        <button onClick={()=>setShowForm(true)} className="slt-btn-primary slt-btn-dark-text" style={{width:"100%",marginBottom:16}}>+ Add Document</button>
        {showForm&&(
          <div className="slt-card" style={{marginBottom:16,border:"2px solid #E8962E"}}>
            <div style={{fontWeight:800,fontSize:15,marginBottom:14,color:"#E8962E"}}>📋 New Document</div>
            <div style={{marginBottom:10}}><label className="slt-label">Document Name</label><input value={form.name} placeholder="e.g. Driver's License" onChange={e=>setForm(p=>({...p,name:e.target.value}))} className="slt-input"/></div>
            <div style={{marginBottom:10}}><label className="slt-label">Type</label><select value={form.type} onChange={e=>setForm(p=>({...p,type:e.target.value}))} className="slt-input"><option value="license">🪪 Driver's License</option><option value="insurance">🛡 Insurance</option><option value="permit">📄 Permit</option><option value="registration">🚛 Registration</option><option value="medical">🏥 Medical Certificate</option><option value="other">📋 Other</option></select></div>
            <div style={{marginBottom:10}}><label className="slt-label">Expiry Date</label><input type="date" value={form.expiry_date} onChange={e=>setForm(p=>({...p,expiry_date:e.target.value}))} className="slt-input"/></div>
            <div style={{marginBottom:14}}><label className="slt-label">Notes</label><input value={form.notes} placeholder="Optional" onChange={e=>setForm(p=>({...p,notes:e.target.value}))} className="slt-input"/></div>
            <div style={{display:"flex",gap:10}}><button onClick={save} disabled={saving} className="slt-btn-primary slt-btn-dark-text" style={{flex:2}}>{saving?"Saving...":"💾 Save"}</button><button onClick={()=>setShowForm(false)} className="slt-btn-ghost" style={{flex:1}}>Cancel</button></div>
          </div>
        )}
        {loading&&<div style={{textAlign:"center",padding:40,color:"#999"}}>Loading...</div>}
        {!loading&&docs.length===0&&!showForm&&<div className="slt-card" style={{textAlign:"center",padding:40}}><div style={{fontSize:40,marginBottom:12}}>📋</div><div style={{fontWeight:700}}>No documents tracked yet</div><div style={{fontSize:13,color:"#999",marginTop:6}}>Add your license, insurance and permits to get expiry alerts</div></div>}
        {docs.map(d=>{const status=getStatus(d.expiry_date);return(
          <div key={d.id} className="slt-card" style={{marginBottom:10,borderLeft:`4px solid ${status.color}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}><span>{typeIcons[d.type]||"📋"}</span><span style={{fontWeight:700,fontSize:15}}>{d.name}</span><span style={{fontSize:13,fontWeight:800,padding:"2px 8px",borderRadius:20,background:status.bg,color:status.color}}>{status.label}</span></div>
                <div style={{fontSize:12,color:"#999"}}>Expires: {d.expiry_date}</div>
                {d.notes&&<div style={{fontSize:12,color:"#4B5563",marginTop:2,fontStyle:"italic"}}>{d.notes}</div>}
              </div>
              <button onClick={()=>del(d.id)} style={{background:"none",border:"none",color:"#EF4444",cursor:"pointer",fontSize:18}}>🗑</button>
            </div>
          </div>
        );})}
      </div>
    </div>
  );
}

// ─── LOAD PHOTOS MODAL ────────────────────────────────────────────────────────
function LoadPhotosModal({ load, session, onClose, onPhotosUpdated }) {
  const [photos, setPhotos] = useState(load.photos || []);
  const [uploading, setUploading] = useState(false);
  useEffect(()=>{document.body.style.overflow="hidden";return()=>{document.body.style.overflow="";};},[]);
  const handleUpload = async (files) => {
    setUploading(true);
    const newPhotos=[];
    for(const file of files){
      await new Promise(res=>{
        const reader=new FileReader();
        reader.onload=e=>{
          const img=new Image();
          img.onload=()=>{
            const canvas=document.createElement("canvas");
            let[w,h]=[img.width,img.height];
            if(w>1200){h=h*1200/w;w=1200;}
            canvas.width=w;canvas.height=h;
            canvas.getContext("2d").drawImage(img,0,0,w,h);
            newPhotos.push({url:canvas.toDataURL("image/jpeg",0.7),timestamp:new Date().toISOString()});
            res();
          };
          img.src=e.target.result;
        };
        reader.readAsDataURL(file);
      });
    }
    const updated=[...photos,...newPhotos];
    setPhotos(updated);
    try{await sb.from("loads").update({data:{...load,photos:updated}}).eq("id",load.id);if(onPhotosUpdated)onPhotosUpdated(updated);}catch(e){}
    setUploading(false);
  };
  const deletePhoto=async(idx)=>{const updated=photos.filter((_,i)=>i!==idx);setPhotos(updated);try{await sb.from("loads").update({data:{...load,photos:updated}}).eq("id",load.id);}catch(e){}};
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",zIndex:600,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={onClose}>
      <div style={{background:"#fff",borderRadius:24,width:"100%",maxWidth:560,maxHeight:"88vh",overflowY:"auto",padding:24,paddingBottom:48}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:22}}>📷 Load Photos</div>
          <button onClick={onClose} style={{background:"rgba(0,0,0,0.07)",border:"none",borderRadius:"50%",width:32,height:32,cursor:"pointer",fontSize:18}}>✕</button>
        </div>
        <label style={{display:"block",width:"100%",padding:"14px",background:"#E8962E",color:"#1C1C1E",borderRadius:12,fontWeight:800,fontSize:14,cursor:"pointer",textAlign:"center",marginBottom:20}}>
          {uploading?"Uploading...":"📷 Add Photos"}
          <input type="file" accept="image/*" multiple style={{display:"none"}} onChange={e=>handleUpload(Array.from(e.target.files))} disabled={uploading}/>
        </label>
        {photos.length===0&&<div style={{textAlign:"center",padding:40,color:"#999"}}><div style={{fontSize:40,marginBottom:12}}>📷</div>No photos yet.</div>}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          {photos.map((p,i)=>{const pUrl=typeof p==="string"?p:(p?.url||"");const pTs=typeof p==="object"&&p?.timestamp?p.timestamp:null;return(
            <div key={i} style={{position:"relative",borderRadius:12,overflow:"hidden",aspectRatio:"4/3"}}>
              <img src={pUrl} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
              <button onClick={()=>deletePhoto(i)} style={{position:"absolute",top:6,right:6,background:"rgba(239,68,68,0.9)",border:"none",borderRadius:"50%",width:28,height:28,color:"#fff",fontSize:14,cursor:"pointer"}}>✕</button>
              {pTs&&<div style={{position:"absolute",bottom:0,left:0,right:0,background:"rgba(0,0,0,0.5)",color:"#fff",fontSize:12,padding:"4px 8px"}}>{new Date(pTs).toLocaleString()}</div>}
            </div>
          );})}
        </div>
      </div>
    </div>
  );
}

// ─── OFFLINE SYNC HOOK ────────────────────────────────────────────────────────
const OFFLINE_QUEUE_KEY="tp-offline-queue";
const getOfflineQueue=()=>{try{return JSON.parse(localStorage.getItem(OFFLINE_QUEUE_KEY)||"[]");}catch{return[];}};
const addToOfflineQueue=(action)=>{const q=getOfflineQueue();q.push({...action,queuedAt:new Date().toISOString()});localStorage.setItem(OFFLINE_QUEUE_KEY,JSON.stringify(q));};
const clearOfflineQueue=()=>localStorage.removeItem(OFFLINE_QUEUE_KEY);
function useOfflineSync(session){
  const[isOnline,setIsOnline]=useState(navigator.onLine);
  const[queueCount,setQueueCount]=useState(getOfflineQueue().length);
  useEffect(()=>{
    const handleOnline=async()=>{
      setIsOnline(true);
      const queue=getOfflineQueue();
      if(queue.length>0&&session){
        for(const action of queue){try{if(action.type==="save_load")await sbSaveLoad(action.load,action.uid,action.ownerUid);}catch(e){}}
        clearOfflineQueue();setQueueCount(0);
      }
    };
    const handleOffline=()=>setIsOnline(false);
    window.addEventListener("online",handleOnline);window.addEventListener("offline",handleOffline);
    return()=>{window.removeEventListener("online",handleOnline);window.removeEventListener("offline",handleOffline);};
  },[session]);
  return{isOnline,queueCount};
}

// ─── MULTI-LANGUAGE ───────────────────────────────────────────────────────────
const TRANSLATIONS={
  en:{dashboard:"Dashboard",addLoad:"Add Load",myLoads:"My Loads",reports:"Reports",profile:"Profile",expenses:"Expenses",drivers:"Drivers",payroll:"Payroll",analytics:"Analytics",taxExport:"Tax Export",maintenance:"Maintenance",inspection:"Inspection",documents:"Documents",emergency:"Emergency",jobBoard:"Job Board",community:"Community",signIn:"Sign In",signOut:"Sign Out",save:"Save",cancel:"Cancel",loading:"Loading...",noData:"No data yet",settings:"Settings"},
  ar:{dashboard:"الرئيسية",addLoad:"إضافة حمولة",myLoads:"حمولاتي",reports:"التقارير",profile:"الملف الشخصي",expenses:"المصاريف",drivers:"السائقون",payroll:"الرواتب",analytics:"الإحصائيات",taxExport:"تصدير الضرائب",maintenance:"الصيانة",inspection:"الفحص",documents:"الوثائق",emergency:"الطوارئ",jobBoard:"لوحة الوظائف",community:"المجتمع",signIn:"تسجيل الدخول",signOut:"تسجيل الخروج",save:"حفظ",cancel:"إلغاء",loading:"جاري التحميل...",noData:"لا توجد بيانات",settings:"الإعدادات"},
  fr:{dashboard:"Tableau de bord",addLoad:"Ajouter chargement",myLoads:"Mes chargements",reports:"Rapports",profile:"Profil",expenses:"Dépenses",drivers:"Chauffeurs",payroll:"Paie",analytics:"Analytique",taxExport:"Export fiscal",maintenance:"Maintenance",inspection:"Inspection",documents:"Documents",emergency:"Urgence",jobBoard:"Offres d'emploi",community:"Communauté",signIn:"Se connecter",signOut:"Se déconnecter",save:"Enregistrer",cancel:"Annuler",loading:"Chargement...",noData:"Aucune donnée",settings:"Paramètres"},
};
function LangSelector({lang,changeLang}){
  return(<div style={{display:"flex",gap:8}}>{[["en","🇨🇦 EN"],["ar","🇸🇦 AR"],["fr","🇫🇷 FR"]].map(([code,label])=>(<button key={code} onClick={()=>changeLang(code)} style={{padding:"6px 12px",borderRadius:20,border:`2px solid ${lang===code?"#E8962E":"#ddd"}`,background:lang===code?"#E8962E":"#fff",color:lang===code?"#fff":"#666",fontWeight:700,fontSize:12,cursor:"pointer"}}>{label}</button>))}</div>);
}

export default function TruckPilot() {
  const isAdminRoute = window.location.pathname === "/admin";
  const [session, setSession] = useState(() => getSession()); // load instantly from localStorage
  const [authChecked, setAuthChecked] = useState(() => !!getSession()); // skip auth screen if we have cached session
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [loads, setLoads] = useState([]);
  const [allDrivers, setAllDrivers] = useState([]);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [appLoading, setAppLoading] = useState(() => !getSession()); // don't show loading if we have session
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("tp-dark") !== null ? localStorage.getItem("tp-dark") === "1" : true);
  const [showAI, setShowAI] = useState(false);
  const [aiMode, setAIMode] = useState("chat");
  const [showWelcome, setShowWelcome] = useState(false);
  const [appEnterKey, setAppEnterKey] = useState(0);  // bumped on login → re-triggers fade-in
  const [loggingOut, setLoggingOut] = useState(false); // triggers exit animation

  // ── Theme: locked to Clean Light (afternoon) — no time-based switching ───────
  const timeTheme = "afternoon";

  useEffect(() => {
    // Always clean light — remove all theme/dark classes
    document.body.classList.remove("slt-dark","tp-theme-morning","tp-theme-afternoon","tp-theme-evening","tp-theme-night");
    if (darkMode) {
      document.body.classList.add("slt-dark");
    } else {
      document.body.classList.add("tp-theme-afternoon");
    }
    localStorage.setItem("tp-dark", darkMode?"1":"0");
  }, [darkMode]);
  const [rates, setRates] = useState(DEFAULT_RATES);
  const [customRoutes, setCustomRoutes] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [featureFlags, setFeatureFlags] = useState({});

  // Load feature flags from Supabase on mount
  useEffect(() => {
    sb.from("app_config").select("feature_flags").eq("id","global").maybeSingle().then(({data}) => {
      if (data?.feature_flags) setFeatureFlags(data.feature_flags);
    });
  }, []);
  const [tab, setTab_raw] = useState(() => {
    const saved = sessionStorage.getItem("tp-last-tab");
    // Don't restore certain tabs on refresh (form screens, modals)
    const noRestore = ["new","admin"];
    return saved && !noRestore.includes(saved) ? saved : "dashboard";
  });
  const [prevTab, setPrevTab] = useState("dashboard");
  const MAIN_TABS = ["dashboard","new","log","report","profile"];
  const scrollPositions = useRef({});

  // Listen for navigation events from Analytics action items
  useEffect(() => {
    const onTpNav = (e) => { if(e.detail?.tab) setTab(e.detail.tab); };
    window.addEventListener("tp-nav", onTpNav);
    return () => window.removeEventListener("tp-nav", onTpNav);
  }, []);

  const setTab = (newTab) => {
    // Save current scroll position before leaving
    scrollPositions.current[tab] = window.scrollY;
    setPrevTab(tab);
    setTab_raw(newTab);
    // Persist tab so refresh returns here
    sessionStorage.setItem("tp-last-tab", newTab);
    // Restore scroll position for the new tab, or go to top
    setTimeout(() => {
      window.scrollTo(0, scrollPositions.current[newTab] || 0);
    }, 50);
  };
  const goBack = () => {
    scrollPositions.current[tab] = window.scrollY;
    setTab_raw(prevTab);
    setPrevTab("dashboard");
    setTimeout(() => {
      window.scrollTo(0, scrollPositions.current[prevTab] || 0);
    }, 50);
  };
  const [showSettings, setShowSettings] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [userPlan, setUserPlan] = useState("free");
  const [detailLoad, setDetailLoad] = useState(null);
  const [tripSummaryLoad, setTripSummaryLoad] = useState(null);
  const [inspectionAlerts, setInspectionAlerts] = useState([]);
  const { showUpdate, applyUpdate } = useServiceWorkerUpdate();
  const { isOnline, queueCount } = useOfflineSync(session);

  // ── Poll for clear_flag every 30s while app is active ────────────────────────
  useEffect(() => {
    if (!session) return;
    const checkClearFlag = async () => {
      try {
        const { data: profile } = await sb.from("profiles").select("clear_flag").eq("id", session.uid).maybeSingle();
        if (!profile?.clear_flag) return;
        const lastClear = new Date(profile.clear_flag).getTime();
        const lastWipe = parseInt(localStorage.getItem(`tp-wiped-${session.uid}`) || "0");
        if (lastClear > lastWipe) {
          // Wipe all keys for this user
          Object.keys(localStorage).filter(k => k.includes(session.uid)).forEach(k => localStorage.removeItem(k));
          localStorage.setItem(`tp-wiped-${session.uid}`, lastClear.toString());
          await sb.from("profiles").update({ clear_flag: null }).eq("id", session.uid);
          // Force reload to show fresh empty state
          window.location.reload();
        }
      } catch(e) {}
    };
    const interval = setInterval(checkClearFlag, 30000);
    return () => clearInterval(interval);
  }, [session?.uid]);
  const [lang, setLang] = useState(() => localStorage.getItem("tp-lang") || "en");
  const changeLang = (l) => { setLang(l); localStorage.setItem("tp-lang", l); };
  const t = (key) => TRANSLATIONS[lang]?.[key] || TRANSLATIONS.en[key] || key;
  const [showLoadPhotos, setShowLoadPhotos] = useState(null);
  const [docBannerDismissed, setDocBannerDismissed] = useState(() => sessionStorage.getItem("tp-doc-banner-dismissed") === "1");
  const [docBannerDocs, setDocBannerDocs] = useState([]);

  useEffect(() => {
    if (!session) return;
    const docsKey = `tp-docs-${session.uid}`;
    try {
      const docs = JSON.parse(localStorage.getItem(docsKey) || "[]");
      const today = new Date();
      const expiring = docs.filter(d => {
        if (!d.expiry) return false;
        const diff = (new Date(d.expiry) - today) / (1000 * 60 * 60 * 24);
        return diff <= 30;
      });
      setDocBannerDocs(expiring);
    } catch(e) {}
  }, [session?.uid]);

  // ── App Notifications ─────────────────────────────────────────────────────────
  const [appNotifications, setAppNotifications] = useState([]);
  const [dismissedNotifs, setDismissedNotifs] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("tp_dismissed_notifs") || "[]"); } catch { return []; }
  });

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const { data } = await sb.from("app_notifications")
          .select("*")
          .eq("active", true)
          .neq("type", "expense_cleanup") // cleanup commands are silent — never show as banners
          .or("expires_at.is.null,expires_at.gt." + new Date().toISOString())
          .order("created_at", { ascending: false });
        setAppNotifications(data || []);
      } catch(e) {}
    };
    fetchNotifications();
    // Poll every 60 seconds for new notifications
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  const dismissNotif = (id) => {
    const updated = [...dismissedNotifs, id];
    setDismissedNotifs(updated);
    sessionStorage.setItem("tp_dismissed_notifs", JSON.stringify(updated));
  };

  const visibleNotifs = appNotifications.filter(n => {
    if (dismissedNotifs.includes(n.id)) return false;
    if (n.visibility === "before") return false; // before-only shown on login screen
    if (n.visibility === "silent") return false;  // silent = command bus, never a banner
    if (n.type === "expense_cleanup") return false; // cleanup commands are never banners
    if (n.target === "all") return true;
    if (n.target === "owners" && isOwner) return true;
    if (n.target === "drivers" && !isOwner) return true;
    if (n.target === "specific" && session && n.target_uid === session.uid) return true;
    return false;
  });

  // Load inspection alerts for owner
  const refreshInspectionAlerts = (s) => {
    if (s && (s.role === "owner")) {
      setInspectionAlerts(getInspectionAlerts(s.ownerUid || s.uid));
    }
  };
  const [editLoad, setEditLoad] = useState(null);
  const [invoiceLoad, setInvoiceLoad] = useState(null);
  const [showUpgradeEnabled, setShowUpgradeEnabled] = useState(true);
  useEffect(() => {
    sb.from("settings").select("rates").eq("user_id", "55661ff3-cd77-4e29-8de3-a4e2ad129dca").maybeSingle().then(({ data }) => {
      if (data?.rates?.showUpgradeOption !== undefined) {
        setShowUpgradeEnabled(data.rates.showUpgradeOption);
      }
    }).catch(() => {});
  }, []);

  // ── On mount: restore session and load Supabase data ─────────────────────────
  useEffect(() => {
    // ── Auto hard-refresh on new deploy ──────────────────────────────────────
    // Each deploy gets a new BUILD_VERSION timestamp. If the stored version
    // differs, clear stale load/expense caches and reload so every device
    // always runs the latest code with fresh Supabase data.
    const BUILD_VERSION = "2026-04-01-v2";
    const storedVersion = localStorage.getItem("tp-build-version");
    if (storedVersion && storedVersion !== BUILD_VERSION) {
      // Clear stale load/expense caches — Supabase is source of truth
      Object.keys(localStorage).forEach(k => {
        if (k.startsWith("tp-loads-") || k.startsWith("tp-expenses-") || k.startsWith("tp-expensesv2-")) {
          localStorage.removeItem(k);
        }
      });
      localStorage.setItem("tp-build-version", BUILD_VERSION);
      window.location.reload();
      return;
    }
    localStorage.setItem("tp-build-version", BUILD_VERSION);
    // ─────────────────────────────────────────────────────────────────────────

    // Immediately load from localStorage so no white screen
    const cachedSession = getSession();
    if (cachedSession) {
      loadLocalData(cachedSession);
      setAuthChecked(true);
    }
    // Then verify with Supabase in background and refresh data
    sb.auth.getSession().then(({ data: { session: sbSess } }) => {
      setAuthChecked(true);
      if (sbSess) { loadSupabaseData(sbSess); }
      else if (!cachedSession) { setAppLoading(false); }
    }).catch(() => { setAuthChecked(true); if (!cachedSession) setAppLoading(false); });
    const { data: { subscription } } = sb.auth.onAuthStateChange((event, sbSess) => {
      if (event === 'PASSWORD_RECOVERY') {
        setShowResetPassword(true);
      } else if (event === 'SIGNED_IN' && sbSess) {
        loadSupabaseData(sbSess);
      } else if (event === 'SIGNED_OUT') {
        setAuthChecked(true);
      }
      // Ignore TOKEN_REFRESHED and other events — they cause full re-renders
    });
    // Poll every 5 minutes to detect if account was deleted by admin → force logout
    const deletionPoll = setInterval(async () => {
      const sbSess = (await sb.auth.getSession().catch(() => ({}))).data?.session;
      if (!sbSess) return;
      const profile = await sbGetProfile(sbSess.user.id).catch(() => null);
      if (!profile) {
        clearSession();
        sessionStorage.removeItem("tp-last-tab");
        setSession(null); setLoads([]); setRates(DEFAULT_RATES); setCustomRoutes([]); setTrucks([]);
        setAuthChecked(true);
        sb.auth.signOut().catch(() => {});
      }
    }, 5 * 60 * 1000);

    // ── Realtime: live load sync ──────────────────────────────────────────────
    // When owner marks contractor paid, pays driver, or driver confirms receipt,
    // both sides see the update instantly — no refresh needed.
    const realtimeChannel = sb.channel("loads-realtime")
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "loads" }, (payload) => {
        const updated = payload.new;
        if (!updated?.id || !updated?.data) return;
        const incomingLoad = { id: updated.id, user_id: updated.user_id, ...updated.data };
        if (incomingLoad._payStatusFor) return; // pay-status rows removed
        setLoads(prev => {
          const exists = prev.some(l => l.id === incomingLoad.id);
          if (exists) return prev.map(l => l.id === incomingLoad.id ? { ...l, ...incomingLoad } : l);
          const sessionData = JSON.parse(localStorage.getItem("tp-session-v1") || "{}");
          const myUid = sessionData?.uid;
          if (incomingLoad.user_id === myUid && !incomingLoad.deleted) return [incomingLoad, ...prev];
          return prev;
        });
        setDetailLoad(prev => prev?.id === incomingLoad.id ? { ...prev, ...incomingLoad } : prev);
      })
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "loads" }, (payload) => {
        const inserted = payload.new;
        if (!inserted?.id || !inserted?.data) return;
        const incomingLoad = { id: inserted.id, user_id: inserted.user_id, ...inserted.data };
        if (incomingLoad.deleted) return;
        setLoads(prev => {
          if (prev.some(l => l.id === incomingLoad.id)) return prev;
          const sessionData = JSON.parse(localStorage.getItem("tp-session-v1") || "{}");
          const myUid = sessionData?.uid;
          if (incomingLoad.user_id === myUid) return [incomingLoad, ...prev];
          return prev;
        });
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
      clearInterval(deletionPoll);
      sb.removeChannel(realtimeChannel);
    };
  }, []);

  const loadSupabaseData = async (sbSess, silent=false) => {
    const uid = sbSess.user.id;
    const profile = await sbGetProfile(uid);
    // If profile doesn't exist (account deleted by admin), force logout immediately
    if (!profile) {
      clearSession();
      sessionStorage.removeItem("tp-last-tab");
      setSession(null); setLoads([]); setRates(DEFAULT_RATES); setCustomRoutes([]); setTrucks([]);
      setAuthChecked(true);
      sb.auth.signOut().catch(() => {});
      return;
    }
    const sess = {
      uid, email: sbSess.user.email,
      fullName: profile?.name || sbSess.user.user_metadata?.name || sbSess.user.email,
      name: profile?.name || sbSess.user.user_metadata?.name || sbSess.user.email,
      companyName: profile?.company_name || null,
      role: profile?.role || sbSess.user.user_metadata?.role || "owner",
      plan: profile?.plan || "free",
      created_at: profile?.created_at || sbSess.user.created_at || null,
      driverType: profile?.driver_type || "employee",
      supabase: true,
    };
    // Show onboarding for first-time users
    const onboardKey = `tp-onboarded-${uid}`;
    localStorage.setItem(onboardKey, "1"); // onboarding disabled
    // Welcome screen disabled

    saveSession(sess);
    // Check if admin flagged this account for data clear
    if (profile?.clear_flag) {
      const lastClear = new Date(profile.clear_flag).getTime();
      const lastWipe = parseInt(localStorage.getItem(`tp-wiped-${uid}`) || "0");
      if (lastClear > lastWipe) {
        // Wipe all localStorage keys for this user
        const keysToWipe = [
          `tp-loads-${uid}`, `tp-expenses-${uid}`, `tp-expensesv2-${uid}`,
          `tp-rates-${uid}`, `tp-routes-${uid}`, `tp-trucks-${uid}`,
          `tp-maint-${uid}`, `tp-ifta-${uid}`, `tp-payroll-${uid}`, `tp-inspection-alerts-v1-${uid}`, `tp-monthly-goal-${uid}`,
          `tp-referrals-v1`, `tp-docs-${uid}`, `tp-inspection-${uid}`,
          `tp-last-truck-${uid}`, `tp-last-route-${uid}`,
        ];
        // Also wipe any other keys that start with tp- for this uid
        Object.keys(localStorage).filter(k => k.includes(uid)).forEach(k => localStorage.removeItem(k));
        keysToWipe.forEach(k => localStorage.removeItem(k));
        localStorage.setItem(`tp-wiped-${uid}`, lastClear.toString());
        // Clear the flag in Supabase
        await sb.from("profiles").update({ clear_flag: null }).eq("id", uid);
      }
    }

    // Update session silently - don't flash loading screen
    setSession(sess);
    setUserPlan(sess.plan || "free"); // Use actual plan from Supabase
    try {
      // Each user loads only their own data — no fleet merging
      const [sbLoads, sbTrucks, sbSettings] = await Promise.all([
        sbGetLoads(uid),
        sbGetTrucks(uid),
        sbGetSettings(uid),
      ]);
      const allLoads = [...sbLoads];
      try { localStorage.setItem(loadsKey(uid), JSON.stringify(allLoads)); } catch(e) {}
      setLoads(allLoads);
      setTrucks(sbTrucks);
      setRates({ ...DEFAULT_RATES, ...(sbSettings?.rates || {}) });
      if (Array.isArray(sbSettings?.routes)) setCustomRoutes(sbSettings.routes);
    } catch (e) { console.error("Supabase data load error:", e); setAppLoading(false); return; }
    setAppLoading(false);
    if (sess.role === "owner") {
      setInspectionAlerts(getInspectionAlerts(uid));
    }
  };

  const refreshData = async () => {
    if (!session) return;
    const uid = session.uid;
    try {
      if (session.supabase) {
        // Each user loads only their own data — no fleet merging
        const [sbLoads, sbTrucks, sbSettings] = await Promise.all([
          sbGetLoads(uid),
          sbGetTrucks(uid),
          sbGetSettings(uid),
        ]);
        const allLoads = [...sbLoads];
        try { localStorage.setItem(loadsKey(uid), JSON.stringify(allLoads)); } catch(e) {}
        setLoads(allLoads);
        setTrucks(sbTrucks);
        setRates({ ...DEFAULT_RATES, ...(sbSettings?.rates || {}) });
        if (Array.isArray(sbSettings?.routes)) setCustomRoutes(sbSettings.routes);
      } else {
        const d = localStorage.getItem(loadsKey(uid));
        setLoads(d ? JSON.parse(d) : []);
      }
    } catch(e) { console.error("Refresh error:", e); }
  };

  const loadLocalData = (s) => {
    setSession(s);
    setUserPlan(s.plan || "free");
    // Always fetch fresh plan from Supabase in background
    sbGetProfile(s.uid).then(profile => {
      if (profile?.plan !== undefined) {
        setUserPlan(profile.plan || "free");
        const updated = { ...s, plan: profile.plan || "free" };
        setSession(updated);
        saveSession(updated);
      }
    }).catch(() => {});
    setAppLoading(false); // Local data loads instantly
    const ownerUid = s.ownerUid || s.uid;
    try { const d = localStorage.getItem(loadsKey(uid)); setLoads(d ? JSON.parse(d) : []); } catch {}
    // Each user loads only their own rates, routes and trucks — no fleet merging
    // Load rates/routes/trucks from Supabase first, fallback to localStorage
    try {
      sbGetSettings(s.ownerUid||s.uid).then(sbSettings => {
        if (sbSettings && sbSettings.rates) {
          localStorage.setItem(ratesKey(s.uid), JSON.stringify(sbSettings.rates));
          setRates(sbSettings.rates);
        }
        if (sbSettings && sbSettings.routes && sbSettings.routes.length > 0) {
          localStorage.setItem(routesKey(s.uid), JSON.stringify(sbSettings.routes));
          setCustomRoutes(sbSettings.routes);
        }
      }).catch(()=>{});
    } catch {}
    try {
      const ownRatesRaw = localStorage.getItem(ratesKey(s.uid));
      const ownRates = ownRatesRaw ? JSON.parse(ownRatesRaw) : {};
      setRates(ownRatesRaw ? { ...DEFAULT_RATES, ...ownRates } : DEFAULT_RATES);
    } catch { setRates(DEFAULT_RATES); }
    try { const d = localStorage.getItem(routesKey(s.uid)); setCustomRoutes(d ? JSON.parse(d) : []); } catch {}
    try { const d = localStorage.getItem(trucksKey(s.uid)); setTrucks(d ? JSON.parse(d) : []); } catch {}
    if (s.role === "owner") setInspectionAlerts(getInspectionAlerts(ownerUid));
  };

  const persist = async (updated) => {
    const sessionOwnerUid = session.ownerUid || session.uid;
    setLoads(updated);
    // Always cache to localStorage so loads show instantly on next refresh
    // (loadLocalData runs first on startup before Supabase data arrives)
    try { localStorage.setItem(loadsKey(sessionOwnerUid), JSON.stringify(updated)); } catch(e) {}
    if (session?.supabase) {
      // Each load is saved under this user's own uid only
      for (const load of updated) {
        sbSaveLoad(load, session.uid, session.uid).catch(console.error);
      }
    }
  };

  const handleLogin = (s) => {
    saveSession(s);
    // Always land on home (dashboard) after login — clear any stale tab
    sessionStorage.removeItem("tp-last-tab");
    if (s.role === "superadmin") setTab("admin");
    else setTab("dashboard");
    setAppEnterKey(k => k + 1); // trigger app fade-in animation
    if (s.supabase) {
      sb.auth.getSession().then(({ data: { session: sbSess } }) => {
        if (sbSess) loadSupabaseData(sbSess);
        setAuthChecked(true);
      });
    } else {
      loadLocalData(s);
      setAuthChecked(true);
    }
  };

  const handleLogout = async () => {
    // Animate out first, then clear state
    setLoggingOut(true);
    setTimeout(async () => {
      clearSession();
      sessionStorage.removeItem("tp-last-tab");
      setSession(null); setLoads([]); setRates(DEFAULT_RATES); setCustomRoutes([]); setTrucks([]);
      setAuthChecked(true);
      setLoggingOut(false);
      if (session?.supabase) sb.auth.signOut().catch(() => {});
    }, 380);
  };

  const saveLoad = async (load) => {
    const ex = loads.find(l => l.id === load.id);
    // CRITICAL: When editing, preserve the original owner_uid — never overwrite it
    const originalOwnerUid = ex?.owner_uid || ex?.ownerUid;
    const fleetOwnerUid = session.fleetOwnerUid || session.ownerUid;
    // "My Own Load" = always private — force owner_uid to driver's own uid regardless
    // of what it was previously. This is critical when a driver EDITS a fleet load and
    // switches it to "My Own Load" — the old owner_uid must be overwritten so the
    // owner's DB query stops returning it.
    // Fleet load = prefer the original owner_uid (preserves accuracy), fall back to
    // fleetOwnerUid or driver's own uid.
    const ownerUid = load.isOwnLoad
      ? session.uid
      : (originalOwnerUid || fleetOwnerUid || session.uid);
    const updated = ex ? loads.map(l => l.id === load.id ? { ...load, owner_uid: ownerUid } : l) : [{ ...load, owner_uid: ownerUid }, ...loads];
    persist(updated);
    if (session?.supabase) {
      sbSaveLoad(load, session.uid, ownerUid).catch(console.error);
      // Auto-sync fuel to OWNER expenses only — never driver's expenses
      if (Number(load.fuelTotal) > 0) {
        // For fleet driver, save to fleet owner. For solo/owner, save to their own.
        const fuelOwnerUid = session.fleetOwnerUid || ownerUid;
        const fuelExp = {
          id: `fuel-${load.id}`, loadRef: load.id, category: "fuel",
          amount: Number(load.fuelTotal),
          description: `Fuel – ${load.location||"Load"} (${load.fuelLitres||"?"}L @ $${Number(load.fuelPricePerLitre||0).toFixed(3)}/L)`,
          date: load.date || todayStr(), source: "load",
          taxCategory: "Line 9220", taxLabel: "Fuel & Oil",
          ownerExpense: true
        };
        sbSaveExpense(fuelExp, fuelOwnerUid).catch(console.error);
        // Make sure it never shows in driver's expenses
        const driverExps = getStored(expensesKey(session.uid)).filter(e => e.id !== fuelExp.id);
        localStorage.setItem(expensesKey(session.uid), JSON.stringify(driverExps));
      }
    } else {
      // Load fuel = owner/business expense only
      if (Number(load.fuelTotal) > 0) {
        const ownerExpKey = expensesKey(ownerUid);
        const allOwnerExp = getStored(ownerExpKey).filter(e => e.loadRef !== load.id);
        const fuelExp = {
          id: `fuel-${load.id}`, loadRef: load.id, category: "fuel",
          amount: Number(load.fuelTotal),
          description: `Fuel – ${load.location||"Load"} (${load.fuelLitres||"?"}L @ $${Number(load.fuelPricePerLitre||0).toFixed(3)}/L)`,
          date: load.date || todayStr(), source: "load",
          taxCategory: "Line 9220", taxLabel: "Fuel & Oil", ownerExpense: true
        };
        localStorage.setItem(ownerExpKey, JSON.stringify([fuelExp, ...allOwnerExp]));
      }
    }
    const isEdit = !!ex;
    tpToast(isEdit ? "Load updated" : "Load saved!", "success", `${load.location||"Load"} · ${fmtC(load.earnings||0)}`);
    setTab("log"); setEditLoad(null);
  };

  const deleteLoad = async (id) => {
    const load = loads.find(l => l.id === id);
    // Owner cannot delete a driver's load
    if (isOwner && load?.user_id && load.user_id !== session.uid) {
      alert("You cannot delete a driver's load.");
      return;
    }
    // Soft-delete: remove from visible state, keep in Supabase as deleted (recoverable for 90 days)
    const softDeleted = { ...load, deleted: true, deleted_at: new Date().toISOString() };
    persist(loads.filter(l => l.id !== id));   // removes from local state / localStorage
    if (session?.supabase) {
      sbSoftDeleteLoad(softDeleted, session.uid).catch(console.error); // write deleted flag to Supabase
    }
  };

  const toggleComplete = async (id, completed) => {
    const updated = loads.map(l => l.id === id ? { ...l, completed, completedAt: completed ? new Date().toISOString() : null } : l);
    persist(updated);
    if (completed) fireConfetti(); // 🎉 celebrate!
    if (session?.supabase) {
      const load = updated.find(l => l.id === id);
      const ownerUid = session.ownerUid || session.uid;
      sbSaveLoad(load, session.uid, ownerUid).catch(console.error);
    }
    if (detailLoad?.id === id) setDetailLoad(updated.find(l => l.id === id));
  };

  // Generic field updater — used for contractor paid, driver received, etc.
  const updateLoadFields = (id, fields) => {
    const updated = loads.map(l => l.id === id ? { ...l, ...fields } : l);
    setLoads(updated);
    try { localStorage.setItem(loadsKey(session.uid), JSON.stringify(updated)); } catch(e) {}
    if (session?.supabase) {
      const load = updated.find(l => l.id === id);
      // Each user saves to their own row only — no cross-user writes.
      sbSaveLoad(load, session.uid).catch(console.error);
    }
    if (detailLoad?.id === id) setDetailLoad(updated.find(l => l.id === id));
  };

  const addNote = (loadId, text, author) => {
    const msg = { text, authorUid: author.uid, authorName: author.fullName || author.name, timestamp: new Date().toISOString() };
    const updated = loads.map(l => l.id === loadId ? { ...l, messages: [...(l.messages || []), msg] } : l);
    persist(updated);
    if (detailLoad?.id === loadId) setDetailLoad(updated.find(l => l.id === loadId));
  };

  if (showResetPassword) return <><GlobalCSS darkMode={darkMode} timeTheme={timeTheme} /><StaticCSS /><ResetPasswordScreen onDone={() => { setShowResetPassword(false); }} /></>;
  if (!authChecked) return (
    <div style={{position:"fixed",inset:0,background:"linear-gradient(160deg,#0e1422 0%,#1C2B4A 55%,#0a1220 100%)",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:0,overflow:"hidden"}}>
      <GlobalCSS darkMode={darkMode} timeTheme={timeTheme} /><StaticCSS />
      {/* Expanding rings */}
      <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",pointerEvents:"none"}}>
        {[0,1,2,3].map(i=>(
          <div key={i} className="tp-splash-ring" style={{position:"absolute",border:"1.5px solid rgba(232,150,46,.18)",borderRadius:"50%",width:(120+i*90)+"px",height:(120+i*90)+"px",animationDelay:(i*0.55)+"s"}} />
        ))}
      </div>
      {/* Logo icon */}
      <div className="tp-splash-glow" style={{width:88,height:88,background:"linear-gradient(135deg,#E8962E,#f5b660)",borderRadius:24,display:"flex",alignItems:"center",justifyContent:"center",fontSize:44,marginBottom:20,animation:"tp-splash-glow 2s ease-in-out infinite"}}>🚛</div>
      {/* Name shimmer */}
      <div className="tp-shimmer" style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:38,letterSpacing:4}}>TRUCKPILOT</div>
      <div style={{fontSize:11,color:"rgba(255,255,255,.35)",letterSpacing:4,textTransform:"uppercase",marginTop:6,marginBottom:28}}>Your Fleet. Simplified.</div>
      {/* Loading dots */}
      <div className="tp-dot-wave"><span/><span/><span/></div>
    </div>
  );
  if (!session && isAdminRoute) return <AdminLoginScreen onLogin={handleLogin} />;
  if (!session) return <><GlobalCSS darkMode={darkMode} timeTheme={timeTheme} /><StaticCSS /><AuthScreen onLogin={handleLogin} loginNotifs={appNotifications.filter(n => n.active && (n.visibility === "before" || n.visibility === "both") && !dismissedNotifs.includes(n.id))} onDismissNotif={dismissNotif} /></>;

  const isSuperAdmin = session.role === "superadmin";

  // Super Admin sees ONLY the Admin Panel — clean and separate
  if (isSuperAdmin) return (
    <div style={{ fontFamily:"'Barlow',sans-serif", minHeight:"100vh", background:"#F5F6F8" }}>
      <GlobalCSS darkMode={darkMode} timeTheme={timeTheme} /><StaticCSS />
      {/* Admin Top Nav */}
      <div style={{ background:"linear-gradient(135deg,#1a0030,#E8962E)", padding:"0 20px", position:"sticky", top:0, zIndex:200, boxShadow:"0 2px 20px rgba(0,0,0,0.55)", height:56, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        {/* Logo on the LEFT */}
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:20,color:"#FFFFFF",letterSpacing:"1px"}}>TRUCK</span><span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:20,color:"#E8962E",letterSpacing:"1px"}}>PILOT</span>
        </div>
        {/* ADMIN + Sign Out on right */}
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,color:"#fff",fontSize:22,letterSpacing:2}}>ADMIN</span>
          <button onClick={handleLogout} style={{ padding:"6px 16px", borderRadius:8, border:"none", background:"rgba(255,255,255,0.15)", color:"#fff", fontWeight:700, fontSize:12, cursor:"pointer" }}>Sign Out</button>
        </div>
      </div>
      <SuperAdminTab session={session} />
    </div>
  );

  const isOwner = session.role === "owner";
  const ownerUid = session.ownerUid || session.uid;
  // allDrivers comes from Supabase fleet drivers (loaded in useEffect below)
  // Use state allDrivers which is populated from sbGetFleetDrivers
  const mergedRoutes = customRoutes.map(r => ({ ...r, billingMethod: r.billingMethod || "per_load", rate: r.rate || 0 }));
  // visibleLoads — fleet loads already filtered at fetch time by sbGetFleetLoads
  const visibleLoads = isOwner
    // Owner sees: their own loads + fleet driver loads
    // Exclude driver "My Own Load" entries (owner_uid = driver's own uid, not this owner's uid)
    ? loads.filter(l => !l.user_id || l.user_id === session.uid || l.owner_uid === session.uid)
    : loads.filter(l => l.assignedDriverUid === session.uid || l.addedBy === session.uid || l.user_id === session.uid);
  const unreadMessages = visibleLoads.filter(l => l.messages && l.messages.some(m => m.authorUid !== session.uid)).length;
  const plan = userPlan;
  const handleUpgrade = (planId) => { setUserPlan(planId); };

  const openUpgrade = () => { if (showUpgradeEnabled) setShowUpgrade(true); };

  // Extended nav items for ALL users
  const allOwnerTabs = ["dashboard","log","new","expenses","drivers","messages","profit","maintenance","report","ifta","payroll","analytics","documents","loadboard","tax","emergency","pay_history"];
  const allDriverTabs = ["dashboard","log","new","expenses","messages","profit","maintenance","report","analytics","documents","emergency","pay_history"];

  // Nav items for dropdown
  const ownerNavItems = [
    { id:"dashboard",     icon:"🏠", label:"Dashboard",    core:true },
    { id:"new",           icon:"➕", label:"Add Load",     core:true },
    { id:"log",           icon:"📋", label:"My Loads",      core:true },
    { id:"report",        icon:"📊", label:"Reports",       core:true },
    { id:"drivers",       icon:"👥", label:"Drivers",       core:true },
    { id:"expenses",      icon:"🧾", label:"Expenses",      core:true },
    { id:"support_inbox", icon:"🎧", label:"Inbox",         core:true },
    { id:"payroll",       icon:"💵", label:"Payroll",       core:false },
    { id:"analytics",     icon:"📈", label:"Analytics",     core:false },
    { id:"tax",           icon:"🗂", label:"Tax Export",    core:false },
    { id:"maintenance",   icon:"🔧", label:"Maintenance",   core:false },
    { id:"documents",     icon:"📁", label:"Documents",     core:false },
    { id:"emergency",     icon:"🚨", label:"Emergency",     core:false },
  ];
  const driverNavItems = [
    { id:"dashboard",   icon:"🏠", label:"Dashboard",  core:true },
    { id:"new",         icon:"➕", label:"Add Load",   core:true },
    { id:"log",         icon:"📋", label:"My Loads",   core:true },
    { id:"report",      icon:"📊", label:"Reports",    core:true },
    { id:"expenses",    icon:"🧾", label:"Expenses",   core:true },
    { id:"contact",     icon:"💬", label:"Support",    core:true },
    { id:"tax",         icon:"🗂", label:"Tax Export", core:false },
    { id:"maintenance", icon:"🔧", label:"Maintenance",core:false },
    { id:"analytics",   icon:"📈", label:"Analytics",  core:false },
    { id:"documents",   icon:"📁", label:"Documents",  core:false },
    { id:"emergency",   icon:"🚨", label:"Emergency",  core:false },
  ];

  return (<>
    <div key={appEnterKey} className={`tp-app-enter${loggingOut?" tp-app-exit":""}`} style={{ fontFamily: "'Barlow',sans-serif", minHeight: "100dvh", width: "100%", maxWidth: "100vw", overflowX: "hidden", position: "relative" }}>
      {/* ── Update banner ── */}
      {showUpdate && (
        <div onClick={applyUpdate} style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999,
          background: "#E8962E", color: "#1C1C1E", textAlign: "center",
          padding: "12px", fontSize: 14, fontWeight: 700, cursor: "pointer"
        }}>
          🚀 New update available — tap here to refresh!
        </div>
      )}
      {/* ── Offline banner ── */}
      {!isOnline && (
        <div style={{ position:"fixed", top:0, left:0, right:0, zIndex:9997, background:"#B45309", color:"#fff", textAlign:"center", padding:"10px", fontSize:13, fontWeight:700 }}>
          📵 You're offline — loads will sync when you reconnect {queueCount > 0 ? `(${queueCount} queued)` : ""}
        </div>
      )}
      {/* ── Document Expiry Banner (once per session) ── */}
      {!docBannerDismissed && docBannerDocs.length > 0 && isOnline && (
        <div style={{ position:"fixed", top: showUpdate || !isOnline ? 52 : 0, left:0, right:0, zIndex:9996, background:"#92400e", color:"#fff", padding:"10px 48px 10px 16px", fontSize:13, fontWeight:700, display:"flex", alignItems:"center", gap:10 }}>
          <span>⚠️</span>
          <span>{docBannerDocs.length} document{docBannerDocs.length>1?"s":""} expiring soon — tap to view</span>
          <button onClick={()=>setTab("documents")} style={{ background:"rgba(255,255,255,0.2)", border:"none", color:"#fff", borderRadius:8, padding:"3px 10px", fontSize:12, fontWeight:800, cursor:"pointer", marginLeft:4 }}>View</button>
          <button onClick={()=>{ setDocBannerDismissed(true); sessionStorage.setItem("tp-doc-banner-dismissed","1"); }} style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"rgba(255,255,255,0.2)", border:"none", color:"#fff", borderRadius:"50%", width:28, height:28, fontSize:16, cursor:"pointer" }}>✕</button>
        </div>
      )}
      {/* ── App Notifications banners ── */}
      {!showUpdate && visibleNotifs.map((notif, idx) => {
        const colors = {
          update:      { bg:"#E8962E", color:"#fff", icon:"🚀" },
          maintenance: { bg:"#B45309", color:"#fff", icon:"🔧" },
          announcement:{ bg:"#166534", color:"#fff", icon:"📣" },
          info:        { bg:"#E8962E", color:"#fff", icon:"💬" },
        };
        const style = colors[notif.type] || colors.info;
        return (
          <div key={notif.id} style={{
            position: "fixed", top: idx * 52, left: 0, right: 0, zIndex: 9998 - idx,
            background: style.bg, color: style.color,
            padding: "12px 48px 12px 16px", fontSize: 14, fontWeight: 700,
            display: "flex", alignItems: "center", gap: 10,
            boxShadow: "0 2px 8px rgba(0,0,0,0.55)"
          }}>
            <span style={{ fontSize: 18 }}>{style.icon}</span>
            <div style={{ flex: 1 }}>
              {notif.title && <span style={{ fontWeight: 900, marginRight: 8 }}>{notif.title}:</span>}
              {notif.message}
            </div>
            <button onClick={() => dismissNotif(notif.id)} style={{
              position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
              background: "rgba(255,255,255,0.2)", border: "none", color: "#fff",
              borderRadius: "50%", width: 28, height: 28, fontSize: 16, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900
            }}>✕</button>
          </div>
        );
      })}
      <GlobalCSS darkMode={darkMode} timeTheme={timeTheme} /><StaticCSS />
      <div className="slt-nav-safe" />
      <NavBar
        session={session}
        tab={tab}
        setTab={setTab}
        setShowSettings={setShowSettings}
        onDarkToggle={()=>setDarkMode(d=>!d)}
        darkModeOn={darkMode}
        onLogout={handleLogout}
        isOwner={isOwner}
        isSuperAdmin={isSuperAdmin}
        unreadMessages={unreadMessages}
        navItems={isOwner ? ownerNavItems : driverNavItems}
        plan={plan}
        openUpgrade={openUpgrade}
        onEditProfile={()=>setShowEditProfile(true)}
      />

      {/* ── Core tabs ── */}
      {tab === "dashboard"  && appLoading && !session && <SkeletonDashboard />}
      {tab === "dashboard"  && (!appLoading || session) && <DashboardTab   session={session} loads={visibleLoads} rates={rates} isOwner={isOwner} setTab={setTab} allDrivers={allDrivers} trucks={trucks} plan={plan} openUpgrade={showUpgradeEnabled ? openUpgrade : null} inspectionAlerts={inspectionAlerts} setShowAI={setShowAI} setAIMode={setAIMode} onClearAlert={(id)=>{ const updated = inspectionAlerts.map(a=>a.id===id?{...a,read:true}:a); setInspectionAlerts(updated); saveInspectionAlerts(session.ownerUid||session.uid, updated); }} onRefresh={refreshData} onUpdateLoad={updateLoadFields} />}
      {tab === "log"        && <HaulLogTab      session={session} loads={visibleLoads} rates={rates} isOwner={isOwner} trucks={trucks} setTab={setTab} setEditLoad={setEditLoad} deleteLoad={deleteLoad} setDetailLoad={setDetailLoad} toggleComplete={toggleComplete} allDrivers={allDrivers} darkMode={darkMode} onUpdateLoad={updateLoadFields} />}
      {tab === "new"        && <LoadFormTab     session={session} isOwner={isOwner} rates={rates} allRoutes={mergedRoutes} trucks={trucks} onSave={saveLoad} editLoad={editLoad} onCancel={() => { setEditLoad(null); goBack(); }} darkMode={darkMode} />}
      {tab === "expenses"   && <ExpensesTab     session={session} isOwner={isOwner} allLoads={loads} goBack={goBack} darkMode={darkMode} />}
      {tab === "drivers"    && isOwner && (canAccessFeature(plan,"drivers") ? <DriversTab session={session} loads={loads} rates={rates} goBack={goBack} /> : <PlanGate feature="drivers" plan={plan} onUpgrade={openUpgrade} />)}
      {tab === "drivers"    && !isOwner && <div className="slt-page" style={{background:"#070B14",color:"#F0F0F0"}}><div className="slt-hero"><div className="slt-hero-title">🔒 Owner Only</div><div className="slt-hero-sub">Driver management is for fleet owners</div></div></div>}
      {tab === "profit"     && <ProfitTab       isOwner={isOwner} />}
      {tab === "maintenance"&& <MaintenanceTab  session={session} isOwner={isOwner} trucks={trucks} goBack={goBack} />}
      {tab === "report"     && <ReportTab       loads={visibleLoads} session={session} rates={rates} isOwner={isOwner} allDrivers={allDrivers} goBack={goBack} setTab={setTab} setDetailLoad={setDetailLoad} darkMode={darkMode} />}
      {tab === "messages"   && <MessagesTab     session={session} loads={visibleLoads} isOwner={isOwner} onAddNote={addNote} />}

      {/* ── New Premium tabs ── */}
      {tab === "ifta"       && isOwner && (canAccessFeature(plan,"ifta") ? <IFTATab session={session} loads={visibleLoads} /> : <PlanGate feature="ifta" plan={plan} onUpgrade={openUpgrade} />)}
      {tab === "ifta"       && !isOwner && <div className="slt-page" style={{background:"#070B14",color:"#F0F0F0"}}><div className="slt-hero"><div className="slt-hero-title">🔒 Owner Only</div><div className="slt-hero-sub">IFTA Tax is managed by your fleet owner</div></div></div>}
      {tab === "payroll"    && isOwner && (canAccessFeature(plan,"payroll") ? <PayrollTab session={session} loads={loads} rates={rates} allDrivers={allDrivers} goBack={goBack} onUpdateLoad={updateLoadFields} onRefresh={refreshData} /> : <PlanGate feature="payroll" plan={plan} onUpgrade={openUpgrade} />)}
      {tab === "payroll"    && !isOwner && <div className="slt-page" style={{background:"#070B14",color:"#F0F0F0"}}><div className="slt-hero"><div className="slt-hero-title">🔒 Owner Only</div></div></div>}
      {tab === "analytics"  && <AnalyticsTab    session={session} loads={visibleLoads} isOwner={isOwner} rates={rates} goBack={goBack} />}
      {tab === "documents"  && <DocumentsTab    session={session} goBack={goBack} />}
      {tab === "financial_reports" && <FinancialReportsTab session={session} loads={visibleLoads} rates={rates} isOwner={isOwner} allDrivers={allDrivers} goBack={goBack} darkMode={darkMode} />}
      {tab === "tax"        && <TaxTab          session={session} isOwner={isOwner} allLoads={loads} rates={rates} goBack={goBack} darkMode={darkMode} />}
      {tab === "emergency"  && <EmergencyTab goBack={goBack} />}
      {tab === "pay_history" && <PayStubHistoryTab session={session} loads={visibleLoads} rates={rates} isOwner={isOwner} setDetailLoad={setDetailLoad} goBack={goBack} darkMode={darkMode} />}
      {tab === "contact"    && <ContactUsTab session={session} onBack={goBack} />}
      {tab === "profile"    && <ProfileTab session={session} loads={visibleLoads} trucks={trucks} plan={plan} isOwner={isOwner} onLogout={handleLogout} setTab={setTab} setShowSettings={setShowSettings} onDarkToggle={()=>setDarkMode(d=>!d)} darkModeOn={darkMode} onEditProfile={()=>setShowEditProfile(true)} openUpgrade={showUpgradeEnabled ? openUpgrade : null} lang={lang} changeLang={changeLang} featureFlags={featureFlags} />}
      {tab === "fuel_log"         && <FuelLogTab2 session={session} trucks={trucks} goBack={goBack} />}
      {tab === "doc_expiry"       && <DocExpiryTab session={session} isOwner={isOwner} goBack={goBack} />}
      {tab === "support_inbox" && isOwner && <SupportInboxTab session={session} />}
      {tab === "admin" && isSuperAdmin && <SuperAdminTab session={session} />}

      {/* ── Welcome Screen ── */}
{/* WelcomeScreen disabled */}

{/* AI Assistant modal removed */}

      {/* ── Bottom Tab Bar moved outside animated wrapper ── */}

      {/* ── Onboarding ── */}
{/* OnboardingScreen disabled */}

{/* Floating buttons removed */}

      {/* ── Footer ── */}
      <TruckPilotFooter lang={lang} setLang={changeLang} setTab={setTab} session={session} setShowAI={setShowAI} setAIMode={setAIMode} />
      {/* ── Toast notifications ── */}
      <TPToastContainer />
    </div>
    {/* ── Modals — outside animated wrapper so position:fixed is relative to viewport, not transformed div ── */}
    {showLoadPhotos && <LoadPhotosModal load={showLoadPhotos} session={session} onClose={()=>setShowLoadPhotos(null)} onPhotosUpdated={(photos)=>{ setShowLoadPhotos(prev=>prev?{...prev,photos}:null); }} />}
    {detailLoad && <LoadDetailModal load={detailLoad} onClose={() => setDetailLoad(null)} rates={rates} isOwner={isOwner} trucks={trucks} session={session} allDrivers={allDrivers} darkMode={darkMode} onToggleComplete={toggleComplete} onGenerateInvoice={(l) => { setInvoiceLoad(l); setDetailLoad(null); }} onAddNote={addNote} onSummary={() => { setTripSummaryLoad(detailLoad); setDetailLoad(null); }} onViewPhotos={(l)=>{ setShowLoadPhotos(l); setDetailLoad(null); }} onUpdateLoad={updateLoadFields} />}
    {invoiceLoad && <InvoiceModal load={invoiceLoad} onClose={() => setInvoiceLoad(null)} rates={rates} trucks={trucks} session={session} />}
    {showSettings && <SettingsModal session={session} rates={rates} setRates={setRates} customRoutes={customRoutes} setCustomRoutes={setCustomRoutes} trucks={trucks} setTrucks={setTrucks} onClose={() => setShowSettings(false)} isOwner={isOwner} darkMode={darkMode} />}
    {showUpgrade && showUpgradeEnabled && <UpgradeModal session={session} onClose={() => setShowUpgrade(false)} onUpgrade={handleUpgrade} />}
    {showEditProfile && <EditProfileModal session={session} onClose={()=>setShowEditProfile(false)} onSave={(newName, newCompany)=>{ setSession(s=>({...s,fullName:newName,name:newName,companyName:newCompany})); }} />}
    {tripSummaryLoad && <TripSummaryModal load={tripSummaryLoad} onClose={() => setTripSummaryLoad(null)} rates={rates} session={session} trucks={trucks} />}
    {/* ── Bottom Tab Bar — outside animated wrapper so position:fixed works correctly ── */}
    {!isSuperAdmin && (
      <BottomTabBar tab={tab} setTab={setTab} isOwner={isOwner} unreadMessages={unreadMessages} inspectionAlerts={inspectionAlerts} darkMode={darkMode} />
    )}
  </>
  );
}

// ─── TruckPilot Footer ───────────────────────────────────────────────────────
function TruckPilotFooter({ lang, setLang, setTab, session, setShowAI, setAIMode }) {
  const [modal, setModal] = useState(null); // "contact" | "chat" | "help" | "terms" | "privacy"

  const openModal = (key) => { setModal(key); };
  const closeModal = () => setModal(null);

  const LINKS = [
    { key: "contact", label: "Contact Us" },
    { key: "chat",    label: "Live Chat"   },
    { key: "help",    label: "Help Center" },
    { key: "terms",   label: "Terms"       },
    { key: "privacy", label: "Privacy"     },
  ];

  const overlayStyle = {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 9000,
    display: "flex", alignItems: "flex-end", justifyContent: "center",
  };
  const sheetStyle = {
    background: "#fff", borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 540,
    maxHeight: "92vh", overflowY: "auto", fontFamily: "'Barlow',sans-serif",
  };
  const headerStyle = {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "18px 20px 12px", borderBottom: "1px solid #E5E7EB",
    position: "sticky", top: 0, background: "#fff", zIndex: 1,
  };
  const inputStyle = {
    width: "100%", padding: "11px 14px", borderRadius: 10,
    border: "1.5px solid #D1D5DB", fontSize: 14, fontFamily: "inherit",
    boxSizing: "border-box", outline: "none", marginTop: 4,
  };
  const labelStyle = { fontSize: 12, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 2 };
  const btnPrimary = {
    width: "100%", padding: "13px", background: "#1A1A1A", color: "#fff",
    border: "none", borderRadius: 12, fontWeight: 800, fontSize: 15,
    cursor: "pointer", fontFamily: "inherit", marginTop: 4,
  };

  // ── Contact Us Modal ──────────────────────────────────────────────────────
  const ContactModal = () => {
    const [form, setForm] = useState({
      name: session?.fullName || session?.name || "",
      email: session?.email || "",
      phone: "",
      subject: "general",
      message: "",
    });
    const [sent, setSent] = useState(false);
    const [sending, setSending] = useState(false);

    const send = async () => {
      if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
        alert("Please fill in name, email and message."); return;
      }
      setSending(true);
      try {
        const subjectLabels = { general:"General Inquiry", billing:"Billing", bug:"Bug Report", feature:"Feature Request", fleet:"Fleet Help", other:"Other" };
        const msgText = `📬 Contact Form\n\nName: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone||"—"}\nSubject: ${subjectLabels[form.subject]||form.subject}\n\n${form.message}`;
        const uid = session?.uid || `guest-${Date.now()}`;
        const name = form.name;
        const email = form.email;
        const msgObj = { id: Date.now().toString(), from: "user", text: msgText, time: new Date().toISOString() };
        await chatSendMsg(uid, name, email, msgObj);
        setSent(true);
      } catch(e) { alert("Could not send. Please email support@truckpilot.ca directly."); }
      setSending(false);
    };

    if (sent) return (
      <div style={{ textAlign: "center", padding: "40px 24px" }}>
        <div style={{ fontSize: 56, marginBottom: 12 }}>✅</div>
        <div style={{ fontWeight: 900, fontSize: 20, color: "#1A1A1A", marginBottom: 8 }}>Message Sent!</div>
        <div style={{ fontSize: 14, color: "#6B7280", marginBottom: 24 }}>Our team will get back to you at <strong>{form.email}</strong> within 1 business day.</div>
        <button onClick={closeModal} style={{ ...btnPrimary, width: "auto", padding: "11px 32px" }}>Close</button>
      </div>
    );

    return (
      <div style={{ padding: "0 20px 32px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 16 }}>
          <div>
            <label style={labelStyle}>Full Name *</label>
            <input style={inputStyle} value={form.name} onChange={e => setForm(f=>({...f,name:e.target.value}))} placeholder="Your name" />
          </div>
          <div>
            <label style={labelStyle}>Phone Number</label>
            <input style={inputStyle} value={form.phone} onChange={e => setForm(f=>({...f,phone:e.target.value}))} placeholder="+1 (780) 000-0000" type="tel" />
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <label style={labelStyle}>Email Address *</label>
          <input style={inputStyle} value={form.email} onChange={e => setForm(f=>({...f,email:e.target.value}))} placeholder="you@email.com" type="email" />
        </div>
        <div style={{ marginTop: 12 }}>
          <label style={labelStyle}>Subject</label>
          <select style={{ ...inputStyle }} value={form.subject} onChange={e => setForm(f=>({...f,subject:e.target.value}))}>
            <option value="general">General Inquiry</option>
            <option value="billing">Billing / Plan</option>
            <option value="bug">Bug Report</option>
            <option value="feature">Feature Request</option>
            <option value="fleet">Fleet Help</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div style={{ marginTop: 12 }}>
          <label style={labelStyle}>Message *</label>
          <textarea style={{ ...inputStyle, minHeight: 120, resize: "vertical" }} value={form.message} onChange={e => setForm(f=>({...f,message:e.target.value}))} placeholder="How can we help you?" />
        </div>
        <button onClick={send} disabled={sending} style={{ ...btnPrimary, marginTop: 20 }}>
          {sending ? "Sending…" : "📨 Send Message"}
        </button>
        <div style={{ textAlign: "center", marginTop: 12, fontSize: 12, color: "#9CA3AF" }}>
          Or email us directly at{" "}
          <a href="mailto:support@truckpilot.ca" style={{ color: "#1A1A1A", fontWeight: 700 }}>support@truckpilot.ca</a>
        </div>
      </div>
    );
  };

  // ── Live Chat Modal ───────────────────────────────────────────────────────
  const LiveChatModal = () => {
    if (!session?.uid) return (
      <div style={{ padding: "32px 24px", textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>💬</div>
        <div style={{ fontWeight: 800, fontSize: 16, color: "#1A1A1A", marginBottom: 8 }}>Sign in to use Live Chat</div>
        <div style={{ fontSize: 14, color: "#6B7280", marginBottom: 20 }}>Log in or create an account to chat with our support team.</div>
        <a href="mailto:support@truckpilot.ca" style={{ display: "inline-block", padding: "12px 28px", background: "#1A1A1A", color: "#fff", borderRadius: 12, fontWeight: 800, textDecoration: "none", fontSize: 14 }}>
          📧 Email Support
        </a>
      </div>
    );
    return (
      <div style={{ height: "70vh", display: "flex", flexDirection: "column" }}>
        <ContactUsTab session={session} onBack={closeModal} />
      </div>
    );
  };

  // ── Help Center Modal ─────────────────────────────────────────────────────
  const HelpModal = () => {
    const [open, setOpen] = useState(null);
    const FAQS = [
      { q: "How do I log a load?", a: "Tap '+ Add Load' from the Home screen or Load Log tab. Fill in origin, destination, date, earnings, and any wait time. Tap Save — that's it. The load appears in your log instantly." },
      { q: "How do I join a fleet?", a: "Go to Profile → Edit Profile, scroll to 'Join a Fleet', enter your owner's invite code, and tap Join Fleet Instantly. Your owner will see you in their Drivers tab right away." },
      { q: "Why can't I rejoin a fleet?", a: "If you see 'already in fleet', just enter the invite code again and tap Join — the app will refresh your connection. If issues persist, ask your owner to remove and re-add you from their Drivers tab." },
      { q: "How is my pay calculated?", a: "Route Pay + Wait Pay = Total Pay. Route Pay uses your per-mile or flat rate. Wait Pay uses your driver wait rate × total wait hours. You can edit these rates in Settings." },
      { q: "What expenses can I deduct?", a: "CRA allows fuel, maintenance, insurance, union dues, tools, lodging, and more. Log these in the Expenses tab. Business expenses go to your owner's report. Personal expenses track your out-of-pocket costs." },
      { q: "How does IFTA work in TruckPilot?", a: "The IFTA tab (owners only) tracks your fuel purchases and miles by province/state. It auto-generates quarterly fuel tax reports you can export for filing." },
      { q: "Can I delete a load or expense?", a: "Yes. Open the load or expense and tap Delete. Items are soft-deleted — they're hidden from your view but can be recovered by an admin within 90 days." },
      { q: "How do I export my data for taxes?", a: "Go to Profile → Privacy & Security → Export My Data. You'll get a CSV with all your loads and expenses. The Reports tab also has a 'Download Report PDF' button." },
      { q: "How do I contact support?", a: "Use the Contact Us form below, or tap Live Chat to message our team directly. We typically respond within 1 business day." },
    ];
    return (
      <div style={{ padding: "0 20px 32px" }}>
        {/* AI Help CTA */}
        <div style={{ background: "linear-gradient(135deg,#1A1A1A,#E8962E)", borderRadius: 14, padding: "16px 18px", margin: "16px 0", display: "flex", alignItems: "center", gap: 14, cursor: "pointer" }}
          onClick={() => { closeModal(); if (setShowAI) { setAIMode("chat"); setShowAI(true); } else if (setTab) setTab("profile"); }}>
          <div style={{ fontSize: 32 }}>🤖</div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15, color: "#fff" }}>Ask TruckPilot AI</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", marginTop: 2 }}>Get instant answers to any trucking question — pay, taxes, IFTA, fleet setup and more.</div>
          </div>
          <div style={{ marginLeft: "auto", color: "#FFD700", fontSize: 20 }}>›</div>
        </div>

        {/* FAQ accordion */}
        <div style={{ fontWeight: 800, fontSize: 13, color: "#6B7280", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10, marginTop: 4 }}>Frequently Asked Questions</div>
        {FAQS.map((faq, i) => (
          <div key={i} style={{ borderBottom: "1px solid #E5E7EB" }}>
            <button
              onClick={() => setOpen(open === i ? null : i)}
              style={{ width: "100%", background: "none", border: "none", textAlign: "left", padding: "14px 0", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", fontFamily: "inherit" }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: "#1A1A1A" }}>{faq.q}</span>
              <span style={{ color: "#9CA3AF", fontSize: 18, flexShrink: 0, marginLeft: 8 }}>{open === i ? "−" : "+"}</span>
            </button>
            {open === i && (
              <div style={{ fontSize: 13, color: "#4B5563", lineHeight: 1.65, paddingBottom: 14 }}>{faq.a}</div>
            )}
          </div>
        ))}

        {/* Bottom CTAs */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 20 }}>
          <button onClick={() => { setModal("contact"); }} style={{ padding: "12px", background: "#F3F4F6", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer", color: "#1A1A1A", fontFamily: "inherit" }}>
            📨 Contact Us
          </button>
          <button onClick={() => { setModal("chat"); }} style={{ padding: "12px", background: "#1A1A1A", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer", color: "#fff", fontFamily: "inherit" }}>
            💬 Live Chat
          </button>
        </div>
      </div>
    );
  };

  // ── Terms Modal ───────────────────────────────────────────────────────────
  const TermsModal = () => (
    <div style={{ padding: "16px 20px 32px" }}>
      <div style={{ background: "#F9FAFB", borderRadius: 12, padding: "16px 18px", marginBottom: 16, fontSize: 13, color: "#374151", lineHeight: 1.7 }}>
        <p style={{ margin: "0 0 12px", fontWeight: 700, color: "#1A1A1A" }}>Last updated: January 1, 2025</p>
        <p style={{ margin: "0 0 10px" }}>By using TruckPilot ("the App"), you agree to these Terms of Service. If you do not agree, do not use the App.</p>
        <p style={{ margin: "0 0 6px", fontWeight: 700 }}>1. Use of Service</p>
        <p style={{ margin: "0 0 10px" }}>TruckPilot is a fleet and load management tool for Canadian commercial truckers and fleet owners. You agree to use the App only for lawful purposes and in compliance with all applicable Canadian laws and regulations.</p>
        <p style={{ margin: "0 0 6px", fontWeight: 700 }}>2. Account Responsibility</p>
        <p style={{ margin: "0 0 10px" }}>You are responsible for maintaining the confidentiality of your account credentials and for all activity under your account. Notify us immediately at support@truckpilot.ca if you suspect unauthorized use.</p>
        <p style={{ margin: "0 0 6px", fontWeight: 700 }}>3. Data Accuracy</p>
        <p style={{ margin: "0 0 10px" }}>TruckPilot stores the data you enter. You are responsible for the accuracy of load, expense, and tax data. The App does not provide professional tax, legal, or accounting advice.</p>
        <p style={{ margin: "0 0 6px", fontWeight: 700 }}>4. Fleet Relationships</p>
        <p style={{ margin: "0 0 10px" }}>Fleet owners and drivers share data under agreed fleet settings. TruckPilot is not responsible for disputes between fleet owners and drivers.</p>
        <p style={{ margin: "0 0 6px", fontWeight: 700 }}>5. Subscription & Billing</p>
        <p style={{ margin: "0 0 10px" }}>Free plans have limited features. Pro plans are billed monthly or annually. Cancellations take effect at the end of the billing period. No refunds for partial months.</p>
        <p style={{ margin: "0 0 6px", fontWeight: 700 }}>6. Limitation of Liability</p>
        <p style={{ margin: "0 0 10px" }}>TruckPilot is provided "as is." We are not liable for lost data, missed tax deadlines, or financial decisions made based on App data.</p>
        <p style={{ margin: "0 0 6px", fontWeight: 700 }}>7. Changes to Terms</p>
        <p style={{ margin: 0 }}>We may update these terms. Continued use after changes constitutes acceptance. Contact us at support@truckpilot.ca with any questions.</p>
      </div>
      <a href="https://truckpilot.ca/terms" target="_blank" rel="noreferrer"
        style={{ display: "block", textAlign: "center", padding: "13px", background: "#1A1A1A", color: "#fff", borderRadius: 12, fontWeight: 800, fontSize: 14, textDecoration: "none" }}>
        View Full Terms on Website ↗
      </a>
    </div>
  );

  // ── Privacy Modal ─────────────────────────────────────────────────────────
  const PrivacyModal = () => (
    <div style={{ padding: "16px 20px 32px" }}>
      <div style={{ background: "#F9FAFB", borderRadius: 12, padding: "16px 18px", marginBottom: 16, fontSize: 13, color: "#374151", lineHeight: 1.7 }}>
        <p style={{ margin: "0 0 12px", fontWeight: 700, color: "#1A1A1A" }}>Last updated: January 1, 2025</p>
        <p style={{ margin: "0 0 10px" }}>TruckPilot respects your privacy. This policy explains what data we collect, how we use it, and your rights.</p>
        <p style={{ margin: "0 0 6px", fontWeight: 700 }}>What We Collect</p>
        <p style={{ margin: "0 0 10px" }}>Account info (name, email, role), load and expense data you enter, truck and maintenance records, fuel log entries, and fleet membership details. We do NOT collect GPS location data or sell your data to third parties.</p>
        <p style={{ margin: "0 0 6px", fontWeight: 700 }}>How We Use Your Data</p>
        <p style={{ margin: "0 0 10px" }}>To operate the App, calculate pay and taxes, generate reports, support fleet management, and provide customer support. We may use anonymized aggregate data to improve the App.</p>
        <p style={{ margin: "0 0 6px", fontWeight: 700 }}>Fleet Data Sharing</p>
        <p style={{ margin: "0 0 10px" }}>When you join a fleet, your fleet loads and business expenses are visible to your fleet owner for the duration of your active membership. Private ("My Own Load") entries are never shared. After leaving a fleet, your historical fleet data remains accessible to both parties for reference.</p>
        <p style={{ margin: "0 0 6px", fontWeight: 700 }}>Data Retention</p>
        <p style={{ margin: "0 0 10px" }}>Your data is retained while your account is active. Soft-deleted items are kept for 90 days for recovery. You can export or delete your data at any time via Profile → Privacy & Security.</p>
        <p style={{ margin: "0 0 6px", fontWeight: 700 }}>Security</p>
        <p style={{ margin: "0 0 10px" }}>Data is encrypted in transit (TLS) and at rest. We use Supabase (hosted in Canada/US) with row-level security. We never store passwords in plain text.</p>
        <p style={{ margin: "0 0 6px", fontWeight: 700 }}>Your Rights</p>
        <p style={{ margin: 0 }}>You have the right to access, correct, export, or delete your data. Contact us at <a href="mailto:privacy@truckpilot.ca" style={{ color: "#1A1A1A", fontWeight: 700 }}>privacy@truckpilot.ca</a>.</p>
      </div>
      <a href="https://truckpilot.ca/privacy" target="_blank" rel="noreferrer"
        style={{ display: "block", textAlign: "center", padding: "13px", background: "#1A1A1A", color: "#fff", borderRadius: 12, fontWeight: 800, fontSize: 14, textDecoration: "none" }}>
        View Full Privacy Policy ↗
      </a>
    </div>
  );

  const MODAL_TITLES = {
    contact: "📨 Contact Us",
    chat:    "💬 Live Chat",
    help:    "❓ Help Center",
    terms:   "📋 Terms of Service",
    privacy: "🔒 Privacy Policy",
  };

  return (
    <>
    {/* ── Modal overlay ── */}
    {modal && (
      <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) closeModal(); }}>
        <div style={sheetStyle}>
          <div style={headerStyle}>
            <div style={{ fontWeight: 900, fontSize: 17, color: "#1A1A1A", fontFamily: "'Barlow Condensed',sans-serif" }}>
              {MODAL_TITLES[modal]}
            </div>
            <button onClick={closeModal} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#9CA3AF", lineHeight: 1 }}>✕</button>
          </div>
          {modal === "contact" && <ContactModal />}
          {modal === "chat"    && <LiveChatModal />}
          {modal === "help"    && <HelpModal />}
          {modal === "terms"   && <TermsModal />}
          {modal === "privacy" && <PrivacyModal />}
        </div>
      </div>
    )}
    </>
  );
}
 
